import CompanyEmailPanel from './CompanyEmailPanel';
import { recentFeatureCommands } from './recentFeatureCommands';
import RecruitmentAISettings from './RecruitmentAISettings';
import PayslipGroupNotification from './PayslipGroupNotification';
import EmployeeWelcomeEmail from './EmployeeWelcomeEmail';
import EmployeeTemporaryPassword from './EmployeeTemporaryPassword';
import BulkEmployeeInvitations from './BulkEmployeeInvitations';
import EmployeeEmailPolicy from './EmployeeEmailPolicy';
import HRNotificationAction from './HRNotificationAction';
import SendPayslipEmailModal from './PayslipEmailDialog';
import { companyEmail, withoutLegacyEmailCredentials, clearLegacyEmailCaches } from './companyEmail';
import CompanyAddonsPanel from './CompanyAddonsPanel';
import CompanyConnectivitySetup from './CompanyConnectivitySetup';
import { loadWorkspace, applyWorkspaceBranding } from './workspace';
import AdminAccessPanel from './AdminAccessPanel';
import { adminSession, signInAdmin, signOutAdmin, syncFileSession, provisionCompanyAdmin } from './adminAuth';
import AdminPasswordChange from './AdminPasswordChange';
import LogViewer from './LogViewer';
import { employeeLogin, employeeAuthSession, resumeEmployeeSession, requestPasswordReset, employeeLogout, attachEmployeeSession } from './employeeAuth';
import PasswordSetup from './PasswordSetup';
import DocHub from './DocHub';
import EmployeeReferrals from './EmployeeReferrals';
import ReportingManagerControl from "./ReportingManagerControl";
import { applySalaryRevisions, salaryToday, sortSalaryRevisions } from "./salaryRevisions";
import { planEmployeeImport, employeeSalaryPatch } from "./employeeImport";
import RecruitmentDashboard from './RecruitmentHub';
import OffboardingDashboard from './OffboardingHub';
import EmployeeInterviews from './EmployeeInterviews';
import { convertJoinedCandidate } from './recruitmentAccess';
import { createSystemRegressionChecks, runSystemRegressionChecks } from "./systemRegressionChecks";
import { runLegacySystemTests } from "./systemTestLegacy";
import { createSystemTestIdentity, cleanupSystemTestFixtures } from "./systemTestFixtures";
import { PAYSLIP_TEMPLATES, PAYSLIP_COLOURS, normalizePayslipStyle, getPayslipTheme, saveCompanyPayslipStyle } from "./payslipStyles";
import { addPayslipCompanyHeader, getPayslipCompanyDetails } from "./payslipCompanyHeader";
import CompanyNavSection, { sidebarGroupFor } from "./CompanyNavSection";
import PayrollWorkflowPanel from "./PayrollWorkflowPanel";
import DashboardCharts, { DashboardComparison } from './DashboardCharts';
import { summarizeDashboardMonth } from './dashboardAnalytics';
import { assertPayrollDraft, buildPayrollRows, usePayrollWorkflow } from "./payrollWorkflow";
import { validateEmployeeRecord, validateEmployeeSalary } from "./employeeValidation";
import { getOrgRelationships } from "./orgRelationships";
import React, { useState, useMemo, useRef, useEffect, useCallback, Fragment } from "react";
import * as XLSX from "xlsx-js-style";
import { createClient } from "@supabase/supabase-js";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import AdminReportsHub from "./ReportsHub";
import TrainingHub from "./TrainingWorkspace";
import EmployeeTraining from "./EmployeeTraining";
import SecurityThreatDashboard, {
  checkAdminLockout,
  recordSuccessfulEmployeeLogin,
  recordFailedAdminLogin,
  recordSuccessfulAdminLogin,
  unlockEmployeeAccount,
  unlockAdminAccount,
  AccountLockBadge,
  getLocalEmployeeLockStatus,
} from "./SecurityHub";

// ── Modular Imports
import { LOGO, HEADER_IMG } from "./assets/branding";
import {
  COMPANY_TEMPLATE_B64,
  BULK_COMPANY_TEMPLATE_B64,
  SINGLE_COMPANY_DETAILS_TEMPLATE_B64,
  ALL_COMPANIES_EXCEL_TEMPLATE_B64,
  SALARY_STRUCTURE_TEMPLATE_B64,
} from "./assets/templates";
import { supabase, logAudit, getSupabaseConfig } from "./supabaseClient";
import {
  MONTHS,
  MONTH_SHORT,
  PLAN_LIMITS,
  DEFAULT_CITY,
  DEFAULT_STATE,
  DEFAULT_CONFIG,
} from "./payrollConstants";
import {
  calcSalaryFull,
  fmtINR,
  fmtNum,
  formatDateDDMMYYYY,
  parseDateFlexible,
  fmtDate,
  fmtDDMMYYYY,
  parseDDMMYYYYToISO,
  getEmployeeMonthStatus,
  validatePAN,
  calcAge,
  validateDOJ,
  getEmpIdCode,
  cmpEmpId,
  cmpEmpName,
} from "./salaryCalculator";

// ── Re-exports for backward compatibility
export {
  supabase,
  logAudit,
  getSupabaseConfig,
  formatDateDDMMYYYY,
  parseDateFlexible,
  calcAge,
  validateDOJ,
};

const EmpStatusBadge = ({status}) => {
  const s = String(status||"confirmed").toLowerCase();
  const colors = { confirmed: "#10b981", probation: "#f59e0b", notice: "#ef4444", terminated: "#64748b" };
  const c = colors[s] || "#9ba8c8";
  return <span style={{display:"inline-block",padding:"2px 6px",borderRadius:4,fontSize:10,fontWeight:700,color:"#fff",background:c,textTransform:"uppercase"}}>{s}</span>;
};

const ExperienceInfo = ({ employee }) => {
  const y = employee.prev_exp_years || 0;
  const m = employee.prev_exp_months || 0;
  if (!y && !m) return <div style={{padding:"12px 16px",color:"#6b7a9e"}}>No previous experience recorded.</div>;
  return (
    <div className="info-grid" style={{padding:"16px"}}>
      <div className="info-field"><div className="info-label">Previous Experience</div><div className="info-val">{y} Years, {m} Months</div></div>
    </div>
  );
};

// ── CSS
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#fafafa;color:#1e293b}
  .app{min-height:100vh;background:#fafafa}

  /* Login Page */
  .login-page{min-height:100vh;background:radial-gradient(circle at 50% 0%, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%);display:flex;align-items:center;justify-content:center;padding:20px}
  .login-card{background:#ffffff;border:1.5px solid #cbd5e1;border-radius:24px;padding:48px 42px;width:100%;max-width:420px;box-shadow:0 25px 50px -12px rgba(15,23,42,0.12), 0 4px 6px -1px rgba(0,0,0,0.05)}
  .login-logo{text-align:center;margin-bottom:28px}
  .login-logo-icon{width:64px;height:64px;background:linear-gradient(135deg,#2563eb,#4f46e5);border-radius:18px;display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 16px;box-shadow:0 10px 20px -5px rgba(37,99,235,0.4)}
  .login-title{font-family:'Inter',sans-serif;font-weight:900;font-size:26px;color:#0f172a;text-align:center;margin-bottom:4px;letter-spacing:-0.6px}
  .login-sub{font-size:14px;color:#475569;text-align:center;margin-bottom:28px;font-weight:600}
  .login-field{display:flex;flex-direction:column;gap:6px;margin-bottom:18px}
  .login-field label{font-size:12px;font-weight:800;color:#1e293b;text-transform:uppercase;letter-spacing:.8px}
  .login-field input{background:#f8fafc;border:1.5px solid #94a3b8;border-radius:12px;padding:12px 16px;font-size:15px;font-weight:600;color:#0f172a;outline:none;font-family:'Inter',sans-serif;transition:all .2s}
  .login-field input:focus{border-color:#2563eb;background:#ffffff;box-shadow:0 0 0 4px rgba(37,99,235,0.18)}
  .login-btn{width:100%;background:linear-gradient(135deg,#2563eb,#4f46e5);color:#ffffff;border:none;border-radius:12px;padding:14px;font-size:15.5px;font-weight:800;letter-spacing:.2px;cursor:pointer;font-family:'Inter',sans-serif;margin-top:10px;box-shadow:0 6px 18px rgba(37,99,235,0.35);transition:all .2s}
  .login-btn:hover{opacity:.96;transform:translateY(-1.5px);box-shadow:0 8px 22px rgba(37,99,235,0.45)}
  .login-btn.employee-login-btn{background:linear-gradient(145deg,#ffffff,#edf1f5);color:#334155;border:1px solid #cbd5e1;box-shadow:0 3px 10px rgba(15,23,42,.08)}
  .login-btn.employee-login-btn:hover{background:linear-gradient(145deg,#f8fafc,#e2e8f0);box-shadow:0 5px 14px rgba(15,23,42,.12)}
  .login-err{background:#fef2f2;border:1.5px solid #ef4444;border-radius:12px;padding:12px 16px;font-size:13px;color:#991b1b;margin-bottom:18px;text-align:center;font-weight:700}

  /* Header */
  .header{background:#fff;border-bottom:1.5px solid #e2e8f0;padding:0 28px;height:58px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50;box-shadow:0 1px 4px rgba(0,0,0,.06)}
  .breadcrumb{display:flex;align-items:center;gap:8px;font-size:13px;color:#94a3b8}
  .breadcrumb .sep{color:#cbd5e1}
  .breadcrumb .active{color:#1e293b;font-weight:500}
  .breadcrumb .crumb{color:#4f8ef7;cursor:pointer}
  .breadcrumb .crumb:hover{text-decoration:underline}
  .header-right{display:flex;align-items:center;gap:8px}

  /* Layout */
  .main{max-width:1200px;margin:0 auto;padding:28px 22px}
  .page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:24px;flex-wrap:wrap;gap:12px}
  .page-title{font-family:'Inter',sans-serif;font-weight:800;font-size:24px;color:#1e293b;letter-spacing:-0.5px}
  .page-sub{font-size:13px;color:#64748b;margin-top:3px}

  /* Stats */
  .stats-bar{display:flex;gap:10px;margin-bottom:22px;flex-wrap:wrap}
  .stat-chip{background:#fff;border:1.5px solid #e2e8f0;border-radius:10px;padding:10px 16px;box-shadow:0 1px 3px rgba(0,0,0,.05)}
  .stat-num{font-family:'Inter',sans-serif;font-weight:800;font-size:19px;color:#1e293b}
  .stat-label{font-size:11px;color:#64748b}

  /* Company grid */
  .company-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(290px,1fr));gap:14px}
  .company-card{background:#fff;border:1.5px solid #e2e8f0;border-radius:14px;padding:18px;cursor:pointer;transition:all .2s;position:relative;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.05)}
  .company-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#4f8ef7,#7c5cfc);opacity:0;transition:opacity .2s}
  .company-card:hover{border-color:#bfdbfe;transform:translateY(-2px);box-shadow:0 8px 24px rgba(79,142,247,.12)}
  .company-card:hover::before{opacity:1}
  .company-card:hover .card-actions{opacity:1}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:12px}
  .card-icon{width:40px;height:40px;background:linear-gradient(135deg,#dbeafe,#ede9fe);border:1px solid #bfdbfe;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:16px;cursor:pointer;transition:all .2s}
  .card-icon:hover{background:linear-gradient(135deg,#4f8ef7,#7c5cfc);border-color:#4f8ef7;transform:scale(1.08);box-shadow:0 0 10px rgba(79,142,247,0.3)}
  .card-actions{display:flex;gap:5px;opacity:0;transition:opacity .2s}
  .icon-btn{width:28px;height:28px;border-radius:7px;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;transition:all .15s}
  .icon-btn.edit{background:#dbeafe;color:#4f8ef7}
  .icon-btn.edit:hover{background:#4f8ef7;color:#fff}
  .icon-btn.del{background:#fee2e2;color:#ef4444}
  .icon-btn.del:hover{background:#ef4444;color:#fff}
  .card-name{font-family:'Inter',sans-serif;font-weight:700;font-size:14px;color:#1e293b;margin-bottom:3px;line-height:1.3}
  .card-tin{font-size:11px;color:#94a3b8;font-family:monospace;margin-bottom:10px}
  .card-meta{display:flex;flex-direction:column;gap:3px}
  .card-meta-row{display:flex;align-items:center;gap:6px;font-size:12px;color:#64748b}
  .card-footer{display:flex;align-items:center;justify-content:space-between;margin-top:14px;padding-top:12px;border-top:1px solid #f1f5f9}
  .emp-badge{background:#dbeafe;border:1px solid #bfdbfe;border-radius:20px;padding:3px 11px;font-size:11px;color:#4f8ef7;font-weight:600}
  .config-chips{display:flex;gap:5px;flex-wrap:wrap;margin-top:10px}
  .chip{font-size:10px;padding:3px 8px;border-radius:10px;font-weight:600}
  .chip-green{background:#dcfce7;color:#16a34a;border:1px solid #bbf7d0}
  .chip-blue{background:#dbeafe;color:#4f8ef7;border:1px solid #bfdbfe}
  .chip-yellow{background:#fef3c7;color:#d97706;border:1px solid #fde68a}
  .chip-red{background:#fee2e2;color:#ef4444;border:1px solid #fecaca}
  .chip-orange{background:#ffedd5;color:#ea580c;border:1px solid #fed7aa}
  .chip-gray{background:#f1f5f9;color:#64748b;border:1px solid #e2e8f0}

  /* Buttons */
  .btn{display:inline-flex;align-items:center;gap:6px;border:none;border-radius:9px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s;white-space:nowrap}
  .btn-primary{background:linear-gradient(180deg,#ffffff 0%,#f1f5f9 50%,#e2e8f0 100%);color:#0f172a;font-weight:700;border:1.5px solid #cbd5e1;box-shadow:0 1px 3px rgba(0,0,0,0.06),inset 0 1px 0 rgba(255,255,255,0.9);}
  .btn-primary:hover{background:linear-gradient(180deg,#ffffff 0%,#e2e8f0 50%,#cbd5e1 100%);color:#020617;border-color:#94a3b8;transform:translateY(-1.5px);box-shadow:0 4px 12px rgba(15,23,42,0.08),inset 0 1px 0 rgba(255,255,255,1);}
  .btn-primary:active{background:#e2e8f0;transform:translateY(0);box-shadow:inset 0 2px 4px rgba(0,0,0,0.08);}
  .btn-outline{background:#ffffff;border:1.5px solid #cbd5e1;color:#1e293b;font-weight:600;transition:all 0.15s ease}
  .btn-outline:hover{border-color:#94a3b8;color:#0f172a;background:#f8fafc;transform:translateY(-1.5px);box-shadow:0 2px 8px rgba(0,0,0,0.05)}
  
  /* Form Inputs */
  .form-input, .form-select, .form-textarea {
    width: 100%;
    border: 1.5px solid #cbd5e1;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    background: #f8fafc;
    outline: none;
    transition: all 0.2s ease;
    font-family: 'Inter', sans-serif;
    color: #1e293b;
    box-sizing: border-box;
  }
  .form-input:hover, .form-select:hover, .form-textarea:hover {
    border-color: #94a3b8;
    background: #ffffff;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: #2563eb;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(37,99,235,0.15);
  }
  .form-label {
    font-size: 11px;
    font-weight: 800;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: block;
  }

  /* Dashboard Cards & Hover Effects */
  .dash-container { width: 100%; }
  .dash-card { background: #fff; border: 1.5px solid #cbd5e1; border-radius: 12px; padding: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
  .dash-card-title { font-size: 14px; font-weight: 800; color: #1e293b; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1.5px solid #f1f5f9; text-transform: uppercase; letter-spacing: 0.5px; }
  .dash-card-row { display: flex; justify-content: space-between; font-size: 13.5px; color: #475569; margin-bottom: 8px; }
  .dash-card-row:last-child { margin-bottom: 0; }
  .dash-card-row strong { color: #0f172a; font-size: 14.5px; }
  
  .dash-section-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .dash-section-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }

  .dash-stat-pill {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: default;
  }
  .dash-stat-pill:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.07);
    filter: brightness(0.97);
  }

  /* Executive Summary Dashboard Grid: 1st & 3rd compact, middle expanded */
  .dash-summary-grid {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(420px, 2.2fr) minmax(180px, 0.8fr);
    gap: 20px;
    align-items: stretch;
  }
  @media (max-width: 1100px) and (min-width: 769px) {
    .dash-summary-grid {
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .dash-summary-statutory {
      grid-column: 1 / -1;
      order: 1;
    }
    .dash-summary-financial {
      order: 2;
    }
    .dash-summary-lifecycle {
      order: 3;
    }
  }
  @media (max-width: 768px) {
    .dash-summary-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    .dash-summary-statutory {
      grid-column: auto;
    }
  }

  /* Exception & Compliance Accordions */
  .dash-accordion {
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .dash-accordion:hover {
    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }
  .dash-accordion-header {
    padding: 13px 20px;
    cursor: pointer;
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .dash-accordion-header:hover {
    padding-left: 24px;
  }
  .dash-accordion-header.lifecycle {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-left: 5px solid #64748b;
  }
  .dash-accordion-header.lifecycle:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-left: 8px solid #334155;
  }
  .dash-accordion-header.compliance-ok {
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
    border-left: 5px solid #10b981;
  }
  .dash-accordion-header.compliance-ok:hover {
    background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%);
    border-left: 8px solid #059669;
  }
  .dash-accordion-header.compliance-warn {
    background: linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%);
    border-left: 5px solid #ef4444;
  }
  .dash-accordion-header.compliance-warn:hover {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    border-left: 8px solid #dc2626;
  }
  .dash-accordion-header.eps-active {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border-left: 5px solid #f59e0b;
  }
  .dash-accordion-header.eps-active:hover {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-left: 8px solid #d97706;
  }
  .dash-accordion-header.eps-none {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-left: 5px solid #94a3b8;
  }
  .dash-accordion-header.eps-none:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-left: 8px solid #64748b;
  }
  .dash-accordion-header.pf-inelig {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-left: 5px solid #64748b;
  }
  .dash-accordion-header.pf-inelig:hover {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-left: 8px solid #2563eb;
  }
  .dash-accordion-header.esi-inelig {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-left: 5px solid #64748b;
  }
  .dash-accordion-header.esi-inelig:hover {
    background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
    border-left: 8px solid #7c3aed;
  }

  .dash-accordion-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 700;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: #475569;
    transition: all 0.2s ease;
  }
  .dash-accordion-header:hover .dash-accordion-btn {
    border-color: #94a3b8;
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  }
  .dash-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .dash-table th { text-align: left; padding: 10px; border-bottom: 1.5px solid #e2e8f0; color: #475569; font-weight: 700; background: #f8fafc; }
  .dash-table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
  .dash-table tr:last-child td { border-bottom: none; }
  .btn-ghost{background:#fff;border:1.5px solid #e2e8f0;color:#000;transition:all 0.2s ease}
  .btn-ghost:hover{border-color:#93c5fd;color:#1e293b;background:#eff6ff;transform:scale(1.05)}
  .btn-danger{background:#fff;border:1.5px solid #fecaca;color:#ef4444;transition:all 0.2s ease}
  .btn-danger:hover{background:#ef4444;color:#fff;border-color:#ef4444;transform:scale(1.05)}
  .btn-success{background:#f0fdf4;border:1.5px solid #bbf7d0;color:#16a34a}
  .btn-success:hover{background:#16a34a;color:#fff;border-color:#16a34a}
  .btn-sm{padding:7px 13px;font-size:12px}
  .btn-xs{padding:5px 10px;font-size:11px;border-radius:7px}

  /* Search */
  .search-wrap{position:relative;margin-bottom:20px}
  .search-icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#94a3b8;font-size:14px}
  .search-input{width:100%;background:#fff;border:1.5px solid #e2e8f0;border-radius:10px;padding:10px 14px 10px 36px;font-size:13px;color:#1e293b;outline:none;font-family:'Inter',sans-serif;transition:border-color .2s;box-shadow:0 1px 3px rgba(0,0,0,.04)}
  .search-input::placeholder{color:#94a3b8}
  .search-input:focus{border-color:#4f8ef7;box-shadow:0 0 0 3px rgba(79,142,247,.08)}

  /* Banner */
  .company-banner{background:#fff;border:1.5px solid #e2e8f0;border-radius:14px;padding:16px 20px;margin-bottom:20px;display:flex;align-items:center;gap:14px;flex-wrap:wrap;box-shadow:0 1px 4px rgba(0,0,0,.05)}
  .banner-icon{width:42px;height:42px;background:linear-gradient(135deg,#4f8ef7,#7c5cfc);border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
  .banner-info{flex:1;min-width:180px}
  .banner-name{font-family:'Inter',sans-serif;font-weight:800;font-size:17px;color:#1e293b;margin-bottom:3px}
  .banner-details{display:flex;gap:14px;flex-wrap:wrap}
  .banner-detail{font-size:12px;color:#64748b}

  /* Table */
  .table-wrap{background:#fff;border:1.5px solid #e2e8f0;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 4px rgba(0,0,0,.05)}
  .table-header{padding:12px 18px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px}
  .table-title{font-family:'Inter',sans-serif;font-weight:700;font-size:14px;color:#1e293b}
  table{width:100%;border-collapse:collapse}
  thead th{padding:12px 20px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#000;text-align:left;border-bottom:1px solid #f1f5f9;white-space:nowrap;background:#f8fafc}
  tbody td{padding:14px 20px;font-size:13px;color:#475569;border-bottom:1px solid #f8fafc;white-space:nowrap}
  tbody tr:last-child td{border-bottom:none}
  tbody tr:hover td{background:#f8fafc}
  tbody tr.emp-row td { transition: all 0.2s ease; border-top: 1px solid transparent; }
  tbody tr.emp-row td:first-child { border-left: 3px solid transparent; }
  tbody tr.emp-row:hover td { background: #f8fafc; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
  tbody tr.emp-row:hover td:first-child { border-left: 3px solid #4f8ef7; }
  .td-name{color:#1e293b!important;font-weight:600;cursor:pointer}
  .td-name:hover{color:#4f8ef7!important;text-decoration:underline}
  .td-pan{font-family:monospace;font-size:11px;color:#4f8ef7!important}
  .td-actions{display:flex;gap:4px;flex-wrap:wrap}
  .td-num{text-align:right;font-family:monospace;font-size:11px}
  .td-net{color:#16a34a!important;font-weight:700;font-family:monospace;text-align:right}
  .td-month{font-weight:600;color:#1e293b!important}
  .td-center{text-align:center}
  .tot-row td{background:#f1f5f9!important;font-weight:700;color:#1e293b!important;font-family:monospace;text-align:right;padding:10px 13px}

  .obscured {
    background: #1e293b !important;
    color: #1e293b !important;
    border-radius: 4px;
    user-select: none;
    display: inline-block;
  }

  /* Tabs */
  .tab-nav{display:flex;gap:3px;margin-bottom:18px;background:#f1f5f9;border:1.5px solid #e2e8f0;border-radius:12px;padding:4px;width:fit-content}
  .tab-btn{padding:7px 18px;border-radius:8px;border:none;background:transparent;color:#64748b;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
  .tab-btn.active{background:linear-gradient(135deg,#4f8ef7,#7c5cfc);color:#fff;box-shadow:0 2px 8px rgba(79,142,247,.3)}
  .tab-btn:not(.active):hover{color:#1e293b;background:#fff}

  /* Payroll month flow */
  .payroll-grid{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:24px}
  .month-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 6px 14px; cursor: pointer; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); user-select: none; }
  .month-card:hover { border-color: #93c5fd; background: #f8fafc; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(59,130,246,0.12); }
  .month-card.selected { border-color: #3b82f6; background: #eff6ff; box-shadow: 0 0 0 1.5px #3b82f6; }
  .month-card.selected:hover { box-shadow: 0 4px 12px rgba(59,130,246,0.2), 0 0 0 1.5px #2563eb; }
  .month-card-title { font-family: 'Inter', sans-serif; font-weight: 700; font-size: 13px; color: #1e293b; display: inline-block; }
  .month-card-sub { font-size: 11px; color: #64748b; font-weight: 500; display: inline-block; margin-left: 6px; }
  .status-badge{display:inline-block;padding:2px 9px;border-radius:10px;font-size:10px;font-weight:700}
  .status-draft{background:#f1f5f9;color:#64748b;border:1px solid #e2e8f0}
  .status-done{background:#dcfce7;color:#16a34a;border:1px solid #bbf7d0}

  /* Attendance stepper */
  .att-ctrl{display:inline-flex;align-items:center;border:1.5px solid #e2e8f0;border-radius:8px;overflow:hidden;background:#fff}
  .att-ctrl:focus-within{border-color:#4f8ef7;box-shadow:0 0 0 2px rgba(79,142,247,.12)}
  .att-step{width:28px;height:32px;background:#f1f5f9;border:none;color:#64748b;font-size:18px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;padding:0;user-select:none}
  .att-step:hover{background:#e2e8f0;color:#1e293b}
  .att-step:active{background:#4f8ef7;color:#fff}
  .att-input{width:38px;background:#fff;border:none;border-left:1px solid #e2e8f0;border-right:1px solid #e2e8f0;padding:5px 2px;font-size:13px;color:#1e293b;outline:none;text-align:center;font-family:monospace;font-weight:600;-moz-appearance:textfield}
  .att-input::-webkit-inner-spin-button,.att-input::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
  .att-ctrl.has-val .att-input{color:#ea580c}
  .att-ctrl.has-val{border-color:#fed7aa}
  .att-ctrl.ot-val .att-input{color:#16a34a}
  .att-ctrl.ot-val{border-color:#bbf7d0}

  /* Summary cards */
  .sum-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin-bottom:18px}
  .sum-card{background:#fff;border:1.5px solid #e2e8f0;border-radius:10px;padding:12px 14px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
  .sum-label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
  .sum-val{font-family:'Inter',sans-serif;font-weight:700;font-size:15px;color:#1e293b}
  .sum-val.green{color:#16a34a}.sum-val.blue{color:#4f8ef7}.sum-val.yellow{color:#d97706}

  /* Config form */
  .config-section{margin-bottom:20px}
  .config-section-title{font-size:11px;font-weight:700;color:#4f8ef7;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px;padding-bottom:6px;border-bottom:1px solid #e2e8f0}
  .config-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px}
  .config-field{display:flex;flex-direction:column;gap:5px;padding:8px;margin:-8px;border-radius:8px;transition:all 0.2s ease}
  .config-field:hover{background:#f8fafc;box-shadow:0 2px 5px rgba(0,0,0,0.04);transform:translateY(-1px)}
  .config-label{font-size:11px;font-weight:600;color:#000;text-transform:uppercase;letter-spacing:.5px}
  .config-input{background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:8px;padding:8px 10px;font-size:13px;color:#000;outline:none;font-family:'Inter',sans-serif;transition:border-color .2s;width:100%}
  .config-input:focus{border-color:#4f8ef7}
  .config-input::placeholder{color:#cbd5e1;font-weight:400}
  .toggle-row{display:flex;align-items:center;justify-content:space-between;padding:6px 12px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;transition:all 0.2s ease}
  .toggle-row:hover{background:#f8fafc;border-color:#cbd5e1;box-shadow:0 1px 3px rgba(0,0,0,0.03);transform:translateY(-1px)}
  .toggle-label{font-size:13px;color:#000;font-weight:500}
  .toggle{position:relative;width:40px;height:22px;cursor:pointer}
  .toggle input{opacity:0;width:0;height:0}
  .toggle-slider{position:absolute;inset:0;background:#e2e8f0;border-radius:22px;transition:.2s}
  .toggle-slider::before{content:'';position:absolute;height:16px;width:16px;left:3px;bottom:3px;background:#fff;border-radius:50%;transition:.2s;box-shadow:0 1px 3px rgba(0,0,0,.2)}
  .toggle input:checked+.toggle-slider{background:linear-gradient(135deg,#4f8ef7,#7c5cfc)}
  .toggle input:checked+.toggle-slider::before{transform:translateX(18px)}

  /* Employee banner */
  .emp-banner{background:#fff;border:1.5px solid #e2e8f0;border-radius:14px;padding:16px 20px;margin-bottom:18px;display:flex;align-items:center;gap:14px;box-shadow:0 1px 4px rgba(0,0,0,.05)}
  .emp-avatar{width:46px;height:46px;background:linear-gradient(135deg,#7c5cfc,#4f8ef7);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Inter',sans-serif;font-weight:800;font-size:17px;color:#fff;flex-shrink:0}
  .emp-info-name{font-family:'Inter',sans-serif;font-weight:800;font-size:17px;color:#1e293b}
  .emp-info-details{display:flex;gap:16px;margin-top:3px;flex-wrap:wrap}
  .emp-info-detail{font-size:12px;color:#000}
  .emp-info-detail strong{color:#475569}

  /* Sal summary */
  .sal-summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:9px;margin-bottom:18px}
  .sal-card{background:#fff;border:1.5px solid #e2e8f0;border-radius:10px;padding:11px 13px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
  .sal-card-label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px}
  .sal-card-val{font-family:'Inter',sans-serif;font-weight:700;font-size:15px;color:#1e293b}
  .sal-card-val.green{color:#16a34a}.sal-card-val.blue{color:#4f8ef7}

  /* Info card */
  .info-card{background:#fff;border:1.5px solid #e2e8f0;border-radius:14px;padding:18px 20px;margin-bottom:12px;box-shadow:0 1px 4px rgba(0,0,0,.05)}
  .info-card-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
  .info-card-title{font-family:'Inter',sans-serif;font-weight:700;font-size:14px;color:#1e293b}
  .info-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px}
  .info-field{background:#f8fafc;border:1.5px solid #f1f5f9;border-radius:10px;padding:11px 13px}
  .info-label{font-size:10px;font-weight:700;color:#000;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px}
  .info-val{font-size:13px;color:#1e293b;font-weight:600}
  .mono{font-family:monospace;letter-spacing:.5px;color:#4f8ef7!important}
  .na{color:#cbd5e1;font-weight:400;font-style:italic}

  /* Member chips */
  .member-chip{display:flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:20px;padding:4px 11px 4px 4px}
  .member-avatar{width:24px;height:24px;background:linear-gradient(135deg,#7c5cfc,#4f8ef7);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;color:#fff;flex-shrink:0}
  .member-name{font-size:12px;color:#1e293b;font-weight:600}
  .member-rel{font-size:10px;color:#64748b}

  /* Document upload */
  .doc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:14px}
  .doc-slot{background:#f8fafc;border:1.5px dashed #cbd5e1;border-radius:12px;padding:14px;display:flex;flex-direction:column;align-items:center;gap:10px;transition:border-color .2s;position:relative}
  .doc-slot:hover{border-color:#4f8ef7}
  .doc-slot.uploaded{border-style:solid;border-color:#bbf7d0}
  .doc-label{font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;text-align:center}
  .doc-preview{width:100%;height:100px;object-fit:cover;border-radius:8px;cursor:pointer}
  .doc-preview-icon{font-size:36px;opacity:.3}
  .doc-actions{display:flex;gap:6px;width:100%}
  .doc-upload-btn{flex:1;background:#fff;border:1.5px solid #e2e8f0;border-radius:8px;padding:6px;font-size:11px;color:#64748b;cursor:pointer;text-align:center;transition:all .2s}
  .doc-upload-btn:hover{border-color:#4f8ef7;color:#4f8ef7}
  .doc-upload-btn.remove{color:#ef4444;border-color:#fecaca}
  .doc-upload-btn.remove:hover{background:#ef4444;color:#fff}
  .doc-uploading{font-size:11px;color:#4f8ef7;animation:pulse 1s infinite}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .doc-section-title{font-size:11px;font-weight:700;color:#4f8ef7;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;padding-bottom:8px;border-bottom:1px solid #e2e8f0}
  .status-dot{display:inline-block;width:7px;height:7px;border-radius:50%;margin-left:7px;vertical-align:middle;flex-shrink:0}
  .status-dot.red{background:#ef4444;box-shadow:0 0 4px rgba(239,68,68,.4)}
  .status-dot.green{background:#16a34a;box-shadow:0 0 4px rgba(22,163,74,.4)}
  .tab-btn.incomplete::after{content:"";display:inline-block;width:6px;height:6px;border-radius:50%;background:#d97706;margin-left:6px;vertical-align:middle;flex-shrink:0}

  /* Overlay & Modal */
  .overlay{position:fixed;inset:0;background:rgba(15,23,42,.5);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:100;padding:16px;animation:fadeIn .15s ease}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  .modal{background:#fff;border:1.5px solid #e2e8f0;border-radius:16px;padding:26px;width:100%;max-width:520px;max-height:92vh;overflow-y:auto;animation:slideUp .2s ease;box-shadow:0 20px 60px rgba(0,0,0,.15)}
  .modal-lg{max-width:900px}
  @keyframes slideUp{from{transform:translateY(10px);opacity:0}to{transform:translateY(0);opacity:1}}
  .modal-title{font-family:'Inter',sans-serif;font-weight:800;font-size:17px;color:#1e293b;margin-bottom:3px}
  .modal-sub{font-size:12px;color:#000;margin-bottom:18px}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .field{display:flex;flex-direction:column;gap:5px}
  .field.full{grid-column:1/-1}
  .field label{font-size:11px;font-weight:600;color:#000;text-transform:uppercase;letter-spacing:.5px}
  .field input,.field select{background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:8px;padding:8px 10px;font-size:13px;color:#1e293b;outline:none;font-family:'Inter',sans-serif;transition:border-color .2s}
  .field input:focus,.field select:focus{border-color:#4f8ef7;background:#fff;box-shadow:0 0 0 3px rgba(79,142,247,.08)}
  .field input.error{border-color:#ef4444}
  .field .hint{font-size:11px;color:#ef4444}
  .field .ok{font-size:11px;color:#16a34a}
  .field input[readonly]{color:#16a34a;font-weight:600;background:#f0fdf4;border-color:#bbf7d0}
  .divider{grid-column:1/-1;border:none;border-top:1px solid #f1f5f9;margin:4px 0}
  .section-label{grid-column:1/-1;font-size:11px;font-weight:700;color:#4f8ef7;text-transform:uppercase;letter-spacing:1px;margin-top:4px}
  .modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:18px}
  .err-msg{background:#fee2e2;border:1px solid #fecaca;border-radius:8px;padding:9px 13px;font-size:12px;color:#dc2626;margin-bottom:13px}
  .success-msg{background:#dcfce7;border:1px solid #bbf7d0;border-radius:8px;padding:9px 13px;font-size:12px;color:#16a34a;margin-bottom:13px}

  /* Confirm */
  .confirm-modal{max-width:370px;text-align:center}
  .confirm-icon{font-size:38px;margin-bottom:10px}
  .confirm-title{font-family:'Inter',sans-serif;font-weight:800;font-size:17px;color:#1e293b;margin-bottom:5px}
  .confirm-text{font-size:13px;color:#64748b;margin-bottom:20px;line-height:1.5}
  .confirm-btns{display:flex;gap:10px;justify-content:center}

  /* Upload area */
  .upload-area{border:2px dashed #cbd5e1;border-radius:12px;padding:28px;text-align:center;cursor:pointer;transition:all .2s;margin-bottom:14px;background:#f8fafc}
  .upload-area:hover{border-color:#4f8ef7;background:#eff6ff}
  .upload-icon{font-size:32px;margin-bottom:8px}
  .upload-text{font-size:13px;color:#000}
  
  /* Custom Tooltips */
  .custom-tooltip-container { position: relative; display: inline-flex; align-items: center; cursor: help; }
  .custom-tooltip { visibility: hidden; opacity: 0; position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%); background-color: #1e293b; color: #fff; text-align: center; padding: 10px 14px; border-radius: 8px; z-index: 1000; width: max-content; max-width: 260px; font-size: 12px; font-weight: 500; line-height: 1.5; box-shadow: 0 4px 15px rgba(0,0,0,0.15); transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); pointer-events: none; text-transform: none; letter-spacing: normal; white-space: pre-wrap; }
  .custom-tooltip::after { content: ""; position: absolute; top: 100%; left: 50%; margin-left: -5px; border-width: 5px; border-style: solid; border-color: #1e293b transparent transparent transparent; }
  .custom-tooltip-container:hover .custom-tooltip { visibility: visible; opacity: 1; bottom: calc(100% + 12px); }
  .upload-text strong{color:#4f8ef7}

  /* Back btn */
  .back-btn{display:flex;align-items:center;gap:6px;font-size:13px;color:#4f8ef7;cursor:pointer;margin-bottom:20px;width:fit-content}
  .back-btn:hover{color:#7c5cfc}

  .empty{text-align:center;padding:44px 20px}
  .empty-icon{font-size:40px;margin-bottom:12px;opacity:.4}
  .empty-title{font-family:'Inter',sans-serif;font-weight:700;font-size:16px;color:#94a3b8;margin-bottom:5px}
  .empty-sub{font-size:13px;color:#cbd5e1}

  @media(max-width:640px){
    .form-grid{grid-template-columns:1fr}
    .company-grid{grid-template-columns:1fr}
    .main{padding:18px 14px}
    .page-title{font-size:20px}
    .modal-lg{max-width:100%}
  }

  /* Joining Kit Mobile & Checklist Enhancements */
  .jk-checklist-hero {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    padding: 18px 22px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  }
  .jk-checklist-card {
    background: #ffffff;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    padding: 15px 18px;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
    transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    cursor: default;
  }
  .jk-checklist-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 6px 18px -2px rgba(37, 99, 235, 0.12), 0 2px 6px -1px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
    background: #ffffff;
  }
  .jk-checklist-card:hover .jk-checklist-icon {
    background: #eff6ff;
    border-color: #bfdbfe;
    transform: scale(1.05);
  }
  .jk-checklist-icon {
    font-size: 21px;
    width: 42px;
    height: 42px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }
  .jk-checklist-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
  }
  @media (max-width: 1024px) {
    .jk-checklist-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
  }
  .jk-tabs-scroll::-webkit-scrollbar {
    height: 4px;
  }
  .jk-tabs-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  .jk-tab-text-full {
    display: inline;
  }
  .jk-tab-text-short {
    display: none;
  }
  .jk-content-body {
    padding: 24px;
    min-height: 300px;
  }
  .jk-footer {
    margin-top: auto;
    padding: 16px 24px;
    border-top: 1.5px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    background: #fafafa;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
            @media (max-width: 640px) {
              .employee-salary-summary-cards { gap: 8px !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
              .employee-salary-summary-card { padding: 10px 11px !important; border-radius: 9px !important; }
              .employee-salary-summary-card > div:first-child { font-size: 9px !important; letter-spacing: .2px !important; line-height: 1.35 !important; }
              .employee-salary-summary-card > div:nth-child(2) { font-size: 16px !important; line-height: 1.25 !important; overflow-wrap: anywhere; }
            }
  .mobile-submit-label { display: none; }
  @media (max-width: 640px) {
    .jk-content-body {
      padding: 14px 12px !important;
    }
    .jk-footer {
      padding: 12px 14px !important;
      gap: 10px !important;
    }
    .employee-profile-update-footer {
      flex-wrap: nowrap !important;
      justify-content: space-between !important;
      gap: 6px !important;
      padding: 9px 8px !important;
    }
    .employee-profile-update-footer > div {
      min-width: 0;
      gap: 5px !important;
    }
    .employee-profile-update-footer > div:first-child > button {
      width: 38px;
      height: 38px;
      justify-content: center;
      padding: 0 !important;
      flex-shrink: 0;
      font-size: 16px !important;
    }
    .employee-profile-update-footer .employee-draft-saved { display: none !important; }
    .employee-profile-update-footer .desktop-submit-label { display: none !important; }
    .employee-profile-update-footer .mobile-submit-label { display: inline !important; }
    .employee-profile-update-footer .mobile-control-label { display: none !important; }
    .employee-profile-update-footer .mobile-control-icon { display: inline !important; }
    .employee-profile-update-footer > div:last-child > button {
      min-width: 36px;
      min-height: 38px;
      justify-content: center;
      padding: 7px 10px !important;
      white-space: nowrap;
      flex-shrink: 1;
    }
    .employee-profile-update-footer .employee-profile-submit {
      font-size: 11px !important;
      padding: 8px 9px !important;
    }
    .jk-checklist-hero {
      padding: 12px 14px !important;
    }
    .jk-checklist-hero .jk-hero-title {
      font-size: 15px !important;
    }
    .jk-checklist-hero .jk-hero-desc {
      font-size: 12px !important;
      line-height: 1.45 !important;
    }
    .jk-checklist-grid {
      grid-template-columns: 1fr !important;
      gap: 8px !important;
    }
    .jk-checklist-card {
      padding: 10px 12px !important;
      gap: 10px !important;
    }
    .jk-checklist-card .jk-item-title {
      font-size: 13px !important;
    }
    .jk-checklist-card .jk-item-desc {
      font-size: 11.5px !important;
      line-height: 1.4 !important;
    }
    .jk-checklist-icon {
      width: 32px !important;
      height: 32px !important;
      font-size: 18px !important;
    }
    .jk-action-box {
      padding: 12px 14px !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 10px !important;
    }
    .jk-action-box button {
      width: 100% !important;
      justify-content: center !important;
    }
    .jk-tab-text-full {
      display: none !important;
    }
    .jk-tab-text-short {
      display: inline !important;
    }
  }

  /* Company Dashboard Layout */
  .company-layout-wrapper { display: flex; min-height: 100vh; background: #f8fafc; }
  .company-sidebar { width: 225px; background: #fff; border-right: 1px solid #e2e8f0; display: flex; flex-direction: column; flex-shrink: 0; }
  .company-sidebar-header { padding: 24px 20px; border-bottom: 1px solid #e2e8f0; }
  .company-sidebar-title { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 800; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .company-sidebar-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
  .company-nav { display: flex; flex-direction: column; gap: 4px; padding: 20px 12px; flex: 1; }
  .company-nav-item { display: flex; align-items: center; gap: 12px; padding: 10px 14px; border-radius: 8px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: #334155; cursor: pointer; transition: color 0.3s ease; border: none; background: transparent; text-align: left; position: relative; overflow: hidden; z-index: 1; }
  .company-nav-item::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: #e2e8f0; transform: translateX(-101%); transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); z-index: -1; border-radius: 8px; }
  .company-nav-item:hover { color: #0f172a; }
  .company-nav-item:hover::before { transform: translateX(0); }
  .company-nav-item.active { background: #eff6ff; color: #2563eb; }
  .company-nav-item.active::before { display: none; }
  
  /* Admin Sidebar Styles */
  .admin-sidebar { background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); }
  .admin-nav-item.active { background: #eff6ff; color: #1d4ed8; font-weight: 700; box-shadow: 0 1px 3px rgba(37, 99, 235, 0.1); }
  
  /* People Suite Expandable Navigation Styles */
  .people-subnav-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-left: 14px;
    padding-left: 8px;
    border-left: 2px solid #cbd5e1;
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease, margin 0.3s ease;
  }
  .people-subnav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    border: none;
    background: transparent;
    text-align: left;
    position: relative;
    overflow: hidden;
    z-index: 1;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease, color 0.2s ease;
    transform: translateX(-10px);
    opacity: 0;
  }
  .people-subnav-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #e2e8f0;
    transform: translateX(-101%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: -1;
    border-radius: 8px;
  }
  .people-subnav-wrapper.expanded .people-subnav-item {
    transform: translateX(0);
    opacity: 1;
  }
  .people-subnav-wrapper.expanded .people-subnav-item:nth-child(1) { transition-delay: 0.04s; }
  .people-subnav-wrapper.expanded .people-subnav-item:nth-child(2) { transition-delay: 0.09s; }
  .people-subnav-wrapper.expanded .people-subnav-item:nth-child(3) { transition-delay: 0.14s; }
  .people-subnav-wrapper.expanded .people-subnav-item:nth-child(4) { transition-delay: 0.19s; }
  .people-subnav-wrapper.expanded .people-subnav-item:nth-child(5) { transition-delay: 0.24s; }

  .people-subnav-item:hover {
    color: #0f172a;
  }
  .people-subnav-item:hover::before {
    transform: translateX(0);
  }
  .people-subnav-item.active {
    background: #eff6ff;
    color: #1d4ed8;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.1);
  }
  .people-subnav-item.active::before {
    display: none;
  }
  
  /* Employee Sidebar Styles */
  .employee-sidebar { background: linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%); }
  .employee-nav-item.active { background: #ccfbf1; color: #0f766e; font-weight: 700; box-shadow: 0 1px 3px rgba(15, 118, 110, 0.1); }
  .company-nav-icon { font-size: 18px; display: flex; align-items: center; justify-content: center; width: 24px; }
  .company-main-content { flex: 1; display: flex; flex-direction: column; overflow: hidden; height: 100vh; }
  .company-main-scroll { flex: 1; overflow-y: auto; padding: 32px 40px; }
  .company-header-bar { display: flex; justify-content: flex-end; padding: 16px 24px; background: #fff; border-bottom: 1px solid #e2e8f0; }
  
  /* Both company header cells share one row, including when details wrap. */
  .company-admin-layout { display: grid; grid-template-rows: auto minmax(0, 1fr); height: 100vh; transition: grid-template-columns 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
  .company-admin-layout > .company-sidebar-header { grid-column: 1; grid-row: 1; display: flex; align-items: center; min-width: 0; padding: 16px 20px; background: #fff; border-right: 1px solid #e2e8f0; overflow: hidden; }
  .company-admin-layout > .company-header-bar { grid-column: 2; grid-row: 1; align-items: center; min-width: 0; }
  .company-admin-layout > .admin-sidebar { grid-column: 1; grid-row: 2; min-height: 0; overflow-y: auto !important; }
  .company-admin-layout > .company-main-content { grid-column: 2; grid-row: 2; height: auto; min-height: 0; min-width: 0; }
  .admin-sidebar .company-nav-item { white-space: nowrap; font-size: 13px; gap: 8px; padding: 10px; }
  .admin-sidebar .company-nav-icon { flex-shrink: 0; }
  .company-nav-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .dash-drilldown { cursor: pointer; }
  .dash-drilldown:hover { box-shadow: 0 0 0 2px #94a3b8; }
  .dash-drilldown:focus-visible, .dash-employee-link:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
  .dash-employee-link { background: transparent; border: 0; padding: 0; font: inherit; font-weight: 600; color: #2563eb; text-align: left; text-decoration: underline; cursor: pointer; }

  /* Company Profile View */
  .profile-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
  .profile-header { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 800; color: #0f172a; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9; }

  /* Company Profile Accordion Headers */
  .profile-accordion-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .profile-accordion-card:hover {
    box-shadow: 0 6px 18px rgba(0,0,0,0.08);
    border-color: #cbd5e1;
  }
  .profile-accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 22px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .profile-accordion-header:hover {
    padding-left: 26px;
  }
  .profile-accordion-header.blue {
    background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
    border-left: 5px solid #2563eb;
  }
  .profile-accordion-header.blue:hover {
    background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
    border-left: 8px solid #1d4ed8;
  }
  .profile-accordion-header.purple {
    background: linear-gradient(135deg, #faf5ff 0%, #f8fafc 100%);
    border-left: 5px solid #7c3aed;
  }
  .profile-accordion-header.purple:hover {
    background: linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%);
    border-left: 8px solid #6d28d9;
  }
  .profile-accordion-header.amber {
    background: linear-gradient(135deg, #fffbeb 0%, #f8fafc 100%);
    border-left: 5px solid #d97706;
  }
  .profile-accordion-header.amber:hover {
    background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
    border-left: 8px solid #b45309;
  }
  .profile-accordion-header.emerald {
    background: linear-gradient(135deg, #ecfdf5 0%, #f8fafc 100%);
    border-left: 5px solid #059669;
  }
  .profile-accordion-header.emerald:hover {
    background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%);
    border-left: 8px solid #047857;
  }
  .profile-accordion-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }
  .profile-accordion-btn.blue {
    background: #dbeafe;
    color: #1d4ed8;
    border-color: #bfdbfe;
  }
  .profile-accordion-header.blue:hover .profile-accordion-btn.blue {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
  }
  .profile-accordion-btn.purple {
    background: #f3e8ff;
    color: #6d28d9;
    border-color: #e9d5ff;
  }
  .profile-accordion-header.purple:hover .profile-accordion-btn.purple {
    background: #7c3aed;
    color: #ffffff;
    border-color: #7c3aed;
  }
  .profile-accordion-btn.amber {
    background: #fef3c7;
    color: #b45309;
    border-color: #fde68a;
  }
  .profile-accordion-header.amber:hover .profile-accordion-btn.amber {
    background: #d97706;
    color: #ffffff;
    border-color: #d97706;
  }
  .profile-accordion-btn.emerald {
    background: #d1fae5;
    color: #047857;
    border-color: #a7f3d0;
  }
  .profile-accordion-header.emerald:hover .profile-accordion-btn.emerald {
    background: #059669;
    color: #ffffff;
    border-color: #059669;
  }
  .profile-accordion-header.indigo {
    background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%);
    border-left: 5px solid #4f46e5;
  }
  .profile-accordion-header.indigo:hover {
    background: linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%);
    border-left: 8px solid #4338ca;
  }
  .profile-accordion-btn.indigo {
    background: #e0e7ff;
    color: #4338ca;
    border-color: #c7d2fe;
  }
  .profile-accordion-header.indigo:hover .profile-accordion-btn.indigo {
    background: #4f46e5;
    color: #ffffff;
    border-color: #4f46e5;
  }
  .profile-accordion-header.slate {
    background: linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%);
    border-left: 5px solid #475569;
  }
  .profile-accordion-header.slate:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%);
    border-left: 8px solid #334155;
  }
  .profile-accordion-btn.slate {
    background: #e2e8f0;
    color: #334155;
    border-color: #cbd5e1;
  }
  .profile-accordion-header.slate:hover .profile-accordion-btn.slate {
    background: #475569;
    color: #ffffff;
    border-color: #475569;
  }
  .profile-accordion-tag {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Leave Types Quotas cards */
  .leave-type-card {
    background: #ffffff;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    cursor: default;
    gap: 12px;
  }
  .leave-type-card:hover {
    transform: translateY(-3px);
    border-color: #94a3b8;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    box-shadow: 0 6px 18px -3px rgba(0, 0, 0, 0.08), 0 2px 5px -1px rgba(0, 0, 0, 0.03);
  }
  .leave-type-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    transition: color 0.2s ease;
  }
  .leave-type-card:hover .leave-type-name {
    color: #0f172a;
  }
  .leave-type-quota-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    padding: 3px 10px;
    border-radius: 12px;
    transition: all 0.2s ease;
    margin-top: 4px;
  }
  .leave-type-card:hover .leave-type-quota-badge {
    background: #e2e8f0;
    color: #1e293b;
    border-color: #cbd5e1;
  }
  .leave-type-delete-btn {
    border-radius: 8px !important;
    padding: 5px 8px !important;
    transition: all 0.2s ease !important;
    opacity: 0.65;
  }
  .leave-type-card:hover .leave-type-delete-btn {
    opacity: 1;
  }
  .leave-type-delete-btn:hover {
    background: #fee2e2 !important;
    color: #dc2626 !important;
    transform: scale(1.1);
  }
  .add-leave-type-btn {
    transition: all 0.2s ease !important;
  }
  .add-leave-type-btn:hover {
    background: #f1f5f9 !important;
    color: #1e293b !important;
    border-color: #cbd5e1 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }

  /* Company Holidays Cards & Interactive Elements */
  .company-holiday-card {
    background: #ffffff;
    padding: 14px 16px;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    user-select: none;
  }
  .company-holiday-card:hover {
    transform: translateY(-3px);
    border-color: #94a3b8;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    box-shadow: 0 8px 22px -3px rgba(0, 0, 0, 0.08), 0 3px 6px -1px rgba(0, 0, 0, 0.03);
  }
  .company-holiday-card .holiday-title {
    font-size: 14px;
    font-weight: 700;
    color: #475569;
    transition: color 0.2s ease;
  }
  .company-holiday-card:hover .holiday-title {
    color: #0f172a;
  }
  .company-holiday-card.active {
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border: 1.5px solid #94a3b8;
    box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  }
  .company-holiday-card.active .holiday-title {
    color: #0f172a;
  }
  .company-holiday-card.active:hover {
    transform: translateY(-4px);
    border-color: #64748b;
    background: linear-gradient(135deg, #f1f5f9 0%, #ffffff 100%);
    box-shadow: 0 10px 22px -2px rgba(0, 0, 0, 0.1), 0 4px 8px -1px rgba(0, 0, 0, 0.04);
  }
  .holiday-cal-badge {
    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .company-holiday-card:hover .holiday-cal-badge {
    transform: scale(1.08) rotate(-2deg);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);
  }
  .holiday-mandatory-badge {
    transition: all 0.2s ease;
  }
  .company-holiday-card:hover .holiday-mandatory-badge {
    transform: scale(1.05);
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.25);
  }
  .holiday-day-pill {
    transition: all 0.2s ease;
  }
  .company-holiday-card:hover .holiday-day-pill {
    background: #2563eb !important;
    color: #ffffff !important;
    border-color: #2563eb !important;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
  }
  .holiday-date-input {
    transition: all 0.2s ease;
  }
  .holiday-date-input:hover {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    background: #ffffff;
  }
  .holiday-date-input:focus {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
  }
  .holiday-count-badge {
    transition: all 0.2s ease;
    cursor: default;
  }
  .holiday-count-badge:hover {
    transform: translateY(-1px);
    background: #d1fae5 !important;
    border-color: #6ee7b7 !important;
  }
  .holiday-year-badge {
    transition: all 0.2s ease;
    cursor: default;
  }
  .holiday-year-badge:hover {
    transform: translateY(-1px);
    background: #dbeafe !important;
    border-color: #93c5fd !important;
  }
  .working-day-chip {
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .working-day-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.07);
    border-color: #10b981 !important;
  }

  /* Loading Spinner */
  .modern-spinner { width: 64px; height: 64px; border: 5px solid #e2e8f0; border-top: 5px solid #2563eb; border-radius: 50%; animation: spin 1s cubic-bezier(0.5, 0.1, 0.4, 0.9) infinite; margin-bottom: 28px; box-shadow: 0 8px 16px rgba(37,99,235,0.1); }
  /* Mobile Employee Navigation */
  .mobile-only-nav { display: none; }
  .mobile-only-logout { display: none; }
  @media (max-width: 768px) {
    .employee-sidebar { position: fixed !important; bottom: 0; left: 0; width: 100% !important; height: auto !important; padding-bottom: env(safe-area-inset-bottom, 0px); flex-direction: row; border-right: none; border-top: 1px solid #e2e8f0; z-index: 1000; box-shadow: 0 -4px 12px rgba(0,0,0,0.05); }
    .employee-sidebar .company-sidebar-header { display: none !important; }
    .employee-sidebar .company-nav { opacity: 1 !important; pointer-events: auto !important; flex-direction: row; padding: 0 10px; justify-content: flex-start; align-items: center; background: #fff; margin: 0; height: 65px; overflow-x: auto; gap: 4px; }
    .employee-sidebar .company-nav-item { flex-direction: column; gap: 4px; padding: 8px 12px; font-size: 10px; justify-content: center; border-radius: 8px; text-align: center; flex: 0 0 auto; border-top: none; }
    .employee-sidebar .company-nav-item.active { background: #eff6ff; color: #2563eb; }
    .employee-sidebar .company-nav-icon { font-size: 22px; width: auto; margin-bottom: 2px; }
    .employee-sidebar .employee-sidebar-bottom { display: none !important; }
    .employee-sidebar-toggle-float { display: none !important; }
    .company-main-content.employee-main { height: 100vh !important; height: 100dvh !important; min-width: 0; box-sizing: border-box; padding-bottom: calc(65px + env(safe-area-inset-bottom, 0px)); }
    .company-main-scroll.employee-scroll { padding: 16px; }
    .employee-leave-dialog .config-field, .employee-leave-dialog .config-input { min-width: 0; }
    .employee-leave-dialog .config-input { font-size: 16px; min-height: 44px; }
    .employee-scroll input:not([type="checkbox"]):not([type="radio"]), .employee-scroll select, .employee-scroll textarea { font-size: 16px !important; }
    .employee-main .mobile-only-logout { min-width: 44px !important; min-height: 44px; }
    .employee-main > .company-header-bar > :first-child { min-width: 0; }
    .login-page { min-height: 100dvh; padding: max(16px, env(safe-area-inset-top, 0px)) max(16px, env(safe-area-inset-right, 0px)) max(16px, env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px)); }
    .login-card { padding: 28px 24px; }
    .login-field input { font-size: 16px; }

    .mobile-only-nav { display: flex !important; }
    .desktop-only-nav { display: none !important; }
    .mobile-only-logout { display: flex !important; }
    .company-header-bar { padding: 12px 16px; }
  }

  /* ── Org Tree & HRMS Culture Styles ────────────────────────── */
  .org-chart-wrap {
    overflow-x: auto;
    overflow-y: visible;
    padding: 24px 16px;
    display: flex;
    justify-content: center;
    min-height: 480px;
    background: radial-gradient(circle, #e2e8f0 10%, transparent 11%);
    background-size: 20px 20px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }
  .org-search-panel, .org-focus-panel { background:linear-gradient(135deg,#fff,#f8fafc); border:1px solid #e2e8f0; border-radius:16px; padding:22px; box-shadow:0 4px 18px rgba(15,23,42,.04); }
  .org-section-eyebrow { font-size:11px; font-weight:800; letter-spacing:1px; text-transform:uppercase; color:#0f766e; }
  .org-search-results { display:grid; grid-template-columns:repeat(auto-fit,minmax(min(100%,260px),1fr)); gap:10px; max-height:420px; overflow:auto; }
  .org-person-result { display:flex; align-items:center; gap:12px; padding:14px; border:1px solid #e2e8f0; background:rgba(255,255,255,.9); border-radius:12px; text-align:left; cursor:pointer; font-family:inherit; transition:background .15s,border-color .15s; }
  .org-person-result:hover { background:#f0fdfa; border-color:#99f6e4; }
  .org-person-result:focus-visible, .org-chain-person:focus-visible { outline:2px solid #0f766e; outline-offset:3px; }
  .org-chain-person { font-family:inherit; font-size:12px; font-weight:700; padding:7px 11px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; color:#475569; cursor:pointer; }
  .org-chain-person.selected { color:#0f766e; background:#f0fdfa; border-color:#99f6e4; cursor:default; }
  @media(max-width:640px) { .org-dir-row { flex-wrap:wrap; gap:12px; } .org-search-panel,.org-focus-panel { padding:16px; } }
  .org-manager-control { display:flex; align-items:center; gap:8px; width:100%; min-width:0; margin-top:10px; padding:7px 9px; border:1px solid #dbe5ed; border-radius:8px; background:#f8fafc; color:#334155; text-align:left; font-family:inherit; cursor:pointer; transition:background .15s,border-color .15s; }
  .org-manager-control:hover { background:#f0fdfa; border-color:#99d6cd; }
  .org-manager-control:focus-visible { outline:2px solid #0f766e; outline-offset:3px; }
  .org-manager-label { flex-shrink:0; font-size:10px; font-weight:600; color:#64748b; }
  .org-manager-name { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:11.5px; font-weight:700; }
  .org-manager-edit { flex-shrink:0; margin-left:auto; color:#64748b; }
  .org-manager-control.compact { display:inline-flex; width:auto; max-width:100%; margin-top:0; padding:4px 8px; }
  .org-node-card {
    background: #fff;
    border-radius: 12px;
    border: 1.5px solid #cbd5e1;
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    padding: 14px 16px;
    width: 252px;
    text-align: left;
    position: relative;
    transition: all 0.2s ease;
    user-select: none;
  }
  .org-node-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    border-color: #3b82f6;
  }
  .org-dir-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    margin-bottom: 6px;
    transition: all 0.15s ease;
  }
  .org-dir-row:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  .celebration-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 11.5px;
    font-weight: 700;
  }
  .hike-timeline-item {
    position: relative;
    padding-left: 28px;
    padding-bottom: 24px;
    border-left: 2px dashed #cbd5e1;
  }
  .hike-timeline-item:last-child {
    border-left: 2px solid transparent;
    padding-bottom: 0;
  }
  .hike-timeline-dot {
    position: absolute;
    left: -8px;
    top: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #3b82f6;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px #93c5fd;
  }

  /* Recruitment Kanban & Offboarding styles */
  .kanban-board {
    display: flex;
    gap: 18px;
    overflow-x: auto;
    padding-bottom: 24px;
    align-items: flex-start;
  }
  .kanban-col {
    flex: 0 0 310px;
    min-width: 310px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: calc(100vh - 270px);
    overflow-y: auto;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  }
  .kanban-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 0.2s ease;
  }
  .kanban-card:hover {
    border-color: #93c5fd;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(37,99,235,0.08);
  }
  .fnf-calc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  @media (max-width: 768px) {
    .fnf-calc-grid {
      grid-template-columns: 1fr;
    }
  }

  /* Offboarding KPI & Notice Card Mouseover Hover Effects */
  .offboarding-kpi-card {
    background: #ffffff;
    padding: 16px 20px;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    cursor: default;
  }
  .offboarding-kpi-card:hover {
    transform: translateY(-3px);
  }
  .offboarding-kpi-yellow {
    border: 1px solid #fef08a;
    border-left: 4px solid #eab308;
  }
  .offboarding-kpi-yellow:hover {
    border-color: #fde047;
    border-left-color: #ca8a04;
    box-shadow: 0 10px 24px rgba(234, 179, 8, 0.16), 0 2px 6px rgba(0,0,0,0.04);
  }
  .offboarding-kpi-blue {
    border: 1px solid #bfdbfe;
    border-left: 4px solid #2563eb;
  }
  .offboarding-kpi-blue:hover {
    border-color: #93c5fd;
    border-left-color: #1d4ed8;
    box-shadow: 0 10px 24px rgba(37, 99, 235, 0.16), 0 2px 6px rgba(0,0,0,0.04);
  }
  .offboarding-kpi-green {
    border: 1px solid #bbf7d0;
    border-left: 4px solid #16a34a;
  }
  .offboarding-kpi-green:hover {
    border-color: #86efac;
    border-left-color: #15803d;
    box-shadow: 0 10px 24px rgba(22, 163, 74, 0.16), 0 2px 6px rgba(0,0,0,0.04);
  }
  .offboarding-kpi-indigo {
    border: 1px solid #e2e8f0;
    border-left: 4px solid #6366f1;
  }
  .offboarding-kpi-indigo:hover {
    border-color: #c7d2fe;
    border-left-color: #4f46e5;
    box-shadow: 0 10px 24px rgba(99, 102, 241, 0.16), 0 2px 6px rgba(0,0,0,0.04);
  }
  .offboarding-notice-card {
    background: #ffffff;
    border-radius: 14px;
    padding: 20px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .offboarding-notice-card:hover {
    transform: translateY(-3px);
    border-color: #cbd5e1;
    box-shadow: 0 12px 28px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.04);
  }
`;

// ── Phone number normalization & 10-digit extractor helper ───────────────────
const getPhone10 = (raw) => {
  if (!raw) return "";
  let s = String(raw).trim();
  // Strip +91 country code prefix if explicitly provided (e.g. +91 93530 10107, +919353010107, +91 9)
  if (s.startsWith("+91")) {
    s = s.slice(3).trim();
  } else if (s.startsWith("+")) {
    s = s.replace(/^\+\d{1,3}\s*[-.]?\s*/, "");
  }
  let d = s.replace(/\D/g, "");
  // Trunk prefix 0 (e.g. 09353010107) -> strip leading 0
  if (d.length === 11 && d.startsWith("0")) {
    d = d.slice(1);
  }
  // If user pasted/typed 12 digits starting with 91 (e.g. 919353010107), strip 91 country code
  if (d.length >= 12 && d.startsWith("91")) {
    d = d.slice(2);
  }
  // If more than 10 digits and starts with 91, strip 91
  if (d.length > 10 && d.startsWith("91")) {
    d = d.slice(2);
  }
  return d.slice(0, 10);
};

// ── Email validation helper ──────────────────────────────────────────────────
const isValidEmail = (email) => {
  if (!email || !String(email).trim()) return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(String(email).trim());
};

// ── Org Tree Hierarchy & Celebrations Helpers ─────────────────────────────────
function buildOrgTree(employees = [], reportingHierarchy = {}) {
  const activeEmps = (employees || []).filter(e => e.employment_status !== "terminated" && e.is_active !== false);
  const empMap = new Map();
  activeEmps.forEach(emp => {
    empMap.set(emp.id, { ...emp, children: [] });
  });

  const roots = [];
  activeEmps.forEach(emp => {
    const managerId = reportingHierarchy[emp.id] || reportingHierarchy[emp.employee_code];
    const node = empMap.get(emp.id);
    if (managerId && managerId !== emp.id && empMap.has(managerId)) {
      empMap.get(managerId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  empMap.forEach(node => {
    node.children.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  });
  roots.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return { roots, empMap, totalActive: activeEmps.length };
}

function calcCelebrations(employees = []) {
  const activeEmps = (employees || []).filter(e => e.employment_status !== "terminated" && e.is_active !== false);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentDay = now.getDate(); // 1-31
  const nextMonth = (currentMonth % 12) + 1;
  const nextMonthYear = currentMonth === 12 ? currentYear + 1 : currentYear;

  const birthdaysToday = [];
  const birthdaysThisMonth = [];
  const birthdaysNextMonth = [];

  const anniversariesToday = [];
  const anniversariesThisMonth = [];
  const anniversariesNextMonth = [];

  const newHires = [];

  activeEmps.forEach(emp => {
    // 1. Birthdays
    const dobStr = emp.dob || emp.date_of_birth;
    if (dobStr && typeof dobStr === "string") {
      const parts = dobStr.split("-").map(Number);
      if (parts.length === 3 && parts[0] > 1900 && parts[1] >= 1 && parts[1] <= 12 && parts[2] >= 1 && parts[2] <= 31) {
        const bYear = parts[0];
        const bMonth = parts[1];
        const bDay = parts[2];
        const turningAge = currentYear - bYear;
        const daysDiff = bDay - currentDay;

        if (bMonth === currentMonth && bDay === currentDay) {
          birthdaysToday.push({ ...emp, turningAge, bDay, bMonth, isToday: true });
        } else if (bMonth === currentMonth) {
          birthdaysThisMonth.push({ ...emp, turningAge, bDay, bMonth, daysDiff });
        } else if (bMonth === nextMonth) {
          birthdaysNextMonth.push({ ...emp, turningAge: nextMonthYear - bYear, bDay, bMonth });
        }
      }
    }

    // 2. Anniversaries & New Hires
    const dojStr = emp.doj || emp.date_of_joining;
    if (dojStr && typeof dojStr === "string") {
      const parts = dojStr.split("-").map(Number);
      if (parts.length === 3 && parts[0] > 1900 && parts[1] >= 1 && parts[1] <= 12 && parts[2] >= 1 && parts[2] <= 31) {
        const jYear = parts[0];
        const jMonth = parts[1];
        const jDay = parts[2];
        const yearsCompleted = currentYear - jYear;
        const daysDiff = jDay - currentDay;

        const joinDate = new Date(jYear, jMonth - 1, jDay);
        const daysSinceJoin = Math.floor((now.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceJoin >= 0 && daysSinceJoin <= 45) {
          newHires.push({ ...emp, daysSinceJoin, jDay, jMonth, jYear });
        }

        if (yearsCompleted >= 1) {
          if (jMonth === currentMonth && jDay === currentDay) {
            anniversariesToday.push({ ...emp, yearsCompleted, jDay, jMonth, isToday: true });
          } else if (jMonth === currentMonth) {
            anniversariesThisMonth.push({ ...emp, yearsCompleted, jDay, jMonth, daysDiff });
          } else if (jMonth === nextMonth) {
            anniversariesNextMonth.push({ ...emp, yearsCompleted: (nextMonthYear - jYear), jDay, jMonth });
          }
        }
      }
    }
  });

  birthdaysThisMonth.sort((a, b) => a.bDay - b.bDay);
  birthdaysNextMonth.sort((a, b) => a.bDay - b.bDay);
  anniversariesThisMonth.sort((a, b) => a.jDay - b.jDay);
  anniversariesNextMonth.sort((a, b) => a.jDay - b.jDay);
  newHires.sort((a, b) => a.daysSinceJoin - b.daysSinceJoin);

  return {
    birthdaysToday,
    birthdaysThisMonth,
    birthdaysNextMonth,
    anniversariesToday,
    anniversariesThisMonth,
    anniversariesNextMonth,
    newHires
  };
}

// ── PDF Export (single slip)

// ── Shared document header/footer helpers ────────────────────────────────────
export function cacheLogoImage(url) {
  if (!url || typeof window === "undefined") return Promise.resolve(null);
  window.__tw_logo_cache = window.__tw_logo_cache || {};
  if (window.__tw_logo_cache[url]) return Promise.resolve(window.__tw_logo_cache[url]);

  return new Promise((resolve) => {
    if (typeof url === "string" && url.startsWith("data:")) {
      if (url.startsWith("data:image/webp")) {
        const img = new Image();
        img.onload = () => {
          try {
            const cvs = document.createElement("canvas");
            cvs.width = img.naturalWidth || 200;
            cvs.height = img.naturalHeight || 200;
            cvs.getContext("2d").drawImage(img, 0, 0);
            const png = cvs.toDataURL("image/png");
            window.__tw_logo_cache[url] = png;
            resolve(png);
          } catch (_) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
        return;
      }
      window.__tw_logo_cache[url] = url;
      resolve(url);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const cvs = document.createElement("canvas");
        cvs.width = img.naturalWidth || img.width || 200;
        cvs.height = img.naturalHeight || img.height || 200;
        const ctx = cvs.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const png = cvs.toDataURL("image/png");
        window.__tw_logo_cache[url] = png;
        resolve(png);
      } catch (_) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function addDocHeader(doc, W, pageH, company) {
  if (company) {
    const H = 26;
    doc.setFillColor(26,58,92); doc.rect(0, 0, W, H, "F");
    const cfg = company.config || {};
    if (cfg.logo_base64) {
      try {
        const cached = window.__tw_logo_cache && window.__tw_logo_cache[cfg.logo_base64];
        if (cached) {
          doc.addImage(cached, "PNG", 10, 3, 20, 20);
        } else if (typeof cfg.logo_base64 === "string" && cfg.logo_base64.startsWith("data:")) {
          const fmt = (cfg.logo_base64.includes("image/jpeg") || cfg.logo_base64.includes("image/jpg")) ? "JPEG" : "PNG";
          doc.addImage(cfg.logo_base64, fmt, 10, 3, 20, 20);
        } else if (typeof cfg.logo_base64 === "string") {
          const existingImg = document.querySelector(`img[src="${cfg.logo_base64}"]`);
          if (existingImg && existingImg.complete && existingImg.naturalWidth > 0) {
            doc.addImage(existingImg, "PNG", 10, 3, 20, 20);
          }
        }
      } catch(e) { console.error("Error drawing logo to PDF", e); }
    }
    doc.setTextColor(255,255,255); 
    doc.setFontSize(15); doc.setFont("helvetica","bold");
    doc.text(company.name || "Company Name", W/2, 9, {align:"center"});
    doc.setFontSize(8); doc.setFont("helvetica","normal");
    doc.setTextColor(200,210,230);
    const contactLine = [company.phone, company.email, company.website].filter(Boolean).join("  |  ");
    if (contactLine) doc.text(contactLine, W/2, 15, {align:"center"});
    if (company.address) {
       const addr = company.address.replace(/\n/g, ", ");
       doc.text(addr.length > 100 ? addr.substring(0, 97) + "..." : addr, W/2, 21, {align:"center"});
    }
    return H + 4;
  }
  const H = 18;
  try { doc.addImage(HEADER_IMG, "PNG", 0, 0, W, H); } catch(_) {
    doc.setFillColor(15,17,23); doc.rect(0,0,W,H,"F");
  }
  return H + 4;
}
function addDocFooter(doc, W, pageH) {
  const footY = pageH - 6;
  doc.setFontSize(6.5); doc.setTextColor(120,130,150); doc.setFont("helvetica","normal");
  doc.text("© TrustWork Solutions. Confidential - for authorised use only.  |  Email: payroll@trustwork.co.in  |  Ph: +91 93530 10107  |  Web: www.trustwork.co.in", W/2, footY, {align:"center"});
}
export async function generatePayslipPdfDoc(emp, sal, company) {
  const doc = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
  const W=210, M=14;
  const cfg = company?.config || DEFAULT_CONFIG;
  const theme = getPayslipTheme(cfg.payslip_style);

  // Normalise field names — DB vs calcSalaryFull use different keys
  sal = {
    ...sal,
    ot_pay:            sal.ot_pay            ?? sal.ot           ?? 0,
    absence_deduction: sal.absence_deduction ?? sal.absence_ded  ?? 0,
    pf_emp:            sal.pf_emp            ?? 0,
    pf_er:             sal.pf_er             ?? 0,
    esi_emp:           sal.esi_emp           ?? 0,
    esi_er:            sal.esi_er            ?? 0,
    pt:                sal.pt                ?? 0,
    tds:               sal.tds               ?? 0,
    lwf:               sal.lwf               ?? ((cfg.lwf_applicable && sal.month === 12) ? 50 : 0),
    vpf:               sal.vpf               ?? 0,
    gratuity:          sal.gratuity          ?? (cfg.gratuity !== false ? Math.round((sal.basic || 0) * 0.0481) : 0),
    gross:             sal.gross             ?? 0,
    net:               sal.net               ?? 0,
    ctc:               sal.ctc               ?? (sal.gross + (sal.pf_er || 0) + (sal.esi_er || 0) + (cfg.gratuity !== false ? Math.round((sal.basic || 0) * 0.0481) : 0)),
    basic:             sal.basic             ?? 0,
    hra:               sal.hra               ?? 0,
    da:                sal.da                ?? 0,
    special:           sal.special           ?? sal.special_allowance ?? 0,
    conveyance:        sal.conveyance        ?? 0,
    medical:           sal.medical           ?? 0,
    bonus:             sal.bonus             ?? 0,
  };

  if (company?.config?.logo_base64) {
    await cacheLogoImage(company.config.logo_base64);
  }

  // Full company identity, address, contact information and saved registrations.
  const logoImage = company?.config?.logo_base64
    ? window.__tw_logo_cache?.[company.config.logo_base64] || (company.config.logo_base64.startsWith("data:") ? company.config.logo_base64 : null)
    : null;
  const HDR = addPayslipCompanyHeader(doc, W, company, logoImage);

  // ── Title
  doc.setTextColor(...theme.palette.rgb); doc.setFontSize(12); doc.setFont("helvetica","bold");
  doc.text("SALARY SLIP", W/2, HDR+9, {align:"center"});

  // Employee details box
  const fmtDOJ = d => { if(!d) return "—"; const p=d.split("-"); return p.length===3?p[2]+"-"+p[1]+"-"+p[0]:d; };
  // Pay Date = last day of the pay month
  const lastDay = new Date(sal.year, sal.month, 0).getDate();
  const payDateStr = String(lastDay).padStart(2,"0") + "-" + String(sal.month).padStart(2,"0") + "-" + sal.year;
  const eRows=[
    ["Employee Name", emp.name,          "Employee ID",  emp.employee_code || emp.employee_id || "—"],
    ["Pay Period",    MONTHS[sal.month]+" "+sal.year, "Pay Date", payDateStr],
    ["Date of Joining", fmtDOJ(emp.doj), "PAN",         emp.pan || "—"],
  ];
  if (emp.designation || emp.department) eRows.push(["Designation", emp.designation || "—", "Department", emp.department || "—"]);
  if (emp.uan || emp.esi_id) {
    eRows.push(["UAN", emp.uan || "—", "ESI Number", emp.esi_id || "—"]);
  }
  
  doc.setFillColor(245,248,252); doc.roundedRect(M,HDR+11,W-M*2, eRows.length * 9 + 4, 3,3,"F");
  
  eRows.forEach((r,i)=>{
    const ey=(HDR+17)+i*9;
    doc.setFont("helvetica","bold"); doc.setTextColor(...theme.palette.rgb); doc.setFontSize(9);
    doc.text(String(r[0])+":",M+3,ey); doc.setFont("helvetica","normal"); doc.setTextColor(20,20,20);
    doc.text(String(r[1]),M+33,ey);
    doc.setFont("helvetica","bold"); doc.setTextColor(...theme.palette.rgb);
    doc.text(String(r[2])+":",W/2+3,ey); doc.setFont("helvetica","normal"); doc.setTextColor(20,20,20);
    doc.text(String(r[3]),W/2+29,ey);
  });

  // Attendance line
  let y = HDR + 11 + (eRows.length * 9 + 4) + 5;
  doc.setFillColor(...theme.palette.tint); doc.rect(M,y,W-M*2,7,"F");
  doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(...theme.palette.rgb);
  const actWd = cfg.working_days === -1 ? new Date(sal.year, sal.month, 0).getDate() : (cfg.working_days||26);
  const presentD = actWd - (sal.absent_days||0);
  doc.text("Working Days: "+actWd+" | Present: "+presentD+" | Absent: "+(sal.absent_days||0)+(cfg.ot_applicable?" | OT Hours: "+(sal.ot_hours||0):"")+(emp.date_of_exit?" | LWD: "+fmtDate(emp.date_of_exit):""),M+3,y+4.5);
  y+=10;

  // Table header
  const c1=M,c2=M+60,c3=M+100,c4=M+150;
  doc.setFillColor(...theme.palette.rgb); doc.rect(M,y,W-M*2,8,"F");
  doc.setTextColor(255,255,255); doc.setFontSize(8.5); doc.setFont("helvetica","bold");
  doc.text("EARNINGS",c1+2,y+5.5); doc.text("Amount (Rs.)",c2,y+5.5);
  doc.text("DEDUCTIONS",c3+2,y+5.5); doc.text("Amount (Rs.)",c4,y+5.5);
  y+=8;

  // Calculate Earned values for components based on attendance
  const e_basic = actWd ? Math.round(sal.basic / actWd * presentD) : 0;
  // Use sal.hra from saved calculation (already accounts for custom HRA and statutory budget squeeze)
  const _hra_rate = sal.hra || 0;
  const e_hra = actWd ? Math.round(_hra_rate / actWd * presentD) : 0;
  const e_da = actWd ? Math.round(sal.da / actWd * presentD) : 0;
  const e_lta = actWd ? Math.round(sal.lta / actWd * presentD) : 0;
  const e_special = actWd ? Math.round(sal.special / actWd * presentD) : 0;
  const e_conveyance = actWd ? Math.round(sal.conveyance / actWd * presentD) : 0;
  const e_medical = actWd ? Math.round(sal.medical / actWd * presentD) : 0;

  // Build earnings/deductions rows
  const earns = [["Basic Salary", e_basic]];
  const isCustomHRA = cfg.hra_customised && emp.custom_hra_pct !== undefined && emp.custom_hra_pct !== null && emp.custom_hra_pct !== "";
  const label = isCustomHRA ? "HRA (Custom)" : (cfg.hra_type==="fixed" ? "HRA (Fixed)" : "HRA (" + (cfg.hra_value !== undefined ? cfg.hra_value : cfg.hra_pct) + "%)");
  earns.push([label, e_hra]);
  if(cfg.da&&sal.da>0) earns.push(["DA ("+cfg.da_pct+"% of Basic)", e_da]);
  if(cfg.lta&&sal.lta>0) earns.push(["LTA ("+cfg.lta_pct+"% of Basic)", e_lta]);
  if(cfg.special_allowance || sal.special > 0) earns.push([cfg.custom_special_display_name || "Other Earnings", e_special]);
  if(cfg.conveyance) earns.push(["Conveyance", e_conveyance]);
  if(cfg.medical) earns.push(["Medical Allowance", e_medical]);
  if(sal.bonus>0) earns.push([cfg.bonus_display_name || "Bonus", sal.bonus]);
  if(sal.ot_pay>0) earns.push(["Excess Duty Payment", sal.ot_pay]);
  // Absence Deduction is now implicit since components are shown as Earned

  // ESI is computed on Earned Basic + DA under New Wage Code rules
  const esiGross = e_basic + e_da;
  const deds = [];
  if(cfg.pf_applicable) deds.push(["PF Employee",fmtNum(sal.pf_emp||0)]);
  if((sal.vpf||0)>0) deds.push(["VPF",fmtNum(sal.vpf||0)]);
  if(cfg.esi_applicable && sal.esi_emp>0) deds.push(["ESI Employee",fmtNum(sal.esi_emp)]);
  deds.push(["Professional Tax",fmtNum(sal.pt)]);
  if((sal.tds||0)>0) deds.push(["TDS", fmtNum(sal.tds)]);
  if(sal.lwf > 0) deds.push(["LWF (Karnataka)", fmtNum(sal.lwf)]);
  while(deds.length<earns.length) deds.push(["",""]);

  const maxRows = Math.max(earns.length, deds.length);
  for(let i=0;i<maxRows;i++){
    if(i%2===0){doc.setFillColor(245,248,253);doc.rect(M,y,W-M*2,7,"F");}
    doc.setTextColor(30,30,30); doc.setFont("helvetica","normal"); doc.setFontSize(8.5);
    if(earns[i]){doc.text(String(earns[i][0]),c1+2,y+5);doc.text(String(fmtNum(earns[i][1])),c2+28,y+5,{align:"right"});}
    if(deds[i]&&deds[i][0]){doc.text(String(deds[i][0]),c3+2,y+5);doc.text(String(deds[i][1]),c4+28,y+5,{align:"right"});}
    y+=7;
  }

  // Gross/Deductions row — BUG FIX: include TDS + LWF in total deductions footer
  const totalDedsForDisplay = (sal.pf_emp||0)+(sal.vpf||0)+(sal.esi_emp||0)+(sal.pt||0)+(sal.tds||0)+(sal.lwf||0);
  doc.setFillColor(...theme.palette.tint); doc.rect(M,y,W-M*2,8,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(8.5); doc.setTextColor(...theme.palette.rgb);
  doc.text("Gross Salary:",c1+2,y+5.5); doc.text("Rs. "+fmtNum(sal.gross),c2+28,y+5.5,{align:"right"});
  doc.text("Total Deductions:",c3+2,y+5.5); doc.text("Rs. "+fmtNum(totalDedsForDisplay),c4+28,y+5.5,{align:"right"});
  y+=12;

  // Net banner
  doc.setFillColor(...theme.palette.rgb); doc.roundedRect(M,y,W-M*2,11,3,3,"F");
  doc.setTextColor(255,255,255); doc.setFontSize(10); doc.setFont("helvetica","bold");
  doc.text("NET SALARY (TAKE HOME)",M+5,y+7.5);
  doc.text("Rs. "+fmtNum(sal.net),W-M-3,y+7.5,{align:"right"});
  y+=16;

  // Employer contributions
  if(cfg.pf_applicable || cfg.esi_applicable || (cfg.gratuity !== false && sal.gratuity > 0)){
    doc.setFillColor(245,248,252); doc.roundedRect(M,y,W-M*2,18,3,3,"F");
    doc.setTextColor(80,80,80); doc.setFontSize(7); doc.setFont("helvetica","bold");
    doc.text("EMPLOYER CONTRIBUTIONS",M+3,y+5);
    doc.setFont("helvetica","normal"); doc.setFontSize(8);
    if(cfg.pf_applicable){
      doc.setTextColor(50,50,50); doc.text("PF Employer: Rs."+fmtNum(sal.pf_er),M+3,y+11);
      const isPfCapped = cfg.pf_wage_cap !== false && sal.pfWage && sal.pfWage < (sal.basic + (sal.da || 0));
      doc.text(isPfCapped ? "(12% capped at Rs.25,000)" : "(12% on Basic"+(cfg.da?" + DA":"")+")",M+3,y+15.5);
    }
    if(cfg.esi_applicable&&sal.esi_er>0){
      doc.setFontSize(8); doc.setTextColor(50,50,50);
      doc.text("ESI Employer: Rs."+fmtNum(sal.esi_er),M+65,y+11);
      doc.setFontSize(6.5); doc.setTextColor(120,120,120);
      doc.text("(3.25% on Rs."+fmtNum(esiGross)+")",M+65,y+15.5);
    }
    if(cfg.gratuity !== false && sal.gratuity > 0){
      doc.setFontSize(8); doc.setTextColor(50,50,50);
      doc.text("Gratuity: Rs."+fmtNum(sal.gratuity),M+105,y+11);
      doc.setFontSize(6.5); doc.setTextColor(120,120,120);
      doc.text("(4.81% on Basic)",M+105,y+15.5);
    }
    doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(...theme.palette.rgb);
    doc.text("CTC: Rs."+fmtNum(sal.ctc),W-M-3,y+11,{align:"right"});
    doc.setFontSize(6.5); doc.setFont("helvetica","normal"); doc.setTextColor(120,120,120);
    doc.text("(Gross + PF + ESI + Gratuity)",W-M-3,y+15.5,{align:"right"});
    y+=22;
  }

  if(cfg.ot_applicable && (sal.ot_hours||0) > 0) {
    doc.setFontSize(7.5); doc.setTextColor(100,100,100); doc.setFont("helvetica","bold");
    doc.text("Overtime Hours Logged: "+sal.ot_hours+" hrs", W/2, y, {align:"center"});
    y+=6;
  }

  // Rounded logo corners — clip to rounded rect before placing logo
  doc.setTextColor(150,150,150); doc.setFontSize(7.5); doc.setFont("helvetica","italic");
  doc.text("Computer-generated salary slip. No physical signature required.",W/2,y,{align:"center"});
  y+=4;
  doc.setFontSize(7); doc.setTextColor(100,140,200);
  doc.text("Email: payroll@trustwork.co.in  |  Ph: +91 93530 10107  |  Web: www.trustwork.co.in",W/2,y,{align:"center"});
  y+=5;
  doc.setFontSize(6.5); doc.setTextColor(160,160,160); doc.setFont("helvetica","normal");
  return doc;
}

async function exportPDF(emp, sal, company) {
  const doc = await generatePayslipPdfDoc(emp, sal, company);
  doc.save("Slip_" + (emp.name || "Employee").replace(/\s+/g, "_") + "_" + (MONTHS[sal.month] || "Month") + "_" + sal.year + ".pdf");
}

// ── Download blank company template xlsx
// ═══════════════════════════════════════════════════════
// ── COMPANY EXCEL / PDF UTILITIES
// ═══════════════════════════════════════════════════════

// Helper: // ── Employee Excel: download template
async function downloadEmployeeTemplate(companyName = "") {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Employee Template");

    const headers = [
      "Employee ID *",
      "Full Name *",
      "Date of Birth (DD-MM-YYYY) *",
      "Date of Joining (DD-MM-YYYY) *",
      "PAN Number *",
      "Gross Monthly Salary (₹) *",
      "Custom Basic (% - Optional)",
      "Custom HRA (₹ - Optional)",
      "Custom Special Allowance (₹ - Optional)",
      "Father Name",
      "Phone Number",
      "Email Address",
      "Department",
      "Designation",
      "Bank Name",
      "Bank Account Number",
      "Bank IFSC Code",
      "UAN Number",
      "ESI ID",
      "PF Account Number",
      "Force ESI Until (MM-YYYY)",
      "VPF Amount (₹)",
      "TDS Amount (₹)",
      "Date of Exit (DD-MM-YYYY)"
    ];

    const titleRow = ws.addRow([`TRUSTWORK PAYROLL  |  EMPLOYEE ONBOARDING TEMPLATE${companyName ? `  |  Prepared for ${companyName}` : ""}`]);
    ws.mergeCells("A1:H1");
    titleRow.height = 30;
    titleRow.getCell(1).font = { name: 'Aptos Display', bold: true, size: 13, color: { argb: 'FFFFFFFF' } };
    titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
    titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: false, shrinkToFit: true };

    ws.addRow([]);
    ws.getRow(2).height = 8;

    const subRow = ws.addRow(["Mandatory columns marked (*) and highlighted in orange. Basic (50%), Bonus, EPF, and ESI are auto-computed from Gross based on company settings. Custom allowances are optional."]);
    ws.mergeCells("A3:H3");
    subRow.height = 30;
    subRow.getCell(1).font = { name: 'Calibri', size: 10, color: { argb: 'FF475569' } };
    subRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    subRow.getCell(1).alignment = { vertical: 'middle', wrapText: true, indent: 1 };

    const border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };

    const headerRow = ws.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      const mandatory = String(cell.value).endsWith('*');
      cell.border = border;
      cell.font = { bold: true, color: { argb: mandatory ? 'FF9A3412' : 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: mandatory ? 'FFFFEDD5' : 'FFE2E8F0' } };
    });

    for(let i=0; i<50; i++){
      const emptyRow = ws.addRow([]);
      for (let j = 1; j <= headers.length; j++) {
        emptyRow.getCell(j).border = border;
      }
    }

    ws.views = [{ state: "frozen", xSplit: 2, ySplit: 4 }];

    headers.forEach((h, i) => {
      ws.getColumn(i + 1).width = Math.floor(Math.max(h.length + 2, 18) * 1.1);
    });

    ws.getColumn(2).width = 28;

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TrustWork_Employee_Template.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  } catch(e) {
    alert("Download failed: " + e.message);
  }
}

async function exportEmployeesExcel(empList, companyName) {
  try {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Employees");

    const headers = [
      "Employee ID *",
      "Full Name *",
      "Date of Birth (DD-MM-YYYY) *",
      "Date of Joining (DD-MM-YYYY) *",
      "PAN Number *",
      "VPF Amount (₹)",
      "TDS Amount (₹)",
      "Gross Monthly Salary (₹) *",
      "Father Name",
      "Phone Number",
      "Email Address",
      "Department",
      "Designation",
      "Bank Name",
      "Bank Account Number",
      "Bank IFSC Code",
      "UAN Number",
      "ESI ID",
      "PF Account Number"
    ];

    const formatExcelDate = (dateString) => {
      if (!dateString) return "";
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return "";
      return String(d.getDate()).padStart(2, '0') + "-" + String(d.getMonth() + 1).padStart(2, '0') + "-" + d.getFullYear();
    };

    ws.addRow([]);
    ws.addRow([]);
    ws.addRow([]);

    const border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };

    const headerRow = ws.addRow(headers);
    headerRow.font = { bold: true, color: { argb: 'FF0F172A' } };
    headerRow.eachCell(cell => {
      cell.border = border;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
    });

    ws.views = [{ state: "frozen", xSplit: 2, ySplit: 4 }];

    headers.forEach((h, i) => {
      ws.getColumn(i + 1).width = Math.floor(Math.max(h.length + 2, 18) * 1.1);
    });

    (empList || []).forEach((e, idx) => {
      const row = ws.addRow([
        e.employee_code || "",
        e.name || "",
        formatExcelDate(e.dob),
        formatExcelDate(e.doj),
        e.pan || "",
        e.vpf_pct || 0,
        e.tds_amount || 0,
        e.basic || 0,
        e.father_name || "",
        e.phone || "",
        e.email || "",
        e.department || "",
        e.designation || "",
        e.bank_name || "",
        e.bank_account || "",
        e.bank_ifsc || "",
        e.uan || "",
        e.esi_id || "",
        e.pf_account || ""
      ]);

      const bgColor = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = border;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      });
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (companyName || "Company").replace(/[^a-z0-9]/gi, "_") + "_Employees.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  } catch(e) {
    alert("Export failed: " + e.message);
  }
}

// ── Employee Excel: parse uploaded file
function parseEmployeeExcel(file, onSuccess, onError) {
  const reader = new FileReader();
  reader.onload = e => {
    try {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

      if (!rawRows || !rawRows.length) {
        onError("The uploaded Excel file is empty.");
        return;
      }

      // Find header row
      let hIdx = rawRows.findIndex(r => r.some(cell => {
        const s = String(cell || "").toLowerCase();
        return s.includes("full name") || (s.includes("name") && !s.includes("father") && !s.includes("bank"));
      }));

      if (hIdx < 0) {
        hIdx = rawRows.findIndex(r => r.some(cell => String(cell || "").toLowerCase().includes("pan")));
      }

      if (hIdx < 0) {
        onError("Could not find header row. Please make sure the Excel file has 'Full Name' and 'PAN Number' headers.");
        return;
      }

      const headerRow = rawRows[hIdx].map(h => String(h || "").trim().toLowerCase());

      // Smart header matching: prioritize exact and specific matches
      const getColIdx = (keywords) => {
        // 1. Try exact match first
        let idx = headerRow.findIndex(h => keywords.some(k => h === k));
        if (idx >= 0) return idx;
        // 2. Try prefix / includes match
        return headerRow.findIndex(h => keywords.some(k => h.includes(k)));
      };

      const empCodeCol  = getColIdx(["employee id *", "employee id", "employee code", "emp id", "emp code", "employee_id", "emp_code"]);
      const nameCol     = getColIdx(["full name *", "full name", "employee name", "emp name", "name"]);
      const dobCol      = getColIdx(["date of birth (dd-mm-yyyy) *", "date of birth", "birth date", "dob"]);
      const dojCol      = getColIdx(["date of joining (dd-mm-yyyy) *", "date of joining", "joining date", "doj"]);
      
      const panCol      = getColIdx(["pan number *", "pan number", "pan no", "pan"]);
      const vpfCol      = getColIdx(["vpf amount (₹)", "vpf amount", "vpf", "voluntary pf"]);
      const tdsCol      = getColIdx(["tds amount (₹)", "tds amount", "tds", "income tax"]);
      
      // Distinguish Gross Monthly Salary vs Basic Salary
      const grossCol    = getColIdx(["gross monthly salary (₹) *", "gross monthly salary", "gross salary", "monthly gross", "gross pay", "gross"]);
      const basicSalaryCol = getColIdx(["basic salary", "basic pay", "basic"]);
      const basicCol    = grossCol >= 0 ? grossCol : (basicSalaryCol >= 0 ? basicSalaryCol : getColIdx(["salary"]));

      const customBasicCol   = getColIdx(["custom basic (% - optional)", "custom basic", "basic %", "basic pct", "basic percentage"]);
      const customHraCol     = getColIdx(["custom hra (₹ - optional)", "custom hra", "hra amount", "hra (₹)", "house rent allowance", "hra"]);
      const customSpecialCol = getColIdx(["custom special allowance (₹ - optional)", "custom special", "special allowance", "special allow", "special (₹)", "other allowance", "others", "cca", "special"]);

      const fatherCol   = getColIdx(["father name", "father"]);
      const phoneCol    = getColIdx(["phone number", "mobile number", "phone", "mobile", "contact"]);
      const emailCol    = getColIdx(["email address", "email id", "email"]);
      const deptCol     = getColIdx(["department", "dept"]);
      const desigCol    = getColIdx(["designation", "role", "title", "position"]);
      const bankNameCol = getColIdx(["bank name"]);
      const bankAccCol  = getColIdx(["bank account number", "bank account", "account number", "account no", "a/c no"]);
      const ifscCol     = getColIdx(["bank ifsc code", "ifsc code", "ifsc"]);
      const uanCol      = getColIdx(["uan number", "uan no", "uan"]);
      const esiCol      = getColIdx(["esi id", "esi number", "esi no", "esi"]);
      const pfAccCol    = getColIdx(["pf account number", "pf account", "pf no", "pf number", "pf a/c"]);
      const esiForcedCol= getColIdx(["force esi until (mm-yyyy)", "force esi until", "force esi"]);
      const exitCol     = getColIdx(["date of exit (dd-mm-yyyy)", "date of exit", "exit date", "exit", "lwd", "last working day"]);

      if (empCodeCol < 0 || nameCol < 0 || dobCol < 0 || dojCol < 0 || panCol < 0 || basicCol < 0) {
        const missing = [];
        if (empCodeCol < 0) missing.push("'Employee ID'");
        if (nameCol < 0) missing.push("'Full Name'");
        if (dobCol < 0) missing.push("'Date of Birth'");
        if (dojCol < 0) missing.push("'Date of Joining'");
        if (panCol < 0) missing.push("'PAN Number'");
        if (basicCol < 0) missing.push("'Gross Monthly Salary'");
        const err = `Missing mandatory column headers: ${missing.join(", ")}. Please use the exact template columns.`;
        const diag = `TrustWork Excel Header Diagnostic:\nFound Headers: ${headerRow.join(" | ")}\nMissing Required: ${missing.join(", ")}`;
        console.error("[TrustWork Employee Upload Header Mismatch]:", diag);
        onError(err, diag);
        return;
      }

      const parseExcelDateStr = (val) => {
        if (val === null || val === undefined || val === "") return "";
        if (val instanceof Date) {
          if (isNaN(val.getTime())) return "";
          const y = val.getFullYear();
          const m = String(val.getMonth() + 1).padStart(2, "0");
          const d = String(val.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        }
        // Handle Excel numeric serial dates (e.g. 34834 for 1995-05-15, 45292 for 2024-01-01)
        const num = typeof val === "number" ? val : (typeof val === "string" && /^\d{4,5}(\.\d+)?$/.test(val.trim()) ? parseFloat(val.trim()) : NaN);
        if (!isNaN(num) && num > 1000 && num < 100000) {
          if (typeof XLSX !== "undefined" && XLSX.SSF && XLSX.SSF.parse_date_code) {
            try {
              const p = XLSX.SSF.parse_date_code(num);
              if (p && p.y && p.m && p.d) {
                return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
              }
            } catch (e) {}
          }
          const utcDays = num - 25569;
          const dt = new Date(Math.round(utcDays * 86400 * 1000));
          if (!isNaN(dt.getTime())) {
            const y = dt.getUTCFullYear();
            const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
            const d = String(dt.getUTCDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
          }
        }
        const s = String(val).trim();
        if (!s) return "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        const parts = s.split(/[-/.]/);
        if (parts.length === 3) {
          const p0 = parseInt(parts[0], 10);
          const p1 = parseInt(parts[1], 10);
          const p2 = parseInt(parts[2], 10);
          if (parts[0].length === 4) return `${parts[0]}-${String(p1).padStart(2, "0")}-${String(p2).padStart(2, "0")}`;
          if (parts[2].length === 4) return `${parts[2]}-${String(p1).padStart(2, "0")}-${String(p0).padStart(2, "0")}`;
          if (parts[2].length === 2) {
            const fullYear = p2 < 50 ? 2000 + p2 : 1900 + p2;
            return `${fullYear}-${String(p1).padStart(2, "0")}-${String(p0).padStart(2, "0")}`;
          }
        }
        const digits = s.replace(/\D/g, "");
        if (digits.length === 8) {
          if (digits.startsWith("19") || digits.startsWith("20")) {
            return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
          }
          return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
        }
        const nativeDt = new Date(s);
        if (!isNaN(nativeDt.getTime()) && nativeDt.getFullYear() > 1900 && nativeDt.getFullYear() < 2100) {
          const y = nativeDt.getFullYear();
          const m = String(nativeDt.getMonth() + 1).padStart(2, "0");
          const d = String(nativeDt.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        }
        return s;
      };

      const emps = [];
      const rowDiagnostics = [];

      for (let i = hIdx + 1; i < rawRows.length; i++) {
        const r = rawRows[i];
        const empCode = String(r[empCodeCol] || "").trim();
        const name = String(r[nameCol] || "").trim();
        const dobStr = dobCol >= 0 ? parseExcelDateStr(r[dobCol]) : null;
        const dojStr = dojCol >= 0 ? parseExcelDateStr(r[dojCol]) : null;
        const exitStr = exitCol >= 0 ? parseExcelDateStr(r[exitCol]) : null;
        const pan  = panCol >= 0 ? String(r[panCol] || "").trim().toUpperCase() : "";

        if (!empCode && !name && !pan) continue;
        if (name === "Sample Employee (Delete or overwrite)" && empCode.toLowerCase() === "emp001") {
          continue; // Skip example template placeholder row
        }

        const rowNum = i + 1;
        rowDiagnostics.push(`Row ${rowNum}: Code="${empCode}", Name="${name}", PAN="${pan}", DOB="${dobStr}", DOJ="${dojStr}", Gross="${r[basicCol]}"`);

        if (!empCode) {
          const msg = `Row ${rowNum}: Employee ID is required.`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }
        if (!name) {
          const msg = `Row ${rowNum}: Full Name is required for Employee ID "${empCode}".`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }
        if (!dobStr || !/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
          const rawVal = r[dobCol];
          const msg = `Row ${rowNum} (${name}): Date of Birth "${rawVal || "Empty"}" is invalid or missing. Expected format: DD-MM-YYYY (e.g. 15-05-1995).`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${dobStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }
        if (!dojStr || !/^\d{4}-\d{2}-\d{2}$/.test(dojStr)) {
          const rawVal = r[dojCol];
          const msg = `Row ${rowNum} (${name}): Date of Joining "${rawVal || "Empty"}" is invalid or missing. Expected format: DD-MM-YYYY (e.g. 01-01-2024).`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${dojStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }

        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        const dobDate = parseDateFlexible(dobStr);
        const dojDate = parseDateFlexible(dojStr);

        if (dobDate && dobDate > todayEnd) {
          const msg = `Row ${rowNum} (${name}): Date of Birth cannot be in the future.`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${dobStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }
        if (dojDate && dojDate > todayEnd) {
          const msg = `Row ${rowNum} (${name}): Date of Joining cannot be in the future.`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${dojStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }
        if (dobDate && dojDate) {
          let joinAge = dojDate.getFullYear() - dobDate.getFullYear();
          const jm = dojDate.getMonth() - dobDate.getMonth();
          if (jm < 0 || (jm === 0 && dojDate.getDate() < dobDate.getDate())) joinAge--;
          if (joinAge < 18) {
            const msg = `Row ${rowNum} (${name}): Employee must be at least 18 years old on Date of Joining (calculated age on DOJ: ${joinAge} years).`;
            const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${dobStr}", "${dojStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
            onError(msg, diag);
            return;
          }
        }
        if (exitStr) {
          const exitDate = parseDateFlexible(exitStr);
          if (exitDate && dojDate && exitDate < dojDate) {
            const msg = `Row ${rowNum} (${name}): Date of Exit cannot be earlier than Date of Joining.`;
            const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nParsed Value: "${exitStr}", "${dojStr}"\nRow Data:\n${rowDiagnostics.join("\n")}`;
            onError(msg, diag);
            return;
          }
        }

        if (!pan) {
          const msg = `Row ${rowNum} (${name}): PAN Number is required.`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }

        if (pan && !validatePAN(pan)) {
          const msg = `Row ${rowNum} (${name}): Invalid PAN format "${pan}". Must be exactly 10 alphanumeric characters (e.g. ABCDE1234F).`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }

        const basicVal = basicCol >= 0 ? Math.round(Number(r[basicCol])) : 0;
        const vpfVal = vpfCol >= 0 ? Math.round(Number(r[vpfCol] || 0)) : 0;
        const tdsVal = tdsCol >= 0 ? Math.round(Number(r[tdsCol] || 0)) : 0;
        if (basicCol < 0 || isNaN(basicVal) || basicVal <= 0) {
          const msg = `Row ${rowNum} (${name}): Gross Monthly Salary is required and must be greater than ₹0. Received: "${r[basicCol]}".`;
          const diag = `Upload Error Diagnostic:\nFile: ${file?.name}\nError: ${msg}\nRow Data:\n${rowDiagnostics.join("\n")}`;
          onError(msg, diag);
          return;
        }

        let esiForced = esiForcedCol >= 0 ? String(r[esiForcedCol] || "").trim() : "";
        if (esiForced) {
          const match = esiForced.match(/^(\d{1,2})[-/](\d{4})$/);
          if (match) {
            esiForced = `${match[2]}-${match[1].padStart(2, "0")}`;
          } else if (esiForced.match(/^\d{4}-\d{1,2}$/)) {
            const parts = esiForced.split("-");
            esiForced = `${parts[0]}-${parts[1].padStart(2, "0")}`;
          } else if (esiForced instanceof Date) {
            const y = esiForced.getFullYear();
            const m = String(esiForced.getMonth() + 1).padStart(2, "0");
            esiForced = `${y}-${m}`;
          }
        }

        emps.push({
          employee_code: empCode,
          name,
          pan,
          basic: isNaN(basicVal) ? 0 : Math.round(basicVal),
          vpf_pct: vpfCol >= 0 && r[vpfCol] !== "" && r[vpfCol] != null ? Math.round(vpfVal) : null,
          tds_amount: tdsCol >= 0 && r[tdsCol] !== "" && r[tdsCol] != null ? Math.round(tdsVal) : null,
          doj: dojStr,
          dob: dobStr,
          date_of_exit: exitStr,
          custom_basic_pct: customBasicCol >= 0 && r[customBasicCol] !== "" && r[customBasicCol] != null ? Number(r[customBasicCol]) : null,
          custom_hra_pct: customHraCol >= 0 && r[customHraCol] !== "" && r[customHraCol] != null ? Math.round(Number(r[customHraCol])) : null,
          custom_special_allowance: customSpecialCol >= 0 && r[customSpecialCol] !== "" && r[customSpecialCol] != null ? Math.round(Number(r[customSpecialCol])) : null,
          father_name: fatherCol >= 0 ? String(r[fatherCol] || "").trim().slice(0, 100) : "",
          phone: phoneCol >= 0 ? String(r[phoneCol] || "").trim() : "",
          email: emailCol >= 0 ? String(r[emailCol] || "").trim() : "",
          department: deptCol >= 0 ? String(r[deptCol] || "").trim() : "",
          designation: desigCol >= 0 ? String(r[desigCol] || "").trim() : "",
          bank_name: bankNameCol >= 0 ? String(r[bankNameCol] || "").trim() : "",
          bank_account: bankAccCol >= 0 ? String(r[bankAccCol] || "").trim() : "",
          bank_ifsc: ifscCol >= 0 ? String(r[ifscCol] || "").trim() : "",
          uan: uanCol >= 0 ? String(r[uanCol] || "").trim() : "",
          esi_id: esiCol >= 0 ? String(r[esiCol] || "").trim() : "",
          pf_account: pfAccCol >= 0 ? String(r[pfAccCol] || "").trim() : "",
          esi_forced_until: esiForced || null
        });
      }

      if (!emps.length) {
        const msg = "No valid employee rows found in the file. Ensure the template contains employee rows below the header.";
        const diag = `Upload Diagnostic: Empty parsed rows.\nHeader Row: ${headerRow.join(", ")}\nTotal Raw Rows: ${rawRows.length}`;
        onError(msg, diag);
        return;
      }

      onSuccess(emps);
    } catch(err) {
      console.error("[TrustWork parseEmployeeExcel Exception]:", err);
      const diag = `TrustWork Excel Processing Exception:\nMessage: ${err.message}\nStack: ${err.stack || "No stack"}`;
      onError("Failed to read Excel file: " + err.message, diag);
    }
  };
  reader.readAsArrayBuffer(file);
}

// Helper: build company data row for Excel (company info only — no salary structure)
function companyToRow(c) {
  return {
    name:    c.name||"",
    gst:     c.gst||"",
    address: c.address||"",
    contact: c.contact||"",
    phone:   c.phone||"",
    email:   c.email||"",
    website: c.website||"",
  };
}

// ── Download blank template (single company) ─────────────────
function downloadCompanyTemplate() {
  try {
    const b64=COMPANY_TEMPLATE_B64;
    const raw=Uint8Array.from(atob(b64),c=>c.charCodeAt(0));
    const bytes=_twAddXlsxHeader(raw,10);
    const blob=new Blob([bytes],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const a=document.createElement("a");a.href=URL.createObjectURL(blob);
    a.download="TrustWork_Company_Template.xlsx";a.click();URL.revokeObjectURL(a.href);
  }catch(e){alert("Download failed: "+e.message);}
}

function downloadBulkCompanyTemplate() {
  try {
    const b64=BULK_COMPANY_TEMPLATE_B64;
    const raw=Uint8Array.from(atob(b64),c=>c.charCodeAt(0));
    const bytes=_twAddXlsxHeader(raw,10);
    const blob=new Blob([bytes],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const a=document.createElement("a");a.href=URL.createObjectURL(blob);
    a.download="TrustWork_Bulk_Companies_Template.xlsx";a.click();URL.revokeObjectURL(a.href);
  }catch(e){alert("Download failed: "+e.message);}
}


// ── TrustWork xlsx raw-zip helpers (preserves embedded images/drawings) ──
function _twXmlEsc(s){return String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");}
function _twCrc32(data){let c=0xFFFFFFFF;for(let i=0;i<data.length;i++){c^=data[i];for(let j=0;j<8;j++)c=(c>>>1)^(c&1?0xEDB88320:0);}return(c^0xFFFFFFFF)>>>0;}
function _twConcat(arrays){const t=arrays.reduce((s,a)=>s+a.length,0),out=new Uint8Array(t);let p=0;for(const a of arrays){out.set(a,p);p+=a.length;}return out;}

// ── Common header adder for ALL xlsx exports and templates ──────────────────
// Adds HEADER_IMG as a banner image spanning rows 1-5 across numCols columns.
// All existing data rows are shifted down by 5. One change point for all files.
function _twAddXlsxHeader(rawBytes, numCols) {
  const HEADER_ROWS = 5;
  const cols = numCols || 10;
  const colLetter = n => { let s="",x=n+1; while(x){const r=(x-1)%26; s=String.fromCharCode(65+r)+s; x=Math.floor((x-1)/26);} return s; };

  const drawingXml = `<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><xdr:twoCellAnchor editAs="oneCell"><xdr:from><xdr:col>0</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from><xdr:to><xdr:col>${cols}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${HEADER_ROWS}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to><xdr:pic><xdr:nvPicPr><xdr:cNvPr id="2" name="TrustWork Header"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr><xdr:blipFill><a:blip r:embed="rId1"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill><xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic><xdr:clientData/></xdr:twoCellAnchor></xdr:wsDr>`;
  const drawingRelsXml = `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="/xl/media/image1.png" Id="rId1"/></Relationships>`;
  const sheetRelsXml   = `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="/xl/drawings/drawing1.xml" Id="rId1"/></Relationships>`;

  const imgB64 = HEADER_IMG.split(",")[1];
  const imgBytes = Uint8Array.from(atob(imgB64), c => c.charCodeAt(0));

  // Parse ZIP
  const bytes = rawBytes instanceof Uint8Array ? rawBytes : new Uint8Array(rawBytes);
  const view  = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const entries = [];
  let pos = 0;
  while(pos + 30 < bytes.length){
    if(view.getUint32(pos,true) !== 0x04034b50) break;
    const comp=view.getUint16(pos+8,true), csize=view.getUint32(pos+18,true),
          nl=view.getUint16(pos+26,true), el=view.getUint16(pos+28,true);
    const name=new TextDecoder().decode(bytes.subarray(pos+30,pos+30+nl));
    const dstart=pos+30+nl+el;
    let data;
    if(comp===0){data=bytes.slice(dstart,dstart+csize);}
    else{try{data=pako.inflateRaw(bytes.subarray(dstart,dstart+csize));}catch(_){data=bytes.slice(dstart,dstart+csize);}}
    entries.push({name, data: new Uint8Array(data)});
    pos = dstart+csize;
  }

  for(const e of entries){
    if(e.name === "xl/worksheets/sheet1.xml"){
      let xml = new TextDecoder().decode(e.data);

      // ── Shift rows inside <sheetData>...</sheetData> only ──
      // Extract sheetData content, shift row/cell numbers, put back
      xml = xml.replace(/(<sheetData>)([\s\S]*?)(<\/sheetData>)/, (_, open, body, close) => {
        // Shift <row r="N"> → r="N+5"
        body = body.replace(/<row r="(\d+)"/g, (m, n) => `<row r="${+n + HEADER_ROWS}"`);
        // Shift cell refs like r="A2" → r="A7"  (only digits at end)
        body = body.replace(/\br="([A-Z]+)(\d+)"/g, (m, col, row) => `r="${col}${+row + HEADER_ROWS}"`);
        return open + body + close;
      });

      // Prepend 5 header rows into sheetData
      let hdrRows = `<row r="1" ht="60" customHeight="1"><c r="A1" s="1"/></row>`;
      for(let r=2; r<=HEADER_ROWS; r++) hdrRows += `<row r="${r}" ht="10" customHeight="1"><c r="A${r}" s="1"/></row>`;
      xml = xml.replace('<sheetData>', `<sheetData>${hdrRows}`);

      // Add drawing ref before </worksheet>
      xml = xml.replace('</worksheet>', `<drawing xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId1"/></worksheet>`);

      // Update dimension ref
      const allRowNums = [...xml.matchAll(/<row r="(\d+)"/g)].map(m=>+m[1]);
      const maxRow = allRowNums.length ? Math.max(...allRowNums) : HEADER_ROWS + 1;
      xml = xml.replace(/<dimension ref="[^"]*"\/>/, `<dimension ref="A1:${colLetter(cols-1)}${maxRow}"/>`);

      e.data = new TextEncoder().encode(xml);
    }

    if(e.name === "[Content_Types].xml"){
      let xml = new TextDecoder().decode(e.data);
      if(!xml.includes('Extension="png"'))
        xml = xml.replace('</Types>', '<Default Extension="png" ContentType="image/png"/></Types>');
      if(!xml.includes('drawing1.xml'))
        xml = xml.replace('</Types>', '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>');
      e.data = new TextEncoder().encode(xml);
    }
  }

  // Add drawing/image entries
  entries.push({name:"xl/media/image1.png",                   data: imgBytes});
  entries.push({name:"xl/drawings/drawing1.xml",              data: new TextEncoder().encode(drawingXml)});
  entries.push({name:"xl/drawings/_rels/drawing1.xml.rels",   data: new TextEncoder().encode(drawingRelsXml)});
  entries.push({name:"xl/worksheets/_rels/sheet1.xml.rels",   data: new TextEncoder().encode(sheetRelsXml)});

  // Repack ZIP (uncompressed)
  const parts=[],cd=[];let off=0;
  for(const e of entries){
    const nb=new TextEncoder().encode(e.name), crc=_twCrc32(e.data), d=e.data;
    const lh=new Uint8Array(30+nb.length); const lv=new DataView(lh.buffer);
    lv.setUint32(0,0x04034b50,true);lv.setUint16(4,20,true);lv.setUint16(8,0,true);
    lv.setUint32(14,crc,true);lv.setUint32(18,d.length,true);lv.setUint32(22,d.length,true);
    lv.setUint16(26,nb.length,true); lh.set(nb,30);
    parts.push(lh,d);
    const ce=new Uint8Array(46+nb.length); const cv=new DataView(ce.buffer);
    cv.setUint32(0,0x02014b50,true);cv.setUint16(4,20,true);cv.setUint16(6,20,true);
    cv.setUint32(16,crc,true);cv.setUint32(20,d.length,true);cv.setUint32(24,d.length,true);
    cv.setUint16(28,nb.length,true);cv.setUint32(42,off,true); ce.set(nb,46);
    cd.push(ce); off+=lh.length+d.length;
  }
  const cdb=_twConcat(cd), eocd=new Uint8Array(22); const ev=new DataView(eocd.buffer);
  ev.setUint32(0,0x06054b50,true);ev.setUint16(8,entries.length,true);ev.setUint16(10,entries.length,true);
  ev.setUint32(12,cdb.length,true);ev.setUint32(16,off,true);
  return _twConcat([...parts.map(p=>p),cdb,eocd]);
}
function _twPatchXlsx(rawBytes,replacements){
  // Parse ZIP, patch sheet1.xml text replacements, repack - image/drawing entries untouched
  const bytes=rawBytes instanceof Uint8Array?rawBytes:new Uint8Array(rawBytes);
  const view=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  const entries=[];
  let pos=0;
  while(pos+30<bytes.length){
    if(view.getUint32(pos,true)!==0x04034b50)break;
    const comp=view.getUint16(pos+8,true),csize=view.getUint32(pos+18,true),
          usize=view.getUint32(pos+22,true),nl=view.getUint16(pos+26,true),el=view.getUint16(pos+28,true);
    const name=new TextDecoder().decode(bytes.subarray(pos+30,pos+30+nl));
    const dstart=pos+30+nl+el;
    let data;
    if(comp===0){data=bytes.slice(dstart,dstart+csize);}
    else{try{data=pako.inflateRaw(bytes.subarray(dstart,dstart+csize));}catch(_){data=bytes.slice(dstart,dstart+csize);}}
    entries.push({name,data:new Uint8Array(data)});
    pos=dstart+csize;
  }
  for(const e of entries){
    if(e.name==="xl/worksheets/sheet1.xml"){
      let xml=new TextDecoder().decode(e.data);
      for(const[ph,val]of Object.entries(replacements))xml=xml.split(ph).join(val);
      e.data=new TextEncoder().encode(xml);
    }
  }
  // Repack
  const parts=[],cd=[];let off=0;
  for(const e of entries){
    const nb=new TextEncoder().encode(e.name),crc=_twCrc32(e.data),d=e.data;
    const lh=new Uint8Array(30+nb.length);const lv=new DataView(lh.buffer);
    lv.setUint32(0,0x04034b50,true);lv.setUint16(4,20,true);lv.setUint16(8,0,true);
    lv.setUint32(14,crc,true);lv.setUint32(18,d.length,true);lv.setUint32(22,d.length,true);
    lv.setUint16(26,nb.length,true);lh.set(nb,30);
    parts.push(lh,d);
    const ce=new Uint8Array(46+nb.length);const cv=new DataView(ce.buffer);
    cv.setUint32(0,0x02014b50,true);cv.setUint16(4,20,true);cv.setUint16(6,20,true);
    cv.setUint32(16,crc,true);cv.setUint32(20,d.length,true);cv.setUint32(24,d.length,true);
    cv.setUint16(28,nb.length,true);cv.setUint32(42,off,true);ce.set(nb,46);
    cd.push(ce);off+=lh.length+d.length;
  }
  const cdb=_twConcat(cd),eocd=new Uint8Array(22);const ev=new DataView(eocd.buffer);
  ev.setUint32(0,0x06054b50,true);ev.setUint16(8,entries.length,true);ev.setUint16(10,entries.length,true);
  ev.setUint32(12,cdb.length,true);ev.setUint32(16,off,true);
  return _twConcat([...(parts.map(p=>p)),cdb,eocd]);
}
function _twDownloadXlsx(bytes,filename){
  const blob=new Blob([bytes],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
  const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=filename;a.click();URL.revokeObjectURL(a.href);
}

function exportSingleCompanyExcel(company) {
  try {
    const cfg = company.config || {};
    const yn = v => v ? "YES" : "NO";
    const rows = [
      ["Company Name",        company.name||""],
      ["Contact Person",      company.contact||""],
      ["Phone",               company.phone||""],
      ["Email",               company.email||""],
      ["GST Number",          company.gst||""],
      ["Address",             company.address||""],
      ["Website",             company.website||""],
      ["PF Reg No.",          company.pf_number||""],
      ["ESI Code",            company.esi_code||""],
      ["PT Registration",     company.pt_reg||""],
      ["HRA",                 cfg.hra_type==="fixed"?"₹"+cfg.hra_value:(cfg.hra_value!==undefined?cfg.hra_value:cfg.hra_pct)+"%"],
      ["Working Days/Month",  String(cfg.working_days||26)],
      ["PF Applicable",       yn(cfg.pf_applicable)],
      ["PF Wage Cap 25000",   yn(cfg.pf_wage_cap!==false)],
      ["ESI Applicable",      yn(cfg.esi_applicable)],
      ["Special Allowance",   yn(cfg.special_allowance)],
      ["Special Allow Type",  cfg.special_allowance_type||"fixed"],
      ["Special Allow Value", String(cfg.special_allowance_value||0)],
      ["Conveyance",          yn(cfg.conveyance)],
      ["Conveyance Amount",   String(cfg.conveyance_amount||0)],
      ["Medical Allowance",   yn(cfg.medical)],
      ["Medical Amount",      String(cfg.medical_amount||0)],
      ["Bonus",               yn(cfg.bonus)],
      ["Bonus %",             String(cfg.bonus_pct||0)],
      ["Bonus Months",        (cfg.bonus_months||[]).join(",")],
      ["OT Applicable",       yn(cfg.ot_applicable)],
      ["OT Basis",            cfg.ot_basis==="gross"?"Gross Salary":"Basic+DA"],
      ["Absence Deduction",   yn(cfg.absence_deduction)],
      ["Absence Deduct From", cfg.absence_basis||"basic"],
    ];
    const b64 = SINGLE_COMPANY_DETAILS_TEMPLATE_B64;
    const raw = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    let rowsXml = "";
    rows.forEach(([field, val], i) => {
      const r = i + 2;
      rowsXml += `<row r="${r}" ht="18" customHeight="1"><c r="A${r}" t="inlineStr"><is><t>${_twXmlEsc(field)}</t></is></c><c r="B${r}" t="inlineStr"><is><t>${_twXmlEsc(val)}</t></is></c></row>`;
    });
    const lastRow = rows.length + 1;
    const patched = _twPatchXlsx(raw, {
      '<row r="2" ht="18" customHeight="1"><c r="A2" s="0" t="inlineStr"><is><t>__ROWS__</t></is></c></row>': rowsXml,
      'ref="A1:A2"': `ref="A1:B${lastRow}"`,
    });
    _twDownloadXlsx(patched, (company.name||"Company").replace(/[^a-zA-Z0-9_\-]/g,"_")+"_Details.xlsx");
  } catch(e) { alert("Export failed: "+e.message); }
}
function exportSingleCompanyPDF(company, empCount=0) {
  const doc = new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
  const W=210, M=16;
  const cfg = company.config||{};
  const yn = v => v ? "YES" : "NO";

  // Header image
  let y = addDocHeader(doc, W, 297, company) + 4;
  doc.setFontSize(13); doc.setFont("helvetica","bold"); doc.setTextColor(232,234,240);
  doc.text("COMPANY PROFILE",M,y); y+=7;
  doc.setFontSize(8); doc.setFont("helvetica","normal"); doc.setTextColor(155,168,200);
  doc.text(new Date().toLocaleDateString("en-IN"),M,y); y+=6;
  const section = (title) => {
    doc.setFillColor(15,20,32); doc.rect(M,y,W-M*2,8,"F");
    doc.setTextColor(79,142,247); doc.setFontSize(8); doc.setFont("helvetica","bold");
    doc.text(title.toUpperCase(),M+3,y+5.5); y+=11;
  };
  const row2 = (l1,v1,l2,v2) => {
    doc.setFont("helvetica","bold"); doc.setTextColor(107,122,158); doc.setFontSize(8);
    doc.text(l1,M,y); doc.text(l2||"",M+90,y);
    doc.setFont("helvetica","bold"); doc.setTextColor(20,30,60);
    doc.text(String(v1||"—"),M+45,y); doc.text(String(v2||""),M+135,y);
    y+=7;
  };

  section("Basic Information");
  row2("Company Name",company.name,"Contact Person",company.contact||"—");
  row2("Phone",company.phone||"—","Email",company.email||"—");
  row2("Address",company.address||"—","Employees",empCount+" registered");
  if(company.website) row2("Website",company.website,"","");
  y+=4;

  if(company.config) {
    section("Salary Structure");
    row2("HRA", cfg.hra_type==="fixed"?"₹"+cfg.hra_value:(cfg.hra_value!==undefined?cfg.hra_value:cfg.hra_pct)+"%", "Working Days/Month", cfg.working_days||"—");
    row2("PF Applicable",yn(cfg.pf_applicable),"ESI Applicable",yn(cfg.esi_applicable));
    if(cfg.da) row2("DA — Dearness Allowance","YES — "+cfg.da_pct+"% of Basic","","");
    if(cfg.special_allowance) row2("Special Allowance","YES — "+(cfg.special_allowance_type==="pct"?cfg.special_allowance_value+"%":"₹"+cfg.special_allowance_value),"","");
    if(cfg.lta) row2("LTA","YES — "+cfg.lta_pct+"% of Basic","","");
    if(cfg.conveyance) row2("Conveyance","YES — ₹"+cfg.conveyance_amount,"","");
    if(cfg.medical) row2("Medical Allowance","YES — ₹"+cfg.medical_amount,"","");
    if(cfg.bonus) row2("Bonus","YES — "+cfg.bonus_pct+"%","Bonus Months",(cfg.bonus_months||[]).join(", ")||"—");
    if(cfg.ot_applicable) row2("Overtime",`YES — Basis: ${cfg.ot_basis==="gross"?"Gross":"Basic+DA"} (${cfg.ot_rate_multiplier===2?"2x":"1x"})`,"","");
    if(cfg.absence_deduction) row2("Absence Deduction","YES — from "+cfg.absence_basis,"","");
    if(cfg.tds_applicable) row2("TDS","Enabled — entered per employee monthly","","");
    y+=4;
  } else {
    section("Salary Structure");
    doc.setTextColor(107,122,158); doc.setFontSize(9); doc.setFont("helvetica","italic");
    doc.text("Not configured yet.",M,y); y+=10;
  }

  addDocFooter(doc, W, doc.internal.pageSize.height);
  doc.save((company.name||"Company").replace(/\s+/g,"_")+"_Profile.pdf");
}

// ── Export ALL companies to Excel (company info only, styled) ─────────────────
function exportAllCompaniesExcel(companies) {
  try {
    const exportDate = new Date().toLocaleDateString("en-IN");
    const b64 = ALL_COMPANIES_EXCEL_TEMPLATE_B64;
    const raw = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const KEYS = ["name","contact","phone","email","gst","address","website"];
    const COLS = ["A","B","C","D","E","F","G"];
    // Build row XML for data rows
    let rowsXml = "";
    companies.forEach((c, ri) => {
      const r = 2 + ri;
      const s = (ri % 2 === 0) ? "4" : "5";
      let cells = "";
      KEYS.forEach((k, ci) => {
        cells += `<c r="${COLS[ci]}${r}" s="${s}" t="inlineStr"><is><t>${_twXmlEsc(c[k]||"")}</t></is></c>`;
      });
      rowsXml += `<row r="${r}" ht="20" customHeight="1">${cells}</row>`;
    });
    const lastRow = Math.max(2, 1 + companies.length);
    const subtitle = _twXmlEsc(exportDate + " | " + companies.length + " companies");
    const patched = _twPatchXlsx(raw, {
      "__SUBTITLE__": subtitle,
      '<row r="2" ht="20" customHeight="1"><c r="A2" s="4" t="inlineStr"><is><t>__ROWS__</t></is></c></row>': rowsXml,
      'ref="A1:G2"': `ref="A1:G${lastRow}"`,
    });
    _twDownloadXlsx(_twAddXlsxHeader(patched,7), "All_Companies_Export.xlsx");
  } catch(e) { alert("Export failed: "+e.message); }
}


// ── Export ALL companies to PDF (company info only) ─────────────────
function exportAllCompaniesPDF(companies, empMap) {
  const doc = new jsPDF({orientation:"landscape",unit:"mm",format:"a4"});
  const W=297, M=14;

  // Cover page
  doc.setFillColor(15,17,23); doc.rect(0,0,W,210,"F");
  addDocHeader(doc, W, 210);
  doc.setFillColor(26,37,80); doc.roundedRect(M,75,W-M*2,55,4,4,"F");
  doc.setTextColor(79,142,247); doc.setFontSize(22); doc.setFont("helvetica","bold");
  doc.text("Company Portfolio",W/2,98,{align:"center"});
  doc.setTextColor(232,234,240); doc.setFontSize(13); doc.setFont("helvetica","normal");
  doc.text(companies.length+" Companies",W/2,112,{align:"center"});
  doc.setTextColor(107,122,158); doc.setFontSize(8.5);
  doc.text("Generated: "+new Date().toLocaleDateString("en-IN"),W/2,123,{align:"center"});

  companies.forEach((c, idx) => {
    doc.addPage();
    const empCount = (empMap[c.id]||[]).length;

    // Header image
    addDocHeader(doc, W, 210);
    let y = 26;
    doc.setFontSize(12); doc.setFont("helvetica","bold"); doc.setTextColor(232,234,240);
    doc.text((idx+1)+". "+c.name, M, y);
    doc.setTextColor(155,168,200); doc.setFontSize(8); doc.setFont("helvetica","normal");
    doc.text("Employees: "+empCount, W-M, y, {align:"right"});
    y += 8;
    // Section label
    doc.setFillColor(22,26,39); doc.rect(M,y,W-M*2,7,"F");
    doc.setTextColor(79,142,247); doc.setFontSize(7.5); doc.setFont("helvetica","bold");
    doc.text("COMPANY INFORMATION",M+2,y+5); y+=12;

    const field = (label, value, cx, rowY) => {
      doc.setFont("helvetica","bold"); doc.setTextColor(107,122,158); doc.setFontSize(7);
      doc.text(label,cx,rowY);
      doc.setFont("helvetica","bold"); doc.setTextColor(20,30,60); doc.setFontSize(9);
      doc.text(String(value||"—"),cx,rowY+5);
    };

    // Row 1: Name, Contact, Phone
    field("Company Name",c.name,M,y);
    field("Contact Person",c.contact||"—",M+90,y);
    field("Phone",c.phone||"—",M+180,y);
    y+=14;
    // Row 2: Email, Address, Website
    field("Email",c.email||"—",M,y);
    field("Address",c.address||"—",M+90,y);
    if(c.website){ field("Website",c.website,M+180,y); }
    y+=14;

    // Divider
    doc.setDrawColor(30,33,48); doc.setLineWidth(0.3);
    doc.line(M,y,W-M,y); y+=6;

    // Footer
    addDocFooter(doc, W, 210);
    doc.setFontSize(6.5); doc.setTextColor(120,130,150); doc.setFont("helvetica","normal");
    doc.text("Page "+(idx+2), W-M, 204, {align:"right"});
  });

  doc.save("TrustWork_All_Companies_"+new Date().toLocaleDateString("en-IN").replace(/\//g,"-")+".pdf");
}

// ── Salary Structure Excel: download template for a company
function downloadSalaryStructureTemplate(company) {
  try {
    const b64=SALARY_STRUCTURE_TEMPLATE_B64;
    const raw=Uint8Array.from(atob(b64),c=>c.charCodeAt(0));
    const bytes=_twAddXlsxHeader(raw,10);
    const name=(company?.name||"TrustWork").replace(/[^a-zA-Z0-9]/g,"_");
    const blob=new Blob([bytes],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const a=document.createElement("a");a.href=URL.createObjectURL(blob);
    a.download=name+"_SalaryStructure.xlsx";a.click();URL.revokeObjectURL(a.href);
  }catch(e){alert("Download failed: "+e.message);}
}

// ── Salary Structure Excel: parse uploaded file
function parseSalaryStructureExcel(file, onSuccess, onError) {
  const reader = new FileReader();
  reader.onload = e=>{
    try {
      const wb = XLSX.read(e.target.result,{type:"array"});
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws,{header:1,defval:""});
      const bool = v=>String(v||"").trim().toUpperCase()==="YES";
      const num  = v=>parseFloat(v)||0;
      const map = {};
      rows.forEach(r=>{ if(r[0]&&r[1]!=="") map[String(r[0]).trim().toLowerCase()] = r[1]; });
      const g = k=>{ const found=Object.keys(map).find(mk=>mk.includes(k.toLowerCase())); return found?map[found]:undefined; };
      const cfg = {
        _configured: true,
        hra_type: String(g("hra_type")||"pct").toLowerCase(),
        hra_value: num(g("hra_value")) || num(g("hra")),
        hra_pct: num(g("hra")),
        da: g("da applicable")!==undefined?bool(g("da applicable")):(num(g("da %"))||num(g("da pct")))>0,
        da_pct: num(g("da %"))||num(g("da pct"))||0,
        working_days: num(g("working days")),
        pf_applicable: g("pf applicable")!==undefined?bool(g("pf applicable")):true,
        pf_wage_cap: g("pf wage cap")!==undefined?bool(g("pf wage cap")):true,
        esi_applicable: g("esi applicable")!==undefined?bool(g("esi applicable")):true,
        special_allowance: bool(g("special allowance")),
        special_allowance_type: String(g("special allow type")||"fixed").trim().toLowerCase(),
        special_allowance_value: num(g("special allow value")),
        conveyance: bool(g("conveyance")),
        conveyance_amount: num(g("conveyance amount")),
        medical: bool(g("medical allowance")),
        medical_amount: num(g("medical amount")),
        bonus: bool(g("bonus")),
        bonus_pct: num(g("bonus %")),
        bonus_months: String(g("bonus months")||"").split(",").map(s=>parseInt(s)).filter(n=>!isNaN(n)&&n>0),
        ot_applicable: bool(g("ot applicable")),
        ot_rate: num(g("ot rate")),
        absence_deduction: bool(g("absence deduction")),
        absence_basis: String(g("absence deduct from")||"basic").trim().toLowerCase(),
      };
      if(!cfg.hra_value && !cfg.hra_pct && !cfg.working_days){ onError("Could not read salary structure fields. Please use the template."); return; }
      onSuccess(cfg);
    } catch(err){ onError("Parse error: "+err.message); }
  };
  reader.readAsArrayBuffer(file);
}


function parseCompanyExcel(file, onSuccess, onError) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(e.target.result, {type:"array"});

      // ── Detect bulk upload (has "All Companies" sheet)
      if(wb.SheetNames.includes("All Companies")) {
        const ws = wb.Sheets["All Companies"];
        const bulkKeys = wb.Sheets["_bulk_keys"];
        const rawData = XLSX.utils.sheet_to_json(ws, {header:1, defval:""});
        if(rawData.length < 2) { onError("No company data found in 'All Companies' sheet."); return; }

        // Find the actual header row (look for "Company Name" in col 0)
        // Handles both SheetJS exports (row 0 = headers) and openpyxl templates (row 0 = title, row 1 = headers)
        const HEADER_MAP = {
          "Company Name":"name","GST":"gst","Address":"address",
          "Contact":"contact","Contact Person":"contact",
          "Phone":"phone","Email":"email","HRA%":"hra_pct",
          "PF":"pf_applicable","PF (YES/NO)":"pf_applicable",
          "ESI":"esi_applicable","ESI (YES/NO)":"esi_applicable",
          "Sp.Allow":"special_allowance","Special Allowance (YES/NO)":"special_allowance",
          "Sp.Allow Type (fixed/pct)":"special_allowance_type",
          "Sp.Allow Value":"special_allowance_value",
          "Conv":"conveyance","Conveyance (YES/NO)":"conveyance",
          "Conv Amt":"conveyance_amount","Conveyance Amt ₹":"conveyance_amount",
          "Medical":"medical","Medical (YES/NO)":"medical",
          "Med Amt":"medical_amount","Medical Amt ₹":"medical_amount",
          "Bonus":"bonus","Bonus (YES/NO)":"bonus",
          "Bonus%":"bonus_pct","Bonus %":"bonus_pct","Bonus Months":"bonus_months",
          "OT":"ot_applicable","OT (YES/NO)":"ot_applicable",
          "OT Rate":"ot_rate","OT Rate/hr ₹":"ot_rate",
          "Abs.Ded":"absence_deduction","Absence Deduction (YES/NO)":"absence_deduction",
          "Abs.Basis":"absence_basis","Deduct From":"absence_basis",
          "Work Days":"working_days","Working Days":"working_days",
        };
        let headerRowIdx = 0;
        const stripStar2 = s => String(s||"").replace(/^[★\s]+/,"").trim();
        for(let i=0;i<Math.min(rawData.length,8);i++){
          const firstCell = stripStar2(rawData[i][0]);
          // Skip title/tagline/contact/padding rows and the REQUIRED tag row
          if(firstCell && firstCell!=="REQUIRED" && firstCell!=="optional" && HEADER_MAP[firstCell]){
            headerRowIdx=i; break;
          }
        }
        const headerRow = rawData[headerRowIdx].map(h=>stripStar2(h));
        const data = rawData.slice(headerRowIdx+1).filter(r=>!["REQUIRED","optional",""].includes(stripStar2(r[0])));

        // Build col index → field key (prefer _bulk_keys if present, else use header row)
        const colToKey = {};
        if(bulkKeys){
          const kr = XLSX.utils.sheet_to_json(bulkKeys, {header:1})[0]||[];
          for(let i=0;i<kr.length;i+=2){ if(kr[i+1]) colToKey[Math.floor(i/2)] = String(kr[i+1]); }
        } else {
          headerRow.forEach((h,i) => { if(h&&HEADER_MAP[h]) colToKey[i]=HEADER_MAP[h]; });
        }

        const bool = (v,def=false) => { if(v===undefined||v==="") return def; if(typeof v==="boolean") return v; return String(v).toLowerCase().trim()==="yes"; };
        const num = (v,def=0) => { const n=parseFloat(v); return isNaN(n)?def:n; };

        const companies = data.filter(r=>r[0]&&String(r[0]).trim()).map(r=>{
          const p = {};
          Object.entries(colToKey).forEach(([ci,key])=>{ p[key]=r[parseInt(ci)]; });
          const rawPhone = String(p.phone||"").trim();
          const p10 = getPhone10(rawPhone);
          return {
            name: String(p.name||"").trim().slice(0, 100),
            gst: String(p.gst||"").trim(),
            address: String(p.address||"").trim(),
            website: String(p.website||"").trim(),
            contact: String(p.contact||"").trim().slice(0, 100),
            phone: p10 ? `+91 ${p10}` : rawPhone,
            email: String(p.email||"").trim(),
            config: null,
          };
        }).filter(c=>c.name && c.name.trim());

        // Validate each row — all 3 mandatory fields required
        const invalid = companies.filter(c => {
          const p10 = getPhone10(c.phone);
          return !c.contact?.trim() || !p10 || p10.length !== 10 || !/^[6-9][0-9]{9}$/.test(p10);
        });
        if(invalid.length){
          const names = invalid.map(c=>c.name).join(", ");
          onError("Missing or invalid required fields (Contact Person and valid 10-digit Phone starting with 6–9) for: "+names+". All 3 fields — Company Name, Contact Person and Phone — are mandatory.");
          return;
        }
        if(!companies.length){ onError("No valid companies found. Make sure Company Name, Contact Person and Phone columns are filled."); return; }
        onSuccess(companies, true); // true = bulk
        return;
      }

      // ── Single company upload ─────────────────────────────
      const ws = wb.Sheets["Company Info"] || wb.Sheets["Company"] || wb.Sheets[wb.SheetNames[0]];
      const wsKeys = wb.Sheets["_keys"];
      if(!ws) { onError("No sheet found. Please use the TrustWork template."); return; }

      const stripStar = s => String(s||"").replace(/[★\*\s]+$/,"").replace(/^[★\*\s]+/,"").trim();
      // Hardcoded fallback map — covers both old and new template label variants
      const FALLBACK_MAP = {
        "Company Name":"name","Company Name *":"name","Company Name*":"name",
        "GST Number":"gst","GST":"gst",
        "Address":"address","Website":"website",
        "Contact Person":"contact","Contact Person *":"contact","Contact Person*":"contact",
        "Phone":"phone","Phone *":"phone","Phone*":"phone",
        "Email":"email","Email *":"email","Email*":"email",
        "PF Reg No.":"pf_reg","PF Reg No":"pf_reg","PT Registration":"pt_reg","ESI Code":"esi_code",
        "HRA % of Basic":"hra_pct","HRA %":"hra_pct","HRA":"hra_pct",
        "Working Days / Month":"working_days","Working Days per Month":"working_days","Working Days":"working_days",
        "PF Applicable":"pf_applicable","PF Applicable (YES/NO)":"pf_applicable",
        "PF Wage Cap 25000":"pf_wage_cap","PF Wage Cap":"pf_wage_cap",
        "ESI Applicable":"esi_applicable","ESI Applicable (YES/NO)":"esi_applicable",
        "Special Allowance":"special_allowance","Special Allowance (YES/NO)":"special_allowance",
        "Special Allow Type":"special_allowance_type","Sp. Allow Type":"special_allowance_type","Sp. Allow Type (fixed/pct)":"special_allowance_type",
        "Special Allow Value":"special_allowance_value","Special Allowance Value":"special_allowance_value","Special Allow Value":"special_allowance_value",
        "Conveyance":"conveyance","Conveyance (YES/NO)":"conveyance",
        "Conveyance Amount":"conveyance_amount","Conveyance Amount ₹":"conveyance_amount","Conveyance Amt ₹":"conveyance_amount","Conveyance Amount (₹)":"conveyance_amount",
        "Medical Allowance":"medical","Medical Allowance (YES/NO)":"medical",
        "Medical Amount":"medical_amount","Medical Amount ₹":"medical_amount","Medical Amt ₹":"medical_amount","Medical Amount (₹)":"medical_amount",
        "Bonus":"bonus","Bonus Applicable":"bonus","Bonus (YES/NO)":"bonus",
        "Bonus %":"bonus_pct","Bonus Percent":"bonus_pct",
        "Bonus Months":"bonus_months","Bonus Months (e.g. 3,9)":"bonus_months",
        "OT Applicable":"ot_applicable","OT Applicable (YES/NO)":"ot_applicable",
        "OT Rate/Hour":"ot_rate","OT Rate per Hour ₹":"ot_rate","OT Rate/Hour ₹":"ot_rate","OT Rate per Hour (₹)":"ot_rate",
        "Absence Deduction":"absence_deduction","Absence Deduction (YES/NO)":"absence_deduction",
        "Absence Deduct From":"absence_basis","Deduct From (basic/gross)":"absence_basis","Deduct From":"absence_basis","Absence Basis":"absence_basis",
      };
      const labelToKey = {...FALLBACK_MAP};
      if(wsKeys) {
        XLSX.utils.sheet_to_json(wsKeys,{header:1}).slice(1).forEach(row=>{
          if(row[0]&&row[1]) labelToKey[stripStar(row[0])]=String(row[1]).trim();
          if(row[2]&&row[3]) labelToKey[stripStar(row[2])]=String(row[3]).trim();
        });
      }

      const parsed = {};
      const HINT_PREFIXES = ["↓","Fill if","e.g.","fill","select","type"];
      const isHint = v => v===""||v===undefined||v===null||HINT_PREFIXES.some(p=>String(v).trim().toLowerCase().startsWith(p.toLowerCase()));

      const rows = XLSX.utils.sheet_to_json(ws,{header:1,defval:""});

      // Detect layout: if row 0 has recognizable header labels → horizontal layout
      const headerRow = rows[0] || [];
      const isHorizontal = headerRow.some(h => labelToKey[stripStar(String(h||""))]);

      if(isHorizontal){
        // Horizontal: row 0 = headers, row 1+ = data rows (use first data row)
        const dataRow = rows[1] || [];
        headerRow.forEach((hdr, idx) => {
          const key = labelToKey[stripStar(String(hdr||""))];
          if(key && !isHint(dataRow[idx]) && dataRow[idx]!=="") {
            parsed[key] = dataRow[idx];
          }
        });
      } else {
        // Vertical: each row is [label, value] or [label, value, label, value]
        rows.forEach(row=>{
          const tryPairs = [
            [stripStar(row[1]), row[2], stripStar(row[4]), row[5]],  // new layout B/C/E/F
            [stripStar(row[0]), row[1], stripStar(row[2]), row[3]],  // old layout A/B/C/D
          ];
          for(const [l1,v1,l2,v2] of tryPairs) {
            if(l1&&labelToKey[l1]&&!isHint(v1)&&v1!=="") { parsed[labelToKey[l1]]=v1; }
            if(l2&&labelToKey[l2]&&!isHint(v2)&&v2!=="") { parsed[labelToKey[l2]]=v2; }
            if(l1&&labelToKey[l1]) break;
          }
        });
      }

      if(!parsed.name){ onError("Company Name is required. Please fill the ★ Company Name field."); return; }
      if(!parsed.contact?.toString().trim()){ onError("Contact Person is required. Please fill the ★ Contact Person field."); return; }
      const singlePhone10 = getPhone10(parsed.phone);
      if(!singlePhone10 || singlePhone10.length !== 10 || !/^[6-9][0-9]{9}$/.test(singlePhone10)){
        onError("Valid 10-digit Phone number starting with 6–9 is required. Please fill the ★ Phone field."); return;
      }

      onSuccess([{
        name:String(parsed.name||"").trim().slice(0, 100), gst:String(parsed.gst||"").trim(),
        address:String(parsed.address||"").trim(), website:String(parsed.website||"").trim(),
        contact:String(parsed.contact||"").trim().slice(0, 100), phone:`+91 ${singlePhone10}`,
        email:String(parsed.email||"").trim(), config:null,
      }], false); // false = single
    } catch(err) {
      onError("Could not read file: "+err.message);
    }
  };
  reader.readAsArrayBuffer(file);
}


// ── Export master Excel/CSV (uses ExcelJS for freeze pane + styling support)
async function exportMasterCSV(emps, salMap, company, month, year) {
  try {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];


  const fmtDate = (val) => {
    if (!val) return "";
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return val;
  };

  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Payroll Master");

  // Column widths
  ws.columns = [
    {width:6},{width:13},{width:26},{width:13},{width:25},{width:17},{width:9},{width:19},{width:15},{width:15},
    {width:15},{width:7},{width:7},{width:11},{width:15},{width:11},{width:11},{width:11},{width:11},{width:13},
    {width:15},{width:13},{width:11},{width:11},{width:11},{width:11},{width:13},{width:13},{width:11},{width:11},
    {width:11},{width:11},{width:11},{width:11},{width:13},{width:15},{width:17}
  ];

  // Freeze panes: top 6 rows + first 3 columns (Sl No, Emp ID, Employee Name)
  ws.views = [{ state: "frozen", xSplit: 3, ySplit: 6, activeCell: "D7" }];

  const thinBorder  = { style: "thin",   color: { argb: "FF000000" } };
  const thickBorder = { style: "medium", color: { argb: "FF000000" } };
  const allBorders  = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
  const topThick    = { top: thickBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

  // Indian number format: #,##,##0
  const INR_FMT = '#,##,##0';
  const NUM_COLS = new Set([...Array.from({length:22}, (_,i)=>i+14), 35]); // cols 15-36 are numbers

  const applyCell = (row, col, value, opts = {}) => {
    const cell = ws.getCell(row, col);
    cell.value = value;
    cell.font = { bold: opts.bold || false, name: "Arial", size: 10, color: { argb: opts.fontColor || "FF000000" } };
    cell.alignment = { horizontal: opts.align || "center", vertical: "middle", wrapText: true };
    if (opts.border) cell.border = opts.topThick ? topThick : allBorders;
    if (opts.fill) cell.fill = opts.fill;
    // Apply Indian number format to numeric salary columns (col index 1-based)
    if (typeof value === 'number' && NUM_COLS.has(col - 1)) {
      cell.numFmt = INR_FMT;
    }
    return cell;
  };

  const grayFill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  const blueFill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
  const greenFill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  const redFill    = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
  const row1Fill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } }; // light gray stripe
  const row2Fill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }; // white stripe
  const totalFill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } }; // elegant light gray for totals

  // 3 empty rows - using row 1 for company, row 2 for config rates, row 3 for month
  ws.getRow(1).height = 26;
  ws.getRow(2).height = 20;
  ws.getRow(3).height = 22;

  // Row 1: Company Name
  ws.mergeCells(1, 1, 1, 10);
  const cell1 = ws.getCell(1, 1);
  cell1.value = company.name || "Company Name";
  cell1.font = { name: "Arial", size: 16, bold: true, color: { argb: "FF0F172A" } };
  cell1.alignment = { horizontal: "left", vertical: "middle" };

  // Row 3: Month & Year
  ws.mergeCells(3, 1, 3, 10);
  const cell3 = ws.getCell(3, 1);
  cell3.value = `Payroll Register - ${MONTHS_L[month]} ${year}`;
  cell3.font = { name: "Arial", size: 12, bold: true };
  cell3.alignment = { horizontal: "left", vertical: "middle" };

  const c = company.config || {};
  const hra_type = c.hra_type || "pct";
  const hra_val = c.hra_value !== undefined ? c.hra_value : (c.hra_pct || 40);
  const esiPct = c.esi_applicable ? 0.75 : 0;
  const pfPct = c.pf_applicable ? 12 : 0;

  applyCell(2, 2, "Config Rates:", { bold: true, align: "right" });
  applyCell(2, 4, hra_type==="fixed"?"HRA:":"HRA %:", { bold: true, align: "right" });
  applyCell(2, 5, hra_type==="fixed"?hra_val:hra_val / 100, { bold: true, align: "left" });
  if(hra_type==="pct") ws.getCell(2, 5).numFmt = "0%";
  applyCell(2, 6, "ESI %:", { bold: true, align: "right" });
  applyCell(2, 7, esiPct / 100, { bold: true, align: "left" });
  ws.getCell(2, 7).numFmt = "0.00%";
  applyCell(2, 8, "PF %:", { bold: true, align: "right" });
  applyCell(2, 9, pfPct / 100, { bold: true, align: "left" });
  ws.getCell(2, 9).numFmt = "0%";

  // ── Row 4 + 5: Category headers
  ws.getRow(4).height = 22;
  ws.getRow(5).height = 22;

  const singleCols = [
    ["Sl No",1],["Emp ID",2],["Employee Name",3],["Date of Birth",4],
    ["Father/Husband Name",5],["Date of Joining",6],["Gender",7],
    ["Designation",8],["ESI Number",9],["PF Number",10],["UAN",11],
    ["WP",12],["NWD",13],["Att Bonus",14],["NET PAID",36],["SIGNATURE",37]
  ];
  // Single cols: merge rows 4 & 5 vertically (they span both rows)
  singleCols.forEach(([label, col]) => {
    ws.mergeCells(4, col, 5, col);
    applyCell(4, col, label, { bold: true, border: true, fill: grayFill });
  });

  // Group headers: merge ONLY horizontally in row 4 (row 5 will have sub-headers)
  const groupCols = [
    ["RATE OF WAGES", 15, 20, blueFill],
    ["EARNED WAGES",  21, 28, greenFill],
    ["Deduction",     29, 35, redFill],
  ];
  groupCols.forEach(([label, c1, c2, fill]) => {
    ws.mergeCells(4, c1, 4, c2); // horizontal only in row 4
    applyCell(4, c1, label, { bold: true, border: true, fill });
  });

  // ── Row 5: Sub-headers for group sections
  const sub5 = [
    ["BASIC + DA R",15,blueFill],["HRA R",16,blueFill],["CON R",17,blueFill],
    ["CCA R",18,blueFill],["MEDICAL R",19,blueFill],["TOTAL D",20,blueFill],
    ["BASIC + DA",21,greenFill],["PF Wages",22,greenFill],["HRA",23,greenFill],
    ["CON",24,greenFill],["CCA",25,greenFill],["MEDICAL",26,greenFill],
    ["ATT BONUS",27,greenFill],["TOTAL",28,greenFill],
    ["ESI",29,redFill],["PF",30,redFill],["PT",31,redFill],
    ["TDS / Adv",32,redFill],["VPF",33,redFill],["KLWF",34,redFill],["TOTAL D",35,redFill],
  ];
  sub5.forEach(([label, col, fill]) => {
    applyCell(5, col, label, { bold: true, border: true, fill });
  });

  // ── Row 6: Numbers 1-41
  ws.getRow(6).height = 18;
  for (let c = 1; c <= 41; c++) {
    applyCell(6, c, c, { bold: true, border: true, fill: grayFill });
  }

  const wpConfig = company.config?.working_days === -1 ? new Date(year, month, 0).getDate() : (company.config?.working_days || 26);
  const eligibleEmps = emps.map(e => ({ ...e, _status: getEmployeeMonthStatus(e, month, year, wpConfig) })).filter(e => e._status.eligible);

  // ── Data rows (starting at Excel row 7)
  const dataRows = eligibleEmps.map((e,i)=>{
    const s = salMap[e.id] || {};
    const lwdAbsent = e._status?.lwd_absent || 0;
    
    // WP = configured working days; NWD = net working days (WP - absent)
    const wp  = wpConfig;  // configured
    const baseAbsent = s.absent_days || 0;
    const nwd = wp - (baseAbsent + lwdAbsent);            // actually worked
    
    // Rate Columns (full month without absent deduction)
    const grossR = e.basic_salary || e.basic || 30000;
    const rateCalc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: 0, ot_hours: 0,
      cfg: company.config || DEFAULT_CONFIG,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.dob || e.date_of_birth, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until }
    });
    
    const basicR = rateCalc.basic || 0;
    const hraR = rateCalc.hra || 0;
    const daR = rateCalc.da || 0;
    const ccaR = rateCalc.special || 0; // Special includes others_earning
    const conR = rateCalc.conveyance || 0;
    const medicalR = rateCalc.medical || 0;
    const totalDR = grossR;

    // Fully recompute salary so earned/deductions reflect actual attendance
    const calc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: baseAbsent + lwdAbsent, ot_hours: s.ot_hours || 0,
      cfg: company.config || DEFAULT_CONFIG,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.dob || e.date_of_birth, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until }
    });

    // Earned
    const basicDaEarned = wp ? Math.round((basicR + daR) / wp * nwd) : 0;
    const hraEarned = wp ? Math.round(hraR / wp * nwd) : 0;
    const conEarned = wp ? Math.round(conR / wp * nwd) : 0;
    const ccaEarned = wp ? Math.round(ccaR / wp * nwd) : 0;
    const medicalEarned = wp ? Math.round(medicalR / wp * nwd) : 0;
    const pfWageEarned = calc.pfWage || 0;
    const totalEarned = calc.gross || 0;
    
    // Deductions (from fresh calc)
    const esiDed = calc.esi_emp || 0;
    const pfDed = calc.pf_emp || 0;
    const ptDed = calc.pt || 0;
    const tdsDed = calc.tds || 0;
    const vpfDed = calc.vpf || 0;
    const lwfDed = calc.lwf || 0;
    const totalDeduction = esiDed + pfDed + ptDed + tdsDed + vpfDed + lwfDed;
    
    const actualBonus = calc.bonus || s.bonus || 0;
    const rateBonus = rateCalc.bonus || 0;
    return [
      i + 1, e.employee_code || "", e.name || "", fmtDate(e.dob), e.father_name || "", fmtDate(e.doj),
      e.gender || "", e.designation || "", e.esi_id || "", e.pf_account || "", e.uan || "",
      wp, nwd, 0, // WP, NWD, Att Bonus (Kept 0, moved to CON)
      basicR + daR, hraR, conR + rateBonus, ccaR, medicalR, totalDR + rateBonus,
      basicDaEarned, pfWageEarned, hraEarned, conEarned + actualBonus, ccaEarned, medicalEarned, 0, totalEarned,
      esiDed, pfDed, ptDed, tdsDed, vpfDed, lwfDed, totalDeduction,
      calc.net || 0, calc.pf_er || 0, calc.esi_er || 0, calc.gratuity || 0, calc.ctc || 0, ""
    ];
  });
  
  // Auto-filter on header row 6 (the number row)
  ws.autoFilter = { from: { row: 6, column: 1 }, to: { row: 6, column: 41 } };

  // Write data rows with alternating row colors + formulas for computed columns
  // Column reference map (1-based):
  // 20=T (Rate TOTAL D), 28=AB (Earned TOTAL), 35=AI (Ded TOTAL D), 36=AJ (NET PAID)
  const colLetter = (n) => {
    let s = "";
    while (n > 0) { s = String.fromCharCode(64 + (n % 26 || 26)) + s; n = Math.floor((n - 1) / 26); }
    return s;
  };

  dataRows.forEach((rowData, ri) => {
    const r = ri + 7;
    ws.getRow(r).height = 20;
    const fill = ri % 2 === 0 ? row1Fill : row2Fill;

    rowData.forEach((val, ci) => {
      const col = ci + 1;
      const isName = ci === 2;
      const cell = ws.getCell(r, col);
      cell.font = { name: "Arial", size: 10 };
      cell.alignment = { horizontal: isName ? "left" : "center", vertical: "middle", wrapText: true };
      cell.border = allBorders;
      cell.fill = fill;

      // ── Formulas for computed columns
      // Column map:
      //  L=12(WP), M=13(NWD), O=15(Basic+DA R), P=16(HRA R), Q=17(CON R), R=18(CCA R), S=19(MED R)
      //  T=20(Rate Total), U=21(Basic+DA Earned), V=22(PF Wages), W=23(HRA), X=24(CON),
      //  Y=25(CCA), Z=26(MED), AA=27(Att Bonus), AB=28(Earned Total)
      //  AC=29(ESI), AD=30(PF), AE=31(PT), AF=32(Adv), AG=33(Others/Abs.Ded), AH=34(KLWF), AI=35(Ded Total)
      //  AJ=36(Net Paid)

      if (col === 16) {
        // HRA R = Basic+DA R * HRA % (from E2)
        const emp = eligibleEmps[ri];
        const isCustomHRA = c.hra_customised && emp.custom_hra_pct !== undefined && emp.custom_hra_pct !== null && emp.custom_hra_pct !== "";
        if (hra_type === "fixed" || isCustomHRA) {
          cell.value = val;
        } else {
          cell.value = { formula: `=IFERROR(ROUND(O${r}*$E$2,0),0)`, result: val };
        }
        cell.numFmt = INR_FMT;
      } else if (col === 18) {
        // CCA R = Rate Total - (Basic+DA R + HRA R + CON R + MED R)
        cell.value = { formula: `=IFERROR(T${r}-O${r}-P${r}-Q${r}-S${r},0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 20) {
        // Rate TOTAL D = sum of all rate cols
        cell.value = { formula: `=SUM(O${r}:S${r})`, result: val };
      } else if (col === 21) {
        // BASIC+DA Earned = (BASIC+DA Rate / WP) × NWD
        cell.value = { formula: `=IFERROR(ROUND(O${r}/L${r}*M${r},0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 22) {
        // PF Wages = MIN(Basic Earned, 25000)
        cell.value = { formula: `=IFERROR(MIN(U${r},25000),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 23) {
        // HRA Earned = (HRA Rate / WP) × NWD
        cell.value = { formula: `=IFERROR(ROUND(P${r}/L${r}*M${r},0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 24) {
        // CON Earned = (CON Rate / WP) × NWD
        cell.value = { formula: `=IFERROR(ROUND(Q${r}/L${r}*M${r},0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 25) {
        // CCA Earned = (CCA Rate / WP) × NWD
        cell.value = { formula: `=IFERROR(ROUND(R${r}/L${r}*M${r},0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 26) {
        // Medical Earned = (Medical Rate / WP) × NWD
        cell.value = { formula: `=IFERROR(ROUND(S${r}/L${r}*M${r},0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 28) {
        // Earned TOTAL = SUM(U) + SUM(W:AA) - skipping V (PF Wages)
        cell.value = { formula: `=U${r}+SUM(W${r}:AA${r})`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 29) {
        // ESI = Earned TOTAL * ESI % (from G2)
        cell.value = { formula: `=IFERROR(ROUND(AB${r}*$G$2,0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 30) {
        // PF = PF Wages * PF % (from I2)
        cell.value = { formula: `=IFERROR(ROUND(V${r}*$I$2,0),0)`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 33) {
        // Others (Absence Deduction) - temporarily disabled per user request
        cell.value = 0;
        cell.numFmt = INR_FMT;
      } else if (col === 34) {
        // KLWF = ₹50 in December only if LWF applicable
        cell.value = val;
        if (val > 0) cell.numFmt = INR_FMT;
        // Deduction TOTAL D = SUM(AC:AH)
        cell.value = { formula: `=SUM(AC${r}:AH${r})`, result: val };
        cell.numFmt = INR_FMT;
      } else if (col === 36) {
        // NET PAID = Earned TOTAL − Deduction TOTAL
        cell.value = { formula: `=AB${r}-AI${r}`, result: val };
        cell.font = { name: "Arial", size: 10, bold: true };
        cell.numFmt = INR_FMT;
      } else {
        cell.value = val;
        if (typeof val === "number" && NUM_COLS.has(ci)) cell.numFmt = INR_FMT;
      }
    });
  });

  // Totals row — dark navy background, white text, thick top border, SUM formulas
  const totExcelRow = dataRows.length + 7;
  const firstDataRow = 7;
  const lastDataRow  = totExcelRow - 1;
  ws.getRow(totExcelRow).height = 24;

  let mTotal = Array(40).fill(0);
  for(let i=0; i<dataRows.length; i++) {
    for(let c=15; c<=36; c++) {
      if(c!==20 && c!==28 && c!==35 && c!==36) mTotal[c] += (dataRows[i]?.[c-1] || 0);
    }
    mTotal[20] += (dataRows[i]?.[19] || 0);
    mTotal[28] += (dataRows[i]?.[27] || 0);
    mTotal[35] += (dataRows[i]?.[34] || 0);
  }
  mTotal[36] = mTotal[28] - mTotal[35];

  for (let col = 1; col <= 37; col++) {
    const cell = ws.getCell(totExcelRow, col);
    cell.font = { name: "Arial", size: 10, bold: true, color: { argb: "FF0F172A" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = topThick;
    cell.fill = totalFill;

    if (col === 1) {
      cell.value = "TOTAL";
    } else if (col === 3) {
      cell.value = eligibleEmps.length + " employees";
    } else if (col >= 15 && col <= 36) {
      cell.value = mTotal[col];
      if (NUM_COLS.has(col - 1) || col === 20 || col === 28 || col === 35 || col === 36) cell.numFmt = INR_FMT;
    }
  }

  // Footer: Configuration Summary
  const footerRow = totExcelRow + 3;
  ws.getRow(footerRow).height = 20;
  ws.getRow(footerRow + 1).height = 20;
  ws.getRow(footerRow + 2).height = 20;
  ws.getRow(footerRow + 3).height = 20;
  ws.getRow(footerRow + 4).height = 20;

  ws.mergeCells(footerRow, 2, footerRow, 4);
  applyCell(footerRow, 2, "Configuration Summary:", { bold: true, align: "left" });
  ws.getCell(footerRow, 2).font = { name: "Arial", size: 11, bold: true, color: { argb: "FF0F172A" } };
  
  ws.mergeCells(footerRow + 1, 2, footerRow + 1, 3);
  applyCell(footerRow + 1, 2, hra_type==="fixed"?"HRA Configured:":"HRA % Configured:", { bold: true, align: "right" });
  applyCell(footerRow + 1, 4, hra_type==="fixed"?`₹${hra_val}`:`${hra_val}%`, { align: "left", bold: true });
  
  ws.mergeCells(footerRow + 2, 2, footerRow + 2, 3);
  applyCell(footerRow + 2, 2, "PF Wage Capped to ₹25,000:", { bold: true, align: "right" });
  applyCell(footerRow + 2, 4, "Yes", { align: "left", bold: true });

  const wdStr = company.config?.working_days === -1 ? "Calendar Days (Dynamic)" : `${company.config?.working_days || 26} Days`;
  ws.mergeCells(footerRow + 3, 2, footerRow + 3, 3);
  applyCell(footerRow + 3, 2, "Working Days / Month:", { bold: true, align: "right" });
  applyCell(footerRow + 3, 4, wdStr, { align: "left", bold: true });

  const otStr = company.config?.ot_applicable ? `Yes (On ${company.config.ot_basis === "gross" ? "Gross" : "Basic"} @ ${company.config.ot_rate_multiplier===2?"2x":"1x"})` : "No";
  ws.mergeCells(footerRow + 4, 2, footerRow + 4, 3);
  applyCell(footerRow + 4, 2, "OT Applicable:", { bold: true, align: "right" });
  applyCell(footerRow + 4, 4, otStr, { align: "left", bold: true });

  // Print setup: landscape, fit to 1 page wide, A3 paper
  ws.pageSetup = {
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 8, // A3
    margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 }
  };
  ws.headerFooter = {
    oddHeader: `&C&B${company.name} — Payroll Register — ${MONTHS_L[month]} ${year}`,
    oddFooter: "&LGenerated by TrustEdge Payroll&RPage &P of &N"
  };

  // ── Sheet 2: ESI ──
  const wsEsi = workbook.addWorksheet("ESI");
  wsEsi.views = [{ state: "frozen", xSplit: 3, ySplit: 6, activeCell: "D7" }];
  wsEsi.pageSetup = ws.pageSetup;
  wsEsi.headerFooter = ws.headerFooter;
  
  wsEsi.columns = [
    { width: 8 }, { width: 15 }, { width: 25 }, { width: 15 },
    { width: 10 }, { width: 15 }, { width: 15 }, { width: 15 }
  ];

  // Top header (Rows 1-5)
  wsEsi.getRow(1).height = 26;
  wsEsi.getRow(2).height = 20;
  wsEsi.getRow(3).height = 22;
  wsEsi.getRow(4).height = 10;
  wsEsi.getRow(5).height = 10;

  // Row 1: Company Name
  wsEsi.mergeCells(1, 1, 1, 8);
  const esiCell1 = wsEsi.getCell(1, 1);
  esiCell1.value = company.name || "Company Name";
  esiCell1.font = { name: "Arial", size: 16, bold: true, color: { argb: "FF0F172A" } };
  esiCell1.alignment = { horizontal: "left", vertical: "middle" };

  // Row 3: Month & Year
  wsEsi.mergeCells(3, 1, 3, 8);
  const esiCell3 = wsEsi.getCell(3, 1);
  esiCell3.value = `ESI Register - ${MONTHS_L[month]} ${year}`;
  esiCell3.font = { name: "Arial", size: 12, bold: true };
  esiCell3.alignment = { horizontal: "left", vertical: "middle" };

  // ESI Table Headers (Row 6)
  wsEsi.getRow(6).height = 22;
  const esiHeaders = ["SL NO", "New ID", "NAME", "ESI NO", "NWD", "Wages", "ESI 0.75%", "ESI 3.25%"];
  esiHeaders.forEach((lbl, idx) => {
    const c = idx + 1;
    const cell = wsEsi.getCell(6, c);
    cell.value = lbl;
    cell.font = { bold: true, name: "Arial", size: 10 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = allBorders;
    cell.fill = grayFill;
  });

  let esiTotalF = 0, esiTotalG = 0, esiTotalH = 0;
  let esiRowCounter = 0;

  eligibleEmps.forEach((e, i) => {
    const sEsi = salMap[e.id] || {};
    const grossR = e.basic_salary || e.basic || 0;
    const lwdAbsent = e._status?.lwd_absent || 0;
    const calc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: (sEsi.absent_days || 0) + lwdAbsent, ot_hours: sEsi.ot_hours || 0,
      cfg: company.config || DEFAULT_CONFIG,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.dob || e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }
    });

    if (calc.esi_emp === 0 && calc.esi_er === 0) return; // Skip ineligible employees

    const r = esiRowCounter + 7;
    esiRowCounter++;
    wsEsi.getRow(r).height = 20;
    const nwdVal = dataRows[i]?.[12] ?? 0; // NWD is at index 12
    
    // Sl No
    const c1 = wsEsi.getCell(r, 1);
    c1.value = esiRowCounter; c1.border = allBorders; c1.alignment = { horizontal: "center" };
    // New ID
    const c2 = wsEsi.getCell(r, 2);
    c2.value = e.esi_id || ""; c2.border = allBorders; c2.alignment = { horizontal: "center" };
    // NAME
    const c3 = wsEsi.getCell(r, 3);
    c3.value = e.name || ""; c3.border = allBorders;
    // ESI NO
    const c4 = wsEsi.getCell(r, 4);
    c4.value = e.esi_id || ""; c4.border = allBorders; c4.alignment = { horizontal: "center" };
    const c5 = wsEsi.getCell(r, 5);
    c5.value = nwdVal; c5.border = allBorders; c5.alignment = { horizontal: "center" };

    // Wages (Earned Basic + DA if ESI is applicable)
    const c6 = wsEsi.getCell(r, 6);
    const wageVal = dataRows[i]?.[20] || 0;
    c6.value = wageVal;
    esiTotalF += wageVal;
    c6.numFmt = INR_FMT; c6.border = allBorders; c6.alignment = { horizontal: "center" };
    // ESI 0.75%
    const c7 = wsEsi.getCell(r, 7);
    const empShare = calc.esi_emp || 0;
    c7.value = empShare;
    esiTotalG += empShare;
    c7.numFmt = INR_FMT; c7.border = allBorders; c7.alignment = { horizontal: "center" };
    // ESI 3.25%
    const c8 = wsEsi.getCell(r, 8);
    const erShare = calc.esi_er || 0;
    c8.value = erShare;
    esiTotalH += erShare;
    c8.numFmt = INR_FMT; c8.border = allBorders; c8.alignment = { horizontal: "center" };
  });

  const esiTotRow = esiRowCounter + 7;
  wsEsi.getRow(esiTotRow).height = 24;
  for(let c=1; c<=8; c++) {
    const cell = wsEsi.getCell(esiTotRow, c);
    cell.font = { bold: true, name: "Arial", size: 10, color: { argb: "FF0F172A" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = totalFill;
    cell.border = topThick;
    
    if (c === 1) cell.value = "TOTAL";
    else if (c === 6) cell.value = esiTotalF;
    else if (c === 7) cell.value = esiTotalG;
    else if (c === 8) cell.value = esiTotalH;
    
    if (c >= 6) cell.numFmt = INR_FMT;
  }

  // ESI Summary block at the bottom
  const esiSummaryRow = esiTotRow + 2;
  wsEsi.getRow(esiSummaryRow).height = 20;
  wsEsi.getRow(esiSummaryRow + 1).height = 20;
  wsEsi.getRow(esiSummaryRow + 2).height = 10; // Empty row for spacing
  wsEsi.getRow(esiSummaryRow + 3).height = 22;

  // EMPLOYEE'S SHARE
  wsEsi.mergeCells(esiSummaryRow, 2, esiSummaryRow, 4);
  const cellEmpShareLbl = wsEsi.getCell(esiSummaryRow, 2);
  cellEmpShareLbl.value = "EMPLOYEE'S SHARE .75%";
  cellEmpShareLbl.font = { bold: true, name: "Arial", size: 10 };
  cellEmpShareLbl.alignment = { horizontal: "left" };
  const cellEmpShareVal = wsEsi.getCell(esiSummaryRow, 5);
  cellEmpShareVal.value = esiTotalG;
  cellEmpShareVal.font = { bold: true, name: "Arial", size: 10 };
  cellEmpShareVal.numFmt = INR_FMT;

  // EMPLOYER'S SHARE
  wsEsi.mergeCells(esiSummaryRow + 1, 2, esiSummaryRow + 1, 4);
  const cellEmprShareLbl = wsEsi.getCell(esiSummaryRow + 1, 2);
  cellEmprShareLbl.value = "EMPLOYER'S SHARE 3.25%";
  cellEmprShareLbl.font = { bold: true, name: "Arial", size: 10 };
  cellEmprShareLbl.alignment = { horizontal: "left" };
  const cellEmprShareVal = wsEsi.getCell(esiSummaryRow + 1, 5);
  cellEmprShareVal.value = esiTotalH;
  cellEmprShareVal.font = { bold: true, name: "Arial", size: 10 };
  cellEmprShareVal.numFmt = INR_FMT;

  // TOTAL
  wsEsi.mergeCells(esiSummaryRow + 3, 2, esiSummaryRow + 3, 4);
  const cellTotalLbl = wsEsi.getCell(esiSummaryRow + 3, 2);
  cellTotalLbl.value = "TOTAL";
  cellTotalLbl.font = { bold: true, name: "Arial", size: 11, color: { argb: "FF0F172A" } };
  cellTotalLbl.alignment = { horizontal: "left" };
  const cellTotalVal = wsEsi.getCell(esiSummaryRow + 3, 5);
  cellTotalVal.value = esiTotalG + esiTotalH;
  cellTotalVal.font = { bold: true, name: "Arial", size: 11, color: { argb: "FF0F172A" } };
  cellTotalVal.numFmt = INR_FMT;
  cellTotalVal.border = { top: thickBorder, bottom: thickBorder };

  // ── Sheet 3: PF ──
  const wsPf = workbook.addWorksheet("PF");
  wsPf.views = [{ state: "frozen", xSplit: 4, ySplit: 6, activeCell: "E7" }];
  wsPf.pageSetup = ws.pageSetup;
  wsPf.headerFooter = ws.headerFooter;

  wsPf.columns = [
    { width: 6 }, { width: 12 }, { width: 15 }, { width: 25 }, 
    { width: 15 }, { width: 15 }, { width: 15 }, { width: 10 }, 
    { width: 15 }, { width: 15 }, { width: 15 }, { width: 12 }, 
    { width: 10 }, { width: 15 }, { width: 12 }, { width: 12 }, 
    { width: 15 }, { width: 15 }, { width: 8 }
  ];

  // Top header (Rows 1-5)
  wsPf.getRow(1).height = 26;
  wsPf.getRow(2).height = 20;
  wsPf.getRow(3).height = 22;
  wsPf.getRow(4).height = 10;
  wsPf.getRow(5).height = 10;

  wsPf.mergeCells(1, 1, 1, 19);
  const pfCell1 = wsPf.getCell(1, 1);
  pfCell1.value = company.name || "Company Name";
  pfCell1.font = { name: "Arial", size: 16, bold: true, color: { argb: "FF0F172A" } };
  pfCell1.alignment = { horizontal: "left", vertical: "middle" };

  wsPf.mergeCells(3, 1, 3, 19);
  const pfCell3 = wsPf.getCell(3, 1);
  pfCell3.value = `PF Register - ${MONTHS_L[month]} ${year}`;
  pfCell3.font = { name: "Arial", size: 12, bold: true };
  pfCell3.alignment = { horizontal: "left", vertical: "middle" };

  // PF Table Headers (Row 6)
  wsPf.getRow(6).height = 40;
  const pfHeaders = [
    "Sl No", "ODL ID", "New ID", "Name of the Employees", "UAN No", 
    "PF Number", "Gross Wages", "NCP Dayes", "PF Salary", "PENSION", 
    "EDLI WAGES", "EMP YEE\n12%", "VPF", "Total Contr\nPF + VPF", 
    "EMP YER\n3.67%", "EMP YER\n8.33%", "ADMIN Changes\n0.5%", 
    "EDLI Changes\n0.5%", "NWD"
  ];
  pfHeaders.forEach((lbl, idx) => {
    const c = idx + 1;
    const cell = wsPf.getCell(6, c);
    cell.value = lbl;
    cell.font = { bold: true, name: "Arial", size: 9 };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = allBorders;
    cell.fill = grayFill;
  });

  const pfSums = Array(20).fill(0);

  eligibleEmps.forEach((e, i) => {
    const r = i + 7;
    wsPf.getRow(r).height = 20;
    
    const sPf = salMap[e.id] || {};
    const grossR = e.basic_salary || e.basic || 0;
    const lwdAbsent = e._status?.lwd_absent || 0;
    const calc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: (sPf.absent_days || 0) + lwdAbsent, ot_hours: sPf.ot_hours || 0,
      cfg: company.config || DEFAULT_CONFIG,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.dob || e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }
    });
    const pfWage = calc.pfWage || 0;
    const pensionWage = Math.min(pfWage, 25000);
    const ncpDays = (sPf.absent_days || 0) + lwdAbsent;
    
    pfSums[7] += calc.gross || 0;
    pfSums[9] += pfWage;
    pfSums[10] += pensionWage;
    pfSums[11] += pensionWage;
    pfSums[12] += calc.pf_emp || 0;
    pfSums[13] += calc.vpf || 0;
    pfSums[14] += (calc.pf_emp || 0) + (calc.vpf || 0);
    pfSums[15] += calc.epf_er || 0;
    pfSums[16] += calc.eps || 0;
    pfSums[17] += Math.round(pensionWage * 0.005);
    pfSums[18] += Math.round(pensionWage * 0.005);

    const fill = i % 2 === 0 ? row1Fill : row2Fill;
    const applyPfCell = (col, val, isFmt = false, isLeft = false) => {
      const c = wsPf.getCell(r, col);
      c.value = val;
      c.font = { name: "Arial", size: 10 };
      c.alignment = { horizontal: isLeft ? "left" : "center", vertical: "middle" };
      c.border = allBorders;
      c.fill = fill;
      if (isFmt) c.numFmt = INR_FMT;
    };

    applyPfCell(1, i + 1);                                      // Sl No
    applyPfCell(2, e.pf_account || "");                         // ODL ID (PF Account No)
    applyPfCell(3, e.uan || "");                                // New ID (UAN)
    applyPfCell(4, e.name || "", false, true);                  // Name
    applyPfCell(5, e.uan || "");                                // UAN No
    applyPfCell(6, e.pf_account || "");                         // PF Number (Member ID)
    applyPfCell(7, calc.gross || 0, true);                      // Gross Wages
    applyPfCell(8, ncpDays);                                    // NCP Days
    applyPfCell(9, pfWage, true);                               // PF Salary
    applyPfCell(10, pensionWage, true);                         // PENSION (J)
    applyPfCell(11, pensionWage, true);                         // EDLI WAGES (K)
    applyPfCell(12, calc.pf_emp || 0, true);                    // EMP YEE 12%
    applyPfCell(13, calc.vpf || 0, true);                       // VPF
    applyPfCell(14, (calc.pf_emp || 0) + (calc.vpf || 0), true); // Total Contr PF + VPF
    applyPfCell(15, calc.epf_er || 0, true);                    // EMP YER EPF (O)
    applyPfCell(16, calc.eps || 0, true);                       // EMP YER EPS 8.33% (P)
    applyPfCell(17, Math.round(pensionWage * 0.005), true);     // ADMIN 0.50% (Q)
    applyPfCell(18, Math.round(pensionWage * 0.005), true);     // EDLI 0.50% (R)
    applyPfCell(19, (company.config?.working_days === -1 ? new Date(year, month, 0).getDate() : (company.config?.working_days || 26)) - ncpDays); // NWD
  });

  const pfTotRow = eligibleEmps.length + 7;
  wsPf.getRow(pfTotRow).height = 24;
  for (let c = 1; c <= 19; c++) {
    const cell = wsPf.getCell(pfTotRow, c);
    cell.font = { bold: true, name: "Arial", size: 10, color: { argb: "FF0F172A" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = totalFill;
    cell.border = topThick;
    
    if (c === 1) cell.value = "TOTAL";
    else if (c >= 7 && c <= 18 && c !== 8) {
      cell.value = pfSums[c] || 0;
      cell.numFmt = INR_FMT;
    }
  }

  // PF Challan Summary block
  const pfSumStart = pfTotRow + 2;
  const pfSumApply = (r, c, v, b = false, align = "left", isNum = false) => {
    const cell = wsPf.getCell(r, c);
    cell.value = v;
    cell.font = { bold: b, name: "Arial", size: 10 };
    cell.alignment = { horizontal: align };
    if (isNum) cell.numFmt = INR_FMT;
  };

  pfSumApply(pfSumStart, 1, "Account No", true);
  wsPf.mergeCells(pfSumStart, 2, pfSumStart, 4);
  pfSumApply(pfSumStart, 2, "Particulars", true);

  const pfSumData = [
    [1,  "Account No: 01 Emp Yee",       pfSums[12] || 0],
    [1,  "Account No: 01 Emp Yer",       pfSums[15] || 0],
    [2,  "A/c 02 - EPF Admin 0.5%",      (pfSums[9] || 0) > 0 ? Math.max(Math.round(pfSums[9] * 0.005), 500) : 75],
    [10, "Account No: 10 (EPS)",         pfSums[16] || 0],
    [21, "A/c 21 - EDLI 0.5%",           pfSums[18] || 0],
    [22, "A/c 22 - EDLI Admin (Waived)", 0],
  ];

  pfSumData.forEach(([accNo, particular, amount], idx) => {
    const r = pfSumStart + 1 + idx;
    wsPf.getRow(r).height = 20;
    pfSumApply(r, 1, accNo, false, "center");
    wsPf.mergeCells(r, 2, r, 4);
    pfSumApply(r, 2, particular, false);
    pfSumApply(r, 5, amount, false, "right", true);
  });

  const pfSumTRow = pfSumStart + 8;
  wsPf.getRow(pfSumTRow).height = 22;
  wsPf.mergeCells(pfSumTRow, 2, pfSumTRow, 4);
  pfSumApply(pfSumTRow, 2, "TOTAL", true, "right");
  const totalSumAcc = pfSumData.reduce((sum, [, , amt]) => sum + (amt || 0), 0);
  pfSumApply(pfSumTRow, 5, totalSumAcc, true, "right", true);

  // ── Sheet 4: Executive Summary ──
  const wsSumm = workbook.addWorksheet("Executive Summary");
  
  let summAbs = 0, summOtH = 0, summOtP = 0, summPfElig = 0, summEsiElig = 0;
  let summGross = 0, summNet = 0, summEmpPf = 0, summEmpEsi = 0, summGratuity = 0;
  let summErPf = 0, summErEsi = 0, summPfWage = 0, summAdmin = 0, summEdli = 0;
  let otViolators = [];

  eligibleEmps.forEach(e => {
    const s = salMap[e.id] || {};
    const grossR = e.basic_salary || e.basic || 0;
    const lwdAbsent = e._status?.lwd_absent || 0;
    const calc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: (s.absent_days || 0) + lwdAbsent, ot_hours: s.ot_hours || 0,
      cfg: company.config || DEFAULT_CONFIG,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.dob || e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }
    });
    
    summAbs += ((s.absent_days || 0) + lwdAbsent);
    summOtH += (s.ot_hours || 0);
    summOtP += (calc.ot_pay || 0);
    if (calc.pf_emp > 0) summPfElig++;
    if (calc.esi_emp > 0) summEsiElig++;
    
    summGross += (calc.gross || 0);
    summNet += (calc.net || 0);
    summGratuity += (calc.gratuity || 0);
    
    const pfw = calc.pfWage || 0;
    const pw = Math.min(pfw, 25000);
    summPfWage += pfw;
    summAdmin += Math.round(pw * 0.005);
    summEdli += Math.round(pw * 0.005);
    
    summEmpPf += (calc.pf_emp || 0) + (calc.vpf || 0);
    summEmpEsi += (calc.esi_emp || 0);
    summErPf += (calc.epf_er || 0) + (calc.eps || 0);
    summErEsi += (calc.esi_er || 0);

    const wp = company.config?.working_days === -1 ? new Date(year, month, 0).getDate() : (company.config?.working_days || 26);
    const present = wp - ((s.absent_days || 0) + lwdAbsent);
    const totalEq = present + ((s.ot_hours || 0) / 8);
    if (totalEq > 31) otViolators.push({ name: e.name || "Unknown", days: totalEq });
  });

  const finalAdmin = (c.pf_applicable !== false && eligibleEmps.length > 0) ? (summPfWage > 0 ? Math.max(summAdmin, 500) : 75) : 0;
  const totalErPfExpenditure = summErPf + finalAdmin + summEdli;
  const totalExpenditure = summGross + totalErPfExpenditure + summErEsi + summGratuity;

  wsSumm.columns = [
    { width: 5 }, { width: 50 }, { width: 25 }, { width: 5 }
  ];
  
  const applySumCell = (r, c, val, bold, size, color, bg, align="left") => {
    const cell = wsSumm.getCell(r, c);
    cell.value = val;
    cell.font = { name: "Arial", size: size, bold: bold, color: { argb: color } };
    cell.alignment = { horizontal: align, vertical: "middle", wrapText: true };
    if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    if (typeof val === "number") cell.numFmt = INR_FMT;
  };
  
  wsSumm.getRow(2).height = 40;
  wsSumm.mergeCells(2, 2, 2, 3);
  applySumCell(2, 2, "📊 PAYROLL EXECUTIVE SUMMARY", true, 18, "FF0F172A", "FFE2E8F0", "center");
  wsSumm.getCell(2, 2).border = { top: thickBorder, bottom: thickBorder, left: thickBorder, right: thickBorder };

  wsSumm.getRow(3).height = 25;
  wsSumm.mergeCells(3, 2, 3, 3);
  applySumCell(3, 2, `${company.name}  |  ${MONTHS_L[month]} ${year}`, true, 12, "FF64748B", null, "center");

  wsSumm.getRow(5).height = 25;
  wsSumm.mergeCells(5, 2, 5, 3);
  applySumCell(5, 2, "👥 Headcount & Attendance", true, 13, "FF0F172A", "FFE2E8F0", "left");
  
  applySumCell(6, 2, "Total Employees Processed", true, 12, "FF1E293B", null, "left");
  applySumCell(6, 3, eligibleEmps.length, true, 12, "FF0F172A", null, "right");
  wsSumm.getCell(6, 3).numFmt = "0";
  
  applySumCell(7, 2, "Total Absent Days", false, 12, "FF334155", null, "left");
  applySumCell(7, 3, summAbs, true, 12, "FFDC2626", null, "right");
  wsSumm.getCell(7, 3).numFmt = "0.0";

  applySumCell(8, 2, "Employees Eligible for PF", false, 12, "FF334155", null, "left");
  applySumCell(8, 3, summPfElig, true, 12, "FF0F172A", null, "right");
  wsSumm.getCell(8, 3).numFmt = "0";

  applySumCell(9, 2, "Employees Eligible for ESI", false, 12, "FF334155", null, "left");
  applySumCell(9, 3, summEsiElig, true, 12, "FF0F172A", null, "right");
  wsSumm.getCell(9, 3).numFmt = "0";

  wsSumm.getRow(11).height = 25;
  wsSumm.mergeCells(11, 2, 11, 3);
  applySumCell(11, 2, "⏱️ Overtime Metrics", true, 13, "FF0F172A", "FFE2E8F0", "left");
  
  applySumCell(12, 2, "Total OT Hours Logged", false, 12, "FF334155", null, "left");
  applySumCell(12, 3, summOtH, true, 12, "FFD97706", null, "right");
  wsSumm.getCell(12, 3).numFmt = "0.0";

  applySumCell(13, 2, "Total OT Amount Paid", true, 12, "FF1E293B", null, "left");
  applySumCell(13, 3, summOtP, true, 12, "FFD97706", null, "right");

  wsSumm.getRow(15).height = 25;
  wsSumm.mergeCells(15, 2, 15, 3);
  applySumCell(15, 2, "💰 Financial Outflow", true, 13, "FF0F172A", "FFE2E8F0", "left");
  
  applySumCell(16, 2, "Total Gross Salary Paid", true, 12, "FF1E293B", null, "left");
  applySumCell(16, 3, summGross, true, 12, "FF0F172A", null, "right");

  applySumCell(17, 2, "Total Net Salary Paid (In-Hand)", true, 13, "FF0F172A", "FFF1F5F9", "left");
  applySumCell(17, 3, summNet, true, 13, "FF0F172A", "FFF1F5F9", "right");

  applySumCell(18, 2, "Total Employee Contributions (PF+ESI)", false, 11, "FF64748B", null, "left");
  applySumCell(18, 3, summEmpPf + summEmpEsi, false, 11, "FF64748B", null, "right");

  applySumCell(19, 2, "Total Employer PF Contr. (inc. Admin/EDLI)", false, 11, "FF334155", null, "left");
  applySumCell(19, 3, totalErPfExpenditure, true, 11, "FF334155", null, "right");

  applySumCell(20, 2, "Total Employer ESI Contr.", false, 11, "FF334155", null, "left");
  applySumCell(20, 3, summErEsi, true, 11, "FF334155", null, "right");

  applySumCell(21, 2, "Total Employer Gratuity Provision", false, 11, "FF334155", null, "left");
  applySumCell(21, 3, summGratuity, true, 11, "FF334155", null, "right");

  wsSumm.getRow(22).height = 30;
  applySumCell(22, 2, "🏆 TOTAL EMPLOYER EXPENDITURE (CTC)", true, 14, "FF0F172A", "FFE2E8F0", "left");
  applySumCell(22, 3, totalExpenditure, true, 16, "FF0F172A", "FFE2E8F0", "right");
  wsSumm.getCell(22, 2).border = { top: thickBorder, bottom: thickBorder, left: thickBorder };
  wsSumm.getCell(22, 3).border = { top: thickBorder, bottom: thickBorder, right: thickBorder };

  if (otViolators.length > 0) {
    wsSumm.getRow(25).height = 30;
    wsSumm.mergeCells(25, 2, 25, 3);
    applySumCell(25, 2, "⚠️ COMPLIANCE VIOLATION DETECTED", true, 12, "FF991B1B", "FFFEE2E2", "center");
    wsSumm.getCell(25, 2).border = { top: thickBorder, bottom: thickBorder, left: thickBorder, right: thickBorder };
    
    wsSumm.getRow(26).height = 30;
    wsSumm.mergeCells(26, 2, 26, 3);
    applySumCell(26, 2, "The following employees have Equivalent Work Days (Present + OT) exceeding 31 days:", false, 11, "FF991B1B", null, "center");
    
    otViolators.forEach((v, idx) => {
      const r = 27 + idx;
      wsSumm.getRow(r).height = 20;
      wsSumm.mergeCells(r, 2, r, 3);
      applySumCell(r, 2, `• ${v.name} (${v.days.toFixed(1)} days)`, false, 11, "FF991B1B", null, "center");
    });
  } else {
    wsSumm.getRow(25).height = 30;
    wsSumm.mergeCells(25, 2, 25, 3);
    applySumCell(25, 2, "✅ All compliance checks passed (No OT violations)", true, 12, "FF0F172A", "FFF1F5F9", "center");
  }

  for(let r=6; r<=20; r++) {
    if(r===10 || r===14) continue;
    wsSumm.getCell(r, 2).border = { left: thickBorder, bottom: allBorders.bottom };
    wsSumm.getCell(r, 3).border = { right: thickBorder, bottom: allBorders.bottom };
  }

  // Download
  const buf = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = company.name.replace(/ /g,"_")+"_Master_"+(MONTHS_L[month]||month)+"_"+year+".xlsx";
  a.click();
  } catch (err) {
    alert("Master Excel Export failed: " + err.message);
    console.error(err);
  }
}

// ── Export PF challan
function exportPFChallan(emps, salMap, pfMap, company, month, year) {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
  const HDR = ["Employee ID","Name","UAN","PF Account","DOJ","Basic","PF Wage","Employee PF","Employer PF","Total PF"];
  const NC = HDR.length;
  const dataRows = emps.filter(e=>salMap[e.id]?.pf_emp>0).map((e,i)=>{
    const s=salMap[e.id], pf=pfMap[e.id]||{};
    const basicVal = Math.round(s.basic || 0);
    const pfWageVal = Math.round(s.pfWage ?? Math.min(basicVal, 25000));
    const pfEmpVal = Math.round(s.pf_emp || 0);
    const pfErVal = Math.round(s.pf_er || 0);
    return [e.employee_code||"", e.name, pf.uan||"", pf.pf_account||"", e.doj||"", basicVal, pfWageVal, pfEmpVal, pfErVal, pfEmpVal + pfErVal];
  });
  const total = dataRows.reduce((a,r)=>({emp:a.emp+r[7], er:a.er+r[8], tot:a.tot+r[9]}), {emp:0, er:0, tot:0});
  const totRow = ["TOTAL", dataRows.length+" employees", "", "", "",
    Math.round(dataRows.reduce((a,r)=>a+(parseFloat(r[5])||0), 0)),
    Math.round(dataRows.reduce((a,r)=>a+(parseFloat(r[6])||0), 0)),
    total.emp, total.er, total.tot];
  const aoa = [
    HDR,
    ...dataRows,
    totRow,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{wch:14},{wch:26},{wch:16},{wch:18},{wch:13},{wch:12},{wch:12},{wch:14},{wch:14},{wch:14}];

  // Title header and total row in bold, right align numbers
  for (let r = 0; r <= dataRows.length + 1; r++) {
    for (let c = 0; c < NC; c++) {
      const ref = XLSX.utils.encode_cell({r, c});
      if (ws[ref]) {
        const isBold = r === 0 || r === dataRows.length + 1;
        const isNum = c >= 5;
        ws[ref].s = {
          font: { bold: isBold },
          alignment: { horizontal: isNum ? "right" : "left", vertical: "center" }
        };
      }
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "PF Challan");
  XLSX.writeFile(wb, company.name.replace(/ /g,"_")+"_PF_Challan_"+(MONTHS_L[month]||month)+"_"+year+".xlsx");
}

// ── Export ESI challan
function exportESIChallan(emps, salMap, esiMap, company, month, year) {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
  const HDR = ["Employee ID","Name","ESI ID","DOJ","Gross","Employee ESI (0.75%)","Employer ESI (3.25%)","Total ESI"];
  const NC = HDR.length;
  const dataRows = emps.filter(e => {
    const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (salMap[e.id]?.absent_days||0), ot_hours: (salMap[e.id]?.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, custom_special_allowance:e.custom_special_allowance }});
    return calc.esi_emp > 0;
  }).map((e,i) => {
    const s = salMap[e.id] || {};
    const esi = esiMap[e.id] || {};
    const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (s.absent_days||0), ot_hours: (s.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, custom_special_allowance:e.custom_special_allowance }});
    const grossVal = Math.round(calc.gross || 0);
    const esiEmpVal = Math.round(calc.esi_emp || 0);
    const esiErVal = Math.round(calc.esi_er || 0);
    return [e.employee_code||"", e.name, esi.esi_id||e.esi_id||"", e.doj||"", grossVal, esiEmpVal, esiErVal, esiEmpVal + esiErVal];
  });
  const total = dataRows.reduce((a,r)=>({emp:a.emp+r[5], er:a.er+r[6], tot:a.tot+r[7]}), {emp:0, er:0, tot:0});
  const totRow = ["TOTAL", dataRows.length+" employees", "", "",
    Math.round(dataRows.reduce((a,r)=>a+(parseFloat(r[4])||0), 0)),
    total.emp, total.er, total.tot];
  const aoa = [
    HDR,
    ...dataRows,
    totRow,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{wch:14},{wch:26},{wch:16},{wch:13},{wch:14},{wch:22},{wch:22},{wch:14}];

  // Title header and total row in bold, right align numbers
  for (let r = 0; r <= dataRows.length + 1; r++) {
    for (let c = 0; c < NC; c++) {
      const ref = XLSX.utils.encode_cell({r, c});
      if (ws[ref]) {
        const isBold = r === 0 || r === dataRows.length + 1;
        const isNum = c >= 4;
        ws[ref].s = {
          font: { bold: isBold },
          alignment: { horizontal: isNum ? "right" : "left", vertical: "center" }
        };
      }
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "ESI Challan");
  XLSX.writeFile(wb, company.name.replace(/ /g,"_")+"_ESI_Challan_"+(MONTHS_L[month]||month)+"_"+year+".xlsx");
}

// ── Export ESI MC_Template
async function exportESIMcTemplate(emps, salMap, esiMap, company, month, year) {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
  try {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Sheet1");
  
  const HDR = [
    "IP Number \n(10 Digits)",
    "IP Name\n( Only alphabets and space )",
    "No of Days for which wages paid/payable during the month",
    "Total Monthly Wages",
    " Reason Code for Zero workings days(numeric only; provide 0 for all other reasons- Click on the link for reference)",
    " Last Working Day\n( Format DD/MM/YYYY  or DD-MM-YYYY)"
  ];
  
  const headerRow = ws.addRow(HDR);
  headerRow.height = 90;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FF000000" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
    cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
    cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
  });

  ws.columns = [
    { width: 15 }, { width: 30 }, { width: 18 }, { width: 15 }, { width: 30 }, { width: 20 }
  ];

  // Include employees who have ESI deductions OR (have an ESI ID and 0 working days)
  const dataRows = emps.filter(e => {
    const s = salMap[e.id] || {};
    const hasEsiId = (esiMap[e.id]?.esi_id || e.esi_id) && (esiMap[e.id]?.esi_id || e.esi_id).trim().length >= 10;
    const hasDeduction = (s.esi_emp || 0) > 0;
    const wd = company.config?.working_days === -1 ? new Date(year, month, 0).getDate() : (company.config?.working_days||26);
    const presentDays = Math.max(0, wd - (s.absent_days||0));
    return hasDeduction || (hasEsiId && presentDays === 0);
  }).map((e) => {
    const s = salMap[e.id] || {};
    const ipNo = esiMap[e.id]?.esi_id || e.esi_id || "";
    const ipName = (e.name||"").replace(/[^a-zA-Z\s]/g, ""); 
    const wd = company.config?.working_days === -1 ? new Date(year, month, 0).getDate() : (company.config?.working_days||26);
    const presentDays = Math.max(0, wd - (s.absent_days||0));
    const wages = s.gross || 0;
    let reasonCode = presentDays === 0 ? "1" : "0";
    let lwd = "";
    if (e.date_of_exit) {
      const exitDate = new Date(e.date_of_exit);
      if (exitDate.getMonth() + 1 === month && exitDate.getFullYear() === year) {
        lwd = `${exitDate.getDate().toString().padStart(2,'0')}/${(exitDate.getMonth()+1).toString().padStart(2,'0')}/${exitDate.getFullYear()}`;
        reasonCode = "2"; // Left service
      }
    }
    return [ipNo, ipName, presentDays, wages, reasonCode, lwd];
  });

  dataRows.forEach(r => {
    const row = ws.addRow(r);
    row.eachCell({includeEmpty:true}, cell => {
      cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = company.name.replace(/ /g,"_")+"_ESI_MC_Template_"+(MONTHS_L[month]||month)+"_"+year+".xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
  } catch (err) {
    alert("ESI MC_Template Export failed: " + err.message);
    console.error(err);
  }
}

// ── Export PF Wagedata (ECR format)
async function exportPFWagedata(emps, salMap, pfMap, company, month, year) {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
  try {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("ECRSheet");

  const HDR = [
    "UAN",
    "MEMBER NAME",
    "GROSS WAGES",
    "EPF WAGES",
    "EPS WAGES",
    "EDLI WAGES",
    "EPF CONTRI REMITTED",
    "EPS CONTRI REMITTED",
    "EPF EPS DIFF REMITTED",
    "NCP DAYS",
    "REFUND OF ADVANCES"
  ];
  
  const headerRow = ws.addRow(HDR);
  headerRow.height = 30;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FF000000" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
    cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
    cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
  });

  ws.columns = [
    { width: 15 }, { width: 30 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 22 }, { width: 22 }, { width: 25 }, { width: 12 }, { width: 22 }
  ];

  // Filter for employees with PF applicable
  const dataRows = emps.filter(e => {
    const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (salMap[e.id]?.absent_days||0), ot_hours: (salMap[e.id]?.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, custom_special_allowance:e.custom_special_allowance }});
    const uan = e.uan || pfMap[e.id]?.uan || "";
    return calc.pf_emp > 0 || (uan && uan.trim().length >= 12);
  }).map((e) => {
    const s = salMap[e.id] || {};
    const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (s.absent_days||0), ot_hours: (s.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, custom_special_allowance:e.custom_special_allowance }});
    const uan = e.uan || pfMap[e.id]?.uan || "";
    const name = (e.name||"").replace(/[^a-zA-Z\s]/g, "");
    const gross = calc.gross || 0;
    const epfWage = calc.pfWage || 0;
    const epsWage = (e.pension_applicable !== false) ? epfWage : 0;
    const edliWage = epfWage;
    const epfContri = calc.pf_emp || 0;
    const epsContri = calc.eps || 0;
    const epfEpsDiff = calc.epf_er || 0;
    const ncpDays = s.absent_days || 0;
    const advances = 0;
    return [uan, name, gross, epfWage, epsWage, edliWage, epfContri, epsContri, epfEpsDiff, ncpDays, advances];
  });

  dataRows.forEach(r => {
    const row = ws.addRow(r);
    row.eachCell({includeEmpty:true}, cell => {
      cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = company.name.replace(/ /g,"_")+"_PF_Wagedata_"+(MONTHS_L[month]||month)+"_"+year+".xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
  } catch (err) {
    alert("PF Wagedata Export failed: " + err.message);
    console.error(err);
  }
}

// ── Export PF Wagedata Text format (Ready to upload)
function exportPFWagedataText(emps, salMap, pfMap, company, month, year) {
  const MONTHS_L = ["","January","February","March","April","May","June","July","August","September","October","November","December"];
  try {
    const dataRows = emps.filter(e => {
      const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (salMap[e.id]?.absent_days||0), ot_hours: (salMap[e.id]?.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }});
      const uan = e.uan || pfMap[e.id]?.uan || "";
      return calc.pf_emp > 0 || (uan && uan.trim().length >= 12);
    }).map((e) => {
      const s = salMap[e.id] || {};
      const calc = calcSalaryFull({ basic: e.basic_salary||e.basic||0, month, year, absent_days: (s.absent_days||0), ot_hours: (s.ot_hours||0), cfg: company.config||DEFAULT_CONFIG, extra: { vpf_enabled:(e.vpf_pct||0)>0, vpf_pct:e.vpf_pct||0, tds_amount:e.tds_amount||0, emp_dob:e.dob||e.date_of_birth, custom_basic_pct: e.custom_basic_pct, custom_hra_pct:e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }});
      const uan = e.uan || pfMap[e.id]?.uan || "";
      const name = (e.name||"").replace(/[^a-zA-Z\s]/g, "");
      const gross = calc.gross || 0;
      const epfWage = calc.pfWage || 0;
      const epsWage = (e.pension_applicable !== false) ? epfWage : 0;
      const edliWage = epfWage;
      const epfContri = calc.pf_emp || 0;
      const epsContri = calc.eps || 0;
      const epfEpsDiff = calc.epf_er || 0;
      const ncpDays = s.absent_days || 0;
      const advances = 0;
      return [uan, name, gross, epfWage, epsWage, edliWage, epfContri, epsContri, epfEpsDiff, ncpDays, advances].join("#~#");
    });

    const textContent = dataRows.join("\n");
    const blob = new Blob([textContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = company.name.replace(/ /g,"_")+"_PF_Wagedata_ReadyToUpload_"+(MONTHS_L[month]||month)+"_"+year+".txt";
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    alert("TXT Wagedata Export failed: " + err.message);
    console.error(err);
  }
}


// ── Export Service Invoice
function exportInvoice(company, month, year, emps, salMap) {
  const totalNet = Math.round(emps.reduce((a,e)=>a+(salMap[e.id]?.net||0),0));
  const totalPF  = Math.round(emps.reduce((a,e)=>a+(salMap[e.id]?.pf_emp||0)+(salMap[e.id]?.pf_er||0),0));
  const totalESI = Math.round(emps.reduce((a,e)=>a+(salMap[e.id]?.esi_emp||0)+(salMap[e.id]?.esi_er||0),0));
  const totalLiability = totalNet + totalPF + totalESI;
  const fee = Math.round(emps.length * 150);
  const invoiceNo = "TW-"+year+("0"+month).slice(-2)+"-"+String(company.name||"").substring(0,3).toUpperCase().replace(/\s/g,"");
  const doc = new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
  const W=210, M=16;
  const fmtN = v => "Rs."+Math.round(parseFloat(v||0)).toLocaleString("en-IN",{minimumFractionDigits:0,maximumFractionDigits:0});

  // Header image
  addDocHeader(doc, W, 297);
  let invHdrY = 22;
  doc.setTextColor(79,142,247); doc.setFontSize(16); doc.setFont("helvetica","bold");
  doc.text("SERVICE INVOICE", W-M, invHdrY-4, {align:"right"});

  let y=44;
  // Invoice meta
  doc.setFillColor(240,245,255); doc.roundedRect(M,y,W-M*2,28,3,3,"F");
  const metaLeft=[["Invoice No:",invoiceNo],["Invoice Date:",new Date().toLocaleDateString("en-IN")],["Pay Period:",MONTHS[month]+" "+year]];
  const metaRight=[["Client:",company.name||""],["Contact:",company.contact||company.email||"—"],["Employees:",String(emps.length)]];
  metaLeft.forEach(([l,v],i)=>{
    doc.setFont("helvetica","bold"); doc.setTextColor(26,58,92); doc.setFontSize(8.5);
    doc.text(l,M+4,y+8+i*7); doc.setFont("helvetica","normal"); doc.setTextColor(50,50,80);
    doc.text(v,M+32,y+8+i*7);
  });
  metaRight.forEach(([l,v],i)=>{
    doc.setFont("helvetica","bold"); doc.setTextColor(26,58,92); doc.setFontSize(8.5);
    doc.text(l,W/2+4,y+8+i*7); doc.setFont("helvetica","normal"); doc.setTextColor(50,50,80);
    doc.text(v,W/2+26,y+8+i*7);
  });
  y+=34;

  // Service table
  doc.setFillColor(26,58,92); doc.rect(M,y,W-M*2,8,"F");
  doc.setTextColor(255,255,255); doc.setFontSize(8.5); doc.setFont("helvetica","bold");
  doc.text("#",M+3,y+5.5); doc.text("Description",M+12,y+5.5); doc.text("Amount",W-M-3,y+5.5,{align:"right"});
  y+=8;
  doc.setFillColor(247,250,255); doc.rect(M,y,W-M*2,8,"F");
  doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(8.5);
  doc.text("1",M+3,y+5.5);
  doc.text("Payroll Processing — "+emps.length+" employees @ Rs.150 per employee",M+12,y+5.5);
  doc.text(fmtN(fee),W-M-3,y+5.5,{align:"right"});
  y+=8;
  doc.setFillColor(220,232,255); doc.rect(M,y,W-M*2,10,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(26,58,92);
  doc.text("TOTAL AMOUNT DUE",M+3,y+7); doc.text(fmtN(fee),W-M-3,y+7,{align:"right"});
  y+=12;
  doc.setTextColor(120,120,140); doc.setFontSize(7.5); doc.setFont("helvetica","italic");
  doc.text("GST is not applicable on this invoice.",M+3,y+3);
  y+=8;

  // Payroll summary
  doc.setFillColor(26,58,92); doc.rect(M,y,W-M*2,8,"F");
  doc.setTextColor(255,255,255); doc.setFontSize(8.5); doc.setFont("helvetica","bold");
  doc.text("PAYROLL DISBURSEMENT SUMMARY — "+MONTHS[month]+" "+year,M+3,y+5.5);
  y+=8;
  [[  "Net Salaries Payable to Employees",totalNet],
   ["PF Contributions (Employee + Employer)",totalPF],
   ["ESI Contributions (Employee + Employer)",totalESI],
  ].forEach(([desc,amt],i)=>{
    if(i%2===0){doc.setFillColor(247,250,255);doc.rect(M,y,W-M*2,8,"F");}
    else{doc.setFillColor(255,255,255);doc.rect(M,y,W-M*2,8,"F");}
    doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(8.5);
    doc.text(desc,M+3,y+5.5); doc.text(fmtN(amt),W-M-3,y+5.5,{align:"right"});
    y+=8;
  });
  doc.setFillColor(210,235,210); doc.rect(M,y,W-M*2,10,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(9.5); doc.setTextColor(20,80,40);
  doc.text("TOTAL COMPANY LIABILITY THIS MONTH",M+3,y+7);
  doc.text(fmtN(totalLiability+fee),W-M-3,y+7,{align:"right"});
  y+=16;

  // Employee breakdown
  if(emps.length>0){
    doc.setFont("helvetica","bold"); doc.setTextColor(26,58,92); doc.setFontSize(8);
    doc.text("EMPLOYEE-WISE BREAKDOWN",M,y); y+=5;
    doc.setFillColor(26,58,92); doc.rect(M,y,W-M*2,7,"F");
    doc.setTextColor(255,255,255); doc.setFontSize(7.5); doc.setFont("helvetica","bold");
    doc.text("Employee",M+2,y+4.8); doc.text("Basic",M+66,y+4.8,{align:"right"});
    doc.text("Gross",M+86,y+4.8,{align:"right"}); doc.text("Deductions",M+112,y+4.8,{align:"right"});
    doc.text("Net Pay",M+136,y+4.8,{align:"right"}); doc.text("CTC",W-M-2,y+4.8,{align:"right"});
    y+=7;
    emps.slice(0,22).forEach((e,i)=>{
      const s=salMap[e.id]||{};
      if(i%2===0){doc.setFillColor(248,251,255);doc.rect(M,y,W-M*2,6.5,"F");}
      doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(7.5);
      doc.text((e.name||"").substring(0,24),M+2,y+4.5);
      doc.text(fmtN(s.basic||0),M+66,y+4.5,{align:"right"});
      doc.text(fmtN(s.gross||0),M+86,y+4.5,{align:"right"});
      doc.text(fmtN((s.pf_emp||0)+(s.esi_emp||0)+(s.pt||0)),M+112,y+4.5,{align:"right"});
      doc.text(fmtN(s.net||0),M+136,y+4.5,{align:"right"});
      doc.text(fmtN(s.ctc||0),W-M-2,y+4.5,{align:"right"});
      y+=6.5;
      if(y>270){doc.addPage();y=20;}
    });
    if(emps.length>22){
      doc.setTextColor(130,130,150); doc.setFontSize(7);
      doc.text("... and "+(emps.length-22)+" more employees",M+2,y+4); y+=8;
    }
  }
  y+=5;
  doc.setDrawColor(180,200,230); doc.setLineWidth(0.3); doc.line(M,y,W-M,y); y+=5;
  doc.setTextColor(130,130,150); doc.setFontSize(7); doc.setFont("helvetica","italic");
  doc.text("This is a computer-generated invoice. For queries: Email: payroll@trustwork.co.in | Ph: +91 93530 10107",W/2,y,{align:"center"});
  doc.text("TrustWork Solutions — Work Simplified. Compliance Assured.",W/2,y+4,{align:"center"});

  doc.save(company.name.replace(/ /g,"_")+"_Invoice_"+MONTHS[month]+"_"+year+".pdf");
}


// ─────────────────────────────────────────────────────────────────────────────
// ── JOINING KIT — FORM BUILDERS
// ─────────────────────────────────────────────────────────────────────────────

export function jkFmtDate(s) {
  return formatDateDDMMYYYY(s);
}

export function DatePickerDDMMYYYY({ value, onChange, min, max, style = {}, className = "config-input" }) {
  const hiddenInputRef = useRef(null);

  const displayVal = useMemo(() => {
    return formatDateDDMMYYYY(value);
  }, [value]);

  const [textVal, setTextVal] = useState(displayVal);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setTextVal(displayVal);
    setErrorMsg("");
  }, [displayVal]);

  const validateAndNotify = (d, m, y) => {
    const dayNum = parseInt(d, 10);
    const monthNum = parseInt(m, 10);
    const yearNum = parseInt(y, 10);
    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) return false;
    if (monthNum < 1 || monthNum > 12 || yearNum < 1900 || yearNum > 2100 || dayNum < 1 || dayNum > 31) {
      setErrorMsg("Invalid date components");
      return false;
    }
    const dt = new Date(yearNum, monthNum - 1, dayNum);
    if (dt.getFullYear() !== yearNum || dt.getMonth() !== monthNum - 1 || dt.getDate() !== dayNum) {
      setErrorMsg("Invalid calendar date for this month");
      return false;
    }
    const iso = `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    if (max && iso > max) {
      setErrorMsg(`Date cannot be after ${formatDateDDMMYYYY(max)}`);
      return false;
    }
    if (min && iso < min) {
      setErrorMsg(`Date cannot be before ${formatDateDDMMYYYY(min)}`);
      return false;
    }
    setErrorMsg("");
    onChange(iso);
    return true;
  };

  const handleTextChange = (e) => {
    const raw = e.target.value;
    setTextVal(raw);
    const clean = raw.trim();
    if (!clean) {
      setErrorMsg("");
      onChange("");
      return;
    }

    let d, m, y;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
      [d, m, y] = clean.split("/");
    } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
      [d, m, y] = clean.split("-");
    } else if (/^\d{8}$/.test(clean)) {
      d = clean.slice(0, 2);
      m = clean.slice(2, 4);
      y = clean.slice(4, 8);
    }

    if (d && m && y && y.length === 4) {
      const ok = validateAndNotify(d, m, y);
      if (ok && /^\d{8}$/.test(clean)) {
        setTextVal(`${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`);
      }
    }
  };

  const handleBlur = () => {
    const clean = textVal.trim();
    if (!clean) {
      setTextVal("");
      setErrorMsg("");
      onChange("");
      return;
    }
    let d, m, y;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
      [d, m, y] = clean.split("/");
    } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
      [d, m, y] = clean.split("-");
    } else if (/^\d{8}$/.test(clean)) {
      d = clean.slice(0, 2);
      m = clean.slice(2, 4);
      y = clean.slice(4, 8);
    }
    if (d && m && y && validateAndNotify(d, m, y)) {
      setTextVal(`${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`);
    } else {
      setTextVal(displayVal);
      setErrorMsg("");
    }
  };

  const isFullWidth = style?.width === "100%";

  return (
    <div style={{ position: "relative", display: isFullWidth ? "flex" : "inline-flex", flexDirection: "column", width: isFullWidth ? "100%" : undefined }}>
      <div style={{ position: "relative", display: isFullWidth ? "flex" : "inline-flex", alignItems: "center", width: isFullWidth ? "100%" : undefined }}>
        <input
          type="text"
          className={className}
          value={textVal}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder="DD/MM/YYYY"
          title={errorMsg ? errorMsg : "Date in DD/MM/YYYY format (e.g. 15/08/1995 or 15081995). Click calendar to pick."}
          style={{
            height: 34,
            padding: "4px 34px 4px 10px",
            fontSize: 13,
            fontWeight: 600,
            margin: 0,
            width: isFullWidth ? "100%" : 140,
            letterSpacing: 0.5,
            boxSizing: "border-box",
            borderColor: errorMsg ? "#ef4444" : style?.borderColor,
            background: errorMsg ? "#fef2f2" : (style?.background || "#f8fafc"),
            ...style
          }}
        />
        <button
          type="button"
          title="Open calendar picker"
          onClick={() => {
            try {
              if (hiddenInputRef.current?.showPicker) {
                hiddenInputRef.current.showPicker();
              } else {
                hiddenInputRef.current?.focus();
              }
            } catch (e) {
              hiddenInputRef.current?.focus();
            }
          }}
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            padding: 0,
            display: "flex",
            alignItems: "center"
          }}
        >
          📅
        </button>
        <input
          ref={hiddenInputRef}
          type="date"
          value={value || ""}
          min={min}
          max={max}
          onChange={e => {
            if (e.target.value) onChange(e.target.value);
          }}
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 0,
            height: 0,
            bottom: 0,
            right: 0
          }}
          tabIndex={-1}
        />
      </div>
      {errorMsg && (
        <div style={{ fontSize: 10.5, color: "#dc2626", fontWeight: 700, marginTop: 2, lineHeight: 1.2 }}>
          ⚠️ {errorMsg}
        </div>
      )}
    </div>
  );
}

async function buildJKForm11(e, c) {
  const { Document, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, Packer } = window.docx;
  const T = (t, o={}) => new TextRun({ text: String(t ?? ''), bold: o.b, size: o.s||18, underline: o.u?{}:undefined });
  const P = (t, o={}) => new Paragraph({ alignment: o.a, spacing: { after: o.after??60, before: o.before??0 }, children: [T(t, o)] });
  const W = [6000, 4466];
  const cell = (t, w, o={}) => new TableCell({ width: { size: w, type: WidthType.DXA }, margins: { top:50, bottom:50, left:80, right:80 }, children: [P(t, { after:0, ...o })] });
  const row = (a, b, o={}) => new TableRow({ children: [cell(a, W[0], o), cell(b, W[1])] });
  const yn = v => (v === 'Yes' ? 'Yes' : 'No');
  const tick = v => v ? '\u2611' : '\u2610';
  const isSp = e.rel_type === 'Spouse' || (!e.father_name && e.spouse_name);
  const prev = e.prev_pf === 'Yes' || e.prev_eps === 'Yes';
  const maritalStatus = e.marital_status || e.maritial_status || e.maritalStatus || e.marital || (e.spouse_name ? 'Married' : 'Single');
  const rows = [
    row('1.  Name of the member', e.name),
    row(`2.  Father's Name ${tick(!isSp)}   Spouse's Name ${tick(isSp)}`, isSp ? (e.spouse_name || e.father_name || '') : (e.father_name || e.spouse_name || '')),
    row('3.  Date of Birth (DD/MM/YYYY)', jkFmtDate(e.dob || e.date_of_birth)),
    row('4.  Gender (Male/Female/Transgender)', e.gender || ''),
    row('5.  Marital Status', maritalStatus),
    row('6.  (a) Email ID   (b) Mobile No.', `${e.email||''}  |  ${e.phone||''}`),
    row('7.  Earlier a member of Employees\u2019 Provident Fund Scheme, 1952', yn(e.prev_pf)),
    row('8.  Earlier a member of Employees\u2019 Pension Scheme, 1995', yn(e.prev_eps)),
    row('9.  Previous employment (if Yes to 7 and/or 8)', '', { b:true }),
    row('     a) Universal Account Number', prev ? (e.uan || '') : ''),
    row('     b) Previous PF Account Number', prev ? (e.prev_pf_account || e.pf_account || e.pf_account_number || '') : ''),
    row('     c) Date of exit from previous employment', prev ? jkFmtDate(e.prev_exit_date) : ''),
    row('     d) Scheme Certificate No. (if issued)', prev ? (e.scheme_cert || '') : ''),
    row('     e) Pension Payment Order (PPO) No. (if issued)', prev ? (e.ppo_number || '') : ''),
    row('10. (a) International Worker', yn(e.intl_worker)),
    row('     b) Country of origin', e.intl_worker==='Yes' ? (e.country_of_origin || '') : ''),
    row('     c) Passport No.', e.intl_worker==='Yes' ? (e.passport_number || '') : ''),
    row('     d) Validity of passport', e.intl_worker==='Yes' ? (e.passport_validity || '') : ''),
    row('11. KYC Details (attach self-attested copies)', '', { b:true }),
    row('     a) Bank Account No. & IFSC Code', `${e.bank_account || e.bank_account_number || ''}  ${e.bank_ifsc || ''}`.trim()),
    row('     b) Aadhaar Number (last 4 digits)', e.aadhaar_last4 ? `XXXX XXXX ${e.aadhaar_last4}` : ''),
    row('     c) Permanent Account Number (PAN)', e.pan || ''),
  ];
  const und = [
    'Certified that the particulars are true to the best of my knowledge.',
    'I authorize EPFO to use my Aadhaar for verification/authentication/eKYC purpose for service delivery.',
    'Kindly transfer the funds and service details, if applicable, from the previous PF account as declared above to the present P.F. Account.',
    'In case of changes in above details, the same will be intimated to employer at the earliest.',
  ];
  const doc = new Document({
    sections: [{
      properties: { page: { size: { width:11906, height:16838 }, margin: { top:500, bottom:500, left:720, right:720 } } },
      children: [
        P('New Form No.-11 \u2013 Declaration Form', { b:true, s:22, a:AlignmentType.RIGHT, after:0 }),
        P('(To be retained by the employer for future reference)', { s:16, a:AlignmentType.RIGHT }),
        P('EMPLOYEES\u2019 PROVIDENT FUND ORGANISATION', { b:true, s:22, a:AlignmentType.CENTER, after:0 }),
        P('Employees\u2019 Provident Funds Scheme, 1952 (Paragraph 34 & 57) & Employees\u2019 Pension Scheme, 1995 (Paragraph 24)', { s:16, a:AlignmentType.CENTER, after:0 }),
        P('(Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable)', { b:true, s:16, a:AlignmentType.CENTER, after:100 }),
        new Table({ width: { size:10466, type:WidthType.DXA }, columnWidths:W, rows }),
        P('UNDERTAKING', { b:true, u:true, a:AlignmentType.CENTER, before:120 }),
        ...und.map((t,i) => P(`${i+1})  ${t}`, { s:16, after:30 })),
        P('Date:', { before:160, after:0 }),
        new Paragraph({ spacing:{ after:100 }, children:[T('Place:'), T('\t\t\t\t\t\t\t\tSignature of Member')] }),
        P('DECLARATION BY PRESENT EMPLOYER', { b:true, u:true, a:AlignmentType.CENTER, before:80 }),
        P(`A.  The member Mr./Ms. ${e.name}  has joined on ${jkFmtDate(e.doj || e.date_of_joining)}  and has been allotted PF Number ${e.pf_account || e.pf_account_number || '......................................'}`, { s:16 }),
        P('B.  In case the person was earlier not a member of EPF Scheme, 1952 and EPS, 1995:', { s:16 }),
        P('     (Post allotment of UAN) The UAN allotted for the member is ..............................', { s:16, after:30 }),
        P(`Date:                                                  Signature of Employer with Seal${c ? ' \u2013 '+c.name : ''}`, { before:160 }),
      ],
    }],
  });
  return Packer.toBlob(doc);
}

async function buildJKForm2(e, c) {
  const { Document, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, Packer } = window.docx;
  const T = (t, o={}) => new TextRun({ text: String(t ?? ''), bold: o.b, size: o.s||18 });
  const P = (t, o={}) => new Paragraph({ alignment: o.a, spacing: { after: o.after??60, before: o.before??0 }, children: [T(t, o)] });
  const cell = (t, w, o={}) => new TableCell({ width: { size:w, type:WidthType.DXA }, margins:{top:50,bottom:50,left:80,right:80}, children:[P(t,{after:0,...o})] });
  const hdrRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[new Paragraph({children:[new TextRun({text:t,bold:true,size:18})]})] })) });
  const dataRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[P(t,{after:0})] })) });
  
  let noms = e.nominees || [];
  if (typeof noms === 'string') {
    try { noms = JSON.parse(noms); } catch(err) { noms = []; }
  }
  if (!Array.isArray(noms)) noms = [];

  let famList = e.family_members || e.family || [];
  if (typeof famList === 'string') {
    try { famList = JSON.parse(famList); } catch(err) { famList = []; }
  }
  if (!Array.isArray(famList)) famList = [];

  // Fallback to nominees or spouse/father if family_members list is empty so Part B EPS nomination is never blank
  if (famList.length === 0 && noms.length > 0) {
    famList = noms.map(n => ({
      name: n.name,
      relationship: n.relationship,
      dob: n.dob || n.date_of_birth || '',
      residing: 'Yes'
    }));
  }
  if (famList.length === 0 && e.spouse_name) {
    famList.push({
      name: e.spouse_name,
      relationship: 'Spouse',
      dob: '',
      residing: 'Yes'
    });
  }
  if (famList.length === 0 && e.father_name) {
    famList.push({
      name: e.father_name,
      relationship: 'Father',
      dob: '',
      residing: 'Yes'
    });
  }

  const maritalStatus = e.marital_status || e.maritial_status || e.maritalStatus || e.marital || (e.spouse_name ? 'Married' : 'Single');
  const nomRows = noms.length > 0 ? noms.map(n => dataRow(
    [n.name || '', 2500],
    [n.relationship || '', 1500],
    [(n.dob || n.date_of_birth) ? jkFmtDate(n.dob || n.date_of_birth) : '', 1200],
    [n.address || e.perm_address || '', 2500],
    [String(n.share || (noms.length === 1 ? '100' : '')) + '%', 800]
  )) : [dataRow(['',2500],['',1500],['',1200],['',2500],['',800])];

  const famRows = famList.length > 0 ? famList.map(f => dataRow(
    [f.name || '', 3000],
    [f.relationship || '', 2000],
    [(f.dob || f.date_of_birth) ? jkFmtDate(f.dob || f.date_of_birth) : '', 1500],
    [String(f.residing).toLowerCase() === 'no' ? 'No' : 'Yes', 1000]
  )) : [dataRow(['',3000],['',2000],['',1500],['',1000])];

  const doc = new Document({
    sections:[{
      properties:{ page:{ size:{width:11906,height:16838}, margin:{top:500,bottom:500,left:720,right:720} } },
      children:[
        P('FORM 2 (Revised)', {b:true, s:24, a:AlignmentType.CENTER, after:20}),
        P('[See para 33 & 61(1) of the Employees Provident Fund Scheme, 1952 and para 18 of the Employees Pension Scheme, 1995]', {s:16, a:AlignmentType.CENTER}),
        P('NOMINATION AND DECLARATION FORM FOR UNEXEMPTED / EXEMPTED ESTABLISHMENTS', {b:true, s:20, a:AlignmentType.CENTER}),
        P(' ', {after:20}),
        P(`Name of Member: ${e.name}`, {b:true}),
        P(`Father's / Husband's Name: ${e.rel_type==='Spouse'?e.spouse_name:e.father_name}`),
        P(`Date of Birth: ${jkFmtDate(e.dob || e.date_of_birth)}    Gender: ${e.gender || ''}    Marital Status: ${maritalStatus}`),
        P(`Date of Joining: ${jkFmtDate(e.doj || e.date_of_joining)}    Department: ${e.department||''}    Designation: ${e.designation||''}`),
        P(`PF Account No.: ${e.pf_account || e.pf_account_number || ''}    UAN: ${e.uan||''}`),
        P(`Permanent Address: ${e.perm_address||''}, ${e.perm_city||''}, ${e.perm_state||''} - ${e.perm_pincode||''}`),
        P(' ', {after:10}),
        P('PART A — NOMINATION FOR EPF (Employees Provident Fund)', {b:true, s:20, after:20}),
        new Table({ width:{size:10466,type:WidthType.DXA}, rows:[
          hdrRow(['Name of Nominee',2500],['Relationship',1500],['Date of Birth',1200],['Address of Nominee',2500],['Share %',800]),
          ...nomRows
        ]}),
        P(' ', {after:20}),
        P('PART B — NOMINATION FOR EPS (Employees Pension Scheme)', {b:true, s:20, after:20}),
        P('(Family members eligible for pension on your death)', {s:16}),
        new Table({ width:{size:10466,type:WidthType.DXA}, rows:[
          hdrRow(['Name',3000],['Relationship',2000],['Date of Birth',1500],['Residing With You',1000]),
          ...famRows
        ]}),
        P(' ', {after:40}),
        P('Declaration: I hereby declare that the particulars given above are true and correct.', {s:16}),
        P(' ', {after:80}),
        P(`Date:                                                 Signature of Employee: ${e.name}`, {before:60}),
        P(' ', {after:40}),
        P(`Certified by Employer: ${c?.name||''}                   Authorised Signatory: ${c?.contact||''}`, {before:40}),
      ],
    }],
  });
  return Packer.toBlob(doc);
}

async function buildJKESIForm(e, c) {
  const { Document, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, Packer } = window.docx;
  const T = (t, o={}) => new TextRun({ text: String(t ?? ''), bold: o.b, size: o.s||18 });
  const P = (t, o={}) => new Paragraph({ alignment: o.a, spacing: { after: o.after??60, before: o.before??0 }, children: [T(t, o)] });
  const cell = (t, w, o={}) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[P(t,{after:0,...o})] });
  const hdrRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[new Paragraph({children:[new TextRun({text:t,bold:true,size:18})]})] })) });
  const dataRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[P(t,{after:0})] })) });
  
  let famList = e.family_members || e.family || [];
  if (typeof famList === 'string') {
    try { famList = JSON.parse(famList); } catch(err) { famList = []; }
  }
  if (!Array.isArray(famList)) famList = [];
  if (famList.length === 0 && (e.nominees || []).length > 0) {
    famList = e.nominees;
  }
  const maritalStatus = e.marital_status || e.maritial_status || e.maritalStatus || e.marital || (e.spouse_name ? 'Married' : 'Single');
  const famRows = famList.length > 0 ? famList.map(f => dataRow([f.name||'',3000],[f.relationship||'',2000],[(f.dob || f.date_of_birth) ? jkFmtDate(f.dob || f.date_of_birth) : '',2466])) : [dataRow(['',3000],['',2000],['',2466])];
  const doc = new Document({
    sections:[{
      properties:{ page:{ size:{width:11906,height:16838}, margin:{top:500,bottom:500,left:720,right:720} } },
      children:[
        P('EMPLOYEES STATE INSURANCE CORPORATION', {b:true, s:26, a:AlignmentType.CENTER, after:10}),
        P('DECLARATION FORM (PEHCHAN)', {b:true, s:22, a:AlignmentType.CENTER}),
        P('[See Regulation 14 of the ESI (General) Regulations, 1950]', {s:16, a:AlignmentType.CENTER}),
        P(' ', {after:20}),
        P(`ESI Insurance No.: ${e.esi_id||'(To be allotted)'}    Employer Code: ${c?.esi_code||''}`, {b:true}),
        P(`Name of Employer / Establishment: ${c?.name||''}`),
        P(`Dispensary / Hospital: ${e.dispensary||c?.dispensary||''}`),
        P(' ', {after:10}),
        P('EMPLOYEE DETAILS', {b:true, s:20}),
        P(`Full Name: ${e.name}`),
        P(`Father's / Husband's Name: ${e.rel_type==='Spouse'?e.spouse_name:e.father_name}`),
        P(`Date of Birth: ${jkFmtDate(e.dob || e.date_of_birth)}    Gender: ${e.gender || ''}    Marital Status: ${maritalStatus}`),
        P(`Date of Joining: ${jkFmtDate(e.doj || e.date_of_joining)}    Designation: ${e.designation||''}    Department: ${e.department||''}`),
        P(`Mobile: ${e.phone||''}    Email: ${e.email||''}`),
        P(`Permanent Address: ${e.perm_address||''}, ${e.perm_city||''}, ${e.perm_state||''} - ${e.perm_pincode||''}`),
        P(`Aadhaar: XXXX XXXX ${e.aadhaar_last4||'XXXX'}    PAN: ${e.pan||''}`),
        P(`Bank Account: ${e.bank_account || e.bank_account_number || ''}    IFSC: ${e.bank_ifsc||''}    Bank: ${e.bank_name||''}`),
        P(' ', {after:10}),
        P('FAMILY MEMBERS (eligible for ESI medical benefit)', {b:true, s:20, after:20}),
        new Table({ width:{size:10466,type:WidthType.DXA}, rows:[
          hdrRow(['Name',3000],['Relationship',2000],['Date of Birth',2466]),
          ...famRows
        ]}),
        P(' ', {after:40}),
        P('Declaration: I hereby declare that the information furnished above is true and correct to the best of my knowledge.', {s:16}),
        P(' ', {after:80}),
        P(`Date:                                                 Signature of Employee`, {before:60}),
        P(`Place:                                                Name: ${e.name}`, {before:20}),
        P(' ', {after:60}),
        P(`Employer Certification: ${c?.name||''}`, {before:40}),
        P(`Authorised Signatory: ${c?.contact||''}    Designation: ${c?.pt_reg||''}`),
      ],
    }],
  });
  return Packer.toBlob(doc);
}

async function buildJKFormF(e, c) {
  const { Document, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, Packer } = window.docx;
  const T = (t, o={}) => new TextRun({ text: String(t ?? ''), bold: o.b, size: o.s||18 });
  const P = (t, o={}) => new Paragraph({ alignment: o.a, spacing: { after: o.after??60, before: o.before??0 }, children: [T(t, o)] });
  const hdrRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[new Paragraph({children:[new TextRun({text:t,bold:true,size:18})]})] })) });
  const dataRow = (...cols) => new TableRow({ children: cols.map(([t,w]) => new TableCell({ width:{size:w,type:WidthType.DXA}, margins:{top:50,bottom:50,left:80,right:80}, children:[new Paragraph({children:[new TextRun({text:String(t??''),size:18})]})] })) });
  
  let noms = e.nominees || [];
  if (typeof noms === 'string') {
    try { noms = JSON.parse(noms); } catch(err) { noms = []; }
  }
  if (!Array.isArray(noms)) noms = [];

  const maritalStatus = e.marital_status || e.maritial_status || e.maritalStatus || e.marital || (e.spouse_name ? 'Married' : 'Single');
  const nomRows = noms.length > 0 ? noms.map(n => {
    let ageStr = '';
    if (n.age !== undefined && n.age !== null && String(n.age).trim() !== '') {
      ageStr = String(n.age).includes('yr') ? String(n.age) : `${n.age} yrs`;
    } else if (n.dob || n.date_of_birth) {
      const a = calcAge(n.dob || n.date_of_birth);
      if (typeof a === 'number' && a >= 0) ageStr = `${a} yrs`;
    }
    const shareStr = n.share !== undefined && n.share !== null && String(n.share).trim() !== '' 
      ? (String(n.share).includes('%') ? String(n.share) : `${n.share}%`)
      : (noms.length === 1 ? '100%' : '');
    const nameWithAddr = n.name ? (n.address ? `${n.name}, ${n.address}` : n.name) : '';
    return dataRow(
      [nameWithAddr, 3200],
      [n.relationship || '', 1800],
      [ageStr, 1000],
      [shareStr, 800]
    );
  }) : [dataRow(['',3200],['',1800],['',1000],['',800])];

  const doc = new Document({
    sections:[{
      properties:{ page:{ size:{width:11906,height:16838}, margin:{top:500,bottom:500,left:720,right:720} } },
      children:[
        P("FORM 'F'", {b:true, s:24, a:AlignmentType.CENTER, after:10}),
        P('[See sub-rule (1) of rule 6]', {s:16, a:AlignmentType.CENTER}),
        P('NOMINATION UNDER THE PAYMENT OF GRATUITY ACT, 1972', {b:true, s:20, a:AlignmentType.CENTER}),
        P(' ', {after:20}),
        P(`To,\nThe Employer,\n${c?.name||''}\n${c?.address||''}`, {after:30}),
        P(`I, ${e.name}, whose particulars are given in the statement below, hereby nominate the person(s) mentioned below to receive the gratuity payable after my death and direct that the said amount shall be paid in proportion indicated against their name(s).`, {s:16, after:30}),
        P(' ', {after:10}),
        P('NOMINEE DETAILS', {b:true, s:20, after:20}),
        new Table({ width:{size:10466,type:WidthType.DXA}, rows:[
          hdrRow(['Name & Address of Nominee',3200],['Relationship',1800],['Age',1000],['Share',800]),
          ...nomRows
        ]}),
        P(' ', {after:30}),
        P('STATEMENT OF EMPLOYEE', {b:true, s:20, after:20}),
        P(`1. Name of Employee: ${e.name}`),
        P(`2. Religion: ${e.religion||''}`),
        P(`3. Marital Status: ${maritalStatus}`),
        P(`4. Department / Section: ${e.department||''}`),
        P(`5. Post Held / Designation: ${e.designation||''}`),
        P(`6. Date of Appointment: ${jkFmtDate(e.doj || e.date_of_joining)}`),
        P(`7. Permanent Address: ${e.perm_address||''}, ${e.perm_city||''}, ${e.perm_state||''} - ${e.perm_pincode||''}`),
        P(' ', {after:60}),
        P('Nomination signed before me.', {before:40}),
        P(' ', {after:80}),
        P(`Date:                                           Signature / Thumb Impression of Employee`, {before:40}),
        P(`Place:                                          Name: ${e.name}`, {before:20}),
        P(' ', {after:60}),
        P('CERTIFICATE BY THE EMPLOYER', {b:true, s:20}),
        P(`Certified that the particulars of the above nomination have been verified and recorded in this establishment.`),
        P(`Employer's Reference No.: ${e.employee_code||''}`),
        P(' ', {after:80}),
        P(`Signature of Employer / Authorised Officer`, {before:40}),
        P(`Designation: ${c?.contact||''}    Establishment: ${c?.name||''}`, {before:20}),
      ],
    }],
  });
  return Packer.toBlob(doc);
}

async function exportJKMasterXLS(employees, company) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Joinees Master Data');

  const fmtDDMMYYYY = (val) => {
    if (!val) return "";
    const clean = String(val).split("T")[0].trim();
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(clean)) {
      const [y, m, d] = clean.split("-");
      return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
      const [d, m, y] = clean.split("-");
      return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
      const [d, m, y] = clean.split("/");
      return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
    }
    const dt = new Date(val);
    if (!isNaN(dt.getTime())) {
      return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
    }
    return clean;
  };

  const headers = [
    'Sl No', 'Emp ID', 'Employee Name', 'Date of Birth (DD-MM-YYYY)',
    'Father/Husband Name', 'Date of Joining (DD-MM-YYYY)', 'Gender',
    'Designation', 'Department', 'Location', 'Gross Monthly Salary (₹)',
    'ESI Number', 'PF Number', 'UAN', 'PAN', 'Aadhaar Last4',
    'Bank A/c', 'IFSC', 'Bank Name', 'Bank Branch',
    'Blood Group', 'Mobile', 'Email', 'Emergency Contact', 'Emergency Phone',
    'Marital Status', 'Spouse Name', 'Religion',
    'Present Address', 'Present PIN', 'Permanent Address', 'Permanent PIN', 'State',
    'Nominee Name', 'Nominee %', 'Prev PF Member', 'Prev PF A/c', 'Prev Exit Date (DD-MM-YYYY)'
  ];

  const hdrRow = ws.addRow(headers);
  hdrRow.font = { bold: true, color: { argb: 'FF0F172A' }, size: 10 };
  hdrRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
  ws.columns = headers.map(() => ({ width: 18 }));
  ws.views = [{ state: "frozen", xSplit: 3, ySplit: 1 }];

  (employees || []).forEach((e, idx) => {
    let noms = e.nominees || [];
    if (typeof noms === 'string') {
      try { noms = JSON.parse(noms); } catch(err) { noms = []; }
    }
    const firstNom = (Array.isArray(noms) ? noms : [])[0] || {};
    const grossSal = Math.round(parseFloat(String(e.basic_salary || e.basic || e.gross || 0).replace(/,/g, '')) || 0);

    ws.addRow([
      idx + 1,
      e.employee_code || e.emp_id || '',
      e.name || '',
      fmtDDMMYYYY(e.dob || e.date_of_birth),
      e.rel_type === 'Spouse' && e.spouse_name ? e.spouse_name : (e.father_name || e.spouse_name || ''),
      fmtDDMMYYYY(e.doj || e.date_of_joining),
      e.gender || '',
      e.designation || '',
      e.department || '',
      e.location || '',
      grossSal || '',
      e.esi_id || '',
      e.pf_account || e.pf_account_number || '',
      e.uan || '',
      e.pan || '',
      e.aadhaar_last4 || '',
      e.bank_account || e.bank_account_number || '',
      e.bank_ifsc || '',
      e.bank_name || '',
      e.bank_branch || '',
      e.blood_group || '',
      e.phone || '',
      e.email || '',
      e.emergency_name || '',
      e.emergency_phone || '',
      e.marital_status || e.maritial_status || e.maritalStatus || (e.spouse_name ? 'Married' : 'Single'),
      e.spouse_name || '',
      e.religion || '',
      `${e.temp_address || ''} ${e.temp_city || ''}`.trim(),
      e.temp_pincode || '',
      `${e.perm_address || ''} ${e.perm_city || ''}`.trim(),
      e.perm_pincode || '',
      e.perm_state || '',
      firstNom.name || '',
      firstNom.share ? `${firstNom.share}%` : '',
      e.prev_pf || 'No',
      e.prev_pf_account || e.pf_account || e.pf_account_number || '',
      fmtDDMMYYYY(e.prev_exit_date)
    ]);
  });
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `${(company?.name||'Company').replace(/ /g,'_')}_Master_Joinees_Data.xlsx`;
  a.click(); setTimeout(() => URL.revokeObjectURL(url), 3000);
}

function jkDownloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── JOINING KIT PAGE
// ─────────────────────────────────────────────────────────────────────────────

export const RELIGION_OPTIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi', 'Jewish', 'Other'];

export const POPULAR_INDIAN_BANKS = [
  'State Bank of India',
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
  'Punjab National Bank',
  'Bank of Baroda',
  'Canara Bank',
  'Kotak Mahindra Bank',
  'Union Bank of India',
  'IndusInd Bank',
  'Bank of India',
  'Central Bank of India',
  'Indian Bank',
  'IDBI Bank',
  'Yes Bank',
  'Federal Bank',
  'Indian Overseas Bank',
  'UCO Bank',
  'Bank of Maharashtra',
  'Punjab & Sind Bank',
  'Standard Chartered Bank',
  'HSBC',
  'Citibank',
  'DBS Bank India',
  'Bandhan Bank',
  'IDFC FIRST Bank',
  'RBL Bank',
  'South Indian Bank',
  'Karur Vysya Bank',
  'City Union Bank',
  'Karnataka Bank'
];

export const IFSC_BANK_PREFIX_MAP = {
  'SBIN': 'State Bank of India',
  'HDFC': 'HDFC Bank',
  'ICIC': 'ICICI Bank',
  'UTIB': 'Axis Bank',
  'PUNB': 'Punjab National Bank',
  'BARB': 'Bank of Baroda',
  'CNRB': 'Canara Bank',
  'KKBK': 'Kotak Mahindra Bank',
  'UBIN': 'Union Bank of India',
  'INDB': 'IndusInd Bank',
  'BKID': 'Bank of India',
  'CBIN': 'Central Bank of India',
  'IDIB': 'Indian Bank',
  'IBKL': 'IDBI Bank',
  'YESB': 'Yes Bank',
  'FDRL': 'Federal Bank',
  'IOBA': 'Indian Overseas Bank',
  'UCBA': 'UCO Bank',
  'MAHB': 'Bank of Maharashtra',
  'PSIB': 'Punjab & Sind Bank',
  'SCBL': 'Standard Chartered Bank',
  'HSBC': 'HSBC',
  'CITI': 'Citibank',
  'DBSS': 'DBS Bank India',
  'BDBL': 'Bandhan Bank',
  'IDFB': 'IDFC FIRST Bank',
  'RATN': 'RBL Bank',
  'SIBL': 'South Indian Bank',
  'KVBL': 'Karur Vysya Bank',
  'CIUB': 'City Union Bank',
  'KARB': 'Karnataka Bank',
};

export const isValidIFSC = (code) => {
  if (!code) return false;
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(code).trim().toUpperCase());
};

export const isValidBankAccount = (acc) => {
  if (!acc) return false;
  const clean = String(acc).trim().replace(/\D/g, '');
  return clean.length >= 9 && clean.length <= 18;
};

export const isValidUAN = (uan) => {
  if (!uan) return false;
  const clean = String(uan).trim().replace(/\D/g, '');
  return clean.length === 12;
};

function ReligionField({ value, onChange, label = "Religion" }) {
  const isStandard = RELIGION_OPTIONS.slice(0, -1).includes(value);
  const isOther = value === 'Other' || (value && !isStandard);
  const [customText, setCustomText] = useState(isStandard ? '' : (value === 'Other' ? '' : value));

  return (
    <div>
      <label className="form-label">{label}</label>
      <select
        className="form-select"
        value={isStandard ? value : (isOther ? 'Other' : '')}
        onChange={e => {
          const val = e.target.value;
          if (val === 'Other') {
            onChange(customText.trim() || 'Other');
          } else {
            setCustomText('');
            onChange(val);
          }
        }}
      >
        <option value="">-- Select Religion --</option>
        {RELIGION_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      {(isOther || value === 'Other') && (
        <input
          type="text"
          className="form-input"
          style={{marginTop: 6}}
          placeholder="Please specify other religion..."
          value={customText || (value !== 'Other' ? (value || '') : '')}
          onChange={e => {
            const txt = e.target.value;
            setCustomText(txt);
            onChange(txt || 'Other');
          }}
        />
      )}
    </div>
  );
}

function processEmployeePhoto(file) {
  return new Promise((resolve) => {
    if (!file) return resolve({ error: 'No file selected.' });
    if (typeof file === 'string') return resolve({ ok: true, dataUrl: file });
    
    // Strict file format check (jpg, jpeg, png, webp)
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const validExts = ['.jpg', '.jpeg', '.png', '.webp'];
    const ext = (file.name || '').substring((file.name || '').lastIndexOf('.')).toLowerCase();
    const fileType = (file.type || '').toLowerCase();
    
    if (!validTypes.includes(fileType) && !validExts.includes(ext)) {
      return resolve({ error: 'Unsupported file format. Please upload JPG, PNG, or WEBP images only.' });
    }
    
    // Strict file size check (Max 4MB)
    const MAX_SIZE = 4 * 1024 * 1024; // 4MB
    if (file.size > MAX_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return resolve({ error: `File size (${sizeMB} MB) exceeds the 4MB limit. Please upload an image under 4MB.` });
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const maxDim = 320;
          let w = img.width;
          let h = img.height;
          if (w > h) {
            if (w > maxDim) {
              h = Math.round((h * maxDim) / w);
              w = maxDim;
            }
          } else {
            if (h > maxDim) {
              w = Math.round((w * maxDim) / h);
              h = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          let compressedDataUrl = canvas.toDataURL('image/webp', 0.82);
          if (!compressedDataUrl || !compressedDataUrl.startsWith('data:image/webp')) {
            const fallbackCanvas = document.createElement('canvas');
            fallbackCanvas.width = w;
            fallbackCanvas.height = h;
            const fbCtx = fallbackCanvas.getContext('2d');
            fbCtx.fillStyle = '#ffffff';
            fbCtx.fillRect(0, 0, w, h);
            fbCtx.drawImage(canvas, 0, 0);
            compressedDataUrl = fallbackCanvas.toDataURL('image/jpeg', 0.85);
          }
          resolve({ ok: true, dataUrl: compressedDataUrl });
        } catch (err) {
          resolve({ ok: true, dataUrl: e.target.result });
        }
      };
      img.onerror = () => {
        resolve({ error: 'Could not load image. File may be corrupted.' });
      };
      img.src = e.target.result;
    };
    reader.onerror = () => resolve({ error: 'Failed to read file.' });
    reader.readAsDataURL(file);
  });
}

export function processEmployeeDocument(file) {
  return new Promise((resolve) => {
    if (!file) return resolve({ error: 'No file selected.' });
    if (typeof file === 'string') return resolve({ ok: true, dataUrl: file });

    const MAX_DOC_SIZE = 4 * 1024 * 1024; // 4MB limit
    if (file.size > MAX_DOC_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return resolve({ error: `File size (${sizeMB} MB) exceeds the 4MB limit. Please upload a document or image under 4MB.` });
    }

    const isPdf = file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf');
    if (isPdf) {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ ok: true, isPdf: true, dataUrl: e.target.result, file });
      reader.onerror = () => resolve({ error: 'Failed to read PDF file.' });
      reader.readAsDataURL(file);
      return;
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const ext = (file.name || '').substring((file.name || '').lastIndexOf('.')).toLowerCase();
    const fileType = (file.type || '').toLowerCase();
    if (!validImageTypes.includes(fileType) && !['.jpg', '.jpeg', '.png', '.webp'].includes(ext)) {
      return resolve({ error: 'Please upload JPG, PNG, WEBP, or PDF files only.' });
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const maxDim = 1280;
          let w = img.width;
          let h = img.height;
          if (w > h) {
            if (w > maxDim) {
              h = Math.round((h * maxDim) / w);
              w = maxDim;
            }
          } else {
            if (h > maxDim) {
              w = Math.round((w * maxDim) / h);
              h = maxDim;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          let compressed = canvas.toDataURL('image/webp', 0.82);
          if (!compressed || !compressed.startsWith('data:image/webp')) {
            const fbCanvas = document.createElement('canvas');
            fbCanvas.width = w; fbCanvas.height = h;
            const fbCtx = fbCanvas.getContext('2d');
            fbCtx.fillStyle = '#ffffff';
            fbCtx.fillRect(0, 0, w, h);
            fbCtx.drawImage(canvas, 0, 0);
            compressed = fbCanvas.toDataURL('image/jpeg', 0.85);
          }
          resolve({ ok: true, isPdf: false, dataUrl: compressed, file });
        } catch (err) {
          resolve({ ok: true, isPdf: false, dataUrl: e.target.result, file });
        }
      };
      img.onerror = () => resolve({ error: 'Could not load image.' });
      img.src = e.target.result;
    };
    reader.onerror = () => resolve({ error: 'Failed to read file.' });
    reader.readAsDataURL(file);
  });
}

export const getCleanFileIdentifier = (empCode, empName, docType = '') => {
  const sanitize = s => String(s || '').trim().replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  const cCode = sanitize(empCode) || 'EMP';
  const cName = sanitize(empName) || 'Employee';
  const cDoc = sanitize(docType);
  return [cCode, cName, cDoc].filter(Boolean).join('_');
};

export async function uploadToR2(fileOrDataUrl, keyPrefix = 'photos', customIdentifier = null) {
  if (!fileOrDataUrl) return null;
  if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.startsWith('/api/files/')) {
    return fileOrDataUrl;
  }
  try {
    const timestamp = Date.now();
    let fileBaseName = '';
    if (customIdentifier && typeof customIdentifier === 'object') {
      fileBaseName = getCleanFileIdentifier(
        customIdentifier.empCode || customIdentifier.employee_code || customIdentifier.id,
        customIdentifier.empName || customIdentifier.name,
        customIdentifier.docType || customIdentifier.doc
      );
    } else if (typeof customIdentifier === 'string' && customIdentifier.trim()) {
      fileBaseName = customIdentifier.trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    } else {
      const defaultPrefix = keyPrefix === 'logos' ? 'logo' : (keyPrefix === 'documents' ? 'doc' : 'emp');
      const rand = Math.random().toString(36).substring(2, 6);
      fileBaseName = `${defaultPrefix}_${rand}`;
    }

    let path = '';
    let body = null;
    let headers = {};

    if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.startsWith('data:')) {
      let ext = 'webp';
      if (fileOrDataUrl.includes('application/pdf')) ext = 'pdf';
      else if (fileOrDataUrl.includes('image/webp')) ext = 'webp';
      else if (fileOrDataUrl.includes('image/png')) ext = 'png';
      else if (fileOrDataUrl.includes('image/jpeg') || fileOrDataUrl.includes('image/jpg')) ext = 'jpg';
      path = `${keyPrefix}/${fileBaseName}_${timestamp}.${ext}`;
      body = JSON.stringify({ dataUrl: fileOrDataUrl, path });
      headers['Content-Type'] = 'application/json';
    } else if (fileOrDataUrl instanceof Blob || fileOrDataUrl instanceof File) {
      let ext = (fileOrDataUrl.name && fileOrDataUrl.name.split('.').pop().toLowerCase()) || 'webp';
      path = `${keyPrefix}/${fileBaseName}_${timestamp}.${ext}`;
      const fd = new FormData();
      fd.append('file', fileOrDataUrl);
      fd.append('path', path);
      body = fd;
    } else {
      return null;
    }

    const savedSession = JSON.parse(localStorage.getItem('tw_current_user') || 'null');
    const companyId = ['employee','company'].includes(savedSession?.role) ? savedSession.companyId : sessionStorage.getItem('tw_active_company_id');
    const employeeId = savedSession?.role === 'employee' ? savedSession.employeeId : (customIdentifier?.employee_id || null);
    const shared = keyPrefix === 'logos';
    if (body instanceof FormData) {
      if (companyId) body.append('company_id', companyId);
      if (employeeId) body.append('employee_id', employeeId);
      body.append('shared', String(shared));
    } else {
      body = JSON.stringify({ ...JSON.parse(body), company_id: companyId, employee_id: employeeId, shared });
    }
    const res = await fetch('/api/upload', {
      method: 'POST',
      headers,
      body
    });

    if (res.ok) {
      const json = await res.json();
      if (json && json.url) {
        return json.url;
      }
    } else {
      const errText = await res.text();
      console.error("R2 upload failed:", res.status, errText);
    }
  } catch (err) {
    console.error("R2 upload error:", err);
  }
  return null;
}

export async function deleteFromR2(keys = [], prefixes = []) {
  if (prefixes.length) throw new Error('Use explicit authorized file keys for deletion.');
  if (!keys.length) return null;
  let deletedCount = 0;
  for (let offset = 0; offset < keys.length; offset += 500) {
    const response = await fetch('/api/delete', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys: keys.slice(offset, offset + 500) })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'File cleanup failed. Records have not been deleted; retry before continuing.');
    deletedCount += result.deletedCount || 0;
  }
  return { ok: true, deletedCount };
}

export async function deleteEmployeeFilesAndStorage(employeesList, company = null) {
  const emps = Array.isArray(employeesList) ? employeesList : (employeesList ? [employeesList] : []);
  if (emps.length === 0) return null;

  const keysSet = new Set();

  emps.forEach(emp => {
    if (!emp) return;
    const empId = emp.id;
    const empCode = emp.employee_code || emp.emp_code;
    const empName = emp.name;

    const addUrl = url => {
      if (!url || typeof url !== 'string') return;
      if (url.startsWith('/api/files/')) {
        keysSet.add(url.replace('/api/files/', ''));
      } else if (url.startsWith('photos/') || url.startsWith('documents/')) {
        keysSet.add(url);
      }
    };

    // Employee photos & documents
    addUrl(emp.emp_photo);
    addUrl(emp.emp_aadhaar_front_doc);
    addUrl(emp.emp_aadhaar_back_doc);
    addUrl(emp.emp_aadhaar_doc);
    addUrl(emp.emp_pan_doc);
    addUrl(emp.emp_bank_doc);

    // Company config caches
    if (company?.config?.employee_photos) {
      if (empId) addUrl(company.config.employee_photos[empId]);
      if (empCode) addUrl(company.config.employee_photos[empCode]);
    }
    if (company?.config?.employee_documents) {
      const docEntries = [
        company.config.employee_documents[empId],
        empCode && company.config.employee_documents[empCode]
      ].filter(Boolean);
      docEntries.forEach(entry => {
        if (typeof entry === 'string') addUrl(entry);
        else if (typeof entry === 'object') {
          Object.values(entry).forEach(v => addUrl(v));
        }
      });
    }

    // Family member photos and documents
    const family = Array.isArray(emp.family_members) ? emp.family_members : (Array.isArray(emp.family) ? emp.family : []);
    family.forEach(m => {
      if (m && typeof m === 'object') {
        Object.values(m).forEach(v => addUrl(v));
      }
    });

    // Custom documents
    if (Array.isArray(emp.documents)) {
      emp.documents.forEach(d => {
        if (typeof d === 'string') addUrl(d);
        else if (d && typeof d === 'object') Object.values(d).forEach(v => addUrl(v));
      });
    } else if (emp.documents && typeof emp.documents === 'object') {
      Object.values(emp.documents).forEach(v => addUrl(v));
    }

    // LocalStorage keys
    try {
      if (empId) {
        localStorage.removeItem("tw_photo_" + empId);
        localStorage.removeItem("tw_docs_" + empId);
      }
      if (empCode) {
        localStorage.removeItem("tw_photo_" + empCode);
        localStorage.removeItem("tw_docs_" + empCode);
      }
    } catch(e) {}
  });

  if (company?.id) {
    const { data, error } = await supabase.rpc('file_keys', { p_company_id: company.id, p_employee_ids: emps.map(emp => emp.id).filter(Boolean) });
    if (error) throw error;
    (data || []).forEach(key => keysSet.add(key));
  }
  return await deleteFromR2(Array.from(keysSet));
}

export async function purgeEmployeeDatabaseRecords(companyId, employeeIds) {
  const ids = Array.isArray(employeeIds) ? employeeIds : (employeeIds ? [employeeIds] : []);
  if (!companyId || ids.length === 0) return;

  // 1. Delete child table records first so foreign keys don't block and no stale rows remain
  await Promise.allSettled([
    supabase.from("salary_records").delete().eq("company_id", companyId).in("employee_id", ids),
    supabase.from("attendance_records").delete().eq("company_id", companyId).in("employee_id", ids),
    supabase.from("employee_leaves").delete().eq("company_id", companyId).in("employee_id", ids),
    supabase.from("employee_update_requests").delete().eq("company_id", companyId).in("employee_id", ids),
    supabase.from("fnf_settlements").delete().eq("company_id", companyId).in("employee_id", ids),
    supabase.from("candidates").update({ converted_employee_id: null }).eq("company_id", companyId).in("converted_employee_id", ids)
  ]);

  // 2. Delete from employees table
  const { error } = await supabase.from("employees").delete().eq("company_id", companyId).in("id", ids);
  if (error) throw error;
}

export async function cleanOrphanDatabaseRecords(companyId) {
  if (!companyId) return { cleaned: 0 };
  try {
    const { data: emps } = await supabase.from("employees").select("id").eq("company_id", companyId);
    const validIds = new Set((emps || []).map(e => e.id));

    // Stale salary records
    const { data: sals } = await supabase.from("salary_records").select("id, employee_id").eq("company_id", companyId);
    const staleSals = (sals || []).filter(s => s.employee_id && !validIds.has(s.employee_id)).map(s => s.id);
    if (staleSals.length > 0) {
      await supabase.from("salary_records").delete().in("id", staleSals);
    }

    // Stale attendance records
    const { data: atts } = await supabase.from("attendance_records").select("id, employee_id").eq("company_id", companyId);
    const staleAtts = (atts || []).filter(a => a.employee_id && !validIds.has(a.employee_id)).map(a => a.id);
    if (staleAtts.length > 0) {
      await supabase.from("attendance_records").delete().in("id", staleAtts);
    }

    // Stale leaves
    const { data: leaves } = await supabase.from("employee_leaves").select("id, employee_id").eq("company_id", companyId);
    const staleLeaves = (leaves || []).filter(l => l.employee_id && !validIds.has(l.employee_id)).map(l => l.id);
    if (staleLeaves.length > 0) {
      await supabase.from("employee_leaves").delete().in("id", staleLeaves);
    }

    // Stale update requests
    const { data: reqs } = await supabase.from("employee_update_requests").select("id, employee_id").eq("company_id", companyId);
    const staleReqs = (reqs || []).filter(r => r.employee_id && !validIds.has(r.employee_id)).map(r => r.id);
    if (staleReqs.length > 0) {
      await supabase.from("employee_update_requests").delete().in("id", staleReqs);
    }

    // Stale fnf settlements
    const { data: fnfs } = await supabase.from("fnf_settlements").select("id, employee_id").eq("company_id", companyId);
    const staleFnfs = (fnfs || []).filter(f => f.employee_id && !validIds.has(f.employee_id)).map(f => f.id);
    if (staleFnfs.length > 0) {
      await supabase.from("fnf_settlements").delete().in("id", staleFnfs);
    }

    const totalCleaned = staleSals.length + staleAtts.length + staleLeaves.length + staleReqs.length + staleFnfs.length;
    return {
      cleaned: totalCleaned,
      salaries: staleSals.length,
      attendance: staleAtts.length,
      leaves: staleLeaves.length,
      updateRequests: staleReqs.length,
      fnf: staleFnfs.length
    };
  } catch (err) {
    console.error("cleanOrphanDatabaseRecords error:", err);
    return { cleaned: 0, error: err.message };
  }
}

function EmployeePhotoUploader({ value, onChange, label = "Employee Photograph", required = false, disabled = false, empCode = '', empName = '', style = {}, onBusyChange }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(''); setLoading(true); onBusyChange?.(true);
    try {
      const res = await processEmployeePhoto(file);
      if (res.error) { setError(res.error); return; }
      if (!res.dataUrl) { setError('Could not read the photo. Please try again.'); return; }
      const fileId = getCleanFileIdentifier(empCode, empName, 'Photo');
      const uploadedUrl = await uploadToR2(res.dataUrl, 'photos', fileId);
      if (uploadedUrl) onChange(uploadedUrl);
      else setError('Upload failed. Could not save photo to storage.');
    } catch (err) { setError('Upload failed: ' + err.message); }
    finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(false); onBusyChange?.(false);
    }
  };

  const handleRemove = () => {
    setError("");
    onChange("");
  };

  return (
    <div style={{background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", marginBottom: 14, maxWidth: 440, width: "100%", boxSizing: "border-box", ...style}}>
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10}}>
        <div style={{fontSize: 11.5, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 0.6}}>
          📷 {label} {required && <span style={{color: "#dc2626"}}>*</span>}
        </div>
        <span style={{fontSize: 10.5, fontWeight: 700, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "2px 8px", borderRadius: 12, whiteSpace: "nowrap"}}>
          JPG, PNG, WEBP • Max 4MB
        </span>
      </div>

      <div style={{display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap"}}>
        <div style={{position: "relative", width: 72, height: 72, flexShrink: 0}}>
          {value ? (
            <a href={value} target="_blank" rel="noreferrer noopener" title="View photo full size">
              <img
                src={value}
                alt="Employee Photo"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2.5px solid #3b82f6",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  background: "#fff",
                  cursor: "pointer"
                }}
              />
            </a>
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
                border: "2px dashed #94a3b8",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b"
              }}
            >
              <span style={{fontSize: 22}}>👤</span>
            </div>
          )}
          {loading && (
            <div style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: "rgba(255,255,255,0.75)", display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: 18
            }}>
              ⏳
            </div>
          )}
        </div>

        <div style={{flex: 1, minWidth: 200}}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            style={{display: "none"}}
            disabled={disabled || loading}
            onChange={handleFileChange}
          />
          <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={disabled || loading}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              style={{padding: "6px 14px", fontSize: 12, fontWeight: 700}}
            >
              {value ? "🔄 Change Photo" : "📤 Upload Photo"}
            </button>
            {value && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={disabled || loading}
                onClick={handleRemove}
                style={{color: "#dc2626", borderColor: "#fecaca", background: "#fef2f2", padding: "6px 12px", fontSize: 12}}
              >
                ✕ Remove
              </button>
            )}
          </div>
          <div style={{fontSize: 11.5, color: "#64748b", marginTop: 6, lineHeight: 1.4}}>
            Clear front-facing passport photo or selfie.
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: 10,
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 8,
          padding: "6px 10px",
          color: "#b91c1c",
          fontSize: 11.5,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

const JK_EMPTY = {
  name:'', employee_code:'', rel_type:'Father', father_name:'', spouse_name:'', gender:'Male',
  dob:'', marital_status:'', religion:'', blood_group:'', phone:'', email:'',
  emergency_name:'', emergency_relation:'', emergency_phone:'',
  doj:'', designation:'', department:'', location:'', hr_induction_date:'', employment_status:'confirmed',
  basic_salary:0, date_of_exit:'',
  aadhaar_last4:'', pan:'', bank_account:'', bank_ifsc:'', bank_name:'', bank_branch:'',
  uan:'', pf_account:'', esi_id:'', dispensary:'', prev_pf:'No', prev_eps:'No', prev_esi:'No',
  prev_pf_account:'', prev_exit_date:'', scheme_cert:'', ppo_number:'',
  intl_worker:'No', country_of_origin:'', passport_number:'', passport_validity:'',
  perm_address:'', perm_city:'', perm_state:'', perm_pincode:'',
  temp_address:'', temp_city:'', temp_state:'', temp_pincode:'',
  family_members:[], nominees:[], specially_abled:'No',
  emp_photo:'', emp_aadhaar_doc:'', emp_aadhaar_front_doc:'', emp_aadhaar_back_doc:'', emp_pan_doc:'', emp_bank_doc:'',
  login_pin:'',
};

export function validateJoiningKitStep(form, tab) {
    if (!form.employee_code?.trim()) { return 'Employee ID is required.'; }
    if (!form.name?.trim()) { return 'Employee Name is required.'; }
    if (form.name.trim().length > 100) { return 'Employee Name cannot exceed 100 characters.'; }
    if (form.father_name && form.father_name.trim().length > 100) { return "Father's Name cannot exceed 100 characters."; }
    if (form.spouse_name && form.spouse_name.trim().length > 100) { return "Spouse's Name cannot exceed 100 characters."; }
    if (form.emergency_name && form.emergency_name.trim().length > 100) { return "Emergency Contact Name cannot exceed 100 characters."; }
    const tabOrder = ['checklist', 'personal', 'job', 'kyc', 'pf_esi', 'family', 'nominees', 'forms'];
    const curIdx = tabOrder.indexOf(tab);

    if (curIdx >= tabOrder.indexOf('personal')) {
      if (!form.dob) { return 'Date of Birth is required.'; }
      if (!form.emp_photo) { return 'Employee Photo upload is mandatory.'; }
      if (!form.emp_aadhaar_front_doc && !form.emp_aadhaar_doc) { return 'Aadhaar Document (Front Side) upload is mandatory.'; }
      if (!form.emp_pan_doc) { return 'PAN Document upload is mandatory.'; }
      if (form.phone && !/^\d{10}$/.test(form.phone.trim())) { return 'Phone number must be 10 digits.'; }
      if (form.emergency_phone && !/^\d{10}$/.test(form.emergency_phone.trim())) { return 'Emergency phone number must be 10 digits.'; }
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { return 'Invalid email address format.'; }
    }
    if (curIdx >= tabOrder.indexOf('kyc')) {
      if (!form.pan?.trim()) { return 'PAN Number is required.'; }
      if (!form.emp_bank_doc) { return 'Cancelled Cheque / Passbook upload is mandatory.'; }
      if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(form.pan.trim())) { return 'Invalid PAN format (e.g. ABCDE1234F).'; }
      if (form.aadhaar_last4 && !/^\d{4}$/.test(form.aadhaar_last4.trim())) { return 'Aadhaar must be exactly 4 digits.'; }
      if (form.bank_ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(form.bank_ifsc.trim())) { return 'Invalid IFSC Code format.'; }
      if (form.bank_account && !isValidBankAccount(form.bank_account)) { return 'Bank Account Number must be between 9 and 18 numeric digits.'; }
    }
    if (curIdx >= tabOrder.indexOf('job')) {
      if (!form.doj) { return 'Date of Joining is required.'; }
      if (form.dob) {
        const empDob = new Date(form.dob);
        const today = new Date();
        let age = today.getFullYear() - empDob.getFullYear();
        const m = today.getMonth() - empDob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < empDob.getDate())) age--;
        if (age > 60) { return 'Employee age cannot be greater than 60 years.'; }

        const compareDate = form.doj ? new Date(form.doj) : today;
        let joinAge = compareDate.getFullYear() - empDob.getFullYear();
        const jm = compareDate.getMonth() - empDob.getMonth();
        if (jm < 0 || (jm === 0 && compareDate.getDate() < empDob.getDate())) joinAge--;
        if (joinAge < 18) { return 'Employee must be at least 18 years old on the Date of Joining.'; }
      }
      if (form.hr_induction_date && form.doj) {
        if (new Date(form.hr_induction_date) < new Date(form.doj)) {
          return 'HR Induction Date cannot be before Date of Joining.';
        }
      }
      if (form.date_of_exit && form.doj) {
        if (new Date(form.date_of_exit) < new Date(form.doj)) {
          return 'Last Working Day cannot be before Date of Joining.';
        }
      }
      if (form.prev_exit_date && form.doj) {
        if (new Date(form.prev_exit_date) >= new Date(form.doj)) {
          return 'Previous employment exit date must be before Date of Joining.';
        }
      }
    }
    if (curIdx >= tabOrder.indexOf('pf_esi')) {
      if (form.uan && !isValidUAN(form.uan)) {
        return 'Universal Account Number (UAN) must be exactly 12 numeric digits (or leave blank if unknown).';
      }
      if (form.prev_esi === 'Yes' && form.esi_id && form.esi_id.trim().length !== 17) {
        return 'ESI Insurance Number (IP) must be exactly 17 numeric digits (or leave blank).';
      }
    }
    if (curIdx >= tabOrder.indexOf('family')) {
      const fams = (form.family_members || []).filter(f => f && (f.name?.trim() || f.relationship?.trim() || f.dob || f.photo || f.aadhaar_doc || f.aadhaar_front_doc));
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
      const empDobDate = form.dob ? parseDateFlexible(form.dob) : null;
      for (let i = 0; i < fams.length; i++) {
        const f = fams[i];
        const rowNum = i + 1;
        const dispName = f.name?.trim() ? `"${f.name.trim()}"` : `Member #${rowNum}`;
        if (!f.name?.trim()) {
          return `Family member #${rowNum}: Name is required.`;
        }
        if (f.name.trim().length > 100) {
          return `Family member ${dispName}: Name cannot exceed 100 characters.`;
        }
        if (!f.relationship?.trim()) {
          return `Family member ${dispName}: Relationship is required.`;
        }
        if (!f.dob) {
          return `Family member ${dispName}: Date of Birth (DOB) is required.`;
        }
        const itemDob = parseDateFlexible(f.dob);
        if (!itemDob) {
          return `Family member ${dispName}: Invalid Date of Birth.`;
        }
        if (itemDob > todayEnd) {
          return `Family member ${dispName}: Date of Birth cannot be in the future.`;
        }
        if (!f.photo || (!f.aadhaar_doc && !f.aadhaar_front_doc)) {
          return `Family member ${dispName}: Photo and Aadhaar Document are mandatory.`;
        }
        if (empDobDate) {
          const rel = f.relationship.trim().toLowerCase();
          const empAge = calcAge(form.dob);
          const memberAge = calcAge(f.dob);
          if (rel === 'father' || rel === 'mother') {
            if (itemDob >= empDobDate) {
              return `Family member ${dispName} (${f.relationship}) must be older than the employee.`;
            }
            if (typeof memberAge === 'number' && typeof empAge === 'number' && (memberAge - empAge < 14)) {
              return `Family member ${dispName} (${f.relationship}) must be at least 14 years older than the employee.`;
            }
          }
          if (rel === 'son' || rel === 'daughter') {
            if (itemDob <= empDobDate) {
              return `Family member ${dispName} (${f.relationship}) must be younger than the employee.`;
            }
            if (typeof memberAge === 'number' && typeof empAge === 'number' && (empAge - memberAge < 14)) {
              return `Employee must be at least 14 years older than their ${f.relationship.toLowerCase()} (${dispName}).`;
            }
          }
          if (rel === 'spouse') {
            if (typeof memberAge === 'number' && memberAge < 18) {
              return `Spouse ${dispName} must be at least 18 years of age.`;
            }
          }
        }
      }
    }
    if (curIdx >= tabOrder.indexOf('nominees')) {
      const noms = form.nominees || [];
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
      const empDobDate = form.dob ? parseDateFlexible(form.dob) : null;
      let totalShare = 0;
      for (let i = 0; i < noms.length; i++) {
        const n = noms[i];
        const rowNum = i + 1;
        const dispName = n.name?.trim() ? `"${n.name.trim()}"` : `Nominee #${rowNum}`;
        if (!n.name?.trim()) {
          return `Nominee #${rowNum}: Name is required.`;
        }
        if (n.name.trim().length > 100) {
          return `Nominee ${dispName}: Name cannot exceed 100 characters.`;
        }
        if (!n.relationship?.trim()) {
          return `Nominee ${dispName}: Relationship is required.`;
        }
        if (n.dob) {
          const itemDob = parseDateFlexible(n.dob);
          if (!itemDob) {
            return `Nominee ${dispName}: Invalid Date of Birth.`;
          }
          if (itemDob > todayEnd) {
            return `Nominee ${dispName}: Date of Birth cannot be in the future.`;
          }
          if (empDobDate) {
            const rel = n.relationship.trim().toLowerCase();
            if (rel === 'father' || rel === 'mother') {
              if (itemDob >= empDobDate) {
                return `Nominee ${dispName} (${n.relationship}) must be older than the employee.`;
              }
            }
            if (rel === 'son' || rel === 'daughter') {
              if (itemDob <= empDobDate) {
                return `Nominee ${dispName} (${n.relationship}) must be younger than the employee.`;
              }
            }
          }
        }
        if (n.share !== undefined && n.share !== '') {
          const s = parseFloat(n.share);
          if (isNaN(s) || s <= 0 || s > 100) {
            return `Nominee ${dispName}: Share % must be between 1 and 100.`;
          }
          totalShare += s;
        }
      }
      if (noms.length > 0 && Math.round(totalShare) !== 100) {
        return `Total nominee share must equal 100% (currently ${totalShare}%).`;
      }
    }

    return '';
}

const joiningKitErrorTarget = message => {
  if (/^Family member/.test(message) || /their .*\(/.test(message)) return { tab: 'family', field: 'family_members' };
  if (/^Nominee|nominee share/.test(message)) return { tab: 'nominees', field: 'nominees' };
  const targets = [
    [/Employee ID/, 'personal', 'employee_code'], [/Employee Name/, 'personal', 'name'],
    [/Father's Name/, 'personal', 'father_name'], [/Spouse's Name/, 'personal', 'spouse_name'],
    [/Emergency Contact Name/, 'personal', 'emergency_name'], [/Emergency phone/, 'personal', 'emergency_phone'],
    [/Phone number/, 'personal', 'phone'], [/email address/, 'personal', 'email'],
    [/Employee Photo/, 'personal', 'emp_photo'], [/Aadhaar Document/, 'personal', 'emp_aadhaar_front_doc'],
    [/PAN Document/, 'personal', 'emp_pan_doc'], [/Date of Birth|Employee age|at least 18/, 'personal', 'dob'],
    [/PAN Number|PAN format/, 'kyc', 'pan'], [/Cancelled Cheque/, 'kyc', 'emp_bank_doc'],
    [/Aadhaar must/, 'kyc', 'aadhaar_last4'], [/IFSC/, 'kyc', 'bank_ifsc'], [/Bank Account/, 'kyc', 'bank_account'],
    [/HR Induction/, 'job', 'hr_induction_date'], [/Last Working Day/, 'job', 'date_of_exit'],
    [/Previous employment exit/, 'pf_esi', 'prev_exit_date'], [/Date of Joining/, 'job', 'doj'],
    [/UAN/, 'pf_esi', 'uan'], [/ESI Insurance/, 'pf_esi', 'esi_id'],
  ];
  const match = targets.find(([pattern]) => pattern.test(message));
  return match ? { tab: match[1], field: match[2] } : null;
};

export function JoiningKitPage({ employees, company, onSave, onBack, isCompanyRole, initialTab = 'checklist', onRefresh, onDirtyChange }) {
  const draftPrefix = `tw_onboarding_draft_${company?.id}_`;
  const [selId, setSelId] = useState(() => {
    try { return sessionStorage.getItem(`${draftPrefix}active`) || 'new'; } catch { return 'new'; }
  });
  const [form, setForm] = useState({...JK_EMPTY, company_id: company?.id});
  const [tab, setTab] = useState(initialTab || 'checklist');
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);
  const [uploadingDoc, setUploadingDoc] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [genMsg, setGenMsg] = useState('');
  const tabsBarRef = useRef(null);
  const contentRef = useRef(null);
  const skipHydrationRef = useRef(false);
  const persistedIdRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const hydratedRef = useRef('');
  const [baseline, setBaseline] = useState(JSON.stringify({...JK_EMPTY, company_id: company?.id}));
  const [reloadVersion, setReloadVersion] = useState(0);
  const [fieldIssue, setFieldIssue] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const dirty = JSON.stringify(form) !== baseline;
  const uploading = Object.values(uploadingDoc).some(Boolean);
  const busy = saving || uploading;
  const onPhotoBusyChange = useCallback(value => setUploadingDoc(previous => ({...previous, emp_photo: value})), []);
  useEffect(() => { onDirtyChange?.(dirty || busy); }, [dirty, busy, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    const preventUnload = event => { if (dirty || busy) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty, busy]);
  useEffect(() => {
    if (!fieldIssue) return;
    const wrapper = contentRef.current?.querySelector(`[data-jk-field="${fieldIssue.field}"]`);
    const input = fieldIssue.field === 'emp_photo' ? wrapper?.querySelector('button') : wrapper?.querySelector('input:not([type="hidden"]), select, button');
    input?.setAttribute('aria-invalid', 'true');
    input?.setAttribute('aria-describedby', `jk-error-${fieldIssue.field}`);
    wrapper?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    input?.focus();
    return () => { input?.removeAttribute('aria-invalid'); input?.removeAttribute('aria-describedby'); };
  }, [fieldIssue, tab]);
  const confirmDiscard = () => {
    if (busy) { setMsg('Please wait for saving or uploads to finish.'); return false; }
    return !dirty || window.confirm('You have unsaved onboarding changes. Leave without saving a draft?');
  };
  const showFieldIssue = message => {
    const target = joiningKitErrorTarget(message);
    setMsg(message);
    setFieldIssue(target ? { ...target, message } : null);
    if (target) setTab(target.tab);
  };
  const inlineError = field => fieldIssue?.field === field ? <div id={`jk-error-${field}`} role="alert" style={{color:'#b91c1c',fontSize:12,marginTop:6}}>{fieldIssue.message}</div> : null;
  const saveDraft = () => {
    try {
      sessionStorage.setItem(`${draftPrefix}${selId}`, JSON.stringify({ form, tab }));
      sessionStorage.setItem(`${draftPrefix}active`, selId);
      setBaseline(JSON.stringify(form));
      setHasDraft(true);
      setMsg('✅ Draft saved in this browser tab. You can resume after refreshing. It has not been submitted to payroll.');
    } catch { setMsg('Draft could not be saved. Keep this page open and try again.'); }
  };

  // Departments & Designations from company profile config merged with employee records
  const cfg = company?.config || {};
  const existingDepts = useMemo(() => [...new Set((employees || []).map(e => e.department).filter(Boolean))], [employees]);
  const deptOptions = useMemo(() => {
    return [...new Set([...(cfg.departments || DEFAULT_CONFIG.departments || []), ...existingDepts])].filter(Boolean);
  }, [cfg.departments, existingDepts]);

  const existingDesigs = useMemo(() => [...new Set((employees || []).map(e => e.designation).filter(Boolean))], [employees]);
  const desigOptions = useMemo(() => {
    return [...new Set([...(cfg.designations || DEFAULT_CONFIG.designations || []), ...existingDesigs])].filter(Boolean);
  }, [cfg.designations, existingDesigs]);

  useEffect(() => {
    if (tabsBarRef.current) {
      const activeBtn = tabsBarRef.current.querySelector('[data-active="true"]');
      if (activeBtn) {
        activeBtn.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [tab]);

  const [ocrLoading, setOcrLoading] = useState(false);

  const handleFieldChange = (k, val) => {
    let updates = { [k]: val };
    if (k === 'doj') {
      // Auto-copy Joining Date to HR Induction Date (editable independently if needed)
      updates.hr_induction_date = val;
    }
    if (k === 'prev_pf') {
      updates.prev_eps = val;
      if (val === 'No') {
        updates.uan = '';
        updates.pf_account = '';
        updates.prev_pf_account = '';
        updates.prev_exit_date = '';
        updates.scheme_cert = '';
        updates.ppo_number = '';
      }
    }
    if (k === 'prev_esi') {
      if (val === 'No') {
        updates.esi_id = '';
        updates.dispensary = '';
      }
    }
    setCompleted(false);
    setFieldIssue(null);
    setForm(p => ({...p, ...updates}));
    setMsg('');
  };

  const set = k => v => handleFieldChange(k, v);
  const setF = k => e => {
    let val = e.target.value;
    if (k === 'phone' || k === 'emergency_phone') val = val.replace(/\D/g, '').slice(0, 10);
    else if (k === 'aadhaar_last4') val = val.replace(/\D/g, '').slice(0, 4);
    else if (k === 'bank_ifsc') {
      val = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
      if (val.length >= 4 && !form.bank_name) {
        const pfx = val.slice(0, 4);
        if (IFSC_BANK_PREFIX_MAP[pfx]) {
          handleFieldChange('bank_name', IFSC_BANK_PREFIX_MAP[pfx]);
        }
      }
    }
    else if (k === 'pan') val = val.toUpperCase().trim();
    else if (k === 'uan') val = val.replace(/\D/g, '').slice(0, 12);
    else if (k === 'esi_id') val = val.replace(/\D/g, '').slice(0, 17);
    handleFieldChange(k, val);
  };

  useEffect(() => {
    const identity = `${company?.id}:${selId}:${reloadVersion}`;
    if (hydratedRef.current === identity) return;
    if (skipHydrationRef.current) { skipHydrationRef.current = false; hydratedRef.current = identity; return; }
    let draft;
    try { draft = JSON.parse(sessionStorage.getItem(`${draftPrefix}${selId}`) || 'null'); } catch {}
    if (draft?.form) {
      setHasDraft(true);
      setForm(draft.form); setBaseline(JSON.stringify(draft.form)); setTab(draft.tab || 'personal');
      persistedIdRef.current = selId === 'new' ? null : selId;
      setMsg('Draft restored from this browser tab.');
      hydratedRef.current = identity;
      return;
    }
    setHasDraft(false);
    let nextForm;
    if (selId === 'new') {
      nextForm = {...JK_EMPTY, company_id: company?.id};
      setTab(initialTab || 'checklist');
    } else {
      const emp = employees.find(e => String(e.id) === String(selId));
      if (!emp) return;
      const photo = emp.emp_photo || company?.config?.employee_photos?.[emp.id] || company?.config?.employee_photos?.[emp.employee_code] || '';
      const docs = company?.config?.employee_documents?.[emp.id] || company?.config?.employee_documents?.[emp.employee_code] || {};
      const prevPfVal = (emp.prev_pf === 'Yes' || emp.uan) ? 'Yes' : (emp.prev_pf || 'No');
      const prevEpsVal = (emp.prev_eps === 'Yes' || emp.prev_pf === 'Yes' || emp.uan) ? 'Yes' : (emp.prev_eps || 'No');
      const prevEsiVal = (emp.prev_esi === 'Yes' || emp.esi_id) ? 'Yes' : (emp.prev_esi || 'No');
      nextForm = {...JK_EMPTY, ...emp, ...docs, prev_pf: prevPfVal, prev_eps: prevEpsVal, prev_esi: prevEsiVal, emp_photo: photo, company_id: company?.id};
      setTab(initialTab !== 'checklist' ? initialTab : 'personal');
    }
    persistedIdRef.current = selId === 'new' ? null : selId;
    setForm(nextForm); setBaseline(JSON.stringify(nextForm)); setFieldIssue(null); setCompleted(false); setMsg('');
    hydratedRef.current = identity;
  }, [selId, company, employees, reloadVersion, draftPrefix, initialTab]);

  /*
  // OCR Auto-population (Aadhaar, PAN, Cheque via Tesseract.js) commented out to ensure fast upload & save:
  const scanDocument = async (file, docType) => {
    if (!file || !docType) return;
    if (file.type && file.type.includes('pdf')) {
      setMsg(`ℹ️ Skipping OCR for ${docType.toUpperCase()}: PDF scanning requires image format.`);
      return;
    }
    setOcrLoading(true);
    setMsg(`Scanning ${docType.toUpperCase()}...`);
    try {
      const Tesseract = (await import('tesseract.js')).default;
      const { data: { text } } = await Tesseract.recognize(file, 'eng');
      console.log(`OCR Extracted Text (${docType}):\n`, text);

      let updates = {};
      const cleanText = text.replace(/\n/g, ' ');

      // Common extractions (DOB, PIN)
      const dobMatch = cleanText.match(/\b(\d{2})[\/\-](\d{2})[\/\-](\d{4})\b/);
      if (dobMatch && !form.dob) {
        updates.dob = `${dobMatch[3]}-${dobMatch[2]}-${dobMatch[1]}`;
      }

      const pinMatch = cleanText.match(/\b[1-9][0-9]{5}\b/);
      if (pinMatch && !form.temp_pincode) {
        updates.temp_pincode = pinMatch[0];
      }

      if (docType === 'pan') {
        const panMatch = cleanText.match(/[A-Z]{5}[0-9]{4}[A-Z]{1}/i);
        if (panMatch) updates.pan = panMatch[0].toUpperCase();
      } else if (docType === 'aadhaar') {
        const aadhaarMatch = cleanText.match(/(?:\d{4}\s?){3}/);
        if (aadhaarMatch) {
          const digitsOnly = aadhaarMatch[0].replace(/\s/g, '');
          if (digitsOnly.length === 12) updates.aadhaar_last4 = digitsOnly.slice(-4);
        }
      } else if (docType === 'cheque') {
        const ifscMatch = cleanText.match(/[A-Z]{4}[0O][A-Z0-9]{6}/i);
        if (ifscMatch) {
          let ifsc = ifscMatch[0].toUpperCase();
          updates.bank_ifsc = ifsc.substring(0, 4) + '0' + ifsc.substring(5);
        }

        const accMatch = cleanText.match(/(?:A\/C|ACC|ACCOUNT|A\/c)[^\d]*((?:\d[\s\-]*){9,18})/i);
        if (accMatch) {
          updates.bank_account = accMatch[1].replace(/[\s\-]/g, '');
        } else {
          // Fallback: look for the longest sequence of digits between 9 and 18, allowing spaces/dashes
          const digitMatches = cleanText.match(/\b(?:\d[\s\-]*){9,18}\b/g);
          if (digitMatches) {
            const cleanMatches = digitMatches.map(m => m.replace(/[\s\-]/g, ''));
            updates.bank_account = cleanMatches.sort((a,b) => b.length - a.length)[0];
          }
        }

        // Bank Name Extraction
        const bankNameMatch = cleanText.match(/\b(STATE BANK OF INDIA|HDFC|ICICI|AXIS|KOTAK|PUNJAB NATIONAL|BANK OF BARODA|CANARA|INDUSIND|YES BANK|IDFC|UNION BANK|BANK OF INDIA|CENTRAL BANK)\b/i);
        if (bankNameMatch) {
          let bName = bankNameMatch[0].toUpperCase();
          if (bName === 'HDFC') bName = 'HDFC BANK';
          if (bName === 'ICICI') bName = 'ICICI BANK';
          if (bName === 'AXIS') bName = 'AXIS BANK';
          if (bName === 'KOTAK') bName = 'KOTAK MAHINDRA BANK';
          updates.bank_name = bName;
        }
      }

      if (Object.keys(updates).length > 0) {
        setForm(p => ({ ...p, ...updates }));
        setMsg(`✅ Auto-filled: ${Object.keys(updates).join(', ')}`);
      } else {
        setMsg(`⚠️ Could not find expected details in this ${docType.toUpperCase()} scan.`);
      }
    } catch (err) {
      console.error(err);
      setMsg('❌ OCR failed: ' + err.message);
    } finally {
      setOcrLoading(false);
    }
  };
  */

  const handleSaveAndContinue = async () => {
    if (tab === 'checklist') {
      setTab('personal');
      return;
    }
    if (saveInFlightRef.current || busy) { setMsg('Please wait for saving or uploads to finish.'); return; }
    const error = validateJoiningKitStep(form, tab);
    if (error) { showFieldIssue(error); return; }

    saveInFlightRef.current = true;
    setSaving(true); setMsg(''); setFieldIssue(null);
    const savedForm = form;
    const oldId = selId;
    const retainIdentity = id => {
      if (!id) throw new Error('Employee was saved without an ID. Please refresh before trying again.');
      const identityChanged = String(id) !== String(persistedIdRef.current);
      persistedIdRef.current = id;
      if (identityChanged && String(id) !== String(selId)) { skipHydrationRef.current = true; setSelId(String(id)); }
      // Keep locally saved drafts attached to the newly created record even if document saving fails.
      try {
        sessionStorage.setItem(`${draftPrefix}${id}`, JSON.stringify({ form: savedForm, tab }));
        if (String(oldId) !== String(id)) sessionStorage.removeItem(`${draftPrefix}${oldId}`);
        sessionStorage.setItem(`${draftPrefix}active`, String(id));
        setHasDraft(true);
      } catch {}
    };
    try {
      const id = await onSave(savedForm, persistedIdRef.current || (selId === 'new' ? null : selId), retainIdentity);
      retainIdentity(id || persistedIdRef.current);
      setBaseline(JSON.stringify(savedForm));
      setHasDraft(false);
      try {
        sessionStorage.removeItem(`${draftPrefix}${oldId}`);
        sessionStorage.removeItem(`${draftPrefix}${persistedIdRef.current}`);
        sessionStorage.removeItem(`${draftPrefix}active`);
      } catch {}
      const currentIndex = TABS.findIndex(t => t.id === tab);
      if (currentIndex < TABS.length - 1) {
        setTab(TABS[currentIndex + 1].id);
        setMsg('✅ Saved. Continue with the next section.');
      } else {
        setCompleted(true);
        setMsg('✅ Onboarding saved successfully.');
      }
    } catch(e) { setMsg('❌ ' + e.message); }
    finally { saveInFlightRef.current = false; setSaving(false); }

  };

  const handleDownloadAllDocs = async () => {
    setGenMsg('Zipping documents…');
    try {
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();

      const cleanName = slug(form.name || 'Employee');
      const cleanCode = slug(form.employee_code || 'ID');
      const prefix = `${cleanName}_${cleanCode}`;

      const tasks = [];
      const addFile = async (urlOrB64, filename) => {
        if (!urlOrB64) return;
        if (typeof urlOrB64 === 'string' && urlOrB64.startsWith('data:')) {
          const matches = urlOrB64.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
          if (!matches || matches.length !== 3) return;
          let ext = 'jpg';
          if (matches[1].includes('pdf')) ext = 'pdf';
          else if (matches[1].includes('png')) ext = 'png';
          else if (matches[1].includes('webp')) ext = 'webp';
          zip.file(`${filename}.${ext}`, matches[2], {base64: true});
        } else if (typeof urlOrB64 === 'string') {
          try {
            const res = await fetch(urlOrB64);
            if (res.ok) {
              const blob = await res.blob();
              let ext = 'webp';
              if (urlOrB64.toLowerCase().endsWith('.pdf') || blob.type.includes('pdf')) ext = 'pdf';
              else if (urlOrB64.toLowerCase().endsWith('.png') || blob.type.includes('png')) ext = 'png';
              else if (urlOrB64.toLowerCase().endsWith('.jpg') || urlOrB64.toLowerCase().endsWith('.jpeg')) ext = 'jpg';
              zip.file(`${filename}.${ext}`, blob);
            }
          } catch(e) {}
        }
      };

      let fileCount = 0;
      const countAdd = (urlOrB64, name) => {
        if (urlOrB64) {
          tasks.push(addFile(urlOrB64, name));
          fileCount++;
        }
      };

      countAdd(form.emp_photo, `${prefix}_Photo`);
      countAdd(form.emp_aadhaar_front_doc || form.emp_aadhaar_doc, `${prefix}_Aadhaar_Front`);
      countAdd(form.emp_aadhaar_back_doc, `${prefix}_Aadhaar_Back`);
      countAdd(form.emp_pan_doc, `${prefix}_PAN`);
      countAdd(form.emp_bank_doc, `${prefix}_Bank_Proof`);

      (form.family_members||[]).forEach((f, i) => {
        const rel = slug(f.relationship || `member${i+1}`);
        const fName = slug(f.name || `fam${i+1}`);
        countAdd(f.photo, `${prefix}_Family_${rel}_${fName}_Photo`);
        countAdd(f.aadhaar_front_doc || f.aadhaar_doc, `${prefix}_Family_${rel}_${fName}_Aadhaar_Front`);
        countAdd(f.aadhaar_back_doc, `${prefix}_Family_${rel}_${fName}_Aadhaar_Back`);
      });

      if (fileCount === 0) {
        setGenMsg('⚠️ No uploaded documents found for this employee to zip.');
        setTimeout(() => setGenMsg(''), 3500);
        return;
      }

      await Promise.all(tasks);
      const content = await zip.generateAsync({type: "blob"});
      saveAs(content, `${prefix}_Documents.zip`);

      setGenMsg(`✅ ZIP Downloaded (${fileCount} files): ${prefix}_Documents.zip`);
    } catch (err) {
      console.error(err);
      setGenMsg('❌ ' + err.message);
    }
    setTimeout(() => setGenMsg(''), 3500);
  };

  const listField = (listKey, cols) => (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      {(form[listKey]||[]).map((row, i) => (
        <div key={i} style={{display:'grid',gridTemplateColumns:`repeat(${cols.length},1fr) 36px`,gap:8,alignItems:'end',background:'#f8fafc',borderRadius:8,padding:'10px 12px',border:'1px solid #e2e8f0'}}>
          {cols.map(([k,label,type,opts,cond]) => {
            if (cond && !cond(row)) return <div key={k} />;
            return (
            <div key={k}>
              <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:3}}>{label}</div>
              {type==='select' ? (
                <select style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'7px 10px',fontSize:13}} value={row[k]||''} onChange={ev=>{
                  const nl = [...(form[listKey]||[])]; nl[i]={...nl[i],[k]:ev.target.value}; set(listKey)(nl);
                }}>{opts.map(o=><option key={o}>{o}</option>)}</select>
              ) : type==='datalist' && opts ? (
                <>
                  <input type="text" list={`${listKey}_${k}_${i}_list`} style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'7px 10px',fontSize:13}} value={row[k]||''}
                    onChange={ev=>{
                      const val = ev.target.value;
                      const nl=[...(form[listKey]||[])];
                      nl[i]={...nl[i],[k]:val};
                      if (listKey === 'nominees' && k === 'name') {
                        const fm = (form.family_members||[]).find(f => f.name === val);
                        if (fm) {
                          nl[i].relationship = fm.relationship || nl[i].relationship || '';
                          nl[i].dob = fm.dob || nl[i].dob || '';
                          if (fm.dob) {
                            const d = new Date(fm.dob); const t = new Date();
                            if (!isNaN(d.getTime())) {
                              let a = t.getFullYear() - d.getFullYear();
                              if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
                              nl[i].age = a >= 0 ? a : '';
                            }
                          }
                          const isResiding = String(fm.residing || '').toLowerCase() === 'yes';
                          if (isResiding) {
                            nl[i].address = [form.temp_address || form.perm_address, form.temp_city || form.perm_city, form.temp_state || form.perm_state, form.temp_pincode || form.perm_pincode].filter(Boolean).join(", ") || `${form.perm_address||''} ${form.perm_city||''}`.trim();
                          } else if (fm.address) {
                            nl[i].address = fm.address;
                          }
                          if ((nl.length === 1 || !nl[i].share) && nl.filter(x => x.share).length === 0) {
                            nl[i].share = '100';
                          }
                        }
                      }
                      set(listKey)(nl);
                    }} />
                  <datalist id={`${listKey}_${k}_${i}_list`}>{opts.map((o, idx)=><option key={idx} value={o}/>)}</datalist>
                </>
              ) : type==='file' ? (
                <div style={{display:'flex', gap:4, alignItems:'center', width:'100%', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'4px 6px', overflow:'hidden'}}>
                  <input type="file" accept={opts?.accept || 'image/*,.pdf'} style={{width:'100%', fontSize:11}}
                    disabled={uploadingDoc[`${listKey}_${i}_${k}`]}
                    onChange={async ev=>{
                      const file = ev.target.files[0];
                      if(!file) return;
                      const uploadKey = `${listKey}_${i}_${k}`;
                      setUploadingDoc(p => ({ ...p, [uploadKey]: true }));
                      try {
                      const famRel = (row.relationship || `Member${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
                      const famName = (row.name || `Fam${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
                      const docType = k === 'photo' ? 'Photo' : (k === 'aadhaar_back_doc' ? 'Aadhaar_Back' : 'Aadhaar_Front');
                      const fileId = getCleanFileIdentifier(
                        form.employee_code || form.emp_code || (selId !== 'new' ? selId : ''),
                        form.name,
                        `Family_${famRel}_${famName}_${docType}`
                      );
                      if (k === 'photo') {
                        const res = await processEmployeePhoto(file);
                        if (res.error) {
                          alert(res.error);
                          setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                          return;
                        }
                        const r2Url = await uploadToR2(res.dataUrl, 'photos', fileId);
                        if (r2Url) {
                          const updated = [...(form[listKey]||[])];
                          if (updated[i]) {
                            updated[i] = { ...updated[i], [k]: r2Url };
                            set(listKey)(updated);
                          }
                        } else {
                          alert("Failed to upload family photo to storage.");
                        }
                        setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                        return;
                      }
                      const res = await processEmployeeDocument(file);
                      if (res.error) {
                        alert(res.error);
                        setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                        return;
                      }
                      const r2Url = await uploadToR2(res.dataUrl, 'documents', fileId);
                      if (r2Url) {
                        const updated = [...(form[listKey]||[])];
                        if (updated[i]) {
                          updated[i] = { ...updated[i], [k]: r2Url };
                          set(listKey)(updated);
                        }
                      } else {
                        alert("Failed to upload family document to storage.");
                      }
                      setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                      } catch (error) { setMsg('Family document upload failed: ' + error.message); }
                      finally { setUploadingDoc(previous => ({...previous, [uploadKey]: false})); }
                    }} />
                    {uploadingDoc[`${listKey}_${i}_${k}`] ? (
                      <div title="Uploading to storage..." style={{fontSize:14, flexShrink:0}}>⏳</div>
                    ) : (
                      row[k] && (
                        k === 'photo' ? (
                          <a href={row[k]} target="_blank" rel="noreferrer noopener" title="View family member photo in new tab">
                            <img src={row[k]} alt="Preview" title="Family Member Photo" style={{width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid #3b82f6', cursor: 'pointer'}} />
                          </a>
                        ) : (
                          <a href={row[k]} target="_blank" rel="noreferrer noopener" title="View uploaded document in new tab" style={{fontSize:14, flexShrink:0, textDecoration:'none', cursor:'pointer'}}>
                            ✅
                          </a>
                        )
                      )
                    )}
                </div>
              ) : type === 'date' || k === 'dob' ? (
                <div>
                  <DatePickerDDMMYYYY
                    value={row[k] || ''}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={val => {
                      const nl = [...(form[listKey] || [])];
                      nl[i] = { ...nl[i], [k]: val };
                      if (listKey === 'nominees' && k === 'dob') {
                        const a = calcAge(val);
                        nl[i].age = typeof a === 'number' && a >= 0 ? a : '';
                      }
                      set(listKey)(nl);
                    }}
                    style={{width: '100%', height: 32, background: '#fff', borderRadius: 8, fontSize: 12, border: '1.5px solid #cbd5e1'}}
                  />
                  {(() => {
                    if (!row[k]) return null;
                    const memberAge = calcAge(row[k]);
                    let warn = "";
                    if (typeof memberAge === 'number') {
                      if (memberAge < 0) {
                        warn = "⚠️ Future date";
                      } else if (form.dob && row.relationship) {
                        const rel = row.relationship.trim().toLowerCase();
                        const empAge = calcAge(form.dob);
                        if (rel === 'father' || rel === 'mother') {
                          if (typeof empAge === 'number' && memberAge - empAge < 14) {
                            warn = "⚠️ Must be ≥ 14y older";
                          }
                        } else if (rel === 'son' || rel === 'daughter') {
                          if (typeof empAge === 'number' && empAge - memberAge < 14) {
                            warn = "⚠️ Must be ≥ 14y younger";
                          }
                        } else if (rel === 'spouse') {
                          if (memberAge < 18) {
                            warn = "⚠️ Must be ≥ 18 yrs";
                          }
                        }
                      }
                    }
                    return (
                      <div style={{fontSize: 10, marginTop: 2, fontWeight: 700, color: warn ? '#dc2626' : '#2563eb', lineHeight: 1.2}}>
                        {warn ? warn : (typeof memberAge === 'number' && memberAge >= 0 ? `Age: ${memberAge} yrs` : '')}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <input type={k==='email'?'email':(k==='phone'?'tel':(type||'text'))} maxLength={k==='name'?100:undefined} style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'7px 10px',fontSize:13}} value={row[k]||''}
                  onChange={ev=>{
                    let val = ev.target.value;
                    if (k === 'name') val = val.slice(0, 100);
                    if (k === 'phone' || k === 'aadhaar_last4') val = val.replace(/\D/g, '').slice(0, k==='phone'?10:4);
                    const nl=[...(form[listKey]||[])];
                    nl[i]={...nl[i],[k]:val};
                    if (listKey === 'nominees' && k === 'dob') {
                      if (val) {
                        const d = new Date(val); const t = new Date();
                        if (!isNaN(d.getTime())) {
                          let a = t.getFullYear() - d.getFullYear();
                          if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
                          if (a >= 0) nl[i].age = a;
                        }
                      }
                    }
                    set(listKey)(nl);
                  }} />
              )}
            </div>
            );
          })}
          <button style={{height:32,background:'#fee2e2',border:'none',borderRadius:8,cursor:'pointer',color:'#dc2626',fontWeight:700}} onClick={()=>{
            set(listKey)((form[listKey]||[]).filter((_,j)=>j!==i));
          }}>×</button>
        </div>
      ))}
      <button style={{background:'none',border:'1.5px dashed #94a3b8',borderRadius:8,padding:'8px 14px',cursor:'pointer',color:'#475569',fontWeight:600,fontSize:13}} onClick={()=>{
        const cur = form[listKey] || [];
        const defaultShare = listKey === 'nominees' && cur.length === 0 ? '100' : '';
        set(listKey)([...cur, listKey === 'nominees' ? { share: defaultShare } : {}]);
      }}>+ Add Row</button>
    </div>
  );

  const inp = (label, k, type='text', opts=null, hint=null) => {
    if (k === 'email') type = 'email';
    if (k === 'phone' || k === 'emergency_phone') type = 'tel';

    let minAttr, maxAttr;
    if (type === 'date') {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const min18Str = new Date(today.setFullYear(today.getFullYear() - 18)).toISOString().split('T')[0];
      if (k === 'dob') maxAttr = min18Str;
      if (k === 'doj') minAttr = form.dob ? new Date(new Date(form.dob).setFullYear(new Date(form.dob).getFullYear() + 18)).toISOString().split('T')[0] : '';
      if (k === 'hr_induction_date') minAttr = form.doj || '';
      if (k === 'date_of_exit' || k === 'prev_exit_date') maxAttr = todayStr;
    }

    const isDatalist = type === 'datalist';
    return (
    <div data-jk-field={k} data-jk-error={fieldIssue?.field === k || undefined}>
      <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>{label}</div>
      {opts && !isDatalist ? (
        <select aria-label={label}
          style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#f8fafc'}}
          value={form[k]||''}
          onChange={e => {
            if (e.target.value === '__ADD_NEW__') {
              const newVal = window.prompt(`Enter new ${label}:`);
              if (newVal && newVal.trim()) {
                handleFieldChange(k, newVal.trim());
              }
            } else {
              handleFieldChange(k, e.target.value);
            }
          }}
        >
          {!opts.includes('') && <option value="">-- Select {label} --</option>}
          {opts.map(o=><option key={o} value={o}>{o === '' ? `-- Select ${label} --` : o}</option>)}
          {form[k] && !opts.includes(form[k]) && (
            <option value={form[k]}>{form[k]} (Custom)</option>
          )}
          {(k === 'department' || k === 'designation') && (
            <option value="__ADD_NEW__">＋ Enter New / Custom {label}...</option>
          )}
        </select>
      ) : opts && isDatalist ? (
        <>
          <input aria-label={label} type="text" list={`${k}_list`} style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#f8fafc'}} value={form[k]||''} onChange={e=>handleFieldChange(k, e.target.value)} />
          <datalist id={`${k}_list`}>{opts.map(o=><option key={o} value={o}/>)}</datalist>
        </>
      ) : k === 'aadhaar_last4' ? (
        <div style={{display:'flex', alignItems:'center', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'8px 12px', fontSize:13}}>
          <span style={{color:'#94a3b8', marginRight:4, letterSpacing:1, whiteSpace:'nowrap'}}>XXXX-XXXX-</span>
          <input aria-label={label} type={type} min={minAttr} max={maxAttr} style={{border:'none', outline:'none', background:'transparent', width:'100%', fontSize:13, letterSpacing:1}} value={form[k]||''} onChange={e=>{
            let val = e.target.value.replace(/\D/g, '').slice(0,4);
            handleFieldChange(k, val);
          }} placeholder="1234" />
        </div>
      ) : type === 'date' ? (
        <DatePickerDDMMYYYY
          value={form[k] || ''}
          onChange={val => handleFieldChange(k, val)}
          min={minAttr}
          max={maxAttr}
          style={{width: '100%', height: 38, background: '#f8fafc', borderRadius: 8, fontSize: 13, border: '1.5px solid #cbd5e1'}}
        />
      ) : (
        <input aria-label={label} type={type} min={minAttr} max={maxAttr} maxLength={(k==='name'||k==='father_name'||k==='emergency_name'||k==='spouse_name') ? 100 : undefined} style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#f8fafc'}} value={form[k]||''} onChange={e=>{
          let val = e.target.value;
          if (k==='name'||k==='father_name'||k==='emergency_name'||k==='spouse_name') val = val.slice(0, 100);
          if (k==='phone'||k==='emergency_phone') val = val.replace(/\D/g,'').slice(0,10);
          if (k==='pan') val = val.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,10);
          if (k==='bank_account'||k==='bank_account_number') val = val.replace(/\D/g,'').slice(0,18);
          if (k==='uan') val = val.replace(/\D/g,'').slice(0,12);
          if (k==='esi_id') val = val.replace(/\D/g,'').slice(0,17);
          handleFieldChange(k, val);
        }} />
      )}
      {hint && <div style={{marginTop:4,fontSize:11,color:'#64748b',lineHeight:1.35}}>{hint}</div>}
      {inlineError(k)}
    </div>
  )};
  const inpNum = (label, k) => (
    <div data-jk-field={k} data-jk-error={fieldIssue?.field === k || undefined}>
      <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>{label}</div>
      <input aria-label={label} type="number" style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#f8fafc'}} value={form[k]||''} onChange={e=>set(k)(parseFloat(e.target.value)||0)} />
      {inlineError(k)}
    </div>
  );

  const uploadInp = (label, k, accept='image/*,.pdf', scanType=null) => (
    <div data-jk-field={k} data-jk-error={fieldIssue?.field === k || undefined}>
      <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>{label}</div>
      <div style={{display:'flex', gap:4, alignItems:'center', width:'100%', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'6px 8px', overflow:'hidden'}}>
        <input aria-label={label} type="file" accept={accept} style={{width:'100%', fontSize:12}}
          disabled={uploadingDoc[k]}
          onChange={async ev=>{
            const file = ev.target.files[0];
            if(!file) return;
            setUploadingDoc(p => ({ ...p, [k]: true }));
            try {
            const res = await processEmployeeDocument(file);
            if (res.error) {
              alert(res.error);
              setUploadingDoc(p => ({ ...p, [k]: false }));
              return;
            }
            // if (scanType) scanDocument(file, scanType); // OCR scanning disabled to optimize upload speed and prevent freeze
            const docTypeMap = {
              emp_photo: 'Photo',
              emp_aadhaar_front_doc: 'Aadhaar_Front',
              emp_aadhaar_doc: 'Aadhaar_Front',
              emp_aadhaar_back_doc: 'Aadhaar_Back',
              emp_pan_doc: 'PAN',
              emp_bank_doc: 'Bank_Proof'
            };
            const docType = docTypeMap[k] || k.replace(/^emp_/, '').replace(/_doc$/, '');
            const fileId = getCleanFileIdentifier(form.employee_code || form.emp_code || (selId !== 'new' ? selId : ''), form.name, docType);
            const r2Url = await uploadToR2(res.dataUrl, 'documents', fileId);
            if (r2Url) {
              set(k)(r2Url);
              if (k === 'emp_aadhaar_front_doc') set('emp_aadhaar_doc')(r2Url);
            } else {
              alert("Failed to upload document to storage. Please check connection.");
            }
            setUploadingDoc(p => ({ ...p, [k]: false }));
            } catch (error) { setMsg('Document upload failed: ' + error.message); }
            finally { setUploadingDoc(previous => ({...previous, [k]: false})); }
          }} />
          {uploadingDoc[k] ? (
            <div title="Uploading to storage..." style={{fontSize:14, flexShrink:0}}>⏳</div>
          ) : (
            (form[k] || (k === 'emp_aadhaar_front_doc' && form.emp_aadhaar_doc)) && (
              <a
                href={form[k] || form.emp_aadhaar_doc}
                target="_blank"
                rel="noreferrer noopener"
                title="Uploaded to storage (click to view)"
                style={{fontSize:16, flexShrink:0, textDecoration:'none', cursor:'pointer'}}
              >
                ✅
              </a>
            )
          )}
      </div>
      {inlineError(k)}
    </div>
  );

  const grid = (...children) => (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:14}}>{children.map((child,index) => React.isValidElement(child) ? React.cloneElement(child, {key: child.key || child.props['data-jk-field'] || index}) : child)}</div>
  );
  const wide = (...children) => (
    <div style={{display:'grid',gridTemplateColumns:'1fr',gap:14}}>{children.map((child,index) => React.isValidElement(child) ? React.cloneElement(child, {key: child.key || child.props['data-jk-field'] || index}) : child)}</div>
  );

  const TABS = [
    {id:'checklist',label:'📌 Instructions & Checklist',shortLabel:'📌 Checklist'},
    {id:'personal',label:'👤 Personal Details',shortLabel:'👤 Personal'},
    {id:'job',label:'💼 Job & Official',shortLabel:'💼 Job'},
    {id:'kyc',label:'🏦 KYC & Bank',shortLabel:'🏦 KYC'},
    {id:'pf_esi',label:'📋 PF & ESI',shortLabel:'📋 PF/ESI'},
    {id:'family',label:'👨‍👩‍👧 Family Members',shortLabel:'👨‍👩‍👧 Family'},
    {id:'nominees',label:'🤝 Nominees',shortLabel:'🤝 Nominees'},
    {id:'forms',label:'📄 Generate Forms',shortLabel:'📄 Forms'},
  ];

  const dataTabs = TABS.filter(item => !['checklist', 'forms'].includes(item.id));
  const readyCount = dataTabs.filter(item => !validateJoiningKitStep(form, item.id)).length;

  const slug = s => String(s||'').trim().replace(/[^A-Za-z0-9]+/g,'-');

  const renderTab = () => {
    switch(tab) {
      case 'checklist': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{fontSize:13,color:'#475569'}}>Have a photograph, Aadhaar, PAN and bank proof ready. Family documents and previous PF/ESI details apply where needed.</div>
          <details><summary style={{cursor:'pointer',fontWeight:700,fontSize:13}}>Document checklist &amp; instructions</summary>
          {/* Hero Banner with Integrated Privacy Notice */}
          <div className="jk-checklist-hero">
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:8, flexWrap:'wrap'}}>
              <span style={{fontSize:20}}>📋</span>
              <span className="jk-hero-title" style={{fontSize:17, fontWeight:800, color:'#0f172a', letterSpacing:0.2}}>
                Pre-Onboarding Softcopy Checklist
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                Required for PF & ESI
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#fef3c7', color:'#b45309', border:'1px solid #fde68a', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                📁 Max 4MB per file
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#ecfdf5', color:'#047857', border:'1px solid #a7f3d0', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                🔒 Strictly Confidential
              </span>
            </div>
            <div className="jk-hero-desc" style={{fontSize:13, color:'#475569', lineHeight:1.55}}>
              Before filling the Joining KIT, please ensure you have the following information and soft copies (PDF or clear JPG/PNG, maximum 4MB per file) ready on your device. This guarantees seamless employee onboarding and avoids statutory rejection.
            </div>

            {/* Privacy & Confidentiality Notice inside same section */}
            <div style={{marginTop:12, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:10}}>
              <div style={{fontSize:18, flexShrink:0, marginTop:1}}>🛡️</div>
              <div style={{fontSize:12, color:'#166534', lineHeight:1.5}}>
                <strong>Confidentiality & Privacy Notice:</strong> We make sure all submitted data is strictly confidential across both internal and external channels. Documents are accessed only for authorized payroll onboarding and statutory PF/ESI filing. Unmasked Aadhaar copies are <strong>purged immediately</strong> after real-time verification; only masked last 4 digits are retained.
              </div>
            </div>
          </div>

          {/* Checklist Items Grid - 3 items per row */}
          <div className="jk-checklist-grid">
            {/* 1. Photo */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">📷</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>1. Passport Size Photograph</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Clean, front-facing digital photo with clear lighting and neutral background.
                </div>
              </div>
            </div>

            {/* 2. Aadhaar Card */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">🪪</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>2. Aadhaar Card (Front & Back)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Soft copies of both Front Side (name, photo, DoB) and Back Side (address). <em>Purged post-verification</em>.
                </div>
              </div>
            </div>

            {/* 3. PAN Card */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">💳</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>3. PAN Card</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Legible soft copy with 10-digit PAN number and Father's Name clearly visible.
                </div>
              </div>
            </div>

            {/* 4. Bank Proof */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">🏦</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>4. Cancelled Cheque / Passbook</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Must clearly show: Account Holder Name, Bank Account Number, and IFSC Code.
                </div>
              </div>
            </div>

            {/* 5. Family Members */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">👨‍👩‍👧</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>5. Family Members (Spouse, Kids, Parents)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  For EPF Form 2 & ESI Pehchan: Dependents' Date of Birth (DoB), Photo, and Aadhaar copy.
                </div>
              </div>
            </div>

            {/* 6. Previous PF / ESI */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">📜</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>6. Previous PF / UAN (If Applicable)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Previous UAN, PF member ID, date of exit, or Pension Payment Order (PPO).
                </div>
              </div>
            </div>
          </div>

          </details>
          {/* Action to proceed */}
          <div className="jk-action-box" style={{background:'#f8fafc',border:'1.5px dashed #cbd5e1',borderRadius:12,padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
            <div>
              <div style={{fontWeight:800,fontSize:13.5,color:'#0f172a'}}>Ready to proceed?</div>
              <div style={{fontSize:12,color:'#64748b',marginTop:2}}>Once you have verified having these documents ready, proceed to enter personal details.</div>
            </div>
            <button
              className="btn btn-primary"
              style={{padding:'10px 22px',fontSize:13.5,fontWeight:700,display:'inline-flex',alignItems:'center',gap:8,cursor:'pointer'}}
              onClick={() => {
                setTab('personal');
                window.scrollTo({top:0,behavior:'smooth'});
              }}
            >
              🚀 I Have Softcopies Ready — Proceed →
            </button>
          </div>
        </div>
      );
      case 'personal': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div data-jk-field="emp_photo" data-jk-error={fieldIssue?.field === 'emp_photo' || undefined}><EmployeePhotoUploader
            value={form.emp_photo || ""}
            onChange={photo => handleFieldChange("emp_photo", photo)}
            label="Employee Photograph"
            required={true}
            onBusyChange={onPhotoBusyChange}
            empCode={form.employee_code || form.emp_code || (selId !== 'new' ? selId : '')}
            empName={form.name}
          />{inlineError("emp_photo")}</div>

          {grid(
            inp('Employee ID *','employee_code'),
            inp('Employee Name *','name'),
            inp('Gender','gender','text',['Male','Female','Transgender']),
            inp('Date of Birth *','dob','date')
          )}
          {grid(
            inp('Marital Status','marital_status','text',['','Single','Married','Widow','Widower','Divorcee']),
            ...(form.marital_status && form.marital_status !== 'Single' && form.marital_status !== 'Unmarried' ? [
              inp('Spouse Name (Optional)','spouse_name','text',null,'💡 Optional: Fill only if applicable')
            ] : []),
            inp("Father's Name",'father_name'),
            <ReligionField key="religion" value={form.religion||''} onChange={val=>handleFieldChange('religion', val)} />
          )}
          {grid(
            inp('Blood Group','blood_group','text',['','A+','A-','B+','B-','O+','O-','AB+','AB-']),
            inp('Specially Abled','specially_abled','select',['No','Yes']),
            inp('Mobile *','phone','text',null,'💡 Primary 10-digit mobile (linked to Aadhaar & EPFO — do not change)'),
            inp('Email','email')
          )}

          {grid(
            uploadInp('Aadhaar Card (Front Side) *','emp_aadhaar_front_doc','image/*,.pdf','aadhaar'),
            uploadInp('Aadhaar Card (Back Side)','emp_aadhaar_back_doc','image/*,.pdf'),
            uploadInp('PAN Document *','emp_pan_doc','image/*,.pdf','pan')
          )}
          {grid(inp('Emergency Contact Name','emergency_name'), inp('Relation','emergency_relation','datalist',['Father','Mother','Spouse','Son','Daughter','Brother','Sister','Other']), inp('Emergency Phone','emergency_phone'))}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTop:'1px solid #e2e8f0'}}>
            <div style={{fontWeight:700,fontSize:13,color:'#334155'}}>Address</div>
          </div>
          {grid(inp('Present Address','temp_address'), inp('City','temp_city'), inp('State','temp_state'), inp('PIN','temp_pincode'))}
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:-4,marginBottom:-4}}>
            <input type="checkbox" id="same_addr" onChange={(e)=>{
              if(e.target.checked){
                setForm(p=>({...p, perm_address:p.temp_address, perm_city:p.temp_city, perm_state:p.temp_state, perm_pincode:p.temp_pincode}));
              } else {
                setForm(p=>({...p, perm_address:'', perm_city:'', perm_state:'', perm_pincode:''}));
              }
            }} />
            <label htmlFor="same_addr" style={{fontSize:13,color:'#64748b',cursor:'pointer',userSelect:'none'}}>Same as Present Address</label>
          </div>
          {grid(inp('Permanent Address','perm_address'), inp('City','perm_city'), inp('State','perm_state'), inp('PIN','perm_pincode'))}
        </div>
      );
      case 'job': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {grid(inp('Date of Joining *','doj','date'), inp('HR Induction Date','hr_induction_date','date'))}
          {grid(inp('Designation','designation','select',desigOptions), inp('Department','department','select',deptOptions), inp('Location / Branch','location'))}
          {grid(inp('Employment Status','employment_status','text',['confirmed','probation','contract','intern']), inpNum('Gross Salary (₹)','basic_salary'))}

          {company?.config?.hra_customised && (
            <div style={{display:'flex',flexDirection:'column',gap:8,padding:'12px 16px',background:'#f8fafc',border:'1px dashed #cbd5e1',borderRadius:8,maxWidth:560,width:'100%',boxSizing:'border-box'}}>
              <div style={{fontSize:11,fontWeight:800,color:'#475569',textTransform:'uppercase',letterSpacing:0.8}}>🏠 Custom HRA (Specific to this employee)</div>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>Custom HRA Override (₹)</div>
                  <input type="number" style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#fff',boxSizing:'border-box'}}
                    value={form.custom_hra_pct||''} onChange={e=>setForm(p=>({...p, custom_hra_pct: e.target.value}))}
                    placeholder="Enter fixed amount (e.g. 0 or 5000)" />
                </div>
              </div>
            </div>
          )}

          <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:10,padding:'14px 16px',display:'flex',flexDirection:'column',gap:12,maxWidth:560,width:'100%',boxSizing:'border-box'}}>
            <div style={{fontSize:11,fontWeight:800,color:'#334155',textTransform:'uppercase',letterSpacing:0.8}}>📋 Voluntary Deductions (Optional)</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:14}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>VPF Amount (₹/month)</div>
                <input type="number" min="0" style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#fff',boxSizing:'border-box'}}
                  value={form.vpf_pct||''}
                  onChange={e => {
                    const val = parseFloat(e.target.value)||0;
                    const basic = (form.basic_salary||0) * 0.5;
                    if(val > basic) { setMsg(`VPF cannot exceed Basic Salary (₹${Math.floor(basic)}/month)`); return; }
                    setMsg('');
                    set('vpf_pct')(val);
                  }}
                  placeholder="e.g. 2000" />
                {(() => {
                  const gross = parseFloat(form.basic_salary)||0;
                  const basic = gross * 0.5;
                  const epf = basic * 0.12;
                  const vpf = parseFloat(form.vpf_pct)||0;
                  const totalPF = epf + vpf;
                  const monthlyTaxFreeLimit = 20833;
                  return (
                    <div style={{marginTop:5,fontSize:11,color:'#64748b',lineHeight:1.4}}>
                      Max allowed: <strong>₹{Math.floor(basic).toLocaleString('en-IN')}</strong> (Basic Salary)
                      {totalPF > monthlyTaxFreeLimit && (
                        <span style={{display:'block',color:'#b45309',fontWeight:600,marginTop:2}}>⚠️ EPF+VPF exceeds ₹20,833/month — interest above this limit will be taxable.</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:0.5}}>TDS Amount (₹/month)</div>
                <input type="number" min="0" style={{width:'100%',border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 12px',fontSize:13,background:'#fff',boxSizing:'border-box'}}
                  value={form.tds_amount||''}
                  onChange={e => {
                    const val = parseFloat(e.target.value)||0;
                    const gross = parseFloat(form.basic_salary)||0;
                    if(val >= gross) { setMsg('TDS cannot be equal to or more than Gross Salary.'); return; }
                    setMsg('');
                    set('tds_amount')(val);
                  }}
                  placeholder="e.g. 1500" />
                <div style={{marginTop:5,fontSize:11,color:'#64748b',lineHeight:1.4}}>Fixed monthly TDS deduction. Must be less than Gross Salary.</div>
              </div>
            </div>
          </div>
          {grid(inp('Last Working Day (if resigned)','date_of_exit','date'))}
        </div>
      );
      case 'kyc': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#92400e'}}>🔒 Only the last 4 digits of Aadhaar are stored for security.</div>
          {grid(inp('Aadhaar Last 4 Digits','aadhaar_last4'), inp('PAN Number *','pan'))}
          {grid(
            inp('Bank Name','bank_name','datalist',POPULAR_INDIAN_BANKS,'💡 Choose from list or type custom bank name'),
            inp('Bank Account Number','bank_account','text',null,form.bank_account ? (isValidBankAccount(form.bank_account) ? `✅ ${form.bank_account.length} digits (Valid length)` : `⚠️ ${form.bank_account.length} digits (Indian accounts must be 9–18 digits)`) : '💡 9 to 18 numeric digits (e.g. 123456789012)'),
            inp('IFSC Code','bank_ifsc','text',null,form.bank_ifsc ? (isValidIFSC(form.bank_ifsc) ? '✅ Valid IFSC format' : '⚠️ Must be 11 characters (e.g. SBIN0001234)') : '💡 11-digit code e.g. HDFC0001234'),
            inp('Branch','bank_branch')
          )}
          {grid(uploadInp('Cancelled Cheque / Passbook *','emp_bank_doc','image/*,.pdf','cheque'))}
        </div>
      );
      case 'pf_esi': return (
        <div style={{display:'flex',flexDirection:'column',gap:20}}>
          {/* Section 1: Provident Fund (EPFO) */}
          <div style={{background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'20px 22px',boxShadow:'0 1px 3px rgba(0,0,0,0.03)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>🏛️</span>
                <span style={{fontWeight:800,fontSize:14,color:'#0f172a'}}>Employees' Provident Fund (EPF & EPS)</span>
              </div>
            </div>

            {grid(
              inp(
                'Do you have an existing PF Account / UAN? *',
                'prev_pf',
                'text',
                ['No','Yes'],
                '💡 Select "Yes" if employee already has an existing PF account or was previously enrolled under EPFO.'
              )
            )}

            {form.prev_pf === 'Yes' ? (
              <div style={{marginTop:14}}>
                {grid(
                  inp(
                    'Universal Account Number (UAN)',
                    'uan',
                    'text',
                    null,
                    form.uan ? (
                      isValidUAN(form.uan)
                        ? '✅ 12 digits (Valid UAN format)'
                        : `⚠️ ${form.uan.length}/12 digits (Must be exactly 12 digits if provided)`
                    ) : '💡 Don’t know? Leave blank — we will fetch it from your mobile number. UAN is lifelong & cannot be duplicated.'
                  ),
                  inp(
                    'PF Account Number (Member ID)',
                    'pf_account',
                    'text',
                    null,
                    '💡 Company-assigned Member ID (e.g. PYKRP/12345/678). Auto-generated by employer during monthly filing if new.'
                  )
                )}
              </div>
            ) : null}
          </div>

          {/* Section 2: Employee State Insurance (ESIC) */}
          <div style={{background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'20px 22px',boxShadow:'0 1px 3px rgba(0,0,0,0.03)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>🏥</span>
                <div>
                  <div style={{fontWeight:800,fontSize:14,color:'#0f172a'}}>Employee State Insurance (ESIC)</div>
                  <div style={{fontSize:11.5,color:'#64748b',marginTop:2}}>Medical insurance coverage under ESI Act (applicable when Basic + DA ≤ ₹21,000/month).</div>
                </div>
              </div>
            </div>

            {grid(
              inp(
                'Do you have an existing ESIC / IP Number? *',
                'prev_esi',
                'text',
                ['No','Yes'],
                '💡 Select "Yes" if employee already has an existing 17-digit ESIC Insured Person (IP) number.'
              )
            )}

            {form.prev_esi === 'Yes' ? (
              <div style={{marginTop:14}}>
                {grid(
                  inp(
                    'ESI Insurance No. (IP Number)',
                    'esi_id',
                    'text',
                    null,
                    form.esi_id ? (
                      form.esi_id.length === 17
                        ? '✅ 17 digits (Valid ESIC IP format)'
                        : `⚠️ ${form.esi_id.length}/17 digits (Must be exactly 17 digits if provided)`
                    ) : '💡 17-digit Insured Person (IP) number from previous employment.'
                  ),
                  inp(
                    'Dispensary / Hospital',
                    'dispensary',
                    'text',
                    null,
                    '💡 Preferred ESIC branch dispensary or clinic near employee residence for primary outpatient medical care.'
                  )
                )}
              </div>
            ) : null}
          </div>

          {/* Section 4: International Worker (IW) */}
          <div style={{background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'20px 22px',boxShadow:'0 1px 3px rgba(0,0,0,0.03)'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
              <span style={{fontSize:18}}>🌍</span>
              <div>
                <div style={{fontWeight:800,fontSize:14,color:'#0f172a'}}>International Worker (IW) Status</div>
                <div style={{fontSize:11.5,color:'#64748b',marginTop:2}}>Statutory provisions for foreign expatriates or Indian staff returning from reciprocal SSA nations.</div>
              </div>
            </div>

            <div style={{background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#1e40af', lineHeight: 1.5}}>
              💡 <strong>Fill only if you are an International Worker:</strong> Foreign passport holders working in India or employees under a bilateral Social Security Agreement (SSA). <strong>For regular domestic Indian employees, keep this set to "No".</strong>
            </div>

            {grid(
              inp(
                'Is International Worker?',
                'intl_worker',
                'text',
                ['No','Yes'],
                '💡 Keep "No" unless employee holds a foreign passport or SSA certificate.'
              ),
              ...(form.intl_worker === 'Yes' ? [
                inp('Country of Origin *', 'country_of_origin', 'text', null, '💡 Country issuing employee’s foreign passport'),
                inp('Passport Number *', 'passport_number', 'text', null, '💡 Valid foreign passport number'),
                inp('Passport Validity *', 'passport_validity', 'date', null, '💡 Passport expiration date')
              ] : [])
            )}

            {form.intl_worker !== 'Yes' && (
              <div style={{marginTop: 10, fontSize: 11.5, color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6}}>
                <span>✨</span>
                <span>Marked as domestic Indian employee. No foreign passport or SSA details required.</span>
              </div>
            )}
          </div>
        </div>
      );
      case 'family': return (
        <div data-jk-field="family_members" data-jk-error={fieldIssue?.field === 'family_members' || undefined} style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,padding:'10px 14px',fontSize:12.5,color:'#1e40af',lineHeight:1.5}}>
            💡 <strong>Fill only if you are eligible for ESI:</strong> Family details are required for ESIC medical benefit enrollment if your <strong>Basic + DA is less than or equal to ₹21,000/month</strong>. Otherwise, you can leave this section blank. If you’re unsure, check with your HR/Admin.
          </div>
          <div style={{fontSize:13,color:'#64748b'}}>Add spouse, children, parents etc. (used in Form 2 EPS nomination & ESI Form) — Fill only if you are eligible for ESI (Basic + DA is less than or equal to ₹21,000/month; otherwise you can leave this section blank).</div>
          {inlineError('family_members')}
          {listField('family_members',[
            ['name','Name'],
            ['relationship','Rel.','datalist',['Father','Mother','Spouse','Son','Daughter','Brother','Sister','Other']],
            ['dob','Date of Birth','date'],
            ['residing','Residing?','select',['Yes','No']],
            ['address','Address','text',null,(r)=>r?.residing==='No'],
            ['photo','Photo','file',{accept:'image/*'}],
            ['aadhaar_doc','Aadhaar (Front)','file',{accept:'image/*,.pdf'}],
            ['aadhaar_back_doc','Aadhaar (Back)','file',{accept:'image/*,.pdf'}],
          ])}
        </div>
      );
      case 'nominees': return (
        <div data-jk-field="nominees" data-jk-error={fieldIssue?.field === 'nominees' || undefined} style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{fontSize:13,color:'#64748b'}}>Add legal nominees for EPF (Form 2) and Gratuity (Form F). Shares across all nominees should total 100%.</div>

          {(form.family_members||[]).length > 0 && (
            <div style={{marginBottom:10, display:'flex', gap:8, alignItems:'center', background:'#f8fafc', padding:'10px 14px', borderRadius:8, border:'1.5px dashed #cbd5e1'}}>
              <span style={{fontSize:13, fontWeight:700, color:'#334155'}}>🪄 Quick Add from Family:</span>
              <select style={{border:'1.5px solid #cbd5e1',borderRadius:6,padding:'6px 10px',fontSize:13, flex:1, maxWidth:300}} onChange={(e) => {
                const idx = e.target.value;
                if(idx !== "") {
                  const fm = form.family_members[idx];
                  let age = '';
                  if (fm.dob) {
                    const d = new Date(fm.dob); const t = new Date();
                    if (!isNaN(d.getTime())) {
                      age = t.getFullYear() - d.getFullYear();
                      if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) age--;
                      if (age < 0) age = '';
                    }
                  }
                  let address = '';
                  const isResiding = String(fm.residing).toLowerCase() === 'yes';
                  if (isResiding) {
                    address = [form.temp_address || form.perm_address, form.temp_city || form.perm_city, form.temp_state || form.perm_state, form.temp_pincode || form.perm_pincode].filter(Boolean).join(", ") || `${form.perm_address||''} ${form.perm_city||''}`.trim();
                  } else if (fm.address) {
                    address = fm.address;
                  }

                  setForm(p => {
                    const defaultShare = (p.nominees||[]).length === 0 ? '100' : '';
                    return {
                      ...p,
                      nominees: [...(p.nominees||[]), {
                        name: fm.name || '',
                        relationship: fm.relationship || '',
                        dob: fm.dob || '',
                        age: age,
                        address: address,
                        share: defaultShare
                      }]
                    };
                  });
                  e.target.value = "";
                }
              }}>
                <option value="">-- Select a family member --</option>
                {form.family_members.map((f, i) => (
                  <option key={i} value={i}>{f.name || 'Unnamed'} ({f.relationship || 'Unknown'})</option>
                ))}
              </select>
            </div>
          )}

          {inlineError('nominees')}
          {listField('nominees',[
            ['name','Name'],
            ['relationship','Relationship','datalist',['Father','Mother','Spouse','Son','Daughter','Brother','Sister','Other']],
            ['dob','Date of Birth','date'],
            ['age','Age'],
            ['address','Address'],
            ['share','Share %'],
          ])}
        </div>
      );
      case 'forms': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{fontSize:13,color:'#64748b',lineHeight:1.6}}>Generate official compliance forms using the data entered in this Joining KIT. Click any button to download the filled form as a <strong>.docx</strong> file (opens in MS Word / Google Docs). Save first before generating.</div>
          {genMsg && <div style={{background:'#f0fdf4',border:'1px solid #86efac',color:'#166534',padding:'10px 14px',borderRadius:8,fontWeight:600,fontSize:13}}>{genMsg}</div>}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:14}}>
            {[
              {label:'📄 Appointment Letter', sub:'Formal Employment Contract (PDF)', fn: async () => {
                const codeClean = String(form.employee_code || form.emp_code || (selId !== 'new' ? selId : '001')).replace(/[^a-zA-Z0-9]/g, "");
                const defaultRef = form.appointment_ref_no || form.ref_no || `REF/APT/${codeClean}/${new Date().getFullYear()}`;
                const userRef = window.prompt("Enter Reference Number for Appointment Letter (leave blank to omit):", defaultRef);
                if (userRef === null) return; // user cancelled
                await generateAppointmentLetterPDF(form, company, userRef);
              }},
              {label:'📄 PF Form 11', sub:'EPF Declaration on Joining', fn: async () => { const blob = await buildJKForm11(form, company); jkDownloadBlob(blob, `Form11_${slug(form.name)}_${slug(form.employee_code)}.docx`); }},
              {label:'📄 PF Form 2', sub:'EPF & EPS Nomination', fn: async () => { const blob = await buildJKForm2(form, company); jkDownloadBlob(blob, `Form2_${slug(form.name)}_${slug(form.employee_code)}.docx`); }},
              {label:'📄 ESI Form (Pehchan)', sub:'ESI Employee Declaration', fn: async () => { const blob = await buildJKESIForm(form, company); jkDownloadBlob(blob, `ESIForm_${slug(form.name)}_${slug(form.employee_code)}.docx`); }},
              {label:'📄 Form F', sub:'Gratuity Nomination', fn: async () => { const blob = await buildJKFormF(form, company); jkDownloadBlob(blob, `FormF_${slug(form.name)}_${slug(form.employee_code)}.docx`); }},
            ].map(({label,sub,fn}) => (
              <button key={label} onClick={async()=>{ setGenMsg('Generating…'); try{await fn();setGenMsg('✅ Downloaded!');}catch(e){setGenMsg('❌ '+e.message);}setTimeout(()=>setGenMsg(''),3000); }} style={{background:'#ffffff',border:'1px solid #cbd5e1',borderRadius:12,padding:'16px 18px',cursor:'pointer',textAlign:'left',boxShadow:'0 1px 3px rgba(0,0,0,0.05)',transition:'all 0.2s ease'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='#94a3b8';e.currentTarget.style.boxShadow='0 4px 6px rgba(0,0,0,0.05)';e.currentTarget.style.transform='translateY(-1px)'}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)';e.currentTarget.style.transform='translateY(0)'}}>
                <div style={{fontWeight:700,fontSize:14,color:'#0f172a'}}>{label}</div>
                <div style={{fontSize:12,color:'#64748b',marginTop:4}}>{sub}</div>
              </button>
            ))}
            <button onClick={async()=>{ setGenMsg('Generating XLS…'); try{await exportJKMasterXLS(employees,company);setGenMsg('✅ Master XLS Downloaded!');}catch(e){setGenMsg('❌ '+e.message);}setTimeout(()=>setGenMsg(''),3000); }} style={{background:'#ffffff',border:'1px solid #cbd5e1',borderRadius:12,padding:'16px 18px',cursor:'pointer',textAlign:'left',boxShadow:'0 1px 3px rgba(0,0,0,0.05)',transition:'all 0.2s ease'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='#10b981';e.currentTarget.style.boxShadow='0 4px 6px rgba(16,185,129,0.1)';e.currentTarget.style.transform='translateY(-1px)'}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)';e.currentTarget.style.transform='translateY(0)'}}>
              <div style={{fontWeight:700,fontSize:14,color:'#065f46'}}>📊 All Employees Master XLS</div>
              <div style={{fontSize:12,color:'#64748b',marginTop:4}}>Export all joinees data (HR format)</div>
            </button>
            <button onClick={handleDownloadAllDocs} style={{background:'#ffffff',border:'1px solid #cbd5e1',borderRadius:12,padding:'16px 18px',cursor:'pointer',textAlign:'left',boxShadow:'0 1px 3px rgba(0,0,0,0.05)',transition:'all 0.2s ease'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='#3b82f6';e.currentTarget.style.boxShadow='0 4px 6px rgba(59,130,246,0.1)';e.currentTarget.style.transform='translateY(-1px)'}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)';e.currentTarget.style.transform='translateY(0)'}}>
              <div style={{fontWeight:700,fontSize:14,color:'#1e40af'}}>🗂️ Download All Docs (ZIP)</div>
              <div style={{fontSize:12,color:'#64748b',marginTop:4}}>Employee & family uploaded files</div>
            </button>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:0,minHeight:'100%'}}>
      <style>{`[data-jk-error="true"] { outline:2px solid #dc2626; outline-offset:4px; border-radius:8px; } .jk-save-state { font-size:12px; color:#64748b; margin-bottom:10px; }`}</style>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:'#0f172a'}}>📋 Joining KIT</div>
          <div style={{fontSize:13,color:'#64748b',marginTop:2}}>Employee onboarding master data · generate PF, ESI & Gratuity forms</div>
        </div>
        <button
          className="btn btn-ghost"
          style={{display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "8px 16px", borderRadius: 8, background: "#fff", border: "1.5px solid #cbd5e1", cursor: "pointer", fontWeight: 700, color: "#1e293b", boxShadow: "0 1px 2px rgba(0,0,0,0.05)"}}
          disabled={busy}
          onClick={async () => {
            if (!confirmDiscard()) return;
            setSaving(true);
            try {
            if (typeof onRefresh === "function") {
              await onRefresh();
            } else {
              window.location.reload();
            }
            setReloadVersion(value => value + 1);
            } catch (error) { setMsg('Refresh failed: ' + error.message); }
            finally { setSaving(false); }
          }}
          title="Reload Joining Kit and employee records"
        >
          🔄 Refresh Data
        </button>
      </div>

      {/* Employee selector */}
      <div style={{background:'#fff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'16px 20px',marginBottom:18}}>
        <div style={{fontSize:11,fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:0.8,marginBottom:8}}>Select Employee</div>
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
          <select style={{border:'1.5px solid #cbd5e1',borderRadius:8,padding:'9px 14px',fontSize:14,minWidth:280,background:'#f8fafc'}} aria-label="Select onboarding employee" value={selId} disabled={busy} onChange={e=>{ if (confirmDiscard()) setSelId(e.target.value); }}>
            <option value="new">➕ New Employee</option>
            {selId !== 'new' && !employees.some(employee => String(employee.id) === String(selId)) && <option value={selId}>{form.employee_code} {form.name}</option>}
            {[...employees].sort((a,b) => {
              const valA = (a.employee_code ?? a.emp_id ?? a.employee_id ?? '').toString().trim();
              const valB = (b.employee_code ?? b.emp_id ?? b.employee_id ?? '').toString().trim();
              if (!valA && !valB) return (a.name || '').localeCompare(b.name || '');
              if (!valA) return 1;
              if (!valB) return -1;
              const cmp = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
              return cmp !== 0 ? cmp : (a.name || '').localeCompare(b.name || '');
            }).map(e=><option key={e.id} value={e.id}>{e.employee_code ? `[${e.employee_code}] ` : ''}{e.name}</option>)}
          </select>
          <div style={{fontSize:12,color:'#94a3b8'}}>Select an existing employee to view/edit their Joining KIT, or choose "New Employee" to onboard someone new.</div>
        </div>
      </div>

      <div className="jk-save-state" role="status">{busy ? 'Saving or uploading…' : dirty ? 'Unsaved changes' : completed ? 'Onboarding saved' : 'No unsaved changes'} · {readyCount}/{dataTabs.length} data sections ready</div>
      {/* Mobile Stepper & Progress Indicator */}
      <div className="jk-mobile-stepper" style={{padding:'8px 12px 4px',background:'#fff',borderRadius:10,border:'1px solid #e2e8f0',marginBottom:10}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:5}}>
          <span style={{fontSize:12,fontWeight:800,color:'#1e293b'}}>
            Step {TABS.findIndex(t=>t.id===tab)+1} of {TABS.length}: <span style={{color:'#2563eb'}}>{TABS.find(t=>t.id===tab)?.shortLabel || TABS.find(t=>t.id===tab)?.label}</span>
          </span>
          <span style={{fontSize:11.5,fontWeight:700,color:'#64748b'}}>
            {readyCount} of {dataTabs.length} data sections ready
          </span>
        </div>
        <div style={{height:4,background:'#e2e8f0',borderRadius:3,overflow:'hidden'}}>
          <div style={{
            height:'100%',
            width:`${Math.round(readyCount / dataTabs.length * 100)}%`,
            background:'linear-gradient(90deg, #3b82f6, #2563eb)',
            borderRadius:3,
            transition:'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }} />
        </div>
      </div>

      {/* Tabs */}
      <div
        ref={tabsBarRef}
        className="jk-tabs-scroll"
        style={{
          display:'flex',
          gap:6,
          padding:'6px 8px 10px',
          borderBottom:'1.5px solid #e2e8f0',
          marginBottom:16,
          overflowX:'auto',
          WebkitOverflowScrolling:'touch',
          scrollbarWidth:'thin',
          scrollBehavior:'smooth'
        }}
      >
        {TABS.map((t, idx) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              data-active={isActive ? "true" : "false"}
              disabled={busy}
              onClick={()=>{setTab(t.id);setFieldIssue(null);setMsg('');}}
              style={{
                display:'inline-flex',
                alignItems:'center',
                gap:6,
                padding: isActive ? '7px 14px' : '7px 11px',
                borderRadius:20,
                border: isActive ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                background: isActive ? '#eff6ff' : '#ffffff',
                color: isActive ? '#1d4ed8' : '#475569',
                cursor:'pointer',
                fontSize:12.5,
                fontWeight: isActive ? 800 : 600,
                whiteSpace:'nowrap',
                flexShrink:0,
                transition:'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isActive ? '0 2px 6px rgba(37,99,235,0.18)' : '0 1px 2px rgba(0,0,0,0.03)'
              }}
            >
              <span style={{
                width: 17,
                height: 17,
                borderRadius: '50%',
                background: isActive ? '#2563eb' : '#e2e8f0',
                color: isActive ? '#ffffff' : '#64748b',
                fontSize: 10,
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {idx + 1}
              </span>
              <span className="jk-tab-text-full">{t.label}{dataTabs.some(item => item.id === t.id) && !validateJoiningKitStep(form, t.id) ? ' ✓' : ''}</span>
              <span className="jk-tab-text-short">{t.shortLabel}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="jk-content-body" style={{background:'#fff',border:'1.5px solid #e2e8f0',borderRadius:12,flex:1}}>
        <div style={{padding:'12px 16px 0',fontSize:12,color:'#64748b'}}><span style={{color:'#dc2626',fontWeight:700}}>*</span> indicates mandatory fields.</div>
        {fieldIssue && <div role="alert" style={{padding:12,color:'#b91c1c'}}>Please correct the highlighted field: {fieldIssue.message}</div>}
        <fieldset ref={contentRef} disabled={saving} style={{border:0,padding:0,margin:0,minWidth:0}}>{renderTab()}</fieldset>
      </div>

      {/* Footer */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:16,padding:'14px 0',borderTop:'1.5px solid #e2e8f0',flexWrap:'wrap',gap:10}}>
        {msg && <div style={{fontSize:13,fontWeight:600,color:msg.startsWith('✅')?'#166534':'#b91c1c'}}>{msg}</div>}
        <div style={{display:'flex',gap:10,marginLeft:'auto',flexWrap:'wrap'}}>
          {tab !== 'checklist' && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => {
                const idx = TABS.findIndex(t => t.id === tab);
                if (idx > 0) setTab(TABS[idx - 1].id);
              }}
              style={{padding: "11px 18px", fontSize: 14, fontWeight: 700}}
            >
              ← Previous
            </button>
          )}
          {tab === 'checklist' ? (
            <button className="btn btn-primary" style={{padding: "11px 22px", fontSize: 14}} onClick={() => setTab('personal')}>
              Proceed to Personal Details →
            </button>
          ) : (
            <button className="btn btn-primary" style={{padding: "11px 22px", fontSize: 14}} onClick={handleSaveAndContinue} disabled={busy}>{saving?'Saving…':(tab==='forms'?'💾 Finish & Save':'💾 Save & Continue →')}</button>
          )}
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={saveDraft}>Save Draft</button>
          {hasDraft && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => {
            if (!window.confirm('Discard this draft and any unsaved edits, then reload saved employee data?')) return;
            try { sessionStorage.removeItem(`${draftPrefix}${selId}`); sessionStorage.removeItem(`${draftPrefix}active`); }
            catch { setMsg('The draft could not be removed. Please try again.'); return; }
            setHasDraft(false); setReloadVersion(value => value + 1);
          }}>Discard Draft</button>}
          <button className="btn btn-ghost" disabled={busy} onClick={() => { if (confirmDiscard()) onBack(); }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE SELF SERVICE FORM
// ─────────────────────────────────────────────────────────────────────────────
export const hasRealProfileDraftChanges = (draftData, emp) => {
  if (!draftData || typeof draftData !== 'object' || !emp) return false;

  const getEmpVal = (k) => {
    if (k === 'date_of_birth' || k === 'dob') return emp.date_of_birth || emp.dob || '';
    if (k === 'bank_account_number' || k === 'bank_account') return emp.bank_account_number || emp.bank_account || '';
    if (k === 'pf_account_number' || k === 'pf_account') return emp.pf_account_number || emp.pf_account || '';
    if (k === 'marital_status') return emp.marital_status || emp.maritial_status || emp.maritalStatus || 'Single';
    if (k === 'gender') return emp.gender || 'Male';
    if (k === 'prev_pf' || k === 'prev_eps') return (emp[k] === 'Yes' || emp.uan) ? 'Yes' : (emp[k] || 'No');
    if (k === 'prev_esi') return (emp.prev_esi === 'Yes' || emp.esi_id) ? 'Yes' : (emp.prev_esi || 'No');
    if (['intl_worker', 'specially_abled'].includes(k)) return emp[k] || 'No';
    return emp[k] || '';
  };

  const getDraftVal = (k) => {
    if (k === 'date_of_birth' || k === 'dob') return draftData.date_of_birth || draftData.dob || '';
    if (k === 'bank_account_number' || k === 'bank_account') return draftData.bank_account_number || draftData.bank_account || '';
    if (k === 'pf_account_number' || k === 'pf_account') return draftData.pf_account_number || draftData.pf_account || '';
    if (k === 'marital_status') return draftData.marital_status || draftData.maritial_status || draftData.maritalStatus || 'Single';
    if (k === 'gender') return draftData.gender || 'Male';
    if (k === 'prev_pf' || k === 'prev_eps') return (draftData[k] === 'Yes' || draftData.uan) ? 'Yes' : (draftData[k] || 'No');
    if (k === 'prev_esi') return (draftData.prev_esi === 'Yes' || draftData.esi_id) ? 'Yes' : (draftData.prev_esi || 'No');
    if (['intl_worker', 'specially_abled'].includes(k)) return draftData[k] || 'No';
    return draftData[k] || '';
  };

  const keysToCheck = [
    'name', 'email', 'phone', 'gender', 'marital_status', 'date_of_birth',
    'father_name', 'spouse_name', 'blood_group', 'religion', 'specially_abled',
    'current_address_line1', 'current_address_city', 'current_address_state', 'current_address_pincode',
    'permanent_address_line1', 'permanent_address_city', 'permanent_address_state', 'permanent_address_pincode',
    'emergency_name', 'emergency_phone', 'emergency_relation',
    'pan', 'aadhaar', 'uan', 'pf_account_number', 'esi_id', 'dispensary', 'bank_name', 'bank_account_number', 'bank_ifsc', 'bank_branch',
    'prev_pf', 'prev_eps', 'prev_esi', 'prev_exit_date', 'scheme_cert', 'ppo_number',
    'intl_worker', 'country_of_origin', 'passport_number', 'passport_validity'
  ];

  for (const k of keysToCheck) {
    const vDraft = String(getDraftVal(k)).trim();
    const vEmp = String(getEmpVal(k)).trim();
    if (vDraft !== vEmp) return true;
  }

  // Complex arrays: nominees & family_members
  for (const complexKey of ['nominees', 'family_members']) {
    const dVal = draftData[complexKey];
    let eVal = emp[complexKey];
    if (typeof eVal === 'string') {
      try { eVal = JSON.parse(eVal); } catch (e) { eVal = []; }
    }
    const dArr = Array.isArray(dVal) ? dVal.filter(x => x && (x.name || x.nominee_name)) : [];
    const eArr = Array.isArray(eVal) ? eVal.filter(x => x && (x.name || x.nominee_name)) : [];
    if (dArr.length !== eArr.length) return true;
    for (let i = 0; i < dArr.length; i++) {
      const d = dArr[i] || {};
      const e = eArr[i] || {};
      if (
        String(d.name || d.nominee_name || '').trim() !== String(e.name || e.nominee_name || '').trim() ||
        String(d.relation || d.relationship || '').trim() !== String(e.relation || e.relationship || '').trim() ||
        String(d.dob || d.date_of_birth || '').trim() !== String(e.dob || e.date_of_birth || '').trim() ||
        String(d.share || '').trim() !== String(e.share || '').trim()
      ) {
        return true;
      }
    }
  }

  return false;
};

function EmployeeSelfServiceForm({ employee, pendingRequest = null, onSave, onClose }) {
  const [tab, setTab] = useState('checklist');
  const [downloadMsg, setDownloadMsg] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState({});
  const draftKey = employee?.id ? `trustwork_jk_draft_${employee.id}` : null;
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftNotice, setDraftNotice] = useState('');
  const tabsBarRef = useRef(null);

  useEffect(() => {
    if (tabsBarRef.current) {
      const activeBtn = tabsBarRef.current.querySelector('[data-active="true"]');
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [tab]);

  const [form, setForm] = useState(() => {
    const base = {
      dob:'', marital_status:'', religion:'', blood_group:'', phone:'', email:'',
      father_name:'', spouse_name:'', aadhaar_last4:'', specially_abled:'No', gender:'Male',
      current_address:'', permanent_address:'',
      emergency_name:'', emergency_phone:'', emergency_relation:'',
      uan:'', esi_id:'', pf_account_number:'', dispensary:'',
      bank_name:'', bank_account_number:'', bank_ifsc:'', bank_branch:'',
      prev_pf:'No', prev_eps:'No', prev_esi:'No', prev_exit_date:'', scheme_cert:'', ppo_number:'',
      intl_worker:'No', country_of_origin:'', passport_number:'', passport_validity:'',
      emp_photo:'', emp_aadhaar_doc:'', emp_aadhaar_front_doc:'', emp_aadhaar_back_doc:'', emp_pan_doc:'', emp_bank_doc:'',
      ...employee,
      prev_pf: (employee?.prev_pf === 'Yes' || employee?.uan) ? 'Yes' : (employee?.prev_pf || 'No'),
      prev_eps: (employee?.prev_pf === 'Yes' || employee?.uan) ? 'Yes' : (employee?.prev_eps || 'No'),
      prev_esi: (employee?.prev_esi === 'Yes' || employee?.esi_id) ? 'Yes' : (employee?.prev_esi || 'No'),
      pf_account_number: employee?.pf_account_number || employee?.pf_account || '',
      dispensary: employee?.dispensary || '',
      emp_photo: employee?.emp_photo || (employee?.id ? localStorage.getItem("tw_photo_" + employee.id) : "") || (employee?.employee_code ? localStorage.getItem("tw_photo_" + employee.employee_code) : "") || '',
      date_of_birth: employee?.date_of_birth || employee?.dob || '',
    };

    // If there is an active pending request submitted to HR, use submitted data and clear any stale draft
    if (pendingRequest?.requested_data && typeof pendingRequest.requested_data === "object") {
      if (draftKey) {
        try { localStorage.removeItem(draftKey); } catch (e) {}
      }
      return { ...base, ...pendingRequest.requested_data };
    }

    // Only restore draft if no request is pending AND draft has actual unsaved differences with the approved record
    if (draftKey) {
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed.data === "object") {
            const hasChanges = hasRealProfileDraftChanges(parsed.data, employee);
            if (hasChanges) {
              return { ...base, ...parsed.data };
            } else {
              // Stale draft that matches approved database record — clear it
              localStorage.removeItem(draftKey);
            }
          }
        }
      } catch (e) {
        console.warn("Could not load draft:", e);
      }
    }
    return base;
  });

  useEffect(() => {
    if (!draftKey) return;
    if (pendingRequest) {
      // Nothing in draft once submitted for HR review
      try { localStorage.removeItem(draftKey); } catch (e) {}
      setDraftSavedAt(null);
      setDraftNotice('');
      return;
    }
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.savedAt && parsed?.data) {
          const hasChanges = hasRealProfileDraftChanges(parsed.data, employee);
          if (hasChanges) {
            setDraftSavedAt(parsed.savedAt);
            setDraftNotice(`Restored your unsaved draft from ${new Date(parsed.savedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`);
          } else {
            // Already approved or no differences — purge stale draft
            localStorage.removeItem(draftKey);
            setDraftSavedAt(null);
            setDraftNotice('');
          }
        }
      }
    } catch (e) {}
  }, [draftKey, pendingRequest, employee]);

  const saveDraftToStorage = (formData, notify = true) => {
    if (!draftKey || pendingRequest) return;
    const toSave = formData || form;
    // Do not save or notify if there are no meaningful changes compared to employee profile
    if (!hasRealProfileDraftChanges(toSave, employee)) {
      if (notify) {
        setDraftNotice(`ℹ️ No changes made yet to save.`);
        setTimeout(() => setDraftNotice(''), 3000);
      }
      return;
    }
    try {
      const now = new Date().toISOString();
      localStorage.setItem(draftKey, JSON.stringify({ data: toSave, savedAt: now }));
      setDraftSavedAt(now);
      if (notify) {
        setDraftNotice(`✅ Draft saved locally at ${new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}. You can safely close or come back later.`);
        setTimeout(() => setDraftNotice(''), 4000);
      }
    } catch (e) {
      console.warn("Failed to save draft to localStorage:", e);
    }
  };

  const clearDraft = () => {
    if (draftKey) {
      try { localStorage.removeItem(draftKey); } catch (e) {}
    }
    setDraftSavedAt(null);
    setDraftNotice('');
  };

  const TABS = [
    {id:'checklist',label:'📌 Instructions & Checklist',shortLabel:'📌 Checklist'},
    {id:'personal',label:'👤 Personal Details',shortLabel:'👤 Personal'},
    {id:'job',label:'💼 Job & Official',shortLabel:'💼 Job'},
    {id:'kyc',label:'🏦 KYC & Bank',shortLabel:'🏦 KYC'},
    {id:'pf_esi',label:'📋 PF & ESI',shortLabel:'📋 PF/ESI'},
    {id:'nominees',label:'🤝 Family & Nominees',shortLabel:'🤝 Nominees'}
  ];

  const handleDownloadDocsZip = async () => {
    setDownloadMsg('Zipping documents…');
    try {
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();
      
      const cleanName = (employee?.name || form.name || 'Employee').trim().replace(/[^A-Za-z0-9]+/g, '-');
      const cleanCode = (employee?.employee_code || form.employee_code || 'EMP').trim().replace(/[^A-Za-z0-9]+/g, '-');
      const prefix = `${cleanName}_${cleanCode}`;
      
      const tasks = [];
      const addFile = async (urlOrB64, filename) => {
        if (!urlOrB64) return;
        if (typeof urlOrB64 === 'string' && urlOrB64.startsWith('data:')) {
          const matches = urlOrB64.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
          if (!matches || matches.length !== 3) return;
          let ext = 'jpg';
          if (matches[1].includes('pdf')) ext = 'pdf';
          else if (matches[1].includes('png')) ext = 'png';
          else if (matches[1].includes('webp')) ext = 'webp';
          zip.file(`${filename}.${ext}`, matches[2], {base64: true});
        } else if (typeof urlOrB64 === 'string') {
          try {
            const res = await fetch(urlOrB64);
            if (res.ok) {
              const blob = await res.blob();
              let ext = 'webp';
              if (urlOrB64.toLowerCase().endsWith('.pdf') || blob.type.includes('pdf')) ext = 'pdf';
              else if (urlOrB64.toLowerCase().endsWith('.png') || blob.type.includes('png')) ext = 'png';
              else if (urlOrB64.toLowerCase().endsWith('.jpg') || urlOrB64.toLowerCase().endsWith('.jpeg')) ext = 'jpg';
              zip.file(`${filename}.${ext}`, blob);
            }
          } catch(e) {}
        }
      };

      let count = 0;
      const countAdd = (urlOrB64, n) => { if (urlOrB64) { tasks.push(addFile(urlOrB64, n)); count++; } };
      countAdd(form.emp_photo, `${prefix}_Photo`);
      countAdd(form.emp_aadhaar_front_doc || form.emp_aadhaar_doc, `${prefix}_Aadhaar_Front`);
      countAdd(form.emp_aadhaar_back_doc, `${prefix}_Aadhaar_Back`);
      countAdd(form.emp_pan_doc, `${prefix}_PAN`);
      countAdd(form.emp_bank_doc, `${prefix}_Bank_Proof`);

      (form.family_members||[]).forEach((f, i) => {
        const rel = (f.relationship || `Member${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
        const fName = (f.name || `Fam${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
        countAdd(f.photo, `${prefix}_Family_${rel}_${fName}_Photo`);
        countAdd(f.aadhaar_front_doc || f.aadhaar_doc, `${prefix}_Family_${rel}_${fName}_Aadhaar_Front`);
        countAdd(f.aadhaar_back_doc, `${prefix}_Family_${rel}_${fName}_Aadhaar_Back`);
      });

      if (count === 0) {
        setDownloadMsg('⚠️ No uploaded documents found to download.');
        setTimeout(() => setDownloadMsg(''), 3000);
        return;
      }

      await Promise.all(tasks);
      const content = await zip.generateAsync({type: "blob"});
      saveAs(content, `${prefix}_Documents.zip`);
      setDownloadMsg(`✅ Downloaded (${count} files): ${prefix}_Documents.zip`);
    } catch (e) {
      setDownloadMsg('❌ ' + e.message);
    }
    setTimeout(() => setDownloadMsg(''), 3500);
  };

  const handleFieldChange = (k, val) => {
    let updates = { [k]: val };
    if (k === 'prev_pf') {
      updates.prev_eps = val;
      if (val === 'No') {
        updates.uan = '';
        updates.pf_account_number = '';
        updates.pf_account = '';
        updates.prev_pf_account = '';
        updates.prev_exit_date = '';
        updates.scheme_cert = '';
        updates.ppo_number = '';
      }
    }
    if (k === 'prev_esi') {
      if (val === 'No') {
        updates.esi_id = '';
        updates.dispensary = '';
      }
    }
    setForm(p => ({ ...p, ...updates }));
  };

  const setF = k => e => handleFieldChange(k, e.target.value);
  const set = k => v => handleFieldChange(k, v);

  const inp = (label, k, type='text', opts=[], hint=null) => (
    <div>
      <label className="form-label">{label}</label>
      {type==='select' ? (
        <select className="form-select" value={form[k]||''} onChange={setF(k)}>
          {opts.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      ) : type==='textarea' ? (
        <textarea className="form-textarea" style={{minHeight:80}} value={form[k]||''} onChange={setF(k)} />
      ) : (k === 'bank_name' || type === 'datalist') ? (
        <>
          <input
            type="text"
            list="popular_banks_list_emp"
            className="form-input"
            value={form[k]||''}
            onChange={e => set(k)(e.target.value)}
            placeholder="Type or select bank..."
          />
          <datalist id="popular_banks_list_emp">
            {POPULAR_INDIAN_BANKS.map(b => <option key={b} value={b} />)}
          </datalist>
        </>
      ) : k === 'aadhaar_last4' ? (
        <div style={{display:'flex', alignItems:'center', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'8px 12px', fontSize:13, transition:'all 0.2s ease'}}
             onFocus={e=>{e.currentTarget.style.borderColor='#2563eb';e.currentTarget.style.background='#fff';e.currentTarget.style.boxShadow='0 0 0 4px rgba(37,99,235,0.15)'}}
             onBlur={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.background='#f8fafc';e.currentTarget.style.boxShadow='none'}}
             onMouseEnter={e=>{if(document.activeElement.parentElement !== e.currentTarget) {e.currentTarget.style.borderColor='#94a3b8';e.currentTarget.style.background='#fff'}}}
             onMouseLeave={e=>{if(document.activeElement.parentElement !== e.currentTarget) {e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.background='#f8fafc'}}}
        >
          <span style={{color:'#94a3b8', marginRight:4, letterSpacing:1, whiteSpace:'nowrap'}}>XXXX-XXXX-</span>
          <input type={type} style={{border:'none', outline:'none', background:'transparent', width:'100%', fontSize:13, letterSpacing:1, color:'#1e293b'}} value={form[k]||''} onChange={e=>{
            let val = e.target.value.replace(/\D/g, '').slice(0,4);
            set(k)(val);
          }} placeholder="1234" />
        </div>
      ) : type === 'date' ? (
        <DatePickerDDMMYYYY
          value={form[k] || ''}
          onChange={val => set(k)(val)}
          style={{width: '100%', height: 38, background: '#f8fafc', borderRadius: 8, fontSize: 13, border: '1.5px solid #cbd5e1'}}
        />
      ) : (
        <input type={type} className="form-input" maxLength={(k==='name'||k==='father_name'||k==='emergency_name'||k==='spouse_name') ? 100 : undefined} value={form[k]||''} onChange={e => {
            let val = e.target.value;
            if (k === 'name' || k === 'father_name' || k === 'emergency_name' || k === 'spouse_name') val = val.slice(0, 100);
            if (k === 'phone' || k === 'emergency_phone') val = val.replace(/\D/g, '').slice(0, 10);
            if (k === 'pan') val = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0,10);
            if (k === 'bank_account' || k === 'bank_account_number') val = val.replace(/\D/g, '').slice(0, 18);
            if (k === 'bank_ifsc') {
              val = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
              if (val.length >= 4 && !form.bank_name) {
                const pfx = val.slice(0, 4);
                if (IFSC_BANK_PREFIX_MAP[pfx]) {
                  setForm(p => ({ ...p, bank_ifsc: val, bank_name: p.bank_name || IFSC_BANK_PREFIX_MAP[pfx] }));
                  return;
                }
              }
            }
            if (k === 'uan') val = val.replace(/\D/g, '').slice(0, 12);
            if (k === 'esi_id') val = val.replace(/\D/g, '').slice(0, 17);
            set(k)(val);
        }} placeholder={type==='text'?'...':''} />
      )}
      {k === 'bank_ifsc' && form[k] && (
        <div style={{marginTop: 4, fontSize: 11, fontWeight: 700, color: isValidIFSC(form[k]) ? '#166534' : '#dc2626'}}>
          {isValidIFSC(form[k]) ? '✅ Valid IFSC format' : '⚠️ Must be 11 characters (e.g. SBIN0001234, 5th char "0")'}
        </div>
      )}
      {hint && <div style={{marginTop:4,fontSize:11,color:'#64748b',lineHeight:1.35}}>{hint}</div>}
    </div>
  );

  const ro = (label, val, note=null) => {
    let displayVal = val;
    if (typeof displayVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(displayVal)) {
      displayVal = formatDateDDMMYYYY(displayVal);
    }
    return (
      <div>
        <label className="form-label">🔒 {label}</label>
        <div style={{width:'100%',border:'1.5px solid #e2e8f0',borderRadius:8,padding:'10px 14px',fontSize:13,background:'#f1f5f9',color:'#475569',fontWeight:600}}>{displayVal || "—"}</div>
        {note && <div style={{marginTop:4,fontSize:11,color:'#64748b',lineHeight:1.35}}>{note}</div>}
      </div>
    );
  };

  const uploadInp = (label, k, accept='image/*,.pdf') => (
    <div>
      <label className="form-label">{label}</label>
      <div style={{display:'flex', gap:4, alignItems:'center', width:'100%', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'8px 10px', overflow:'hidden', transition:'all 0.2s ease'}}
           onMouseEnter={e=>{e.currentTarget.style.borderColor='#94a3b8';e.currentTarget.style.background='#fff'}}
           onMouseLeave={e=>{e.currentTarget.style.borderColor='#cbd5e1';e.currentTarget.style.background='#f8fafc'}}
      >
        <input type="file" accept={accept} style={{width:'100%', fontSize:12}}
          disabled={uploadingDoc[k]}
          onChange={async ev=>{
            const file = ev.target.files[0];
            if(!file) return;
            setUploadingDoc(p => ({ ...p, [k]: true }));
            const empCode = employee?.employee_code || form.employee_code || employee?.id || form.id || 'EMP';
            const empName = employee?.name || form.name || 'Employee';
            if (k === 'emp_photo') {
              const res = await processEmployeePhoto(file);
              if (res.error) {
                alert(res.error);
                setUploadingDoc(p => ({ ...p, [k]: false }));
                return;
              }
              const fileId = getCleanFileIdentifier(empCode, empName, 'Photo');
              const r2Url = await uploadToR2(res.dataUrl, 'photos', fileId);
              if (r2Url) set('emp_photo')(r2Url);
              else alert("Failed to upload photo to storage. Please try again.");
              setUploadingDoc(p => ({ ...p, [k]: false }));
              return;
            }
            const res = await processEmployeeDocument(file);
            if (res.error) {
              alert(res.error);
              setUploadingDoc(p => ({ ...p, [k]: false }));
              return;
            }
            const docTypeMap = {
              emp_photo: 'Photo',
              emp_aadhaar_front_doc: 'Aadhaar_Front',
              emp_aadhaar_doc: 'Aadhaar_Front',
              emp_aadhaar_back_doc: 'Aadhaar_Back',
              emp_pan_doc: 'PAN',
              emp_bank_doc: 'Bank_Proof'
            };
            const docType = docTypeMap[k] || k.replace(/^emp_/, '').replace(/_doc$/, '');
            const fileId = getCleanFileIdentifier(empCode, empName, docType);
            const r2Url = await uploadToR2(res.dataUrl, 'documents', fileId);
            if (r2Url) {
              set(k)(r2Url);
              if (k === 'emp_aadhaar_front_doc') set('emp_aadhaar_doc')(r2Url);
            } else {
              alert("Failed to upload document to storage. Please try again.");
            }
            setUploadingDoc(p => ({ ...p, [k]: false }));
          }} />
          {uploadingDoc[k] ? (
            <div title="Uploading to storage..." style={{fontSize:14, flexShrink:0}}>⏳</div>
          ) : (
            (form[k] || (k === 'emp_aadhaar_front_doc' && form.emp_aadhaar_doc)) && (
              <a
                href={form[k] || form.emp_aadhaar_doc}
                target="_blank"
                rel="noreferrer noopener"
                title={k === 'emp_photo' ? "View photo in new tab" : "Uploaded to storage (click to view)"}
                style={{display:'inline-flex', alignItems:'center', textDecoration:'none', cursor:'pointer', flexShrink:0}}
              >
                {k === 'emp_photo' ? (
                  <img src={form[k]} alt="Photo" style={{width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #3b82f6'}} />
                ) : (
                  <span style={{fontSize: 16}}>✅</span>
                )}
              </a>
            )
          )}
      </div>
    </div>
  );

  const grid = (...children) => (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:14}}>{children}</div>
  );

  const listField = (listKey, cols, gridCols) => (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      {(form[listKey]||[]).map((row, i) => (
        <div key={i} style={{display:'grid',gridTemplateColumns:gridCols || `repeat(${cols.length},1fr) 36px`,gap:8,alignItems:'end',background:'#f8fafc',borderRadius:8,padding:'10px 12px',border:'1px solid #e2e8f0'}}>
          {cols.map(([k,label,type,opts,cond]) => {
            if (cond && !cond(row)) return <div key={k} />;
            return (
            <div key={k}>
              <label className="form-label" style={{marginBottom:3}}>{label}</label>
              {type==='select' ? (
                <select className="form-select" style={{padding:'8px 12px'}} value={row[k]||''} onChange={ev=>{
                  const nl = [...(form[listKey]||[])]; nl[i]={...nl[i],[k]:ev.target.value}; set(listKey)(nl);
                }}>{opts.map(o=><option key={o}>{o}</option>)}</select>
              ) : type==='datalist' && opts ? (
                <>
                  <input type="text" list={`emp_ss_${listKey}_${k}_${i}_list`} maxLength={k==='name'?100:undefined} className="form-input" style={{padding:'8px 12px'}} value={row[k]||''}
                    onChange={ev=>{
                      let val = ev.target.value;
                      if (k === 'name') val = val.slice(0, 100);
                      const nl=[...(form[listKey]||[])];
                      nl[i]={...nl[i],[k]:val};
                      if (listKey === 'nominees' && k === 'name') {
                        const fm = (form.family_members||[]).find(f => f.name === val);
                        if (fm) {
                          nl[i].relationship = fm.relationship || nl[i].relationship || '';
                          nl[i].dob = fm.dob || nl[i].dob || '';
                          if (fm.dob) {
                            const d = new Date(fm.dob); const t = new Date();
                            if (!isNaN(d.getTime())) {
                              let a = t.getFullYear() - d.getFullYear();
                              if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
                              nl[i].age = a >= 0 ? a : '';
                            }
                          }
                          const isResiding = String(fm.residing || '').toLowerCase() === 'yes';
                          if (isResiding) {
                            nl[i].address = [form.temp_address || form.perm_address || form.current_address, form.temp_city || form.perm_city, form.temp_state || form.perm_state, form.temp_pincode || form.perm_pincode].filter(Boolean).join(", ") || `${form.perm_address||''} ${form.perm_city||''}`.trim();
                          } else if (fm.address) {
                            nl[i].address = fm.address;
                          }
                          if ((nl.length === 1 || !nl[i].share) && nl.filter(x => x.share).length === 0) {
                            nl[i].share = '100';
                          }
                        }
                      }
                      set(listKey)(nl);
                    }} />
                  <datalist id={`emp_ss_${listKey}_${k}_${i}_list`}>{opts.map((o, idx)=><option key={idx} value={o}/>)}</datalist>
                </>
              ) : type==='file' ? (
                <div style={{display:'flex', gap:4, alignItems:'center', width:'100%', border:'1.5px solid #cbd5e1', borderRadius:8, background:'#f8fafc', padding:'4px 6px', overflow:'hidden'}}>
                  <input type="file" accept={opts?.accept || 'image/*,.pdf'} style={{width:'100%', fontSize:11}}
                    disabled={uploadingDoc[`${listKey}_${i}_${k}`]}
                    onChange={async ev=>{
                      const file = ev.target.files[0];
                      if(!file) return;
                      const uploadKey = `${listKey}_${i}_${k}`;
                      setUploadingDoc(p => ({ ...p, [uploadKey]: true }));
                      const empCode = employee?.employee_code || form.employee_code || employee?.id || form.id || 'EMP';
                      const empName = employee?.name || form.name || 'Employee';
                      const famRel = (row.relationship || `Member${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
                      const famName = (row.name || `Fam${i+1}`).trim().replace(/[^A-Za-z0-9]+/g, '-');
                      const docType = k === 'photo' ? 'Photo' : (k === 'aadhaar_back_doc' ? 'Aadhaar_Back' : 'Aadhaar_Front');
                      const fileId = getCleanFileIdentifier(empCode, empName, `Family_${famRel}_${famName}_${docType}`);
                      if (k === 'photo') {
                        const res = await processEmployeePhoto(file);
                        if (res.error) {
                          alert(res.error);
                          setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                          return;
                        }
                        const r2Url = await uploadToR2(res.dataUrl, 'photos', fileId);
                        if (r2Url) {
                          const updated = [...(form[listKey]||[])];
                          if (updated[i]) {
                            updated[i] = { ...updated[i], [k]: r2Url };
                            set(listKey)(updated);
                          }
                        } else {
                          alert("Failed to upload family photo to storage.");
                        }
                        setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                        return;
                      }
                      const res = await processEmployeeDocument(file);
                      if (res.error) {
                        alert(res.error);
                        setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                        return;
                      }
                      const r2Url = await uploadToR2(res.dataUrl, 'documents', fileId);
                      if (r2Url) {
                        const updated = [...(form[listKey]||[])];
                        if (updated[i]) {
                          updated[i] = { ...updated[i], [k]: r2Url };
                          set(listKey)(updated);
                        }
                      } else {
                        alert("Failed to upload family document to storage.");
                      }
                      setUploadingDoc(p => ({ ...p, [uploadKey]: false }));
                    }} />
                    {uploadingDoc[`${listKey}_${i}_${k}`] ? (
                      <div title="Uploading to storage..." style={{fontSize:14, flexShrink:0}}>⏳</div>
                    ) : (
                      row[k] && (
                        k === 'photo' ? (
                          <a href={row[k]} target="_blank" rel="noreferrer noopener" title="View family member photo in new tab">
                            <img src={row[k]} alt="Preview" title="Family Member Photo" style={{width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid #3b82f6', cursor: 'pointer'}} />
                          </a>
                        ) : (
                          <a href={row[k]} target="_blank" rel="noreferrer noopener" title="View uploaded document in new tab" style={{fontSize:14, flexShrink:0, textDecoration:'none', cursor:'pointer'}}>
                            ✅
                          </a>
                        )
                      )
                    )}
                </div>
              ) : type === 'date' || k === 'dob' ? (
                <div>
                  <DatePickerDDMMYYYY
                    value={row[k] || ''}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={val => {
                      const nl = [...(form[listKey] || [])];
                      nl[i] = { ...nl[i], [k]: val };
                      if (listKey === 'nominees' && k === 'dob') {
                        const a = calcAge(val);
                        nl[i].age = typeof a === 'number' && a >= 0 ? a : '';
                      }
                      set(listKey)(nl);
                    }}
                    style={{width: '100%', height: 34, background: '#fff', borderRadius: 8, fontSize: 12, border: '1.5px solid #cbd5e1'}}
                  />
                  {(() => {
                    if (!row[k]) return null;
                    const memberAge = calcAge(row[k]);
                    let warn = "";
                    if (typeof memberAge === 'number') {
                      if (memberAge < 0) {
                        warn = "⚠️ Future date";
                      } else if (form.dob && row.relationship) {
                        const rel = row.relationship.trim().toLowerCase();
                        const empAge = calcAge(form.dob);
                        if (rel === 'father' || rel === 'mother') {
                          if (typeof empAge === 'number' && memberAge - empAge < 14) {
                            warn = "⚠️ Must be ≥ 14y older";
                          }
                        } else if (rel === 'son' || rel === 'daughter') {
                          if (typeof empAge === 'number' && empAge - memberAge < 14) {
                            warn = "⚠️ Must be ≥ 14y younger";
                          }
                        } else if (rel === 'spouse') {
                          if (memberAge < 18) {
                            warn = "⚠️ Must be ≥ 18 yrs";
                          }
                        }
                      }
                    }
                    return (
                      <div style={{fontSize: 10, marginTop: 2, fontWeight: 700, color: warn ? '#dc2626' : '#2563eb', lineHeight: 1.2}}>
                        {warn ? warn : (typeof memberAge === 'number' && memberAge >= 0 ? `Age: ${memberAge} yrs` : '')}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <input type={k==='email'?'email':(k==='phone'?'tel':(type||'text'))} maxLength={k==='name'?100:undefined} className="form-input" style={{padding:'8px 12px'}} value={row[k]||''}
                  onChange={ev=>{
                    let val = ev.target.value;
                    if (k === 'name') val = val.slice(0, 100);
                    if (k === 'phone' || k === 'aadhaar_last4') val = val.replace(/\D/g, '').slice(0, k==='phone'?10:4);
                    const nl=[...(form[listKey]||[])];
                    nl[i]={...nl[i],[k]:val};
                    if (listKey === 'nominees' && k === 'dob') {
                      if (val) {
                        const d = new Date(val); const t = new Date();
                        if (!isNaN(d.getTime())) {
                          let a = t.getFullYear() - d.getFullYear();
                          if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--;
                          if (a >= 0) nl[i].age = a;
                        }
                      }
                    }
                    set(listKey)(nl);
                  }} />
              )}
            </div>
            );
          })}
          <button style={{height:32,background:'#fee2e2',border:'none',borderRadius:8,cursor:'pointer',color:'#dc2626',fontWeight:700}} onClick={()=>{
            set(listKey)((form[listKey]||[]).filter((_,j)=>j!==i));
          }}>×</button>
        </div>
      ))}
      <button style={{background:'none',border:'1.5px dashed #94a3b8',borderRadius:8,padding:'8px 14px',cursor:'pointer',color:'#475569',fontWeight:600,fontSize:13}} onClick={()=>{
        const cur = form[listKey] || [];
        const defaultShare = listKey === 'nominees' && cur.length === 0 ? '100' : '';
        set(listKey)([...cur, listKey === 'nominees' ? { share: defaultShare } : {}]);
      }}>+ Add Row</button>
    </div>
  );

  const renderTab = () => {
    switch(tab) {
      case 'checklist': return (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {/* Hero Banner with Integrated Privacy Notice */}
          <div className="jk-checklist-hero">
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:8, flexWrap:'wrap'}}>
              <span style={{fontSize:20}}>📋</span>
              <span className="jk-hero-title" style={{fontSize:17, fontWeight:800, color:'#0f172a', letterSpacing:0.2}}>
                Pre-Onboarding Softcopy Checklist
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                Required for PF & ESI
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#fef3c7', color:'#b45309', border:'1px solid #fde68a', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                📁 Max 4MB per file
              </span>
              <span style={{fontSize:10.5, fontWeight:800, background:'#ecfdf5', color:'#047857', border:'1px solid #a7f3d0', padding:'2px 8px', borderRadius:12, textTransform:'uppercase'}}>
                🔒 Strictly Confidential
              </span>
            </div>
            <div className="jk-hero-desc" style={{fontSize:13, color:'#475569', lineHeight:1.55}}>
              Before filling the Joining KIT, please ensure you have the following information and soft copies (PDF or clear JPG/PNG, maximum 4MB per file) ready on your device. This guarantees seamless employee onboarding and avoids statutory rejection.
            </div>

            {/* Privacy & Confidentiality Notice inside same section */}
            <div style={{marginTop:12, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'flex-start', gap:10}}>
              <div style={{fontSize:18, flexShrink:0, marginTop:1}}>🛡️</div>
              <div style={{fontSize:12, color:'#166534', lineHeight:1.5}}>
                <strong>Confidentiality & Privacy Notice:</strong> We make sure all submitted data is strictly confidential across both internal and external channels. Documents are accessed only for authorized payroll onboarding and statutory PF/ESI filing. Unmasked Aadhaar copies are <strong>purged immediately</strong> after real-time verification; only masked last 4 digits are retained.
              </div>
            </div>
          </div>

          {/* Checklist Items Grid - 3 items per row */}
          <div className="jk-checklist-grid">
            {/* 1. Photo */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">📷</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>1. Passport Size Photograph</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Clean, front-facing digital photo with clear lighting and neutral background.
                </div>
              </div>
            </div>

            {/* 2. Aadhaar Card */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">🪪</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>2. Aadhaar Card (Front & Back)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Soft copies of both Front Side (name, photo, DoB) and Back Side (address). <em>Purged post-verification</em>.
                </div>
              </div>
            </div>

            {/* 3. PAN Card */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">💳</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>3. PAN Card</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Legible soft copy with 10-digit PAN number and Father's Name clearly visible.
                </div>
              </div>
            </div>

            {/* 4. Bank Proof */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">🏦</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>4. Cancelled Cheque / Passbook</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Must clearly show: Account Holder Name, Bank Account Number, and IFSC Code.
                </div>
              </div>
            </div>

            {/* 5. Family Members */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">👨‍👩‍👧</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>5. Family Members (Spouse, Kids, Parents)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  For EPF Form 2 & ESI Pehchan: Dependents' Date of Birth (DoB), Photo, and Aadhaar copy.
                </div>
              </div>
            </div>

            {/* 6. Previous PF / ESI */}
            <div className="jk-checklist-card">
              <div className="jk-checklist-icon">📜</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="jk-item-title" style={{fontSize: 13.5, fontWeight: 800, color: '#0f172a'}}>6. Previous PF / UAN (If Applicable)</div>
                <div className="jk-item-desc" style={{fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5}}>
                  Previous UAN, PF member ID, date of exit, or Pension Payment Order (PPO).
                </div>
              </div>
            </div>
          </div>

          {/* Action to proceed */}
          <div className="jk-action-box" style={{background:'#f8fafc',border:'1.5px dashed #cbd5e1',borderRadius:12,padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
            <div>
              <div style={{fontWeight:800,fontSize:13.5,color:'#0f172a'}}>Ready to proceed?</div>
              <div style={{fontSize:12,color:'#64748b',marginTop:2}}>Once you have verified having these documents ready, proceed to Personal Information.</div>
            </div>
            <button
              className="btn btn-primary"
              style={{padding:'10px 22px',fontSize:13.5,fontWeight:700,display:'inline-flex',alignItems:'center',gap:8,cursor:'pointer'}}
              onClick={() => {
                setTab('personal');
                window.scrollTo({top:0,behavior:'smooth'});
              }}
            >
              🚀 I Have Softcopies Ready — Proceed →
            </button>
          </div>
        </div>
      );
      case 'personal': return (
        <div style={{display:'flex',flexDirection:'column',gap:20}}>
          <EmployeePhotoUploader
            value={form.emp_photo || ""}
            onChange={photo => set("emp_photo")(photo)}
            label="Profile Photograph"
            empCode={employee?.employee_code || form.employee_code || employee?.id || form.id}
            empName={employee?.name || form.name}
          />
          {grid(
            inp('Date of Birth','date_of_birth','date'), 
            inp('Gender','gender','select',['Male','Female','Other']), 
            inp('Marital Status','marital_status','select',['Single','Married','Divorced','Widowed']), 
            inp('Father\'s Name','father_name')
          )}
          {grid(
            ...(form.marital_status && form.marital_status !== 'Single' ? [
              inp('Spouse Name (Optional)','spouse_name','text',[],'💡 Optional: Fill only if applicable')
            ] : []),
            <ReligionField key="religion" value={form.religion||''} onChange={val => setForm(p => ({...p, religion: val}))} />,
            inp('Blood Group','blood_group','select',['','A+','A-','B+','B-','O+','O-','AB+','AB-']), 
            inp('Specially Abled','specially_abled','select',['No','Yes'])
          )}
          {grid(
            inp('Aadhaar (Last 4)','aadhaar_last4','text'), 
            inp('Mobile *','phone','text',[],'💡 Primary 10-digit mobile (linked to Aadhaar & EPFO — do not change)'), 
            inp('Email','email')
          )}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTop:'1px solid #e2e8f0'}}>
            <div style={{fontWeight:700,fontSize:13,color:'#334155'}}>Address</div>
          </div>
          {grid(inp('Present Address','temp_address'), inp('City','temp_city'), inp('State','temp_state'), inp('PIN','temp_pincode'))}
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:-4,marginBottom:-4}}>
            <input type="checkbox" id="same_addr_emp" onChange={(e)=>{
              if(e.target.checked){
                setForm(p=>({...p, perm_address:p.temp_address, perm_city:p.temp_city, perm_state:p.temp_state, perm_pincode:p.temp_pincode}));
              } else {
                setForm(p=>({...p, perm_address:'', perm_city:'', perm_state:'', perm_pincode:''}));
              }
            }} />
            <label htmlFor="same_addr_emp" style={{fontSize:13,color:'#64748b',cursor:'pointer',userSelect:'none'}}>Same as Present Address</label>
          </div>
          {grid(inp('Permanent Address','perm_address'), inp('City','perm_city'), inp('State','perm_state'), inp('PIN','perm_pincode'))}
          <div>
            <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:12}}>Emergency Contact</div>
            {grid(inp('Contact Name','emergency_name'), inp('Relationship','emergency_relation'), inp('Phone Number','emergency_phone'))}
          </div>
        </div>
      );
      case 'job': return (
        <div style={{display:'flex',flexDirection:'column',gap:20}}>
          {/* Official HR Allocation Banner */}
          <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'flex-start',gap:10}}>
            <div style={{fontSize:18,flexShrink:0,marginTop:1}}>ℹ️</div>
            <div style={{fontSize:12.5,color:'#1e40af',lineHeight:1.5}}>
              <strong>Official Employment Details:</strong> Designation, Department, Gross Salary, and Date of Induction are filled and configured by your HR department. Date of Joining (DOJ) is your official joining record.
            </div>
          </div>

          {grid(
            ro("Employee Name", employee?.name || form.name),
            ro("Employee ID", employee?.employee_code || form.employee_code),
            ro("PAN Number", employee?.pan || form.pan)
          )}
          {grid(
            (employee?.date_of_joining || employee?.doj || form.doj)
              ? ro("Date of Joining (DOJ) *", employee?.date_of_joining || employee?.doj || form.doj, "Official date of joining")
              : inp("Date of Joining (DOJ) *", "doj", "date", [], "Mandatory official date of joining"),
            ro("HR Induction Date", employee?.hr_induction_date || form.hr_induction_date || employee?.date_of_joining || employee?.doj || form.doj, "Set by HR"),
            ro("Department", employee?.department || form.department, "Set by HR")
          )}
          {grid(
            ro("Designation", employee?.designation || form.designation, "Set by HR"),
            ro("Gross Monthly Salary", (employee?.gross_salary ? `₹${Math.round(Number(employee.gross_salary) || 0).toLocaleString('en-IN')}` : (form.basic_salary ? `₹${Math.round(Number(form.basic_salary) || 0).toLocaleString('en-IN')}` : (employee?.gross ? `₹${Math.round(Number(employee.gross) || 0).toLocaleString('en-IN')}` : null))), "Set by HR in compensation structure"),
            ro("Employment Status", employee?.employment_status || form.employment_status || "Active", "Managed by HR")
          )}
          
          {/* International Worker (IW) Section */}
          <div style={{background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'18px 20px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:6}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>🌍</span>
                <span style={{fontSize:13,fontWeight:800,color:'#1e293b'}}>International Worker (IW) Status</span>
              </div>
              <span style={{fontSize:11,fontWeight:700,background:'#eff6ff',color:'#1d4ed8',padding:'2px 8px',borderRadius:12}}>
                EPFO Statutory Field
              </span>
            </div>

            <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#1e40af',lineHeight:1.5}}>
              💡 <strong>Fill only if you are an International Worker:</strong> Expatriates holding a foreign passport or Indian staff under a bilateral Social Security Agreement (SSA). <strong>For regular domestic Indian employees, keep this set to "No".</strong>
            </div>

            {grid(
              inp(
                'Are you an International Worker?',
                'intl_worker',
                'select',
                ['No','Yes'],
                'Keep "No" unless you hold a foreign passport or SSA certificate.'
              ),
              ...(form.intl_worker === 'Yes' ? [
                inp('Country of Origin *','country_of_origin','text',[],'Country that issued your foreign passport'),
                inp('Passport Number *','passport_number','text',[],'Your valid foreign passport number'),
                inp('Passport Validity *','passport_validity','date',[],'Passport expiration date')
              ] : [])
            )}

            {form.intl_worker !== 'Yes' && (
              <div style={{marginTop:10,fontSize:11.5,color:'#166534',fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                <span>✨</span>
                <span>Marked as domestic Indian employee. No foreign passport details required.</span>
              </div>
            )}
          </div>
        </div>
      );
      case 'kyc': return (
        <div style={{display:'flex',flexDirection:'column',gap:24}}>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:12}}>Bank Details</div>
            {grid(
              inp('Bank Name','bank_name','datalist',[],'💡 Select from list or type custom bank name'), 
              inp('Account Number','bank_account_number','text',[],form.bank_account_number ? (isValidBankAccount(form.bank_account_number) ? `✅ ${form.bank_account_number.length} digits (Valid length)` : `⚠️ ${form.bank_account_number.length} digits (Indian accounts must be 9–18 digits)`) : '💡 9 to 18 numeric digits (e.g. 123456789012)'), 
              inp('IFSC Code','bank_ifsc','text',[],form.bank_ifsc ? (isValidIFSC(form.bank_ifsc) ? '✅ Valid IFSC format' : '⚠️ Must be 11 characters (e.g. SBIN0001234)') : '💡 11-character code e.g. HDFC0001234'), 
              inp('Branch Name','bank_branch')
            )}
          </div>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:12}}>Document Uploads</div>

            {grid(
              uploadInp('Employee Photo','emp_photo','image/*'), 
              uploadInp('Aadhaar Card (Front Side)','emp_aadhaar_front_doc','image/*,.pdf'), 
              uploadInp('Aadhaar Card (Back Side)','emp_aadhaar_back_doc','image/*,.pdf'), 
              uploadInp('PAN Document','emp_pan_doc','image/*,.pdf'), 
              uploadInp('Cancelled Cheque/Passbook','emp_bank_doc','image/*,.pdf')
            )}
          </div>
        </div>
      );
      case 'pf_esi': return (
        <div style={{display:'flex',flexDirection:'column',gap:20}}>
          {/* Section 1: Provident Fund (EPFO) */}
          <div style={{background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'20px 22px',boxShadow:'0 1px 3px rgba(0,0,0,0.03)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>🏛️</span>
                <span style={{fontWeight:800,fontSize:14,color:'#0f172a'}}>Employees' Provident Fund (EPF & EPS)</span>
              </div>
            </div>

            {grid(
              inp(
                'Do you have an existing PF Account / UAN? *', 
                'prev_pf', 
                'select', 
                ['No','Yes'], 
                '💡 Select "Yes" if you already have an existing PF account or were previously enrolled under EPFO.'
              )
            )}

            {form.prev_pf === 'Yes' ? (
              <div style={{marginTop:14}}>
                {grid(
                  inp(
                    'Universal Account Number (UAN)', 
                    'uan', 
                    'text', 
                    [], 
                    form.uan ? (
                      isValidUAN(form.uan) 
                        ? '✅ 12 digits (Valid UAN format)' 
                        : `⚠️ ${form.uan.length}/12 digits (Must be exactly 12 digits if provided)`
                    ) : '💡 Don’t know? Leave blank — we will fetch it from your mobile number. UAN is lifelong & cannot be duplicated.'
                  ),
                  inp(
                    'PF Account Number (Member ID)', 
                    'pf_account_number', 
                    'text', 
                    [], 
                    '💡 Company-assigned Member ID (e.g. PYKRP/12345/678). Auto-generated by employer during monthly filing if new.'
                  )
                )}
              </div>
            ) : null}
          </div>

          {/* Section 2: Employee State Insurance (ESIC) */}
          <div style={{background:'#ffffff',border:'1.5px solid #e2e8f0',borderRadius:12,padding:'20px 22px',boxShadow:'0 1px 3px rgba(0,0,0,0.03)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,paddingBottom:10,borderBottom:'1px solid #f1f5f9'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>🏥</span>
                <div>
                  <div style={{fontWeight:800,fontSize:14,color:'#0f172a'}}>Employee State Insurance (ESIC)</div>
                  <div style={{fontSize:11.5,color:'#64748b',marginTop:2}}>Medical insurance coverage under ESI Act (applicable when Basic + DA ≤ ₹21,000/month).</div>
                </div>
              </div>
            </div>

            {grid(
              inp(
                'Do you have an existing ESIC / IP Number? *', 
                'prev_esi', 
                'select', 
                ['No','Yes'], 
                '💡 Select "Yes" if you already have an existing 17-digit ESIC Insured Person (IP) number.'
              )
            )}

            {form.prev_esi === 'Yes' ? (
              <div style={{marginTop:14}}>
                {grid(
                  inp(
                    'ESI Insurance No. (IP Number)', 
                    'esi_id', 
                    'text', 
                    [], 
                    form.esi_id ? (
                      form.esi_id.length === 17 
                        ? '✅ 17 digits (Valid ESIC IP format)' 
                        : `⚠️ ${form.esi_id.length}/17 digits (Must be exactly 17 digits if provided)`
                    ) : '💡 17-digit Insured Person (IP) number from previous employment.'
                  ),
                  inp(
                    'Dispensary / Hospital', 
                    'dispensary', 
                    'text', 
                    [], 
                    '💡 Preferred ESIC branch dispensary or clinic near employee residence for primary outpatient medical care.'
                  )
                )}
              </div>
            ) : null}
          </div>
        </div>
      );
      case 'nominees': return (
        <div style={{display:'flex',flexDirection:'column',gap:24}}>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div style={{fontSize:14,fontWeight:700,color:'#475569'}}>👨‍👩‍👧 Family Members</div>
            <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,padding:'10px 14px',fontSize:12.5,color:'#1e40af',lineHeight:1.5}}>
              💡 <strong>Fill only if you are eligible for ESI:</strong> Family details are required for ESIC medical benefit enrollment if your <strong>Basic + DA is less than or equal to ₹21,000/month</strong>. Otherwise, you can leave this section blank. If you’re unsure, check with your HR/Admin.
            </div>
            <div style={{fontSize:13,color:'#64748b'}}>Add spouse, children, parents etc. (used in Form 2 EPS nomination & ESI Form) — Fill only if you are eligible for ESI (Basic + DA is less than or equal to ₹21,000/month; otherwise you can leave this section blank).</div>
            {listField('family_members',[
              ['name','Name'],
              ['relationship','Rel.','datalist',['Father','Mother','Spouse','Son','Daughter','Brother','Sister','Other']],
              ['dob','Date of Birth','date'],
              ['residing','Residing?','select',['Yes','No']],
              ['address','Address','text',null,(r)=>r?.residing==='No'],
              ['photo','Photo','file',{accept:'image/*'}],
              ['aadhaar_doc','Aadhaar (Front)','file',{accept:'image/*,.pdf'}],
              ['aadhaar_back_doc','Aadhaar (Back)','file',{accept:'image/*,.pdf'}],
            ], 'minmax(120px, 1.5fr) minmax(90px, 1fr) minmax(110px, 1fr) minmax(80px, 0.8fr) minmax(120px, 1.5fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) 36px')}
          </div>

          <div style={{borderTop:'1.5px solid #e2e8f0',margin:'8px 0'}}></div>

          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div style={{fontSize:14,fontWeight:700,color:'#475569'}}>🤝 Nominees</div>
            <div style={{fontSize:13,color:'#64748b'}}>Add legal nominees for EPF (Form 2) and Gratuity (Form F). Shares across all nominees should total 100%.</div>
            
            {(form.family_members||[]).length > 0 && (
              <div style={{marginBottom:10, display:'flex', gap:8, alignItems:'center', background:'#f8fafc', padding:'10px 14px', borderRadius:8, border:'1.5px dashed #cbd5e1'}}>
                <span style={{fontSize:13, fontWeight:700, color:'#334155'}}>🪄 Quick Add from Family:</span>
                <select style={{border:'1.5px solid #cbd5e1',borderRadius:6,padding:'6px 10px',fontSize:13, flex:1, maxWidth:300}} onChange={(e) => {
                  const idx = e.target.value;
                  if(idx !== "") {
                    const fm = form.family_members[idx];
                    let age = '';
                    if (fm.dob) {
                      const d = new Date(fm.dob); const t = new Date();
                      if (!isNaN(d.getTime())) {
                        age = t.getFullYear() - d.getFullYear();
                        if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) age--;
                        if (age < 0) age = '';
                      }
                    }
                    let address = '';
                    const isResiding = String(fm.residing || '').toLowerCase() === 'yes';
                    if (isResiding) {
                      address = [form.temp_address || form.perm_address || form.current_address, form.temp_city || form.perm_city, form.temp_state || form.perm_state, form.temp_pincode || form.perm_pincode].filter(Boolean).join(", ") || `${form.perm_address||''} ${form.perm_city||''}`.trim();
                    } else if (fm.address) {
                      address = fm.address;
                    }
                    
                    setForm(p => {
                      const curNominees = p.nominees || [];
                      const defaultShare = curNominees.length === 0 || !curNominees.some(n => n.share) ? '100' : '';
                      return {
                        ...p,
                        nominees: [...curNominees, {
                          name: fm.name || '',
                          relationship: fm.relationship || '',
                          dob: fm.dob || '',
                          age: age,
                          address: address,
                          share: defaultShare
                        }]
                      };
                    });
                    e.target.value = "";
                  }
                }}>
                  <option value="">-- Select a family member --</option>
                  {form.family_members.map((f, i) => (
                    f.name ? <option key={i} value={i}>{f.name} ({f.relationship || 'Unknown'})</option> : null
                  ))}
                </select>
              </div>
            )}

            {listField('nominees',[
              ['name','Name'],
              ['relationship','Relationship','datalist',['Father','Mother','Spouse','Son','Daughter','Brother','Sister','Other']],
              ['dob','Date of Birth','date'],
              ['age','Age'],
              ['address','Address'],
              ['share','Share %'],
            ], '1.5fr 1fr 1fr 0.5fr 1.5fr 0.8fr 36px')}
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="employee-self-service-form" style={{background:"#fff",borderRadius:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:"1px solid #e2e8f0",fontFamily:"'Inter',sans-serif",display:'flex',flexDirection:'column'}}>
      {/* Draft Notification / Recovery Bar */}
      {draftNotice && (
        <div style={{
          background: "#f0fdf4",
          borderBottom: "1.5px solid #bbf7d0",
          padding: "10px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          fontSize: 12.5,
          color: "#166534",
          fontWeight: 600,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16
        }}>
          <div style={{display: "flex", alignItems: "center", gap: 8}}>
            <span style={{fontSize: 14}}>💾</span>
            <span>{draftNotice}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Clear your saved in-progress draft and reset to current profile?")) {
                clearDraft();
                setForm({
                  dob:'', marital_status:'', religion:'', blood_group:'', phone:'', email:'',
                  father_name:'', spouse_name:'', aadhaar_last4:'', specially_abled:'No', gender:'Male',
                  current_address:'', permanent_address:'',
                  emergency_name:'', emergency_phone:'', emergency_relation:'',
                  uan:'', esi_id:'', pf_account_number:'', dispensary:'',
                  bank_name:'', bank_account_number:'', bank_ifsc:'', bank_branch:'',
                  prev_pf:'No', prev_eps:'No', prev_esi:'No', prev_exit_date:'', scheme_cert:'', ppo_number:'',
                  intl_worker:'No', country_of_origin:'', passport_number:'', passport_validity:'',
                  emp_photo:'', emp_aadhaar_doc:'', emp_aadhaar_front_doc:'', emp_aadhaar_back_doc:'', emp_pan_doc:'', emp_bank_doc:'',
                  ...employee,
                  prev_pf: (employee?.prev_pf === 'Yes' || employee?.uan) ? 'Yes' : (employee?.prev_pf || 'No'),
                  prev_eps: (employee?.prev_pf === 'Yes' || employee?.uan) ? 'Yes' : (employee?.prev_eps || 'No'),
                  prev_esi: (employee?.prev_esi === 'Yes' || employee?.esi_id) ? 'Yes' : (employee?.prev_esi || 'No'),
                  pf_account_number: employee?.pf_account_number || employee?.pf_account || '',
                  dispensary: employee?.dispensary || '',
                  emp_photo: employee?.emp_photo || (employee?.id ? localStorage.getItem("tw_photo_" + employee.id) : "") || (employee?.employee_code ? localStorage.getItem("tw_photo_" + employee.employee_code) : "") || '',
                  date_of_birth: employee?.date_of_birth || employee?.dob || '',
                });
                setDraftNotice("Draft cleared.");
                setTimeout(() => setDraftNotice(""), 3000);
              }
            }}
            style={{background: "none", border: "none", color: "#b91c1c", fontSize: 11.5, cursor: "pointer", textDecoration: "underline", fontWeight: 700}}
          >
            Clear Draft
          </button>
        </div>
      )}

      {/* Mobile Stepper & Progress Indicator */}
      <div className="jk-mobile-stepper" style={{padding:'10px 16px 4px',background:'#fff',borderBottom:'1px solid #f1f5f9'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:5}}>
          <span style={{fontSize:12,fontWeight:800,color:'#1e293b'}}>
            Step {TABS.findIndex(t=>t.id===tab)+1} of {TABS.length}: <span style={{color:'#2563eb'}}>{TABS.find(t=>t.id===tab)?.shortLabel || TABS.find(t=>t.id===tab)?.label}</span>
          </span>
          <span style={{fontSize:11.5,fontWeight:700,color:'#64748b'}}>
            {Math.round(((TABS.findIndex(t=>t.id===tab)+1)/TABS.length)*100)}%
          </span>
        </div>
        <div style={{height:4,background:'#e2e8f0',borderRadius:3,overflow:'hidden'}}>
          <div style={{
            height:'100%',
            width:`${Math.round(((TABS.findIndex(t=>t.id===tab)+1)/TABS.length)*100)}%`,
            background:'linear-gradient(90deg, #3b82f6, #2563eb)',
            borderRadius:3,
            transition:'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }} />
        </div>
      </div>

      {/* Tabs Bar */}
      <div 
        ref={tabsBarRef} 
        className="jk-tabs-scroll"
        style={{
          display:'flex',
          gap:6,
          padding:'8px 16px 10px',
          borderBottom:'1.5px solid #e2e8f0',
          overflowX:'auto',
          WebkitOverflowScrolling:'touch',
          scrollbarWidth:'thin',
          scrollBehavior:'smooth'
        }}
      >
        {TABS.map((t, idx) => {
          const isActive = tab === t.id;
          return (
            <button 
              key={t.id} 
              data-active={isActive ? "true" : "false"}
              onClick={() => setTab(t.id)} 
              style={{
                display:'inline-flex',
                alignItems:'center',
                gap:6,
                padding: isActive ? '7px 14px' : '7px 11px',
                borderRadius:20,
                border: isActive ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                background: isActive ? '#eff6ff' : '#ffffff',
                color: isActive ? '#1d4ed8' : '#475569',
                cursor:'pointer',
                fontSize:12.5,
                fontWeight: isActive ? 800 : 600,
                whiteSpace:'nowrap',
                flexShrink:0,
                transition:'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isActive ? '0 2px 6px rgba(37,99,235,0.18)' : '0 1px 2px rgba(0,0,0,0.03)'
              }}
            >
              <span style={{
                width: 17,
                height: 17,
                borderRadius: '50%',
                background: isActive ? '#2563eb' : '#e2e8f0',
                color: isActive ? '#ffffff' : '#64748b',
                fontSize: 10,
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {idx + 1}
              </span>
              <span className="jk-tab-text-full">{t.label}</span>
              <span className="jk-tab-text-short">{t.shortLabel}</span>
            </button>
          );
        })}
      </div>
      
      <div className="jk-content-body">
        <div style={{padding:'12px 16px 0',fontSize:12,color:'#64748b'}}><span style={{color:'#dc2626',fontWeight:700}}>*</span> indicates mandatory fields.</div>
        {renderTab()}
      </div>

      {/* Footer with Persistent Save & Actions */}
      <div className="jk-footer employee-profile-update-footer">
        {/* Left: Save Draft or Pending Review status */}
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          {pendingRequest ? (
            <span aria-label="Pending HR Review" title="Pending HR Review" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 700
            }}>
              <span>⏳</span>
              <span className="mobile-control-label">Pending HR Review</span>
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => saveDraftToStorage(form, true)}
                style={{
                  background: "#fff",
                  color: "#2563eb",
                  border: "1.5px solid #bfdbfe",
                  borderRadius: 8,
                  padding: "9px 18px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                }}
                aria-label="Save draft"
                title={draftSavedAt ? `Last saved ${new Date(draftSavedAt).toLocaleTimeString()}` : 'Save your progress locally so you can close and resume anytime'}
              >
                <span aria-hidden="true">{draftSavedAt ? "✓" : "💾"}</span>
                <span className="mobile-control-label">Save Draft</span>
              </button>
              {draftSavedAt && (
                <span className="employee-draft-saved" style={{fontSize: 11.5, color: "#166534", fontWeight: 600, display: "flex", alignItems: "center", gap: 4}}>
                  <span>✅</span>
                  <span>Saved ({new Date(draftSavedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })})</span>
                </span>
              )}
            </>
          )}
        </div>

        {/* Right: Tab Navigation & Final Submission */}
        <div style={{display: "flex", alignItems: "center", gap: 12}}>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              style={{fontWeight: 700, fontSize: 13, color: "#64748b"}}
            aria-label="Cancel and close profile update"
            >
              <span className="mobile-control-icon" aria-hidden="true">✕</span><span className="mobile-control-label">Cancel & Close</span>
            </button>
          )}
          {tab !== 'checklist' && (
            <button
              type="button"
              onClick={() => {
                const idx = TABS.findIndex(t => t.id === tab);
                if (idx > 0) setTab(TABS[idx - 1].id);
              }}
              className="btn btn-ghost"
              style={{fontWeight: 700, fontSize: 13}}
              aria-label="Previous profile section"
            >
              <span className="mobile-control-icon" aria-hidden="true">←</span><span className="mobile-control-label">Previous</span>
            </button>
          )}

          {tab !== 'nominees' ? (
            <button
              type="button"
              aria-label="Continue to next profile section" title="Continue to next section"
              onClick={() => {
                const idx = TABS.findIndex(t => t.id === tab);
                if (idx < TABS.length - 1) {
                  setTab(TABS[idx + 1].id);
                  window.scrollTo({top:0,behavior:'smooth'});
                }
              }}
              className="btn btn-primary"
              style={{
                padding: "10px 22px",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6
              }}
            >
              <span className="mobile-control-label">{tab === 'checklist' ? 'Proceed to Personal Details ' : 'Continue'}</span><span aria-hidden="true">→</span>
            </button>
          ) : (
            <button
              className="btn btn-primary employee-profile-submit"
              aria-label={pendingRequest ? "Update submitted request" : "Submit profile for approval"}
              onClick={() => {
                const accNo = form.bank_account_number || form.bank_account;
                if (accNo && !isValidBankAccount(accNo)) {
                  alert("Invalid Bank Account Number. Indian bank accounts must be between 9 and 18 digits.");
                  return;
                }
                if (form.uan && !isValidUAN(form.uan)) {
                  alert("Invalid UAN. Universal Account Number must be exactly 12 numeric digits, or leave it blank if unknown.");
                  return;
                }
                if (form.father_name && form.father_name.trim().length > 100) {
                  alert("Father's Name cannot exceed 100 characters.");
                  return;
                }
                if (form.spouse_name && form.spouse_name.trim().length > 100) {
                  alert("Spouse's Name cannot exceed 100 characters.");
                  return;
                }
                if (form.emergency_name && form.emergency_name.trim().length > 100) {
                  alert("Emergency Contact Name cannot exceed 100 characters.");
                  return;
                }
                const fams = (form.family_members || []).filter(f => f && (f.name?.trim() || f.relationship?.trim() || f.dob || f.photo || f.aadhaar_doc || f.aadhaar_front_doc));
                const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
                const empDobDate = (form.dob || form.date_of_birth) ? parseDateFlexible(form.dob || form.date_of_birth) : null;
                for (let i = 0; i < fams.length; i++) {
                  const f = fams[i];
                  const rowNum = i + 1;
                  const dispName = f.name?.trim() ? `"${f.name.trim()}"` : `Member #${rowNum}`;
                  if (!f.name?.trim()) {
                    alert(`Family member #${rowNum}: Name is required.`);
                    return;
                  }
                  if (f.name.trim().length > 100) {
                    alert(`Family member ${dispName}: Name cannot exceed 100 characters.`);
                    return;
                  }
                  if (!f.relationship?.trim()) {
                    alert(`Family member ${dispName}: Relationship is required.`);
                    return;
                  }
                  if (!f.dob) {
                    alert(`Family member ${dispName}: Date of Birth (DOB) is required.`);
                    return;
                  }
                  const itemDob = parseDateFlexible(f.dob);
                  if (!itemDob) {
                    alert(`Family member ${dispName}: Invalid Date of Birth.`);
                    return;
                  }
                  if (itemDob > todayEnd) {
                    alert(`Family member ${dispName}: Date of Birth cannot be in the future.`);
                    return;
                  }
                  if (!f.photo || (!f.aadhaar_doc && !f.aadhaar_front_doc)) {
                    alert(`Family member ${dispName}: Photo and Aadhaar Document are mandatory.`);
                    return;
                  }
                  if (empDobDate) {
                    const rel = f.relationship.trim().toLowerCase();
                    const empAge = calcAge(form.dob || form.date_of_birth);
                    const memberAge = calcAge(f.dob);
                    if (rel === 'father' || rel === 'mother') {
                      if (itemDob >= empDobDate) {
                        alert(`Family member ${dispName} (${f.relationship}) must be older than the employee.`);
                        return;
                      }
                      if (typeof memberAge === 'number' && typeof empAge === 'number' && (memberAge - empAge < 14)) {
                        alert(`Family member ${dispName} (${f.relationship}) must be at least 14 years older than the employee.`);
                        return;
                      }
                    }
                    if (rel === 'son' || rel === 'daughter') {
                      if (itemDob <= empDobDate) {
                        alert(`Family member ${dispName} (${f.relationship}) must be younger than the employee.`);
                        return;
                      }
                      if (typeof memberAge === 'number' && typeof empAge === 'number' && (empAge - memberAge < 14)) {
                        alert(`Employee must be at least 14 years older than their ${f.relationship.toLowerCase()} (${dispName}).`);
                        return;
                      }
                    }
                    if (rel === 'spouse') {
                      if (typeof memberAge === 'number' && memberAge < 18) {
                        alert(`Spouse ${dispName} must be at least 18 years of age.`);
                        return;
                      }
                    }
                  }
                }
                const noms = form.nominees || [];
                let totalShare = 0;
                for (let i = 0; i < noms.length; i++) {
                  const n = noms[i];
                  const rowNum = i + 1;
                  const dispName = n.name?.trim() ? `"${n.name.trim()}"` : `Nominee #${rowNum}`;
                  if (!n.name?.trim()) {
                    alert(`Nominee #${rowNum}: Name is required.`);
                    return;
                  }
                  if (n.name.trim().length > 100) {
                    alert(`Nominee ${dispName}: Name cannot exceed 100 characters.`);
                    return;
                  }
                  if (!n.relationship?.trim()) {
                    alert(`Nominee ${dispName}: Relationship is required.`);
                    return;
                  }
                  if (n.dob) {
                    const itemDob = parseDateFlexible(n.dob);
                    if (!itemDob) {
                      alert(`Nominee ${dispName}: Invalid Date of Birth.`);
                      return;
                    }
                    if (itemDob > todayEnd) {
                      alert(`Nominee ${dispName}: Date of Birth cannot be in the future.`);
                      return;
                    }
                  }
                  if (n.share !== undefined && n.share !== '') {
                    const s = parseFloat(n.share);
                    if (isNaN(s) || s <= 0 || s > 100) {
                      alert(`Nominee ${dispName}: Share % must be between 1 and 100.`);
                      return;
                    }
                    totalShare += s;
                  }
                }
                if (noms.length > 0 && Math.round(totalShare) !== 100) {
                  alert(`Total nominee share must equal 100% (currently ${totalShare}%).`);
                  return;
                }
                clearDraft();
                if (draftKey) {
                  try { localStorage.removeItem(draftKey); } catch (e) {}
                }
                onSave(form);
              }}
              style={{padding: "10px 24px", fontSize: 14, fontWeight: 800}}
            >
              <span className="desktop-submit-label">{pendingRequest ? "📝 Update Submitted Request" : "📝 Submit Profile for Approval"}</span>
              <span className="mobile-submit-label">{pendingRequest ? "Update request" : "Submit for approval"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── UNIFIED LEAVE CALCULATION HELPER
// ─────────────────────────────────────────────────────────────────────────────
export const getEmployeeLeaveMetrics = (emp, companyConfig, leavesList = [], leaveTypeName = null, targetYear = new Date().getFullYear(), excludeLeaveId = null) => {
  const cfg = companyConfig || {};
  const leaveTypes = cfg.leave_types && cfg.leave_types.length > 0 ? cfg.leave_types : [
    { name: "Casual Leave", days: 12 },
    { name: "Sick Leave", days: 6 }
  ];

  const rawDoj = emp?.date_of_joining || emp?.doj || null;
  let monthsWorked = 12;
  let joinedThisYear = false;
  if (rawDoj) {
    const parsedDoj = new Date(rawDoj);
    if (!isNaN(parsedDoj.getTime()) && parsedDoj.getFullYear() === targetYear) {
      joinedThisYear = true;
      monthsWorked = Math.max(1, 12 - parsedDoj.getMonth());
    }
  }

  const empId = emp?.id;
  const empLeavesInYear = (leavesList || []).filter(l => {
    if (empId && l.employee_id && l.employee_id !== empId) return false;
    const lYear = new Date(l.start_date || l.created_at || new Date()).getFullYear();
    return lYear === targetYear;
  });

  const calculateForType = (lt) => {
    const baseQuota = parseFloat(lt.days) || 0;
    const totalQuota = joinedThisYear ? Math.round(baseQuota * (monthsWorked / 12)) : baseQuota;

    const approvedUsed = empLeavesInYear
      .filter(l => l.status === "approved" && (l.leave_type || "").toLowerCase() === (lt.name || "").toLowerCase())
      .reduce((sum, l) => sum + (parseFloat(l.days) || 0), 0);

    const pendingUsed = empLeavesInYear
      .filter(l => l.status === "pending" && (excludeLeaveId ? l.id !== excludeLeaveId : true) && (l.leave_type || "").toLowerCase() === (lt.name || "").toLowerCase())
      .reduce((sum, l) => sum + (parseFloat(l.days) || 0), 0);

    const remainingBalance = Math.max(0, totalQuota - approvedUsed - pendingUsed);
    const balanceBeforePending = Math.max(0, totalQuota - approvedUsed);

    return {
      name: lt.name,
      leaveType: lt.name,
      baseQuota,
      totalQuota,
      approvedUsed,
      pendingUsed,
      remainingBalance,
      balanceBeforePending,
      availableBalBeforeReview: remainingBalance,
      joinedThisYear,
      monthsWorked,
    };
  };

  if (leaveTypeName) {
    const matchedLt = leaveTypes.find(lt => (lt.name || "").toLowerCase() === leaveTypeName.toLowerCase()) || { name: leaveTypeName, days: 12 };
    return calculateForType(matchedLt);
  }

  return leaveTypes.map(calculateForType);
};

// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE PORTAL
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE ATTENDANCE VIEW
// ─────────────────────────────────────────────────────────────────────────────
function EmployeeAttendanceView({ company, employee, session }) {
  const [todayRecord, setTodayRecord] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [punchMsg, setPunchMsg] = useState("");
  const [showRegModal, setShowRegModal] = useState(false);
  const [regForm, setRegForm] = useState({ date: "", reason: "" });
  const [regLoading, setRegLoading] = useState(false);
  const [regMsg, setRegMsg] = useState("");
  const [histFilter, setHistFilter] = useState("all");

  const today = new Date().toISOString().split("T")[0];
  const cfg = company?.config || {};
  const stdIn = cfg.attendance_punch_in || "09:00";
  const stdOut = cfg.attendance_punch_out || "18:00";
  const graceMin = cfg.attendance_grace_minutes || 15;

  const formatTimeStr12 = (tStr) => {
    if (!tStr) return "—";
    const [h, m] = tStr.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const displayH = h % 12 || 12;
    return `${String(displayH).padStart(2, "0")}:${String(m || 0).padStart(2, "0")} ${period}`;
  };

  const getGraceDeadlineStr = (tStr, grace) => {
    if (!tStr) return "—";
    const [h, m] = tStr.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m + (grace || 0), 0, 0);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const isLate = (punchInTs) => {
    if (!punchInTs) return false;
    const t = new Date(punchInTs);
    const [h, m] = stdIn.split(":").map(Number);
    const deadline = new Date(t);
    deadline.setHours(h, m + graceMin, 0, 0);
    return t > deadline;
  };

  const fmtTime = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const calcHours = (inTs, outTs) => {
    if (!inTs || !outTs) return null;
    const diff = (new Date(outTs) - new Date(inTs)) / 3600000;
    return diff > 0 ? diff.toFixed(1) : null;
  };

  const fetchAttendance = async () => {
    setLoading(true);
    const { data } = await supabase.from("attendance_records")
      .select("*")
      .eq("employee_id", session.employeeId)
      .order("date", { ascending: false })
      .limit(60);
    const records = data || [];
    const rec = records.find(r => r.date === today);
    setTodayRecord(rec || null);
    setAttendanceHistory(records);
    setLoading(false);
  };

  useEffect(() => { fetchAttendance(); }, [session.employeeId]);

  const handlePunchIn = async () => {
    setPunching(true); setPunchMsg("");
    const now = new Date().toISOString();
    const late = isLate(now);
    const { error } = await supabase.from("attendance_records").insert([{
      company_id: session.companyId,
      employee_id: session.employeeId,
      date: today,
      punch_in: now,
      status: late ? "late" : "present",
    }]);
    setPunching(false);
    if (error) { setPunchMsg("Error: " + error.message); return; }
    logAudit(session, "EMPLOYEE_PUNCH_IN", { date: today, time: now, late });
    await fetchAttendance();
    setPunchMsg(late ? "⏰ Punched in — marked as Late" : "✅ Punched in successfully!");
    setTimeout(() => setPunchMsg(""), 4000);
  };

  const handlePunchOut = async () => {
    if (!todayRecord) return;
    setPunching(true); setPunchMsg("");
    const now = new Date().toISOString();
    const hours = calcHours(todayRecord.punch_in, now);
    const stdOutStr = cfg.attendance_punch_out || "18:00";
    const [sh, sm] = stdOutStr.split(":").map(Number);
    const expectedOut = new Date(); expectedOut.setHours(sh, sm, 0, 0);
    const earlyExit = new Date(now) < expectedOut;
    const earlyMin = earlyExit ? Math.round((expectedOut.getTime() - new Date(now).getTime()) / 60000) : 0;
    const { error } = await supabase.from("attendance_records")
      .update({
        punch_out: now,
        early_minutes: earlyMin > 0 ? earlyMin : 0,
        updated_at: now
      })
      .eq("id", todayRecord.id);
    setPunching(false);
    if (error) { setPunchMsg("Error: " + error.message); return; }
    logAudit(session, "EMPLOYEE_PUNCH_OUT", { date: today, time: now, hours, early_minutes: earlyMin });
    await fetchAttendance();
    setPunchMsg(earlyExit ? "👋 Punched out — Early departure noted" : "✅ Punched out successfully!");
    setTimeout(() => setPunchMsg(""), 4000);
  };

  const handleRegularization = async (e) => {
    e.preventDefault();
    setRegMsg("");
    if (!regForm.date || !regForm.reason.trim()) return setRegMsg("Date and reason are required.");
    setRegLoading(true);
    // Check if record exists for that date
    const { data: existing } = await supabase.from("attendance_records")
      .select("id")
      .eq("employee_id", session.employeeId)
      .eq("date", regForm.date)
      .maybeSingle();
    let error;
    if (existing) {
      ({ error } = await supabase.from("attendance_records")
        .update({ regularization_reason: regForm.reason, regularization_status: "pending" })
        .eq("id", existing.id));
    } else {
      ({ error } = await supabase.from("attendance_records").insert([{
        company_id: session.companyId,
        employee_id: session.employeeId,
        date: regForm.date,
        status: "absent",
        regularization_reason: regForm.reason,
        regularization_status: "pending",
      }]));
    }
    setRegLoading(false);
    if (error) { setRegMsg("Error: " + error.message); return; }
    logAudit(session, "EMPLOYEE_REGULARIZATION_REQUEST", { date: regForm.date, reason: regForm.reason });
    setShowRegModal(false);
    setRegForm({ date: "", reason: "" });
    await fetchAttendance();
  };

  // Monthly summary
  const thisMonth = new Date().getMonth();
  const thisYear = new Date().getFullYear();
  const monthRecords = attendanceHistory.filter(r => {
    const d = new Date(r.date);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });
  const presentCount = monthRecords.filter(r => r.status === "present" || r.status === "late" || r.status === "regularized").length;
  const lateCount = monthRecords.filter(r => r.status === "late").length;
  const absentCount = monthRecords.filter(r => r.status === "absent" && r.regularization_status !== "approved").length;

  const filteredHistory = histFilter === "all" ? attendanceHistory
    : attendanceHistory.filter(r => r.status === histFilter || r.regularization_status === histFilter);

  const statusBadge = (r) => {
    if (r.regularization_status === "pending") return { bg: "#fef9c3", color: "#a16207", label: "🔄 Regularization Pending" };
    if (r.regularization_status === "approved") return { bg: "#dcfce7", color: "#166534", label: "✅ Regularized" };
    if (r.status === "present") return { bg: "#dcfce7", color: "#166534", label: "✅ Present" };
    if (r.status === "late") return { bg: "#fef3c7", color: "#92400e", label: "⏰ Late" };
    if (r.status === "absent") return { bg: "#fee2e2", color: "#991b1b", label: "🔴 Absent" };
    return { bg: "#f1f5f9", color: "#475569", label: r.status };
  };

  const handleExportExcel = () => {
    const recordsToExport = filteredHistory && filteredHistory.length > 0 ? filteredHistory : attendanceHistory;
    if (!recordsToExport || recordsToExport.length === 0) {
      alert("No attendance records to export.");
      return;
    }

    try {
      const rows = recordsToExport.map(r => {
        const d = new Date(r.date + "T00:00:00");
        const weekday = d.toLocaleDateString("en-IN", { weekday: "long" });
        const hours = calcHours(r.punch_in, r.punch_out);
        const badge = statusBadge(r);

        return {
          "Date": r.date,
          "Day": weekday,
          "Status": (badge.label || "").replace(/[^\w\s]/gi, '').trim(),
          "Punch In": r.punch_in ? fmtTime(r.punch_in) : "—",
          "Punch Out": r.punch_out ? fmtTime(r.punch_out) : "—",
          "Total Hours": hours ? `${hours} hrs` : (r.punch_in && !r.punch_out ? "In progress" : "—"),
          "Arrival / Departure Note": isLate(r.punch_in) 
            ? "Late arrival" 
            : (r.early_minutes > 0 ? `Early departure (${r.early_minutes}m)` : "Normal"),
          "Regularization Status": r.regularization_status ? r.regularization_status.toUpperCase() : "—",
          "Regularization Reason": r.regularization_reason || "—"
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      // Auto-fit column widths
      ws['!cols'] = Object.keys(rows[0]).map(k => {
        const maxLen = Math.max(
          k.length,
          ...rows.map(r => String(r[k] ?? "").length)
        );
        return { wch: Math.min(Math.max(maxLen + 4, 12), 40) };
      });

      XLSX.utils.book_append_sheet(wb, ws, "Attendance History");

      const empCode = employee?.employee_code || session?.employeeCode || "EMP";
      const empName = (employee?.name || session?.employeeName || "Employee").replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `${empName}_${empCode}_Attendance_History.xlsx`;

      XLSX.writeFile(wb, filename);
      logAudit(session, "EMPLOYEE_EXPORT_ATTENDANCE_EXCEL", { count: rows.length });
    } catch (err) {
      alert("Failed to export Excel: " + err.message);
    }
  };

  if (loading) return <div style={{padding: 32, textAlign: "center", color: "#64748b"}}>Loading attendance...</div>;

  const hasPunchedIn = !!todayRecord?.punch_in;
  const hasPunchedOut = !!todayRecord?.punch_out;

  return (
    <div>
      <style>{`
        .att-today-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 14px;
          padding: 16px 20px;
          margin-bottom: 14px;
          color: #fff;
          box-shadow: 0 4px 16px rgba(15,23,42,0.12);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .att-shift-bar {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 6px 12px;
          margin-top: 8px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justifyContent: space-between;
          flex-wrap: wrap;
          gap: 6px 12px;
          font-size: 12px;
        }
        .att-stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 10px;
          margin: 12px 0;
        }
        .att-stat-cell {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          padding: 10px 12px;
          min-width: 0;
        }
        .att-stat-lbl {
          font-size: 10.5px;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .att-time-val {
          font-size: 17px;
          font-weight: 800;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .att-punch-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 9px 24px;
          min-width: 150px;
          max-width: 220px;
          border: none;
          border-radius: 8px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .att-punch-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .att-summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          margin-bottom: 14px;
        }
        .att-summary-card {
          border-radius: 8px;
          padding: 6px 10px;
          display: flex;
          align-items: center;
          justifyContent: space-between;
          min-width: 0;
        }
        .att-summary-lbl {
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .att-summary-badge {
          font-size: 13.5px;
          font-weight: 900;
          padding: 1px 7px;
          border-radius: 6px;
        }
        .att-history-item {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 11px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
          gap: 10px;
        }
        @media (max-width: 640px) {
          .att-today-card {
            padding: 12px 12px !important;
            border-radius: 12px !important;
            margin-bottom: 12px !important;
          }
          .att-shift-bar {
            padding: 6px 8px !important;
            font-size: 10.5px !important;
            margin-top: 6px !important;
            margin-bottom: 10px !important;
            gap: 4px 8px !important;
          }
          .att-stats-grid {
            gap: 6px !important;
            margin: 10px 0 !important;
          }
          .att-stat-cell {
            padding: 8px 6px !important;
            border-radius: 8px !important;
          }
          .att-stat-lbl {
            font-size: 9px !important;
            margin-bottom: 2px !important;
          }
          .att-time-val {
            font-size: 13.5px !important;
          }
          .att-punch-btn {
            padding: 8px 18px !important;
            font-size: 13px !important;
            min-width: 130px !important;
            max-width: 190px !important;
            border-radius: 7px !important;
          }
          .att-summary-grid {
            gap: 5px !important;
            margin-bottom: 10px !important;
          }
          .att-summary-card {
            padding: 5px 7px !important;
            border-radius: 6px !important;
          }
          .att-summary-lbl {
            font-size: 9.5px !important;
          }
          .att-summary-badge {
            font-size: 12px !important;
            padding: 1px 5px !important;
          }
          .att-history-item {
            padding: 9px 11px !important;
            border-radius: 8px !important;
          }
        }
      `}</style>

      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14}}>
        <div>
          <div className="page-title" style={{marginBottom: 2, fontSize: 18}}>🕐 Attendance</div>
          <div className="page-sub" style={{fontSize: 12}}>Track your daily punch-in & punch-out.</div>
        </div>
        <button className="btn btn-ghost btn-sm" style={{fontSize: 11.5, padding: "5px 10px"}} onClick={() => setShowRegModal(true)}>+ Regularize</button>
      </div>

      {/* Today's Punch Card */}
      <div className="att-today-card">
        <div style={{fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 6}}>
          <span>📅 Today • {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
        </div>

        {/* Compact Admin Configured Shift & Timing Schedule */}
        <div className="att-shift-bar">
          <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
            <span>🏢</span>
            <span style={{color: "#94a3b8", fontWeight: 700}}>Shift:</span>
            <span style={{color: "#4ade80", fontWeight: 800}}>{formatTimeStr12(stdIn)}</span>
            <span style={{color: "#64748b"}}>–</span>
            <span style={{color: "#60a5fa", fontWeight: 800}}>{formatTimeStr12(stdOut)}</span>
          </div>
          <div style={{color: "#cbd5e1"}}>
            Grace: <strong style={{color: "#fde047"}}>{graceMin}m</strong> <span style={{color: "#94a3b8"}}>(until {getGraceDeadlineStr(stdIn, graceMin)})</span>
          </div>
        </div>

        <div className="att-stats-grid">
          <div className="att-stat-cell">
            <div className="att-stat-lbl">Punch In</div>
            <div className="att-time-val" style={{color: hasPunchedIn ? "#4ade80" : "#64748b"}}>
              {hasPunchedIn ? fmtTime(todayRecord.punch_in) : "—"}
            </div>
            {hasPunchedIn && isLate(todayRecord.punch_in) && (
              <div style={{fontSize: 9.5, color: "#fbbf24", marginTop: 2, fontWeight: 700}}>⏰ Late</div>
            )}
          </div>
          <div className="att-stat-cell">
            <div className="att-stat-lbl">Punch Out</div>
            <div className="att-time-val" style={{color: hasPunchedOut ? "#60a5fa" : "#64748b"}}>
              {hasPunchedOut ? fmtTime(todayRecord.punch_out) : "—"}
            </div>
            {hasPunchedOut && (todayRecord.early_minutes > 0 || todayRecord.early_exit) && (
              <div style={{fontSize: 9.5, color: "#fbbf24", marginTop: 2, fontWeight: 700}}>⚡ Early {todayRecord.early_minutes ? `(${todayRecord.early_minutes}m)` : ""}</div>
            )}
          </div>
          <div className="att-stat-cell">
            <div className="att-stat-lbl">Hours</div>
            <div className="att-time-val" style={{color: "#e2e8f0", fontSize: hasPunchedOut ? undefined : 13}}>
              {hasPunchedOut ? `${calcHours(todayRecord.punch_in, todayRecord.punch_out) || "0.0"}h` : hasPunchedIn ? "In progress" : "—"}
            </div>
          </div>
        </div>

        <div style={{display: "flex", justifyContent: "center", alignItems: "center", marginTop: 4}}>
          {!hasPunchedIn && (
            <button
              onClick={handlePunchIn}
              disabled={punching}
              className="att-punch-btn"
              style={{background: "#22c55e", color: "#fff", boxShadow: "0 2px 8px rgba(34,197,94,0.3)"}}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.01)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              {punching ? "Processing..." : "🟢 Punch In"}
            </button>
          )}
          {hasPunchedIn && !hasPunchedOut && (
            <button
              onClick={handlePunchOut}
              disabled={punching}
              className="att-punch-btn"
              style={{background: "#3b82f6", color: "#fff", boxShadow: "0 2px 8px rgba(59,130,246,0.3)"}}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.01)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              {punching ? "Processing..." : "🔵 Punch Out"}
            </button>
          )}
          {hasPunchedIn && hasPunchedOut && (
            <div className="att-punch-btn" style={{background: "rgba(255,255,255,0.06)", color: "#94a3b8", textAlign: "center", cursor: "default"}}>
              ✅ Day complete
            </div>
          )}
        </div>
        {punchMsg && <div style={{marginTop: 8, fontSize: 12, fontWeight: 700, color: "#fbbf24", textAlign: "center"}}>{punchMsg}</div>}
      </div>

      {/* Monthly Summary Strip */}
      <div className="att-summary-grid">
        {[
          { label: "Present", val: presentCount, color: "#166534", badgeColor: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", badgeBg: "#dcfce7" },
          { label: "Late", val: lateCount, color: "#92400e", badgeColor: "#b45309", bg: "#fffbeb", border: "#fde68a", badgeBg: "#fef3c7" },
          { label: "Absent", val: absentCount, color: "#991b1b", badgeColor: "#dc2626", bg: "#fef2f2", border: "#fecaca", badgeBg: "#fee2e2" }
        ].map(item => (
          <div key={item.label} className="att-summary-card" style={{background: item.bg, border: `1px solid ${item.border}`}}>
            <span className="att-summary-lbl" style={{color: item.color}}>{item.label}</span>
            <span className="att-summary-badge" style={{color: item.badgeColor, background: item.badgeBg}}>{item.val}</span>
          </div>
        ))}
      </div>

      {/* History */}
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8}}>
        <div style={{fontSize: 12.5, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 0.8}}>Attendance History</div>
        <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
          <select className="config-input" style={{width: "auto", height: 28, padding: "2px 8px", fontSize: 11.5, margin: 0}} value={histFilter} onChange={e => setHistFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="present">Present</option>
            <option value="late">Late</option>
            <option value="absent">Absent</option>
            <option value="pending">Regularization Pending</option>
          </select>
          <button 
            type="button" 
            onClick={handleExportExcel}
            className="btn btn-ghost btn-sm"
            style={{
              height: 28, 
              padding: "0 10px", 
              fontSize: 11.5, 
              fontWeight: 700, 
              display: "inline-flex", 
              alignItems: "center", 
              gap: 4, 
              background: "#f0fdf4", 
              color: "#166534", 
              border: "1px solid #bbf7d0", 
              borderRadius: 6, 
              cursor: "pointer"
            }}
            title="Download attendance history as Excel spreadsheet"
          >
            <span>📥</span> <span>Excel</span>
          </button>
        </div>
      </div>

      {filteredHistory.length === 0 ? (
        <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, textAlign: "center", color: "#64748b"}}>
          <div style={{fontSize: 26, marginBottom: 8}}>📋</div>
          <div style={{fontWeight: 600, fontSize: 13}}>No attendance records yet</div>
        </div>
      ) : (
        <div style={{display: "flex", flexDirection: "column", gap: 8}}>
          {filteredHistory.map(r => {
            const badge = statusBadge(r);
            const hours = calcHours(r.punch_in, r.punch_out);
            return (
              <div key={r.id} className="att-history-item">
                <div style={{minWidth: 0, flex: 1}}>
                  <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap"}}>
                    <span style={{fontWeight: 700, color: "#1e293b", fontSize: 13}}>
                      {new Date(r.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                    </span>
                    <span style={{fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 8, background: badge.bg, color: badge.color}}>{badge.label}</span>
                  </div>
                  <div style={{fontSize: 11.5, color: "#64748b", display: "flex", gap: 12, flexWrap: "wrap"}}>
                    {r.punch_in && <span>In: <strong style={{color: "#334155"}}>{fmtTime(r.punch_in)}</strong></span>}
                    {r.punch_out && <span>Out: <strong style={{color: "#334155"}}>{fmtTime(r.punch_out)}</strong></span>}
                    {hours && <span>Hours: <strong style={{color: "#334155"}}>{hours}h</strong></span>}
                  </div>
                  {r.regularization_reason && <div style={{fontSize: 10.5, color: "#64748b", marginTop: 3, fontStyle: "italic"}}>Reason: "{r.regularization_reason}"</div>}
                </div>
                {r.status === "absent" && !r.regularization_status && r.date < today && (
                  <button className="btn btn-ghost btn-sm" style={{fontSize: 11, padding: "4px 8px", flexShrink: 0}} onClick={() => { setRegForm({ date: r.date, reason: "" }); setShowRegModal(true); }}>Request Fix</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Regularization Modal */}
      {showRegModal && (
        <div className="overlay" style={{zIndex: 9999}} onClick={() => setShowRegModal(false)}>
          <div className="modal" style={{maxWidth: 440}} onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20}}>
              <div style={{fontSize: 17, fontWeight: 800, color: "#1e293b"}}>🔄 Request Regularization</div>
              <button className="btn btn-ghost btn-xs" style={{fontSize: 20, padding: 0}} onClick={() => setShowRegModal(false)}>×</button>
            </div>
            <div style={{fontSize: 13, color: "#64748b", marginBottom: 16}}>Missed a punch? Submit a regularization request with reason. Your admin will review it.</div>
            {regMsg && <div className="err-msg" style={{marginBottom: 12}}>⚠️ {regMsg}</div>}
            <form onSubmit={handleRegularization} style={{display: "flex", flexDirection: "column", gap: 14}}>
              <div className="config-field">
                <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Date</div>
                <input type="date" className="config-input" value={regForm.date} max={today} onChange={e => setRegForm({...regForm, date: e.target.value})} />
              </div>
              <div className="config-field">
                <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Reason</div>
                <textarea className="config-input" rows={3} value={regForm.reason} onChange={e => setRegForm({...regForm, reason: e.target.value})} placeholder="Was present but forgot to punch in..." />
              </div>
              <div style={{display: "flex", justifyContent: "flex-end", gap: 8}}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowRegModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={regLoading}>{regLoading ? "Submitting..." : "Submit Request"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EmployeeLeavesView({ company, employee, leaveRequests, fetchRequests, session, onResetApplyModal }) {
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({ type: "", startDate: "", endDate: "", reason: "" });
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  const cfg = company?.config || {};
  const leaveTypes = cfg.leave_types && cfg.leave_types.length > 0 ? cfg.leave_types : [
    { name: "Casual Leave", days: 12 },
    { name: "Sick Leave", days: 6 }
  ];
  const holidays = cfg.company_holidays || [];
  const workingDays = cfg.working_days_schedule || [1, 2, 3, 4, 5, 6];

  const currentYear = new Date().getFullYear();

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const [sY, sM, sD] = start.split("-").map(Number);
    const [eY, eM, eD] = end.split("-").map(Number);
    let cur = new Date(sY, sM - 1, sD, 12, 0, 0);
    const endD = new Date(eY, eM - 1, eD, 12, 0, 0);
    let count = 0;
    while (cur <= endD) {
      const isWorkingDay = workingDays.includes(cur.getDay());
      const curStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      const isHoliday = holidays.some(h => h.date === curStr);
      if (isWorkingDay && !isHoliday) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  const requestedDays = calculateDays(applyForm.startDate, applyForm.endDate);

  const getBalance = (leaveTypeName) => {
    const m = getEmployeeLeaveMetrics(employee, company?.config, leaveRequests, leaveTypeName, currentYear);
    return m.remainingBalance;
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplyMsg("");
    if (!applyForm.type) return setApplyMsg("Please select a leave type.");
    if (!applyForm.startDate || !applyForm.endDate) return setApplyMsg("Please select dates.");
    if (applyForm.startDate > applyForm.endDate) return setApplyMsg("End date cannot be earlier than start date.");
    if (requestedDays <= 0) return setApplyMsg("Selected range has 0 working days (dates fall on unselected non-working days or holidays).");

    // Overlapping leave validation (checks against active pending or approved requests across all leave types)
    const overlapping = leaveRequests.find(r => {
      if (r.status === "rejected" || r.status === "cancelled") return false;
      return r.start_date <= applyForm.endDate && r.end_date >= applyForm.startDate;
    });

    if (overlapping) {
      return setApplyMsg(
        `Overlap detected: You already have an active leave request (${overlapping.leave_type}, status: ${overlapping.status}) from ${overlapping.start_date} to ${overlapping.end_date}. You cannot apply multiple times for overlapping dates.`
      );
    }
    
    const balance = getBalance(applyForm.type);
    if (requestedDays > balance) return setApplyMsg(`Insufficient balance. You only have ${balance} days left.`);

    setApplyLoading(true);

    const { error } = await supabase.from("employee_leaves").insert([{
      company_id: company.id,
      employee_id: employee.id,
      leave_type: applyForm.type,
      start_date: applyForm.startDate,
      end_date: applyForm.endDate,
      days: requestedDays,
      reason: applyForm.reason || null,
      status: "pending"
    }]);
    
    setApplyLoading(false);
    if (error) {
      setApplyMsg("Error: " + error.message);
    } else {
      logAudit(session, "EMPLOYEE_APPLIED_LEAVE", {
        leave_type: applyForm.type,
        start_date: applyForm.startDate,
        end_date: applyForm.endDate,
        days: requestedDays,
        reason: applyForm.reason || null
      });
      setShowApplyModal(false);
      if (onResetApplyModal) onResetApplyModal();
      setApplyForm({ type: "", startDate: "", endDate: "", reason: "" });
      fetchRequests();
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    const req = leaveRequests.find(l => l.id === id);
    const { error } = await supabase.from("employee_leaves").update({ status: "cancelled" }).eq("id", id);
    if (!error) {
      logAudit(session, "EMPLOYEE_CANCELLED_LEAVE", {
        leave_id: id,
        leave_type: req?.leave_type,
        start_date: req?.start_date,
        end_date: req?.end_date,
        days: req?.days
      });
      fetchRequests();
    }
  };

  // Extract available years
  const availableYears = Array.from(new Set([
    String(currentYear),
    ...leaveRequests.map(r => new Date(r.start_date).getFullYear().toString())
  ])).sort().reverse();

  // Filter leaves
  const filteredLeaves = leaveRequests.filter(req => {
    if (filterStatus !== "all" && req.status !== filterStatus) return false;
    if (filterType !== "all" && req.leave_type !== filterType) return false;
    if (filterYear !== "all") {
      const yr = new Date(req.start_date).getFullYear().toString();
      if (yr !== filterYear) return false;
    }
    return true;
  });

  // 🌴 Helper calculations for upcoming next 3 holidays
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  const upcomingHolidays = (() => {
    const rawHols = (cfg.company_holidays && cfg.company_holidays.length > 0)
      ? cfg.company_holidays
      : [
          { name: "Republic Day", date: `${currentYear}-01-26` },
          { name: "May Day", date: `${currentYear}-05-01` },
          { name: "Independence Day", date: `${currentYear}-08-15` },
          { name: "Gandhi Jayanti", date: `${currentYear}-10-02` },
          { name: "Dussehra", date: `${currentYear}-10-19` },
          { name: "Diwali", date: `${currentYear}-11-08` },
          { name: "Christmas", date: `${currentYear}-12-25` },
          { name: "New Year's Day", date: `${currentYear + 1}-01-01` },
          { name: "Republic Day", date: `${currentYear + 1}-01-26` }
        ];

    let valid = rawHols
      .filter(h => h && h.date && h.date >= todayStr)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));

    if (valid.length === 0) {
      valid = rawHols
        .map(h => ({
          ...h,
          date: h.date ? h.date.replace(/^\d{4}/, String(currentYear + 1)) : ""
        }))
        .filter(h => h.date && h.date >= todayStr)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }
    return valid.slice(0, 3);
  })();

  const getHolidayEmoji = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("diwali") || n.includes("deepavali")) return "🪔";
    if (n.includes("christmas")) return "🎄";
    if (n.includes("republic") || n.includes("independence")) return "🇮🇳";
    if (n.includes("gandhi")) return "🕊️";
    if (n.includes("holi")) return "🌸";
    if (n.includes("eid") || n.includes("ramzan") || n.includes("bakrid")) return "🌙";
    if (n.includes("new year")) return "🎆";
    if (n.includes("dussehra") || n.includes("dasara") || n.includes("durga")) return "🏹";
    if (n.includes("ganesh")) return "🐘";
    if (n.includes("raksha") || n.includes("rakhi")) return "🧵";
    if (n.includes("may day") || n.includes("labour")) return "🛠️";
    if (n.includes("good friday") || n.includes("easter")) return "✝️";
    if (n.includes("onam") || n.includes("pongal") || n.includes("sankranti")) return "🌾";
    return "🎉";
  };

  const formatHolidayDate = (dateStr) => {
    if (!dateStr || !dateStr.includes("-")) return { month: "", day: "", weekday: "", fullWeekday: "" };
    const parts = dateStr.split("-").map(Number);
    if (parts.length !== 3) return { month: "", day: "", weekday: "", fullWeekday: "" };
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return {
      month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      day: String(parts[2]).padStart(2, "0"),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      fullWeekday: d.toLocaleDateString("en-US", { weekday: "long" })
    };
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr || !dateStr.includes("-")) return { label: "", urgent: false };
    const parts = dateStr.split("-").map(Number);
    const target = new Date(parts[0], parts[1] - 1, parts[2]);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffMs = target.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return { label: "🎉 Today!", urgent: true };
    if (diffDays === 1) return { label: "⚡ Tomorrow!", urgent: true };
    if (diffDays <= 7) return { label: `⏳ In ${diffDays} days`, urgent: true };
    return { label: `🗓️ In ${diffDays} days`, urgent: false };
  };

  return (
    <div>
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20}}>
        <div>
          <div className="page-title" style={{marginBottom: 8}}>🏖️ Leaves</div>
          <div className="page-sub">Apply for time off and track your balances.</div>
        </div>
        <div style={{display: "flex", gap: 8, alignItems: "center"}}>
          <button 
            type="button" 
            className="btn btn-ghost btn-sm" 
            onClick={async () => {
              setRefreshing(true);
              if (fetchRequests) await fetchRequests();
              setTimeout(() => setRefreshing(false), 500);
            }} 
            disabled={refreshing}
            style={{display: "flex", alignItems: "center", gap: 6, fontSize: 13, background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 14px"}}
            title="Refresh leave approvals and balances"
          >
            <span style={{display: "inline-block", transform: refreshing ? "rotate(180deg)" : "none", transition: "transform 0.5s ease"}}>🔄</span>
            <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowApplyModal(true)}>+ Apply for Leave</button>
        </div>
      </div>

      {/* 🌴 Floating & Blinking Upcoming Next 3 Holidays Banner */}
      {upcomingHolidays.length > 0 && (
        <div className="holiday-floating-banner">
          <style>{`
            @keyframes holidayFloat {
              0%, 100% {
                transform: translateY(0px);
                box-shadow: 0 10px 25px -4px rgba(245, 158, 11, 0.18), 0 4px 10px -2px rgba(245, 158, 11, 0.1);
              }
              50% {
                transform: translateY(-5px);
                box-shadow: 0 18px 32px -4px rgba(245, 158, 11, 0.28), 0 8px 14px -2px rgba(245, 158, 11, 0.16);
              }
            }
            @keyframes holidayLiveBlink {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.25; transform: scale(0.8); }
            }
            @keyframes pulseRing {
              0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
              70% { transform: scale(1); box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
              100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
            }
            .holiday-floating-banner {
              animation: holidayFloat 4s ease-in-out infinite;
              background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 45%, #ffedd5 100%);
              border: 1.5px solid #fcd34d;
              border-radius: 16px;
              padding: 16px 20px;
              margin-bottom: 22px;
              position: relative;
              overflow: hidden;
              transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            }
            .holiday-floating-banner:hover {
              border-color: #f59e0b;
              transform: translateY(-6px);
            }
            .holiday-pill-blink {
              animation: holidayLiveBlink 1.4s ease-in-out infinite;
            }
            .holiday-pulse-dot {
              animation: pulseRing 1.8s infinite;
            }
            .holiday-name-text {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .holiday-mobile-tap-hint {
              display: none;
            }
            @media (max-width: 640px) {
              .holiday-floating-banner {
                padding: 10px 12px !important;
                margin-bottom: 14px !important;
                border-radius: 12px !important;
                animation: none !important;
              }
              .holiday-floating-banner:hover {
                transform: none !important;
              }
              .holiday-header-alert {
                display: none !important;
              }
              .holiday-cards-grid {
                display: flex !important;
                overflow-x: auto !important;
                scroll-snap-type: x mandatory !important;
                gap: 10px !important;
                padding-bottom: 4px !important;
                -webkit-overflow-scrolling: touch !important;
                scrollbar-width: none !important;
              }
              .holiday-cards-grid::-webkit-scrollbar {
                display: none !important;
              }
              .holiday-card-item {
                min-width: 205px !important;
                max-width: 225px !important;
                flex: 0 0 auto !important;
                padding: 8px 11px !important;
                border-radius: 11px !important;
                scroll-snap-align: start !important;
                cursor: pointer;
                user-select: none;
                -webkit-tap-highlight-color: transparent;
              }
              .holiday-card-item:active {
                transform: scale(0.98);
              }
              .holiday-date-badge {
                min-width: 38px !important;
                height: 40px !important;
                border-radius: 8px !important;
              }
              .holiday-name-text {
                white-space: normal !important;
                display: -webkit-box !important;
                -webkit-line-clamp: 2 !important;
                -webkit-box-orient: vertical !important;
                line-height: 1.25 !important;
                font-size: 12px !important;
                word-break: break-word !important;
              }
              .holiday-mobile-tap-hint {
                display: inline-block !important;
              }
            }
          `}</style>
          
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8}}>
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              {/* Glowing blinking dot */}
              <div style={{position: "relative", width: 12, height: 12, display: "flex", alignItems: "center", justifyContent: "center"}}>
                <span className="holiday-pulse-dot" style={{position: "absolute", width: 12, height: 12, borderRadius: "50%", background: "#ef4444"}} />
                <span className="holiday-pill-blink" style={{position: "relative", width: 8, height: 8, borderRadius: "50%", background: "#dc2626"}} />
              </div>
              <div>
                <span style={{fontSize: 12, fontWeight: 900, color: "#92400e", letterSpacing: 0.8, textTransform: "uppercase"}}>
                  🌴 Upcoming Holidays
                </span>
                <span className="holiday-mobile-tap-hint" style={{fontSize: 10, color: "#b45309", marginLeft: 6, fontWeight: 600}}>
                  • Tap card for details
                </span>
              </div>
            </div>
            
            <div className="holiday-header-alert" style={{display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.85)", border: "1px solid #fde68a", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, color: "#b45309"}}>
              <span className="holiday-pill-blink" style={{fontSize: 12}}>✨</span>
              <span>Next 3 Days Off</span>
            </div>
          </div>

          <div className="holiday-cards-grid" style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10}}>
            {upcomingHolidays.map((hol, idx) => {
              const dt = formatHolidayDate(hol.date);
              const emoji = getHolidayEmoji(hol.name);
              const countdown = getDaysUntil(hol.date);
              const isFirst = idx === 0;
              const isSelected = selectedHoliday?.name === hol.name && selectedHoliday?.date === hol.date;

              return (
                <div 
                  key={hol.id || hol.date + hol.name}
                  className="holiday-card-item"
                  title={`${hol.name} • ${dt.fullWeekday}, ${dt.day} ${dt.month} (${countdown.label})`}
                  onClick={() => setSelectedHoliday(prev => (prev?.name === hol.name && prev?.date === hol.date) ? null : hol)}
                  style={{
                    background: isSelected ? "#fffbeb" : (isFirst ? "#ffffff" : "rgba(255, 255, 255, 0.9)"),
                    border: isSelected ? "2px solid #d97706" : (isFirst ? "1.5px solid #f59e0b" : "1px solid #fde68a"),
                    borderRadius: 12,
                    padding: "10px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    boxShadow: isSelected ? "0 4px 14px rgba(217, 119, 6, 0.28)" : (isFirst ? "0 4px 12px rgba(245, 158, 11, 0.15)" : "0 2px 6px rgba(0,0,0,0.03)"),
                    position: "relative",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(245,158,11,0.22)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isSelected ? "0 4px 14px rgba(217, 119, 6, 0.28)" : (isFirst ? "0 4px 12px rgba(245, 158, 11, 0.15)" : "0 2px 6px rgba(0,0,0,0.03)"); }}
                >
                  {/* Date badge */}
                  <div className="holiday-date-badge" style={{
                    minWidth: 44,
                    height: 46,
                    borderRadius: 9,
                    background: isFirst ? "linear-gradient(135deg, #f59e0b, #d97706)" : "linear-gradient(135deg, #fef3c7, #fde68a)",
                    color: isFirst ? "#ffffff" : "#92400e",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: isFirst ? "0 2px 6px rgba(217, 119, 6, 0.25)" : "none",
                  }}>
                    <span style={{fontSize: 8.5, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", lineHeight: 1}}>{dt.month}</span>
                    <span style={{fontSize: 16, fontWeight: 900, lineHeight: 1.1}}>{dt.day}</span>
                    <span style={{fontSize: 8.5, fontWeight: 700, opacity: 0.85, lineHeight: 1}}>{dt.weekday}</span>
                  </div>

                  {/* Holiday info */}
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{display: "flex", alignItems: "center", gap: 5}}>
                      <span style={{fontSize: 15, flexShrink: 0}}>{emoji}</span>
                      <span 
                        className="holiday-name-text"
                        style={{
                          fontSize: 12.5, 
                          fontWeight: 800, 
                          color: "#1e293b",
                          whiteSpace: isSelected ? "normal" : undefined
                        }}
                      >
                        {hol.name}
                      </span>
                    </div>
                    <div style={{fontSize: 10.5, color: "#64748b", marginTop: 2}}>
                      <span>{dt.fullWeekday}</span>
                    </div>
                  </div>

                  {/* Blinking countdown chip */}
                  <div style={{flexShrink: 0, textAlign: "right"}}>
                    <div 
                      className={countdown.urgent ? "holiday-pill-blink" : ""}
                      style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        padding: "3px 7px",
                        borderRadius: 7,
                        background: isFirst ? "#fef2f2" : "#f8fafc",
                        color: isFirst ? "#dc2626" : "#475569",
                        border: isFirst ? "1px solid #fecaca" : "1px solid #e2e8f0",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {countdown.urgent && <span style={{fontSize: 8}}>🔴</span>}
                      {countdown.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Tap Details Popup Banner */}
          {selectedHoliday && (
            <div 
              style={{
                marginTop: 10,
                background: "#ffffff",
                border: "1.5px solid #f59e0b",
                borderRadius: 12,
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                boxShadow: "0 4px 14px rgba(245, 158, 11, 0.2)",
              }}
            >
              <div style={{display: "flex", alignItems: "center", gap: 10, minWidth: 0}}>
                <span style={{fontSize: 22, flexShrink: 0}}>{getHolidayEmoji(selectedHoliday.name)}</span>
                <div style={{minWidth: 0}}>
                  <div style={{fontSize: 13.5, fontWeight: 900, color: "#0f172a", wordBreak: "break-word", lineHeight: 1.3}}>
                    {selectedHoliday.name}
                  </div>
                  <div style={{fontSize: 11, color: "#78350f", fontWeight: 700, marginTop: 2}}>
                    📅 {formatHolidayDate(selectedHoliday.date).fullWeekday}, {formatHolidayDate(selectedHoliday.date).day} {formatHolidayDate(selectedHoliday.date).month} • <span style={{color: "#b45309"}}>{getDaysUntil(selectedHoliday.date).label}</span>
                  </div>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setSelectedHoliday(null)}
                style={{
                  background: "#fef3c7", 
                  border: "none", 
                  borderRadius: 6,
                  width: 26,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13, 
                  fontWeight: 900,
                  color: "#92400e", 
                  cursor: "pointer", 
                  flexShrink: 0
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8}}>
        <div style={{fontSize: 13, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 8}}>
          <span>Leaves for {currentYear}</span>
          <span style={{fontSize: 11, fontWeight: 700, background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 12}}>
            {leaveTypes.length} Policy Categories
          </span>
        </div>
        <button
          onClick={() => setShowPolicyModal(true)}
          style={{
            background: "#eff6ff",
            color: "#2563eb",
            border: "1px solid #bfdbfe",
            borderRadius: 20,
            padding: "5px 14px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            transition: "all 0.15s ease",
            boxShadow: "0 1px 2px rgba(37,99,235,0.06)"
          }}
          title="Click to view full company leave rules, quotas, and holiday calendar"
        >
          <span>📋</span>
          <span>View Company Leave Policy &amp; Rules</span>
        </button>
      </div>

      <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 24}}>
        {leaveTypes.map(lt => {
          const m = getEmployeeLeaveMetrics(employee, company?.config, leaveRequests, lt.name, currentYear);
          const isProrated = m.joinedThisYear && m.monthsWorked < 12;
          return (
            <div key={lt.name} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4}}>
                <span style={{fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5}}>{lt.name}</span>
                <span style={{fontSize: 10, fontWeight: 700, background: "#f1f5f9", color: "#475569", padding: "1px 6px", borderRadius: 6}}>
                  Quota: {m.totalQuota}d
                </span>
              </div>
              <div style={{fontSize: 22, fontWeight: 800, color: m.remainingBalance > 0 ? "#0f172a" : "#dc2626", lineHeight: 1.2}}>
                {m.remainingBalance} <span style={{fontSize: 13, color: "#64748b", fontWeight: 600}}>days left</span>
              </div>
              <div style={{fontSize: 11, color: "#94a3b8", marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <span>Used: {m.approvedUsed}d {m.pendingUsed > 0 ? `(${m.pendingUsed}d pending)` : ""}</span>
                {isProrated && <span style={{color: "#d97706", fontWeight: 600}}>*Prorated ({m.monthsWorked}m)</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16}}>
        <div style={{fontSize: 14, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 0.8}}>Request History ({filteredLeaves.length})</div>
        <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
          <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">All Leave Types</option>
            {leaveTypes.map(lt => <option key={lt.name} value={lt.name}>{lt.name}</option>)}
          </select>
          <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            <option value="all">All Years</option>
            {availableYears.map(yr => <option key={yr} value={yr}>{yr}</option>)}
          </select>
        </div>
      </div>

      {filteredLeaves.length === 0 ? (
        <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 32, textAlign: "center", color: "#64748b"}}>
          <div style={{fontSize: 32, marginBottom: 12}}>🌴</div>
          <div style={{fontWeight: 600, color: "#1e293b"}}>No Leave Requests Found</div>
          <div style={{fontSize: 13, marginTop: 4}}>{leaveRequests.length === 0 ? "You haven't requested any leaves yet." : "No records match the selected filters."}</div>
        </div>
      ) : (
        <div style={{display: "flex", flexDirection: "column", gap: 12}}>
          {filteredLeaves.map(req => {
            const statusBg = req.status === "approved" ? "#f1f5f9" : req.status === "rejected" ? "#fee2e2" : req.status === "cancelled" ? "#f8fafc" : "#fef9c3";
            const statusColor = req.status === "approved" ? "#334155" : req.status === "rejected" ? "#dc2626" : req.status === "cancelled" ? "#94a3b8" : "#b45309";
            const statusBorder = req.status === "approved" ? "1px solid #cbd5e1" : req.status === "rejected" ? "1px solid #fecaca" : req.status === "cancelled" ? "1px solid #e2e8f0" : "1px solid #fef08a";

            return (
              <div key={req.id} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div>
                  <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                    <span style={{fontWeight: 700, color: "#1e293b", fontSize: 15}}>{req.leave_type}</span>
                    <span style={{fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12, background: statusBg, color: statusColor, border: statusBorder, textTransform: "uppercase"}}>{req.status}</span>
                  </div>
                  <div style={{fontSize: 13, color: "#475569", fontWeight: 600}}>
                    {new Date(req.start_date).toLocaleDateString()} to {new Date(req.end_date).toLocaleDateString()} ({req.days} days)
                  </div>
                  {req.reason && <div style={{fontSize: 12, color: "#64748b", marginTop: 4}}>Reason: "{req.reason}"</div>}
                  {req.status === "rejected" && req.rejection_reason && (
                    <div style={{fontSize: 12, color: "#dc2626", marginTop: 4, fontWeight: 600}}>Note from Admin: "{req.rejection_reason}"</div>
                  )}
                  <div style={{fontSize: 11, color: "#94a3b8", marginTop: 4}}>
                    Applied on {new Date(req.created_at).toLocaleDateString()}
                    {req.reviewed_at && ` • Reviewed on ${new Date(req.reviewed_at).toLocaleDateString()}`}
                  </div>
                </div>
                {req.status === "pending" && (
                  <button className="btn btn-ghost btn-sm" style={{color: "#dc2626"}} onClick={() => handleCancel(req.id)}>Cancel Request</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showApplyModal && (
        <div className="overlay" style={{zIndex: 9999, alignItems: "flex-start", paddingTop: 50, overflowY: "auto"}} onClick={() => { setShowApplyModal(false); if (onResetApplyModal) onResetApplyModal(); }}>
          <div className="modal employee-leave-dialog" role="dialog" aria-modal="true" aria-label="Apply for Leave" style={{maxWidth: 480, margin: "0 auto", textAlign: "left"}} onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20}}>
              <div style={{fontSize: 18, fontWeight: 800, color: "#1e293b"}}>Apply for Leave</div>
              <button className="btn btn-ghost btn-xs" style={{fontSize: 20, padding: 0}} onClick={() => { setShowApplyModal(false); if (onResetApplyModal) onResetApplyModal(); }}>×</button>
            </div>
            
            {applyMsg && <div className="err-msg" style={{marginBottom: 16}}>⚠️ {applyMsg}</div>}
            
            <form onSubmit={handleApply} style={{display: "flex", flexDirection: "column", gap: 16}}>
              <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Leave Type</div>
                <select className="config-input" value={applyForm.type} onChange={e => setApplyForm({...applyForm, type: e.target.value})}>
                  <option value="">-- Select --</option>
                  {leaveTypes.map(lt => <option key={lt.name} value={lt.name}>{lt.name} (Bal: {getBalance(lt.name)})</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Start Date</div>
                  <input type="date" className="config-input" value={applyForm.startDate} min={new Date().toISOString().split("T")[0]} onChange={e => setApplyForm({...applyForm, startDate: e.target.value})} />
                </div>
                <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>End Date</div>
                  <input type="date" className="config-input" value={applyForm.endDate} min={applyForm.startDate || new Date().toISOString().split("T")[0]} onChange={e => setApplyForm({...applyForm, endDate: e.target.value})} />
                </div>
              </div>
              
              {applyForm.startDate && applyForm.endDate && (
                <div style={{background: "#f8fafc", padding: "12px 16px", borderRadius: 8, border: "1px dashed #cbd5e1", fontSize: 13, color: "#475569", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                  <div>
                    <span style={{fontWeight: 700, color: "#334155"}}>Working Days Deducted:</span>
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 2}}>Unselected weekend days & holidays are not deducted</div>
                  </div>
                  <strong style={{color: requestedDays > 0 ? "#1e293b" : "#ef4444", fontSize: 16}}>
                    {requestedDays} {requestedDays === 1 ? "day" : "days"}
                  </strong>
                </div>
              )}

              <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Reason (Optional)</div>
                <textarea className="config-input" value={applyForm.reason} onChange={e => setApplyForm({...applyForm, reason: e.target.value})} placeholder="Going out of town..." rows={3}></textarea>
              </div>

              <div style={{display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8}}>
                <button type="button" className="btn btn-ghost" onClick={() => { setShowApplyModal(false); if (onResetApplyModal) onResetApplyModal(); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={applyLoading}>{applyLoading ? "Submitting..." : "Submit Request"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company Leave Policy & Mandatory Rules Modal */}
      {showPolicyModal && (
        <div style={{position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(3px)"}}>
          <div style={{background: "#ffffff", borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", border: "1px solid #e2e8f0", padding: 28}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, borderBottom: "1.5px solid #f1f5f9", paddingBottom: 16}}>
              <div>
                <div style={{fontSize: 20, fontWeight: 900, color: "#0f172a", display: "flex", alignItems: "center", gap: 8}}>
                  <span>🏢</span>
                  <span>Company Leave Policy &amp; Entitlements ({currentYear})</span>
                </div>
                <div style={{fontSize: 13, color: "#64748b", marginTop: 4}}>
                  Official statutory &amp; company-mandated leave allotments configured by {company?.name || "the management"}.
                </div>
              </div>
              <button
                onClick={() => setShowPolicyModal(false)}
                style={{background: "#f1f5f9", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800}}
              >
                ×
              </button>
            </div>

            {/* Section 1: Leave Quotas */}
            <div style={{marginBottom: 24}}>
              <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6}}>
                <span>📊</span>
                <span>Mandatory Leave Quotas</span>
              </div>
              <div style={{border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden"}}>
                <table style={{width: "100%", borderCollapse: "collapse", fontSize: 13}}>
                  <thead>
                    <tr style={{background: "#f8fafc", borderBottom: "1px solid #e2e8f0"}}>
                      <th style={{padding: "10px 14px", textAlign: "left", fontWeight: 800, color: "#475569"}}>Leave Category</th>
                      <th style={{padding: "10px 14px", textAlign: "center", fontWeight: 800, color: "#475569"}}>Annual Allotment</th>
                      <th style={{padding: "10px 14px", textAlign: "left", fontWeight: 800, color: "#475569"}}>Rules &amp; Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveTypes.map((lt, idx) => (
                      <tr key={lt.name} style={{borderBottom: idx < leaveTypes.length - 1 ? "1px solid #f1f5f9" : "none"}}>
                        <td style={{padding: "12px 14px", fontWeight: 700, color: "#0f172a"}}>
                          {lt.name}
                        </td>
                        <td style={{padding: "12px 14px", textAlign: "center", fontWeight: 800, color: "#2563eb", fontSize: 14}}>
                          {lt.days} days / year
                        </td>
                        <td style={{padding: "12px 14px", color: "#64748b", fontSize: 12}}>
                          Credited per calendar year. Prorated proportionally if joined after January.
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 2: Working Days & Weekends */}
            <div style={{marginBottom: 24, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16}}>
              <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 6, display: "flex", alignItems: "center", gap: 6}}>
                <span>🗓️</span>
                <span>Work Schedule &amp; Deductions</span>
              </div>
              <div style={{fontSize: 12.5, color: "#475569", lineHeight: 1.6}}>
                • <strong>Working Days:</strong> {workingDays.map(d => ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][d]).join(", ")}
                <br />
                • <strong>Weekend &amp; Holiday Protection:</strong> When you apply for a continuous leave period (e.g. Friday to Monday), company holidays and non-working weekend days falling in between are <em>strictly excluded</em> and not deducted from your balance.
              </div>
            </div>

            {/* Section 3: Company Holidays Calendar */}
            <div>
              <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, display: "flex", alignItems: "center", gap: 6}}>
                <span>🎉</span>
                <span>Mandatory Company Holidays ({holidays.length} Days)</span>
              </div>
              {holidays.length === 0 ? (
                <div style={{padding: 16, background: "#f8fafc", borderRadius: 8, color: "#64748b", fontSize: 13, textAlign: "center"}}>
                  No company holidays configured yet for this calendar year.
                </div>
              ) : (
                <div style={{border: "1px solid #e2e8f0", borderRadius: 10, maxHeight: 220, overflowY: "auto"}}>
                  <table style={{width: "100%", borderCollapse: "collapse", fontSize: 12.5}}>
                    <thead style={{position: "sticky", top: 0, background: "#f8fafc"}}>
                      <tr style={{borderBottom: "1px solid #e2e8f0"}}>
                        <th style={{padding: "8px 12px", textAlign: "left", fontWeight: 800, color: "#475569"}}>Date</th>
                        <th style={{padding: "8px 12px", textAlign: "left", fontWeight: 800, color: "#475569"}}>Day</th>
                        <th style={{padding: "8px 12px", textAlign: "left", fontWeight: 800, color: "#475569"}}>Holiday Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.map((h, i) => {
                        const d = new Date(h.date + "T12:00:00");
                        const dayName = isNaN(d) ? "" : d.toLocaleDateString("en-US", { weekday: "long" });
                        return (
                          <tr key={i} style={{borderBottom: i < holidays.length - 1 ? "1px solid #f1f5f9" : "none"}}>
                            <td style={{padding: "8px 12px", fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap"}}>{h.date}</td>
                            <td style={{padding: "8px 12px", color: "#64748b"}}>{dayName}</td>
                            <td style={{padding: "8px 12px", fontWeight: 600, color: "#334155"}}>{h.name}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{display: "flex", justifyContent: "flex-end", marginTop: 24, paddingTop: 16, borderTop: "1px solid #f1f5f9"}}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowPolicyModal(false)}
                style={{padding: "8px 22px", fontSize: 13}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AttractiveWorkspaceLoader({ title = "TrustWork Payroll Software", subtitle = "Loading your workspace..." }) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: "radial-gradient(circle at 50% 40%, #ffffff 0%, #f8fafc 60%, #edf2f7 100%)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      position: "relative",
      overflow: "hidden"
    }}>
      <style>{`
        @keyframes loaderSpinOuter {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes loaderSpinInner {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes loaderOrbPulse {
          0%, 100% { transform: scale(0.92); opacity: 0.85; box-shadow: 0 0 20px 4px rgba(37, 99, 235, 0.4); }
          50% { transform: scale(1.08); opacity: 1; box-shadow: 0 0 35px 10px rgba(56, 189, 248, 0.65); }
        }
        @keyframes loaderTextBreathe {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        @keyframes workspaceLoadingSlide {
          from { transform: translateX(-100%); }
          to { transform: translateX(300%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .workspace-loading-text,
          .workspace-loading-bar {
            animation: none !important;
          }
        }
      `}</style>

      {/* Futuristic Concentric Dual-Ring Rotating Spinner with Pulsing Core */}
      <div style={{ position: "relative", width: 80, height: 80, marginBottom: 26 }}>
        {/* Outer Glowing Gradient Ring */}
        <div style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "4px solid transparent",
          borderTopColor: "#2563eb",
          borderRightColor: "#38bdf8",
          animation: "loaderSpinOuter 1.1s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite",
          filter: "drop-shadow(0 4px 10px rgba(37, 99, 235, 0.35))"
        }} />

        {/* Inner Counter-Rotating Ring */}
        <div style={{
          position: "absolute",
          inset: 10,
          borderRadius: "50%",
          border: "3px solid transparent",
          borderBottomColor: "#818cf8",
          borderLeftColor: "#c084fc",
          animation: "loaderSpinInner 1.4s cubic-bezier(0.4, 0.2, 0.4, 0.8) infinite"
        }} />

        {/* Center Pulsing Orb with Flash */}
        <div style={{
          position: "absolute",
          inset: 22,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 15,
          fontWeight: 900,
          animation: "loaderOrbPulse 1.8s ease-in-out infinite"
        }}>
          ✨
        </div>
      </div>

      {title && (
        <div style={{
          fontSize: 22,
          fontWeight: 900,
          background: "linear-gradient(135deg, #1e293b 0%, #2563eb 55%, #4f46e5 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          lineHeight: 1.2,
          marginBottom: 8,
          letterSpacing: "-0.3px",
          textAlign: "center"
        }}>
          {title}
        </div>
      )}

      <div role="status" className="workspace-loading-text" style={{
        fontSize: 12,
        fontWeight: 800,
        color: "#64748b",
        letterSpacing: "2px",
        textTransform: "uppercase",
        animation: "loaderTextBreathe 2s ease-in-out infinite",
        display: "flex",
        alignItems: "center",
        gap: 6
      }}>
        <span>{subtitle}</span>
      </div>
      <div aria-hidden="true" style={{
        width: 200,
        height: 4,
        marginTop: 18,
        borderRadius: 999,
        background: "#e2e8f0",
        overflow: "hidden"
      }}>
        <div className="workspace-loading-bar" style={{
          width: "40%",
          height: "100%",
          borderRadius: 999,
          background: "linear-gradient(90deg, #2563eb, #38bdf8)",
          animation: "workspaceLoadingSlide 1.4s ease-in-out infinite"
        }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE TEAM & CELEBRATIONS VIEW
// ─────────────────────────────────────────────────────────────────────────────
function EmployeeTeamAndCelebrationsView({ session, employee, company }) {
  const [teamEmps, setTeamEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("team"); // "team" | "celebrations"
  const [wishedMap, setWishedMap] = useState({});
  const [toast, setToast] = useState("");

  const showCelebrationToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4000);
  };

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.rpc('employee_team');
        if (error) throw error;
        if (data) setTeamEmps(data);
      } catch (e) {
        console.error("Failed to load team:", e);
      } finally {
        setLoading(false);
      }
    };
    if (session?.companyId) fetchTeam();
  }, [session?.companyId]);

  const reportingHierarchy = company?.config?.reporting_hierarchy || {};
  const photos = company?.config?.employee_photos || {};

  const myManagerId = reportingHierarchy[employee?.id] || (employee?.employee_code && reportingHierarchy[employee.employee_code]);
  const myManager = teamEmps.find(e => e.id === myManagerId || (e.employee_code && e.employee_code === myManagerId));

  const myReports = teamEmps.filter(e => {
    const mgr = reportingHierarchy[e.id] || reportingHierarchy[e.employee_code];
    return mgr === employee?.id || (employee?.employee_code && mgr === employee.employee_code);
  });

  const myPeers = teamEmps.filter(e => {
    if (e.id === employee?.id) return false;
    const mgr = reportingHierarchy[e.id] || reportingHierarchy[e.employee_code];
    if (myManagerId && mgr === myManagerId) return true;
    if (!myManagerId && employee?.department && e.department === employee.department) return true;
    return false;
  });

  const celebrations = useMemo(() => calcCelebrations(teamEmps), [teamEmps]);

  const getPhoto = (emp) => {
    return photos[emp.id] || (emp.employee_code ? photos[emp.employee_code] : "") || localStorage.getItem("tw_photo_" + emp.id) || (emp.employee_code ? localStorage.getItem("tw_photo_" + emp.employee_code) : "") || "";
  };

  const renderEmpAvatar = (emp, size = 44) => {
    const photo = getPhoto(emp);
    if (photo) {
      return (
        <img
          src={photo}
          alt={emp.name}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #e2e8f0",
            flexShrink: 0
          }}
        />
      );
    }
    return null;
  };

  const totalCelebrationsThisMonth = celebrations.birthdaysThisMonth.length + celebrations.birthdaysToday.length + celebrations.anniversariesThisMonth.length + celebrations.anniversariesToday.length;

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 16}}>
      {toast && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          background: "#1e293b",
          color: "#fff",
          padding: "12px 20px",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          fontSize: 13,
          fontWeight: 700,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 10,
          animation: "fadeIn 0.2s ease"
        }}>
          <span>🎉</span> <span>{toast}</span>
        </div>
      )}

      {/* Header and Sub-tabs */}
      <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12}}>
        <div>
          <div style={{fontSize: 17, fontWeight: 900, color: "#1e293b", display: "flex", alignItems: "center", gap: 8}}>
            <span>👥</span> My Team & Celebrations
          </div>
          <div style={{fontSize: 12, color: "#64748b", marginTop: 2}}>
            Your direct reporting line, teammates, and company-wide celebrations.
          </div>
        </div>

        <div style={{display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 10}}>
          <button
            onClick={() => setActiveTab("team")}
            style={{
              border: "none",
              background: activeTab === "team" ? "#fff" : "transparent",
              color: activeTab === "team" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 12.5,
              padding: "6px 14px",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: activeTab === "team" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span>👥</span> <span>My Team Line</span>
          </button>
          <button
            onClick={() => setActiveTab("celebrations")}
            style={{
              border: "none",
              background: activeTab === "celebrations" ? "#fff" : "transparent",
              color: activeTab === "celebrations" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 12.5,
              padding: "6px 14px",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: activeTab === "celebrations" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span>🎉</span> <span>Celebrations</span>
            {totalCelebrationsThisMonth > 0 && (
              <span style={{background: "#f59e0b", color: "#fff", fontSize: 10, padding: "1px 6px", borderRadius: 10}}>
                {totalCelebrationsThisMonth}
              </span>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 32, textAlign: "center", color: "#64748b", fontSize: 13}}>
          Loading team data...
        </div>
      ) : activeTab === "team" ? (
        /* ── SUB-TAB: TEAM ── */
        <div style={{display: "flex", flexDirection: "column", gap: 16}}>
          {/* Direct Manager Card */}
          <div style={{background: "#fff", border: "1.5px solid #bfdbfe", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(37,99,235,0.05)"}}>
            <div style={{fontSize: 11, fontWeight: 800, color: "#2563eb", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, display: "flex", alignItems: "center", gap: 6}}>
              <span>👔</span> Direct Reporting Manager
            </div>

            {myManager ? (
              <div style={{display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap"}}>
                {renderEmpAvatar(myManager, 56)}
                <div style={{flex: 1, minWidth: 200}}>
                  <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                    <span style={{fontSize: 16, fontWeight: 800, color: "#1e293b"}}>{myManager.name}</span>
                    {myManager.department && (
                      <span style={{fontSize: 11, fontWeight: 700, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "1px 8px", borderRadius: 12}}>
                        {myManager.department}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize: 13, color: "#64748b", marginTop: 3}}>
                    {myManager.designation || "Manager"} • ID: {myManager.employee_code || "N/A"}
                  </div>
                </div>

                <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                  {myManager.phone && (
                    <a href={`tel:${myManager.phone}`} className="btn btn-ghost btn-sm" style={{textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6}}>
                      <span>📞</span> <span>Call</span>
                    </a>
                  )}
                  {myManager.email && (
                    <a href={`mailto:${myManager.email}`} className="btn btn-ghost btn-sm" style={{textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6}}>
                      <span>✉️</span> <span>Email</span>
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div style={{fontSize: 13, color: "#64748b", background: "#f8fafc", padding: "14px 16px", borderRadius: 8, border: "1px solid #e2e8f0"}}>
                🏢 You currently report directly to Company Leadership / Head Office.
              </div>
            )}
          </div>

          {/* Direct Reports (if any) */}
          {myReports.length > 0 && (
            <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
              <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                <span>👥</span> Direct Reports 
                <span style={{background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10, border: "1px solid #bbf7d0"}}>
                  {myReports.length} {myReports.length === 1 ? "member" : "members"}
                </span>
              </div>
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12}}>
                {myReports.map(rep => (
                  <div key={rep.id} style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12}}>
                    {renderEmpAvatar(rep, 40)}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {rep.name}
                      </div>
                      <div style={{fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {rep.designation || "Team Member"}
                      </div>
                      <div style={{fontSize: 10.5, color: "#94a3b8", marginTop: 2}}>
                        ID: {rep.employee_code || "N/A"}
                      </div>
                    </div>
                    {rep.phone && (
                      <a href={`tel:${rep.phone}`} title={`Call ${rep.name}`} style={{fontSize: 14, textDecoration: "none", padding: "6px", borderRadius: 6, background: "#fff", border: "1px solid #cbd5e1"}}>
                        📞
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Peers / Colleagues */}
          <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
            <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
              <span>🤝</span> Team Colleagues & Peers
              <span style={{background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10}}>
                {myPeers.length} {myPeers.length === 1 ? "peer" : "peers"}
              </span>
            </div>
            {myPeers.length === 0 ? (
              <div style={{fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center"}}>
                No other teammates registered in this reporting line.
              </div>
            ) : (
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12}}>
                {myPeers.map(peer => (
                  <div key={peer.id} style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12}}>
                    {renderEmpAvatar(peer, 40)}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {peer.name}
                      </div>
                      <div style={{fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {peer.designation || peer.department || "Colleague"}
                      </div>
                      <div style={{fontSize: 10.5, color: "#94a3b8", marginTop: 2}}>
                        ID: {peer.employee_code || "N/A"}
                      </div>
                    </div>
                    {peer.phone && (
                      <a href={`tel:${peer.phone}`} title={`Call ${peer.name}`} style={{fontSize: 14, textDecoration: "none", padding: "6px", borderRadius: 6, background: "#fff", border: "1px solid #cbd5e1"}}>
                        📞
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── SUB-TAB: CELEBRATIONS ── */
        <div style={{display: "flex", flexDirection: "column", gap: 16}}>
          {/* Today's Spotlight (if any) */}
          {(celebrations.birthdaysToday.length > 0 || celebrations.anniversariesToday.length > 0) && (
            <div style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              border: "1.5px solid #f59e0b",
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 4px 14px rgba(245,158,11,0.15)"
            }}>
              <div style={{fontSize: 15, fontWeight: 900, color: "#92400e", display: "flex", alignItems: "center", gap: 8, marginBottom: 12}}>
                <span>🎉</span> Today's Celebrations!
              </div>
              <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                {celebrations.birthdaysToday.map(b => (
                  <div key={b.id} style={{background: "rgba(255,255,255,0.85)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10}}>
                      {renderEmpAvatar(b, 38)}
                      <div>
                        <div style={{fontWeight: 800, fontSize: 14, color: "#1e293b"}}>🎂 {b.name}</div>
                        <div style={{fontSize: 11.5, color: "#92400e", fontWeight: 600}}>Celebrating birthday today!</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setWishedMap(p => ({ ...p, [b.id]: true }));
                        showCelebrationToast(`Sent Happy Birthday wishes to ${b.name}! 🎂`);
                      }}
                      className="btn btn-sm"
                      style={{
                        background: wishedMap[b.id] ? "#10b981" : "#f59e0b",
                        color: "#fff",
                        border: "none",
                        fontWeight: 800,
                        borderRadius: 8
                      }}
                    >
                      {wishedMap[b.id] ? "✓ Wished! ❤️" : "🎉 Wish"}
                    </button>
                  </div>
                ))}
                {celebrations.anniversariesToday.map(a => (
                  <div key={a.id} style={{background: "rgba(255,255,255,0.85)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10}}>
                      {renderEmpAvatar(a, 38)}
                      <div>
                        <div style={{fontWeight: 800, fontSize: 14, color: "#1e293b"}}>🎖️ {a.name}</div>
                        <div style={{fontSize: 11.5, color: "#92400e", fontWeight: 600}}>Celebrating {a.yearsCompleted} years at the company today!</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setWishedMap(p => ({ ...p, [a.id]: true }));
                        showCelebrationToast(`Sent congrats to ${a.name} for ${a.yearsCompleted} years milestone! 🎖️`);
                      }}
                      className="btn btn-sm"
                      style={{
                        background: wishedMap[a.id] ? "#10b981" : "#f59e0b",
                        color: "#fff",
                        border: "none",
                        fontWeight: 800,
                        borderRadius: 8
                      }}
                    >
                      {wishedMap[a.id] ? "✓ Wished! ❤️" : "🎖️ Congratulate"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Birthdays This Month */}
          <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
            <div style={{fontSize: 13.5, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
              <span>🎂</span> Birthdays This Month
              <span style={{background: "#fdf2f8", color: "#db2777", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10, border: "1px solid #fbcfe8"}}>
                {celebrations.birthdaysThisMonth.length + celebrations.birthdaysToday.length}
              </span>
            </div>
            {celebrations.birthdaysThisMonth.length === 0 && celebrations.birthdaysToday.length === 0 ? (
              <div style={{fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center"}}>
                No birthdays remaining this month.
              </div>
            ) : (
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12}}>
                {[...celebrations.birthdaysToday, ...celebrations.birthdaysThisMonth].map(b => (
                  <div key={b.id} style={{background: b.isToday ? "#fffbeb" : "#f8fafc", border: `1px solid ${b.isToday ? "#fde68a" : "#e2e8f0"}`, borderRadius: 10, padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1}}>
                      {renderEmpAvatar(b, 40)}
                      <div style={{minWidth: 0}}>
                        <div style={{fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                          {b.name}
                        </div>
                        <div style={{fontSize: 11, color: "#64748b"}}>
                          {b.department || b.designation || "Team Member"}
                        </div>
                        <div style={{fontSize: 10.5, fontWeight: 700, color: b.isToday ? "#d97706" : "#2563eb", marginTop: 2}}>
                          {b.isToday ? "🎉 Today!" : b.daysDiff === 1 ? "Tomorrow!" : `On ${MONTHS[b.bMonth].slice(0,3)} ${b.bDay} (in ${b.daysDiff} days)`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setWishedMap(p => ({ ...p, [b.id]: true }));
                        showCelebrationToast(`Sent birthday wishes to ${b.name}! 🎂`);
                      }}
                      className="btn btn-sm"
                      style={{
                        background: wishedMap[b.id] ? "#10b981" : "#eff6ff",
                        color: wishedMap[b.id] ? "#fff" : "#2563eb",
                        border: "1px solid #bfdbfe",
                        fontWeight: 700,
                        fontSize: 11.5,
                        borderRadius: 8,
                        padding: "5px 10px"
                      }}
                    >
                      {wishedMap[b.id] ? "✓" : "🎉 Wish"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Work Anniversaries This Month */}
          <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
            <div style={{fontSize: 13.5, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
              <span>🎖️</span> Work Anniversaries This Month
              <span style={{background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10, border: "1px solid #bbf7d0"}}>
                {celebrations.anniversariesThisMonth.length + celebrations.anniversariesToday.length}
              </span>
            </div>
            {celebrations.anniversariesThisMonth.length === 0 && celebrations.anniversariesToday.length === 0 ? (
              <div style={{fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center"}}>
                No work anniversaries remaining this month.
              </div>
            ) : (
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12}}>
                {[...celebrations.anniversariesToday, ...celebrations.anniversariesThisMonth].map(a => (
                  <div key={a.id} style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1}}>
                      {renderEmpAvatar(a, 40)}
                      <div style={{minWidth: 0}}>
                        <div style={{fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                          {a.name}
                        </div>
                        <div style={{fontSize: 11, color: "#16a34a", fontWeight: 700}}>
                          🎖️ {a.yearsCompleted} {a.yearsCompleted === 1 ? "Year" : "Years"} at Company
                        </div>
                        <div style={{fontSize: 10.5, color: "#64748b", marginTop: 2}}>
                          {a.isToday ? "🎉 Today!" : `On ${MONTHS[a.jMonth].slice(0,3)} ${a.jDay}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setWishedMap(p => ({ ...p, [a.id]: true }));
                        showCelebrationToast(`Sent congratulations to ${a.name}! 🎖️`);
                      }}
                      className="btn btn-sm"
                      style={{
                        background: wishedMap[a.id] ? "#10b981" : "#f0fdf4",
                        color: wishedMap[a.id] ? "#fff" : "#16a34a",
                        border: "1px solid #bbf7d0",
                        fontWeight: 700,
                        fontSize: 11.5,
                        borderRadius: 8,
                        padding: "5px 10px"
                      }}
                    >
                      {wishedMap[a.id] ? "✓" : "🎖️ Congratulate"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* New Hires */}
          {celebrations.newHires.length > 0 && (
            <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
              <div style={{fontSize: 13.5, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                <span>👋</span> Welcome New Teammates
                <span style={{background: "#eff6ff", color: "#2563eb", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10, border: "1px solid #bfdbfe"}}>
                  {celebrations.newHires.length}
                </span>
              </div>
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12}}>
                {celebrations.newHires.map(n => (
                  <div key={n.id} style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12}}>
                    {renderEmpAvatar(n, 40)}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {n.name}
                      </div>
                      <div style={{fontSize: 11, color: "#64748b"}}>
                        {n.designation || n.department || "New Joiner"}
                      </div>
                      <div style={{fontSize: 10.5, color: "#2563eb", fontWeight: 700, marginTop: 2}}>
                        Joined {n.daysSinceJoin === 0 ? "Today" : `${n.daysSinceJoin} days ago`} ({fmtDate(n.doj || n.date_of_joining)})
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmployeePortal({ session, onLogout, onInstallApp, onSessionChange }) {
  const [slips, setSlips]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [authError, setAuthError] = useState("");
  const [company, setCompany]       = useState(null);
  const [employee, setEmployee]     = useState(null);
  const [view, setView]             = useState("profile"); // "profile" | "slips" | "change_password"
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [certRequests, setCertRequests] = useState([]);
  const [showCertModal, setShowCertModal] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toastMsg, setToastMsg] = useState("");

  const fetchRequests = useCallback(async () => {
    if (!session?.employeeId) return;
    try {
      const verified = await resumeEmployeeSession(session);
      const emp = attachEmployeeSession(verified.employee, session.employeeSessionToken);
      const [{ data: reqs }, { data: leaves }] = await Promise.all([
        supabase.from("employee_update_requests")
          .select("*")
          .eq("employee_id", session.employeeId)
          .order("created_at", { ascending: false }),
        supabase.from("employee_leaves")
          .select("*")
          .eq("employee_id", session.employeeId)
          .order("created_at", { ascending: false })
      ]);
      const profilePending = (reqs || []).find(r => r.requested_data?.request_type !== "certificate" && r.status === "pending");
      setPendingRequest(profilePending || null);
      setCertRequests((reqs || []).filter(r => r.requested_data?.request_type === "certificate"));
      setLeaveRequests(leaves || []);
      if (emp) {
        setEmployee(prev => {
          const photo = prev?.emp_photo || (emp ? (emp.emp_photo || "") : "");
          return attachEmployeeSession({ ...(prev || {}), ...emp, ...(photo ? { emp_photo: photo } : {}) }, session.employeeSessionToken);
        });
      }
      // If a profile update is pending approval OR if requests were approved, clear any stale draft from storage
      if (profilePending || (reqs || []).some(r => r.requested_data?.request_type !== "certificate" && r.status === "approved")) {
        try {
          localStorage.removeItem(`trustwork_jk_draft_${session.employeeId}`);
        } catch (e) {}
      }
    } catch (err) {
      setAuthError(err.message);
    }
  }, [session]);

  const handleRequestCertificate = async (data) => {
    try {
      const { error } = await supabase.from("employee_update_requests").insert({
        company_id: session.companyId,
        employee_id: session.employeeId,
        requested_data: {
          request_type: "certificate",
          cert_type: data.cert_type,
          purpose: data.purpose,
          addressed_to: data.addressed_to,
          requested_date: new Date().toISOString()
        },
        status: "pending"
      });
      if (error) throw error;
      setToastMsg("Certificate request submitted to HR!");
      setTimeout(() => setToastMsg(""), 3500);
      await fetchRequests();
      logAudit(session, "EMPLOYEE_REQUESTED_CERTIFICATE", { cert_type: data.cert_type, purpose: data.purpose });
    } catch (e) {
      alert("Failed to submit request: " + e.message);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const verified = await resumeEmployeeSession(session);
        const emp = attachEmployeeSession(verified.employee, session.employeeSessionToken);
        setAuthError("");
        // Load company
        const { data: co, error: companyError } = await supabase.rpc('employee_company_context');
        if (companyError) throw companyError;
        const dbConfig = (typeof co?.config === 'object' && co.config !== null) ? co.config : {};
        // Employee views use only the server-filtered configuration, never an
        // administrator's full cached company record on a shared browser.
        const localConfig = {};
        const mergedCoConfig = {
          ...DEFAULT_CONFIG,
          ...localConfig,
          ...dbConfig,
          leave_module_enabled: dbConfig.leave_module_enabled !== undefined ? dbConfig.leave_module_enabled : (localConfig.leave_module_enabled !== undefined ? localConfig.leave_module_enabled : false),
          leave_types: dbConfig.leave_types || localConfig.leave_types || DEFAULT_CONFIG.leave_types,
          company_holidays: dbConfig.company_holidays || localConfig.company_holidays || DEFAULT_CONFIG.company_holidays,
          working_days_schedule: dbConfig.working_days_schedule || localConfig.working_days_schedule || DEFAULT_CONFIG.working_days_schedule,
          attendance_enabled: dbConfig.attendance_enabled !== undefined ? dbConfig.attendance_enabled : (localConfig.attendance_enabled !== undefined ? localConfig.attendance_enabled : false),
          attendance_punch_in: dbConfig.attendance_punch_in || localConfig.attendance_punch_in || DEFAULT_CONFIG.attendance_punch_in,
          attendance_punch_out: dbConfig.attendance_punch_out || localConfig.attendance_punch_out || DEFAULT_CONFIG.attendance_punch_out,
          attendance_grace_minutes: dbConfig.attendance_grace_minutes !== undefined ? dbConfig.attendance_grace_minutes : (localConfig.attendance_grace_minutes !== undefined ? localConfig.attendance_grace_minutes : DEFAULT_CONFIG.attendance_grace_minutes),
          attendance_eod_cutoff: dbConfig.attendance_eod_cutoff || localConfig.attendance_eod_cutoff || DEFAULT_CONFIG.attendance_eod_cutoff,
          org_culture_enabled: dbConfig.org_culture_enabled !== undefined ? dbConfig.org_culture_enabled : (localConfig.org_culture_enabled !== undefined ? localConfig.org_culture_enabled : false),
          training_enabled: dbConfig.training_enabled === true,
          recruitment_enabled: dbConfig.recruitment_enabled !== undefined ? dbConfig.recruitment_enabled : (localConfig.recruitment_enabled !== undefined ? localConfig.recruitment_enabled : false),
          offboarding_enabled: dbConfig.offboarding_enabled !== undefined ? dbConfig.offboarding_enabled : (localConfig.offboarding_enabled !== undefined ? localConfig.offboarding_enabled : false),
          reports_enabled: dbConfig.reports_enabled !== undefined ? dbConfig.reports_enabled : (localConfig.reports_enabled !== undefined ? localConfig.reports_enabled : true),
        };

        // ── STRICT PRIVACY & DATA ISOLATION FOR EMPLOYEE PORTAL ──
        // 1. Isolate salary hikes: strip all other employees' revision histories from memory
        const myHikes = (mergedCoConfig.salary_hikes && mergedCoConfig.salary_hikes[session.employeeId]) 
          ? mergedCoConfig.salary_hikes[session.employeeId] 
          : [];
        mergedCoConfig.salary_hikes = {
          [session.employeeId]: myHikes
        };

        // Interview assignments come from the scoped RPC, never the HR candidate mirror.
        delete mergedCoConfig.recruitment;
        // 2. Strip sensitive company admin login credentials from client memory
        if (co) {
          delete co.login_password;
          delete co.login_username;
        }

        const photo = dbConfig.employee_photos?.[session.employeeId] || dbConfig.employee_photos?.[emp?.employee_code] || localStorage.getItem("tw_photo_" + session.employeeId) || (emp?.employee_code ? localStorage.getItem("tw_photo_" + emp.employee_code) : "") || "";
        const docs = dbConfig.employee_documents?.[session.employeeId] || dbConfig.employee_documents?.[emp?.employee_code] || {};
        setEmployee(emp ? attachEmployeeSession({ ...emp, ...docs, emp_photo: photo }, session.employeeSessionToken) : null);
        setCompany(co ? { ...co, config: mergedCoConfig } : null);
        if (mergedCoConfig?.logo_base64) cacheLogoImage(mergedCoConfig.logo_base64);
        // Load locked salary records only
        const { data: recs } = await supabase.from("salary_records")
          .select("*")
          .eq("employee_id", session.employeeId)
          .eq("status", "locked")
          .order("year", { ascending: false })
          .order("month", { ascending: false });
        setSlips(recs || []);
        // Load requests
        await fetchRequests();
      } catch(e) { setAuthError(e.message); }
      setLoading(false);
    };
    load();
  }, [session, fetchRequests]);

  useEffect(() => {
    if ((view === "profile" || view === "leaves") && session.employeeId) {
      fetchRequests();
    }
  }, [view, session.employeeId, fetchRequests]);

  // Live polling every 6 seconds + instant auto-refresh on mobile/desktop tab or app switch
  useEffect(() => {
    if (!session.employeeId) return;

    const refreshOnActive = () => {
      if (document.visibilityState === "visible") {
        fetchRequests();
      }
    };

    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchRequests();
      }
    }, 6000);

    window.addEventListener("focus", refreshOnActive);
    document.addEventListener("visibilitychange", refreshOnActive);
    window.addEventListener("pageshow", refreshOnActive);

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refreshOnActive);
      document.removeEventListener("visibilitychange", refreshOnActive);
      window.removeEventListener("pageshow", refreshOnActive);
    };
  }, [session.employeeId, fetchRequests]);

  const guardedLogout = () => {
    if (window.dispatchEvent(new Event('trustwork:before-employee-navigate', {cancelable:true}))) onLogout();
  };
  const navigateView = (newView) => {
    if (newView !== view && !window.dispatchEvent(new Event('trustwork:before-employee-navigate', {cancelable:true}))) return;
    setView(newView);
    if (newView === "profile") {
      setIsEditingProfile(false);
    }
    if (newView === "leaves" || newView === "profile") {
      fetchRequests();
    }
  };


  const handleDownloadSlip = async (rec) => {
    if (!employee || !company) return;
    const emp = {
      name: employee.name, pan: employee.pan, doj: employee.doj,
      uan: employee.uan, esi_id: employee.esi_id,
      employee_code: employee.employee_code,
    };
    const sal = {
      month: rec.month, year: rec.year,
      basic: rec.basic, hra: rec.hra, da: rec.da,
      special: rec.special_allowance, conveyance: rec.conveyance,
      medical: rec.medical, bonus: rec.bonus, ot_pay: rec.ot_amount,
      gross: rec.gross, pf_emp: rec.pf_employee, pf_er: rec.pf_employer,
      esi_emp: rec.esi_employee, esi_er: rec.esi_employer,
      pt: rec.professional_tax, absence_deduction: rec.absence_deduction,
      vpf: rec.vpf, tds: rec.tds, net: rec.net_pay, ctc: rec.ctc,
      absent_days: rec.days_absent, ot_hours: rec.ot_hours,
    };
    await exportPDF(emp, sal, company);
    logAudit(session, "EMPLOYEE_DOWNLOAD_SALARY", { month: rec.month, year: rec.year });
  };

  const fmtINR = n => "\u20b9" + Math.round(parseFloat(n)||0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (authError) return <div className="login-page"><div className="login-card"><div className="login-title">Sign in required</div><p role="alert">{authError}</p><button type="button" className="login-btn" onClick={onLogout}>Back to sign in</button></div></div>;

  if (loading) return (
    <AttractiveWorkspaceLoader
      title={company?.name ? `${company.name} • Employee Portal` : "Employee Workspace"}
      subtitle="Loading your workspace..."
    />
  );

  return (
    <div className="company-layout-wrapper employee-portal">
      {toastMsg && (
        <div style={{position:"fixed", top:"50%", left:"50%", transform:"translate(-50%, -50%)", background:"rgba(0,0,0,0.85)", color:"#fff", padding:"16px 24px", borderRadius:12, zIndex:1000, fontWeight:700, fontSize:15, boxShadow:"0 10px 30px rgba(0,0,0,0.3)", display:"flex", alignItems:"center", gap:12}}>
          ✅ {toastMsg}
        </div>
      )}
      <div className="company-sidebar employee-sidebar" style={{ width: sidebarOpen ? 225 : 60, transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)", overflow: "hidden", borderRight: sidebarOpen ? "1px solid #e2e8f0" : "none" }}>
        <div className="company-sidebar-header" style={{ opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.2s" }}>
          <div className="company-sidebar-title" style={{display:"flex", alignItems:"center", gap:6}}>
            {company?.config?.logo_base64 ? <img src={company.config.logo_base64} alt="Logo" style={{width: 20, height: 20, borderRadius: 4, objectFit:"contain"}} /> : "💼"}
            Employee Portal
          </div>
        </div>
        <div className="company-nav" style={{ opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.2s", pointerEvents: sidebarOpen ? "auto" : "none" }}>
          <button className={`company-nav-item employee-nav-item ${view==="profile"?"active":""}`} onClick={()=>navigateView("profile")}>
            <span className="company-nav-icon">👤</span> 
            <span style={{display: "inline-flex", alignItems: "center", justifyContent: "space-between", flex: 1}}>
              <span>My Profile</span>
              {pendingRequest && (
                <span style={{fontSize: 9.5, background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: 8, fontWeight: 700}}>
                  Pending
                </span>
              )}
            </span>
          </button>
          <button className={`company-nav-item employee-nav-item ${view==="my_salary"?"active":""}`} onClick={()=>navigateView("my_salary")}>
            <span className="company-nav-icon">💰</span> <span>My Salary</span>
          </button>
          {(company?.config?.org_culture_enabled === true) && (
            <button className={`company-nav-item employee-nav-item ${view==="team"?"active":""}`} onClick={()=>navigateView("team")}>
              <span className="company-nav-icon">👥</span> <span>My Team</span>
            </button>
          )}
          {(company?.config?.leave_module_enabled === true) && (
            <button className={`company-nav-item employee-nav-item ${view==="leaves"?"active":""}`} onClick={()=>navigateView("leaves")}>
              <span className="company-nav-icon">🏖️</span> <span>Leaves</span>
            </button>
          )}
          {(company?.config?.attendance_enabled === true) && (
            <button className={`company-nav-item employee-nav-item ${view==="attendance"?"active":""}`} onClick={()=>navigateView("attendance")}>
              <span className="company-nav-icon">🕐</span> <span>Attendance</span>
            </button>
          )}
          {company?.config?.training_enabled === true && (
            <button className={`company-nav-item employee-nav-item ${view === 'training' ? 'active' : ''}`} onClick={() => navigateView('training')}>
              <span className="company-nav-icon">🎓</span><span>My Training</span>
            </button>
          )}
          {company?.config?.recruitment_enabled === true && (
            <button className={`company-nav-item employee-nav-item ${view === 'interviews' ? 'active' : ''}`} onClick={() => navigateView('interviews')}>
              <span className="company-nav-icon">📋</span><span>My Interviews</span>
            </button>
          )}
          {company?.config?.recruitment_enabled === true && <button className={`company-nav-item employee-nav-item ${view==='referrals'?'active':''}`} onClick={()=>navigateView('referrals')}><span className="company-nav-icon">🤝</span><span>My Referrals</span></button>}
          <button className={`company-nav-item employee-nav-item ${view==="slips"?"active":""}`} onClick={()=>navigateView("slips")}>
            <span className="company-nav-icon">📄</span> <span>Salary Slips</span>
          </button>
          {(company?.config?.offboarding_enabled === true && (company?.config?.fnf_settlements?.[employee.id] || employee.date_of_exit || employee.employment_status === "resigned" || employee.employment_status === "relieved")) && (
            <button className={`company-nav-item employee-nav-item ${view==="fnf"?"active":""}`} onClick={()=>navigateView("fnf")}>
              <span className="company-nav-icon">📜</span> <span>Exit & Settlement</span>
            </button>
          )}
          <button className={`company-nav-item employee-nav-item mobile-only-nav ${view==="change_password"?"active":""}`} onClick={()=>navigateView("change_password")}>
            <span className="company-nav-icon">🔑</span> <span>Change password</span>
          </button>
        </div>
        <div className="employee-sidebar-bottom" style={{marginTop:"auto", padding:"16px 20px", borderTop:"1px solid #e2e8f0", display:"flex", flexDirection:"column", gap:12}}>
          {!isPwaMode() && isMobileDevice() && onInstallApp && (
            <button 
              type="button" 
              onClick={onInstallApp}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "8px 14px", borderRadius: 8, border: "1.5px solid #bfdbfe",
                background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: 13,
                cursor: "pointer", transition: "all 0.2s"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#dbeafe"}
              onMouseLeave={e => e.currentTarget.style.background = "#eff6ff"}
            >
              <span>📲</span> Install App
            </button>
          )}
          <button className={`company-nav-item employee-nav-item desktop-only-nav ${view==="change_password"?"active":""}`} onClick={()=>navigateView("change_password")} style={{padding: "8px 14px"}}>
            <span className="company-nav-icon">🔑</span> Change password
          </button>
          <button className="desktop-only-nav" style={{color:"#ef4444", fontSize: 13, fontWeight: 700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent: "center", gap: 8, padding:"8px 24px", background:"transparent", border:"1px solid #fca5a5", borderRadius:20, transition:"all 0.2s ease"}} onClick={guardedLogout}
             onMouseEnter={e=>{e.currentTarget.style.background="#fef2f2"; e.currentTarget.style.transform="scale(1.02)";}}
             onMouseLeave={e=>{e.currentTarget.style.background="transparent"; e.currentTarget.style.transform="scale(1)";}}>
            <span style={{fontSize:14}}>🚪</span> Log Out
          </button>
        </div>
      </div>
      <button 
        className="sidebar-toggle-float employee-sidebar-toggle-float"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          position: "absolute", top: "50%", left: (sidebarOpen ? 225 : 60) - 14, transform: "translateY(-50%)",
          width: 28, height: 28, borderRadius: "50%", background: "#fff", border: "1px solid #e2e8f0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", zIndex: 10, color: "#64748b", fontSize: 16, fontWeight: 800, transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
        onMouseEnter={e => { e.currentTarget.style.color = "#2563eb"; e.currentTarget.style.borderColor = "#bfdbfe"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.15)"; e.currentTarget.style.transform = "translateY(-50%) scale(1.1)"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
      >
        {sidebarOpen ? '«' : '»'}
      </button>
      <div className="company-main-content employee-main">
        <div className="company-header-bar" style={{justifyContent: "space-between"}}>
          <div style={{display:"flex", alignItems:"center", gap: 12, paddingRight: 16, flexWrap: "wrap"}}>
            <div style={{background:"#f8fafc", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700, color: "#475569", border: "1px solid #cbd5e1", display: "flex", alignItems: "center", gap: 6}}>
              🏢 {company?.name || "Company"}
            </div>
            <div style={{width:1, height: 24, background: "#e2e8f0", margin: "0 8px"}}></div>
            <div style={{display:"flex", alignItems:"center", gap: 12, flexWrap: "wrap"}}>
              {(() => {
                const photo = employee?.emp_photo || localStorage.getItem("tw_photo_" + session.employeeId) || (session.employeeCode ? localStorage.getItem("tw_photo_" + session.employeeCode) : "") || "";
                return photo ? (
                  <img src={photo} alt={session.employeeName} style={{width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #3b82f6", boxShadow: "0 1px 4px rgba(0,0,0,0.1)"}} />
                ) : (
                  null
                );
              })()}
              <span style={{fontSize: 15, fontWeight: 800, color: "#0f172a"}}>{session.employeeName}</span>
              <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                <span style={{fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9", padding: "4px 10px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                  ID: {session.employeeCode || "N/A"}
                </span>
                {employee?.date_of_joining && (
                  <span style={{fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9", padding: "4px 10px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                    📅 Joined: {formatDateDDMMYYYY(employee.date_of_joining)}
                  </span>
                )}
                {employee?.phone && (
                  <span style={{fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9", padding: "4px 10px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                    📞 {employee.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="company-header-right" style={{display: "flex", alignItems: "center", gap: 6, flexShrink: 0}}>
            {!isPwaMode() && isMobileDevice() && onInstallApp && (
              <button 
                type="button" 
                onClick={onInstallApp}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "5px 10px", borderRadius: 16, border: "1.5px solid #bfdbfe",
                  background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: 11,
                  cursor: "pointer", transition: "all 0.2s ease", flexShrink: 0
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#dbeafe"}
                onMouseLeave={e => e.currentTarget.style.background = "#eff6ff"}
                title="Install TrustWork App on this device"
              >
                <span>📲</span> <span>Install</span>
              </button>
            )}
            <button 
              className="mobile-only-logout" 
              title="Log Out"
              aria-label="Log Out"
              style={{
                color: "#ef4444", 
                width: 32, 
                height: 32, 
                minWidth: 32,
                cursor: "pointer", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                padding: 0, 
                background: "#fef2f2", 
                border: "1px solid #fca5a5", 
                borderRadius: "50%",
                fontSize: 15,
                flexShrink: 0,
                transition: "all 0.2s ease"
              }} 
              onClick={guardedLogout}
              onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.transform = "scale(1.05)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.transform = "scale(1)"; }}
            >
              🚪
            </button>
          </div>
        </div>

        <div className="company-main-scroll employee-scroll">
          <div style={{maxWidth: 1200, width: "100%"}}>
            
            {view === "profile" && (
              <div>
                {!isEditingProfile ? (
                  <div>
                    {/* Top Header */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 12,
                      marginBottom: 18
                    }}>
                      <div>
                        <div style={{fontSize: 18, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8}}>
                          <span>👤</span> My Profile Overview
                        </div>
                        <div style={{fontSize: 12.5, color: "#64748b", marginTop: 2}}>
                          View your verified personal details, banking, and statutory records.
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setIsEditingProfile(true);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        style={{
                          padding: "9px 18px",
                          fontSize: 13.5,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          borderRadius: 8,
                          boxShadow: "0 2px 6px rgba(37,99,235,0.2)"
                        }}
                      >
                        {pendingRequest ? "✏️ Modify Submitted Request" : "✏️ Edit Profile Details"}
                      </button>
                    </div>

                    {/* Pending Request Alert Card */}
                    {pendingRequest && (
                      <div style={{
                        background: "#fefce8",
                        border: "1.5px solid #fef08a",
                        borderRadius: 12,
                        padding: "16px 20px",
                        marginBottom: 20,
                        boxShadow: "0 2px 8px rgba(234,179,8,0.08)"
                      }}>
                        <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap"}}>
                          <div style={{flex: 1, minWidth: 260}}>
                            <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                              <span style={{fontSize: 18}}>⏳</span>
                              <span style={{fontSize: 15, fontWeight: 800, color: "#854d0e"}}>Profile Updates Pending HR Approval</span>
                              <span style={{fontSize: 11, fontWeight: 700, background: "#fef08a", color: "#713f12", padding: "2px 8px", borderRadius: 10, textTransform: "uppercase"}}>
                                In Review
                              </span>
                            </div>
                            <div style={{fontSize: 13, color: "#713f12", lineHeight: 1.5}}>
                              You submitted updates on <strong>{fmtDate(pendingRequest.created_at)}</strong>. These changes are awaiting review and approval by your HR / Company Administrator.
                            </div>
                            {pendingRequest.requested_data && Object.keys(pendingRequest.requested_data).length > 0 && (
                              <div style={{marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center"}}>
                                <span style={{fontSize: 12, fontWeight: 700, color: "#854d0e"}}>Submitted changes for:</span>
                                {Object.keys(pendingRequest.requested_data).map(k => (
                                  <span key={k} style={{fontSize: 11, background: "#ffffff", border: "1px solid #fde047", color: "#854d0e", padding: "2px 8px", borderRadius: 6, fontWeight: 600}}>
                                    {k.replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setIsEditingProfile(true);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            style={{
                              background: "#854d0e",
                              color: "#fff",
                              border: "none",
                              padding: "8px 14px",
                              borderRadius: 8,
                              fontSize: 12.5,
                              fontWeight: 700,
                              cursor: "pointer",
                              alignSelf: "flex-start"
                            }}
                          >
                            Modify Request
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Employee Hero Card */}
                    <div style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 14,
                      padding: "20px 24px",
                      marginBottom: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 16,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                    }}>
                      <div style={{display: "flex", alignItems: "center", gap: 18}}>
                        {employee?.emp_photo ? (
                          <img
                            src={employee.emp_photo}
                            alt={employee?.name || "Photo"}
                            style={{width: 64, height: 64, flexShrink: 0, borderRadius: "50%", objectFit: "cover", border: "2.5px solid #3b82f6", boxShadow: "0 2px 8px rgba(0,0,0,0.1)"}}
                          />
                        ) : (
                          null
                        )}
                        <div>
                          <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                            <h2 style={{fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0}}>
                              {employee?.name || "Employee"}
                            </h2>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              background: "#dcfce7",
                              color: "#166534",
                              padding: "3px 9px",
                              borderRadius: 12
                            }}>
                              {employee?.employment_status || "Active"}
                            </span>
                          </div>
                          <div style={{fontSize: 13, color: "#475569", marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                            <span style={{fontWeight: 700, color: "#2563eb"}}>{employee?.employee_code || "EMP"}</span>
                            <span>•</span>
                            <span>{employee?.designation || "Designation"}</span>
                            {employee?.department && (
                              <>
                                <span>•</span>
                                <span>{employee.department}</span>
                              </>
                            )}
                          </div>
                          <div style={{fontSize: 12, color: "#64748b", marginTop: 4}}>
                            Joined: <strong>{fmtDate(employee?.date_of_joining || employee?.doj)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Profile Detail Sections */}
                    <div style={{display: "flex", flexDirection: "column", gap: 20}}>
                      {/* Section 1: Personal & Contact */}
                      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)"}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                          <span>👤</span> Personal & Contact Information
                        </div>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: 14}}>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Date of Birth</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{fmtDate(employee?.date_of_birth || employee?.dob)}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Gender</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.gender || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Marital Status</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.marital_status || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Blood Group</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.blood_group || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Father's Name</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.father_name || "—"}</div>
                          </div>
                          {employee?.spouse_name && (
                            <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                              <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Spouse Name</div>
                              <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee.spouse_name}</div>
                            </div>
                          )}
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Mobile Phone</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.phone || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Email</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.email || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Emergency Contact</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>
                              {[employee?.emergency_name, employee?.emergency_relation ? `(${employee.emergency_relation})` : null, employee?.emergency_phone].filter(Boolean).join(" ") || "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Statutory & KYC */}
                      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)"}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                          <span>🏛️</span> Statutory & Identification Details
                        </div>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: 14}}>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>PAN Number</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.pan ? String(employee.pan).toUpperCase() : "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Aadhaar (Masked)</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>
                              {employee?.aadhaar_last4 ? `•••• •••• ${employee.aadhaar_last4}` : (employee?.aadhaar ? `•••• •••• ${String(employee.aadhaar).slice(-4)}` : "—")}
                            </div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Universal Account No (UAN)</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.uan || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>ESI IP Number</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.esi_id || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>PF Account Number</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.pf_account_number || employee?.pf_account || "—"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Section 3: Bank Details */}
                      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)"}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                          <span>🏦</span> Bank Account Information
                        </div>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))", gap: 14}}>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Bank Name</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.bank_name || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Account Number</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>
                              {employee?.bank_account_number || employee?.bank_account || "—"}
                            </div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>IFSC Code</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.bank_ifsc || "—"}</div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Branch</div>
                            <div style={{fontSize: 13.5, fontWeight: 600, color: "#1e293b", marginTop: 2}}>{employee?.bank_branch || "—"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Section 4: Addresses */}
                      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)"}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                          <span>📍</span> Address Details
                        </div>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 14}}>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 4}}>Present Address</div>
                            <div style={{fontSize: 13, color: "#1e293b", lineHeight: 1.5}}>
                              {[employee?.temp_address || employee?.current_address, employee?.temp_city, employee?.temp_state, employee?.temp_pincode].filter(Boolean).join(", ") || "—"}
                            </div>
                          </div>
                          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px"}}>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: 4}}>Permanent Address</div>
                            <div style={{fontSize: 13, color: "#1e293b", lineHeight: 1.5}}>
                              {[employee?.perm_address || employee?.permanent_address, employee?.perm_city, employee?.perm_state, employee?.perm_pincode].filter(Boolean).join(", ") || "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section 5: Verification Documents */}
                      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)"}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                          <span>📑</span> Verification Documents
                        </div>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))", gap: 12}}>
                          {[
                            { label: "Profile Photo", url: employee?.emp_photo },
                            { label: "Aadhaar (Front)", url: employee?.emp_aadhaar_front_doc || employee?.emp_aadhaar_doc },
                            { label: "Aadhaar (Back)", url: employee?.emp_aadhaar_back_doc },
                            { label: "PAN Document", url: employee?.emp_pan_doc },
                            { label: "Bank Proof", url: employee?.emp_bank_doc },
                          ].map(doc => (
                            <div key={doc.label} style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}>
                              <div style={{fontSize: 12, fontWeight: 600, color: "#334155"}}>{doc.label}</div>
                              {doc.url ? (
                                <a href={doc.url} target="_blank" rel="noreferrer noopener" style={{fontSize: 11.5, fontWeight: 700, color: "#2563eb", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4}}>
                                  <span>✅ View</span>
                                </a>
                              ) : (
                                <span style={{fontSize: 11, color: "#94a3b8", fontWeight: 500}}>Not uploaded</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Callout / Edit Action */}
                    <div style={{
                      background: "#f8fafc",
                      border: "1.5px dashed #cbd5e1",
                      borderRadius: 12,
                      padding: "18px 24px",
                      marginTop: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 14
                    }}>
                      <div>
                        <div style={{fontWeight: 800, fontSize: 14, color: "#0f172a"}}>Need to update your profile or joining details?</div>
                        <div style={{fontSize: 12.5, color: "#64748b", marginTop: 2}}>
                          Submit changes to your bank account, contact numbers, or documents for HR verification.
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setIsEditingProfile(true);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        style={{
                          padding: "9px 18px",
                          fontSize: 13.5,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          cursor: "pointer"
                        }}
                      >
                        {pendingRequest ? "✏️ Modify Submitted Request" : "✏️ Edit Profile Details"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Editing Mode Header */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 16,
                      flexWrap: "wrap",
                      gap: 12
                    }}>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingProfile(false);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="btn btn-ghost"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#2563eb",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          padding: "8px 14px",
                          borderRadius: 8,
                          cursor: "pointer"
                        }}
                      >
                        ← Back to Profile Overview
                      </button>
                      <div style={{fontSize: 12.5, color: "#64748b"}}>
                        Submitting or canceling will return you to your Profile Overview
                      </div>
                    </div>

                    {pendingRequest && (
                      <div style={{background: "#fefce8", border: "1px solid #fef08a", borderRadius: 12, padding: "14px 18px", marginBottom: 20}}>
                        <div style={{fontSize: 14, fontWeight: 800, color: "#a16207", marginBottom: 2}}>⏳ Notice: Pending Request Active</div>
                        <div style={{fontSize: 12.5, color: "#854d0e"}}>
                          You have a pending update request submitted on {fmtDate(pendingRequest.created_at)}. Submitting new changes will update the pending request awaiting HR review.
                        </div>
                      </div>
                    )}

                    <div style={{background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "14px 18px", marginBottom: 20}}>
                      <div style={{fontSize: 14, fontWeight: 800, color: "#1e40af", marginBottom: 2}}>Hello, {employee?.name}! 👋</div>
                      <div style={{fontSize: 12.5, color: "#1e3a8a"}}>
                        Please update your details below. Fields with a lock (🔒) are set by HR and cannot be modified here.
                      </div>
                    </div>

                    <EmployeeSelfServiceForm
                      employee={employee}
                      pendingRequest={pendingRequest}
                      onClose={() => {
                        setIsEditingProfile(false);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      onSave={async (updatedData) => {
                        setSavingPin(true);
                        try { localStorage.removeItem(`trustwork_jk_draft_${session.employeeId}`); } catch(e) {}
                        if (updatedData.bank_ifsc && !isValidIFSC(updatedData.bank_ifsc)) {
                          setSavingPin(false);
                          alert("Invalid IFSC Code format. Please enter a valid 11-character IFSC (e.g. SBIN0001234, 5th character must be 0).");
                          return;
                        }
                        const accNo = updatedData.bank_account_number || updatedData.bank_account;
                        if (accNo && !isValidBankAccount(accNo)) {
                          setSavingPin(false);
                          alert("Invalid Bank Account Number. Indian bank accounts must be between 9 and 18 digits.");
                          return;
                        }
                        if (updatedData.uan && !isValidUAN(updatedData.uan)) {
                          setSavingPin(false);
                          alert("Invalid UAN. Universal Account Number must be exactly 12 numeric digits, or leave it blank if unknown.");
                          return;
                        }

                        let photoUpdated = false;
                        if (updatedData.emp_photo !== undefined && updatedData.emp_photo !== employee?.emp_photo) {
                          photoUpdated = true;
                          const newPhoto = updatedData.emp_photo;
                          setEmployee(prev => attachEmployeeSession({ ...prev, emp_photo: newPhoto }, session.employeeSessionToken));
                          const { error: photoError } = await supabase.rpc('employee_save_documents', { p_documents: { emp_photo: newPhoto } });
                          if (photoError) { setSavingPin(false); alert(photoError.message); return; }
                        }

                        let docsUpdated = false;
                        const docKeys = ['emp_aadhaar_front_doc', 'emp_aadhaar_back_doc', 'emp_aadhaar_doc', 'emp_pan_doc', 'emp_bank_doc'];
                        const changedDocs = {};
                        for (const dk of docKeys) {
                          if (updatedData[dk] !== undefined && updatedData[dk] !== employee?.[dk]) {
                            docsUpdated = true;
                            changedDocs[dk] = updatedData[dk];
                          }
                        }
                        if (docsUpdated) {
                          setEmployee(prev => attachEmployeeSession({ ...prev, ...changedDocs }, session.employeeSessionToken));
                          const { error: documentsError } = await supabase.rpc('employee_save_documents', { p_documents: changedDocs });
                          if (documentsError) { setSavingPin(false); alert(documentsError.message); return; }
                        }

                        const changedData = {};
                        const excludeKeys = ['current_address', 'permanent_address', 'dob', 'pf_account', 'bank_account', 'emp_photo', 'emp_aadhaar_front_doc', 'emp_aadhaar_back_doc', 'emp_aadhaar_doc', 'emp_pan_doc', 'emp_bank_doc', 'specially_abled', 'id', 'company_id', 'created_at', 'updated_at', 'is_active', 'status', 'employee_code', 'prev_esi'];
                        for (let key in updatedData) {
                          if (excludeKeys.includes(key)) continue;
                          const oldVal = employee[key];
                          const newVal = updatedData[key];
                          if (typeof newVal === 'object' && newVal !== null) {
                            const oStr = (oldVal === null || oldVal === undefined) ? "[]" : JSON.stringify(oldVal);
                            const nStr = JSON.stringify(newVal);
                            if (nStr !== oStr) changedData[key] = newVal;
                          } else {
                            const oStr = (oldVal === null || oldVal === undefined) ? "" : String(oldVal);
                            const nStr = (newVal === null || newVal === undefined) ? "" : String(newVal);
                            if (nStr !== oStr) changedData[key] = newVal;
                          }
                        }
                        
                        if (Object.keys(changedData).length === 0) {
                          setSavingPin(false);
                          if (photoUpdated || docsUpdated) {
                            try { localStorage.removeItem(`trustwork_jk_draft_${session.employeeId}`); } catch(e) {}
                            setToastMsg(photoUpdated && docsUpdated ? "Profile photo & documents updated successfully!" : (photoUpdated ? "Profile photo updated successfully!" : "Documents updated successfully!"));
                            setTimeout(() => setToastMsg(""), 3000);
                            setIsEditingProfile(false);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            return;
                          }
                          alert("No changes detected to submit.");
                          return;
                        }

                        if (pendingRequest) {
                          const { error } = await supabase.from("employee_update_requests").update({
                            requested_data: changedData,
                            status: 'pending',
                            created_at: new Date().toISOString()
                          }).eq("id", pendingRequest.id);
                          setSavingPin(false);
                          if (error) alert("Failed to submit request: " + error.message);
                          else {
                            try { localStorage.removeItem(`trustwork_jk_draft_${session.employeeId}`); } catch(e) {}
                            setToastMsg("Profile update submitted for HR approval!");
                            setTimeout(() => setToastMsg(""), 3000);
                            setPendingRequest({ ...pendingRequest, requested_data: changedData });
                            logAudit(session, "EMPLOYEE_PROFILE_UPDATE_REQUESTED", { request_id: pendingRequest.id });
                            setIsEditingProfile(false);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        } else {
                          const { data, error } = await supabase.from("employee_update_requests").insert({
                            company_id: session.companyId,
                            employee_id: session.employeeId,
                            requested_data: changedData,
                            status: 'pending'
                          }).select().single();
                          setSavingPin(false);
                          if (error) alert("Failed to submit request: " + error.message);
                          else {
                            try { localStorage.removeItem(`trustwork_jk_draft_${session.employeeId}`); } catch(e) {}
                            setToastMsg("Profile update submitted for HR approval!");
                            setTimeout(() => setToastMsg(""), 3000);
                            setPendingRequest(data);
                            logAudit(session, "EMPLOYEE_PROFILE_UPDATE_REQUESTED", { request_id: data.id });
                            setIsEditingProfile(false);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {view === "my_salary" && (() => {
              const monthlyGross = employee?.basic_salary || 0;
              const annualGross = monthlyGross * 12;
              const sim = calcSalaryFull({ basic: monthlyGross, month: 1, year: new Date().getFullYear(), cfg: company?.config || DEFAULT_CONFIG, emp_id: employee?.id, extra: employee });
              const monthlyCTC = sim.ctc || monthlyGross;
              const annualCTC = monthlyCTC * 12;
              const hikes = company?.config?.salary_hikes?.[employee?.id] || [];

              return (
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#334155",marginBottom:14,textTransform:"uppercase",letterSpacing:0.8}}>💰 My Salary Overview</div>
                  <div className="employee-salary-summary-cards" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 180px), 1fr))",gap:12}}>
                    <div className="employee-salary-summary-card" style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                      <div style={{fontSize:11,color:"#64748b",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Monthly Gross Salary</div>
                      <div style={{fontSize:20,fontWeight:800,color:"#1e293b"}}>{fmtINR(monthlyGross)}</div>
                    </div>
                    <div className="employee-salary-summary-card" style={{background:"#fff",border:"1px solid #bae6fd",borderRadius:10,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                      <div style={{fontSize:11,color:"#0284c7",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Monthly CTC</div>
                      <div style={{fontSize:20,fontWeight:800,color:"#0369a1"}}>{fmtINR(monthlyCTC)}</div>
                    </div>
                    <div className="employee-salary-summary-card" style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                      <div style={{fontSize:11,color:"#64748b",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Annual Gross Salary</div>
                      <div style={{fontSize:20,fontWeight:800,color:"#1e293b"}}>{fmtINR(annualGross)}</div>
                    </div>
                    <div className="employee-salary-summary-card" style={{background:"#fff",border:"1px solid #bbf7d0",borderRadius:10,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                      <div style={{fontSize:11,color:"#15803d",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Annual CTC (Estimated)</div>
                      <div style={{fontSize:20,fontWeight:800,color:"#16a34a"}}>{fmtINR(annualCTC)}</div>
                    </div>
                  </div>

                  {/* Monthly Structure Breakdown */}
                  <div style={{marginTop:24,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:20,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#334155",marginBottom:14,textTransform:"uppercase",letterSpacing:0.8}}>📊 Monthly Earnings Breakdown</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 160px), 1fr))",gap:12}}>
                      <div style={{background:"#f8fafc",padding:"10px 14px",borderRadius:8,border:"1px solid #f1f5f9"}}>
                        <div style={{fontSize:11,color:"#64748b",fontWeight:600}}>Basic Salary</div>
                        <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginTop:2}}>{fmtINR(sim.basic)}</div>
                      </div>
                      <div style={{background:"#f8fafc",padding:"10px 14px",borderRadius:8,border:"1px solid #f1f5f9"}}>
                        <div style={{fontSize:11,color:"#64748b",fontWeight:600}}>House Rent Allowance (HRA)</div>
                        <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginTop:2}}>{fmtINR(sim.hra)}</div>
                      </div>
                      {sim.da > 0 && (
                        <div style={{background:"#f8fafc",padding:"10px 14px",borderRadius:8,border:"1px solid #f1f5f9"}}>
                          <div style={{fontSize:11,color:"#64748b",fontWeight:600}}>Dearness Allowance (DA)</div>
                          <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginTop:2}}>{fmtINR(sim.da)}</div>
                        </div>
                      )}
                      {sim.special_allowance > 0 && (
                        <div style={{background:"#f8fafc",padding:"10px 14px",borderRadius:8,border:"1px solid #f1f5f9"}}>
                          <div style={{fontSize:11,color:"#64748b",fontWeight:600}}>Special Allowance</div>
                          <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginTop:2}}>{fmtINR(sim.special_allowance)}</div>
                        </div>
                      )}
                      {sim.bonus > 0 && (
                        <div style={{background:"#f8fafc",padding:"10px 14px",borderRadius:8,border:"1px solid #f1f5f9"}}>
                          <div style={{fontSize:11,color:"#64748b",fontWeight:600}}>Statutory Bonus Component</div>
                          <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginTop:2}}>{fmtINR(sim.bonus)}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Career & Salary Revision Progression */}
                  <div style={{marginTop:24,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:20,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:16}}>
                      <div style={{fontSize:13,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:0.8}}>📈 Career & Compensation Progression</div>
                      {(employee?.date_of_joining || employee?.doj) && (
                        <span style={{fontSize:12,color:"#64748b",background:"#f8fafc",padding:"4px 10px",borderRadius:6,border:"1px solid #e2e8f0"}}>
                          Joined on <strong>{fmtDate(employee.date_of_joining || employee.doj)}</strong>
                        </span>
                      )}
                    </div>

                    {hikes.length === 0 ? (
                      <div style={{fontSize:12.5,color:"#64748b",padding:"14px 18px",background:"#f8fafc",borderRadius:8,border:"1px dashed #cbd5e1"}}>
                        Starting compensation is <strong>{fmtINR(monthlyGross)}/month</strong>. As salary appraisals or revisions are finalized by HR, your career progression timeline will be displayed here.
                      </div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",paddingLeft:12,paddingTop:8}}>
                        {sortSalaryRevisions(hikes).map((h, idx) => (
                          <div key={idx} className="hike-timeline-item">
                            <div className="hike-timeline-dot" />
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                              <strong style={{fontSize:13.5,color:"#1e293b"}}>{h.note || "Salary Revision"}</strong>
                              <span style={{
                                fontSize:11,
                                fontWeight:800,
                                padding:"1px 8px",
                                borderRadius:10,
                                background: h.hike_pct >= 0 ? "#f0fdf4" : "#fef2f2",
                                color: h.hike_pct >= 0 ? "#16a34a" : "#dc2626",
                                border: `1px solid ${h.hike_pct >= 0 ? "#bbf7d0" : "#fecaca"}`
                              }}>
                                {h.hike_pct >= 0 ? `+${h.hike_pct}%` : `${h.hike_pct}%`}
                              </span>
                              <span style={{fontSize:11.5,color:"#94a3b8"}}>Effective {fmtDate(h.effective_date || h.date)}</span>
                            </div>
                            <div style={{fontSize:12.5,color:"#475569",marginTop:4}}>
                              Revised from <span style={{textDecoration:"line-through",color:"#94a3b8"}}>{fmtINR(h.old_salary)}</span> to <strong style={{color:"#16a34a"}}>{fmtINR(h.new_salary)}/mo</strong>
                            </div>
                            <div style={{marginTop: 6}}>
                              <button
                                type="button"
                                onClick={() => generateIncrementLetterPDF({ employee, company, hikeRecord: h })}
                                style={{display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", fontSize: 11, background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontWeight: 600, color: "#1e293b"}}
                              >
                                <span>📄</span> Download Increment Letter
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Official Corporate Letters & Certificates */}
                  <div style={{marginTop:24,background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:20,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:12}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:0.8}}>📜 Official Letters & Certificates</div>
                        <div style={{fontSize:12,color:"#64748b",marginTop:2}}>Generate appointment contracts or request certified salary/employment proofs for bank loans and visas.</div>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button
                          type="button"
                          onClick={() => generateAppointmentLetterPDF(employee, company, employee.appointment_ref_no || employee.ref_no || "")}
                          style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 12px",background:"#f8fafc",border:"1.5px solid #cbd5e1",borderRadius:8,color:"#334155",fontWeight:700,fontSize:12,cursor:"pointer"}}
                        >
                          <span>📄</span> Download Appointment Letter
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowCertModal(true)}
                          style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 12px",background:"#eff6ff",border:"1.5px solid #93c5fd",borderRadius:8,color:"#1d4ed8",fontWeight:700,fontSize:12,cursor:"pointer"}}
                        >
                          <span>🏛️</span> Request Certificate
                        </button>
                      </div>
                    </div>

                    {certRequests.length > 0 && (
                      <div style={{marginTop:14,borderTop:"1px solid #f1f5f9",paddingTop:12,display:"flex",flexDirection:"column",gap:10}}>
                        <div style={{fontSize:11.5,fontWeight:700,color:"#475569",textTransform:"uppercase"}}>Recent Certificate Requests</div>
                        {certRequests.map(req => {
                          const d = req.requested_data || {};
                          const isApproved = req.status === "approved";
                          const isRejected = req.status === "rejected";
                          return (
                            <div key={req.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"#f8fafc",borderRadius:8,border:"1px solid #e2e8f0",flexWrap:"wrap",gap:8}}>
                              <div>
                                <div style={{fontWeight:700,fontSize:13,color:"#0f172a"}}>
                                  {d.cert_type === "salary" ? "Salary Certificate" : (d.cert_type === "employment" ? "Bonafide Employment Certificate" : "Combined Salary & Employment Certificate")}
                                </div>
                                <div style={{fontSize:11.5,color:"#64748b",marginTop:2}}>
                                  Purpose: <strong>{d.purpose || "Verification"}</strong> {d.addressed_to ? `• Addressed to: ${d.addressed_to}` : ""} • Requested {fmtDate(req.created_at)}
                                </div>
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                {isApproved ? (
                                  <button
                                    type="button"
                                    onClick={() => generateCertificatePDF({
                                      employee,
                                      company,
                                      certType: d.cert_type,
                                      purpose: d.purpose,
                                      addressedTo: d.addressed_to,
                                      issueDate: req.reviewed_at
                                    })}
                                    style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:"#dcfce7",border:"1px solid #86efac",borderRadius:6,color:"#15803d",fontWeight:700,fontSize:12,cursor:"pointer"}}
                                  >
                                    <span>📥</span> Download Certificate PDF
                                  </button>
                                ) : isRejected ? (
                                  <span style={{fontSize:12,fontWeight:700,color:"#dc2626",background:"#fee2e2",padding:"3px 8px",borderRadius:6}}>
                                    ❌ Rejected
                                  </span>
                                ) : (
                                  <span style={{fontSize:12,fontWeight:700,color:"#d97706",background:"#fef3c7",padding:"3px 8px",borderRadius:6}}>
                                    ⏳ Pending HR Approval
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {view === "slips" && (
              <div>
                <style>{`
                  .slip-year-summary {
                    padding: 12px 18px;
                    font-weight: 800;
                    font-size: 14.5px;
                    color: #1e293b;
                    cursor: pointer;
                    background: #f8fafc;
                    user-select: none;
                  }
                  .slip-group-body {
                    padding: 12px 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                  }
                  .slip-card-row {
                    background: #fff;
                    border-radius: 10px;
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justifyContent: space-between;
                    gap: 10px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.03);
                    transition: all .2s;
                  }
                  .slip-card-row:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    transform: translateY(-1px);
                  }
                  .slip-icon-badge {
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    background: #eff6ff;
                    display: flex;
                    align-items: center;
                    justifyContent: center;
                    font-size: 16px;
                    flex-shrink: 0;
                  }
                  .slip-metrics-line {
                    font-size: 11.5px;
                    color: #64748b;
                    margin-top: 2px;
                    display: flex;
                    align-items: center;
                    gap: 8px 12px;
                    flex-wrap: wrap;
                  }
                  .slip-dl-btn {
                    padding: 6px 14px !important;
                    font-size: 11.5px !important;
                    border-radius: 7px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                    flex-shrink: 0 !important;
                    white-space: nowrap !important;
                  }
                  @media (max-width: 640px) {
                    .slip-year-summary {
                      padding: 10px 14px !important;
                      font-size: 13.5px !important;
                    }
                    .slip-group-body {
                      padding: 10px 10px !important;
                      gap: 6px !important;
                    }
                    .slip-card-row {
                      padding: 8px 10px !important;
                      gap: 8px !important;
                      border-radius: 9px !important;
                    }
                    .slip-icon-badge {
                      width: 28px !important;
                      height: 28px !important;
                      font-size: 14px !important;
                      border-radius: 6px !important;
                    }
                    .slip-title-text {
                      font-size: 13px !important;
                    }
                    .slip-metrics-line {
                      font-size: 10.5px !important;
                      gap: 4px 6px !important;
                      margin-top: 1px !important;
                    }
                    .slip-dl-btn {
                      padding: 5px 8px !important;
                      font-size: 11px !important;
                    }
                  }
                `}</style>
                <div style={{fontSize:13.5,fontWeight:800,color:"#334155",marginBottom:14,textTransform:"uppercase",letterSpacing:0.8}}>📄 Salary Slip History</div>
                {slips.length === 0 ? (
                  <div style={{background:"#fff",borderRadius:12,padding:36,textAlign:"center",border:"1px solid #e2e8f0",color:"#94a3b8"}}>
                    <div style={{fontSize:36,marginBottom:8}}>📭</div>
                    <div style={{fontWeight:600,fontSize:14}}>No finalized payslips yet.</div>
                    <div style={{fontSize:12,marginTop:4}}>Your HR admin will publish payslips after running and locking payroll.</div>
                  </div>
                ) : (
                  (() => {
                    const groupedSlips = slips.reduce((acc, slip) => {
                      if (!acc[slip.year]) acc[slip.year] = [];
                      acc[slip.year].push(slip);
                      return acc;
                    }, {});
                    const sortedYears = Object.keys(groupedSlips).sort((a,b)=>b-a);
                    const currentYear = new Date().getFullYear();
                    
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:12}}>
                        {sortedYears.map(year => (
                          <details key={year} open={Number(year) === currentYear || sortedYears.length === 1} style={{background:"#fff", border:"1px solid #e2e8f0", borderRadius: 10, overflow: "hidden"}}>
                            <summary className="slip-year-summary">
                              Year {year}
                            </summary>
                            <div className="slip-group-body">
                              {groupedSlips[year].map(rec => (
                                <div key={rec.id} className="slip-card-row">
                                  <div style={{display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1}}>
                                    <div className="slip-icon-badge">📄</div>
                                    <div style={{minWidth: 0, flex: 1}}>
                                      <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                                        <span className="slip-title-text" style={{fontWeight: 800, fontSize: 14, color: "#1e293b"}}>
                                          {MONTHS[rec.month]} {rec.year}
                                        </span>
                                        <span style={{fontSize: 11, fontWeight: 800, color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "1px 6px", borderRadius: 4}}>
                                          Net: {fmtINR(rec.net_pay)}
                                        </span>
                                      </div>
                                      <div className="slip-metrics-line">
                                        <span>Gross: <strong style={{color: "#334155"}}>{fmtINR(rec.gross)}</strong></span>
                                        <span style={{color: "#cbd5e1"}}>•</span>
                                        <span>Ded: <strong style={{color: "#dc2626"}}>{fmtINR((rec.pf_employee||0)+(rec.esi_employee||0)+(rec.professional_tax||0)+(rec.vpf||0)+(rec.tds||0))}</strong></span>
                                      </div>
                                    </div>
                                  </div>
                                  <button onClick={()=>handleDownloadSlip(rec)} className="btn btn-primary slip-dl-btn">
                                    <span>⬇</span> <span>PDF</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            )}

            {view === "team" && (company?.config?.org_culture_enabled === true) && (
              <EmployeeTeamAndCelebrationsView session={session} employee={employee} company={company} />
            )}

            {view === "leaves" && (company?.config?.leave_module_enabled === true) && (
              <EmployeeLeavesView company={company} employee={employee} leaveRequests={leaveRequests} fetchRequests={fetchRequests} session={session} />
            )}

            {view === "attendance" && (company?.config?.attendance_enabled === true) && (
              <EmployeeAttendanceView company={company} employee={employee} session={session} />
            )}

            {view === 'training' && company?.config?.training_enabled === true && employee && <EmployeeTraining key={employee.id} employee={employee} />}

            {view === 'interviews' && company?.config?.recruitment_enabled === true && employee && <EmployeeInterviews employee={employee} />}
            {view === 'referrals' && company?.config?.recruitment_enabled === true && employee && <EmployeeReferrals key={employee.id} employee={employee} />}

            {view === "fnf" && (company?.config?.offboarding_enabled === true) && (
              <EmployeeExitAndFnFView company={company} employee={employee} session={session} />
            )}

            {view === "change_password" && <PasswordSetup requireCurrent workspace={{kind:'company',companyName:company?.name}} onDone={message=>{if(message)alert(message);onLogout();}}/>}
          </div>
        </div>
      </div>
      {showCertModal && (
        <CertificateModal
          isOpen={showCertModal}
          onClose={() => setShowCertModal(false)}
          employee={employee}
          company={company}
          mode="employee_request"
          onSubmit={handleRequestCertificate}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── MAIN APP
// ─────────────────────────────────────────────────────────────────────────────

// ── PWA DETECTION & INSTALL GUIDE
export const isPwaMode = () => {
  if (typeof window === "undefined") return false;
  const isStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const isNavStandalone = window.navigator && window.navigator.standalone === true;
  const params = new URLSearchParams(window.location.search);
  return !!(isStandalone || isNavStandalone || params.get("mode") === "employee" || params.get("pwa") === "true");
};

export function isMobileDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || window.opera || "";
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua);
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isTouchWithSmallScreen = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0)) && (window.innerWidth <= 820);
  return !!(isMobileUA || isIPadOS || isTouchWithSmallScreen);
}

export function PwaInstallGuideModal({ isOpen, onClose, appName = "TrustWork" }) {
  if (!isOpen) return null;
  const isIOS = typeof navigator !== "undefined" && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const isAndroid = typeof navigator !== "undefined" && /Android/.test(navigator.userAgent);

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(15, 23, 42, 0.7)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 99999, padding: 16
    }} onClick={onClose}>
      <div style={{
        background: "#ffffff", borderRadius: 20, maxWidth: 440, width: "100%",
        padding: "26px 22px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        position: "relative"
      }} onClick={e => e.stopPropagation()}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14}}>
          <div style={{display: "flex", alignItems: "center", gap: 10}}>
            <div style={{width: 40, height: 40, borderRadius: 12, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22}}>
              📲
            </div>
            <div>
              <div style={{fontWeight: 900, fontSize: 17, color: "#0f172a"}}>Install {appName} App</div>
              <div style={{fontSize: 12, color: "#64748b", fontWeight: 600}}>Fast 1-tap employee access</div>
            </div>
          </div>
          <button onClick={onClose} style={{background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", padding: 4}}>✕</button>
        </div>

        <div style={{background: "#f8fafc", borderRadius: 14, padding: "16px", border: "1px solid #e2e8f0", marginBottom: 18, fontSize: 13.5, lineHeight: 1.6, color: "#334155"}}>
          {isIOS ? (
            <div>
              <div style={{fontWeight: 800, color: "#1e293b", marginBottom: 8, display: "flex", alignItems: "center", gap: 6}}>
                🍏 iOS / Safari (iPhone &amp; iPad):
              </div>
              <ol style={{paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6}}>
                <li>Tap the <strong>Share button (⎋)</strong> at the bottom of Safari.</li>
                <li>Scroll down and tap <strong>"Add to Home Screen" ➕</strong>.</li>
                <li>Tap <strong>"Add"</strong> in the top-right corner.</li>
              </ol>
            </div>
          ) : isAndroid ? (
            <div>
              <div style={{fontWeight: 800, color: "#1e293b", marginBottom: 8, display: "flex", alignItems: "center", gap: 6}}>
                🤖 Android / Chrome:
              </div>
              <ol style={{paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6}}>
                <li>Tap the <strong>three dots menu (⋮)</strong> in Chrome.</li>
                <li>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
                <li>Confirm installation when prompted.</li>
              </ol>
            </div>
          ) : (
            <div>
              <div style={{fontWeight: 800, color: "#1e293b", marginBottom: 8}}>
                💻 Desktop / Other Browsers:
              </div>
              <div style={{marginBottom: 8}}>
                In Chrome / Edge, click the <strong>Install icon (⊕)</strong> on the right side of the address bar at the top, or select <strong>"Install {appName}"</strong> from the browser menu.
              </div>
              <div style={{fontSize: 12, color: "#64748b", marginTop: 6}}>
                💡 On iPhone, open in Safari and tap Share (⎋) &gt; "Add to Home Screen".
              </div>
            </div>
          )}
        </div>

        <button onClick={onClose} style={{
          width: "100%", padding: "12px", background: "#2563eb", color: "#ffffff",
          border: "none", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer",
          boxShadow: "0 4px 12px rgba(37,99,235,0.25)"
        }}>
          Got it
        </button>
      </div>
    </div>
  );
}

// ── LOGIN PAGE
function LoginPage({ onLogin, companies, onInstallApp, workspace, notice }) {
  const appName = workspace.kind === 'company' ? workspace.companyName : 'TrustWork';
  const isPwa = isPwaMode();
  const [tab, setTab] = useState(() => {
    if (isPwa) return "employee";
    try {
      const savedTab = localStorage.getItem("tw_login_tab");
      if (savedTab === "admin" || savedTab === "employee") return savedTab;
      const hasPan = localStorage.getItem("tw_remember_employee_email");
      const hasUser = localStorage.getItem("tw_remember_username");
      if (hasPan && !hasUser) return "employee";
    } catch (e) {}
    return "admin";
  });
  // Admin login state
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem("tw_remember_username") || ""; } catch (e) { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Employee login state
  const [empEmail, setEmpEmail] = useState(() => {
    try { return localStorage.getItem("tw_remember_employee_email") || ""; } catch (e) { return ""; }
  });
  const [empPassword, setEmpPassword] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [err, setErr] = useState("");
  const [empLoading, setEmpLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  async function forgotPassword() {
    setErr(''); setResetMessage(''); setResetBusy(true);
    try { setResetMessage(await requestPasswordReset(isPwa || tab === 'employee' ? empEmail : username, isPwa || tab === 'employee')); }
    catch(error) { setErr(error.message); }
    finally { setResetBusy(false); }
  }

  const handleAdminLogin = async (e) => {
    e?.preventDefault();
    if (isPwa) {
      setErr("Admin login is disabled in the Employee App.");
      return;
    }
    if (!username.trim() || !password.trim()) { setErr("Please enter both username and password"); return; }
    const u = username.trim();
    const p = password;

    setErr('');
    try {
      const session = await signInAdmin(u, p);
      localStorage.removeItem('tw_admin_pw');
      await recordSuccessfulAdminLogin(supabase, session.companyId, session.login_username);
      onLogin(session);
    } catch (error) { setErr(error.message); }

  };

  const handleEmployeeLogin = async (e) => {
    e?.preventDefault();
    if (!empEmail.trim() || !empPassword.trim()) { setErr("Please enter your email and password"); return; }
    setEmpLoading(true); setErr("");
    try {
      await signOutAdmin();
      const employeeSession = await employeeLogin(empEmail, empPassword);
      localStorage.setItem('tw_current_user', JSON.stringify(employeeSession));
      if (!employeeSession.passwordChangeRequired) await syncFileSession(employeeSession);
      try { localStorage.setItem("tw_remember_employee_email", employeeSession.email); localStorage.setItem("tw_login_tab", "employee"); } catch (error) {}
      await recordSuccessfulEmployeeLogin(supabase, employeeSession.companyId, employeeSession.employeeId, employeeSession.email);
      setEmpPassword("");
      onLogin(employeeSession);
    } catch (error) { setErr(error.message); }
    finally { setEmpLoading(false); }
  };

  return (
    <div className="login-page">
      <PwaInstallGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} appName={appName}/>
      <div className="login-card" style={{maxWidth: 420}}>
        <div className="login-logo">
          {workspace.logo ? <img src={workspace.logo} alt={`${appName} logo`} style={{maxWidth:180,maxHeight:80,objectFit:'contain',marginBottom:14}}/> : workspace.kind === 'platform' ? <div className="login-logo-icon">{isPwa ? "📱" : "💼"}</div> : null}
          <div className="login-title">{appName} {isPwa ? 'Employee App' : 'Payroll'}</div>
          {workspace.kind === 'company' && <div style={{fontSize:11,color:'#64748b',marginTop:8}}>Powered by TrustWork</div>}
          {isPwa && (
            <div style={{fontSize: 12, fontWeight: 700, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "4px 12px", borderRadius: 14, marginTop: 6, display: "inline-block"}}>
              Employee Self-Service (ESS)
            </div>
          )}
        </div>

        {notice && <p role="status" style={{fontSize:13,lineHeight:1.7}}>{notice}</p>}
        {/* Tab switcher - ONLY rendered in normal browser, completely HIDDEN in PWA */}
        {!isPwa && (
          <div style={{display:"flex", gap:0, marginBottom:24, background:"#f1f5f9", borderRadius:10, padding:4}}>
            <button onClick={()=>{setTab("admin");setErr("");try{localStorage.setItem("tw_login_tab","admin");}catch(e){}}} style={{flex:1,padding:"10px 0",border:"none",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:14,background:tab==="admin"?"#2563eb":"transparent",color:tab==="admin"?"#fff":"#64748b",boxShadow:tab==="admin"?"0 4px 12px rgba(37,99,235,0.3)":"none",transition:"all .2s"}}>
              🏢 Admin / HR
            </button>
            <button onClick={()=>{setTab("employee");setErr("");try{localStorage.setItem("tw_login_tab","employee");}catch(e){}}} style={{flex:1,padding:"10px 0",border:"none",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:14,background:tab==="employee"?"#2563eb":"transparent",color:tab==="employee"?"#fff":"#64748b",boxShadow:tab==="employee"?"0 4px 12px rgba(37,99,235,0.3)":"none",transition:"all .2s"}}>
              👤 Employee
            </button>
          </div>
        )}

        {err && <div className="login-err">⚠️ {err}</div>}

        {/* Admin login: Disallowed and hidden in PWA */}
        {!isPwa && tab === "admin" && (
          <form onSubmit={handleAdminLogin}>
            <div className="login-sub" style={{marginBottom:16, textAlign:"center"}}>{workspace.kind === 'company' ? `Sign in to ${appName} as Admin / HR` : 'Sign in as Admin or Company'}</div>
            <div className="login-field">
              <label>Admin email</label>
              <input 
                type="text" 
                value={username} 
                onChange={e=>{
                  const val = e.target.value;
                  setUsername(val);
                  setErr("");
                  try {
                    if (val.trim()) localStorage.setItem("tw_remember_username", val.trim());
                    else localStorage.removeItem("tw_remember_username");
                  } catch(err) {}
                }} 
                placeholder="hr@company.com" autoComplete="username"
                autoFocus={!username} 
              />
            </div>
            <div className="login-field">
              <label>Password</label>
              <div style={{position:"relative"}}>
                <input 
                  type={showPassword?"text":"password"} 
                  value={password} 
                  onChange={e=>{setPassword(e.target.value);setErr("");}} 
                  placeholder="Enter password" 
                  autoFocus={Boolean(username)}
                  style={{width:"100%",paddingRight:"40px",boxSizing:"border-box"}} 
                />
                <span onMouseDown={()=>setShowPassword(true)} onMouseUp={()=>setShowPassword(false)} onMouseLeave={()=>setShowPassword(false)} onTouchStart={()=>setShowPassword(true)} onTouchEnd={()=>setShowPassword(false)} style={{position:"absolute",right:"12px",top:"50%",transform:"translateY(-50%)",cursor:"pointer",userSelect:"none",fontSize:"16px"}}>
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </span>
              </div>
            </div>
            <button type="submit" className="login-btn">Sign In</button>
            <button type="button" className="btn btn-ghost btn-sm" style={{display:'block',margin:'10px auto 0'}} disabled={resetBusy} onClick={forgotPassword}>{resetBusy?'Requesting…':'Forgot password?'}</button>
            {resetMessage && <p role="status" style={{fontSize:12,lineHeight:1.5,textAlign:'center'}}>{resetMessage}</p>}
          </form>
        )}

        {/* Employee login: The only window allowed in PWA, or on Employee tab in browser */}
        {(isPwa || tab === "employee") && (
          <form onSubmit={handleEmployeeLogin}>
            <div className="login-field">
              <label htmlFor="employee-email">Email</label>
              <input id="employee-email" type="email" value={empEmail} onChange={event=>{setEmpEmail(event.target.value);setErr('');}} placeholder="you@company.com" autoComplete="username" autoCapitalize="none" required/>
            </div>
            <div className="login-field">
              <label htmlFor="employee-password">Password</label>
              <div style={{position:'relative'}}>
                <input id="employee-password" type={showPin?'text':'password'} autoComplete="current-password" value={empPassword} onChange={event=>{setEmpPassword(event.target.value);setErr('');}} required style={{paddingRight:44}}/>
                <button type="button" aria-label={showPin?'Hide password':'Show password'} aria-pressed={showPin} onClick={()=>setShowPin(value=>!value)} style={{position:'absolute',right:2,top:'50%',transform:'translateY(-50%)',width:40,height:40,border:0,background:'transparent',cursor:'pointer',color:'#64748b',display:'grid',placeItems:'center'}}><svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>{!showPin&&<path d="m3 3 18 18"/>}</svg></button>
              </div>
            </div>
            <button type="submit" className="login-btn employee-login-btn" disabled={empLoading}>{empLoading ? "Signing in..." : "Login"}</button>
            <button type="button" className="btn btn-ghost btn-sm" style={{display:'block',margin:'10px auto 0'}} disabled={resetBusy || empLoading} onClick={forgotPassword}>{resetBusy?'Requesting…':'Forgot password?'}</button>
            {resetMessage && <p role="status" style={{fontSize:12,lineHeight:1.5,textAlign:'center'}}>{resetMessage}</p>}

            {/* Install TrustWork App Button: Displayed only on mobile browsers */}
            {!isPwa && isMobileDevice() && (
              <div style={{marginTop: 22, paddingTop: 16, borderTop: "1px dashed #cbd5e1", textAlign: "center"}}>
                <button
                  type="button"
                  onClick={() => {
                    if (onInstallApp) onInstallApp();
                    else setShowGuide(true);
                  }}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    width: "100%", padding: "10px 14px", background: "#f8fafc", border: "1.5px solid #cbd5e1",
                    borderRadius: 12, color: "#1e293b", fontWeight: 700, fontSize: 13, cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.color = "#1d4ed8"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#1e293b"; }}
                >
                  <span style={{fontSize: 16}}>📲</span> Install {appName} App on this device
                </button>
                <div style={{fontSize: 11, color: "#94a3b8", marginTop: 6, fontWeight: 500}}>
                  Instant 1-tap employee access directly from your home screen
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

const mapEmployeeRow = (row) => ({
  id:          row.id,
  company_id:  row.company_id,
  emp_photo:   row.emp_photo || (row.id ? localStorage.getItem("tw_photo_" + row.id) : "") || (row.employee_code ? localStorage.getItem("tw_photo_" + row.employee_code) : "") || "",
  name:        row.name,
  pan:         row.pan || "",
  age:         row.age || "",
  doj:         row.date_of_joining || "",
  date_of_joining: row.date_of_joining || "",
  phone:       row.phone || "",
  email:       row.email || "",
  department:  row.department || "",
  designation: row.designation || "",
  basic:       row.basic_salary || 0,
  basic_salary:row.basic_salary || 0,
  employee_code: row.employee_code || row.emp_id || row.employee_id || "",
  emp_id:        row.employee_code || row.emp_id || row.employee_id || "",
  vpf_pct:     row.vpf_pct || 0,
  pension_applicable: row.pension_applicable !== false,
  tds_amount:  row.tds_amount || 0,
  father_name:       row.father_name || "",
  dob:               row.date_of_birth || "",
  gender:            row.gender || "",
  marital_status:    row.marital_status || "",
  employment_status: row.employment_status || "confirmed",
  prev_exp_years:    row.prev_exp_years || 0,
  prev_exp_months:   row.prev_exp_months || 0,
  perm_address:      row.perm_address || "",
  perm_city:         row.perm_city || "",
  perm_state:        row.perm_state || "",
  perm_pincode:      row.perm_pincode || "",
  temp_address:      row.temp_address || "",
  temp_city:         row.temp_city || "",
  temp_state:        row.temp_state || "",
  temp_pincode:      row.temp_pincode || "",
  same_address:      false,
  emergency_name:    row.emergency_name || "",
  emergency_relation:row.emergency_relation || "",
  emergency_phone:   row.emergency_phone || "",
  uan:         row.uan || "",
  bank_name:   row.bank_name || "",
  bank_account:row.bank_account_number || "",
  bank_ifsc:   row.bank_ifsc || "",
  bank_branch: row.bank_branch || "",
  pf_account:  row.pf_account_number || "",
  esi_id:      row.esi_id || "",
  dispensary:  row.dispensary || "",
  date_of_exit:row.date_of_exit || "",
  is_active:   row.is_active,
  // JoinKit extended fields
  aadhaar_last4:     row.aadhaar_last4 || "",
  religion:          row.religion || "",
  rel_type:          row.rel_type || "Father",
  spouse_name:       row.spouse_name || "",
  blood_group:       row.blood_group || "",
  prev_pf:           row.prev_pf || "No",
  prev_eps:          row.prev_eps || "No",
  prev_esi:          row.prev_esi || (row.esi_id ? "Yes" : "No"),
  prev_exit_date:    row.prev_exit_date || "",
  scheme_cert:       row.scheme_cert || "",
  ppo_number:        row.ppo_number || "",
  intl_worker:       row.intl_worker || "No",
  country_of_origin: row.country_of_origin || "",
  passport_number:   row.passport_number || "",
  passport_validity: row.passport_validity || "",
  location:          row.location || "",
  hr_induction_date: row.hr_induction_date || "",
  family_members:    Array.isArray(row.family_members) ? row.family_members : (row.family_members ? JSON.parse(row.family_members) : []),
  nominees:          Array.isArray(row.nominees) ? row.nominees : (row.nominees ? JSON.parse(row.nominees) : []),
  custom_basic_pct:  row.custom_basic_pct ?? null,
  custom_hra_pct:    row.custom_hra_pct ?? null,
  date_of_birth:     row.date_of_birth || "",
  esi_forced_until:  row.esi_forced_until || "",
});

function WelcomeTourModal({ onClose }) {
  const [step, setStep] = useState(0);
  const slides = [
    {
      tabName: "Company Profile",
      title: "1. Company Profile",
      subtitle: "First Step • Basic Setup, Statutory Numbers, Working Days & Holiday Calendar",
      icon: "🏢",
      badge: "Step 1 • Profile Tab",
      sections: [
        {
          heading: "Company Master Setup",
          items: [
            "Organization Details: Official Company Name, Contact Person, verified 10-digit Phone (+91), Email, Website, and physical office address.",
            "Brand Logo & Payslips: Upload company logo (PNG/JPEG) displayed automatically across all official PDF payslips and employee reports.",
            "Company Admin Credentials: Set login username and password for direct administrative access."
          ]
        },
        {
          heading: "Statutory Registrations & Geolocation",
          items: [
            "Compliance Codes: Enter legal PF Establishment Code, ESI Registration Code, GST/TIN, and Professional Tax (PT) credentials.",
            "Office Geofencing: Configure office GPS coordinates (Latitude & Longitude) and geofence radius for employee mobile punch-in."
          ]
        },
        {
          heading: "Working Schedule, Leave Types & Holidays",
          items: [
            "Weekly Working Days: Select active work days (e.g. Mon–Fri or Mon–Sat); non-working days are automatically exempt from leave deductions.",
            "Leave Types & Quotas: Configure Casual Leave (e.g. 12 days/yr), Sick Leave (e.g. 6 days/yr), and Paid Leave allocations.",
            "Company Holidays Calendar: Manage 15 festival and mandatory public holidays with interactive calendar badges and date pickers."
          ]
        }
      ]
    },
    {
      tabName: "Joining Kit",
      title: "2. Joining Kit",
      subtitle: "Second Step • Collect All Employee Data Required for PF, ESI, Banking & Records",
      icon: "🪪",
      badge: "Step 2 • Joining Kit Tab",
      sections: [
        {
          heading: "Central Pre-requisite Data Intake",
          items: [
            "Where Data is Collected: This is where all mandatory employee data needed for PF (UAN), ESI (Insurance ID), Banking (Account & IFSC), and Identity is gathered before onboarding.",
            "Standardized Excel Templates: Download ready-to-use Single-Company or Bulk Employee Excel onboarding templates directly from this section.",
            "Complete Data Preparation: Enter employee personal details, emergency contacts, PAN, DOB (18+ verified), DOJ, and salary structure in the template.",
            "Bridge to Employee Master: Once filled, this data is seamlessly imported into the Employees tab in one click, or can be retained as paperless records."
          ]
        },
        {
          heading: "Digital Document Repository",
          items: [
            "Paperless Records: Store and organize digital records including Aadhaar, PAN copies, appointment letters, and educational certificates.",
            "Instant Export: Conveniently download documents and employee kits for compliance audits, HR records, or background verification."
          ]
        }
      ]
    },
    {
      tabName: "Salary Structure",
      title: "3. Salary Structure",
      subtitle: "Third Step • Universal Formula, Statutory PF/ESI Engines & Deduction Rules",
      icon: "⚙️",
      badge: "Step 3 • Salary Structure Tab",
      sections: [
        {
          heading: "Universal Salary Configuration",
          items: [
            "Configured Once, Applies Globally: Establish organization-wide wage formulas once; all employee salary structures calculate automatically from these rules.",
            "50% Basic Salary Rule: Basic is automatically computed as 50% of Gross CTC. If 50% falls below the minimum wage (e.g. ₹15,000), Basic is elevated to meet compliance.",
            "HRA (House Rent Allowance): Set HRA as a custom percentage of Basic (default 40% or 50% for metros) or as fixed monthly rupee amounts."
          ]
        },
        {
          heading: "Statutory Compliance Engines",
          items: [
            "EPF (Provident Fund): Automatic 12% employee contribution + 13% employer contribution (12% EPF + 1% admin charges). Mandatory for 20+ employees.",
            "ESI (Employee State Insurance): 0.75% employee + 3.25% employer deduction. Automatically calculated when Basic + DA is ≤ ₹21,000/month. Mandatory for 10+ employees.",
            "Professional Tax (PT): Standard state slabs applied (exempt for Gross < ₹25,000; ₹200/month, ₹300 in February).",
            "Bonus & Gratuity: 8.33% statutory annual bonus for Basic ≤ ₹21,000/mo, 4.8% gratuity allocation, and December Labour Welfare Fund (LWF) deductions."
          ]
        }
      ]
    },
    {
      tabName: "Employees",
      title: "4. Employees",
      subtitle: "Fourth Step • Import from Joining Kit or Add Directly (+ Full Master Form)",
      icon: "👥",
      badge: "Step 4 • Employees Tab",
      sections: [
        {
          heading: "Two Ways to Add Employees",
          items: [
            "Option A — Import from Joining Kit: Bulk upload dozens of employees in seconds using the filled Excel templates prepared in the Joining Kit step.",
            "Option B — Direct Entry (+ Add Employee): Add any employee directly anytime using the comprehensive employee onboarding form."
          ]
        },
        {
          heading: "Mandatory Employee Information",
          items: [
            "Employee ID & Full Legal Name: Unique identifier (custom e.g. EMP-101 or auto-prefixed) and official government name.",
            "Date of Birth: Strictly validated check ensuring the employee is at least 18 years of age on their date of joining.",
            "Date of Joining & Exit (LWD): Joining date sets leave quota & service tenure; setting Last Working Day automates final settlement prorating.",
            "PAN Card & Gross CTC: 10-character PAN number and Gross Monthly CTC (which auto-computes 50% Basic & determines ESI eligibility).",
            "6-Digit Security PIN: Personal numeric PIN enabling the employee to log in to their self-service web & mobile portal."
          ]
        },
        {
          heading: "Salary Overrides, Statutory & Banking Fields",
          items: [
            "Salary Customizations: Set custom fixed HRA, Voluntary PF (VPF capped at Basic; warns if > ₹20,833/mo taxable limit), and monthly TDS deductions.",
            "Statutory Identifiers: Universal Account Number (PF UAN) and ESI Insurance ID for EPFO and ESIC remittances.",
            "Banking Details: Bank Name, Account Number, and IFSC Code for seamless salary transfer via NEFT/RTGS.",
            "Department & Emergency Contact: Organize by department/designation and record family emergency contacts."
          ]
        }
      ]
    },
    {
      tabName: "Run Payroll",
      title: "5. Run Payroll",
      subtitle: "Fifth Step • What Running Payroll Means: 1-Click Monthly Wage Calculation & Payouts",
      icon: "💰",
      badge: "Step 5 • Run Payroll Tab",
      sections: [
        {
          heading: "What 'Run Payroll' Means",
          items: [
            "Monthly Wage Execution: The core execution step where monthly salaries for all active employees are processed in a single click.",
            "Automatic Calendar & Absence Sync: Select the pay month & year; system counts total working days and automatically prorates for approved leaves and unpaid absences.",
            "Live Interactive Ledger: Instantly computes Gross, 50% Basic, HRA, statutory EPF, ESI, PT, TDS, and final Net Take-Home Pay for every employee."
          ]
        },
        {
          heading: "Adjustments, Locking & Disbursal Outputs",
          items: [
            "Variable Adjustments: Add monthly performance bonuses, incentives, arrears, or deduct salary advances on the fly.",
            "Payroll Lock / Unlock: Freeze finalized monthly payroll to prevent tampering, with one-click unlock if corrections are required.",
            "Batch Branded PDF Payslips: Download individual or ZIP batches of PDF salary slips carrying company logo and full earning/deduction tables.",
            "Bank NEFT/RTGS File: One-click export of salary transfer Excel files formatted ready for upload to corporate banking portals.",
            "Statutory ECR & ESI Exports: Export ESI MC_Template and PF Ready-to-Upload text files (#~# delimiter) for EPFO upload."
          ]
        }
      ]
    },
    {
      tabName: "Dashboard",
      title: "6. Dashboard",
      subtitle: "Sixth Step • What Dashboard Means: Executive Payroll Intelligence & Cost Analytics",
      icon: "📊",
      badge: "Step 6 • Dashboard Tab",
      sections: [
        {
          heading: "What 'Dashboard' Means",
          items: [
            "Executive Intelligence Hub: High-level visual command center giving business owners and HR managers complete visibility into monthly and annual company payout costs.",
            "Financial Metrics: Real-time cards showing Total Monthly Gross Salary, Total Net Salary Disbursed, and Total Employer Contributions (EPF + ESI + Admin charges).",
            "Headcount Insights: Active employee counts, new joiners, exited staff, and department-wise payroll expense distributions."
          ]
        },
        {
          heading: "Trends & Statutory Overview",
          items: [
            "Historical Cost Trends: Interactive visual graphs tracking payroll expenditure progression over time.",
            "Statutory Liability Overview: Clear visibility into monthly PF and ESI government liability due dates and compliance status."
          ]
        }
      ]
    },
    {
      tabName: "Approvals",
      title: "7. Approvals",
      subtitle: "Seventh Step • What Approvals Mean: Review Employee Leaves & Profile Updates",
      icon: "⏳",
      badge: "Step 7 • Approvals Tab",
      sections: [
        {
          heading: "What 'Approvals' Mean",
          items: [
            "Central Governance Workflow: Review, approve, or reject requests submitted by employees through their self-service portal.",
            "Real-Time Alert Badges: Dynamic red counter badges alert HR/Admin in the top header and left sidebar whenever applications are pending."
          ]
        },
        {
          heading: "🏖️ Leave Approvals Workflow",
          items: [
            "Pending Leave Requests: Inspect requested dates, leave type (Casual, Sick, etc.), employee reason, and total day count.",
            "Live Quota Balance Check: Preview employee remaining leave quota before making an approval decision.",
            "1-Click Approve / Reject: Approved leaves instantly deduct from the employee leave quota and sync with monthly payroll attendance.",
            "Company Leave Ledger: Filter and inspect complete leave history by employee, calendar year, and status (Approved / Rejected)."
          ]
        },
        {
          heading: "👤 Profile Updates Workflow",
          items: [
            "Self-Service Data Governance: Employees can request updates to Bank Account, Mobile Number, Address, or PAN from their portal.",
            "Side-by-Side Diff: Visual comparison highlighting current values versus requested changes for quick verification.",
            "Instant Master Update: Approving updates the employee master record immediately and creates an immutable audit trail entry."
          ]
        }
      ]
    },
    {
      tabName: "Activity Log",
      title: "8. Activity Log",
      subtitle: "Eighth Step • Complete Audit Trail, Administrative History & Security",
      icon: "📜",
      badge: "Step 8 • Activity Log Tab",
      sections: [
        {
          heading: "Administrative Transparency & Audit Trail",
          items: [
            "Full Accountability: Every significant change is permanently recorded with exact timestamp, username, action type, and details.",
            "Tracked Operations: Tracks Company Profile edits, Salary Structure revisions, Employee salary increments, Payroll locking/unlocking, and Approval actions.",
            "Audit & Compliance Readiness: Essential for statutory audits, internal financial controls, and multi-user administrative security."
          ]
        }
      ]
    }
  ];

  const current = slides[step];

  return (
    <div className="overlay" style={{zIndex:9999, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16}} onClick={onClose}>
      <div 
        className="modal-content" 
        style={{
          width: "100%", 
          maxWidth: 700, 
          height: 600,
          maxHeight: "88vh", 
          borderRadius: 20, 
          background: "#ffffff", 
          boxShadow: "0 25px 60px -15px rgba(0,0,0,0.3)", 
          display: "flex", 
          flexDirection: "column", 
          overflow: "hidden", 
          border: "1px solid #e2e8f0"
        }} 
        onClick={e=>e.stopPropagation()}
      >
        {/* Header with Navigation Pills */}
        <div style={{background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", padding: "20px 24px", color: "#ffffff", borderBottom: "1px solid #334155"}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14}}>
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <span style={{fontSize: 26}}>{current.icon}</span>
              <div>
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                  <h3 style={{margin: 0, fontSize: 18, fontWeight: 800, color: "#ffffff"}}>{current.title}</h3>
                  <span style={{fontSize: 10, fontWeight: 800, textTransform: "uppercase", background: "rgba(59, 130, 246, 0.25)", color: "#93c5fd", border: "1px solid rgba(147, 197, 253, 0.3)", padding: "2px 8px", borderRadius: 10}}>
                    {current.badge}
                  </span>
                </div>
                <div style={{fontSize: 12.5, color: "#94a3b8", marginTop: 3}}>{current.subtitle}</div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              style={{background: "rgba(255,255,255,0.1)", border: "none", color: "#94a3b8", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, transition: "all 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.2)"; e.currentTarget.style.color="#fff";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.1)"; e.currentTarget.style.color="#94a3b8";}}
            >
              ✕
            </button>
          </div>

          {/* Quick Jump Topic Chips matching Left Sidebar */}
          <div style={{display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none"}}>
            {slides.map((s, idx) => (
              <button 
                key={idx} 
                onClick={() => setStep(idx)}
                style={{
                  background: idx === step ? "#2563eb" : "rgba(255,255,255,0.08)",
                  color: idx === step ? "#ffffff" : "#cbd5e1",
                  border: idx === step ? "1px solid #3b82f6" : "1px solid rgba(255,255,255,0.12)",
                  padding: "5px 12px",
                  borderRadius: 14,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.18s ease",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5
                }}
              >
                <span>{s.icon}</span>
                <span>{s.tabName || s.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Body Content */}
        <div style={{padding: "20px 24px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16}}>
          {current.sections.map((sec, secIdx) => (
            <div key={secIdx} style={{background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #e2e8f0"}}>
              <div style={{fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 10, display: "flex", alignItems: "center", gap: 6}}>
                <span style={{color: "#2563eb"}}>▸</span>
                <span>{sec.heading}</span>
              </div>
              <ul style={{margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6}}>
                {sec.items.map((it, itIdx) => (
                  <li key={itIdx} style={{fontSize: 12.5, color: "#475569", lineHeight: 1.5}}>
                    {it.includes(":") ? (
                      <>
                        <strong style={{color: "#0f172a"}}>{it.split(":")[0]}:</strong>
                        <span>{it.slice(it.indexOf(":") + 1)}</span>
                      </>
                    ) : (
                      it
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Modal Footer with Stepper */}
        <div style={{background: "#ffffff", padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between"}}>
          <div style={{display: "flex", alignItems: "center", gap: 6}}>
            {slides.map((_, i) => (
              <div 
                key={i} 
                onClick={() => setStep(i)}
                style={{
                  width: i === step ? 20 : 8, 
                  height: 8, 
                  borderRadius: 4, 
                  background: i === step ? "#2563eb" : "#cbd5e1", 
                  transition: "all 0.25s ease", 
                  cursor: "pointer"
                }} 
              />
            ))}
            <span style={{fontSize: 11, color: "#94a3b8", fontWeight: 700, marginLeft: 8}}>
              {step + 1} of {slides.length}
            </span>
          </div>

          <div style={{display: "flex", gap: 8}}>
            {step > 0 && (
              <button 
                onClick={() => setStep(step - 1)}
                className="btn btn-ghost btn-sm"
                style={{fontWeight: 700, borderRadius: 8}}
              >
                ← Back
              </button>
            )}
            {step < slides.length - 1 ? (
              <button 
                onClick={() => setStep(step + 1)}
                className="btn btn-primary btn-sm"
                style={{fontWeight: 700, borderRadius: 8}}
              >
                Next Step →
              </button>
            ) : (
              <button 
                onClick={onClose}
                className="btn btn-primary btn-sm"
                style={{background: "#10b981", borderColor: "#10b981", fontWeight: 700, borderRadius: 8}}
              >
                ✓ Got It!
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── ADMIN ORG & CULTURE DASHBOARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export function OrgAndCultureDashboard({
  company,
  employees = [],
  currentUser,
  isCompanyRole,
  onUpdateCompanyConfig,
  onEditEmployee,
  onSelectEmployee,
  initialTab = "tree"
}) {
  const [activeTab, setActiveTab] = useState(initialTab || "tree"); // "tree" | "departments" | "celebrations"
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [treeMode, setTreeMode] = useState("chart"); // "chart" | "directory"
  const [cardsPerRow, setCardsPerRow] = useState(4); // 1 | 3 | 4 | 5 | "auto"
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedEmployeeId, setFocusedEmployeeId] = useState(null);
  const focusEmployee = (emp) => {
    setFocusedEmployeeId(emp.id);
    setSearchQuery("");
    setCollapsedNodes({});
  };
  const [deptFilter, setDeptFilter] = useState("all");
  const [collapsedNodes, setCollapsedNodes] = useState({});
  const [reassignTarget, setReassignTarget] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [savingManager, setSavingManager] = useState(false);
  const [celebrationFilter, setCelebrationFilter] = useState("all"); // "all" | "birthdays" | "anniversaries" | "new_hires"
  const [downloadingImage, setDownloadingImage] = useState(false);
  const [downloadFeedback, setDownloadFeedback] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const chartCaptureRef = useRef(null);
  const directoryCaptureRef = useRef(null);

  // Auto-close export dropdown on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(".export-dropdown-container")) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExportMenu]);

  const reportingHierarchy = company?.config?.reporting_hierarchy || {};
  const photos = company?.config?.employee_photos || {};

  const activeEmps = useMemo(() => {
    return (employees || []).filter(e => e.employment_status !== "terminated" && e.is_active !== false);
  }, [employees]);

  const treeData = useMemo(() => {
    return buildOrgTree(activeEmps, reportingHierarchy);
  }, [activeEmps, reportingHierarchy]);

  const focusedRelationships = useMemo(
    () => getOrgRelationships(activeEmps, reportingHierarchy, focusedEmployeeId),
    [activeEmps, reportingHierarchy, focusedEmployeeId]
  );
  const visibleRoots = focusedRelationships?.roots || treeData.roots;
  const searchMatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return activeEmps.filter(emp => [emp.name, emp.employee_code, emp.designation, emp.department]
      .some(value => String(value || "").toLowerCase().includes(query)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [activeEmps, searchQuery]);

  const celebrations = useMemo(() => {
    return calcCelebrations(activeEmps);
  }, [activeEmps]);

  // Group by department
  const departmentGroups = useMemo(() => {
    const groups = {};
    activeEmps.forEach(emp => {
      const d = (emp.department || "").trim() || "General / Unassigned";
      if (!groups[d]) groups[d] = [];
      groups[d].push(emp);
    });
    return groups;
  }, [activeEmps]);

  const deptList = useMemo(() => Object.keys(departmentGroups).sort(), [departmentGroups]);

  const toggleNodeCollapse = (id) => {
    setCollapsedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAllNodes = () => setCollapsedNodes({});
  const collapseAllNodes = () => {
    const next = {};
    activeEmps.forEach(e => { next[e.id] = true; });
    setCollapsedNodes(next);
  };

  const handleDownloadImage = async (format = "jpg", expandAllFirst = false) => {
    if (expandAllFirst) {
      expandAllNodes();
      // Allow DOM to re-render all nodes
      await new Promise(r => setTimeout(r, 150));
    }

    const targetEl = treeMode === "chart" ? chartCaptureRef.current : directoryCaptureRef.current;
    if (!targetEl) return;

    setDownloadingImage(true);
    setDownloadFeedback("Preparing image...");
    setShowExportMenu(false);

    try {
      const html2canvasModule = await import("html2canvas");
      const html2canvas = html2canvasModule.default || html2canvasModule;

      const scrollParent = targetEl.closest(".org-chart-wrap");
      const prevScrollLeft = scrollParent ? scrollParent.scrollLeft : 0;
      const prevScrollTop = scrollParent ? scrollParent.scrollTop : 0;
      if (scrollParent) {
        scrollParent.scrollLeft = 0;
        scrollParent.scrollTop = 0;
      }

      setDownloadFeedback("Rendering graphics...");
      await new Promise(r => setTimeout(r, 60));

      const scrollWidth = targetEl.scrollWidth || targetEl.offsetWidth;
      const scrollHeight = targetEl.scrollHeight || targetEl.offsetHeight;

      const canvas = await html2canvas(targetEl, {
        scale: 2, // 2x for sharp presentation & print quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: scrollWidth,
        height: scrollHeight,
        windowWidth: Math.max(document.documentElement.clientWidth, scrollWidth + 120),
        windowHeight: Math.max(document.documentElement.clientHeight, scrollHeight + 120)
      });

      if (scrollParent) {
        scrollParent.scrollLeft = prevScrollLeft;
        scrollParent.scrollTop = prevScrollTop;
      }

      setDownloadFeedback("Exporting file...");

      const safeCoName = (company?.name || "Company").replace(/[^a-zA-Z0-9_-]/g, "_");
      const dateStr = new Date().toISOString().slice(0, 10);
      const isJpeg = format.toLowerCase() !== "png";
      const mimeType = isJpeg ? "image/jpeg" : "image/png";
      const fileExt = isJpeg ? "jpg" : "png";
      const filename = `${safeCoName}_Org_Structure_${dateStr}.${fileExt}`;

      const triggerDownload = (url) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      if (canvas.toBlob) {
        canvas.toBlob(blob => {
          if (!blob) {
            triggerDownload(canvas.toDataURL(mimeType, 0.95));
          } else {
            const blobUrl = URL.createObjectURL(blob);
            triggerDownload(blobUrl);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
          }
          setDownloadFeedback("✅ Image downloaded!");
          setTimeout(() => {
            setDownloadFeedback("");
            setDownloadingImage(false);
          }, 2500);
        }, mimeType, 0.95);
      } else {
        triggerDownload(canvas.toDataURL(mimeType, 0.95));
        setDownloadFeedback("✅ Image downloaded!");
        setTimeout(() => {
          setDownloadFeedback("");
          setDownloadingImage(false);
        }, 2500);
      }
    } catch (err) {
      console.error("Failed to export org image:", err);
      alert("Could not generate image. Please try again: " + (err.message || err));
      setDownloadFeedback("");
      setDownloadingImage(false);
    }
  };

  const getPhoto = (emp) => {
    return emp.emp_photo || photos[emp.id] || (emp.employee_code ? photos[emp.employee_code] : "") || localStorage.getItem("tw_photo_" + emp.id) || (emp.employee_code ? localStorage.getItem("tw_photo_" + emp.employee_code) : "") || "";
  };

  const renderEmpAvatar = (emp, size = 42, photoOnly = false) => {
    const photo = getPhoto(emp);
    if (photo) {
      return (
        <img
          src={photo}
          alt={emp.name}
          onError={photoOnly ? (event) => { event.currentTarget.style.display = "none"; } : undefined}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #e2e8f0",
            flexShrink: 0
          }}
        />
      );
    }
    return null;
  };

  const handleOpenReassign = (emp) => {
    setReassignTarget(emp);
    const currMgr = reportingHierarchy[emp.id] || (emp.employee_code && reportingHierarchy[emp.employee_code]) || "";
    setSelectedManagerId(currMgr);
  };

  const handleSaveReassign = async () => {
    if (!reassignTarget) return;
    setSavingManager(true);
    try {
      const nextHierarchy = { ...reportingHierarchy };
      if (selectedManagerId) {
        nextHierarchy[reassignTarget.id] = selectedManagerId;
      } else {
        delete nextHierarchy[reassignTarget.id];
        if (reassignTarget.employee_code) delete nextHierarchy[reassignTarget.employee_code];
      }
      const updatedConfig = { ...(company?.config || {}), reporting_hierarchy: nextHierarchy };
      await supabase.from("companies").update({ config: updatedConfig }).eq("id", company.id);
      if (onUpdateCompanyConfig) onUpdateCompanyConfig(updatedConfig);
      setReassignTarget(null);
    } catch (err) {
      alert("Failed to update reporting manager: " + err.message);
    } finally {
      setSavingManager(false);
    }
  };

  // Check circular descent to prevent invalid hierarchy loops
  const isDescendant = (candidateId, targetId) => {
    if (candidateId === targetId) return true;
    let curr = candidateId;
    const visited = new Set();
    while (curr && !visited.has(curr)) {
      visited.add(curr);
      if (curr === targetId) return true;
      curr = reportingHierarchy[curr];
    }
    return false;
  };

  // Node search match
  const isSearchMatch = (emp) => {
    if (!searchQuery.trim()) return false;
    const q = searchQuery.toLowerCase();
    return (
      (emp.name && emp.name.toLowerCase().includes(q)) ||
      (emp.employee_code && emp.employee_code.toLowerCase().includes(q)) ||
      (emp.designation && emp.designation.toLowerCase().includes(q)) ||
      (emp.department && emp.department.toLowerCase().includes(q))
    );
  };

  const getGridMaxWidth = (perRow) => {
    if (perRow === 1) return 300;
    if (perRow === 3) return 860;
    if (perRow === 4) return 1140;
    if (perRow === 5) return 1420;
    return "100%";
  };

  const getCaptureMaxWidth = (perRow) => {
    if (perRow === 1) return 720;
    if (perRow === 3) return 960;
    if (perRow === 4) return 1240;
    if (perRow === 5) return 1520;
    return "100%";
  };

  // Recursive Tree Node Renderer for Chart Mode
  const renderChartNode = (node, isRoot = false, siblingIndex = 0, totalSiblings = 1) => {
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = collapsedNodes[node.id];
    const isMatch = focusedEmployeeId === node.id || isSearchMatch(node);
    const hasSiblings = totalSiblings > 1;
    const isFirst = siblingIndex === 0;
    const isLast = siblingIndex === totalSiblings - 1;
    const willWrapSiblings = cardsPerRow === 1 || (cardsPerRow !== "auto" && totalSiblings > Number(cardsPerRow)) || (cardsPerRow === "auto" && totalSiblings > 4);
    const willWrapChildren = hasChildren && (cardsPerRow === 1 || (cardsPerRow !== "auto" && node.children.length > Number(cardsPerRow)) || (cardsPerRow === "auto" && node.children.length > 4));
    const mgrId = reportingHierarchy[node.id] || (node.employee_code && reportingHierarchy[node.employee_code]);
    const mgr = activeEmps.find(e => e.id === mgrId || (e.employee_code && e.employee_code === mgrId));

    return (
      <div
        key={node.id}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          padding: "0 8px"
        }}
      >
        {/* Horizontal connector bar to siblings (only when siblings are on a single horizontal row) */}
        {!isRoot && hasSiblings && !willWrapSiblings && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: isFirst ? "50%" : 0,
              right: isLast ? "50%" : 0,
              height: 2,
              background: "#cbd5e1"
            }}
          />
        )}

        {/* Stem down into node from above */}
        {!isRoot && (
          <div style={{ width: 2, height: 18, background: "#cbd5e1" }} />
        )}

        {/* Card Component */}
        <div
          className="org-node-card"
          style={{
            borderColor: isMatch ? "#f59e0b" : "#cbd5e1",
            boxShadow: isMatch ? "0 0 0 3px rgba(245,158,11,0.25)" : undefined,
            background: isMatch ? "#fffbeb" : "#fff"
          }}
        >
          {/* Top Department Banner */}
          <div style={{
            height: 4,
            background: "linear-gradient(90deg, #94a3b8, #5eead4)",
            borderRadius: "6px 6px 0 0",
            margin: "-14px -16px 12px -16px"
          }} />

          {/* Employee details with saved photo beside the name */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {renderEmpAvatar(node, 30, true)}
              <div style={{
              minWidth: 0,
              fontWeight: 800,
              fontSize: 14,
              color: "#0f172a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              <button type="button" onClick={() => focusEmployee(node)} title={`Show reporting relationships for ${node.name}`} style={{border:0,padding:0,background:"transparent",font:"inherit",color:"inherit",cursor:"pointer",maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{node.name}</button>
              </div>
            </div>
            <div style={{
              fontSize: 11.5,
              color: "#64748b",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginTop: 2
            }}>
              {node.designation || "Staff Member"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#475569",
                background: "#f1f5f9",
                padding: "1.5px 6px",
                borderRadius: 4
              }}>
                {node.employee_code || "EMP"}
              </span>
              {node.department && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#2563eb",
                  background: "#eff6ff",
                  padding: "1.5px 6px",
                  borderRadius: 4
                }}>
                  {node.department}
                </span>
              )}
            </div>
          </div>

          <ReportingManagerControl employeeName={node.name} managerName={mgr?.name}
            onChange={() => handleOpenReassign(node)} />

          {/* Direct Reports Badge and Action Buttons (Redundant Mgr button removed) */}
          <div style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6
          }}>
            {hasChildren ? (
              <button
                onClick={(e) => { e.stopPropagation(); toggleNodeCollapse(node.id); }}
                style={{
                  border: "1px solid #cbd5e1",
                  background: isCollapsed ? "#eff6ff" : "#f8fafc",
                  color: isCollapsed ? "#2563eb" : "#475569",
                  fontSize: 11,
                  fontWeight: 800,
                  padding: "3px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <span>{isCollapsed ? "➕" : "➖"}</span>
                <span>{node.children.length} {node.children.length === 1 ? "report" : "reports"}</span>
              </button>
            ) : (
              <span style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>Individual Contributor</span>
            )}

            <div data-html2canvas-ignore="true" style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <button
                onClick={() => onSelectEmployee(node)}
                title="View Profile"
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#334155",
                  padding: "4px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3
                }}
              >
                <span>👁️</span>
                <span>Profile</span>
              </button>
              <button
                onClick={() => onEditEmployee(node)}
                title="Edit Employee"
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#334155",
                  padding: "4px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3
                }}
              >
                <span>✏️</span>
                <span>Edit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stem down to children */}
        {hasChildren && !isCollapsed && (
          <div style={{ width: 2, height: 16, background: "#cbd5e1" }} />
        )}

        {/* Children Hub Badge when direct reports wrap */}
        {hasChildren && !isCollapsed && willWrapChildren && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 8
          }}>
            <div style={{ width: 20, height: 1.5, background: "#cbd5e1" }} />
            <span style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: "#475569",
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              padding: "2px 8px",
              borderRadius: 8
            }}>
              👥 {node.children.length} Direct Reports
            </span>
            <div style={{ width: 20, height: 1.5, background: "#cbd5e1" }} />
          </div>
        )}

        {/* Children Row / Wrap */}
        {hasChildren && !isCollapsed && (
          <div
            style={{
              display: "flex",
              flexDirection: cardsPerRow === 1 ? "column" : "row",
              flexWrap: cardsPerRow === 1 ? "nowrap" : "wrap",
              justifyContent: "center",
              alignItems: cardsPerRow === 1 ? "center" : "flex-start",
              gap: cardsPerRow === 1 ? 14 : "16px 20px",
              maxWidth: getGridMaxWidth(cardsPerRow),
              width: "100%",
              position: "relative"
            }}
          >
            {node.children.map((child, idx) =>
              renderChartNode(child, false, idx, node.children.length)
            )}
          </div>
        )}
      </div>
    );
  };

  // Recursive Tree Node Renderer for Directory List Mode
  const renderDirectoryRow = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = collapsedNodes[node.id];
    const isMatch = focusedEmployeeId === node.id || isSearchMatch(node);
    const mgrId = reportingHierarchy[node.id] || (node.employee_code && reportingHierarchy[node.employee_code]);
    const mgr = activeEmps.find(e => e.id === mgrId || (e.employee_code && e.employee_code === mgrId));

    return (
      <Fragment key={node.id}>
        <div
          className="org-dir-row"
          style={{
            marginLeft: depth * 24,
            borderColor: isMatch ? "#f59e0b" : "#e2e8f0",
            background: isMatch ? "#fffbeb" : "#fff"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            {hasChildren ? (
              <button
                onClick={() => toggleNodeCollapse(node.id)}
                style={{
                  width: 24,
                  height: 24,
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: 6,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#475569"
                }}
              >
                {isCollapsed ? "▶" : "▼"}
              </button>
            ) : (
              <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1" }}>
                •
              </div>
            )}

            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {renderEmpAvatar(node, 28, true)}
                <button type="button" onClick={() => focusEmployee(node)} title={`Show reporting relationships for ${node.name}`} style={{border:0,padding:0,background:"transparent",fontFamily:"inherit",fontWeight:800,fontSize:13.5,color:"#1e293b",cursor:"pointer",textAlign:"left"}}>{node.name}</button>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
                  {node.employee_code || "EMP"}
                </span>
                {node.department && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "1px 6px", borderRadius: 4 }}>
                    {node.department}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <span>{node.designation || "Staff"}</span>
                <ReportingManagerControl employeeName={node.name} managerName={mgr?.name} compact
                  onChange={() => handleOpenReassign(node)} />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {hasChildren && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "3px 8px", borderRadius: 6 }}>
                👥 {node.children.length}
              </span>
            )}
            <button
              data-html2canvas-ignore="true"
              onClick={() => onSelectEmployee(node)}
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 11, padding: "5px 10px" }}
            >
              Profile
            </button>
            <button
              data-html2canvas-ignore="true"
              onClick={() => handleOpenReassign(node)}
              style={{
                border: "1px solid rgba(148,163,184,0.45)",
                background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(226,232,240,0.65) 100%)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                color: "#475569",
                padding: "5px 12px",
                borderRadius: 7,
                fontSize: 11.5,
                fontWeight: 800,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 4px rgba(15,23,42,0.06)"
              }}
              title="Change Reporting Manager"
            >
              <span style={{ fontSize: 13 }}>👔</span>
              <span>Change Manager ⇄</span>
            </button>
          </div>
        </div>

        {hasChildren && !isCollapsed && (
          node.children.map(child => renderDirectoryRow(child, depth + 1))
        )}
      </Fragment>
    );
  };

  const totalBirthdays = celebrations.birthdaysToday.length + celebrations.birthdaysThisMonth.length;
  const totalAnniversaries = celebrations.anniversariesToday.length + celebrations.anniversariesThisMonth.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Page Header and Navigation */}
      <div style={{
        background: "linear-gradient(120deg, #ffffff 0%, #f1f5f9 65%, #f0fdfa 100%)",
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        boxShadow: "0 4px 20px rgba(15,23,42,0.04)",
        padding: "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16
      }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 900, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
            <span>🌳</span> Org Structure & Culture
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
            Explore your people, follow reporting relationships, and celebrate team milestones.
          </div>
        </div>

        {/* Tab Controls */}
        <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 10 }}>
          <button
            onClick={() => setActiveTab("tree")}
            style={{
              border: "none",
              background: activeTab === "tree" ? "#fff" : "transparent",
              color: activeTab === "tree" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              padding: "7px 16px",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: activeTab === "tree" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span>🌳</span> <span>Org Tree</span>
            <span style={{ background: activeTab === "tree" ? "#eff6ff" : "#e2e8f0", color: activeTab === "tree" ? "#2563eb" : "#475569", fontSize: 10.5, padding: "1px 6px", borderRadius: 10 }}>
              {activeEmps.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("departments")}
            style={{
              border: "none",
              background: activeTab === "departments" ? "#fff" : "transparent",
              color: activeTab === "departments" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              padding: "7px 16px",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: activeTab === "departments" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span>🏢</span> <span>Departments</span>
            <span style={{ background: activeTab === "departments" ? "#eff6ff" : "#e2e8f0", color: activeTab === "departments" ? "#2563eb" : "#475569", fontSize: 10.5, padding: "1px 6px", borderRadius: 10 }}>
              {deptList.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("celebrations")}
            style={{
              border: "none",
              background: activeTab === "celebrations" ? "#fff" : "transparent",
              color: activeTab === "celebrations" ? "#2563eb" : "#64748b",
              fontWeight: 800,
              fontSize: 13,
              padding: "7px 16px",
              borderRadius: 8,
              cursor: "pointer",
              boxShadow: activeTab === "celebrations" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            <span>🎉</span> <span>Celebrations</span>
            {(celebrations.birthdaysToday.length > 0 || celebrations.anniversariesToday.length > 0) ? (
              <span style={{ background: "#ef4444", color: "#fff", fontSize: 10.5, padding: "1px 6px", borderRadius: 10 }}>
                TODAY!
              </span>
            ) : (totalBirthdays + totalAnniversaries > 0) && (
              <span style={{ background: "#f59e0b", color: "#fff", fontSize: 10.5, padding: "1px 6px", borderRadius: 10 }}>
                {totalBirthdays + totalAnniversaries}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── TAB 1: ORG TREE ── */}
      {activeTab === "tree" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Toolbar */}
          <div style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "12px 18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flex: 1 }}>
              <input
                type="text"
                placeholder="Search name, employee ID, role or department"
                aria-label="Search organization employees"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setFocusedEmployeeId(null); }}
                style={{
                  padding: "7px 12px",
                  borderRadius: 8,
                  border: "1px solid #cbd5e1",
                  fontSize: 12.5,
                  minWidth: 220
                }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={expandAllNodes}
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, padding: "5px 10px" }}
                >
                  ➕ Expand All
                </button>
                <button
                  onClick={collapseAllNodes}
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, padding: "5px 10px" }}
                >
                  ➖ Collapse All
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* View Mode Toggle */}
              <div style={{ display: "flex", background: "#f1f5f9", padding: 3, borderRadius: 8 }}>
                <button
                  onClick={() => setTreeMode("chart")}
                  style={{
                    border: "none",
                    background: treeMode === "chart" ? "#fff" : "transparent",
                    color: treeMode === "chart" ? "#2563eb" : "#64748b",
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  🌿 Visual Chart
                </button>
                <button
                  onClick={() => setTreeMode("directory")}
                  style={{
                    border: "none",
                    background: treeMode === "directory" ? "#fff" : "transparent",
                    color: treeMode === "directory" ? "#2563eb" : "#64748b",
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  📋 Directory List
                </button>
              </div>

              {/* Cards Per Row Selector (Only in Visual Chart Mode) */}
              {treeMode === "chart" && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  background: "#f1f5f9",
                  padding: "3px 6px",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0"
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", paddingLeft: 4, paddingRight: 4 }}>
                    Cards / row:
                  </span>
                  {[
                    { label: "1 (List)", val: 1 },
                    { label: "3", val: 3 },
                    { label: "4", val: 4 },
                    { label: "5", val: 5 },
                    { label: "Auto", val: "auto" }
                  ].map(opt => {
                    const isActive = cardsPerRow === opt.val;
                    return (
                      <button
                        key={opt.val}
                        onClick={() => setCardsPerRow(opt.val)}
                        title={`Display ${opt.label} cards per row`}
                        style={{
                          border: "none",
                          background: isActive ? "#ffffff" : "transparent",
                          color: isActive ? "#0f172a" : "#64748b",
                          fontWeight: isActive ? 800 : 600,
                          fontSize: 11.5,
                          padding: "3px 8px",
                          borderRadius: 6,
                          cursor: "pointer",
                          boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                          transition: "all 0.15s ease"
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Download Image Button & Options (Clean Light Button) */}
              <div className="export-dropdown-container" style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <button
                  onClick={() => handleDownloadImage("jpg", false)}
                  disabled={downloadingImage || treeData.roots.length === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: "6px 13px",
                    borderRadius: showExportMenu ? "7px 0 0 7px" : 7,
                    background: downloadingImage ? "#f8fafc" : "#ffffff",
                    border: "1.5px solid #cbd5e1",
                    borderRight: "none",
                    color: downloadingImage ? "#64748b" : "#0f172a",
                    cursor: downloadingImage || treeData.roots.length === 0 ? "not-allowed" : "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={e => {
                    if (!downloadingImage && treeData.roots.length > 0) {
                      e.currentTarget.style.background = "#f8fafc";
                      e.currentTarget.style.borderColor = "#94a3b8";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!downloadingImage) {
                      e.currentTarget.style.background = "#ffffff";
                      e.currentTarget.style.borderColor = "#cbd5e1";
                    }
                  }}
                  title="Download JPG image for PowerPoint / presentations"
                >
                  {downloadingImage ? (
                    <>
                      <span>⏳</span>
                      <span>{downloadFeedback || "Processing..."}</span>
                    </>
                  ) : (
                    <>
                      <span>🖼️</span>
                      <span>Download JPG</span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowExportMenu(prev => !prev)}
                  disabled={downloadingImage || treeData.roots.length === 0}
                  style={{
                    padding: "6px 9px",
                    fontSize: 11,
                    border: "1.5px solid #cbd5e1",
                    borderLeft: "1px solid #e2e8f0",
                    borderRadius: "0 7px 7px 0",
                    background: downloadingImage ? "#f8fafc" : "#ffffff",
                    color: "#475569",
                    cursor: downloadingImage || treeData.roots.length === 0 ? "not-allowed" : "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={e => {
                    if (!downloadingImage && treeData.roots.length > 0) {
                      e.currentTarget.style.background = "#f8fafc";
                      e.currentTarget.style.borderColor = "#94a3b8";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!downloadingImage) {
                      e.currentTarget.style.background = "#ffffff";
                      e.currentTarget.style.borderColor = "#cbd5e1";
                    }
                  }}
                  title="More download options"
                >
                  ▾
                </button>

                {showExportMenu && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: 6,
                      background: "#fff",
                      border: "1px solid #cbd5e1",
                      borderRadius: 10,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      minWidth: 240,
                      padding: 6
                    }}
                  >
                    <button
                      onClick={() => handleDownloadImage("jpg", false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "8px 12px",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        cursor: "pointer",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#1e293b"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontSize: 16 }}>📷</span>
                      <div>
                        <div>Download JPG (Visible View)</div>
                        <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 500 }}>Standard for presentations & slides</div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleDownloadImage("jpg", true)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "8px 12px",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        cursor: "pointer",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#1e293b"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontSize: 16 }}>🌳</span>
                      <div>
                        <div>Expand All & Download JPG</div>
                        <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 500 }}>Expands all branches then exports JPG</div>
                      </div>
                    </button>

                    <button
                      onClick={() => handleDownloadImage("png", false)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "8px 12px",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        cursor: "pointer",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#1e293b"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontSize: 16 }}>🖼️</span>
                      <div>
                        <div>Download PNG (High-Res)</div>
                        <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 500 }}>Lossless PNG for documents & print</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {focusedRelationships && (
            <div className="org-focus-panel">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <div>
                  <div className="org-section-eyebrow">Reporting relationships</div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10}}>
                    {renderEmpAvatar(focusedRelationships.selected, 40, true)}
                    <div><strong style={{fontSize:20,color:"#0f172a"}}>{focusedRelationships.selected.name}</strong><div style={{fontSize:12,color:"#64748b",marginTop:4}}>{focusedRelationships.selected.designation || "Team member"} · {focusedRelationships.directReports.length} direct report{focusedRelationships.directReports.length === 1 ? "" : "s"}</div></div>
                  </div>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFocusedEmployeeId(null); setSearchQuery(""); setCollapsedNodes({}); }}>Show full organization</button>
              </div>
              <div style={{marginTop:18,fontSize:11,fontWeight:700,color:"#64748b"}}>MANAGER CHAIN · TOP TO BOTTOM</div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:8}}>
                {focusedRelationships.ancestors.map(emp => <Fragment key={emp.id}><button type="button" className="org-chain-person" onClick={() => focusEmployee(emp)}>{emp.name}</button><span aria-hidden="true" style={{color:"#94a3b8"}}>→</span></Fragment>)}
                <span className="org-chain-person selected">{focusedRelationships.selected.name}</span>
              </div>
              {!focusedRelationships.ancestors.length && !focusedRelationships.issue && <div style={{fontSize:12,color:"#64748b",marginTop:10}}>No manager assigned. This employee is at the top level of the recorded hierarchy.</div>}
              {!focusedRelationships.directReports.length && <div style={{fontSize:12,color:"#64748b",marginTop:10}}>No direct reports assigned.</div>}
              {focusedRelationships.issue && <div role="status" style={{fontSize:12,color:"#92400e",marginTop:10}}>{focusedRelationships.issue}</div>}
            </div>
          )}

          {/* Org Tree View Content */}
          {searchQuery.trim() ? (
            <div className="org-search-panel" aria-live="polite">
              <div className="org-section-eyebrow">{searchMatches.length} matching employee{searchMatches.length === 1 ? "" : "s"}</div>
              <div style={{fontSize:13,color:"#64748b",marginTop:6,marginBottom:16}}>Select a person to see their manager chain and direct reports.</div>
              <div className="org-search-results">
                {searchMatches.map(emp => (
                  <button type="button" key={emp.id} className="org-person-result" onClick={() => focusEmployee(emp)}>
                    {renderEmpAvatar(emp, 36, true)}
                    <span style={{minWidth:0,flex:1}}>
                      <strong style={{display:"block",color:"#1e293b",fontSize:13}}>{emp.name}</strong>
                      <span style={{display:"block",fontSize:11.5,color:"#64748b",marginTop:4}}>{[emp.employee_code, emp.designation, emp.department].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span aria-hidden="true" style={{color:"#64748b"}}>→</span>
                  </button>
                ))}
              </div>
              {!searchMatches.length && <div style={{padding:"16px 0",color:"#64748b",fontSize:13}}>No employees match “{searchQuery.trim()}”. Try another name or employee ID.</div>}
              <button type="button" className="btn btn-ghost btn-sm" style={{marginTop:16}} onClick={() => setSearchQuery("")}>Clear search</button>
            </div>
          ) : visibleRoots.length === 0 ? (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 40, textAlign: "center", color: "#64748b" }}>
              No active employees found in this company.
            </div>
          ) : treeMode === "chart" ? (
            <div className="org-chart-wrap" style={{ display: "flex", justifyContent: "center", width: "100%", overflowX: "auto", paddingBottom: 16 }}>
              <div
                ref={chartCaptureRef}
                style={{
                  margin: "0 auto",
                  width: "100%",
                  maxWidth: getCaptureMaxWidth(cardsPerRow),
                  padding: "24px 28px 44px 28px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "#ffffff",
                  borderRadius: 16,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  boxSizing: "border-box"
                }}
              >
                {/* Clean Presentation Header for Slide Exports */}
                <div style={{
                  width: "100%",
                  padding: "20px 28px",
                  marginBottom: 36,
                  background: "#f8fafc",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      border: "1.5px solid #bfdbfe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      flexShrink: 0
                    }}>
                      🏢
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.01em" }}>
                          {company?.name || "Company"}
                        </span>
                        <span style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: "#2563eb",
                          background: "#eff6ff",
                          border: "1.5px solid #bfdbfe",
                          padding: "3px 10px",
                          borderRadius: 8,
                          letterSpacing: "0.02em"
                        }}>
                          Organization Structure
                        </span>
                      </div>
                      <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                        <span>As of {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                        <span>•</span>
                        <span style={{ fontWeight: 600, color: "#475569" }}>Workforce Reporting Hierarchy</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{
                      background: "#ffffff",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "8px 18px",
                      textAlign: "right",
                      minWidth: 120,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                    }}>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Workforce
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginTop: 2 }}>
                        {activeEmps.length} <span style={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>Employees</span>
                      </div>
                    </div>

                    {deptList.length > 0 && (
                      <div style={{
                        background: "#ffffff",
                        border: "1.5px solid #bfdbfe",
                        borderRadius: 12,
                        padding: "8px 18px",
                        textAlign: "right",
                        minWidth: 110,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                      }}>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Departments
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#1d4ed8", marginTop: 2 }}>
                          {deptList.length}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tree Roots (Wrapped based on cardsPerRow) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: cardsPerRow === 1 ? "column" : "row",
                    flexWrap: cardsPerRow === 1 ? "nowrap" : "wrap",
                    justifyContent: "center",
                    alignItems: cardsPerRow === 1 ? "center" : "flex-start",
                    gap: cardsPerRow === 1 ? 20 : "24px 30px",
                    maxWidth: getGridMaxWidth(cardsPerRow),
                    width: "100%",
                    margin: "0 auto"
                  }}
                >
                  {visibleRoots.map((root, idx) =>
                    renderChartNode(root, true, idx, visibleRoots.length)
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              ref={directoryCaptureRef}
              style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
            >
              {/* Directory Header for Slide Exports */}
              <div style={{
                padding: "18px 24px",
                marginBottom: 24,
                background: "#f8fafc",
                border: "1.5px solid #e2e8f0",
                borderRadius: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 20
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    border: "1.5px solid #bfdbfe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    flexShrink: 0
                  }}>
                    📋
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 19, fontWeight: 900, color: "#0f172a" }}>
                        {company?.name || "Company"}
                      </span>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#2563eb",
                        background: "#eff6ff",
                        border: "1.5px solid #bfdbfe",
                        padding: "3px 10px",
                        borderRadius: 8
                      }}>
                        Staff Directory Tree
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                      As of {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} • {activeEmps.length} Active Members
                    </div>
                  </div>
                </div>

                {deptList.length > 0 && (
                  <div style={{
                    background: "#ffffff",
                    border: "1.5px solid #bfdbfe",
                    borderRadius: 12,
                    padding: "8px 18px",
                    textAlign: "right",
                    minWidth: 110
                  }}>
                    <div style={{ fontSize: 10.5, fontWeight: 800, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Departments
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: "#1d4ed8", marginTop: 2 }}>
                      {deptList.length}
                    </div>
                  </div>
                )}
              </div>

              {visibleRoots.map(root => renderDirectoryRow(root, 0))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: DEPARTMENTS ── */}
      {activeTab === "departments" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Total Departments</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1e293b", marginTop: 4 }}>{deptList.length}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Workforce Headcount</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#2563eb", marginTop: 4 }}>{activeEmps.length}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Avg Team Size</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#10b981", marginTop: 4 }}>
                {deptList.length > 0 ? (activeEmps.length / deptList.length).toFixed(1) : 0}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            {deptList.map(deptName => {
              const members = departmentGroups[deptName] || [];
              const totalGross = members.reduce((sum, m) => sum + (parseFloat(m.basic_salary || m.basic) || 0), 0);
              const pctOfTotal = activeEmps.length > 0 ? ((members.length / activeEmps.length) * 100).toFixed(0) : 0;

              return (
                <div key={deptName} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 16, color: "#1e293b", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>🏢</span> {deptName}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                        Monthly Payroll: <strong style={{ color: "#334155" }}>{fmtINR(totalGross)}</strong>
                      </div>
                    </div>
                    <span style={{ background: "#eff6ff", color: "#2563eb", fontSize: 11.5, fontWeight: 800, padding: "3px 10px", borderRadius: 12, border: "1px solid #bfdbfe" }}>
                      {members.length} {members.length === 1 ? "member" : "members"} ({pctOfTotal}%)
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto", paddingRight: 4 }}>
                    {members.map(m => {
                      const mgrId = reportingHierarchy[m.id] || (m.employee_code && reportingHierarchy[m.employee_code]);
                      const mgr = activeEmps.find(e => e.id === mgrId || (e.employee_code && e.employee_code === mgrId));

                      return (
                        <div key={m.id} style={{ background: "#f8fafc", border: "1px solid #f1f5f9", borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            {renderEmpAvatar(m, 32, true)}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 12.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {m.name}
                              </div>
                              <div style={{ fontSize: 11, color: "#64748b" }}>
                                {m.designation || "Staff"} {mgr ? `• Mgr: ${mgr.name}` : ""}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                            <button
                              onClick={() => onSelectEmployee(m)}
                              className="btn btn-ghost btn-sm"
                              style={{ fontSize: 10.5, padding: "3px 6px" }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleOpenReassign(m)}
                              style={{
                                border: "1px solid #2563eb",
                                background: "#eff6ff",
                                color: "#2563eb",
                                padding: "3px 7px",
                                borderRadius: 5,
                                fontSize: 10.5,
                                fontWeight: 800,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3
                              }}
                              title="Change Reporting Manager"
                            >
                              <span>👔</span>
                              <span>Mgr ⇄</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB 3: CELEBRATIONS ── */}
      {activeTab === "celebrations" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Spotlight Hero Banner */}
          {(celebrations.birthdaysToday.length > 0 || celebrations.anniversariesToday.length > 0) && (
            <div style={{
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              border: "1.5px solid #f59e0b",
              borderRadius: 12,
              padding: 22,
              boxShadow: "0 4px 16px rgba(245,158,11,0.2)"
            }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: "#92400e", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span>🎉</span> Company Celebrations Today!
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
                {celebrations.birthdaysToday.map(b => (
                  <div key={b.id} style={{ background: "rgba(255,255,255,0.9)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    {renderEmpAvatar(b, 44)}
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>🎂 Happy Birthday, {b.name}!</div>
                      <div style={{ fontSize: 11.5, color: "#92400e", fontWeight: 600 }}>
                        Turning {b.turningAge} today • {b.department || "Staff"}
                      </div>
                    </div>
                  </div>
                ))}
                {celebrations.anniversariesToday.map(a => (
                  <div key={a.id} style={{ background: "rgba(255,255,255,0.9)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    {renderEmpAvatar(a, 44)}
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>🎖️ Happy Work Anniversary, {a.name}!</div>
                      <div style={{ fontSize: 11.5, color: "#92400e", fontWeight: 600 }}>
                        Celebrating {a.yearsCompleted} years at {company.name} today!
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <HRNotificationAction company={company} label="Email filtered celebrations" targets={[
            ...(['all','birthdays'].includes(celebrationFilter)?[...celebrations.birthdaysToday,...celebrations.birthdaysThisMonth].map(employee=>({kind:'birthday',employee_id:employee.id,label:`${employee.name} · Birthday`})) : []),
            ...(['all','anniversaries'].includes(celebrationFilter)?[...celebrations.anniversariesToday,...celebrations.anniversariesThisMonth].map(employee=>({kind:'work_anniversary',employee_id:employee.id,label:`${employee.name} · Work anniversary`})) : []),
            ...(['all','new_hires'].includes(celebrationFilter)?celebrations.newHires.map(employee=>({kind:'joining_greeting',employee_id:employee.id,label:`${employee.name} · Welcome`})) : [])
          ]}/>

          {/* Sub-filters */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { id: "all", label: "🌟 All Milestones" },
              { id: "birthdays", label: `🎂 Birthdays (${totalBirthdays})` },
              { id: "anniversaries", label: `🎖️ Work Anniversaries (${totalAnniversaries})` },
              { id: "new_hires", label: `👋 New Hires (${celebrations.newHires.length})` }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setCelebrationFilter(f.id)}
                style={{
                  border: "1px solid",
                  borderColor: celebrationFilter === f.id ? "#3b82f6" : "#cbd5e1",
                  background: celebrationFilter === f.id ? "#eff6ff" : "#fff",
                  color: celebrationFilter === f.id ? "#1d4ed8" : "#475569",
                  padding: "6px 14px",
                  borderRadius: 20,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Birthdays Section */}
          {(celebrationFilter === "all" || celebrationFilter === "birthdays") && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span>🎂</span> Birthdays This Month
                <span style={{ background: "#fdf2f8", color: "#db2777", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10 }}>
                  {totalBirthdays}
                </span>
              </div>
              {totalBirthdays === 0 ? (
                <div style={{ fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center" }}>
                  No birthdays registered this month.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 12 }}>
                  {[...celebrations.birthdaysToday, ...celebrations.birthdaysThisMonth].map(b => (
                    <div key={b.id} style={{ background: b.isToday ? "#fffbeb" : "#f8fafc", border: `1px solid ${b.isToday ? "#fde68a" : "#e2e8f0"}`, borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
                      {renderEmpAvatar(b, 40)}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {b.name}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                          {b.department || b.designation || "Staff"}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: b.isToday ? "#d97706" : "#2563eb", marginTop: 2 }}>
                          {b.isToday ? "🎉 Today!" : b.daysDiff === 1 ? "Tomorrow!" : `On ${MONTHS[b.bMonth].slice(0,3)} ${b.bDay} (in ${b.daysDiff} days)`}
                        </div>
                      </div>
                      <HRNotificationAction company={company} label="Email birthday greeting" targets={[{kind:'birthday',employee_id:b.id,label:b.name}]}/>
                    </div>
                  ))}
                </div>
              )}

              {/* Next Month Preview */}
              {celebrations.birthdaysNextMonth.length > 0 && (
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
                    📅 Coming Up Next Month ({celebrations.birthdaysNextMonth.length})
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {celebrations.birthdaysNextMonth.map(b => (
                      <span key={b.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: 16, fontSize: 11.5, color: "#334155", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        🎂 <strong>{b.name}</strong> • {MONTHS[b.bMonth].slice(0,3)} {b.bDay}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Work Anniversaries Section */}
          {(celebrationFilter === "all" || celebrationFilter === "anniversaries") && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span>🎖️</span> Work Anniversaries
                <span style={{ background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10 }}>
                  {totalAnniversaries}
                </span>
              </div>
              {totalAnniversaries === 0 ? (
                <div style={{ fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center" }}>
                  No work anniversaries this month.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 12 }}>
                  {[...celebrations.anniversariesToday, ...celebrations.anniversariesThisMonth].map(a => (
                    <div key={a.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
                      {renderEmpAvatar(a, 40)}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {a.name}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#16a34a", fontWeight: 700 }}>
                          🎖️ {a.yearsCompleted} {a.yearsCompleted === 1 ? "Year" : "Years"} Milestone
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                          {a.isToday ? "🎉 Today!" : `On ${MONTHS[a.jMonth].slice(0,3)} ${a.jDay}`}
                        </div>
                      </div>
                      <HRNotificationAction company={company} label="Email congratulations" targets={[{kind:'work_anniversary',employee_id:a.id,label:a.name}]}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* New Hires Section */}
          {(celebrationFilter === "all" || celebrationFilter === "new_hires") && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span>👋</span> New Hires Joined in Last 45 Days
                <span style={{ background: "#eff6ff", color: "#2563eb", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 10 }}>
                  {celebrations.newHires.length}
                </span>
              </div>
              {celebrations.newHires.length === 0 ? (
                <div style={{ fontSize: 12.5, color: "#64748b", background: "#f8fafc", padding: 16, borderRadius: 8, textAlign: "center" }}>
                  No new hires joined in the last 45 days.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 12 }}>
                  {celebrations.newHires.map(n => (
                    <div key={n.id} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
                      {renderEmpAvatar(n, 40)}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {n.name}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b" }}>
                          {n.designation || n.department || "Staff"}
                        </div>
                        <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 700, marginTop: 2 }}>
                          Joined {n.daysSinceJoin === 0 ? "Today" : `${n.daysSinceJoin} days ago`} ({fmtDate(n.doj || n.date_of_joining)})
                        </div>
                      </div>
                      <HRNotificationAction company={company} label="Email welcome greeting" targets={[{kind:'joining_greeting',employee_id:n.id,label:n.name}]}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── MODAL: QUICK REASSIGN MANAGER ── */}
      {reassignTarget && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.65)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16
        }} onClick={() => setReassignTarget(null)}>
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: 24,
            maxWidth: 500,
            width: "100%",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)"
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", border: "1.5px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  👔
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: "#0f172a" }}>
                    Change Reporting Manager
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    Define organizational hierarchy, team tree, and approval flow
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReassignTarget(null)}
                style={{ border: "none", background: "transparent", fontSize: 22, color: "#94a3b8", cursor: "pointer", padding: "0 4px", lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {/* Target Employee Summary Card */}
            <div style={{
              background: "#f8fafc",
              border: "1.5px solid #e2e8f0",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {renderEmpAvatar(reassignTarget, 38)}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13.5, color: "#0f172a" }}>{reassignTarget.name}</div>
                  <div style={{ fontSize: 11.5, color: "#64748b" }}>
                    {reassignTarget.designation || "Staff"} • {reassignTarget.department || "General"}
                  </div>
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: 6 }}>
                {reassignTarget.employee_code || "EMP"}
              </span>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: "#334155", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span>👔</span>
                <span>Select New Reporting Manager</span>
              </label>
              <select
                className="config-input"
                value={selectedManagerId}
                onChange={e => setSelectedManagerId(e.target.value)}
                style={{ width: "100%", fontSize: 13, height: 40 }}
              >
                <option value="">No direct manager</option>
                {activeEmps
                  .filter(e => e.id !== reassignTarget.id && !isDescendant(e.id, reassignTarget.id))
                  .map(e => (
                    <option key={e.id} value={e.id}>
                      👔 {e.name} {e.employee_code ? `(${e.employee_code})` : ""} {e.designation ? `— ${e.designation}` : ""} {e.department ? `[${e.department}]` : ""}
                    </option>
                  ))
                }
              </select>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.4 }}>
                💡 Direct subordinates and their descendants are automatically hidden to prevent circular hierarchy loops.
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => setReassignTarget(null)}
                className="btn btn-ghost"
                disabled={savingManager}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveReassign}
                className="btn btn-primary"
                disabled={savingManager}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span>👔</span>
                <span>{savingManager ? "Saving..." : "Save Manager"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── ADMIN ATTENDANCE DASHBOARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export function AdminAttendanceDashboard({ company, employees = [], currentUser, session, onBack, isCompanyRole, onUpdateCompanyConfig }) {
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [records, setRecords] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [filterDept, setFilterDept] = useState("all");
  const [sortBy, setSortBy] = useState("emp_id");
  const [sortCol, setSortCol] = useState("emp_id"); // 'emp_id' | 'name' | 'punch_in' | 'punch_out' | 'hours' | 'status'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleColumnSort = (colKey) => {
    if (sortCol === colKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colKey);
      setSortDir('asc');
    }
  };
  
  // Timing / Schedule config state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({
    punch_in: company?.config?.attendance_punch_in || "09:00",
    punch_out: company?.config?.attendance_punch_out || "18:00",
    grace: company?.config?.attendance_grace_minutes !== undefined ? company.config.attendance_grace_minutes : 15,
    cutoff: company?.config?.attendance_eod_cutoff || "19:00"
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMsg, setConfigMsg] = useState("");

  // Manual Punch Modal
  const [manualModal, setManualModal] = useState(null); // { emp, rec }
  const [manualForm, setManualForm] = useState({ punch_in: "", punch_out: "", status: "present", note: "" });
  const [manualSaving, setManualSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [clearingDay, setClearingDay] = useState(false);

  // Range Report State
  const [showReportModal, setShowReportModal] = useState(false);
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [rangeTo, setRangeTo] = useState(todayStr);
  const [exportingReport, setExportingReport] = useState(false);

  const cfg = company?.config || {};
  const stdIn = cfg.attendance_punch_in || "09:00";
  const stdOut = cfg.attendance_punch_out || "18:00";
  const graceMin = cfg.attendance_grace_minutes !== undefined ? cfg.attendance_grace_minutes : 15;

  // Clear bulk selection whenever the selected date changes
  useEffect(() => {
    setSelectedRecordIds([]);
  }, [selectedDate]);

  // Fetch records for selectedDate
  const fetchDayAttendance = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const [{ data, error }, { data: leaves, error: leaveError }] = await Promise.all([
        supabase.from("attendance_records")
          .select("*, employees(name, employee_code, department, designation)")
          .eq("company_id", company.id)
          .eq("date", selectedDate),
        supabase.from("employee_leaves")
          .select("employee_id, start_date, end_date, status")
          .eq("company_id", company.id)
          .eq("status", "approved")
          .lte("start_date", selectedDate)
          .gte("end_date", selectedDate)
      ]);
      if (error) throw error;
      if (leaveError) throw leaveError;
      setApprovedLeaves(leaves || []);

      if (!error && data) {
        setRecords(data);
        setLastSyncTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }));
      }
    } catch (e) {
      console.error("Failed to fetch attendance:", e);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [company?.id, selectedDate]);

  useEffect(() => {
    fetchDayAttendance(false);
  }, [fetchDayAttendance]);

  // Auto-refresh interval (every 15s)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDayAttendance(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchDayAttendance]);

  // Helpers
  const parseTimeToMins = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(":").map(Number);
    return (h * 60) + (m || 0);
  };

  const getPunchMinutes = (isoString) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    return (d.getHours() * 60) + d.getMinutes();
  };

  const getLateDetails = (punchInIso) => {
    if (!punchInIso) return { isLate: false, lateMins: 0 };
    const punchMins = getPunchMinutes(punchInIso);
    const expectedMins = parseTimeToMins(stdIn) + graceMin;
    if (punchMins > expectedMins) {
      return { isLate: true, lateMins: punchMins - parseTimeToMins(stdIn) };
    }
    return { isLate: false, lateMins: 0 };
  };

  const getEarlyOutDetails = (punchOutIso) => {
    if (!punchOutIso) return { isEarly: false, earlyMins: 0 };
    const punchMins = getPunchMinutes(punchOutIso);
    const stdOutMins = parseTimeToMins(stdOut);
    if (punchMins < stdOutMins) {
      return { isEarly: true, earlyMins: stdOutMins - punchMins };
    }
    return { isEarly: false, earlyMins: 0 };
  };

  const formatTimeDisplay = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const calcDuration = (inIso, outIso) => {
    if (!inIso || !outIso) return "—";
    const diffMs = new Date(outIso) - new Date(inIso);
    if (diffMs <= 0) return "—";
    const totalMins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hrs}h ${mins}m`;
  };

  // Helper to extract employee identifier string for clean sorting
  const getEmpIdCode = (emp) => {
    return (emp?.employee_code ?? emp?.emp_id ?? emp?.employee_id ?? emp?.id ?? "").toString().trim();
  };

  // Natural numeric comparator for Employee IDs (e.g. EMP-2 before EMP-10)
  const cmpEmpId = (empA, empB) => {
    const codeA = getEmpIdCode(empA);
    const codeB = getEmpIdCode(empB);
    if (!codeA && !codeB) return 0;
    if (!codeA) return 1;
    if (!codeB) return -1;
    return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: "base" });
  };

  // Selected date Day info: Holiday & Weekly Off check based on company config
  const dayScheduleInfo = useMemo(() => {
    if (!selectedDate) return { isHoliday: false, isWeeklyOff: false, holidayName: "", dayName: "" };
    const parts = selectedDate.split("-").map(Number);
    if (parts.length !== 3) return { isHoliday: false, isWeeklyOff: false, holidayName: "", dayName: "" };
    const [year, month, day] = parts;
    const dateObj = new Date(year, month - 1, day);
    const dayOfWeek = dateObj.getDay(); // 0 = Sun, 1 = Mon, ...
    const dayName = dateObj.toLocaleDateString("en-IN", { weekday: "long" });

    // Company working days schedule (defaults to Mon-Sat: [1, 2, 3, 4, 5, 6])
    const workingDays = cfg.working_days_schedule || [1, 2, 3, 4, 5, 6];
    const isWeeklyOff = !workingDays.includes(dayOfWeek);

    // Company holidays check
    const holidays = cfg.company_holidays || [];
    const mmdd = `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const matchedHoliday = holidays.find(h => {
      if (!h.date) return false;
      const clean = String(h.date).trim();
      return clean === selectedDate || clean.endsWith(mmdd);
    });

    const isHoliday = !!matchedHoliday;
    const holidayName = matchedHoliday?.name || (isHoliday ? "Company Holiday" : "");

    return { isHoliday, isWeeklyOff, holidayName, dayName, isNonWorkingDay: isHoliday || isWeeklyOff };
  }, [selectedDate, cfg.company_holidays, cfg.working_days_schedule]);

  // Build unified employee attendance rows for the date
  const attendanceRows = useMemo(() => {
    const recordMap = new Map();
    records.forEach(r => {
      recordMap.set(r.employee_id, r);
    });

    const onLeaveIds = new Set(approvedLeaves.map(leave => leave.employee_id));
    return employees.map(emp => {
      const rec = recordMap.get(emp.id);
      const punchIn = rec?.punch_in || null;
      const punchOut = rec?.punch_out || null;
      const lateInfo = getLateDetails(punchIn);
      const earlyInfo = getEarlyOutDetails(punchOut);

      let computedStatus = "absent";
      if (rec?.regularization_status === "approved" || rec?.status === "regularized") {
        computedStatus = "regularized";
      } else if (punchIn) {
        if (lateInfo.isLate) {
          computedStatus = "late";
        } else if (punchOut && earlyInfo.isEarly) {
          computedStatus = "early_out";
        } else {
          computedStatus = "present";
        }
      } else if (onLeaveIds.has(emp.id)) {
        computedStatus = "on_leave";
      } else if (dayScheduleInfo.isHoliday) {
        computedStatus = "holiday";
      } else if (dayScheduleInfo.isWeeklyOff) {
        computedStatus = "weekly_off";
      }

      return {
        employee: emp,
        record: rec || null,
        punchIn,
        punchOut,
        lateInfo,
        earlyInfo,
        status: computedStatus,
        workingHours: calcDuration(punchIn, punchOut),
        regularizationStatus: rec?.regularization_status || null,
        regularizationReason: rec?.regularization_reason || null
      };
    });
  }, [employees, records, approvedLeaves, stdIn, stdOut, graceMin, dayScheduleInfo]);

  // Metrics summary
  const metrics = useMemo(() => {
    const total = employees.length;
    let presentCount = 0;
    let onTimeCount = 0;
    let lateCount = 0;
    let earlyOutCount = 0;
    let regularizedCount = 0;
    let holidayCount = 0;
    let weeklyOffCount = 0;
    let absentCount = 0;
    let onLeaveCount = 0;

    attendanceRows.forEach(row => {
      if (row.punchIn || row.status === "regularized") presentCount++;
      if (row.status === "present") onTimeCount++;
      if (row.status === "late") lateCount++;
      if (row.earlyInfo.isEarly && row.punchOut) earlyOutCount++;
      if (row.status === "regularized") regularizedCount++;
      if (row.status === "holiday") holidayCount++;
      if (row.status === "weekly_off") weeklyOffCount++;
      if (row.status === "absent") absentCount++;
      if (row.status === "on_leave") onLeaveCount++;
    });

    return { total, presentCount, onTimeCount, lateCount, earlyOutCount, regularizedCount, holidayCount, weeklyOffCount, absentCount, onLeaveCount };
  }, [attendanceRows, employees.length]);

  // Filtered & Sorted rows - default sorted by employee ID always
  const displayRows = useMemo(() => {
    return attendanceRows.filter(row => {
      const emp = row.employee;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = (emp.name || "").toLowerCase().includes(q);
        const matchCode = (emp.employee_code || "").toLowerCase().includes(q);
        const matchDept = (emp.department || "").toLowerCase().includes(q);
        if (!matchName && !matchCode && !matchDept) return false;
      }
      if (filterDept !== "all" && emp.department !== filterDept) return false;
      if (filterStatus !== "all") {
        if (filterStatus === "punched" && !row.punchIn) return false;
        if (filterStatus === "present" && row.status !== "present") return false;
        if (filterStatus === "late" && row.status !== "late") return false;
        if (filterStatus === "early_out" && (!row.earlyInfo.isEarly || !row.punchOut)) return false;
        if (filterStatus === "holiday" && row.status !== "holiday") return false;
        if (filterStatus === "weekly_off" && row.status !== "weekly_off") return false;
        if (filterStatus === "absent" && row.status !== "absent") return false;
        if (filterStatus === "on_leave" && row.status !== "on_leave") return false;
        if (filterStatus === "regularized" && row.status !== "regularized") return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortCol) {
        if (sortCol === "emp_id") {
          const res = cmpEmpId(a.employee, b.employee);
          return sortDir === "asc" ? res : -res;
        }
        if (sortCol === "name") {
          const valA = (a.employee?.name || "").toString().trim();
          const valB = (b.employee?.name || "").toString().trim();
          const cmp = valA.localeCompare(valB, undefined, { sensitivity: "base" });
          if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;
          return cmpEmpId(a.employee, b.employee);
        }
        if (sortCol === "punch_in") {
          const valA = a.punchIn || (sortDir === "asc" ? "9999" : "");
          const valB = b.punchIn || (sortDir === "asc" ? "9999" : "");
          const cmp = sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
          if (cmp !== 0) return cmp;
          return cmpEmpId(a.employee, b.employee);
        }
        if (sortCol === "punch_out") {
          const valA = a.punchOut || (sortDir === "asc" ? "9999" : "");
          const valB = b.punchOut || (sortDir === "asc" ? "9999" : "");
          const cmp = sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
          if (cmp !== 0) return cmp;
          return cmpEmpId(a.employee, b.employee);
        }
        if (sortCol === "hours") {
          const parseH = (hStr) => {
            if (!hStr) return 0;
            const m = String(hStr).match(/([0-9.]+)h/);
            return m ? parseFloat(m[1]) : 0;
          };
          const valA = parseH(a.workingHours);
          const valB = parseH(b.workingHours);
          const cmp = sortDir === "asc" ? valA - valB : valB - valA;
          if (cmp !== 0) return cmp;
          return cmpEmpId(a.employee, b.employee);
        }
        if (sortCol === "status") {
          const valA = (a.status || "").toLowerCase();
          const valB = (b.status || "").toLowerCase();
          const cmp = sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
          if (cmp !== 0) return cmp;
          return cmpEmpId(a.employee, b.employee);
        }
      }

      if (sortBy === "emp_id") {
        return cmpEmpId(a.employee, b.employee);
      } else if (sortBy === "late_first") {
        const aLate = a.lateInfo.lateMins;
        const bLate = b.lateInfo.lateMins;
        if (bLate !== aLate) return bLate - aLate;
      } else if (sortBy === "early_out_first") {
        const aEarly = a.earlyInfo.earlyMins;
        const bEarly = b.earlyInfo.earlyMins;
        if (bEarly !== aEarly) return bEarly - aEarly;
      } else if (sortBy === "punch_in_desc") {
        const cmp = (b.punchIn || "").localeCompare(a.punchIn || "");
        if (cmp !== 0) return cmp;
      } else if (sortBy === "punch_in_asc") {
        const cmp = (a.punchIn || "9999").localeCompare(b.punchIn || "9999");
        if (cmp !== 0) return cmp;
      } else if (sortBy === "name_asc") {
        const cmp = (a.employee.name || "").localeCompare(b.employee.name || "");
        if (cmp !== 0) return cmp;
      } else if (sortBy === "status") {
        const cmp = a.status.localeCompare(b.status);
        if (cmp !== 0) return cmp;
      }
      return cmpEmpId(a.employee, b.employee);
    });
  }, [attendanceRows, searchQuery, filterDept, filterStatus, sortBy, sortCol, sortDir]);

  // Save Schedule Config
  const handleSaveScheduleConfig = async (e) => {
    e.preventDefault();
    setSavingConfig(true);
    setConfigMsg("");
    try {
      const updatedConfig = {
        ...cfg,
        attendance_punch_in: configForm.punch_in,
        attendance_punch_out: configForm.punch_out,
        attendance_grace_minutes: parseInt(configForm.grace) || 0,
        attendance_eod_cutoff: configForm.cutoff,
        attendance_enabled: true
      };

      const { error } = await supabase.from("companies").update({ config: updatedConfig }).eq("id", company.id);
      if (error) throw error;

      if (onUpdateCompanyConfig) onUpdateCompanyConfig(updatedConfig);
      logAudit(session, "ADMIN_UPDATED_ATTENDANCE_TIMINGS", {
        punch_in: configForm.punch_in,
        punch_out: configForm.punch_out,
        grace: configForm.grace
      });
      setConfigMsg("Attendance timing settings saved successfully!");
      setTimeout(() => {
        setShowConfigModal(false);
        setConfigMsg("");
      }, 1200);
    } catch (err) {
      setConfigMsg("Error: " + err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  // Save Manual Punch / Override
  const handleSaveManualPunch = async (e) => {
    e.preventDefault();
    if (!manualModal) return;
    setManualSaving(true);
    try {
      const emp = manualModal.emp;
      const rec = manualModal.rec;
      const date = selectedDate;

      let inIso = null;
      if (manualForm.punch_in) {
        inIso = new Date(`${date}T${manualForm.punch_in}:00`).toISOString();
      }
      let outIso = null;
      if (manualForm.punch_out) {
        outIso = new Date(`${date}T${manualForm.punch_out}:00`).toISOString();
      }

      const lateData = inIso ? getLateDetails(inIso) : { isLate: false, lateMins: 0 };
      const earlyData = outIso ? getEarlyOutDetails(outIso) : { isEarly: false, earlyMins: 0 };

      const payload = {
        company_id: company.id,
        employee_id: emp.id,
        date: date,
        punch_in: inIso,
        punch_out: outIso,
        status: manualForm.status,
        late_minutes: lateData.lateMins,
        early_minutes: earlyData.earlyMins,
        regularization_reason: manualForm.note || null
      };

      let error = null;
      if (rec?.id) {
        ({ error } = await supabase.from("attendance_records").update(payload).eq("id", rec.id));
      } else {
        ({ error } = await supabase.from("attendance_records").insert([payload]));
      }

      if (error) throw error;

      logAudit(session, "ADMIN_MANUAL_ATTENDANCE_RECORD", {
        employee_id: emp.id,
        date,
        punch_in: manualForm.punch_in,
        punch_out: manualForm.punch_out,
        status: manualForm.status
      });

      await fetchDayAttendance(false);
      setManualModal(null);
    } catch (err) {
      alert("Error saving punch: " + err.message);
    } finally {
      setManualSaving(false);
    }
  };

  // Open manual punch modal
  const openManualPunch = (emp, rec) => {
    let inTime = "";
    if (rec?.punch_in) {
      const d = new Date(rec.punch_in);
      inTime = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    let outTime = "";
    if (rec?.punch_out) {
      const d = new Date(rec.punch_out);
      outTime = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }

    setManualForm({
      punch_in: inTime,
      punch_out: outTime,
      status: rec?.status || (rec?.punch_in ? "present" : "absent"),
      note: rec?.regularization_reason || ""
    });
    setManualModal({ emp, rec });
  };

  // Delete single attendance record
  const handleDeleteRecord = async (emp, rec, fromModal = false) => {
    if (!rec?.id) {
      alert(`${emp.name} has not punched in on ${formatDateDDMMYYYY(selectedDate)}. No database record exists to delete.`);
      return;
    }
    const confirmMsg = `Are you sure you want to delete the attendance record for ${emp.name} (${emp.employee_code || "—"}) on ${formatDateDDMMYYYY(selectedDate)}?\n\nThis will remove their punch-in/punch-out data for this date and recalculate their status for this date.`;
    if (!window.confirm(confirmMsg)) return;

    setDeletingId(rec.id);
    try {
      const { error } = await supabase.from("attendance_records").delete().eq("id", rec.id);
      if (error) throw error;

      logAudit(session, "ADMIN_DELETED_ATTENDANCE_RECORD", {
        employee_id: emp.id,
        employee_name: emp.name,
        employee_code: emp.employee_code,
        date: selectedDate,
        deleted_punch_in: rec.punch_in,
        deleted_punch_out: rec.punch_out,
        deleted_status: rec.status
      });

      if (fromModal) setManualModal(null);
      await fetchDayAttendance(false);
    } catch (err) {
      alert("Error deleting attendance record: " + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Delete all attendance records for selectedDate
  const handleClearDayAttendance = async () => {
    if (!records.length) {
      alert(`No attendance records exist for ${formatDateDDMMYYYY(selectedDate)}.`);
      return;
    }
    const confirmMsg = `⚠️ Are you sure you want to delete ALL ${records.length} attendance record(s) for ${formatDateDDMMYYYY(selectedDate)}?\n\nEmployee statuses for this date will be recalculated. This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setClearingDay(true);
    try {
      const { error } = await supabase.from("attendance_records")
        .delete()
        .eq("company_id", company.id)
        .eq("date", selectedDate);
      if (error) throw error;

      logAudit(session, "ADMIN_CLEARED_DAY_ATTENDANCE", {
        company_id: company.id,
        company_name: company.name,
        date: selectedDate,
        records_deleted: records.length
      });

      await fetchDayAttendance(false);
    } catch (err) {
      alert("Error clearing attendance records: " + err.message);
    } finally {
      setClearingDay(false);
    }
  };

  // Bulk delete selected attendance records
  const handleBulkDeleteRecords = async () => {
    if (!selectedRecordIds.length) return;
    const count = selectedRecordIds.length;
    const confirmMsg = `⚠️ Are you sure you want to delete ${count} selected punch record(s) for ${formatDateDDMMYYYY(selectedDate)}?\n\nThis will remove their check-in and check-out logs and reset employee statuses. This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setBulkDeleting(true);
    try {
      const { error } = await supabase
        .from("attendance_records")
        .delete()
        .in("id", selectedRecordIds);
      if (error) throw error;

      logAudit(session, "ADMIN_BULK_DELETED_ATTENDANCE_RECORDS", {
        company_id: company.id,
        company_name: company.name,
        date: selectedDate,
        count,
        record_ids: selectedRecordIds
      });

      setSelectedRecordIds([]);
      await fetchDayAttendance(false);
    } catch (err) {
      alert("Error deleting attendance records: " + err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Download Daily Attendance Excel
  const handleExportDailyExcel = () => {
    try {
      const rows = displayRows.map(r => ({
        "Employee Code": r.employee.employee_code || "—",
        "Employee Name": r.employee.name,
        "Department": r.employee.department || "—",
        "Designation": r.employee.designation || "—",
        "Date": formatDateDDMMYYYY(selectedDate),
        "Punch In Time": formatTimeDisplay(r.punchIn),
        "Punch Out Time": formatTimeDisplay(r.punchOut),
        "Late By (Mins)": r.lateInfo.isLate ? r.lateInfo.lateMins : 0,
        "Early By (Mins)": r.earlyInfo.isEarly ? r.earlyInfo.earlyMins : 0,
        "Total Hours Worked": r.workingHours,
        "Attendance Status": r.status.toUpperCase().replace("_", " "),
        "Regularization Note": r.regularizationReason || "—"
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      if (rows.length > 0) {
        ws['!cols'] = Object.keys(rows[0]).map(k => {
          const maxLen = Math.max(
            k.length,
            ...rows.map(r => String(r[k] ?? "").length)
          );
          return { wch: Math.min(Math.max(maxLen + 4, 15), 45) };
        });
      }
      XLSX.utils.book_append_sheet(wb, ws, "Daily Attendance");
      const safeName = (company?.name || "Company").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `${safeName}_Attendance_${selectedDate}.xlsx`);
    } catch (err) {
      alert("Error exporting Excel: " + err.message);
    }
  };

  // Download Custom Range / Monthly Report
  const handleExportRangeReport = async () => {
    if (!rangeFrom || !rangeTo) return alert("Please select both From and To dates.");
    if (rangeFrom > rangeTo) return alert("From date cannot be after To date.");

    setExportingReport(true);
    try {
      const { data: rangeRecords, error } = await supabase.from("attendance_records")
        .select("*, employees(name, employee_code, department, designation)")
        .eq("company_id", company.id)
        .gte("date", rangeFrom)
        .lte("date", rangeTo)
        .order("date", { ascending: true });

      if (error) throw error;

      // Group records by employee
      const empStats = new Map();
      employees.forEach(emp => {
        empStats.set(emp.id, {
          code: emp.employee_code || "—",
          name: emp.name,
          dept: emp.department || "—",
          presentDays: 0,
          lateDays: 0,
          earlyOutDays: 0,
          absentDays: 0,
          regularizedDays: 0,
          totalMinutesWorked: 0
        });
      });

      (rangeRecords || []).forEach(r => {
        const stat = empStats.get(r.employee_id);
        if (stat) {
          if (r.regularization_status === "approved" || r.status === "regularized") {
            stat.regularizedDays++;
            stat.presentDays++;
          } else if (r.punch_in) {
            stat.presentDays++;
            const lateInfo = getLateDetails(r.punch_in);
            if (lateInfo.isLate) stat.lateDays++;
            if (r.punch_out) {
              const earlyInfo = getEarlyOutDetails(r.punch_out);
              if (earlyInfo.isEarly) stat.earlyOutDays++;
              const diffMs = new Date(r.punch_out) - new Date(r.punch_in);
              if (diffMs > 0) stat.totalMinutesWorked += Math.floor(diffMs / 60000);
            }
          }
        }
      });

      // Sheet 1: Summary Report
      const summaryRows = Array.from(empStats.values()).map(s => {
        const hrs = Math.floor(s.totalMinutesWorked / 60);
        const mins = s.totalMinutesWorked % 60;
        return {
          "Employee Code": s.code,
          "Employee Name": s.name,
          "Department": s.dept,
          "Present Days": s.presentDays,
          "Late Arrivals": s.lateDays,
          "Early Departures": s.earlyOutDays,
          "Regularized Days": s.regularizedDays,
          "Total Hours Worked": `${hrs}h ${mins}m`
        };
      });

      // Sheet 2: Detailed Daily Records
      const detailedRows = (rangeRecords || []).map(r => ({
        "Date": formatDateDDMMYYYY(r.date),
        "Employee Code": r.employees?.employee_code || "—",
        "Employee Name": r.employees?.name || "—",
        "Department": r.employees?.department || "—",
        "Punch In Time": formatTimeDisplay(r.punch_in),
        "Punch Out Time": formatTimeDisplay(r.punch_out),
        "Status": (r.status || "").toUpperCase(),
        "Regularization Status": r.regularization_status || "—",
        "Reason / Note": r.regularization_reason || "—"
      }));

      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      const wsDetail = XLSX.utils.json_to_sheet(detailedRows);

      if (summaryRows.length > 0) {
        wsSummary['!cols'] = Object.keys(summaryRows[0]).map(k => {
          const maxLen = Math.max(
            k.length,
            ...summaryRows.map(r => String(r[k] ?? "").length)
          );
          return { wch: Math.min(Math.max(maxLen + 4, 16), 40) };
        });
      }

      if (detailedRows.length > 0) {
        wsDetail['!cols'] = Object.keys(detailedRows[0]).map(k => {
          const maxLen = Math.max(
            k.length,
            ...detailedRows.map(r => String(r[k] ?? "").length)
          );
          return { wch: Math.min(Math.max(maxLen + 4, 15), 45) };
        });
      }

      XLSX.utils.book_append_sheet(wb, wsSummary, "Attendance Summary");
      XLSX.utils.book_append_sheet(wb, wsDetail, "Detailed Log");

      const safeName = (company?.name || "Company").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `${safeName}_Attendance_Report_${rangeFrom}_to_${rangeTo}.xlsx`);
      setShowReportModal(false);
    } catch (err) {
      alert("Error generating report: " + err.message);
    } finally {
      setExportingReport(false);
    }
  };

  const departmentsList = useMemo(() => {
    return Array.from(new Set(employees.map(e => e.department).filter(Boolean)));
  }, [employees]);

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 24}}>
      {/* Top Header & Navigation */}
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16}}>
        <div>
          {!isCompanyRole && onBack && (
            <div className="back-btn" onClick={onBack} style={{marginBottom: 8}}>← Back</div>
          )}
          <div className="page-title" style={{display: "flex", alignItems: "center", gap: 10}}>
            <span>⏱️ Attendance Live Dashboard</span>

          </div>
          <div className="page-sub">
            Real-time punch monitoring, late arrival tracking, and monthly attendance reports for {company?.name}.
          </div>
        </div>

        <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
          <button
            className="btn btn-ghost"
            style={{fontSize: 13, display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #cbd5e1"}}
            onClick={() => fetchDayAttendance(false)}
            title="Refresh current view"
          >
            <span>🔄</span> Refresh Now
          </button>
          <button
            className="btn btn-ghost"
            style={{fontSize: 13, display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #cbd5e1"}}
            onClick={() => setShowConfigModal(true)}
            title="Configure preferred punch timings"
          >
            <span>⚙️</span> Timings ({stdIn} - {stdOut})
          </button>
          <button
            className="btn btn-ghost"
            style={{fontSize: 13, display: "flex", alignItems: "center", gap: 6, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 700}}
            onClick={() => setShowReportModal(true)}
          >
            <span>📊</span> Custom Range / Monthly Report
          </button>
          <button
            className="btn btn-primary"
            style={{
              fontSize: 13,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #4f46e5 100%)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              boxShadow: "0 3px 12px rgba(37, 99, 235, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              borderRadius: 9,
              padding: "8px 16px",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-1.5px)";
              e.currentTarget.style.boxShadow = "0 6px 18px rgba(37, 99, 235, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 3px 12px rgba(37, 99, 235, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.4)";
            }}
            onClick={handleExportDailyExcel}
            title="Download today's attendance log as Excel spreadsheet"
          >
            <span style={{fontSize: 14}}>⬇</span>
            <span>Export Today (Excel)</span>
          </button>
          {records.length > 0 && (
            <button
              className="btn btn-ghost"
              style={{fontSize: 13, display: "flex", alignItems: "center", gap: 6, background: "#fff", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 700}}
              onClick={handleClearDayAttendance}
              disabled={clearingDay}
              title={`Delete all ${records.length} punch record(s) for ${formatDateDDMMYYYY(selectedDate)}`}
            >
              <span>🗑️</span> {clearingDay ? "Clearing..." : `Clear Day (${records.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Date Navigation & Controls Bar */}
      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
        <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
          <button
            className="btn btn-ghost btn-xs"
            style={{fontSize: 12, padding: "6px 12px", border: "1px solid #cbd5e1"}}
            onClick={() => {
              const [y, m, d] = selectedDate.split("-").map(Number);
              const dt = new Date(y, m - 1, d - 1);
              setSelectedDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`);
            }}
          >
            ◀ Previous Day
          </button>
          <div style={{display: "flex", alignItems: "center", gap: 6}}>
            <span style={{fontSize: 13, fontWeight: 700, color: "#475569"}}>📅 Date:</span>
            <DatePickerDDMMYYYY
              value={selectedDate}
              onChange={newDate => setSelectedDate(newDate)}
            />
          </div>
          <button
            className="btn btn-ghost btn-xs"
            style={{fontSize: 12, padding: "6px 12px", border: "1px solid #cbd5e1"}}
            onClick={() => {
              const [y, m, d] = selectedDate.split("-").map(Number);
              const dt = new Date(y, m - 1, d + 1);
              setSelectedDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`);
            }}
          >
            Next Day ▶
          </button>
          {selectedDate !== todayStr && (
            <button
              className="btn btn-ghost btn-xs"
              style={{fontSize: 12, padding: "6px 12px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 700}}
              onClick={() => setSelectedDate(todayStr)}
            >
              Today
            </button>
          )}

          {/* Holiday or Weekly Off Badges */}
          {dayScheduleInfo.isHoliday && (
            <span style={{fontSize: 12, fontWeight: 700, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", padding: "4px 11px", borderRadius: 16, display: "inline-flex", alignItems: "center", gap: 5}}>
              <span>🏖️</span> Holiday: {dayScheduleInfo.holidayName}
            </span>
          )}
          {!dayScheduleInfo.isHoliday && dayScheduleInfo.isWeeklyOff && (
            <span style={{fontSize: 12, fontWeight: 700, background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "4px 11px", borderRadius: 16, display: "inline-flex", alignItems: "center", gap: 5}}>
              <span>☕</span> Weekly Off ({dayScheduleInfo.dayName})
            </span>
          )}
        </div>

        <div style={{display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#64748b"}}>
          {lastSyncTime && <span>Last synced at {lastSyncTime}</span>}
        </div>
      </div>

      {/* KPI Metric Summary Cards */}
      <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10}}>
        {[
          {
            id: "all",
            label: "Total Staff",
            icon: "👥",
            value: metrics.total,
            sub: "Registered employees",
            gradient: "linear-gradient(135deg, #f8fafc 0%, #eef2f6 50%, #e2e8f0 100%)",
            border: "#cbd5e1",
            activeBorder: "#2563eb",
            textCol: "#0f172a",
            labelCol: "#475569",
            subCol: "#64748b",
            shadow: "rgba(15, 23, 42, 0.12)"
          },
          {
            id: "punched",
            label: "Punched In Today",
            icon: "⏱️",
            value: metrics.presentCount,
            sub: metrics.total > 0 ? `${Math.round((metrics.presentCount / metrics.total) * 100)}% attendance` : "0%",
            gradient: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)",
            border: "#86efac",
            activeBorder: "#16a34a",
            textCol: "#065f46",
            labelCol: "#047857",
            subCol: "#059669",
            shadow: "rgba(16, 185, 129, 0.22)"
          },
          {
            id: "present",
            label: "🟢 On Time",
            icon: "⚡",
            value: metrics.onTimeCount,
            sub: `Before ${stdIn} (+${graceMin}m)`,
            gradient: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 50%, #99f6e4 100%)",
            border: "#5eead4",
            activeBorder: "#0d9488",
            textCol: "#115e59",
            labelCol: "#0f766e",
            subCol: "#0d9488",
            shadow: "rgba(20, 184, 166, 0.22)"
          },
          {
            id: "late",
            label: "⚠️ Late Arrivals",
            icon: "⏰",
            value: metrics.lateCount,
            sub: "After grace cutoff",
            gradient: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)",
            border: "#fcd34d",
            activeBorder: "#d97706",
            textCol: "#92400e",
            labelCol: "#b45309",
            subCol: "#b45309",
            shadow: "rgba(245, 158, 11, 0.22)"
          },
          {
            id: "early_out",
            label: "🚪 Early Departures",
            icon: "🏃",
            value: metrics.earlyOutCount,
            sub: `Before ${stdOut}`,
            gradient: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)",
            border: "#d8b4fe",
            activeBorder: "#9333ea",
            textCol: "#6b21a8",
            labelCol: "#7e22ce",
            subCol: "#7e22ce",
            shadow: "rgba(168, 85, 247, 0.22)"
          },
          dayScheduleInfo.isHoliday
            ? {
                id: "holiday",
                label: "🏖️ Holiday",
                icon: "🏖️",
                value: metrics.holidayCount,
                sub: dayScheduleInfo.holidayName || "Official Holiday",
                gradient: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)",
                border: "#86efac",
                activeBorder: "#16a34a",
                textCol: "#065f46",
                labelCol: "#047857",
                subCol: "#059669",
                shadow: "rgba(16, 185, 129, 0.22)"
              }
            : dayScheduleInfo.isWeeklyOff
            ? {
                id: "weekly_off",
                label: "☕ Weekly Off",
                icon: "🛋️",
                value: metrics.weeklyOffCount,
                sub: `${dayScheduleInfo.dayName} (Non-Working Day)`,
                gradient: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #cbd5e1 100%)",
                border: "#cbd5e1",
                activeBorder: "#475569",
                textCol: "#1e293b",
                labelCol: "#334155",
                subCol: "#475569",
                shadow: "rgba(100, 116, 139, 0.22)"
              }
            : {
                id: "absent",
                label: "🔴 Not Punched In",
                icon: "📋",
                value: metrics.absentCount,
                sub: "No check-in record",
                gradient: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 50%, #fecdd3 100%)",
                border: "#fda4af",
                activeBorder: "#e11d48",
                textCol: "#9f1239",
                labelCol: "#be123c",
                subCol: "#be123c",
                shadow: "rgba(239, 68, 68, 0.22)"
              },
          {
            id: "on_leave", label: "On leave", icon: "📅", value: metrics.onLeaveCount,
            sub: "Approved leave", gradient: "linear-gradient(135deg, #eff6ff, #dbeafe)",
            border: "#bfdbfe", activeBorder: "#2563eb", textCol: "#1e3a8a",
            labelCol: "#1d4ed8", subCol: "#2563eb", shadow: "rgba(37, 99, 235, 0.16)"
          }
        ].map(c => {
          const isSelected = filterStatus === c.id;
          const isHovered = hoveredCard === c.id;
          return (
            <div
              key={c.id}
              onClick={() => setFilterStatus(prev => prev === c.id && c.id !== "all" ? "all" : c.id)}
              onMouseEnter={() => setHoveredCard(c.id)}
              onMouseLeave={() => setHoveredCard(null)}
              title={`Click to filter attendance by ${c.label}`}
              style={{
                background: c.gradient,
                border: isSelected ? `2px solid ${c.activeBorder}` : `1px solid ${isHovered ? c.activeBorder : c.border}`,
                borderRadius: 10,
                padding: "10px 12px",
                cursor: "pointer",
                boxShadow: isHovered
                  ? `0 10px 20px -3px ${c.shadow}, 0 4px 6px -2px rgba(0,0,0,0.04)`
                  : isSelected
                  ? `0 4px 12px -2px ${c.shadow}`
                  : "0 1px 3px rgba(0,0,0,0.04)",
                transform: isHovered ? "translateY(-3px)" : "translateY(0px)",
                transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: 74,
                userSelect: "none",
                position: "relative",
                overflow: "hidden"
              }}
            >
              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <span style={{fontSize: 10.5, fontWeight: 800, color: c.labelCol, textTransform: "uppercase", letterSpacing: 0.5}}>
                  {c.label}
                </span>
                <span style={{fontSize: 12, lineHeight: 1, opacity: 0.85}}>{c.icon}</span>
              </div>
              <div style={{fontSize: 21, fontWeight: 900, color: c.textCol, marginTop: 3, letterSpacing: "-0.5px", lineHeight: 1.1}}>
                {c.value}
              </div>
              <div style={{fontSize: 10.5, fontWeight: 600, color: c.subCol, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                {c.sub}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter, Search & Sorting Controls */}
      <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
        <div style={{display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 260, flexWrap: "wrap"}}>
          <input
            type="text"
            className="config-input"
            placeholder="🔍 Search employee by name, ID or department..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{margin: 0, height: 38, fontSize: 13, padding: "4px 12px", width: "100%", maxWidth: 360}}
          />
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            padding: "5px 12px",
            borderRadius: 20,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: records.length > 0 ? "rgba(37, 99, 235, 0.08)" : "#f1f5f9",
            color: records.length > 0 ? "#1d4ed8" : "#64748b",
            border: records.length > 0 ? "1px solid #bfdbfe" : "1px solid #e2e8f0"
          }}
          title={records.length > 0 ? `${records.length} employee punch records in database for this date` : "Listing active employee roster. Punch logs are recorded when employees punch in or an admin creates an entry."}
          >
            <span>{records.length > 0 ? "⏱️" : "📋"}</span>
            <span>{records.length > 0 ? `${records.length} punch log${records.length > 1 ? "s" : ""} in DB` : "0 punch logs recorded"}</span>
          </div>
        </div>

        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          {/* Status Filter */}
          <div style={{display: "flex", alignItems: "center", gap: 6}}>
            <span style={{fontSize: 12, fontWeight: 700, color: "#64748b"}}>Status:</span>
            <select
              className="config-input"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{height: 36, margin: 0, padding: "4px 10px", fontSize: 13, fontWeight: 600}}
            >
              <option value="all">All Statuses ({attendanceRows.length})</option>
              <option value="punched">Punched In Today ({metrics.presentCount})</option>
              <option value="present">Present / On Time ({metrics.onTimeCount})</option>
              <option value="late">Late Punch-In ({metrics.lateCount})</option>
              <option value="early_out">Early Punch-Out ({metrics.earlyOutCount})</option>
              {metrics.holidayCount > 0 && <option value="holiday">🏖️ Holiday ({metrics.holidayCount})</option>}
              {metrics.weeklyOffCount > 0 && <option value="weekly_off">☕ Weekly Off ({metrics.weeklyOffCount})</option>}
              <option value="on_leave">On leave ({metrics.onLeaveCount})</option>
              <option value="absent">Not Punched In ({metrics.absentCount})</option>
              <option value="regularized">Regularized ({metrics.regularizedCount})</option>
            </select>
          </div>

          {/* Department Filter */}
          {departmentsList.length > 0 && (
            <div style={{display: "flex", alignItems: "center", gap: 6}}>
              <span style={{fontSize: 12, fontWeight: 700, color: "#64748b"}}>Dept:</span>
              <select
                className="config-input"
                value={filterDept}
                onChange={e => setFilterDept(e.target.value)}
                style={{height: 36, margin: 0, padding: "4px 10px", fontSize: 13, fontWeight: 600}}
              >
                <option value="all">All Departments</option>
                {departmentsList.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {/* Sort By - Default to Employee ID */}
          <div style={{display: "flex", alignItems: "center", gap: 6}}>
            <span style={{fontSize: 12, fontWeight: 700, color: "#64748b"}}>Sort:</span>
            <select
              className="config-input"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{height: 36, margin: 0, padding: "4px 10px", fontSize: 13, fontWeight: 600}}
            >
              <option value="emp_id">🔢 Employee ID (Ascending)</option>
              <option value="late_first">⚠️ Late Arrivals First</option>
              <option value="early_out_first">🚪 Early Departures First</option>
              <option value="punch_in_desc">Punch In (Latest First)</option>
              <option value="punch_in_asc">Punch In (Earliest First)</option>
              <option value="name_asc">Employee Name (A-Z)</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Delete Selection Bar */}
      {selectedRecordIds.length > 0 && (
        <div style={{
          background: "#fef2f2",
          border: "1.5px solid #fecaca",
          borderRadius: 12,
          padding: "12px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          boxShadow: "0 2px 8px rgba(220, 38, 38, 0.08)"
        }}>
          <div style={{display: "flex", alignItems: "center", gap: 10}}>
            <span style={{fontSize: 16}}>☑️</span>
            <div>
              <span style={{fontSize: 13, fontWeight: 700, color: "#991b1b"}}>
                {selectedRecordIds.length} punch log{selectedRecordIds.length > 1 ? "s" : ""} selected
              </span>
              <span style={{fontSize: 12, color: "#b91c1c", marginLeft: 8}}>
                (for {formatDateDDMMYYYY(selectedDate)})
              </span>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              style={{fontSize: 11, padding: "2px 8px", background: "#fff", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6}}
              onClick={() => setSelectedRecordIds([])}
            >
              Clear selection
            </button>
          </div>
          <button
            type="button"
            className="btn btn-danger"
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "#ffffff",
              border: "1px solid #b91c1c",
              padding: "7px 16px",
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(220, 38, 38, 0.35)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
            onClick={handleBulkDeleteRecords}
            disabled={bulkDeleting}
          >
            <span>🗑️</span>
            <span>{bulkDeleting ? "Deleting..." : `Bulk Delete (${selectedRecordIds.length})`}</span>
          </button>
        </div>
      )}

      {/* Attendance Table */}
      {(() => {
        const rowsWithRecords = displayRows.filter(r => r.record && r.record.id);
        const isAllVisibleSelected = rowsWithRecords.length > 0 && rowsWithRecords.every(r => selectedRecordIds.includes(r.record.id));

        return (
          <div style={{background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)"}}>
            {loading ? (
              <div style={{padding: 48, textAlign: "center", color: "#64748b"}}>
                <div style={{fontSize: 24, marginBottom: 8}}>⏳</div>
                <div>Loading live attendance data...</div>
              </div>
            ) : displayRows.length === 0 ? (
              <div style={{padding: 48, textAlign: "center", color: "#64748b"}}>
                <div style={{fontSize: 32, marginBottom: 10}}>🔍</div>
                <div style={{fontWeight: 700, fontSize: 16, color: "#1e293b"}}>No records found</div>
                <div style={{fontSize: 13, marginTop: 4}}>No employees matched your active filter or search criteria for this date.</div>
              </div>
            ) : (
              <div style={{overflowX: "auto"}}>
                <table className="payroll-table" style={{width: "100%", minWidth: 1050, margin: 0}}>
                  <thead>
                    <tr style={{background: "#f8fafc", borderBottom: "2px solid #e2e8f0", userSelect: "none"}}>
                      {/* Checkbox Column for Bulk Selection */}
                      <th style={{padding: "14px 16px", width: 44, textAlign: "center"}}>
                        <input
                          type="checkbox"
                          title={rowsWithRecords.length > 0 ? "Select/Deselect all visible punch logs" : "No punch logs recorded on this date. The table lists active employees from the employee database; punch logs are created once employees check in or an admin creates a manual punch."}
                          checked={isAllVisibleSelected}
                          disabled={rowsWithRecords.length === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const ids = rowsWithRecords.map(r => r.record.id);
                              setSelectedRecordIds(prev => Array.from(new Set([...prev, ...ids])));
                            } else {
                              const visibleIds = new Set(rowsWithRecords.map(r => r.record.id));
                              setSelectedRecordIds(prev => prev.filter(id => !visibleIds.has(id)));
                            }
                          }}
                          style={{cursor: rowsWithRecords.length > 0 ? "pointer" : "not-allowed", width: 16, height: 16, accentColor: "#2563eb", verticalAlign: "middle"}}
                        />
                      </th>
                      <th
                        onClick={() => handleColumnSort("emp_id")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "emp_id" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 130}}
                        title="Click to sort by Employee ID"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>EMPLOYEE ID</span>
                          <span style={{fontSize: 10, color: sortCol === "emp_id" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "emp_id" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => handleColumnSort("name")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "name" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 180}}
                        title="Click to sort by Employee Name"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>EMPLOYEE</span>
                          <span style={{fontSize: 10, color: sortCol === "name" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "name" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => handleColumnSort("punch_in")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "punch_in" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 140}}
                        title="Click to sort by Punch In Time"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>PUNCH IN</span>
                          <span style={{fontSize: 10, color: sortCol === "punch_in" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "punch_in" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => handleColumnSort("punch_out")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "punch_out" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 150}}
                        title="Click to sort by Punch Out Time"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>PUNCH OUT</span>
                          <span style={{fontSize: 10, color: sortCol === "punch_out" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "punch_out" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => handleColumnSort("hours")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "hours" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 120}}
                        title="Click to sort by Total Hours"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>TOTAL HOURS</span>
                          <span style={{fontSize: 10, color: sortCol === "hours" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "hours" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => handleColumnSort("status")}
                        style={{padding: "14px 18px", textAlign: "left", fontSize: 12, fontWeight: 800, color: sortCol === "status" ? "#2563eb" : "#475569", cursor: "pointer", minWidth: 160}}
                        title="Click to sort by Status"
                      >
                        <div style={{display: "inline-flex", alignItems: "center", gap: 5}}>
                          <span>STATUS</span>
                          <span style={{fontSize: 10, color: sortCol === "status" ? "#2563eb" : "#94a3b8"}}>
                            {sortCol === "status" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th style={{padding: "14px 18px", textAlign: "right", fontSize: 12, fontWeight: 800, color: "#475569", minWidth: 190}}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map(row => {
                      const emp = row.employee;
                      const rec = row.record;

                      return (
                        <tr key={emp.id} style={{borderBottom: "1px solid #f1f5f9", transition: "background 0.15s"}}>
                          {/* Bulk Select Checkbox */}
                          <td style={{padding: "14px 16px", textAlign: "center", width: 44}}>
                            {rec?.id ? (
                              <input
                                type="checkbox"
                                checked={selectedRecordIds.includes(rec.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRecordIds(prev => [...prev, rec.id]);
                                  } else {
                                    setSelectedRecordIds(prev => prev.filter(id => id !== rec.id));
                                  }
                                }}
                                style={{cursor: "pointer", width: 16, height: 16, accentColor: "#2563eb", verticalAlign: "middle"}}
                                title={`Select punch record for ${emp.name}`}
                              />
                            ) : (
                              <span 
                                title={`${emp.name} has not punched in on this date. No punch record exists in the database to select or delete.`}
                                style={{color: "#cbd5e1", fontSize: 13, userSelect: "none", cursor: "help"}}
                              >
                                —
                              </span>
                            )}
                          </td>

                          {/* Employee ID */}
                          <td className="td-emp-id" style={{padding: "14px 18px", color: "#64748b", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap"}}>
                            {emp.employee_code || "—"}
                          </td>

                          {/* Employee Info */}
                          <td className="td-name" style={{padding: "14px 18px", verticalAlign: "middle", whiteSpace: "nowrap"}}>
                            <div style={{color: "#1e293b", fontWeight: 600, fontSize: 13}}>{emp.name}</div>
                          </td>

                          {/* Punch In */}
                          <td style={{padding: "14px 18px"}}>
                            {row.punchIn ? (
                              <div style={{display: "flex", flexDirection: "column", gap: 3}}>
                                <div style={{fontWeight: 700, fontSize: 13, color: "#0f172a"}}>
                                  {formatTimeDisplay(row.punchIn)}
                                </div>
                                {row.lateInfo.isLate ? (
                                  <span style={{fontSize: 11, fontWeight: 700, background: "#fef3c7", color: "#b45309", padding: "2px 8px", borderRadius: 12, display: "inline-block", width: "fit-content"}}>
                                    ⚠️ Late (+{row.lateInfo.lateMins}m)
                                  </span>
                                ) : (
                                  <span style={{fontSize: 11, fontWeight: 700, background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 12, display: "inline-block", width: "fit-content"}}>
                                    🟢 On Time
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{color: "#94a3b8", fontSize: 13}}>—</span>
                            )}
                          </td>

                          {/* Punch Out */}
                          <td style={{padding: "14px 18px"}}>
                            {row.punchOut ? (
                              <div style={{display: "flex", flexDirection: "column", gap: 3}}>
                                <div style={{fontWeight: 700, fontSize: 13, color: "#0f172a"}}>
                                  {formatTimeDisplay(row.punchOut)}
                                </div>
                                {row.earlyInfo.isEarly ? (
                                  <span style={{fontSize: 11, fontWeight: 700, background: "#f3e8ff", color: "#7e22ce", padding: "2px 8px", borderRadius: 12, display: "inline-block", width: "fit-content"}}>
                                    🚪 Early Out (-{row.earlyInfo.earlyMins}m)
                                  </span>
                                ) : (
                                  <span style={{fontSize: 11, fontWeight: 700, background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 12, display: "inline-block", width: "fit-content"}}>
                                    🟢 Standard
                                  </span>
                                )}
                              </div>
                            ) : row.punchIn ? (
                              <span style={{fontSize: 11, fontWeight: 700, background: "#eff6ff", color: "#1d4ed8", padding: "3px 8px", borderRadius: 12, display: "inline-flex", alignItems: "center", gap: 4}}>
                                <span style={{width: 6, height: 6, borderRadius: "50%", background: "#3b82f6"}} /> Active / In Office
                              </span>
                            ) : (
                              <span style={{color: "#94a3b8", fontSize: 13}}>—</span>
                            )}
                          </td>

                          {/* Total Hours */}
                          <td style={{padding: "14px 18px"}}>
                            <span style={{fontWeight: 700, fontSize: 13, color: row.workingHours !== "—" ? "#0f172a" : "#94a3b8"}}>
                              {row.workingHours}
                            </span>
                          </td>

                          {/* Status */}
                          <td style={{padding: "14px 18px"}}>
                            {row.status === "regularized" ? (
                              <span style={{background: "#dcfce7", color: "#166534", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                ✅ Regularized
                              </span>
                            ) : row.status === "late" ? (
                              <span style={{background: "#fef3c7", color: "#b45309", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                ⚠️ Late Punch-In
                              </span>
                            ) : row.status === "early_out" ? (
                              <span style={{background: "#f3e8ff", color: "#7e22ce", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                🚪 Early Out
                              </span>
                            ) : row.status === "present" ? (
                              <span style={{background: "#dcfce7", color: "#166534", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                🟢 Present
                              </span>
                            ) : row.status === "on_leave" ? (
                              <span style={{background: "#eff6ff", color: "#1d4ed8", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                On leave
                              </span>
                            ) : row.status === "holiday" ? (
                              <span style={{background: "#ecfdf5", color: "#065f46", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12, border: "1px solid #a7f3d0", display: "inline-flex", alignItems: "center", gap: 5}}>
                                🏖️ Holiday{dayScheduleInfo.holidayName ? ` (${dayScheduleInfo.holidayName})` : ""}
                              </span>
                            ) : row.status === "weekly_off" ? (
                              <span style={{background: "#f1f5f9", color: "#475569", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12, border: "1px solid #cbd5e1", display: "inline-flex", alignItems: "center", gap: 5}}>
                                ☕ Weekly Off
                              </span>
                            ) : (
                              <span style={{background: "#fee2e2", color: "#dc2626", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12}}>
                                🔴 Not Punched In
                              </span>
                            )}
                            {row.regularizationReason && (
                              <div style={{fontSize: 11, color: "#64748b", marginTop: 4, fontStyle: "italic", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={row.regularizationReason}>
                                Reason: "{row.regularizationReason}"
                              </div>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{padding: "14px 18px", textAlign: "right", whiteSpace: "nowrap"}}>
                            <div style={{display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end"}}>
                              <button
                                className="btn btn-ghost btn-xs"
                                style={{fontSize: 12, padding: "4px 10px", border: "1px solid #cbd5e1", background: "#fff"}}
                                onClick={() => openManualPunch(emp, rec)}
                                title="Record or adjust punch for this employee"
                              >
                                ✏️ Adjust / Record
                              </button>
                              {rec?.id ? (
                                <button
                                  className="btn btn-ghost btn-xs"
                                  style={{fontSize: 12, padding: "4px 8px", border: "1px solid #fecaca", color: "#dc2626", background: "#fef2f2", fontWeight: 700}}
                                  onClick={() => handleDeleteRecord(emp, rec)}
                                  disabled={deletingId === rec.id}
                                  title={`Delete attendance record for ${emp.name} on ${formatDateDDMMYYYY(selectedDate)}`}
                                >
                                  {deletingId === rec.id ? "..." : "🗑️ Delete"}
                                </button>
                              ) : (
                                <button
                                  className="btn btn-ghost btn-xs"
                                  style={{fontSize: 12, padding: "4px 8px", border: "1px solid #e2e8f0", color: "#94a3b8", background: "#f8fafc", cursor: "not-allowed"}}
                                  onClick={() => alert(`${emp.name} has not punched in on ${formatDateDDMMYYYY(selectedDate)}. No database record exists to delete.`)}
                                  title="No attendance record exists for this employee on this date"
                                >
                                  🗑️ Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── MODAL 1: Preferred Timing Configuration ── */}
      {showConfigModal && (
        <div className="overlay" style={{zIndex: 9999}} onClick={() => setShowConfigModal(false)}>
          <div className="modal" style={{maxWidth: 480}} onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18}}>
              <div style={{fontSize: 17, fontWeight: 800, color: "#1e293b"}}>⏱️ Preferred Company Punch Timings</div>
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  padding: 0,
                  lineHeight: 1
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#fee2e2";
                  e.currentTarget.style.color = "#dc2626";
                  e.currentTarget.style.borderColor = "#fca5a5";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.color = "#64748b";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Close dialog"
              >
                ✕
              </button>
            </div>
            <div style={{fontSize: 13, color: "#64748b", marginBottom: 16}}>
              Set official daily working hours for {company?.name}. Employees checking in after Preferred In + Grace will be flagged Late automatically.
            </div>

            {configMsg && (
              <div className={configMsg.includes("Error") ? "err-msg" : "success-msg"} style={{marginBottom: 14}}>
                {configMsg}
              </div>
            )}

            <form onSubmit={handleSaveScheduleConfig} style={{display: "flex", flexDirection: "column", gap: 16}}>
              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Preferred Punch-In</div>
                  <input
                    type="time"
                    className="config-input"
                    value={configForm.punch_in}
                    onChange={e => setConfigForm({...configForm, punch_in: e.target.value})}
                    required
                  />
                </div>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Preferred Punch-Out</div>
                  <input
                    type="time"
                    className="config-input"
                    value={configForm.punch_out}
                    onChange={e => setConfigForm({...configForm, punch_out: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Grace Period (Minutes)</div>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    className="config-input"
                    value={configForm.grace}
                    onChange={e => setConfigForm({...configForm, grace: e.target.value})}
                    required
                  />
                </div>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>EOD Auto Cutoff</div>
                  <input
                    type="time"
                    className="config-input"
                    value={configForm.cutoff}
                    onChange={e => setConfigForm({...configForm, cutoff: e.target.value})}
                  />
                </div>
              </div>

              <div style={{display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8}}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowConfigModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingConfig}>
                  {savingConfig ? "Saving..." : "Save Timings"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL 2: Manual Punch / Adjustment ── */}
      {manualModal && (
        <div className="overlay" style={{zIndex: 9999}} onClick={() => setManualModal(null)}>
          <div className="modal" style={{maxWidth: 460}} onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
              <div style={{fontSize: 17, fontWeight: 800, color: "#1e293b"}}>✏️ Record / Adjust Attendance</div>
              <button
                type="button"
                onClick={() => setManualModal(null)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  padding: 0,
                  lineHeight: 1
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#fee2e2";
                  e.currentTarget.style.color = "#dc2626";
                  e.currentTarget.style.borderColor = "#fca5a5";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.color = "#64748b";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Close dialog"
              >
                ✕
              </button>
            </div>
            <div style={{fontSize: 13, color: "#64748b", marginBottom: 16}}>
              Adjust punch records for <strong>{manualModal.emp.name}</strong> ({manualModal.emp.employee_code}) on <strong>{formatDateDDMMYYYY(selectedDate)}</strong>.
            </div>

            <form onSubmit={handleSaveManualPunch} style={{display: "flex", flexDirection: "column", gap: 14}}>
              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Punch-In Time</div>
                  <input
                    type="time"
                    className="config-input"
                    value={manualForm.punch_in}
                    onChange={e => setManualForm({...manualForm, punch_in: e.target.value})}
                  />
                </div>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Punch-Out Time</div>
                  <input
                    type="time"
                    className="config-input"
                    value={manualForm.punch_out}
                    onChange={e => setManualForm({...manualForm, punch_out: e.target.value})}
                  />
                </div>
              </div>

              <div className="config-field">
                <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Attendance Status</div>
                <select
                  className="config-input"
                  value={manualForm.status}
                  onChange={e => setManualForm({...manualForm, status: e.target.value})}
                >
                  <option value="present">Present (On-Time)</option>
                  <option value="late">Late Punch-In</option>
                  <option value="early_out">Early Punch-Out</option>
                  <option value="regularized">Regularized</option>
                  <option value="absent">Absent</option>
                </select>
              </div>

              <div className="config-field">
                <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>Admin Note / Reason</div>
                <input
                  type="text"
                  className="config-input"
                  value={manualForm.note}
                  onChange={e => setManualForm({...manualForm, note: e.target.value})}
                  placeholder="e.g. On-site client meeting / verified manual punch"
                />
              </div>

              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8}}>
                {manualModal?.rec?.id ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{color: "#dc2626", border: "1px solid #fecaca", background: "#fef2f2", fontSize: 13, fontWeight: 700}}
                    onClick={() => handleDeleteRecord(manualModal.emp, manualModal.rec, true)}
                    disabled={manualSaving || deletingId === manualModal.rec.id}
                    title="Delete this attendance record"
                  >
                    {deletingId === manualModal.rec.id ? "Deleting..." : "🗑️ Delete Record"}
                  </button>
                ) : <div />}
                <div style={{display: "flex", gap: 8}}>
                  <button type="button" className="btn btn-ghost" onClick={() => setManualModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={manualSaving}>
                    {manualSaving ? "Saving..." : "Save Record"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL 3: Custom Date Range / Monthly Report Download ── */}
      {showReportModal && (
        <div className="overlay" style={{zIndex: 9999}} onClick={() => setShowReportModal(false)}>
          <div className="modal" style={{maxWidth: 480}} onClick={e => e.stopPropagation()}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
              <div style={{fontSize: 17, fontWeight: 800, color: "#1e293b"}}>📊 Download Attendance Report</div>
              <button
                type="button"
                onClick={() => setShowReportModal(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  padding: 0,
                  lineHeight: 1
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#fee2e2";
                  e.currentTarget.style.color = "#dc2626";
                  e.currentTarget.style.borderColor = "#fca5a5";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.color = "#64748b";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Close dialog"
              >
                ✕
              </button>
            </div>
            <div style={{fontSize: 13, color: "#64748b", marginBottom: 16}}>
              Export comprehensive attendance summary and daily records for <strong>{company?.name}</strong> across any date range.
            </div>

            <div style={{display: "flex", flexDirection: "column", gap: 16}}>
              {/* Range Presets */}
              <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  style={{fontSize: 12, padding: "5px 10px", border: "1px solid #cbd5e1"}}
                  onClick={() => {
                    const d = new Date();
                    setRangeFrom(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
                    setRangeTo(todayStr);
                  }}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  style={{fontSize: 12, padding: "5px 10px", border: "1px solid #cbd5e1"}}
                  onClick={() => {
                    const d = new Date();
                    const prevMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                    const lastDay = new Date(d.getFullYear(), d.getMonth(), 0);
                    setRangeFrom(`${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}-01`);
                    setRangeTo(`${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`);
                  }}
                >
                  Last Month
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  style={{fontSize: 12, padding: "5px 10px", border: "1px solid #cbd5e1"}}
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() - 7);
                    setRangeFrom(d.toISOString().split("T")[0]);
                    setRangeTo(todayStr);
                  }}
                >
                  Last 7 Days
                </button>
              </div>

              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>From Date (DD/MM/YYYY)</div>
                  <DatePickerDDMMYYYY
                    value={rangeFrom}
                    onChange={setRangeFrom}
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="config-field">
                  <div style={{fontSize: 11, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>To Date (DD/MM/YYYY)</div>
                  <DatePickerDDMMYYYY
                    value={rangeTo}
                    max={todayStr}
                    onChange={setRangeTo}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <div style={{background: "#f8fafc", padding: 12, borderRadius: 8, fontSize: 12, color: "#475569", border: "1px solid #e2e8f0"}}>
                <div>📁 The generated Excel workbook will contain:</div>
                <div style={{marginTop: 4}}>• <strong>Sheet 1 (Summary):</strong> Total days present, late arrivals, early departures, and total hours worked per employee.</div>
                <div style={{marginTop: 2}}>• <strong>Sheet 2 (Detailed Log):</strong> Every single check-in, check-out, and regularization entry for the selected range.</div>
              </div>

              <div style={{display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4}}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowReportModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleExportRangeReport} disabled={exportingReport}>
                  {exportingReport ? "Generating Excel..." : "⬇ Download Excel Report"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── PILLAR 1 & 5: RECRUITMENT & OFFBOARDING PDF GENERATORS
// ─────────────────────────────────────────────────────────────────────────────

function generateOfferLetterPDF(candidate, company) {
  try {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 15;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 10;

    // Date & Ref
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    const todayStr = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    doc.text(`Date: ${todayStr}`, M, curY);
    const refNo = `REF/OFF/${String(candidate.id || "001").slice(-4).toUpperCase()}/${new Date().getFullYear()}`;
    doc.text(`Ref No: ${refNo}`, W - M, curY, { align: "right" });
    curY += 8;

    if (candidate.requisition_code) { doc.text(`Requisition: ${candidate.requisition_code}`, M, curY); curY += 5; }
    // Recipient
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);
    doc.text("To,", M, curY); curY += 5;
    doc.text(candidate.name || "Candidate", M, curY); curY += 4.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    if (candidate.email) { doc.text(`Email: ${candidate.email}`, M, curY); curY += 4.5; }
    if (candidate.phone) { doc.text(`Phone: +91 ${candidate.phone}`, M, curY); curY += 4.5; }
    curY += 4;

    // Subject / Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(26, 58, 92);
    doc.text(`SUBJECT: OFFER OF APPOINTMENT AS ${String(candidate.role || "ASSOCIATE").toUpperCase()}`, M, curY);
    curY += 7;

    // Formal body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    const ctcVal = parseFloat(candidate.offered_ctc || 0);
    const ctcText = ctcVal > 0 ? `INR ${fmtINR(ctcVal)} per annum` : "mutually agreed terms";
    const introText = `Dear ${candidate.name},\n\nWe are pleased to offer you the position of "${candidate.role || "Team Member"}" in the ${candidate.department || "General"} department at ${company.name || "TrustWork Client"}. We were impressed with your credentials, skills, and experience during the evaluation process, and we look forward to having you on our team.\n\nYour scheduled Date of Joining is ${candidate.expected_doj ? fmtDDMMYYYY(candidate.expected_doj) : "as mutually agreed"}. You will be based at ${company.address ? company.address.replace(/\n/g, ", ") : "our corporate office"}.\n\nYour Total Cost to Company (CTC) will be ${ctcText}. The detailed breakdown of your compensation structure is outlined in Annexure A below.`;
    const splitIntro = doc.splitTextToSize(introText, W - M * 2);
    doc.text(splitIntro, M, curY);
    curY += splitIntro.length * 4.3 + 5;

    // Annexure A: Compensation Table
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(26, 58, 92);
    doc.text("ANNEXURE A: COMPENSATION DETAILS", M, curY);
    curY += 3;

    const annualCtc = Math.max(0, parseFloat(candidate.offered_ctc || 0));
    const monthlyCtc = Math.round(annualCtc / 12);
    const monthlyBasic = candidate.basic_salary && parseFloat(candidate.basic_salary) > 0 ? parseFloat(candidate.basic_salary) : Math.round(monthlyCtc * 0.5);
    const monthlyHra = Math.round(monthlyBasic * 0.4);
    const pfEr = Math.round(Math.min(monthlyBasic, 25000) * 0.12);
    const esiEr = monthlyCtc <= 21000 ? Math.ceil(monthlyCtc * 0.0325) : 0;
    const gratuity = Math.round(monthlyBasic * 0.0481);
    const monthlySpecial = Math.max(0, monthlyCtc - (monthlyBasic + monthlyHra + pfEr + esiEr + gratuity));

    const salaryRows = [
      ["Basic Salary", fmtINR(monthlyBasic), fmtINR(monthlyBasic * 12)],
      ["House Rent Allowance (HRA)", fmtINR(monthlyHra), fmtINR(monthlyHra * 12)],
      ["Special / Other Allowance", fmtINR(monthlySpecial), fmtINR(monthlySpecial * 12)],
      ["Employer Provident Fund (PF)", fmtINR(pfEr), fmtINR(pfEr * 12)],
      ["Employer ESIC", esiEr > 0 ? fmtINR(esiEr) : "N/A", esiEr > 0 ? fmtINR(esiEr * 12) : "N/A"],
      ["Statutory Gratuity Provision", fmtINR(gratuity), fmtINR(gratuity * 12)],
      ["Total Cost to Company (CTC)", fmtINR(monthlyCtc), fmtINR(annualCtc)]
    ];

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      head: [["Compensation Component", "Monthly (INR)", "Annual (INR)"]],
      body: salaryRows,
      theme: "grid",
      headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      bodyStyles: { fontSize: 8, textColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: 42, halign: "right" },
        2: { cellWidth: 43, halign: "right" }
      },
      didParseCell: (data) => {
        if (data.row.index === salaryRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = [15, 23, 42];
        }
      }
    });

    curY = doc.lastAutoTable.finalY + 8;

    // Employment Terms
    const terms = candidate.offer_letter_data?.terms?.trim()
      ? doc.splitTextToSize(candidate.offer_letter_data.terms.trim(), W - M * 2)
      : [
      "1. Probation Period: You will be on probation for a period of 3 months from your date of joining.",
      "2. Notice Period: During probation, notice period is 15 days; post confirmation, notice period is 30 days.",
      "3. Working Hours: Normal business hours are 9:30 AM to 6:30 PM, Monday through Friday / Saturday as per policy."
    ];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    terms.forEach(t => {
      if (curY > 270) { doc.addPage(); curY = addDocHeader(doc, W, 297, company) + 12; }
      doc.text(t, M, curY);
      curY += 4;
    });

    curY += 6;

    if (curY > 245) { doc.addPage(); curY = addDocHeader(doc, W, 297, company) + 12; }
    // Signatures
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "TrustWork Client"}`, M, curY);
    doc.text("Candidate Acceptance", W - M, curY, { align: "right" });
    curY += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory", M, curY);
    doc.text("Signature & Date", W - M, curY, { align: "right" });

    addDocFooter(doc, W, 297);
    const fname = `Offer_Letter_${(candidate.name || "Candidate").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating offer letter PDF", err);
    alert("Could not generate Offer Letter PDF: " + err.message);
  }
}

function generateFnFPdfDoc(settlement, emp, company) {
  try {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 14;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 8;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(26, 58, 92);
    doc.text("FULL & FINAL SETTLEMENT STATEMENT", W / 2, curY, { align: "center" });
    curY += 6;

    // Employee & Exit metadata box
    const metaRows = [
      ["Employee Name", emp.name || "—", "Employee Code", emp.employee_code || "—"],
      ["Designation", emp.designation || "—", "Department", emp.department || "—"],
      ["Date of Joining", (emp.doj || emp.date_of_joining) ? fmtDate(emp.doj || emp.date_of_joining) : "—", "Last Working Day (LWD)", settlement.lwd ? fmtDate(settlement.lwd) : "—"],
      ["PAN Number", emp.pan || "—", "Settlement Date", settlement.settlement_date ? fmtDate(settlement.settlement_date) : fmtDate(new Date().toISOString())],
      ["Payment Status", settlement.payment_status === "paid" ? "Paid" : "Payment not confirmed", "Payment Date", settlement.payment_date ? fmtDate(settlement.payment_date) : "—"]
    ];

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      body: metaRows,
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 1.5 },
      columnStyles: {
        0: { fontStyle: "bold", textColor: [100, 116, 139], cellWidth: 40 },
        1: { textColor: [15, 23, 42], cellWidth: 50 },
        2: { fontStyle: "bold", textColor: [100, 116, 139], cellWidth: 42 },
        3: { textColor: [15, 23, 42], cellWidth: 50 }
      }
    });

    curY = doc.lastAutoTable.finalY + 6;

    // Earnings & Deductions Tables
    const earningItems = [
      ["Unpaid Days Salary in Final Month (" + (settlement.payable_days || 0) + " days)", fmtINR(settlement.unpaid_salary || 0)],
      ["Leave Encashment (" + (settlement.leave_balance || 0) + " days unused)", fmtINR(settlement.leave_encashment || 0)],
      ["Gratuity Settlement (" + (settlement.gratuity_years || 0) + " completed yrs)", fmtINR(settlement.gratuity || 0)],
      ["Bonus / Arrears / Incentives", fmtINR(settlement.bonus || 0)],
      ["TOTAL EARNINGS (A)", fmtINR(settlement.total_earnings || 0)]
    ];

    const deductionItems = [
      ["Notice Period Shortfall (" + (settlement.shortfall_days || 0) + " days)", fmtINR(settlement.notice_recovery || 0)],
      ["Salary Advance / Loan Recovery", fmtINR(settlement.advance_recovery || 0)],
      ["Asset / Damage Recovery", fmtINR(settlement.asset_recovery || 0)],
      ["Other Deductions (as recorded)", fmtINR(settlement.statutory_deductions || 0)],
      ["TOTAL DEDUCTIONS (B)", fmtINR(settlement.total_deductions || 0)]
    ];

    const tableBody = [];
    for (let i = 0; i < Math.max(earningItems.length, deductionItems.length); i++) {
      const earn = earningItems[i] || ["", ""];
      const ded = deductionItems[i] || ["", ""];
      tableBody.push([earn[0], earn[1], ded[0], ded[1]]);
    }

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      head: [["Earnings & Entitlements (A)", "Amount (INR)", "Deductions & Recoveries (B)", "Amount (INR)"]],
      body: tableBody,
      theme: "grid",
      headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 7.5, textColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 62 },
        1: { cellWidth: 29, halign: "right" },
        2: { cellWidth: 62 },
        3: { cellWidth: 29, halign: "right" }
      },
      didParseCell: (data) => {
        if (data.row.index === tableBody.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = [15, 23, 42];
        }
      }
    });

    curY = doc.lastAutoTable.finalY + 8;

    // Net Payable Banner
    const netVal = settlement.net_payable || 0;
    doc.setFillColor(240, 253, 244);
    doc.setDrawColor(187, 247, 208);
    doc.roundedRect(M, curY, W - M * 2, 16, 2, 2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(22, 101, 52);
    doc.text("NET FULL & FINAL SETTLEMENT PAYABLE (A − B):", M + 6, curY + 7);
    doc.setFontSize(13);
    doc.text(`INR ${fmtINR(netVal)}`, W - M - 6, curY + 11, { align: "right" });
    curY += 22;

    // Clearance Sign-offs
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(26, 58, 92);
    doc.text("DEPARTMENT CLEARANCE & NO-DUES SIGN-OFF", M, curY);
    curY += 4;

    const clearanceRows = [
      ["IT & Assets", "Laptop & hardware returned. Email & system access deactivated.", (settlement.clearance?.it_assets && settlement.clearance?.email_revoked) ? "CLEARED" : "PENDING"],
      ["HR & Admin", "ID Card, access keys, company documents surrendered.", settlement.clearance?.id_badge ? "CLEARED" : "PENDING"],
      ["Finance & Accounts", "All salary advances, imprest cash, and loans reconciled.", settlement.clearance?.accounts_cleared ? "CLEARED" : "PENDING"]
    ];

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      body: clearanceRows,
      theme: "plain",
      styles: { fontSize: 7.5, cellPadding: 1.5 },
      columnStyles: {
        0: { fontStyle: "bold", textColor: [15, 23, 42], cellWidth: 35 },
        1: { textColor: [71, 85, 105], cellWidth: 105 },
        2: { fontStyle: "bold", textColor: [22, 163, 74], cellWidth: 42, halign: "right" }
      }
    });

    curY = doc.lastAutoTable.finalY + 14;

    // Signatures
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "Company"}`, M, curY);
    doc.text("Employee Acceptance & Discharge", W - M, curY, { align: "right" });
    curY += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory", M, curY);
    doc.text("Employee Signature", W - M, curY, { align: "right" });

    addDocFooter(doc, W, 297);
    const fname = `FnF_Settlement_${(emp.name || "Employee").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating FnF PDF", err);
    alert("Could not generate FnF Settlement PDF: " + err.message);
  }
}

function generateRelievingLetterPDF(settlement, emp, company) {
  try {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 12;

    const todayStr = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date: ${todayStr}`, M, curY);
    const refNo = `REF/REL/${String(emp.employee_code || emp.id || "001").replace(/[^a-zA-Z0-9]/g, "")}/${new Date().getFullYear()}`;
    doc.text(`Ref No: ${refNo}`, W - M, curY, { align: "right" });
    curY += 12;

    // Header Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12.5);
    doc.setTextColor(26, 58, 92);
    doc.text("SERVICE CERTIFICATE & RELIEVING LETTER", W / 2, curY, { align: "center" });
    curY += 10;

    doc.setFontSize(9.5);
    doc.text("TO WHOMSOEVER IT MAY CONCERN", M, curY);
    curY += 8;

    const dojStr = emp.doj ? fmtDate(emp.doj) : "their date of joining";
    const lwdStr = settlement.lwd ? fmtDate(settlement.lwd) : (emp.date_of_exit ? fmtDate(emp.date_of_exit) : "their last working day");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(51, 65, 85);

    const para1 = `This is to certify that ${emp.name} (Employee Code: ${emp.employee_code || "—"}) was in employment with ${company.name || "the company"} from ${dojStr} to ${lwdStr}.`;
    const splitP1 = doc.splitTextToSize(para1, W - M * 2);
    doc.text(splitP1, M, curY);
    curY += splitP1.length * 5 + 4;

    const para2 = `At the time of leaving, ${emp.name} was serving as "${emp.designation || "Team Member"}" in the ${emp.department || "Operations"} Department.`;
    const splitP2 = doc.splitTextToSize(para2, W - M * 2);
    doc.text(splitP2, M, curY);
    curY += splitP2.length * 5 + 4;

    const para3 = `During their tenure with us, their performance and conduct were found to be satisfactory. All company property, assets, ID credentials, and documentation entrusted to them have been duly handed over, and all financial accounts have been completely reconciled.`;
    const splitP3 = doc.splitTextToSize(para3, W - M * 2);
    doc.text(splitP3, M, curY);
    curY += splitP3.length * 5 + 4;

    const para4 = `We hereby relieve ${emp.name} from all official duties and responsibilities with effect from the close of business hours on ${lwdStr}.\n\nWe thank them for their contributions to our organization and wish them the very best in all their future endeavors.`;
    const splitP4 = doc.splitTextToSize(para4, W - M * 2);
    doc.text(splitP4, M, curY);
    curY += splitP4.length * 5 + 16;

    // Signatory
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "TrustWork Client"}`, M, curY);
    curY += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory / Head of HR", M, curY);

    addDocFooter(doc, W, 297);
    const fname = `Relieving_Letter_${(emp.name || "Employee").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating relieving letter PDF", err);
    alert("Could not generate Relieving Letter PDF: " + err.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ── PRIORITY 1: SALARY & EMPLOYMENT CERTIFICATE GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export async function generateCertificatePDF({ employee, company, certType = "combined", purpose = "Bank Loan Application", addressedTo = "", refNo = null }) {
  try {
    if (company?.config?.logo_base64) {
      await cacheLogoImage(company.config.logo_base64);
    }
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 10;

    const todayStr = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date: ${todayStr}`, M, curY);
    const codeClean = String(employee.employee_code || employee.id || "001").replace(/[^a-zA-Z0-9]/g, "");
    const generatedRef = refNo || `REF/CERT/${codeClean}/${new Date().getFullYear()}`;
    doc.text(`Ref No: ${generatedRef}`, W - M, curY, { align: "right" });
    curY += 10;

    // Recipient
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    if (addressedTo && addressedTo.trim()) {
      doc.text("To,", M, curY); curY += 4.5;
      const addrLines = doc.splitTextToSize(addressedTo.trim(), W - M * 2);
      doc.text(addrLines, M, curY);
      curY += addrLines.length * 4.5 + 4;
    } else {
      doc.text("TO WHOMSOEVER IT MAY CONCERN", M, curY);
      curY += 8;
    }

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(26, 58, 92);
    const titleText = certType === "salary" 
      ? "SALARY / INCOME CERTIFICATE" 
      : (certType === "employment" ? "BONAFIDE EMPLOYMENT CERTIFICATE" : "CERTIFICATE OF EMPLOYMENT AND COMPENSATION");
    doc.text(titleText, W / 2, curY, { align: "center" });
    curY += 8;

    // Body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(51, 65, 85);

    const dojStr = employee.doj || employee.date_of_joining ? fmtDate(employee.doj || employee.date_of_joining) : "their date of joining";
    const statusText = employee.employment_status === "confirmed" ? "permanent and confirmed" : (employee.employment_status || "regular active");
    const introText = `This is to certify that Mr./Ms. ${employee.name} (Employee Code: ${employee.employee_code || "—"}, PAN: ${employee.pan || "—"}) is in active employment with ${company.name || "the company"} since ${dojStr}. They are currently serving as "${employee.designation || "Team Member"}" in the ${employee.department || "General"} Department as a ${statusText} employee.`;
    const splitIntro = doc.splitTextToSize(introText, W - M * 2);
    doc.text(splitIntro, M, curY);
    curY += splitIntro.length * 4.8 + 4;

    if (certType === "salary" || certType === "combined") {
      const basicVal = parseFloat(employee.basic_salary || employee.basic || 0);
      const sim = calcSalaryFull({
        basic: basicVal,
        month: 1,
        year: new Date().getFullYear(),
        cfg: company.config || DEFAULT_CONFIG,
        emp_id: employee.id,
        extra: employee
      });

      const monthlyGross = sim.gross || basicVal;
      const annualGross = monthlyGross * 12;
      const monthlyCtc = sim.ctc || monthlyGross;
      const annualCtc = monthlyCtc * 12;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(26, 58, 92);
      doc.text("CURRENT SALARY & COMPENSATION BREAKDOWN", M, curY);
      curY += 3;

      const salaryRows = [
        ["Basic Salary", fmtINR(sim.basic), fmtINR(sim.basic * 12)],
        ["House Rent Allowance (HRA)", fmtINR(sim.hra), fmtINR(sim.hra * 12)],
      ];
      if (sim.da > 0) salaryRows.push(["Dearness Allowance (DA)", fmtINR(sim.da), fmtINR(sim.da * 12)]);
      if (sim.special > 0) salaryRows.push(["Special / Other Allowance", fmtINR(sim.special), fmtINR(sim.special * 12)]);
      if (sim.bonus > 0) salaryRows.push(["Statutory Bonus Component", fmtINR(sim.bonus), fmtINR(sim.bonus * 12)]);
      salaryRows.push(["Gross Monthly Salary", fmtINR(monthlyGross), fmtINR(annualGross)]);

      const pfEmp = sim.pf_emp || 0;
      const esiEmp = sim.esi_emp || 0;
      const pt = sim.pt || 0;
      const totalDeduc = pfEmp + esiEmp + pt;
      const netPay = Math.max(0, monthlyGross - totalDeduc);

      salaryRows.push(["Employee Deductions (PF / ESI / PT)", fmtINR(totalDeduc), fmtINR(totalDeduc * 12)]);
      salaryRows.push(["Net Monthly Take-Home Pay", fmtINR(netPay), fmtINR(netPay * 12)]);
      salaryRows.push(["Total Cost to Company (CTC)", fmtINR(monthlyCtc), fmtINR(annualCtc)]);

      doc.autoTable({
        startY: curY,
        margin: { left: M, right: M },
        head: [["Compensation Component", "Monthly Amount", "Annualized Amount"]],
        body: salaryRows,
        theme: "grid",
        headStyles: { fillColor: [26, 58, 92], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85] },
        columnStyles: {
          0: { cellWidth: 94 },
          1: { cellWidth: 40, halign: "right" },
          2: { cellWidth: 40, halign: "right" }
        },
        didParseCell: (data) => {
          if ([salaryRows.length - 3, salaryRows.length - 2, salaryRows.length - 1].includes(data.row.index)) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [241, 245, 249];
            data.cell.styles.textColor = [15, 23, 42];
          }
        }
      });

      curY = doc.lastAutoTable.finalY + 6;
    }

    // Purpose & Non-liability clause
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    const purposeClean = purpose || "Bank Loan / Financial Verification";
    const clauseText = `This certificate is issued at the specific written request of the employee for the purpose of "${purposeClean}" without any financial liability, guarantee, or commitment on the part of ${company.name || "the company"} or its management.`;
    const splitClause = doc.splitTextToSize(clauseText, W - M * 2);
    doc.text(splitClause, M, curY);
    curY += splitClause.length * 4.3 + 12;

    // Signatory Block
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "TrustWork Client"}`, M, curY);
    curY += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory", M, curY);
    doc.text("Human Resources & Payroll Administration", M, curY + 4);

    addDocFooter(doc, W, 297);
    const fname = `Certificate_${(employee.name || "Employee").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating certificate PDF", err);
    alert("Could not generate Certificate PDF: " + err.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ── PRIORITY 2: SALARY INCREMENT & APPRAISAL LETTER GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export async function generateIncrementLetterPDF({ employee, company, hikeRecord }) {
  try {
    if (company?.config?.logo_base64) {
      await cacheLogoImage(company.config.logo_base64);
    }
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 10;

    const todayStr = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date: ${todayStr}`, M, curY);
    const codeClean = String(employee.employee_code || employee.id || "001").replace(/[^a-zA-Z0-9]/g, "");
    const generatedRef = `REF/INC/${codeClean}/${new Date().getFullYear()}`;
    doc.text(`Ref No: ${generatedRef}`, W - M, curY, { align: "right" });
    curY += 8;

    // Recipient
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text("To,", M, curY); curY += 4.5;
    doc.text(employee.name || "Employee", M, curY); curY += 4.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text(`Employee Code: ${employee.employee_code || "—"}  |  Department: ${employee.department || "General"}`, M, curY); curY += 4;
    doc.text(`Designation: ${employee.designation || "Team Member"}`, M, curY); curY += 7;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(26, 58, 92);
    doc.text("ANNUAL COMPENSATION REVISION / SALARY INCREMENT LETTER", W / 2, curY, { align: "center" });
    curY += 8;

    // Formal body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(51, 65, 85);

    const effDateStr = hikeRecord?.date ? fmtDate(hikeRecord.date) : todayStr;
    const hikePct = hikeRecord?.hike_pct !== undefined ? hikeRecord.hike_pct : 0;
    const signText = hikePct >= 0 ? `+${hikePct}%` : `${hikePct}%`;
    const introText = `Dear ${employee.name},\n\nWe take great pleasure in commending you for your performance, dedication, and valuable contributions toward the goals and growth of ${company.name || "the company"}.\n\nIn recognition of your continued efforts, the Management is pleased to inform you that your compensation has been revised with effect from ${effDateStr}. Your revised monthly compensation and updated Cost to Company (CTC) are detailed below:`;
    const splitIntro = doc.splitTextToSize(introText, W - M * 2);
    doc.text(splitIntro, M, curY);
    curY += splitIntro.length * 4.6 + 4;

    const oldGross = parseFloat(hikeRecord?.old_salary || 0);
    const newGross = parseFloat(hikeRecord?.new_salary || employee.basic_salary || employee.basic || 0);

    const oldSim = calcSalaryFull({ basic: oldGross, cfg: company.config || DEFAULT_CONFIG, emp_id: employee.id, extra: employee });
    const newSim = calcSalaryFull({ basic: newGross, cfg: company.config || DEFAULT_CONFIG, emp_id: employee.id, extra: employee });

    const summaryRows = [
      ["Monthly Gross Salary", fmtINR(oldGross), fmtINR(newGross), signText],
      ["Annual Cost to Company (CTC)", fmtINR(oldSim.ctc * 12), fmtINR(newSim.ctc * 12), signText]
    ];

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      head: [["Component", "Previous (INR)", "Revised (INR)", "Revision"]],
      body: summaryRows,
      theme: "grid",
      headStyles: { fillColor: [26, 58, 92], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5, textColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 74 },
        1: { cellWidth: 35, halign: "right" },
        2: { cellWidth: 35, halign: "right" },
        3: { cellWidth: 30, halign: "center", fontStyle: "bold", textColor: [22, 163, 74] }
      }
    });

    curY = doc.lastAutoTable.finalY + 6;

    // Detailed Annexure
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(26, 58, 92);
    doc.text("REVISED MONTHLY SALARY STRUCTURE (ANNEXURE)", M, curY);
    curY += 3;

    const annexureRows = [
      ["Basic Salary", fmtINR(newSim.basic), fmtINR(newSim.basic * 12)],
      ["House Rent Allowance (HRA)", fmtINR(newSim.hra), fmtINR(newSim.hra * 12)],
    ];
    if (newSim.da > 0) annexureRows.push(["Dearness Allowance (DA)", fmtINR(newSim.da), fmtINR(newSim.da * 12)]);
    if (newSim.special > 0) annexureRows.push(["Special / Other Allowance", fmtINR(newSim.special), fmtINR(newSim.special * 12)]);
    annexureRows.push(["Total Monthly Gross", fmtINR(newGross), fmtINR(newGross * 12)]);
    annexureRows.push(["Employer Statutory Benefits (PF/ESI/Gratuity)", fmtINR(newSim.ctc - newGross), fmtINR((newSim.ctc - newGross) * 12)]);
    annexureRows.push(["Total Annual Cost to Company (CTC)", fmtINR(newSim.ctc), fmtINR(newSim.ctc * 12)]);

    doc.autoTable({
      startY: curY,
      margin: { left: M, right: M },
      head: [["Earnings Component", "Monthly Amount (INR)", "Annualized Amount (INR)"]],
      body: annexureRows,
      theme: "grid",
      headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 8, textColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 94 },
        1: { cellWidth: 40, halign: "right" },
        2: { cellWidth: 40, halign: "right" }
      },
      didParseCell: (data) => {
        if ([annexureRows.length - 3, annexureRows.length - 1].includes(data.row.index)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = [15, 23, 42];
        }
      }
    });

    curY = doc.lastAutoTable.finalY + 6;

    // Confidentiality clause
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const confText = "Confidentiality Note: The terms of this compensation revision are strictly confidential between you and the company. All other terms and conditions of your employment contract remain unchanged. We look forward to your continued commitment and wishing you greater success with us.";
    const splitConf = doc.splitTextToSize(confText, W - M * 2);
    doc.text(splitConf, M, curY);
    curY += splitConf.length * 4 + 10;

    // Signatures
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "TrustWork Client"}`, M, curY);
    doc.text("Employee Acknowledgement", W - M, curY, { align: "right" });
    curY += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory / HR Management", M, curY);
    doc.text("Signature & Date", W - M, curY, { align: "right" });

    addDocFooter(doc, W, 297);
    const fname = `Increment_Letter_${(employee.name || "Employee").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating increment letter PDF", err);
    alert("Could not generate Increment Letter PDF: " + err.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ── PRIORITY 3: FORMAL APPOINTMENT LETTER / EMPLOYMENT CONTRACT
// ─────────────────────────────────────────────────────────────────────────────
export async function generateAppointmentLetterPDF(employee, company, customRef = undefined) {
  try {
    if (company?.config?.logo_base64) {
      await cacheLogoImage(company.config.logo_base64);
    }
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, M = 18;
    const HDR = addDocHeader(doc, W, 297, company);
    let curY = HDR + 8;

    const todayStr = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date: ${todayStr}`, M, curY);

    const codeClean = String(employee.employee_code || employee.id || "001").replace(/[^a-zA-Z0-9]/g, "");
    const autoRef = `REF/APT/${codeClean}/${new Date().getFullYear()}`;
    const finalRef = customRef !== undefined 
      ? String(customRef).trim()
      : (employee.appointment_ref_no !== undefined ? String(employee.appointment_ref_no).trim() : (employee.ref_no !== undefined ? String(employee.ref_no).trim() : autoRef));

    // If reference number is left blank, omit it
    if (finalRef) {
      doc.text(`Ref No: ${finalRef}`, W - M, curY, { align: "right" });
    }
    curY += 7;

    // Recipient
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    doc.text("To,", M, curY); curY += 4.5;
    doc.text(employee.name || "Employee", M, curY); curY += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    if (employee.perm_address || employee.current_address || employee.temp_address) {
      const addrClean = (employee.perm_address || employee.current_address || employee.temp_address || "").replace(/\n/g, ", ");
      doc.text(addrClean.length > 70 ? addrClean.substring(0, 67) + "..." : addrClean, M, curY);
      curY += 4;
    }
    if (employee.phone) { doc.text(`Phone: +91 ${employee.phone}`, M, curY); curY += 4; }
    curY += 3;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(26, 58, 92);
    doc.text("FORMAL LETTER OF APPOINTMENT", W / 2, curY, { align: "center" });
    curY += 7;

    // Formal body clauses
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(51, 65, 85);

    const dojStr = employee.doj || employee.date_of_joining ? fmtDate(employee.doj || employee.date_of_joining) : "your joining date";
    const desig = employee.designation || "Associate";
    const dept = employee.department || "General";

    const clauses = [
      `1. APPOINTMENT & COMMENCEMENT: We are pleased to appoint you as "${desig}" in the ${dept} Department with effect from ${dojStr}. Your place of posting will be at our office in ${company.address ? company.address.split("\n")[0] : "our corporate headquarters"}.`,
      "2. PROBATION & CONFIRMATION: You shall be on probation for an initial period of six (6) months from your date of joining. Upon successful completion of the probation period and satisfactory performance, your employment will be confirmed in writing.",
      "3. WORKING HOURS & CODE OF CONDUCT: You will observe standard company working hours and comply with all policies, confidentiality guidelines, statutory standing orders, and codes of conduct as framed by the Management from time to time.",
      "4. TERMINATION & NOTICE PERIOD: During the probation period, either party may terminate this employment by giving thirty (30) days written notice or gross salary in lieu thereof. Upon confirmation, the notice period shall be sixty (60) days.",
      "5. CONFIDENTIALITY & INTELLECTUAL PROPERTY: You agree to hold all company proprietary information, client data, and trade secrets in strict confidence. Any inventions, software, or works developed during your employment shall belong exclusively to the company."
    ];

    clauses.forEach(cl => {
      const splitCl = doc.splitTextToSize(cl, W - M * 2);
      doc.text(splitCl, M, curY);
      curY += splitCl.length * 4.2 + 2.5;
    });

    curY += 2;

    // Annexure: Salary
    const cleanSalaryStr = String(employee.basic_salary ?? employee.basic ?? employee.gross_salary ?? employee.gross ?? employee.salary ?? 0).replace(/,/g, '').replace(/[^0-9.]/g, '');
    const grossVal = Math.max(0, parseFloat(cleanSalaryStr) || 0);
    const sim = calcSalaryFull({ basic: grossVal, cfg: company.config || DEFAULT_CONFIG, emp_id: employee.id, extra: employee });
    const ctcMonthly = Math.round(sim.ctc || grossVal);
    const ctcAnnual = ctcMonthly * 12;

    const fmtPdfCurr = (val) => {
      const num = Math.round(parseFloat(val) || 0);
      return "Rs. " + num.toLocaleString("en-IN");
    };

    // If space on Page 1 is tight, cleanly break to Page 2
    if (curY > 185) {
      addDocFooter(doc, W, 297);
      doc.addPage();
      const HDR2 = addDocHeader(doc, W, 297, company);
      curY = HDR2 + 10;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(26, 58, 92);
    doc.text(`ANNEXURE: COMPENSATION DETAILS (Annual CTC: ${fmtPdfCurr(ctcAnnual)})`, M, curY);
    curY += 5;

    const salaryRows = [
      ["Basic Salary", fmtPdfCurr(sim.basic), fmtPdfCurr(sim.basic * 12)],
      ["House Rent Allowance (HRA)", fmtPdfCurr(sim.hra), fmtPdfCurr(sim.hra * 12)],
      ["Special / Other Allowance", fmtPdfCurr(sim.special), fmtPdfCurr(sim.special * 12)],
      ["Employer Statutory Benefits (PF/ESI/Gratuity)", fmtPdfCurr(sim.ctc - sim.gross), fmtPdfCurr((sim.ctc - sim.gross) * 12)],
      ["Total Cost to Company (CTC)", fmtPdfCurr(ctcMonthly), fmtPdfCurr(ctcAnnual)]
    ];

    doc.autoTable({
      startY: curY,
      tableWidth: W - M * 2,
      margin: { left: M, right: M },
      head: [["Component", "Monthly (INR)", "Annual (INR)"]],
      body: salaryRows,
      theme: "grid",
      headStyles: { fillColor: [26, 58, 92], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 8, textColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 94, halign: "left" },
        1: { cellWidth: 40, halign: "right" },
        2: { cellWidth: 40, halign: "right" }
      },
      didParseCell: (data) => {
        if (data.row.index === salaryRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = [15, 23, 42];
        }
      }
    });

    curY = doc.lastAutoTable.finalY + 8;

    // Check if signatures fit on page (A4 height is 297, footer starts at 280)
    if (curY > 255) {
      addDocFooter(doc, W, 297);
      doc.addPage();
      const HDR3 = addDocHeader(doc, W, 297, company);
      curY = HDR3 + 12;
    }

    // Signatures
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`For ${company.name || "Company"}`, M, curY);
    doc.text("Employee Acceptance", W - M, curY, { align: "right" });
    curY += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Authorized Signatory", M, curY);
    doc.text("Signature & Acceptance Date", W - M, curY, { align: "right" });

    addDocFooter(doc, W, 297);
    const fname = `Appointment_Letter_${(employee.name || "Employee").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
    doc.save(fname);
  } catch (err) {
    console.error("Error generating appointment letter PDF", err);
    alert("Could not generate Appointment Letter PDF: " + err.message);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ── CERTIFICATE MODAL COMPONENT (Admin direct generation & Employee request)
// ─────────────────────────────────────────────────────────────────────────────
function CertificateModal({ isOpen, onClose, employee, company, mode = "admin_generate", onSubmit }) {
  const [certType, setCertType] = useState("combined");
  const [purpose, setPurpose] = useState("Home Loan Application");
  const [customPurpose, setCustomPurpose] = useState("");
  const [addressedTo, setAddressedTo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !employee) return null;

  const resolvedPurpose = purpose === "Other" ? (customPurpose.trim() || "General Verification") : purpose;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "admin_generate") {
        await generateCertificatePDF({
          employee,
          company,
          certType,
          purpose: resolvedPurpose,
          addressedTo
        });
        onClose();
      } else if (onSubmit) {
        await onSubmit({
          cert_type: certType,
          purpose: resolvedPurpose,
          addressed_to: addressedTo
        });
        onClose();
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose} style={{zIndex: 9999}}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: 520, background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 20px 40px rgba(0,0,0,0.15)"}}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
          <div>
            <div style={{fontSize: 18, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8}}>
              <span>📜</span> {mode === "admin_generate" ? "Issue Official Certificate" : "Request Salary / Employment Certificate"}
            </div>
            <div style={{fontSize: 12, color: "#64748b", marginTop: 2}}>
              For <strong>{employee.name}</strong> ({employee.employee_code || "ID"})
            </div>
          </div>
          <button onClick={onClose} style={{background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b"}}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{display: "flex", flexDirection: "column", gap: 14}}>
          <div>
            <label style={{fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 6}}>
              Certificate Type *
            </label>
            <div style={{display: "flex", flexDirection: "column", gap: 8}}>
              {[
                { id: "combined", label: "Combined Salary & Employment Certificate", desc: "Includes confirmed tenure, designation, full monthly salary breakdown & Annual CTC (Recommended for Home Loans & Visas)" },
                { id: "salary", label: "Salary / Income Certificate", desc: "Detailed breakdown of monthly earnings, standard deductions, and Annual CTC" },
                { id: "employment", label: "Bonafide Employment Certificate", desc: "Certifies active employment, designation, department, and tenure (without salary figures)" }
              ].map(opt => (
                <label key={opt.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                  borderRadius: 8, border: certType === opt.id ? "1.5px solid #2563eb" : "1.5px solid #e2e8f0",
                  background: certType === opt.id ? "#eff6ff" : "#f8fafc", cursor: "pointer"
                }}>
                  <input type="radio" name="cert_type" checked={certType === opt.id} onChange={() => setCertType(opt.id)} style={{marginTop: 3}} />
                  <div>
                    <div style={{fontSize: 13, fontWeight: 700, color: certType === opt.id ? "#1d4ed8" : "#1e293b"}}>{opt.label}</div>
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 2}}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={{fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 6}}>
              Purpose *
            </label>
            <select
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              style={{width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 13, background: "#fff"}}
            >
              <option value="Home Loan Application">Home Loan Application</option>
              <option value="Car Loan Application">Car Loan Application</option>
              <option value="Personal Loan Application">Personal Loan Application</option>
              <option value="Credit Card Application">Credit Card Application</option>
              <option value="Visa / Embassy Application">Visa / Embassy Application</option>
              <option value="Rental / Lease Agreement">Rental / Lease Agreement</option>
              <option value="Bank Account Opening">Bank Account Opening</option>
              <option value="Other">Other Purpose</option>
            </select>
          </div>

          {purpose === "Other" && (
            <div>
              <label style={{fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 6}}>
                Specify Custom Purpose *
              </label>
              <input
                type="text"
                value={customPurpose}
                onChange={e => setCustomPurpose(e.target.value)}
                placeholder="e.g. Higher Education Verification"
                required
                style={{width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 13}}
              />
            </div>
          )}

          <div>
            <label style={{fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 4}}>
              Addressed To (Optional)
            </label>
            <input
              type="text"
              value={addressedTo}
              onChange={e => setAddressedTo(e.target.value)}
              placeholder="e.g. The Branch Manager, State Bank of India, Koramangala Branch"
              style={{width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 13}}
            />
            <div style={{fontSize: 11, color: "#94a3b8", marginTop: 4}}>
              Leave blank to address as: <em>"TO WHOMSOEVER IT MAY CONCERN"</em>
            </div>
          </div>

          <div style={{display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10, paddingTop: 14, borderTop: "1px solid #f1f5f9"}}>
            <button type="button" onClick={onClose} style={{padding: "9px 16px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer"}}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={{padding: "9px 18px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6}}>
              {submitting ? "⏳ Processing..." : (mode === "admin_generate" ? "📥 Download Certificate PDF" : "📤 Submit Request to HR")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE EXIT & SETTLEMENT
// ─────────────────────────────────────────────────────────────────────────────
function EmployeeExitAndFnFView({ company, employee, session }) {
  const [dbSettlement, setDbSettlement] = useState(null);

  useEffect(() => {
    async function loadFnF() {
      if (!employee?.id) return;
      try {
        const { data, error } = await supabase
          .from("fnf_settlements")
          .select("*")
          .eq("employee_id", employee.id)
          .maybeSingle();

        if (!error && data) {
          setDbSettlement({
            ...data,
            emp_id: data.employee_id,
            total_earnings: parseFloat(data.total_earnings || 0),
            total_deductions: parseFloat(data.total_deductions || 0),
            net_payable: parseFloat(data.net_payable || 0),
            unpaid_salary: parseFloat(data.unpaid_salary || 0),
            leave_encashment: parseFloat(data.leave_encashment || 0),
            gratuity: parseFloat(data.gratuity || 0),
            notice_recovery: parseFloat(data.notice_recovery || 0),
            clearance: data.clearance || {}
          });
        }
      } catch (e) {}
    }
    loadFnF();
  }, [employee?.id]);

  const exitRecord = useMemo(() => {
    const exits = company?.config?.offboarding?.exits || [];
    return exits.find(x => x.emp_id === employee.id) || null;
  }, [company, employee]);

  const settlement = useMemo(() => {
    return dbSettlement || company?.config?.fnf_settlements?.[employee.id] || null;
  }, [dbSettlement, company, employee]);

  const lwd = exitRecord?.actual_lwd || employee.date_of_exit;
  const isRelieved = employee.employment_status === "relieved" || exitRecord?.status === "relieved";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div style={{ background: "#ffffff", padding: "20px 24px", borderRadius: 12, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <span>📜</span> Exit, Settlement & Experience Certificate
          </h2>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
            View your notice period timeline, clearance status, and download your Full & Final Settlement statement and Relieving Letter.
          </div>
        </div>

        <span style={{
          fontSize: 12,
          fontWeight: 800,
          padding: "4px 12px",
          borderRadius: 14,
          background: isRelieved ? "#dcfce7" : "#fefce8",
          color: isRelieved ? "#16a34a" : "#ca8a04",
          border: `1px solid ${isRelieved ? "#bbf7d0" : "#fef08a"}`
        }}>
          {isRelieved ? "✓ Formally Relieved" : "⏳ Notice Period Active"}
        </span>
      </div>

      {/* Details Card */}
      <div style={{ background: "#ffffff", borderRadius: 12, padding: 22, border: "1px solid #e2e8f0", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Date of Joining</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{fmtDate(employee.doj || employee.date_of_joining)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Last Working Day (LWD)</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#2563eb", marginTop: 2 }}>{lwd ? fmtDate(lwd) : "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Designation Held</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{employee.designation || "Team Member"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Department</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{employee.department || "General"}</div>
          </div>
        </div>

        {/* Clearance Checklist */}
        <div style={{ background: "#f8fafc", padding: "16px 20px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>Clearance Handover Status</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, fontSize: 12.5 }}>
            {[
              ["it_assets", "💻 IT Hardware & Laptop Return"],
              ["id_badge", "🪪 ID Badge & Access Key Handover"],
              ["email_revoked", "🔒 System Accounts Deactivation"],
              ["accounts_cleared", "📑 Finance No-Due Certificate"]
            ].map(([k, label]) => {
              const isDone = !!(settlement?.clearance?.[k] || exitRecord?.clearance?.[k]);
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, color: isDone ? "#16a34a" : "#64748b" }}>
                  <span>{isDone ? "✅" : "⏳"}</span>
                  <span style={{ fontWeight: isDone ? 700 : 500 }}>{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settlement Summary Banner if available */}
        {settlement && (
          <div style={{ background: "#f0fdf4", padding: "16px 20px", borderRadius: 10, border: "1px solid #bbf7d0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#166534", textTransform: "uppercase" }}>{settlement.payment_status === "paid" ? "Net settlement paid" : "Net settlement payable"}</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#15803d", marginTop: 2 }}>{fmtINR(settlement.net_payable || 0)}</div>
              <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                Includes unpaid days salary ({settlement.payable_days} days) + leave encashment ({settlement.leave_balance} days)
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => generateFnFPdfDoc({...settlement, clearance: exitRecord?.clearance || settlement.clearance, lwd: settlement.lwd || settlement.last_working_day || lwd}, employee, company)}
              style={{ fontWeight: 700 }}
            >
              📄 Download FnF Payslip (PDF)
            </button>
          </div>
        )}

        {/* Official Certificate Download Buttons */}
        {isRelieved && <div style={{ display: "flex", gap: 12, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
          <button
            className="btn btn-secondary"
            onClick={() => generateRelievingLetterPDF(settlement || { lwd }, employee, company)}
            style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}
          >
            <span>📜</span> Download Official Relieving Letter (PDF)
          </button>

          {settlement && (
            <button
              className="btn btn-ghost"
              onClick={() => generateFnFPdfDoc({...settlement, clearance: exitRecord?.clearance || settlement.clearance, lwd: settlement.lwd || settlement.last_working_day || lwd}, employee, company)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span>⬇</span> Download FnF Settlement Breakdown
            </button>
          )}
        </div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── COMMAND PALETTE (CTRL + K) & FEATURE / EMPLOYEE SPOTLIGHT SEARCH
// ─────────────────────────────────────────────────────────────────────────────
export function CommandPaletteModal({
  isOpen,
  onClose,
  activeCompany,
  companies = [],
  employees = [],
  onNavigate,
  isMasterAdmin = false
}) {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveCategory("all");
      setHighlightIdx(0);
      const timer = setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const STATUTORY_FORMULAS = useMemo(() => [
    {
      triggers: ["gratuity", "gratuatiy", "grat", "fnf", "exit", "resignation"],
      title: "Statutory Gratuity Quick Reference",
      icon: "⚖️",
      formula: "(15 × Last Drawn Basic × Completed Years) ÷ 26",
      notes: [
        "Eligibility: Continuous service of 5 or more years under the Payment of Gratuity Act, 1972.",
        "Monthly Provision: ~4.81% of monthly Basic accrued towards employer liability for CTC accounting.",
        "Statutory Form: Form F (Gratuity Nomination) is generated under Employees → Onboarding."
      ],
      quickActionLabel: "Open Gratuity in FnF",
      action: () => onNavigate({ view: "offboarding", companyNav: "offboarding", offboardingTab: "fnf", requiresCompany: true })
    },
    {
      triggers: ["bonus", "statutory bonus", "annual bonus"],
      title: "Statutory Bonus Quick Reference",
      icon: "💰",
      formula: "8.33% to 20% on Basic/DA (Ceiling: ₹7,000/mo or State Min Wage)",
      notes: [
        "Payment of Bonus Act, 1965: Mandatory for establishments with 20+ employees.",
        "Eligibility: Employees earning Basic/DA ≤ ₹21,000/month.",
        "Calculation: Accrues at 8.33% statutory minimum on the statutory wage ceiling."
      ],
      quickActionLabel: "Configure Bonus Rules",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      triggers: ["pf", "epf", "provident", "uan", "eps"],
      title: "EPF / PF Statutory Reference",
      icon: "🏛️",
      formula: "Employee: 12% | Employer: 3.67% EPF + 8.33% EPS (Max ₹1,250) + 1% Admin/EDLI",
      notes: [
        "Statutory Wage Ceiling: ₹15,000/month for statutory EPF & EPS calculation.",
        "Applicability: Mandatory for establishments with 20 or more covered employees."
      ],
      quickActionLabel: "View PF Structure",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      triggers: ["esi", "esic", "medical insurance"],
      title: "ESI Statutory Reference",
      icon: "🏥",
      formula: "Employee: 0.75% of Gross | Employer: 3.25% of Gross",
      notes: [
        "Wage Ceiling: Applicable for employees earning Basic + DA ≤ ₹21,000/month (₹25,000 for disability).",
        "Applicability: Non-seasonal factories and shops with 10 or more employees."
      ],
      quickActionLabel: "View ESI Rules",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      triggers: ["posh", "harassment", "internal committee"],
      title: "POSH Act Compliance Reference",
      icon: "🛡️",
      formula: "Sexual Harassment of Women at Workplace Act, 2013",
      notes: [
        "Mandatory Internal Committee (IC) for any entity with 10+ employees.",
        "Annual POSH compliance report submission to District Officer."
      ],
      quickActionLabel: "Open Training Hub",
      action: () => onNavigate({ view: "training", companyNav: "training", requiresCompany: true })
    },
    {
      triggers: ["org", "tree", "hierarchy", "organogram"],
      title: "Org Tree & Reporting Lines Reference",
      icon: "🌳",
      formula: "Visual Hierarchical Reporting Graph",
      notes: [
        "Hierarchy is established via the 'Reporting Manager' field in employee profiles.",
        "Supports interactive zoom, department filtering, and high-res image export."
      ],
      quickActionLabel: "Open Org Tree",
      action: () => onNavigate({ view: "org", companyNav: "org", orgTab: "tree", requiresCompany: true })
    }
  ], [onNavigate]);

  const COMMAND_ITEMS = useMemo(() => [
    ...recentFeatureCommands(onNavigate,isMasterAdmin),
    {
      id: "dashboard", title: "Dashboard & Payroll Overview", category: "Dashboard", catKey: "payroll", icon: "📊", badge: "Dashboard", path: "Dashboard",
      keywords: ["dashboard", "overview", "analytics", "payroll totals"], description: "View payroll summaries and workforce insights.",
      action: () => onNavigate({ view: "dashboard", companyNav: "dashboard", requiresCompany: true })
    },
    {
      id: "leave_management", title: "Leave Management & Balances", category: "Attendance & Leave", catKey: "attendance", icon: "🏖️", badge: "Leave", path: "Attendance & Leave → Leave Management",
      keywords: ["leave management", "leave balance", "leave approval", "earned leave", "el balance"], description: "Review leave requests, employee balances, and attendance regularization.",
      action: () => onNavigate({ view: "approvals", companyNav: "leave_management", approvalsTab: "leave_attendance", requiresCompany: true })
    },
    {
      id: "payroll_workflow", title: "Payroll Review, Approval & Payment Status", category: "Payroll Processing", catKey: "payroll", icon: "✅", badge: "Payroll", path: "Payroll → Run Payroll → Review & Payment",
      keywords: ["payroll review", "approve payroll", "lock payroll", "paid", "payment status", "record payment"], description: "Choose a payroll month to review, approve, lock, or record a completed payment.",
      action: () => onNavigate({ actionType: "open_payroll", requiresCompany: true })
    },
    {
      id: "security_center", title: "Security Center", category: "Security & Audit", catKey: "company", icon: "🛡️", badge: "Security", path: "Security Center",
      keywords: ["security center", "account locks", "unlock", "access security"], description: "Review account security and lockout information.",
      action: () => onNavigate({ view: "security", companyNav: "security", requiresCompany: true })
    },
    {
      id: "gratuity_fnf",
      title: "Gratuity Calculation (Exit / FnF Settlement)",
      category: "Off-Boarding & Exit",
      catKey: "exit",
      icon: "⚖️",
      badge: "People Suite",
      path: "People → Off-Boarding → FnF Settlement",
      keywords: ["gratuity", "fnf", "settlement", "exit", "resignation", "relieved", "15 days", "service years", "final settlement", "gratuity calculation", "gratuatiy"],
      description: "Calculate 15-day exit gratuity payout under Payment of Gratuity Act upon resignation or retirement.",
      action: () => onNavigate({ view: "offboarding", companyNav: "offboarding", offboardingTab: "fnf", requiresCompany: true })
    },
    {
      id: "gratuity_provision",
      title: "Gratuity Monthly Provision (4.81% on Basic)",
      category: "Salary Structure & CTC",
      catKey: "salary",
      icon: "⚙️",
      badge: "Settings",
      path: "Settings → Salary Structure → Gratuity Provision",
      keywords: ["gratuity", "provision", "4.81", "4.81%", "basic", "ctc", "employer cost", "monthly accrual", "gratuatiy"],
      description: "Toggle whether 4.81% of Basic is accrued monthly towards company Gratuity liability in CTC.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "gratuity_form_f",
      title: "Form F — Gratuity Nomination (DOCX)",
      category: "Statutory Forms",
      catKey: "statutory",
      icon: "📄",
      badge: "Onboarding",
      path: "Employees → Onboarding → Nominees & Compliance → Form F",
      keywords: ["form f", "gratuity form", "gratuity nomination", "payment of gratuity act 1972", "nominee", "joining kit", "gratuatiy"],
      description: "Download official Word Form F nomination document with employee and nominee proportion shares.",
      action: () => onNavigate({ view: "joining_kit", companyNav: "joining_kit", joiningKitTab: "nominees", requiresCompany: true })
    },
    {
      id: "org_tree",
      title: "Org Tree & Interactive Hierarchy Chart",
      category: "People & Culture",
      catKey: "people",
      icon: "🌳",
      badge: "People Suite",
      path: "People → Org & Culture → Org Tree",
      keywords: ["org", "org tree", "hierarchy", "reporting", "manager", "chart", "company tree", "organization", "organogram"],
      description: "Interactive visual reporting hierarchy, managers, reporting lines, and exportable tree chart.",
      action: () => onNavigate({ view: "org", companyNav: "org", orgTab: "tree", requiresCompany: true })
    },
    {
      id: "org_departments",
      title: "Department Directory & Headcount",
      category: "People & Culture",
      catKey: "people",
      icon: "🏢",
      badge: "People Suite",
      path: "People → Org & Culture → Departments",
      keywords: ["department", "departments", "headcount", "teams", "divisions", "org"],
      description: "Breakdown of employees, managers, and headcounts grouped by department.",
      action: () => onNavigate({ view: "org", companyNav: "org", orgTab: "departments", requiresCompany: true })
    },
    {
      id: "org_celebrations",
      title: "Celebrations (Birthdays & Work Anniversaries)",
      category: "People & Culture",
      catKey: "people",
      icon: "🎉",
      badge: "People Suite",
      path: "People → Org & Culture → Celebrations",
      keywords: ["celebration", "celebrations", "birthday", "birthdays", "anniversary", "work anniversary", "new hires", "milestones", "birthday email", "anniversary email", "greetings", "celebration email"],
      description: "Upcoming team birthdays, work anniversaries, and new hire welcomes.",
      action: () => onNavigate({ view: "org", companyNav: "org", orgTab: "celebrations", requiresCompany: true })
    },
    {
      id: "posh_compliance",
      title: "POSH Policy, Prevention & Compliance Hub",
      category: "Training & Compliance",
      catKey: "compliance",
      icon: "🛡️",
      badge: "Compliance",
      path: "Training Hub → POSH Module",
      keywords: ["posh", "posh policy", "sexual harassment", "women safety", "internal committee", "prevention of sexual harassment", "training"],
      description: "Track Indian statutory POSH mandates, awareness training, employee acknowledgments, and policies.",
      action: () => onNavigate({ view: "training", companyNav: "training", requiresCompany: true })
    },
    ...(isMasterAdmin ? [{
      id: "cleanup_stale_data",
      title: "Purge Stale Database Entries & Orphan Storage (Super Admin)",
      category: "System & Maintenance",
      catKey: "compliance",
      icon: "🧹",
      badge: "Super Admin",
      path: "Maintenance → Database & Storage Cleaner",
      keywords: ["clean", "cleanup", "purge", "stale", "orphan", "database", "r2", "storage", "delete", "ghost"],
      description: "Scan and permanently remove stale or orphaned salary, attendance, leave records and storage files.",
      action: () => onNavigate({ modal: "cleanStaleData", requiresCompany: true })
    }] : []),
    {
      id: "training_hub",
      title: "Training Hub (Fire Safety, DPDP, Workplace)",
      category: "Training & Compliance",
      catKey: "compliance",
      icon: "🎓",
      badge: "Compliance",
      path: "Training Hub",
      keywords: ["training", "fire safety", "dpdp", "workplace safety", "compliance training", "certifications", "modules", "training email", "training reminder", "training notification", "training completion"],
      description: "Employee safety modules, certification tracking, and compliance training records.",
      action: () => onNavigate({ view: "training", companyNav: "training", requiresCompany: true })
    },
    {
      id: "bonus_settings",
      title: "Statutory Bonus Rules (8.33% / Payment of Bonus Act)",
      category: "Salary Structure & CTC",
      catKey: "salary",
      icon: "💰",
      badge: "Settings",
      path: "Settings → Salary Structure → Bonus",
      keywords: ["bonus", "statutory bonus", "8.33", "8.33%", "annual bonus", "payment of bonus act", "7000 ceiling", "ex gratia"],
      description: "Configure statutory bonus calculation, inclusion in gross, or flat monthly/annual allocation.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "salary_structure",
      title: "Salary Structure & Component Configuration",
      category: "Company Settings",
      catKey: "salary",
      icon: "⚙️",
      badge: "Settings",
      path: "Settings → Salary Structure",
      keywords: ["salary structure", "basic", "hra", "allowance", "special allowance", "minimum wage", "ctc calculator", "salary rules"],
      description: "Configure Basic %, HRA rules, PF/ESI applicability, minimum wage floors, and live calculator simulator.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "epf_rules",
      title: "EPF / PF Rules & ₹15,000 Wage Ceiling",
      category: "Salary Structure & Compliance",
      catKey: "statutory",
      icon: "🏛️",
      badge: "Statutory",
      path: "Settings → Salary Structure → EPF Rules",
      keywords: ["pf", "epf", "provident fund", "12%", "15000", "wage ceiling", "eps", "uan", "vpf", "voluntary pf"],
      description: "Configure Employee PF 12%, Employer PF/EPS split, and ₹15,000 statutory wage ceiling cap toggle.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "esi_rules",
      title: "ESI Rules (0.75% / 3.25% & ₹21,000 Threshold)",
      category: "Salary Structure & Compliance",
      catKey: "statutory",
      icon: "🏥",
      badge: "Statutory",
      path: "Settings → Salary Structure → ESI Rules",
      keywords: ["esi", "esic", "0.75", "3.25", "21000", "esi threshold", "medical benefit", "insurance"],
      description: "Configure ESI applicability, employee 0.75% and employer 3.25% deductions.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "pt_rules",
      title: "Professional Tax (PT Karnataka Slabs)",
      category: "Salary Structure & Compliance",
      catKey: "statutory",
      icon: "🏷️",
      badge: "Statutory",
      path: "Settings → Salary Structure → Professional Tax",
      keywords: ["pt", "professional tax", "karnataka pt", "200", "tax slabs", "pt deduction"],
      description: "Professional Tax deduction rules and slabs based on state jurisdiction.",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "lwf_rules",
      title: "Labour Welfare Fund (LWF Karnataka December)",
      category: "Salary Structure & Compliance",
      catKey: "statutory",
      icon: "📜",
      badge: "Statutory",
      path: "Settings → Salary Structure → LWF",
      keywords: ["lwf", "labour welfare fund", "december", "karnataka lwf", "welfare fund"],
      description: "State Labour Welfare Fund deduction (₹50 employee share in December payroll).",
      action: () => onNavigate({ view: "salary_structure", companyNav: "salary_structure", requiresCompany: true })
    },
    {
      id: "run_payroll",
      title: "Run Monthly Payroll & Process Salaries",
      category: "Payroll Processing",
      catKey: "payroll",
      icon: "💰",
      badge: "Core Payroll",
      path: "Payroll → Monthly Processing",
      keywords: ["run payroll", "process salary", "monthly payroll", "payroll register", "net pay", "salary computation", "attendance payroll"],
      description: "Calculate monthly salaries, apply leaves/absent deductions, view CTC, and finalize payroll.",
      action: () => onNavigate({ actionType: "open_payroll", requiresCompany: true })
    },
    {
      id: "payroll_excel_export",
      title: "Export Monthly Payroll Register (Excel)",
      category: "Payroll Processing",
      catKey: "payroll",
      icon: "📊",
      badge: "Reports",
      path: "Payroll → Export Payroll Register",
      keywords: ["export payroll", "payroll register", "excel payroll", "bank transfer sheet", "salary register", "ctc sheet"],
      description: "Download detailed monthly payroll register with all earnings, deductions, CTC, and Gratuity provisions.",
      action: () => onNavigate({ actionType: "open_payroll", requiresCompany: true })
    },
    {
      id: "employee_directory",
      title: "Employee Directory & Roster",
      category: "Employees",
      catKey: "employees",
      icon: "👥",
      badge: "Employees",
      path: "Employees → Directory",
      keywords: ["employees", "employee list", "staff", "directory", "roster", "headcount", "people"],
      description: "View all active employees, designations, departments, compensation, and statuses.",
      action: () => onNavigate({ view: "employees", companyNav: "employees", requiresCompany: true })
    },
    {
      id: "add_employee",
      title: "Add New Employee (Manual Entry)",
      category: "Employees",
      catKey: "employees",
      icon: "➕",
      badge: "Employees",
      path: "Employees → Add Employee",
      keywords: ["add employee", "create employee", "new hire", "onboard", "new employee"],
      description: "Create a new employee profile with personal, banking, statutory (PAN/UAN/ESIC), and salary details.",
      action: () => onNavigate({ actionType: "add_emp", requiresCompany: true })
    },
    {
      id: "bulk_upload_employees",
      title: "Bulk Upload Employees (Excel with Smart Dry-Run)",
      category: "Employees",
      catKey: "employees",
      icon: "⬆️",
      badge: "Employees",
      path: "Employees → Upload Excel",
      keywords: ["bulk upload", "upload excel", "import employees", "excel import", "smart preview", "dry run upload", "bulk add"],
      description: "Upload staff from Excel with live dry-run breakdown preview of Basic, HRA, Bonus, and Take-Home.",
      action: () => onNavigate({ actionType: "bulk_upload", requiresCompany: true })
    },
    {
      id: "download_employee_template",
      title: "Download Blank Employee Excel Template",
      category: "Employees",
      catKey: "employees",
      icon: "📋",
      badge: "Employees",
      path: "Employees → Template",
      keywords: ["template", "employee template", "download template", "excel format", "sample excel", "import template"],
      description: "Download a blank employee Excel template with company heading and mandatory column guidelines.",
      action: () => onNavigate({ actionType: "download_template", requiresCompany: true })
    },
    {
      id: "bulk_salary_hikes",
      title: "Salary Revisions & Bulk Hikes",
      category: "Compensation",
      catKey: "payroll",
      icon: "📈",
      badge: "Compensation",
      path: "Payroll → Salary Revisions",
      keywords: ["hike", "salary hike", "appraisal", "increment", "promotion", "bulk hike", "salary revision"],
      description: "Download hike spreadsheet, apply increments in bulk, and generate revision letters.",
      action: () => onNavigate({ actionType: "bulk_hike", requiresCompany: true })
    },
    {
      id: "approvals_hub",
      title: "Approvals Hub (Leaves, Attendance & Profile Changes)",
      category: "Approvals & Workflow",
      catKey: "approvals",
      icon: "🔔",
      badge: "Approvals",
      path: "Approvals",
      keywords: ["approvals", "pending", "leave approval", "regularization", "profile update review", "requests", "payroll review", "payroll approval"],
      description: "Review payroll submissions, employee leave, attendance regularization, and profile updates.",
      action: () => onNavigate({ view: "approvals", companyNav: "approvals", requiresCompany: true })
    },
    {
      id: "attendance_dashboard",
      title: "Attendance Dashboard & Daily Logs",
      category: "Attendance & Leaves",
      catKey: "attendance",
      icon: "⏱️",
      badge: "Attendance",
      path: "Attendance & Leave → Attendance",
      keywords: ["attendance", "daily attendance", "biometric", "clock in", "punch", "timesheet", "overtime", "ot"],
      description: "Track daily presence, late marks, overtime hours, biometric logs, and monthly summaries.",
      action: () => onNavigate({ view: "attendance", companyNav: "attendance", requiresCompany: true })
    },
    {
      id: "offboarding_notice",
      title: "Notice Period & Resignation Tracking",
      category: "Off-Boarding & Exit",
      catKey: "exit",
      icon: "🚪",
      badge: "People Suite",
      path: "People → Off-Boarding → Active Notice",
      keywords: ["notice period", "resignation", "resigned", "exit tracker", "last working day", "lwd", "handover"],
      description: "Track resigning employees, notice periods, pending clearances, and handover milestones.",
      action: () => onNavigate({ view: "offboarding", companyNav: "offboarding", offboardingTab: "notice", requiresCompany: true })
    },
    {
      id: "offboarding_relieved",
      title: "Relieving Letters & Service Certificates Archive",
      category: "Off-Boarding & Exit",
      catKey: "exit",
      icon: "📜",
      badge: "People Suite",
      path: "People → Off-Boarding → Relieved Archive",
      keywords: ["relieving letter", "service certificate", "experience letter", "relieved", "exited employees", "alumni"],
      description: "Download official relieving letters, experience certificates, and view exit records.",
      action: () => onNavigate({ view: "offboarding", companyNav: "offboarding", offboardingTab: "relieved", requiresCompany: true })
    },
    {
      id: "doc_hub",title:"Doc Hub — Company & Employee Documents",category:"Documents",catKey:"people",icon:"📁",badge:"HR/Admin",path:"Doc Hub",
      keywords:["doc hub","documents","company documents","employee documents","upload document","download document"],
      description:"Upload, update, download and delete private company or employee documents.",
      action:()=>onNavigate({view:"doc_hub",companyNav:"doc_hub",requiresCompany:true})
    },
    {
      id: "recruitment_pipeline",
      title: "Recruitment & Candidate Pipeline",
      category: "Recruitment",
      catKey: "people",
      icon: "🎯",
      badge: "People Suite",
      path: "People → Recruitment",
      keywords: ["recruitment", "hiring", "candidates", "interview", "pipeline", "job openings", "applicants", "candidate email", "interviewer email", "interview notification"],
      description: "Manage candidate pipeline from screening to interview rounds and convert candidates to employees.",
      action: () => onNavigate({ view: "recruitment", companyNav: "recruitment", requiresCompany: true })
    },
    {
      id: "pf_esi_exports", title: "PF ECR & ESI Ready-to-Upload Files", category: "Payroll Processing", catKey: "payroll", icon: "⬇️", badge: "Payroll", path: "Payroll → Run Payroll → Export & Deliverables",
      keywords: ["pf ecr", "ecr", "pf upload", "esi return", "esi upload", "mc template", "wagedata", "ready to upload"], description: "Choose a locked payroll month to download PF ECR text, PF Wagedata Excel, or the ESI MC template.",
      action: () => onNavigate({ actionType: "open_payroll", requiresCompany: true })
    },
    {
      id: "reports_statutory",
      title: "Reports — Employees, Payroll, Leave & Salary Revisions",
      category: "Reports & Compliance",
      catKey: "statutory",
      icon: "📈",
      badge: "Reports",
      path: "Reports → All Reports",
      keywords: ["reports", "payroll report", "employee report", "leave audit", "salary revision report", "statutory leave report"],
      description: "Download employee, payroll, salary revision, attendance, leave balance and leave audit reports.",
      action: () => onNavigate({ view: "reports", companyNav: "reports", requiresCompany: true })
    },
    {
      id: "joining_kit_hub",
      title: "Onboarding & Joining Kit Documents",
      category: "Employees & Onboarding",
      catKey: "statutory",
      icon: "📁",
      badge: "Onboarding",
      path: "Employees → Onboarding",
      keywords: ["joining kit", "appointment letter", "nda", "form 11", "form 2", "form f", "offer letter", "contract"],
      description: "Generate appointment letters, NDAs, Form 11 (EPF), Form 2 (Nomination), and Form F (Gratuity).",
      action: () => onNavigate({ view: "joining_kit", companyNav: "joining_kit", joiningKitTab: "checklist", requiresCompany: true })
    },
    {
      id: "company_profile",
      title: "Company Profile & Statutory Registrations",
      category: "Company Profile",
      catKey: "company",
      icon: "🏢",
      badge: "Profile",
      path: "Settings → Company Profile",
      keywords: ["company profile", "company details", "pan", "tan", "gstin", "cin", "address", "company logo", "bank account"],
      description: "View and edit corporate details, logo, address, PAN, TAN, GSTIN, and statutory IDs.",
      action: () => onNavigate({ view: "company_details", companyNav: "profile", requiresCompany: true })
    },
    {
      id: "audit_logs",
      title: "System Activity & Audit Logs",
      category: "Security & Audit",
      catKey: "company",
      icon: "📜",
      badge: "Security",
      path: "Activity Log",
      keywords: ["audit logs", "activity log", "audit trail", "security", "change history", "login tracking"],
      description: "Track system actions, admin logins, salary updates, and compliance downloads.",
      action: () => onNavigate({ view: "audit_logs", companyNav: "audit_logs", requiresCompany: true })
    },
    {
      id: "companies_directory",
      title: "All Companies / Switch Company Directory",
      category: "Companies",
      catKey: "company",
      icon: "🔄",
      badge: "Global",
      path: "Company Directory",
      keywords: ["switch company", "change company", "companies", "all companies", "multi company", "directory"],
      description: "Switch to a different client or subsidiary company in your multi-company workspace.",
      action: () => onNavigate({ view: "companies", requiresCompany: false })
    }
  ], [onNavigate, isMasterAdmin]);

  const q = query.trim().toLowerCase();

  // Matched statutory formulas
  const matchedFormula = useMemo(() => {
    if (!q) return null;
    return STATUTORY_FORMULAS.find(f => f.triggers.some(t => q.includes(t) || t.includes(q)));
  }, [q, STATUTORY_FORMULAS]);

  // Filtered commands
  const filteredCommands = useMemo(() => {
    const config = activeCompany?.config;
    let list = COMMAND_ITEMS.filter(item => {
      if (item.id === "companies_directory") return isMasterAdmin;
      if (item.requiredFeature && config) return config[item.requiredFeature] === true;
      if (!config) return true;
      if (item.id === "attendance_dashboard") return config.attendance_enabled === true;
      if (item.id === "leave_management") return config.leave_module_enabled === true;
      if (item.id === "bulk_salary_hikes") return config.addon_bulk_hike === true;
      if (item.id === "reports_statutory") return config.reports_enabled !== false;
      if (item.id.startsWith("org_")) return config.org_culture_enabled === true;
      if (item.id === "doc_hub") return config.doc_hub_enabled === true;
      if (item.id === "training_hub") return config.training_enabled === true;
      if (item.id === "recruitment_pipeline") return config.recruitment_enabled === true;
      if (item.id.startsWith("offboarding_") || item.id === "gratuity_fnf") return config.offboarding_enabled === true;
      return true;
    });
    if (activeCategory !== "all") {
      list = list.filter(item => item.catKey === activeCategory);
    }
    if (!q) return list.slice(0, 10);
    return list.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(q);
      const descMatch = item.description.toLowerCase().includes(q);
      const catMatch = item.category.toLowerCase().includes(q);
      const kwMatch = item.keywords.some(k => k.includes(q) || q.includes(k));
      return titleMatch || descMatch || catMatch || kwMatch || item.path.toLowerCase().includes(q);
    });
  }, [q, activeCategory, COMMAND_ITEMS, activeCompany?.config, isMasterAdmin]);

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    if (!q || !Array.isArray(employees)) return [];
    return employees.filter(emp => {
      const nameMatch = emp.name && emp.name.toLowerCase().includes(q);
      const codeMatch = emp.employee_code && emp.employee_code.toLowerCase().includes(q);
      const deptMatch = emp.department && emp.department.toLowerCase().includes(q);
      const desigMatch = emp.designation && emp.designation.toLowerCase().includes(q);
      const phoneMatch = emp.phone && emp.phone.includes(q);
      const panMatch = emp.pan && emp.pan.toLowerCase().includes(q);
      return nameMatch || codeMatch || deptMatch || desigMatch || phoneMatch || panMatch;
    }).slice(0, 6);
  }, [q, employees]);

  // Combined selectable items
  const allSelectableItems = useMemo(() => {
    const items = [];
    filteredCommands.forEach(c => items.push({ type: "command", data: c }));
    filteredEmployees.forEach(e => items.push({ type: "employee", data: e }));
    return items;
  }, [filteredCommands, filteredEmployees]);

  useEffect(() => {
    setHighlightIdx(0);
  }, [q, activeCategory]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx(prev => (prev + 1) % Math.max(1, allSelectableItems.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx(prev => (prev - 1 + Math.max(1, allSelectableItems.length)) % Math.max(1, allSelectableItems.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const current = allSelectableItems[highlightIdx];
      if (current) {
        if (current.type === "command") {
          current.data.action();
        } else if (current.type === "employee") {
          onNavigate({ actionType: "select_employee", employee: current.data, requiresCompany: true });
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 16px 20px 16px",
        animation: "fadeIn .15s ease"
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 680,
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
          animation: "scaleIn .2s cubic-bezier(0.16, 1, 0.3, 1)"
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top Input Bar */}
        <div style={{
          display: "flex",
          alignItems: "center",
          padding: "16px 20px",
          borderBottom: "1.5px solid #f1f5f9",
          gap: 12,
          background: "#ffffff"
        }}>
          <span style={{ fontSize: 20, color: "#3b82f6" }}>🔍</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search tools, Gratuity, Org tree, POSH, employees, formulas..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 16,
              fontWeight: 500,
              color: "#0f172a",
              fontFamily: "'Inter', sans-serif"
            }}
          />
          {query ? (
            <button
              onClick={() => { setQuery(""); if (inputRef.current) inputRef.current.focus(); }}
              style={{
                background: "#f1f5f9",
                border: "none",
                borderRadius: "50%",
                width: 22,
                height: 22,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#64748b",
                fontSize: 12,
                fontWeight: 700
              }}
              title="Clear search"
            >
              ✕
            </button>
          ) : (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              padding: "3px 7px",
              borderRadius: 6,
              fontFamily: "monospace"
            }}>
              ESC to exit
            </div>
          )}
        </div>

        {/* Quick Filter Categories (when no query) */}
        {!q && (
          <div style={{
            display: "flex",
            gap: 6,
            padding: "10px 20px",
            borderBottom: "1px solid #f8fafc",
            background: "#f8fafc",
            overflowX: "auto"
          }}>
            {[
              { id: "all", label: "🌟 All" },
              { id: "exit", label: "⚖️ Gratuity & Exit" },
              { id: "people", label: "👥 Org & People" },
              { id: "compliance", label: "🛡️ POSH & Training" },
              { id: "salary", label: "💰 Salary & Bonus" },
              { id: "payroll", label: "💵 Payroll" },
              { id: "employees", label: "👤 Employees" },
              { id: "statutory", label: "📄 Compliance Forms" }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontSize: 11.5,
                  fontWeight: 600,
                  border: activeCategory === cat.id ? "1.5px solid #2563eb" : "1px solid #e2e8f0",
                  background: activeCategory === cat.id ? "#eff6ff" : "#fff",
                  color: activeCategory === cat.id ? "#1d4ed8" : "#475569",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s"
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Results List */}
        <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
          
          {/* Statutory Reference Card (if trigger matched) */}
          {matchedFormula && (
            <div style={{
              background: "linear-gradient(135deg, #f0fdf4, #ecfdf5)",
              border: "1.5px solid #a7f3d0",
              borderRadius: 12,
              padding: "14px 16px",
              boxShadow: "0 2px 6px rgba(16, 185, 129, 0.08)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800, color: "#065f46" }}>
                  <span>{matchedFormula.icon}</span>
                  <span>{matchedFormula.title}</span>
                </div>
                {matchedFormula.quickActionLabel && (
                  <button
                    onClick={matchedFormula.action}
                    style={{
                      background: "#059669",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4
                    }}
                  >
                    <span>{matchedFormula.quickActionLabel}</span>
                    <span>→</span>
                  </button>
                )}
              </div>
              <div style={{
                background: "#ffffff",
                border: "1px solid #bbf7d0",
                borderRadius: 8,
                padding: "8px 12px",
                fontFamily: "monospace",
                fontSize: 13,
                fontWeight: 700,
                color: "#047857",
                marginBottom: 8
              }}>
                Formula: {matchedFormula.formula}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {matchedFormula.notes.map((note, idx) => (
                  <div key={idx} style={{ fontSize: 11.5, color: "#334155", lineHeight: 1.4 }}>
                    • {note}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tools & Navigation Results */}
          {filteredCommands.length > 0 && (
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", padding: "4px 8px 8px 8px" }}>
                {q ? "⚡ Navigation & Tools" : (activeCategory === "all" ? "🌟 Frequently Used Tools" : "⚡ Tools")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {filteredCommands.map((item, idx) => {
                  const isSelected = highlightIdx === idx;
                  return (
                    <div
                      key={item.id}
                      onClick={() => item.action()}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 10,
                        cursor: "pointer",
                        background: isSelected ? "#eff6ff" : "transparent",
                        border: isSelected ? "1px solid #bfdbfe" : "1px solid transparent",
                        transition: "all 0.1s ease"
                      }}
                    >
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: isSelected ? "#dbeafe" : "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        flexShrink: 0
                      }}>
                        {item.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: isSelected ? "#1d4ed8" : "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.title}
                          </span>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: 10,
                            background: isSelected ? "#dbeafe" : "#f1f5f9",
                            color: isSelected ? "#1e40af" : "#64748b",
                            flexShrink: 0
                          }}>
                            {item.badge}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: isSelected ? "#2563eb" : "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                          <span>{item.path}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: isSelected ? "#2563eb" : "#cbd5e1", fontWeight: 800, paddingRight: 4 }}>
                        →
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Matching Employees */}
          {filteredEmployees.length > 0 && (
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", padding: "8px 8px 6px 8px" }}>
                👥 Matching Employees ({filteredEmployees.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {filteredEmployees.map((emp, eIdx) => {
                  const itemIdx = filteredCommands.length + eIdx;
                  const isSelected = highlightIdx === itemIdx;
                  return (
                    <div
                      key={emp.id}
                      onClick={() => onNavigate({ actionType: "select_employee", employee: emp, requiresCompany: true })}
                      onMouseEnter={() => setHighlightIdx(itemIdx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "9px 12px",
                        borderRadius: 10,
                        cursor: "pointer",
                        background: isSelected ? "#eff6ff" : "#f8fafc",
                        border: isSelected ? "1px solid #bfdbfe" : "1px solid #f1f5f9",
                        transition: "all 0.1s ease"
                      }}
                    >

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                            {emp.name}
                          </span>
                          <span style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace", background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
                            {emp.employee_code || "EMP"}
                          </span>
                          {emp.department && (
                            <span style={{ fontSize: 10.5, color: "#475569", background: "#e2e8f0", padding: "1px 6px", borderRadius: 10 }}>
                              {emp.department}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                          {emp.designation || "Staff"} • {emp.gross_salary ? `Gross: ${fmtINR(emp.gross_salary)}` : "Salary configured"}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isSelected ? "#2563eb" : "#94a3b8" }}>
                        View Salary →
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Results Fallback */}
          {allSelectableItems.length === 0 && !matchedFormula && (
            <div style={{ textAlign: "center", padding: "36px 16px" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>No matches found for "{query}"</div>
              <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4, maxWidth: 360, margin: "4px auto 0 auto" }}>
                Try searching for <strong>Gratuity</strong>, <strong>Org Tree</strong>, <strong>POSH</strong>, <strong>Bonus</strong>, <strong>Bulk Upload</strong>, or an employee name.
              </div>
              <button
                onClick={() => setQuery("")}
                style={{
                  marginTop: 14,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#2563eb",
                  fontWeight: 700,
                  fontSize: 12,
                  padding: "6px 14px",
                  borderRadius: 8,
                  cursor: "pointer"
                }}
              >
                Clear Search
              </button>
            </div>
          )}

        </div>

        {/* Modal Footer Bar */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 18px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          fontSize: 11.5,
          color: "#64748b"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span><kbd style={{ background: "#e2e8f0", padding: "2px 5px", borderRadius: 4, fontFamily: "monospace", fontWeight: 700 }}>↑↓</kbd> Navigate</span>
            <span><kbd style={{ background: "#e2e8f0", padding: "2px 5px", borderRadius: 4, fontFamily: "monospace", fontWeight: 700 }}>↵</kbd> Select</span>
            <span><kbd style={{ background: "#e2e8f0", padding: "2px 5px", borderRadius: 4, fontFamily: "monospace", fontWeight: 700 }}>ESC</kbd> Close</span>
          </div>
          <div>
            {activeCompany ? (
              <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#0f172a", fontWeight: 600 }}>
                🏢 {activeCompany.name}
              </span>
            ) : (
              <span style={{ color: "#94a3b8" }}>No active company</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CleanStaleDataModal({ activeCompany, onClose, onRefreshEmployees }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleScanAndClean = async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await cleanOrphanDatabaseRecords(activeCompany.id);
      setResult(res);
      if (res && res.cleaned > 0 && onRefreshEmployees) {
        onRefreshEmployees();
      }
    } catch (err) {
      setError(err.message || "Failed to scan and clean database.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: 540, width: "95%", borderRadius: 14}}>
        <div style={{display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16}}>
          <div style={{fontSize: 28, background: "#f1f5f9", padding: "8px 12px", borderRadius: 12}}>🧹</div>
          <div>
            <div style={{fontSize: 18, fontWeight: 800, color: "#0f172a"}}>Clean Stale Database & Storage Entries</div>
            <div style={{fontSize: 12.5, color: "#64748b", marginTop: 2}}>
              Scans for orphaned records across salary records, attendance, leaves, update requests, and F&F settlements left behind by deleted employees.
            </div>
          </div>
        </div>

        {error && (
          <div style={{background: "#fef2f2", color: "#b91c1c", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 14}}>
            ⚠️ {error}
          </div>
        )}

        {result ? (
          <div style={{background: result.cleaned > 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${result.cleaned > 0 ? "#bbf7d0" : "#e2e8f0"}`, borderRadius: 10, padding: 16, marginBottom: 16}}>
            <div style={{fontWeight: 800, fontSize: 14, color: result.cleaned > 0 ? "#15803d" : "#334155", marginBottom: 8}}>
              {result.cleaned > 0 ? `✅ Cleaned ${result.cleaned} stale orphaned record(s)!` : "✨ Database is clean! No orphaned records found."}
            </div>
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: "#475569"}}>
              <div>• Salary records: <strong>{result.salaries || 0}</strong></div>
              <div>• Attendance records: <strong>{result.attendance || 0}</strong></div>
              <div>• Leave records: <strong>{result.leaves || 0}</strong></div>
              <div>• Update requests: <strong>{result.updateRequests || 0}</strong></div>
              <div>• FnF settlements: <strong>{result.fnf || 0}</strong></div>
            </div>
          </div>
        ) : (
          <div style={{background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 12.5, color: "#475569", lineHeight: 1.5}}>
            Click below to inspect and purge any ghost rows in the database that don't belong to any active employee in <strong>{activeCompany?.name}</strong>.
          </div>
        )}

        <div style={{display: "flex", justifyContent: "flex-end", gap: 10}}>
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>
            {result ? "Close" : "Cancel"}
          </button>
          <button className="btn btn-primary" onClick={handleScanAndClean} disabled={loading}>
            {loading ? "⏳ Scanning & Cleaning..." : (result ? "Scan Again" : "🧹 Scan & Purge Stale Records")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => { clearLegacyEmailCaches(localStorage); }, []);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginNotice, setLoginNotice] = useState('');
  const [passwordSetup, setPasswordSetup] = useState(()=>{
    const tokenHash=new URLSearchParams(window.location.hash.slice(1)).get('employee_setup');
    return {active:Boolean(tokenHash || new URLSearchParams(window.location.search).get('password_setup') === '1'),tokenHash};
  });
  useEffect(()=>{
    if(passwordSetup.tokenHash) window.history.replaceState(null,'',window.location.pathname+window.location.search);
  },[passwordSetup.tokenHash]);

  const [workspace, setWorkspace] = useState(null);
  const [workspaceError, setWorkspaceError] = useState('');
  useEffect(() => {
    let disposed = false;
    loadWorkspace().then(result => {
      if (disposed) return;
      applyWorkspaceBranding(result); setWorkspace(result);
    }).catch(error => { if (!disposed) setWorkspaceError(error.message); });
    return () => { disposed = true; };
  }, []);

  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    if (!workspace) return;
    let disposed = false;
    async function restore() {
      try {
        if (passwordSetup.active) return;
        const saved = JSON.parse(localStorage.getItem('tw_current_user') || 'null');
        let verified;
        if (saved?.role === 'employee' || isPwaMode()) verified=await employeeAuthSession();
        else {
          try { verified=await adminSession(); }
          catch (_) { verified=await employeeAuthSession(); }
          if (!verified) verified=await employeeAuthSession();
        }
        if (!disposed) setCurrentUser(verified);
        if (verified) {
          localStorage.setItem('tw_current_user',JSON.stringify(verified));
          if (!verified.passwordChangeRequired) await syncFileSession(verified.role==='employee'?verified:null);
        } else localStorage.removeItem('tw_current_user');
      } catch (_) {
        if (!disposed) setCurrentUser(null);
        localStorage.removeItem('tw_current_user');
      } finally { if (!disposed) setAuthReady(true); }
    }
    restore();
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED') setTimeout(() => {
        const saved=JSON.parse(localStorage.getItem('tw_current_user') || 'null');
        if (!saved?.passwordChangeRequired) syncFileSession(saved?.role==='employee'?saved:null).catch(() => {});
      }, 0);
      if (event === 'PASSWORD_RECOVERY') setPasswordSetup(current=>current.active?current:{active:true,tokenHash:null});
      if (event === 'SIGNED_OUT') { setCurrentUser(null); localStorage.removeItem('tw_current_user'); }

    });
    return () => { disposed = true; listener?.subscription?.unsubscribe(); };
  }, [workspace,passwordSetup.active]);

  const [deferredPrompt, setDeferredPrompt] = useState(() => {
    return (typeof window !== "undefined" && window.deferredInstallPrompt) || null;
  });
  const [showPwaGuide, setShowPwaGuide] = useState(false);

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      setDeferredPrompt(e);
    };
    const handleReady = () => {
      if (window.deferredInstallPrompt) {
        setDeferredPrompt(window.deferredInstallPrompt);
      }
    };
    const handleInstalled = () => {
      window.deferredInstallPrompt = null;
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("tw-install-ready", handleReady);
    window.addEventListener("tw-app-installed", handleInstalled);
    if (window.deferredInstallPrompt) {
      setDeferredPrompt(window.deferredInstallPrompt);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("tw-install-ready", handleReady);
      window.removeEventListener("tw-app-installed", handleInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    const promptEvent = window.deferredInstallPrompt || deferredPrompt;
    if (promptEvent) {
      promptEvent.prompt();
      try {
        const { outcome } = await promptEvent.userChoice;
        if (outcome === "accepted") {
          window.deferredInstallPrompt = null;
          setDeferredPrompt(null);
        }
      } catch (err) {
        console.error("Install prompt error:", err);
      }
    } else {
      setShowPwaGuide(true);
    }
  };

  const [companies, setCompanies]   = useState([]);
  const [employees, setEmployees]   = useState({});
  const [salaries,  setSalaries]    = useState({});
  const [esiStore,  setEsiStore]    = useState({});
  const [pfStore,   setPfStore]     = useState({});
  const [loading,   setLoading]     = useState(true);
  const [dbError,   setDbError]     = useState(null);

  // Navigation
  const [view, setView]             = useState("companies"); // companies | employees | payroll | employee_detail
  const [companyNav, setCompanyNav] = useState("profile"); // profile | employees | joining_kit | payroll | dashboard
  const [expandedSidebarGroup, setExpandedSidebarGroup] = useState("settings");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSalaryBreakdown, setShowSalaryBreakdown] = useState(true);
  const [showStatBreakdown, setShowStatBreakdown] = useState(false);
  const [showTotalBreakdown, setShowTotalBreakdown] = useState(true);
  const [showPersonalBreakdown, setShowPersonalBreakdown] = useState(true);
  const [showAllowancesBreakdown, setShowAllowancesBreakdown] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [visibleEmpCount, setVisibleEmpCount] = useState(10);
  const [payrollRunSort, setPayrollRunSort] = useState({ key: 'employee_code', dir: 'asc' });

  const safeNavigate = (action, tabName) => {
    if (hasUnsavedChanges) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to leave without saving?")) {
        return;
      }
    }
    setHasUnsavedChanges(false);
    action();
  };

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [offboardingTab, setOffboardingTab] = useState("notice");
  const [orgTab, setOrgTab] = useState("tree");
  const [joiningKitTab, setJoiningKitTab] = useState("checklist");

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const handleCommandNavigate = (destination) => {
    safeNavigate(() => {
      let targetCo = activeCompany;
      if (destination.requiresCompany && !targetCo && companies.length > 0) {
        targetCo = companies[0];
        setActiveCompany(targetCo);
        loadEmployees(targetCo.id);
      }

      const featureForView = {org:"org_culture_enabled",recruitment:"recruitment_enabled",offboarding:"offboarding_enabled",attendance:"attendance_enabled",training:"training_enabled",doc_hub:"doc_hub_enabled"};
      const feature = featureForView[destination.view];
      if (feature && targetCo?.config?.[feature] !== true) { alert("This module is disabled for this company. Enable it in Company Profile → Add-on Features first."); return; }
      if (destination.actionType === "download_template") {
        downloadEmployeeTemplate(targetCo?.name || "");
        return;
      }
      if (destination.actionType === "bulk_upload") {
        setExpandedSidebarGroup("employees");
        setView("employees");
        setCompanyNav("employees");
        setTimeout(() => {
          const inp = document.getElementById("empUploadInput");
          if (inp) inp.click();
        }, 150);
        return;
      }
      if (destination.actionType === "bulk_hike") {
        setExpandedSidebarGroup("payroll");
        setView("payroll");
        setPayMonth(null);
        setCompanyNav("payroll");
        setModal("bulk_hike");
        return;
      }
      if (destination.actionType === "add_emp") {
        setCompanyNav("employees");
        setExpandedSidebarGroup("employees");
        setView("employee_form");
        setEditTarget(null);
        setModal("addEmp");
        return;
      }
      if (destination.actionType === "select_employee") {
        if (destination.employee) {
          setActiveEmployee(destination.employee);
          setView("employee_detail");
          setCompanyNav("employees");
          setExpandedSidebarGroup("employees");
        }
        return;
      }
      if (destination.actionType === "open_payroll") {
        const co = targetCo || companies[0];
        if (co) {
          setActiveCompany(co);
          setCompanyNav("payroll");
          setExpandedSidebarGroup("payroll");
          openPayroll(co);
        }
        return;
      }

      if (destination.view) {
        setView(destination.view);
        if (destination.companyNav) {
          setCompanyNav(destination.companyNav);
          setExpandedSidebarGroup(sidebarGroupFor(destination.companyNav));
        }
        if (destination.offboardingTab) {
          setOffboardingTab(destination.offboardingTab);
        }
        if (destination.orgTab) {
          setOrgTab(destination.orgTab);
        }
        if (destination.approvalsTab) setApprovalsTab(destination.approvalsTab);
        if (destination.dashboardTab) setDashboardTab(destination.dashboardTab);
        if (destination.joiningKitTab) {
          setJoiningKitTab(destination.joiningKitTab);
        }
        if (destination.view === "companies") {
          setActiveCompany(null);
          setActiveEmployee(null);
        }
      }
      if (destination.modal) {
        setModal(destination.modal);
      }
    });
    setCommandPaletteOpen(false);
  };
  const [activeCompany, setActiveCompany] = useState(null);
  useEffect(() => {
    setExpandedSidebarGroup(sidebarGroupFor(companyNav));
  }, [companyNav, activeCompany?.id]);

  const isCompanyRole = (activeCompany && currentUser?.role === "company");
  const isResolvingCompany = (currentUser?.role === "company" && !activeCompany);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const [activeEmployee, setActiveEmployee] = useState(null);
  const [empTab, setEmpTab]         = useState("salary");
  const [hikeModal, setHikeModal]   = useState(false);
  const [hikeForm, setHikeForm]     = useState({ date: salaryToday(), newGross: "", note: "Annual Appraisal" });
  const [savingHike, setSavingHike] = useState(false);
  const [emailSendingProgress, setEmailSendingProgress] = useState(null);
  const [emailModalData, setEmailModalData] = useState(null);

  // Payroll run state
  const [payMonth, setPayMonth]     = useState(null); // {month,year}
  const [selectedFY, setSelectedFY] = useState(null); // e.g. "2026-2027"
  const [attendance, setAttendance] = useState({}); // {empId: [{month,year,present,absent,ot}]}
  const [editAttRow, setEditAttRow] = useState({}); // {empId: {absent_days, ot_hours}}


  // Modals
  const [modal, setModal]           = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [formMsg, setFormMsg]       = useState("");
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, name }
  const [adminToast, setAdminToast] = useState("");
  useEffect(() => {
    try {
      const savedToast = sessionStorage.getItem("tw_toast_msg");
      if (savedToast) {
        sessionStorage.removeItem("tw_toast_msg");
        setAdminToast(savedToast);
        setTimeout(() => setAdminToast(""), 4500);
      }
    } catch (e) {}
  }, []);
  const [uploadLog, setUploadLog]   = useState("");
  const [copiedLog, setCopiedLog]   = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [showTmplMenu, setShowTmplMenu] = useState(false);
  const [empSearch, setEmpSearch]   = useState("");
  const [dashMonth, setDashMonth] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('overview');
  const [empSort, setEmpSort] = useState({key: 'employee_code', dir: 'asc'});
  const [showDashSection, setShowDashSection] = useState("");
  const [dashEmployeeList, setDashEmployeeList] = useState(null);
  const [approvalsTab, setApprovalsTab] = useState("leave_attendance"); // "leave_attendance" | "profile" | "certificates"
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [allPendingLeaves, setAllPendingLeaves] = useState([]);
  const [showPendingLeaves, setShowPendingLeaves] = useState(null); // null = auto (open if > 0, closed if 0)
  const [showPendingRegs, setShowPendingRegs] = useState(null); // null = auto (open if > 0, closed if 0)
  const [allPendingApprovals, setAllPendingApprovals] = useState([]);
  const [allCompanyLeaves, setAllCompanyLeaves] = useState([]);
  const [adminLeaveFilterEmp, setAdminLeaveFilterEmp] = useState("all");
  const [adminLeaveFilterStatus, setAdminLeaveFilterStatus] = useState("all");
  const [adminLeaveFilterYear, setAdminLeaveFilterYear] = useState("all");
  const [adminEmpBalSearch, setAdminEmpBalSearch] = useState("");
  const [showEmpLeaveBalances, setShowEmpLeaveBalances] = useState(false);
  const [showCompanyLeaveRecords, setShowCompanyLeaveRecords] = useState(false);
  const companyLeaveRecordsRef = useRef(null);
  const viewEmployeeLeaveRecords = (employeeId) => {
    setAdminLeaveFilterEmp(adminLeaveFilterEmp === String(employeeId) ? "all" : String(employeeId));
    setAdminLeaveFilterStatus("all");
    setAdminLeaveFilterYear("all");
    setSelectedLeaveRecordIds([]);
    setShowCompanyLeaveRecords(true);
    requestAnimationFrame(() => companyLeaveRecordsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const [adminEmpBalSortDir, setAdminEmpBalSortDir] = useState('asc'); // 'asc' | 'desc'
  const [selectedLeaveRecordIds, setSelectedLeaveRecordIds] = useState([]);
  const [recentApprovals, setRecentApprovals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [clearingActivityLogs, setClearingActivityLogs] = useState(false);
  const auditRequestVersion = useRef(0);
  useEffect(() => {
    if (!currentUser || currentUser.role === "employee") { auditRequestVersion.current++; setAuditLogs([]); setAuditLoading(false); }
  }, [currentUser?.role]);
  const [superAdminLogCompanyFilter, setSuperAdminLogCompanyFilter] = useState("all");
  // ── Attendance state
  const [pendingRegularizations, setPendingRegularizations] = useState([]);
  const [allPendingRegularizations, setAllPendingRegularizations] = useState([]);
  const [adminAttendanceRecords, setAdminAttendanceRecords] = useState([]);
  const [adminAttendanceDate, setAdminAttendanceDate] = useState(new Date().toISOString().split("T")[0]);
  const [adminAttendanceSort, setAdminAttendanceSort] = useState("status");
  const [adminAttReportRange, setAdminAttReportRange] = useState({ from: "", to: "" });
  const [adminAttReportData, setAdminAttReportData] = useState([]);
  const [adminAttReportLoading, setAdminAttReportLoading] = useState(false);

  const handleLogin = useCallback((userSession) => {
    setProfileMenuOpen(false);
    setShowUserMenu(false);
    setCompanies([]); setEmployees({}); setSalaries({}); setActiveCompany(null); setActiveEmployee(null);
    setCurrentUser(userSession);
    if (userSession.role !== 'employee' && !userSession.passwordChangeRequired) setLoading(true);
    try {
      localStorage.setItem("tw_current_user", JSON.stringify(userSession));
      if (userSession.role === "employee") {
        if (userSession.pan) localStorage.setItem("tw_remember_pan", userSession.pan);
        localStorage.setItem("tw_login_tab", "employee");
      } else {
        if (userSession.login_username || userSession.username) {
          localStorage.setItem("tw_remember_username", userSession.login_username || userSession.username);
        }
        localStorage.setItem("tw_login_tab", "admin");
      }
    } catch (e) {}
  }, []);

  const handleLogout = useCallback(() => {
    setProfileMenuOpen(false);
    setShowUserMenu(false);
    if (currentUser) {
      logAudit(currentUser, ["admin", "company"].includes(currentUser.role) ? "ADMIN_LOGOUT" : "EMPLOYEE_LOGOUT", {
        username: currentUser.login_username || currentUser.username || currentUser.employeeName
      });
      try {
        if (currentUser.role === "employee") {
          employeeLogout(currentUser).catch(() => {});
          if (currentUser.email) localStorage.setItem("tw_remember_employee_email", currentUser.email);
          localStorage.setItem("tw_login_tab", "employee");
        } else {
          if (currentUser.login_username || currentUser.username) {
            localStorage.setItem("tw_remember_username", currentUser.login_username || currentUser.username);
          }
          localStorage.setItem("tw_login_tab", "admin");
        }
      } catch (e) {}
    }
    signOutAdmin().catch(() => {});
    setCurrentUser(null);
    setActiveCompany(null);
    setActiveEmployee(null);
    setView("companies");
    try {
      localStorage.removeItem("tw_current_user");
    } catch (e) {}
  }, [currentUser]);

  // ── Load companies from Supabase on mount
  useEffect(() => { loadCompanies(); }, [currentUser?.role, currentUser?.userId, currentUser?.passwordChangeRequired]);

  // ── Dismiss admin profile and user dropdown menus when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (profileMenuOpen) setProfileMenuOpen(false);
        if (showUserMenu) setShowUserMenu(false);
      }
    };
    if (profileMenuOpen || showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileMenuOpen, showUserMenu]);

  // ── Clear form messages on navigation & fetch approvals
  // ── Approvals & leaves data fetching with live polling
  const refreshApprovals = useCallback(() => {
    if (!currentUser || currentUser.passwordChangeRequired || currentUser.role === "employee") return;
    // Skip polling when the browser tab is hidden — no point burning DB quota
    if (document.visibilityState === "hidden") return;

    if (activeCompany) {
      supabase.from("employee_update_requests").select("*, employees(*)")
        .eq("company_id", activeCompany.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
           if (error) console.error("Error fetching approvals:", error);
           else setPendingApprovals(data || []);
        });

      supabase.from("employee_leaves").select("*, employees(name, employee_code, date_of_joining)")
        .eq("company_id", activeCompany.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
          if (error) console.error("Error fetching pending leaves:", error);
          else setPendingLeaves(data || []);
        });

      // Leave history: limit to rolling 12 months — full history is loaded on-demand per employee
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      const twelveMonthsAgoStr = twelveMonthsAgo.toISOString().slice(0, 10); // YYYY-MM-DD
      supabase.from("employee_leaves").select("*, employees(name, employee_code, date_of_joining)")
        .eq("company_id", activeCompany.id)
        .gte("start_date", twelveMonthsAgoStr)
        .order("start_date", { ascending: false })
        .limit(500)
        .then(({ data, error }) => {
          if (error) console.error("Error fetching company leaves history:", error);
          else setAllCompanyLeaves(data || []);
        });

      // Attendance: fetch only today's records for the live dashboard (not full history)
      const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      supabase.from("attendance_records")
        .select("*, employees(name, employee_code, department, designation)")
        .eq("company_id", activeCompany.id)
        .eq("date", todayStr)
        .order("punch_in", { ascending: false })
        .then(({ data }) => { if (data) setAdminAttendanceRecords(data); });

      // Attendance: fetch pending regularizations
      supabase.from("attendance_records")
        .select("*, employees(name, employee_code, department)")
        .eq("company_id", activeCompany.id)
        .eq("regularization_status", "pending")
        .order("date", { ascending: false })
        .then(({ data }) => { if (data) setPendingRegularizations(data || []); });
    }

    // Global pending queries for Super Admin counters — only run when not a company-scoped role
    if (!isCompanyRole) {
      supabase.from("employee_leaves").select("id, company_id, employee_id, leave_type, days, start_date, end_date, created_at, employees(name, employee_code, date_of_joining)")
        .eq("status", "pending")
        .then(({ data }) => { if (data) setAllPendingLeaves(data); });

      supabase.from("employee_update_requests").select("id, company_id, employee_id, created_at")
        .eq("status", "pending")
        .then(({ data }) => { if (data) setAllPendingApprovals(data); });

      supabase.from("attendance_records").select("id, company_id")
        .eq("regularization_status", "pending")
        .then(({ data }) => { if (data) setAllPendingRegularizations(data || []); });
    }
  }, [activeCompany?.id, isCompanyRole, currentUser?.role, currentUser?.passwordChangeRequired]);

  const fetchActivityLogs = useCallback(async () => {
    if (!currentUser || currentUser.passwordChangeRequired || currentUser.role === "employee") return;
    const version = ++auditRequestVersion.current;
    setAuditLoading(true); setAuditError("");
    try {
      let query = supabase.from("audit_logs").select("*, employees(name, employee_code)");
      if (isCompanyRole || currentUser.role === "company") {
        const cid = currentUser.companyId || activeCompany?.id;
        if (!cid) throw new Error("Select a company before viewing its activity log.");
        query = query.eq("company_id", cid);
      } else if (superAdminLogCompanyFilter === "platform") query = query.is("company_id", null);
      else if (superAdminLogCompanyFilter !== "all") query = query.eq("company_id", superAdminLogCompanyFilter);
      const { data, error } = await query.order("created_at", { ascending: false }).limit(500);
      if (error) throw error;
      if (version === auditRequestVersion.current) setAuditLogs(data || []);
    } catch (error) { if (version === auditRequestVersion.current) setAuditError(error.message || "Could not load activity logs."); }
    finally { if (version === auditRequestVersion.current) setAuditLoading(false); }
  }, [currentUser?.role, currentUser?.companyId, currentUser?.passwordChangeRequired, isCompanyRole, activeCompany?.id, superAdminLogCompanyFilter]);

  const clearActivityLogs = async () => {
    const companyScoped = isCompanyRole || currentUser?.role === "company";
    const cid = companyScoped ? currentUser?.companyId || activeCompany?.id : superAdminLogCompanyFilter;
    if (companyScoped && !cid) { setAuditError("Select a company first."); return; }
    const label = companyScoped ? activeCompany?.name || "this company" : cid === "all" ? "all companies and the platform" : cid === "platform" ? "the platform" : companies.find(company => company.id === cid)?.name || "the selected company";
    if (!window.confirm(`Permanently clear all saved activity logs for ${label}? Entries hidden by search/date filters are also included. This cannot be undone.`)) return;
    setClearingActivityLogs(true); setAuditError("");
    try {
      let query = supabase.from("audit_logs").delete();
      if (companyScoped || (cid !== "all" && cid !== "platform")) query = query.eq("company_id", cid);
      else if (cid === "platform") query = query.is("company_id", null);
      else query = query.neq("id", "00000000-0000-0000-0000-000000000000");
      const { error } = await query;
      if (error) throw error;
      await fetchActivityLogs();
    } catch (error) { setAuditError(error.message || "Could not clear activity logs."); }
    finally { setClearingActivityLogs(false); }
  };

  useEffect(() => {
    setFormMsg("");
    if (!currentUser || currentUser.passwordChangeRequired || currentUser.role === "employee") return;
    refreshApprovals();

    if (view === "approvals" && activeCompany) {
      supabase.from("employee_update_requests").select("*, employees(*)")
        .eq("company_id", activeCompany.id)
        .in("status", ["approved", "rejected"])
        .order("reviewed_at", { ascending: false })
        .limit(10)
        .then(({ data }) => setRecentApprovals(data || []));
    }
    if (view === "audit_logs") fetchActivityLogs();
  }, [view, companyNav, modal, activeCompany?.id, refreshApprovals, isCompanyRole, currentUser?.role, currentUser?.passwordChangeRequired, superAdminLogCompanyFilter, fetchActivityLogs]);

  // Live polling for leaves and approvals every 6 seconds + instant refresh on window focus
  useEffect(() => {
    const timer = setInterval(() => {
      refreshApprovals();
    }, 6000);
    const onFocus = () => refreshApprovals();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshApprovals]);

  const loadCompanies = useCallback(async () => {
    if (!currentUser || currentUser.passwordChangeRequired || currentUser.role === "employee" || isPwaMode()) { setLoading(false); return; }
    setLoading(true);
    setDbError(null);
    try {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .order("name");
      if (error) throw error;
      const mapped = data.map(row => {
        const localLogo = localStorage.getItem('logo_' + row.id);
        const hasSalary = row.hra_percent != null || row.pf_applicable != null || row.esi_applicable != null;
        const dbConfig = withoutLegacyEmailCredentials((typeof row.config === 'object' && row.config !== null) ? row.config : {});
        let localConfig = {};
        try {
          localConfig = withoutLegacyEmailCredentials(JSON.parse(localStorage.getItem('co_config_' + row.id) || '{}'));
        } catch(e) {}
        const mergedCustomConfig = { ...localConfig, ...dbConfig };
        
        const baseCfg = hasSalary ? {
          _configured:              true,
          hra_type:                 (row.hra_percent < 0) ? "fixed" : "pct",
          hra_customised:           row.hra_customised || false,
          hra_value:                Math.abs(row.hra_percent || 40),
          hra_pct:                  Math.abs(row.hra_percent || 40),
          da:                       (row.da_percent||0)>0,
          da_pct:                   row.da_percent             || 0,
          special_allowance:        (row.special_allowance||0)>0 || (row.special_allowance_pct||0)>0,
          special_allowance_type:   row.special_allowance_pct>0 ? "pct" : "fixed",
          special_allowance_value:  row.special_allowance_pct>0 ? row.special_allowance_pct : (row.special_allowance||0),
          conveyance:               (row.conveyance_allowance||0)>0,
          conveyance_amount:        row.conveyance_allowance   || 0,
          medical:                  (row.medical_allowance||0)>0,
          bonus:                    row.bonus_percent != null ? ((row.bonus_percent||0)>0) : (mergedCustomConfig.bonus !== false),
          bonus_pct:                (row.bonus_percent && row.bonus_percent > 0) ? row.bonus_percent : (mergedCustomConfig.bonus_pct || 8.33),
          bonus_in_gross:           mergedCustomConfig.bonus_in_gross !== false,
          bonus_frequency:          mergedCustomConfig.bonus_frequency || "monthly",
          bonus_months:             row.bonus_months ? row.bonus_months.split(",").map(Number).filter(Boolean) : (mergedCustomConfig.bonus_months || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
          bonus_display_name:       row.bonus_display_name || "",
          custom_basic_enabled:     row.custom_basic_enabled != null ? row.custom_basic_enabled : (mergedCustomConfig.custom_basic_enabled || false),
          basic_pct:                (row.basic_pct !== undefined && row.basic_pct !== null) ? row.basic_pct : (mergedCustomConfig.basic_pct !== undefined ? mergedCustomConfig.basic_pct : 50),
          hra_customised:           row.hra_customised != null ? row.hra_customised : (mergedCustomConfig.hra_customised || false),
          custom_special_allowance_enabled: row.custom_special_allowance_enabled || false,
          vpf_tds_enabled:          row.vpf_tds_enabled || false,
          custom_special_display_name: row.custom_special_display_name || "",
          ot_applicable:            row.ot_rate_per_hour !== 0 && row.ot_rate_per_hour != null,
          ot_basis:                 (row.ot_rate_per_hour === -1 || row.ot_rate_per_hour === -3) ? "gross" : "basic",
          ot_rate_multiplier:       (row.ot_rate_per_hour === -3 || row.ot_rate_per_hour === -4) ? 2 : 1,
          absence_deduction:        (row.absence_deduction_from != null ? (row.absence_deduction_from !== "no_deduct" && row.absence_deduction_from !== "none") : (mergedCustomConfig.absence_deduction !== false && mergedCustomConfig.absence_basis !== "no_deduct" && mergedCustomConfig.absence_basis !== "none")),
          absence_basis:            (row.absence_deduction_from === "no_deduct" || row.absence_deduction_from === "none") ? "none" : (row.absence_deduction_from || ((mergedCustomConfig.absence_basis === "no_deduct" || mergedCustomConfig.absence_basis === "none") ? "none" : (mergedCustomConfig.absence_basis || "none"))),
          working_days:             row.working_days_per_month || 26,
          min_wage_enabled:         (row.min_wage_enabled !== undefined && row.min_wage_enabled !== null) ? row.min_wage_enabled : (mergedCustomConfig.min_wage_enabled !== undefined ? mergedCustomConfig.min_wage_enabled : true),
          min_wage:                 (row.min_wage !== undefined && row.min_wage !== null) ? row.min_wage : (mergedCustomConfig.min_wage !== undefined ? mergedCustomConfig.min_wage : 15000),
          pf_applicable:            row.pf_applicable          ?? true,
          pf_wage_cap:              true, // default cap on; no DB column yet
          esi_applicable:           row.esi_applicable         ?? true,
          lwf_applicable:           row.lwf_applicable         ?? true,
          plan_tier:                row.plan_tier              || "Free",
          addon_bulk_hike:          mergedCustomConfig.addon_bulk_hike !== undefined ? (mergedCustomConfig.addon_bulk_hike === true || mergedCustomConfig.addon_bulk_hike === "true") : (row.addon_bulk_hike === true || false),
          logo_base64:              row.logo_base64 || localLogo || null,
        } : {
          plan_tier:                row.plan_tier              || "Free",
          addon_bulk_hike:          mergedCustomConfig.addon_bulk_hike !== undefined ? (mergedCustomConfig.addon_bulk_hike === true || mergedCustomConfig.addon_bulk_hike === "true") : (row.addon_bulk_hike === true || false),
          logo_base64:              row.logo_base64 || localLogo || null,
        };

        const cfg = {
          ...DEFAULT_CONFIG,
          ...mergedCustomConfig,
          ...baseCfg,
          leave_module_enabled: mergedCustomConfig.leave_module_enabled !== undefined ? mergedCustomConfig.leave_module_enabled : false,
          company_holidays: mergedCustomConfig.company_holidays || DEFAULT_CONFIG.company_holidays || [],
          leave_types: mergedCustomConfig.leave_types || DEFAULT_CONFIG.leave_types,
          working_days_schedule: mergedCustomConfig.working_days_schedule || DEFAULT_CONFIG.working_days_schedule,
          departments: mergedCustomConfig.departments || DEFAULT_CONFIG.departments,
          designations: mergedCustomConfig.designations || DEFAULT_CONFIG.designations,
          attendance_enabled: mergedCustomConfig.attendance_enabled !== undefined ? mergedCustomConfig.attendance_enabled : false,
          attendance_punch_in: mergedCustomConfig.attendance_punch_in || DEFAULT_CONFIG.attendance_punch_in,
          attendance_punch_out: mergedCustomConfig.attendance_punch_out || DEFAULT_CONFIG.attendance_punch_out,
          attendance_grace_minutes: mergedCustomConfig.attendance_grace_minutes !== undefined ? mergedCustomConfig.attendance_grace_minutes : DEFAULT_CONFIG.attendance_grace_minutes,
          attendance_eod_cutoff: mergedCustomConfig.attendance_eod_cutoff || DEFAULT_CONFIG.attendance_eod_cutoff,
          org_culture_enabled: mergedCustomConfig.org_culture_enabled !== undefined ? mergedCustomConfig.org_culture_enabled : false,
          recruitment_enabled: mergedCustomConfig.recruitment_enabled !== undefined ? mergedCustomConfig.recruitment_enabled : false,
          offboarding_enabled: mergedCustomConfig.offboarding_enabled !== undefined ? mergedCustomConfig.offboarding_enabled : false,
          training_enabled: dbConfig.training_enabled === true,
          doc_hub_enabled: dbConfig.doc_hub_enabled === true,
          reports_enabled: mergedCustomConfig.reports_enabled !== undefined ? mergedCustomConfig.reports_enabled : true,
          addon_bulk_hike: mergedCustomConfig.addon_bulk_hike !== undefined ? (mergedCustomConfig.addon_bulk_hike === true || mergedCustomConfig.addon_bulk_hike === "true") : (row.addon_bulk_hike === true || false),
        };

        if (cfg.logo_base64) cacheLogoImage(cfg.logo_base64);

        return {
          id:      row.id,
          name:    row.name,
          gst:     row.gst_number || row.tin || "",
          address: row.address || "",
          phone:   row.phone || "",
          website: row.website || "",
          contact: row.contact_person || "",
          email:      row.contact_email  || "",
          pf_number:  row.pf_number  || "",
          esi_code:   row.esi_code   || "",
          pt_reg:     row.pt_reg     || "",
          login_username: row.login_username || "",
          config:  cfg,
          is_active: row.is_active,
        };
      });
      setCompanies(mapped);

      // Fetch all employees so Admin Dashboard has global counts
      const { data: allEmps, error: empErr } = await supabase.from("employees").select("*").order("name");
      if (!empErr && allEmps) {
        const grouped = {};
        const activeCompanyIds = new Set(mapped.map(c => c.id));
        const companyMap = new Map(mapped.map(c => [c.id, c]));
        allEmps.forEach(row => {
          if (!activeCompanyIds.has(row.company_id)) return;
          if (!grouped[row.company_id]) grouped[row.company_id] = [];
          const co = companyMap.get(row.company_id);
          const emp = mapEmployeeRow(row);
          const photo = co?.config?.employee_photos?.[row.id] || co?.config?.employee_photos?.[row.employee_code] || emp.emp_photo;
          if (photo) emp.emp_photo = photo;
          const docs = co?.config?.employee_documents?.[row.id] || co?.config?.employee_documents?.[row.employee_code] || {};
          Object.assign(emp, docs);
          grouped[row.company_id].push(emp);
        });
        setEmployees(grouped);
      }

      // Restore active company and view from sessionStorage if set (e.g. after approval reload)
      try {
        const savedCoId = sessionStorage.getItem("tw_active_company_id");
        if (savedCoId) {
          const savedCo = mapped.find(c => c.id === savedCoId);
          if (savedCo) {
            setActiveCompany(savedCo);
            const savedView = sessionStorage.getItem("tw_active_view");
            const savedNav = sessionStorage.getItem("tw_active_company_nav");
            const savedTab = sessionStorage.getItem("tw_active_approvals_tab");
            if (savedView) setView(savedView);
            if (savedNav) setCompanyNav(savedNav);
            if (savedTab) setApprovalsTab(savedTab);
            sessionStorage.removeItem("tw_active_view");
            sessionStorage.removeItem("tw_active_company_nav");
            sessionStorage.removeItem("tw_active_approvals_tab");
            if (typeof loadEmployees === "function") {
              loadEmployees(savedCo.id);
            }
          }
        }
      } catch(e) {}
    } catch(e) {
      console.error("loadCompanies:", e);
      setDbError("Could not load companies. Check Supabase connection.");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.role, currentUser?.userId, currentUser?.passwordChangeRequired]);

  const loadEmployees = useCallback(async (companyId) => {
    try {
      const [{ data, error }, { data: freshCo }] = await Promise.all([
        supabase
          .from("employees")
          .select("*")
          .eq("company_id", companyId)
          .order("name"),
        supabase
          .from("companies")
          .select("*")
          .eq("id", companyId)
          .single()
      ]);
      if (error) throw error;
      const targetCo = freshCo || companies.find(c => c.id === companyId) || activeCompany;
      if (freshCo) {
        setActiveCompany(prev => (prev?.id === companyId ? { ...prev, ...freshCo, config: freshCo.config } : prev));
        setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, ...freshCo, config: freshCo.config } : c));
        try { localStorage.setItem('co_config_' + companyId, JSON.stringify(freshCo.config)); } catch(e) {}
      }
      const mapped = data.map(row => {
        const emp = mapEmployeeRow(row);
        const photo = targetCo?.config?.employee_photos?.[row.id] || targetCo?.config?.employee_photos?.[row.employee_code] || emp.emp_photo;
        if (photo) emp.emp_photo = photo;
        const docs = targetCo?.config?.employee_documents?.[row.id] || targetCo?.config?.employee_documents?.[row.employee_code] || {};
        Object.assign(emp, docs);
        return emp;
      });
      setEmployees(prev => ({ ...prev, [companyId]: mapped }));

      // Also load all salaries for the company so "Done" status and aggregations persist on refresh
      const { data: salData, error: salErr } = await supabase
        .from("salary_records")
        .select("*")
        .eq("company_id", companyId);
      if (!salErr && salData) {
        const validEmpIds = new Set(data.map(r => r.id));
        const grouped = {};
        const orphanSalIds = [];
        salData.forEach(row => {
          if (!validEmpIds.has(row.employee_id)) {
            orphanSalIds.push(row.id);
            return;
          }
          if(!grouped[row.employee_id]) grouped[row.employee_id] = [];
          grouped[row.employee_id].push({
            id:          row.id,
            employee_id: row.employee_id,
            month:       row.month,
            year:        row.year,
            absent_days: row.days_absent      || 0,
            ot_hours:    row.ot_hours         || 0,
            basic:       row.basic            || 0,
            hra:         row.hra              || 0,
            da:          row.da               || 0,
            special:     row.special_allowance|| 0,
            conveyance:  row.conveyance       || 0,
            medical:     row.medical          || 0,
            bonus:       row.bonus            || 0,
            ot_pay:      row.ot_amount        || 0,
            gross:       row.gross            || 0,
            pf_emp:      row.pf_employee      || 0,
            pf_er:       row.pf_employer      || 0,
            esi_emp:     row.esi_employee     || 0,
            esi_er:      row.esi_employer     || 0,
            pt:          row.professional_tax || 0,
            absence_deduction: row.absence_deduction || 0,
            vpf:         row.vpf              || 0,
            tds:         row.tds              || 0,
            net:         row.net_pay          || 0,
            ctc:         row.ctc              || 0,
            status:      row.status           || "draft",
          });
        });
        setSalaries(prev => ({ ...prev, ...grouped }));
        if (orphanSalIds.length > 0) {
          supabase.from("salary_records").delete().in("id", orphanSalIds).catch(console.error);
        }
      }
      // NOTE: cleanOrphanDatabaseRecords is intentionally NOT called here.
      // Automatic cleanup on every employee load is unsafe — a partial/failed employee
      // query can cause valid child records to be wrongly treated as orphans and deleted.
      // Cleanup is only triggered explicitly via the Super Admin "Purge Stale Data" action.
    } catch(e) {
      console.error("loadEmployees:", e);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.role === "company" && currentUser.companyId && companies.length > 0) {
      const co = companies.find(c => c.id === currentUser.companyId);
      if (co) {
        if (!activeCompany || activeCompany.id !== co.id) {
          setActiveCompany(co);
          loadEmployees(co.id);
        }
        if (view === "companies") {
          setView("employees");
          setCompanyNav("employees");
        }
      }
    }
  }, [currentUser, companies, activeCompany, view, loadEmployees]);

  const loadSalaries = useCallback(async (employeeId) => {
    try {
      const { data, error } = await supabase
        .from("salary_records")
        .select("*")
        .eq("employee_id", employeeId)
        .order("year", { ascending: false })
        .order("month", { ascending: false });
      if (error) throw error;
      const mapped = data.map(row => ({
        id:          row.id,
        employee_id: row.employee_id,
        month:       row.month,
        year:        row.year,
        absent_days: row.days_absent      || 0,
        ot_hours:    row.ot_hours         || 0,
        basic:       row.basic            || 0,
        hra:         row.hra              || 0,
        da:          row.da               || 0,
        special:     row.special_allowance|| 0,
        conveyance:  row.conveyance       || 0,
        medical:     row.medical          || 0,
        bonus:       row.bonus            || 0,
        ot:          row.ot_amount        || 0,
        gross:       row.gross            || 0,
        pf_emp:      row.pf_employee      || 0,
        pf_er:       row.pf_employer      || 0,
        pfWage:      row.pf_employee ? Math.round((row.pf_employee||0) / 0.12) : 0,
        eps:         Math.round(Math.min(row.pf_employee ? Math.round((row.pf_employee||0)/0.12) : 0, 25000) * 0.0833),
        epf_er:      Math.max(0, (row.pf_employer||0) - Math.round(Math.min(row.pf_employee ? Math.round((row.pf_employee||0)/0.12) : 0, 25000) * 0.0833)),
        esi_emp:     row.esi_employee     || 0,
        esi_er:      row.esi_employer     || 0,
        pt:          row.professional_tax || 0,
        absence_ded: row.absence_deduction|| 0,
        net:         row.net_pay          || 0,
        ctc:         row.ctc              || 0,
        vpf:         row.vpf              || 0,
        tds:         row.tds              || 0,
        status:      row.status           || "draft",
      }));
      setSalaries(prev => ({ ...prev, [employeeId]: mapped }));
    } catch(e) {
      console.error("loadSalaries:", e);
    }
  }, []);

  // ── Bulk salary reload — replaces N per-employee loadSalaries calls after payroll save/lock.
  // One query fetches all salary records for the company, groups by employee_id, then merges
  // into state in a single setSalaries call. Keeps the same field mapping as loadSalaries.
  const reloadCompanySalaries = useCallback(async (companyId, employeeIds = []) => {
    try {
      const { data, error } = await supabase
        .from("salary_records")
        .select("*")
        .eq("company_id", companyId)
        .order("year",  { ascending: false })
        .order("month", { ascending: false });
      if (error) throw error;
      const grouped = {};
      (data || []).forEach(row => {
        const mapped = {
          id:          row.id,
          employee_id: row.employee_id,
          month:       row.month,
          year:        row.year,
          absent_days: row.days_absent       || 0,
          ot_hours:    row.ot_hours          || 0,
          basic:       row.basic             || 0,
          hra:         row.hra               || 0,
          da:          row.da                || 0,
          special:     row.special_allowance || 0,
          conveyance:  row.conveyance        || 0,
          medical:     row.medical           || 0,
          bonus:       row.bonus             || 0,
          ot:          row.ot_amount         || 0,
          gross:       row.gross             || 0,
          pf_emp:      row.pf_employee       || 0,
          pf_er:       row.pf_employer       || 0,
          pfWage:      row.pf_employee ? Math.round((row.pf_employee||0) / 0.12) : 0,
          eps:         Math.round(Math.min(row.pf_employee ? Math.round((row.pf_employee||0)/0.12) : 0, 25000) * 0.0833),
          epf_er:      Math.max(0, (row.pf_employer||0) - Math.round(Math.min(row.pf_employee ? Math.round((row.pf_employee||0)/0.12) : 0, 25000) * 0.0833)),
          esi_emp:     row.esi_employee      || 0,
          esi_er:      row.esi_employer      || 0,
          pt:          row.professional_tax  || 0,
          absence_ded: row.absence_deduction || 0,
          net:         row.net_pay           || 0,
          ctc:         row.ctc               || 0,
          vpf:         row.vpf               || 0,
          tds:         row.tds               || 0,
          status:      row.status            || "draft",
        };
        if (!grouped[row.employee_id]) grouped[row.employee_id] = [];
        grouped[row.employee_id].push(mapped);
      });
      // Replace this company's salary history, including employees whose last draft row was removed.
      setSalaries(prev => {
        const next = { ...prev };
        employeeIds.forEach(employeeId => { next[employeeId] = []; });
        return { ...next, ...grouped };
      });
    } catch(e) {
      console.error("reloadCompanySalaries:", e);
    }
  }, []);
  const fileRef = useRef(null);
  const companyUploadRef = useRef(null);

  // ── Derived
  const companyEmps = useMemo(()=> employees[activeCompany?.id]||[], [employees, activeCompany]);
  const filteredCompanies = useMemo(()=>
    companies.filter(c=>c.name.toLowerCase().includes(companySearch.toLowerCase())||
      (c.gst||"").toLowerCase().includes(companySearch.toLowerCase())),
    [companies, companySearch]);
  const filteredEmps = useMemo(()=> {
    const q = (empSearch || "").toLowerCase().trim();
    let arr = companyEmps.filter(e => {
      if (!q) return true;
      return (e.name?.toLowerCase() || "").includes(q) ||
             ((e.employee_code || e.emp_id || e.employee_id || "")?.toLowerCase() || "").includes(q) ||
             (e.email?.toLowerCase() || "").includes(q) ||
             (e.phone?.toLowerCase() || "").includes(q) ||
             (e.pan?.toLowerCase() || "").includes(q);
    });

    // Pre-compute salary sort values once per employee (O(n)) before the sort (O(n log n))
    // so calcSalaryFull is never called inside the comparator.
    let salarySortCache = null;
    if (['real_basic', 'net', 'ctc'].includes(empSort.key)) {
      salarySortCache = new Map();
      arr.forEach(e => {
        const s = calcSalaryFull({ basic: e.basic || 0, month: 1, year: 2026, cfg: activeCompany?.config || {}, emp_id: e.id, extra: e });
        salarySortCache.set(e.id, empSort.key === 'real_basic' ? s.basic : s[empSort.key]);
      });
    }

    arr.sort((a,b) => {
      // 1. Employee ID (natural alphanumeric/numeric sort)
      if (empSort.key === 'employee_code' || empSort.key === 'emp_id' || empSort.key === 'employee_id') {
        const valA = (a.employee_code ?? a.emp_id ?? a.employee_id ?? '').toString().trim();
        const valB = (b.employee_code ?? b.emp_id ?? b.employee_id ?? '').toString().trim();
        if (!valA && !valB) return (a.name || '').localeCompare(b.name || '');
        if (!valA) return 1;
        if (!valB) return -1;
        const cmp = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return empSort.dir === 'asc' ? cmp : -cmp;
        return (a.name || '').localeCompare(b.name || '');
      }

      // 2. Name
      if (empSort.key === 'name') {
        const valA = (a.name || '').toString().trim();
        const valB = (b.name || '').toString().trim();
        if (!valA && !valB) return 0;
        if (!valA) return 1;
        if (!valB) return -1;
        const cmp = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
        return empSort.dir === 'asc' ? cmp : -cmp;
      }

      // 3. Date of Joining
      if (empSort.key === 'doj') {
        const d1 = a.date_of_joining || a.doj;
        const d2 = b.date_of_joining || b.doj;
        const t1 = d1 ? new Date(d1).getTime() : 0;
        const t2 = d2 ? new Date(d2).getTime() : 0;
        if (!t1 && !t2) return 0;
        if (!t1) return 1;
        if (!t2) return -1;
        return empSort.dir === 'asc' ? t1 - t2 : t2 - t1;
      }

      // 4. Numeric / Salary fields — use pre-computed cache if available
      let v1 = salarySortCache ? salarySortCache.get(a.id) : a[empSort.key];
      let v2 = salarySortCache ? salarySortCache.get(b.id) : b[empSort.key];
      if (empSort.key === 'basic') {
        v1 = (a.basic || 0);
        v2 = (b.basic || 0);
      }

      if (v1 == null && v2 == null) return 0;
      if (v1 == null) return 1;
      if (v2 == null) return -1;

      if (typeof v1 === 'number' && typeof v2 === 'number') {
        return empSort.dir === 'asc' ? v1 - v2 : v2 - v1;
      }

      const strA = String(v1).trim();
      const strB = String(v2).trim();
      const cmp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
      return empSort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [companyEmps, empSearch, empSort, activeCompany]);

  const empSalaries = useMemo(()=>
    activeEmployee ? (salaries[activeEmployee.id]||[])
      .sort((a,b)=>b.year!==a.year?b.year-a.year:b.month-a.month)
    : [], [salaries, activeEmployee]);

  const eligibleEmps = useMemo(()=>{
    if(!payMonth||!activeCompany) return [];
    const wp = activeCompany.config?.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (activeCompany.config?.working_days || 26);
    const list = companyEmps
      .map(e => ({ ...e, _status: getEmployeeMonthStatus(e, payMonth.month, payMonth.year, wp) }))
      .filter(e => e._status.eligible);
    list.sort((a, b) => {
      let res = 0;
      if (payrollRunSort.key === 'name') {
        res = cmpEmpName(a, b);
      } else {
        res = cmpEmpId(a, b);
      }
      return payrollRunSort.dir === 'asc' ? res : -res;
    });
    return list;
  },[payMonth,activeCompany,companyEmps,payrollRunSort]);

  // payroll run: compute all employees for selected month
  const payrollData = useMemo(()=>{
    if(!payMonth||!activeCompany) return {};
    const cfg = activeCompany.config || DEFAULT_CONFIG;
    const result = {};
    eligibleEmps.forEach(emp=>{
      const existing = (salaries[emp.id]||[]).find(s=>s.month===payMonth.month&&s.year===payMonth.year);
      const runStage = activeCompany.config?.payroll_workflows?.[`${payMonth.year}-${String(payMonth.month).padStart(2, "0")}`]?.stage;
      if (existing && (existing.status === "locked" || ["review", "approved", "locked", "paid"].includes(runStage))) {
        result[emp.id] = { ...existing, ot_pay: existing.ot_pay ?? existing.ot ?? 0, absence_deduction: existing.absence_deduction ?? existing.absence_ded ?? 0, net: existing.net || 0, month: payMonth.month, year: payMonth.year, _saved: true };
        return;
      }
      const att = editAttRow[emp.id] || {};
      const baseAbsent = att.absent_days !== undefined ? att.absent_days : (existing?.absent_days||0);
      const totalAbsent = baseAbsent + emp._status.lwd_absent;
      const otH    = att.ot_hours    !== undefined ? att.ot_hours    : (existing?.ot_hours||0);
      const total_salary = emp.basic || 30000;
      const calc = calcSalaryFull({basic:total_salary, month:payMonth.month, year:payMonth.year, absent_days:totalAbsent, ot_hours:otH, cfg, extra: { vpf_enabled: (emp.vpf_pct||0)>0, vpf_pct: emp.vpf_pct||0, tds_amount: emp.tds_amount||0, emp_dob: emp.date_of_birth, custom_basic_pct: emp.custom_basic_pct, custom_hra_pct: emp.custom_hra_pct, esi_forced_until: emp.esi_forced_until, custom_special_allowance: emp.custom_special_allowance }});
      result[emp.id] = {basic:total_salary, absent_days:baseAbsent, lwd_absent: emp._status.lwd_absent, ot_hours:otH, month:payMonth.month, year:payMonth.year,...calc,
        _saved: !!existing};
    });
    return result;
  },[payMonth,activeCompany,eligibleEmps,salaries,editAttRow]);

  // payroll summary totals
  const payrollTotals = useMemo(()=>{
    const vals = Object.values(payrollData);
    if(!vals.length) return null;
    return {
      gross: Math.round(vals.reduce((a,s)=>a+(s.gross||0),0)),
      net:   Math.round(vals.reduce((a,s)=>a+(s.net||0),0)),
      pf:    Math.round(vals.reduce((a,s)=>a+(s.pf_emp||0)+(s.pf_er||0),0)),
      esi:   Math.round(vals.reduce((a,s)=>a+(s.esi_emp||0)+(s.esi_er||0),0)),
      pt:    Math.round(vals.reduce((a,s)=>a+(s.pt||0),0)),
      ctc:   Math.round(vals.reduce((a,s)=>a+(s.ctc||0),0)),
    };
  },[payrollData]);

  const pendingPayrollReviews = Object.values(activeCompany?.config?.payroll_workflows || {}).filter(run => run.stage === "review").length;
  const payrollRows = useMemo(() => buildPayrollRows(eligibleEmps, payrollData, activeCompany?.id, payMonth), [eligibleEmps, payrollData, activeCompany?.id, payMonth]);
  const payrollWorkflow = usePayrollWorkflow({
    companyId: activeCompany?.id,
    period: payMonth,
    actor: currentUser,
    expectedRows: payrollRows,
    onConfig: (config) => {
      const companyId = activeCompany?.id;
      setActiveCompany(previous => previous?.id === companyId ? { ...previous, config: { ...previous.config, ...config } } : previous);
      setCompanies(previous => previous.map(company => company.id === companyId ? { ...company, config: { ...company.config, ...config } } : company));
    },
    onRecords: () => reloadCompanySalaries(activeCompany.id, companyEmps.map(employee => employee.id)),
  });

  const isPayrollLocked = useMemo(() => {
    if(!payMonth || !salaries) return false;
    if (payrollWorkflow.loading || payrollWorkflow.busy || payrollWorkflow.error || payrollWorkflow.stage !== "draft") return true;
    return eligibleEmps.some(e => {
      const s = (salaries[e.id]||[]).find(s=>s.month===payMonth.month&&s.year===payMonth.year);
      return s && s.status === "locked";
    });
  }, [payMonth, salaries, eligibleEmps, payrollWorkflow.loading, payrollWorkflow.busy, payrollWorkflow.error, payrollWorkflow.stage]);

  const isNoDeduct = useMemo(() => {
    const cfg = activeCompany?.config || DEFAULT_CONFIG;
    return !cfg.absence_deduction || cfg.absence_basis === "no_deduct" || cfg.absence_basis === "none";
  }, [activeCompany]);

  // ── Download employee leave balances & allotments to Excel
  const downloadLeaveBalancesExcel = (empsToExport = null) => {
    try {
      const emps = empsToExport && empsToExport.length > 0 ? empsToExport : (companyEmps || []);
      if (!emps || emps.length === 0) {
        alert("No employees found to export.");
        return;
      }

      const targetYear = new Date().getFullYear();
      const safeName = (activeCompany?.name || "Company").replace(/[^a-zA-Z0-9_-]/g, "_");
      const cfgLeaveTypes = (activeCompany?.config?.leave_types && activeCompany?.config?.leave_types.length > 0)
        ? activeCompany.config.leave_types
        : [{ name: "Casual Leave", days: 12 }, { name: "Sick Leave", days: 6 }];

      const rows = emps.map(emp => {
        const dojStr = emp.date_of_joining || emp.doj;
        let formattedDoj = "—";
        if (dojStr) {
          try {
            const parts = String(dojStr).split("-");
            formattedDoj = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dojStr;
          } catch(e) {
            formattedDoj = dojStr;
          }
        }

        const row = {
          "Employee ID": emp.employee_code || "N/A",
          "Name": emp.name || "",
          "Department": emp.department || "—",
          "Designation": emp.designation || "—",
          "Joining Date": formattedDoj
        };

        let totalAllotted = 0;
        let totalUsed = 0;
        let totalBalance = 0;

        cfgLeaveTypes.forEach(lt => {
          const m = getEmployeeLeaveMetrics(emp, activeCompany?.config, allCompanyLeaves, lt.name, targetYear);
          row[`${lt.name} (Allotted)`] = m.totalQuota;
          row[`${lt.name} (Used)`] = m.approvedUsed;
          row[`${lt.name} (Balance)`] = m.remainingBalance;
          if (m.pendingUsed > 0) {
            row[`${lt.name} (Pending)`] = m.pendingUsed;
          }
          totalAllotted += (m.totalQuota || 0);
          totalUsed += (m.approvedUsed || 0);
          totalBalance += (m.remainingBalance || 0);
        });

        row["Total Allotted"] = totalAllotted;
        row["Total Used"] = totalUsed;
        row["Total Balance"] = totalBalance;

        return row;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      // Auto-fit column widths
      if (rows.length > 0) {
        ws['!cols'] = Object.keys(rows[0]).map(k => {
          const maxLen = Math.max(
            k.length,
            ...rows.map(r => String(r[k] ?? "").length)
          );
          return { wch: Math.min(Math.max(maxLen + 4, 12), 35) };
        });
      }

      XLSX.utils.book_append_sheet(wb, ws, `Leave Balances ${targetYear}`);
      XLSX.writeFile(wb, `${safeName}_Leave_Balances_${targetYear}.xlsx`);
    } catch (err) {
      alert("Error exporting leave balances: " + err.message);
    }
  };

  // ── Download Master Excel for selected month from Reports Hub (same 4-sheet formula workbook)
  const handleDownloadMasterExcelForMonth = async (targetMonth, targetYear) => {
    if (!activeCompany) return;
    try {
      const m = parseInt(targetMonth, 10);
      const y = parseInt(targetYear, 10);
      if (!m || !y) return;

      const wp = activeCompany.config?.working_days === -1 ? new Date(y, m, 0).getDate() : (activeCompany.config?.working_days || 26);
      const elEmps = companyEmps
        .map(e => ({ ...e, _status: getEmployeeMonthStatus(e, m, y, wp) }))
        .filter(e => e._status.eligible);

      if (elEmps.length === 0) {
        alert(`No eligible employees found for ${MONTHS[m] || m} ${y}.`);
        return;
      }

      // Fetch latest salary records for the selected month to ensure 100% accuracy and sync
      let recordsByEmp = {};
      if (supabase) {
        const { data, error } = await supabase
          .from("salary_records")
          .select("*")
          .eq("company_id", activeCompany.id)
          .eq("month", m)
          .eq("year", y);
        if (!error && data) {
          data.forEach(r => {
            recordsByEmp[r.employee_id] = r;
          });
        }
      }

      const cfg = activeCompany.config || DEFAULT_CONFIG;
      const salMap = {};

      elEmps.forEach(emp => {
        const rec = recordsByEmp[emp.id] || (salaries[emp.id] || []).find(s => s.month === m && s.year === y);
        const baseAbsent = rec ? (rec.absent_days ?? 0) : 0;
        const totalAbsent = baseAbsent + (emp._status.lwd_absent || 0);
        const otH = rec ? (rec.ot_hours ?? 0) : 0;
        const total_salary = emp.basic || emp.basic_salary || 30000;
        const calc = calcSalaryFull({
          basic: total_salary,
          month: m,
          year: y,
          absent_days: totalAbsent,
          ot_hours: otH,
          cfg,
          extra: {
            vpf_enabled: (emp.vpf_pct || 0) > 0,
            vpf_pct: emp.vpf_pct || 0,
            tds_amount: emp.tds_amount || 0,
            emp_dob: emp.date_of_birth || emp.dob,
            custom_basic_pct: emp.custom_basic_pct,
            custom_hra_pct: emp.custom_hra_pct,
            esi_forced_until: emp.esi_forced_until,
            custom_special_allowance: emp.custom_special_allowance
          }
        });

        salMap[emp.id] = {
          basic: total_salary,
          absent_days: baseAbsent,
          lwd_absent: emp._status.lwd_absent || 0,
          ot_hours: otH,
          month: m,
          year: y,
          ...calc,
          _saved: !!rec
        };
      });

      await exportMasterCSV(elEmps, salMap, activeCompany, m, y);
    } catch (err) {
      console.error("Error exporting master excel from Reports Hub:", err);
      alert("Error generating Master Excel: " + (err.message || err));
    }
  };

  // ── Nav helpers
  const openCompany = (c) => {
    setActiveCompany(c);
    try { sessionStorage.setItem("tw_active_company_id", c.id); } catch(e) {}
    setEmpSearch("");
    setView("employees");
    setCompanyNav("employees");
    loadEmployees(c.id);
  };
  const openPayroll = (c) => { setActiveCompany(c); setPayMonth(null); setEditAttRow({}); setView("payroll"); };
  const openJoiningKit = () => setView("joining_kit");
  const saveJoiningKitEmployee = async (form, existingId, onPersisted) => {
    if (form.name && form.name.trim().length > 100) {
      throw new Error("Employee Name cannot exceed 100 characters.");
    }
    if (form.father_name && form.father_name.trim().length > 100) {
      throw new Error("Father's Name cannot exceed 100 characters.");
    }
    if (form.spouse_name && form.spouse_name.trim().length > 100) {
      throw new Error("Spouse's Name cannot exceed 100 characters.");
    }
    if (form.emergency_name && form.emergency_name.trim().length > 100) {
      throw new Error("Emergency Contact Name cannot exceed 100 characters.");
    }
    const payload = {
      company_id:         activeCompany.id,
      name:               form.name?.trim().slice(0, 100),
      employee_code:      form.employee_code?.trim()||null,
      father_name:        form.father_name ? form.father_name.trim().slice(0, 100) : null,
      gender:             form.gender||null,
      date_of_birth:      form.dob||null,
      marital_status:     form.marital_status||null,
      phone:              form.phone||null,
      email:              form.email||null,
      department:         form.department||null,
      designation:        form.designation||null,
      date_of_joining:    form.doj||null,
      date_of_exit:       form.date_of_exit||null,
      basic_salary:       form.basic_salary||0,
      employment_status:  form.employment_status||'confirmed',
      pan:                form.pan||null,
      uan:                form.uan||null,
      pf_account_number:  form.pf_account||null,
      esi_id:             form.prev_esi === 'Yes' ? (form.esi_id||null) : null,
      dispensary:         form.prev_esi === 'Yes' ? (form.dispensary||null) : null,
      bank_name:          form.bank_name||null,
      bank_account_number:form.bank_account||null,
      bank_ifsc:          form.bank_ifsc||null,
      bank_branch:        form.bank_branch||null,
      perm_address:       form.perm_address||null,
      perm_city:          form.perm_city||null,
      perm_state:         form.perm_state||null,
      perm_pincode:       form.perm_pincode||null,
      temp_address:       form.temp_address||null,
      temp_city:          form.temp_city||null,
      temp_state:         form.temp_state||null,
      temp_pincode:       form.temp_pincode||null,
      emergency_name:     form.emergency_name ? form.emergency_name.trim().slice(0, 100) : null,
      emergency_relation: form.emergency_relation||null,
      emergency_phone:    form.emergency_phone||null,
      // JoinKit extended
      aadhaar_last4:      form.aadhaar_last4||null,
      religion:           form.religion||null,
      rel_type:           form.rel_type||'Father',
      spouse_name:        form.spouse_name ? form.spouse_name.trim().slice(0, 100) : null,
      blood_group:        form.blood_group||null,
      prev_pf:            form.prev_pf||'No',
      prev_eps:           form.prev_eps||'No',
      prev_exit_date:     form.prev_exit_date||null,
      scheme_cert:        form.scheme_cert||null,
      ppo_number:         form.ppo_number||null,
      intl_worker:        form.intl_worker||'No',
      country_of_origin:  form.country_of_origin||null,
      passport_number:    form.passport_number||null,
      passport_validity:  form.passport_validity||null,
      location:           form.location||null,
      hr_induction_date:  form.hr_induction_date||null,
      family_members:     form.family_members||[],
      nominees:           form.nominees||[],
      is_active:          true,
    };
    let targetId = existingId;
    if (existingId) {
      const { error } = await supabase.from('employees').update(payload).eq('id', existingId);
      if (error) throw new Error(error.message);
      logAudit(currentUser, "JOINING_KIT_SUBMITTED", { employee_id: existingId, name: payload.name });
    } else {
      const { data: inserted, error } = await supabase.from('employees').insert(payload).select().single();
      if (error) throw new Error(error.message);
      targetId = inserted?.id;
      logAudit(currentUser, "JOINING_KIT_SUBMITTED", { action: "New", name: payload.name });
    }
    if (!targetId) throw new Error('Employee ID was not returned. Refresh before retrying.');
    onPersisted?.(targetId);
    if (targetId) {
      const empCode = payload.employee_code;
      const currentPhotos = { ...(activeCompany?.config?.employee_photos || {}) };
      const currentDocs = { ...(activeCompany?.config?.employee_documents || {}) };
      let configChanged = false;

      if (form.emp_photo !== undefined) {
        if (form.emp_photo) {
          currentPhotos[targetId] = form.emp_photo;
          if (empCode) currentPhotos[empCode] = form.emp_photo;
        } else {
          delete currentPhotos[targetId];
          if (empCode) delete currentPhotos[empCode];
        }
        configChanged = true;
      }

      const empDocs = {};
      if (form.emp_aadhaar_front_doc !== undefined || form.emp_aadhaar_doc !== undefined) {
        empDocs.emp_aadhaar_front_doc = form.emp_aadhaar_front_doc || form.emp_aadhaar_doc || "";
      }
      if (form.emp_aadhaar_back_doc !== undefined) {
        empDocs.emp_aadhaar_back_doc = form.emp_aadhaar_back_doc || "";
      }
      if (form.emp_pan_doc !== undefined) {
        empDocs.emp_pan_doc = form.emp_pan_doc || "";
      }
      if (form.emp_bank_doc !== undefined) {
        empDocs.emp_bank_doc = form.emp_bank_doc || "";
      }

      if (Object.keys(empDocs).length > 0) {
        currentDocs[targetId] = { ...(currentDocs[targetId] || {}), ...empDocs };
        if (empCode) currentDocs[empCode] = { ...(currentDocs[empCode] || {}), ...empDocs };
        configChanged = true;
      }

      if (configChanged) {
        const updatedConfig = {
          ...(activeCompany?.config || {}),
          employee_photos: currentPhotos,
          employee_documents: currentDocs
        };
        const { error: configError } = await supabase.from("companies").update({ config: updatedConfig }).eq("id", activeCompany.id);
        if (configError) throw new Error('Employee saved, but documents could not be saved. Retry to update the same employee. ' + configError.message);
        setActiveCompany(prev => ({ ...prev, config: updatedConfig }));
        setCompanies(prev => prev.map(c => c.id === activeCompany.id ? { ...c, config: updatedConfig } : c));
        try { localStorage.setItem('co_config_' + activeCompany.id, JSON.stringify(updatedConfig)); } catch(e) {}
      }
    }
    await loadEmployees(activeCompany.id);
    return targetId;
  };
  const openEmployee = (e) => {
    const photo = e?.emp_photo || activeCompany?.config?.employee_photos?.[e?.id] || activeCompany?.config?.employee_photos?.[e?.employee_code] || "";
    const docs = activeCompany?.config?.employee_documents?.[e?.id] || activeCompany?.config?.employee_documents?.[e?.employee_code] || {};
    setActiveEmployee({ ...e, ...docs, emp_photo: photo });
    setEmpTab("salary");
    setView("employee_detail");
    loadSalaries(e.id);
  };
  const handleOpenSendPayslipEmail = (emp, sal, monthObj) => {
    const pMonth = monthObj || payMonth || { month: sal?.month || 1, year: sal?.year || 2026 };
    setEmailModalData({
      emp,
      sal: { ...(sal || {}), month: pMonth.month, year: pMonth.year },
      payMonth: pMonth,
      company: activeCompany
    });
    setModal("sendPayslipEmail");
  };
  const goBack = () => {
    if(view==="employee_detail"){ setView("employees"); setActiveEmployee(null); }
    else if(view==="approvals"||view==="dashboard"||view==="joining_kit"||view==="audit_logs"){ setView("employees"); }
    else if(view==="security"){ if(activeCompany) setView("employees"); else { setView("companies"); try{sessionStorage.removeItem("tw_active_company_id");}catch(e){} } }
    else if(view==="employees"||view==="payroll"){ setView("companies"); setActiveCompany(null); setPayMonth(null); try{sessionStorage.removeItem("tw_active_company_id");}catch(e){} }
    else if(view==="company_details"){ setView("companies"); setActiveCompany(null); try{sessionStorage.removeItem("tw_active_company_id");}catch(e){} }
  };

  // ── Company CRUD
  const saveCompany = async (form) => {
    const _isSalaryOnly = modal==="salaryConfig" || view==="salary_structure";
    const _isDetailsPage = view==="company_details" || view==="salary_structure";
    if(!_isSalaryOnly && !form.name.trim()){setFormMsg("Company name is required.");return "Company name is required.";}
    if(!_isSalaryOnly && form.name.trim().length > 100){setFormMsg("Company name cannot exceed 100 characters.");return "Company name cannot exceed 100 characters.";}
    if(!_isSalaryOnly && form.contact && form.contact.trim().length > 100){setFormMsg("Contact person name cannot exceed 100 characters.");return "Contact person name cannot exceed 100 characters.";}
    let normalizedPhone = null;
    if(!_isSalaryOnly){
      const localDigits = getPhone10(form.phone);
      if(!localDigits || localDigits.length !== 10){setFormMsg("Phone number is required. Enter 10-digit number after +91.");return "Phone number is required. Enter 10-digit number after +91.";}
      if(!/^[6-9][0-9]{9}$/.test(localDigits)){setFormMsg("Invalid phone number. Must be a 10-digit Indian mobile number starting with 6–9.");return "Invalid phone number.";}
      normalizedPhone = `+91 ${localDigits}`;

      if (form.login_username) {
        const uClean = form.login_username.trim();
        if (uClean.toLowerCase() === "admin") {
          setFormMsg("This username is not available. Please choose a different username.");
          return "This username is not available. Please choose a different username.";
        }
        const targetId = _isDetailsPage ? activeCompany?.id : editTarget?.id;
        const dupCompany = (companies || []).find(c => c.id !== targetId && c.login_username && c.login_username.trim().toLowerCase() === uClean.toLowerCase());
        if (dupCompany) {
          setFormMsg("This username is not available. Please choose a different username.");
          return "This username is not available. Please choose a different username.";
        }
      }
    }
    const cfg = form.config || null;
    const hasSalary = cfg && (cfg._configured === true ||
      cfg.hra_pct !== undefined || cfg.pf_applicable !== undefined);
    // Only include columns that actually exist in the companies table
    const row = {
      name:                    form.name.trim().slice(0, 100),
      tin:                     form.gst||null,
      address:                 form.address||null,
      contact_person:          form.contact ? form.contact.trim().slice(0, 100) : null,
      contact_email:           form.email||null,
      phone:                   normalizedPhone || (form.phone ? String(form.phone).trim() : null),
      pf_number:               form.pf_number||null,
      esi_code:                form.esi_code||null,
      pt_reg:                  form.pt_reg||null,
      login_username:          form.login_username?.trim()||null,
      is_active:               true,
      plan_tier:               cfg?.plan_tier || form.plan_tier || "Free",
      logo_base64:             cfg?.logo_base64 || null,
    };
    // Only write salary structure if it was configured
    if(hasSalary){
      row.hra_customised         = cfg.hra_customised || false;
      row.hra_percent            = (cfg.hra_type === "fixed") ? -(cfg.hra_value||0) : (cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40));
      row.da_percent             = cfg.da ? (cfg.da_pct||0) : 0;
      row.special_allowance      = cfg.special_allowance && cfg.special_allowance_type==="fixed" ? (cfg.special_allowance_value||0) : 0;
      row.special_allowance_pct  = cfg.special_allowance && cfg.special_allowance_type==="pct"   ? (cfg.special_allowance_value||0) : 0;
      row.conveyance_allowance   = cfg.conveyance ? (cfg.conveyance_amount||0) : 0;
      row.medical_allowance      = cfg.medical    ? (cfg.medical_amount||0)    : 0;
      row.bonus_percent          = cfg.bonus      ? (cfg.bonus_pct||0)         : 0;
      row.bonus_months           = cfg.bonus && cfg.bonus_months?.length ? cfg.bonus_months.join(",") : null;
      row.bonus_display_name     = cfg.bonus_display_name || null;
      row.custom_basic_enabled = cfg.custom_basic_enabled || false;
      row.basic_pct = cfg.basic_pct !== undefined ? cfg.basic_pct : 50;
      row.custom_special_allowance_enabled = cfg.custom_special_allowance_enabled || false;
      row.vpf_tds_enabled        = cfg.vpf_tds_enabled || false;
      row.custom_special_display_name = cfg.custom_special_display_name || null;
      row.ot_rate_per_hour       = cfg.ot_applicable ? (cfg.ot_basis==="gross" ? (cfg.ot_rate_multiplier===2?-3:-1) : (cfg.ot_rate_multiplier===2?-4:-2)) : 0;
      row.absence_deduction_from = (["basic","gross","no_deduct"].includes(String(cfg.absence_basis||"").toLowerCase()) ? String(cfg.absence_basis).toLowerCase() : "no_deduct");
      row.working_days_per_month = cfg.working_days||26;
      row.pf_applicable          = cfg.pf_applicable??true;
      row.esi_applicable         = cfg.esi_applicable??true;
      row.min_wage_enabled       = cfg.min_wage_enabled??true;
      row.min_wage               = cfg.min_wage??15000;
      row.lwf_applicable         = cfg.lwf_applicable??true;
    }
    // Optional columns — only add if they exist in the schema
    // (office_lat/lng are known to exist for attendance)
    if(form.office_lat !== undefined && form.office_lat !== "") row.office_lat = parseFloat(form.office_lat)||null;
    if(form.office_lng !== undefined && form.office_lng !== "") row.office_lng = parseFloat(form.office_lng)||null;
    
    // Always persist the entire config object in the JSONB column
    row.config = { ...DEFAULT_CONFIG, ...(cfg || {}), plan_tier: row.plan_tier };
    if (hasSalary) row.config._configured = true;

    const isDetailsPage = view==="company_details" || view==="salary_structure";
    const isSalaryOnly = modal==="salaryConfig" || view==="salary_structure";
    const targetId = isDetailsPage ? activeCompany.id : editTarget?.id;

    // Backup config to local storage immediately
    if (targetId) {
      try {
        localStorage.setItem('co_config_' + targetId, JSON.stringify(row.config || {}));
      } catch (e) {}
    }

    try {
      // For salary-only saves, only write salary columns (don't overwrite company info)
      if(isSalaryOnly && hasSalary){
        const salOnlyRow = {
          hra_customised:         cfg.hra_customised || false,
          hra_percent:            (cfg.hra_type === "fixed") ? -(cfg.hra_value||0) : (cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40)),
          da_percent:             cfg.da ? (cfg.da_pct||0) : 0,
          special_allowance:      cfg.special_allowance && cfg.special_allowance_type==="fixed" ? (cfg.special_allowance_value||0) : 0,
          special_allowance_pct:  cfg.special_allowance && cfg.special_allowance_type==="pct"   ? (cfg.special_allowance_value||0) : 0,
          conveyance_allowance:   cfg.conveyance ? (cfg.conveyance_amount||0) : 0,
          medical_allowance:      cfg.medical    ? (cfg.medical_amount||0)    : 0,
          bonus_percent:          cfg.bonus      ? (cfg.bonus_pct||0)         : 0,
          bonus_months:           cfg.bonus && cfg.bonus_months?.length ? cfg.bonus_months.join(",") : null,
          bonus_display_name:     cfg.bonus_display_name || null,
          custom_basic_enabled: cfg.custom_basic_enabled || false,
          basic_pct: cfg.basic_pct !== undefined ? cfg.basic_pct : 50,
          custom_special_allowance_enabled: cfg.custom_special_allowance_enabled || false,
          vpf_tds_enabled:          cfg.vpf_tds_enabled || false,
          custom_special_display_name: cfg.custom_special_display_name || null,
          ot_rate_per_hour:       cfg.ot_applicable ? (cfg.ot_basis==="gross" ? (cfg.ot_rate_multiplier===2?-3:-1) : (cfg.ot_rate_multiplier===2?-4:-2)) : 0,
          absence_deduction_from: (["basic","gross","no_deduct"].includes(String(cfg.absence_basis||"").toLowerCase()) ? String(cfg.absence_basis).toLowerCase() : "no_deduct"),
          working_days_per_month: cfg.working_days||26,
          pf_applicable:          cfg.pf_applicable??true,
          esi_applicable:         cfg.esi_applicable??true,
          min_wage_enabled:       cfg.min_wage_enabled !== false,
          min_wage:               (cfg.min_wage !== undefined && cfg.min_wage !== null) ? cfg.min_wage : 15000,
          lwf_applicable:         cfg.lwf_applicable??true,
          config:                 { ...DEFAULT_CONFIG, ...(cfg || {}), _configured: true },
        };
        let {error:salErr} = await supabase.from("companies").update(salOnlyRow).eq("id",targetId);
        if(salErr && (salErr.message?.includes("'config'") || salErr.message?.includes("schema cache"))){
          const fallbackSal = {...salOnlyRow};
          delete fallbackSal.config;
          const retry = await supabase.from("companies").update(fallbackSal).eq("id",targetId);
          salErr = retry.error;
        }
        if(salErr) throw salErr;
        const updatedCo = {...(editTarget||activeCompany), ...salOnlyRow, config:{...DEFAULT_CONFIG,...cfg,_configured:true}};
        setCompanies(p=>p.map(c=>c.id===targetId?updatedCo:c));
        setActiveCompany(prev=>prev?.id===targetId?updatedCo:prev);
        try { localStorage.setItem('co_config_' + targetId, JSON.stringify(updatedCo.config)); } catch(e) {}
        logAudit(currentUser, "SALARY_STRUCTURE_UPDATED", { company_id: targetId });
        setModal(null); return null;
      }
      if(modal==="addCompany" && !isSalaryOnly){
        let {data,error} = await supabase.from("companies").insert(row).select().single();
        if(error && (error.message?.includes("login_password") || error.message?.includes("login_username") || error.message?.includes("schema cache") || error.message?.includes("'config'"))){
          const fallbackRow = {...row};
          delete fallbackRow.login_username;
          delete fallbackRow.login_password;
          delete fallbackRow.config;
          const res = await supabase.from("companies").insert(fallbackRow).select().single();
          data = res.data; error = res.error;
        }
        if(error) throw error;
        if (form.login_username && form.login_password) {
          try { await provisionCompanyAdmin(data.id, form.login_username, form.login_password); }
          catch (accountError) {
            await loadCompanies();
            throw new Error(`Company saved, but administrator access was not created: ${accountError.message} Open company details to create access; do not create the company again.`);
          }
        }
        const nc = {id:data.id, name:data.name, gst:data.tin||"", address:data.address||"", contact:data.contact_person||"", email:data.contact_email||"", phone:data.phone||normalizedPhone||"", pf_number:data.pf_number||"", esi_code:data.esi_code||"", pt_reg:data.pt_reg||"", login_username:data.login_username||"", config:{...DEFAULT_CONFIG,...cfg}};
        setCompanies(p=>[...p,nc]); setEmployees(p=>({...p,[data.id]:[]}));
      } else {
        let {error} = await supabase.from("companies").update(row).eq("id",targetId);
        if(error && (error.message?.includes("login_password") || error.message?.includes("login_username") || error.message?.includes("schema cache") || error.message?.includes("'config'"))){
          const fallbackRow = {...row};
          delete fallbackRow.login_username;
          delete fallbackRow.login_password;
          delete fallbackRow.config;
          const res = await supabase.from("companies").update(fallbackRow).eq("id",targetId);
          error = res.error;
        }
        if(error) throw error;
        const updated = isDetailsPage
          ? {...activeCompany,...form,login_password: undefined,phone:normalizedPhone||form.phone,config:{...DEFAULT_CONFIG,...cfg}}
          : {...editTarget,...form,login_password: undefined,phone:normalizedPhone||form.phone,config:{...DEFAULT_CONFIG,...cfg}};
        setCompanies(p=>p.map(c=>c.id===targetId?updated:c));
        setActiveCompany(prev=>prev?.id===targetId?updated:prev);
        if(isDetailsPage){
          if (view === "salary_structure") {
            logAudit(currentUser, "SALARY_STRUCTURE_UPDATED", { company_id: targetId });
          } else {
            logAudit(currentUser, "COMPANY_PROFILE_UPDATED", { company_id: targetId });
          }
          setFormMsg("Company profile updated successfully!"); return null;
        }
      }
      setModal(null);
      return null;
    } catch(e) {
      setFormMsg("Save failed: "+e.message);
      return e.message;
    }
  };
  const confirmDeleteCompany = async () => {
    try {
      const {error} = await supabase.from("companies").update({is_active:false}).eq("id",deleteTarget.id);
      if(error) throw error;
      setCompanies(p => p.map(c => c.id === deleteTarget.id ? { ...c, is_active: false } : c));
      if(activeCompany?.id===deleteTarget.id) setView("companies");
      setModal(null);
    } catch(e) {
      alert("Deactivate failed: "+e.message);
    }
  };

  const confirmRestoreCompany = async () => {
    try {
      const {error} = await supabase.from("companies").update({is_active:true}).eq("id",deleteTarget.id);
      if(error) throw error;
      setCompanies(p => p.map(c => c.id === deleteTarget.id ? { ...c, is_active: true } : c));
      setModal(null);
    } catch(e) {
      alert("Reactivate failed: "+e.message);
    }
  };

  const confirmHardDeleteCompany = async () => {
    try {
      const coId = deleteTarget.id;
      const coEmps = employees[coId] || [];

      // 1. Delete all R2 storage files for company employees and company logo
      await deleteEmployeeFilesAndStorage(coEmps, deleteTarget);
      const coCode = String(deleteTarget.code || deleteTarget.name || '').trim().replace(/[^A-Za-z0-9_-]+/g, '-');
      if (coCode) {
        const { data: storedKeys, error: storageLookupError } = await supabase.rpc('file_keys', { p_company_id: coId, p_employee_ids: null });
        if (storageLookupError) throw storageLookupError;
        if (storedKeys?.length) await deleteFromR2(storedKeys);
      }

      // 2. Cascade delete all sub-tables in Supabase
      await Promise.allSettled([
        supabase.from("salary_records").delete().eq("company_id", coId),
        supabase.from("attendance_records").delete().eq("company_id", coId),
        supabase.from("employee_leaves").delete().eq("company_id", coId),
        supabase.from("employee_update_requests").delete().eq("company_id", coId),
        supabase.from("fnf_settlements").delete().eq("company_id", coId),
        supabase.from("candidates").delete().eq("company_id", coId),
        supabase.from("employees").delete().eq("company_id", coId)
      ]);

      const {error} = await supabase.from("companies").delete().eq("id", coId);
      if(error) throw error;
      setCompanies(p=>p.filter(c=>c.id!==coId));
      setEmployees(p=>{const n={...p};delete n[coId];return n;});
      if(activeCompany?.id===coId) setView("companies");
      setModal(null);
    } catch(e) {
      alert("Hard delete failed: "+e.message);
    }
  };

  // ── Upload company from excel (single or bulk)
  // ── Employee Excel handlers
  const handleEmployeeExcelUpload = (file) => {
    if(!file) return;
    parseEmployeeExcel(
      file, 
      (list)=>{ 
        setEditTarget(list); 
        setFormMsg(""); 
        setUploadLog(""); 
        setCopiedLog(false);
        setModal("uploadEmpPreview"); 
      }, 
      (err, diag)=>{ 
        const log = diag || `TrustWork Employee Upload Error\nFile: ${file?.name}\nTimestamp: ${new Date().toLocaleString("en-IN")}\nError: ${err}`;
        console.error("[TrustWork Employee Upload Parse Error]:", err, log);
        setUploadLog(log);
        setCopiedLog(false);
        setFormMsg(err); 
        setModal("uploadEmpError"); 
      }
    );
  };
  const saveBulkEmployees = async (list) => {
    if(!list||!list.length) return;
    let importPlan;
    try { importPlan = planEmployeeImport(list, employees[activeCompany.id] || []); }
    catch (error) { setFormMsg(error.message); alert(error.message); return; }
    const validationErrors = list.map((emp, index) => {
      const existing = importPlan[index].existing;
      const error = existing
        ? validateEmployeeSalary({ ...existing, ...employeeSalaryPatch(emp) }, activeCompany?.config || {})
        : validateEmployeeRecord(emp, activeCompany?.config || {});
      return error ? `Row ${index + 1} (${emp.name || emp.employee_code || "Employee"}): ${error}` : "";
    }).filter(Boolean);
    if (validationErrors.length) { setFormMsg(validationErrors.join("\n")); return; }
    const cid = activeCompany.id;
    const planTier = activeCompany?.config?.plan_tier || "Free";
    const empLimit = PLAN_LIMITS[planTier] || 20;
    const currentCount = (employees[cid] || []).length;
    
    const newCount = importPlan.filter(item => !item.existing).length;
    if (newCount > 0 && currentCount + newCount > empLimit) {
      alert(`Bulk upload blocked: You are trying to add ${newCount} employees, but you only have ${empLimit - currentCount} slots left on the ${planTier} plan. Please upgrade your plan.`);
      return;
    }

    setUploadProgress({ current: 0, total: list.length, name: "Verifying records..." });
    
    let added=0, updated=0, skipped=0, errs=[];
    const newlyAdded = [];

    // Keep all existing employee records, including relieved/inactive employees.
    const panList = list.map(e => e.pan ? String(e.pan).toUpperCase().trim() : null).filter(Boolean);
    const existingPanRecords = new Map();
    if (panList.length > 0) {
      try {
        const { data: oldRows, error: oldRowsErr } = await supabase
          .from("employees")
          .select("id, pan, name, is_active, company_id")
          .in("pan", panList);
        if (oldRowsErr) throw oldRowsErr;
        (oldRows || []).forEach(record => {
          const pan = String(record.pan || "").toUpperCase().trim();
          existingPanRecords.set(pan, record);
        });
      } catch (error) {
        setUploadProgress(null);
        setFormMsg("Duplicate PAN check failed: " + error.message + ". No employees were saved. Please retry.");
        return;
      }
    }

    const existingCodes = new Set((employees[cid] || []).map(e => String(e.employee_code || "").trim().toLowerCase()));

    for(let i = 0; i < list.length; i++){
      const emp = list[i];
      setUploadProgress({ current: i + 1, total: list.length, name: emp.name || `Row ${i + 1}` });
      try {
        let code = emp.employee_code?.trim();
        const existing = importPlan[i].existing;
        if (existing) {
          const patch = employeeSalaryPatch(emp);
          const result = await applySalaryRevisions({ company: activeCompany, actor: currentUser, source: "Employee Excel upload",
            changes: [{ employee: existing, patch, note: Number(existing.basic_salary ?? existing.basic ?? 0) === 0 ? "Initial Salary Setup (Excel)" : "Salary Revision (Excel)" }] });
          const savedEmployee = { ...existing, ...mapEmployeeRow(result.employees[0]) };
          setEmployees(previous => ({ ...previous, [cid]: (previous[cid] || []).map(record => record.id === existing.id ? savedEmployee : record) }));
          setActiveCompany(previous => ({ ...previous, config: result.config }));
          setCompanies(previous => previous.map(company => company.id === cid ? { ...company, config: result.config } : company));
          logAudit(currentUser, "EMPLOYEE_SALARY_UPDATED", { company_id: cid, employee_id: existing.id, employee_code: code,
            source: "Excel upload", old_salary: existing.basic_salary ?? existing.basic ?? 0, effective_date: salaryToday(), ...patch });
          updated++;
          continue;
        }
        if (code && existingCodes.has(code.toLowerCase())) {
          errs.push(`${emp.name} (${code}): Employee ID already registered in company roster.`);
          skipped++;
          continue;
        }

        const upperPan = emp.pan ? String(emp.pan).toUpperCase().trim() : null;
        const panOwner = upperPan ? existingPanRecords.get(upperPan) : null;
        if (panOwner) {
          skipped++;
          const message = panOwner.is_active === false
            ? "This employee previously worked here or is inactive. Open their existing record to review."
            : "Employee already exists.";
          errs.push(`Row ${i + 1}: ${emp.name} (${code}): ${message} PAN "${upperPan}" is already registered. No existing record was deleted.`);
          continue;
        }


        const row = {
          company_id: cid, 
          name: String(emp.name || "").trim().slice(0, 100), 
          pan: upperPan,
          date_of_joining: emp.doj || null, 
          date_of_birth: emp.dob || null,
          date_of_exit: emp.date_of_exit || null,
          gender: emp.gender || null, 
          department: emp.department || null,
          designation: emp.designation || null, 
          basic_salary: Math.round(parseFloat(emp.basic) || 0),
          phone: emp.phone || null, 
          email: emp.email || null,
          father_name: emp.father_name ? String(emp.father_name).trim().slice(0, 100) : null, 
          marital_status: emp.marital_status || null,
          prev_exp_years: emp.prev_exp_years || 0, 
          prev_exp_months: emp.prev_exp_months || 0,
          uan: emp.uan || null, 
          esi_id: emp.esi_id || null, 
          pf_account_number: emp.pf_account || null,
          bank_name: emp.bank_name || null, 
          bank_account_number: emp.bank_account || null,
          bank_ifsc: emp.bank_ifsc || null, 
          employment_status: "confirmed",
          employee_code: code,
          is_active: true,
          pension_applicable: true,
          login_pin: null,
          vpf_pct: Math.round(parseFloat(emp.vpf_pct) || 0), 
          tds_amount: Math.round(parseFloat(emp.tds_amount) || 0),
          esi_forced_until: emp.esi_forced_until || null,
          custom_basic_pct: emp.custom_basic_pct !== undefined && emp.custom_basic_pct !== null && emp.custom_basic_pct !== "" ? Math.round(parseFloat(emp.custom_basic_pct) || 0) : null,
          custom_hra_pct: emp.custom_hra_pct !== undefined && emp.custom_hra_pct !== null && emp.custom_hra_pct !== "" ? Math.round(parseFloat(emp.custom_hra_pct) || 0) : null,
          custom_special_allowance: emp.custom_special_allowance !== undefined && emp.custom_special_allowance !== null && emp.custom_special_allowance !== "" ? Math.round(parseFloat(emp.custom_special_allowance) || 0) : null,
        };

        const {data,error} = await supabase.from("employees").insert(row).select().single();
        if(error){
          const msg = error.message||"Database insert failed";
          console.error(`[TrustWork Employee Upload Insert Error] ${emp.name} (${code}):`, error, row);
          const isPanDup = (msg.toLowerCase().includes("unique")||msg.toLowerCase().includes("duplicate"))
            && msg.toLowerCase().includes("pan");
          if(isPanDup){ 
            skipped++; 
            errs.push(`${emp.name} (${code}): Skipped — Duplicate PAN "${row.pan}" already registered in database.`);
            continue; 
          }
          errs.push(`${emp.name} (${code}): ${msg}`);
          continue;
        }

        if (code) existingCodes.add(code.toLowerCase());
        if (upperPan) existingPanRecords.set(upperPan, { ...data, is_active: true });

        const ne = {
          id: data.id,
          company_id: cid,
          name: data.name||"",
          pan: data.pan||"",
          dob: data.date_of_birth||"",
          doj: data.date_of_joining||"",
          gender: data.gender||"",
          department: data.department||"",
          designation: data.designation||"",
          basic: data.basic_salary||0,
          basic_salary: data.basic_salary||0,
          custom_basic_pct: data.custom_basic_pct,
          custom_hra_pct: data.custom_hra_pct,
          custom_special_allowance: data.custom_special_allowance,
          phone: data.phone||"",
          email: data.email||"",
          father_name: data.father_name||"",
          marital_status: data.marital_status||"",
          prev_exp_years: data.prev_exp_years||0,
          prev_exp_months: data.prev_exp_months||0,
          uan: data.uan||"",
          esi_id: data.esi_id||"",
          pf_account: data.pf_account_number||"",
          bank_name: data.bank_name||"",
          bank_account: data.bank_account_number||"",
          bank_ifsc: data.bank_ifsc||"",
          bank_branch: data.bank_branch||"",
          employment_status: data.employment_status||"confirmed",
          employee_code: data.employee_code||"",
          is_active: true
        };
        newlyAdded.push(ne);
        added++;
      } catch(err){ 
        console.error(`[TrustWork Employee Upload Exception] ${emp.name}:`, err);
        errs.push(emp.name+": "+err.message); 
      }
    }

    if (newlyAdded.length > 0) {
      setEmployees(p => ({ ...p, [cid]: [...(p[cid] || []), ...newlyAdded] }));
    }
    setUploadProgress(null);

    const summary = `${added} employee(s) added, ${updated} updated successfully`
      +(skipped ? `, ${skipped} skipped` : "")
      +(errs.length ? `, ${errs.length} failed` : "");

    const detailedLog = [
      `=======================================================`,
      `TRUSTWORK PAYROLL - BULK EMPLOYEE UPLOAD DIAGNOSTIC LOG`,
      `=======================================================`,
      `Timestamp: ${new Date().toLocaleString("en-IN")}`,
      `Company: ${activeCompany?.name || cid} (ID: ${cid})`,
      `Outcome: ${added + updated > 0 && !errs.length ? "SUCCESS" : errs.length && !(added + updated) ? "FAILED" : "PARTIAL SUCCESS"}`,
      `Summary: ${summary}`,
      `Total Rows In Batch: ${list.length}`,
      `Added: ${added} | Updated: ${updated} | Skipped: ${skipped} | Errors: ${errs.length}`,
      ...(errs.length > 0 ? [
        `\n[ERROR DETAILS]`,
        ...errs.map((e, idx) => `  ${idx + 1}. ${e}`)
      ] : []),
      `\n[ROW SUMMARY]`,
      ...list.map((emp, i) => `  Row ${i + 1}: ${emp.name} | Code: ${emp.employee_code} | PAN: ${emp.pan} | Gross: ₹${emp.basic} | DOJ: ${emp.doj} | DOB: ${emp.dob}`)
    ].join("\n");

    console.groupCollapsed(`[TrustWork Employee Upload Summary] ${summary}`);
    console.log(detailedLog);
    console.groupEnd();

    setUploadLog(detailedLog);
    setCopiedLog(false);

    if(errs.length && !(added + updated)) {
      setFormMsg(`Upload failed for all ${list.length} rows.\n\n${errs.join("\n")}`);
    } else {
      setFormMsg(summary + (errs.length ? "\n\nIssues encountered:\n" + errs.join("\n") : ""));
    }
    setModal("uploadEmpDone"); setEditTarget(null);
  };
  const handleSalaryStructureUpload = (file) => {
    if(!file) return;
    parseSalaryStructureExcel(file, async (cfg)=>{
      const cid = activeCompany.id;
      const row = {
        hra_percent:(cfg.hra_type==="fixed")?-(cfg.hra_value||0):(cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40)), da_percent:cfg.da?(cfg.da_pct||0):0, working_days_per_month:cfg.working_days||26,
        pf_applicable:cfg.pf_applicable??true, esi_applicable:cfg.esi_applicable??true,
        special_allowance:cfg.special_allowance&&cfg.special_allowance_type==="fixed"?(cfg.special_allowance_value||0):0,
        special_allowance_pct:cfg.special_allowance&&cfg.special_allowance_type==="pct"?(cfg.special_allowance_value||0):0,
        conveyance_allowance:cfg.conveyance?(cfg.conveyance_amount||0):0,
        medical_allowance:cfg.medical?(cfg.medical_amount||0):0,
        bonus_percent:cfg.bonus?(cfg.bonus_pct||0):0,
        bonus_months:cfg.bonus&&cfg.bonus_months?.length?cfg.bonus_months.join(","):null,
        ot_rate_per_hour:cfg.ot_applicable?(cfg.ot_basis==="gross" ? (cfg.ot_rate_multiplier===2?-3:-1) : (cfg.ot_rate_multiplier===2?-4:-2)):0,
        absence_deduction_from:(["basic","gross","no_deduct"].includes(String(cfg.absence_basis||"").toLowerCase())?String(cfg.absence_basis).toLowerCase():"no_deduct"),
      };
      try {
        const {error} = await supabase.from("companies").update(row).eq("id",cid);
        if(error) throw error;
        // Update local state immediately without full reload
        const updatedCfg = {...DEFAULT_CONFIG,...cfg,_configured:true};
        const updatedCo = {...activeCompany, config:updatedCfg};
        setCompanies(p=>p.map(c=>c.id===cid?updatedCo:c));
        setActiveCompany(updatedCo);
        alert("✅ Salary structure updated!");
      } catch(err){ alert("Save failed: "+err.message); }
    }, (err)=>alert("Upload error: "+err));
  };

  const handleCompanyExcelUpload = (file) => {
    if(!file) return;
    parseCompanyExcel(
      file,
      (parsedList, isBulk) => {
        setEditTarget(parsedList);  // always an array now
        setFormMsg("");
        setModal(isBulk ? "uploadBulkPreview" : "uploadCompanyPreview");
      },
      (err) => { setFormMsg(err); setModal("uploadCompanyError"); }
    );
  };

  // ── Save bulk companies from excel upload
  const saveBulkCompanies = async (list) => {
    let added=0; const skipped=[];
    for(const form of list){
      if(!form.name.trim()) continue;
      if(companies.find(c=>c.name.toLowerCase()===form.name.toLowerCase())){
        skipped.push(form.name); continue;
      }
      const cfg = form.config||null;
      const hasSal = cfg && cfg._configured;
      const row = {
        name: form.name.trim().slice(0, 100), tin: form.gst||null, address: form.address||null,
        contact_person: form.contact ? form.contact.trim().slice(0, 100) : null, contact_email: form.email||null, phone: form.phone||null, is_active: true,
      };
      if(hasSal){
        row.hra_percent = (cfg.hra_type==="fixed")?-(cfg.hra_value||0):(cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40));
        row.da_percent = cfg.da ? (cfg.da_pct||0) : 0;
        row.special_allowance = cfg.special_allowance && cfg.special_allowance_type==="fixed" ? (cfg.special_allowance_value||0) : 0;
        row.special_allowance_pct = cfg.special_allowance && cfg.special_allowance_type==="pct" ? (cfg.special_allowance_value||0) : 0;
        row.conveyance_allowance = cfg.conveyance ? (cfg.conveyance_amount||0) : 0;
        row.medical_allowance = cfg.medical ? (cfg.medical_amount||0) : 0;
        row.bonus_percent = cfg.bonus ? (cfg.bonus_pct||0) : 0;
        row.bonus_months = cfg.bonus && cfg.bonus_months?.length ? cfg.bonus_months.join(",") : null;
        row.bonus_display_name = cfg.bonus_display_name || null;
        row.custom_basic_enabled = cfg.custom_basic_enabled || false;
        row.basic_pct = cfg.basic_pct !== undefined && cfg.basic_pct !== null ? cfg.basic_pct : 50;
        row.custom_special_allowance_enabled = cfg.custom_special_allowance_enabled || false;
        row.custom_special_display_name = cfg.custom_special_display_name || null;
        row.ot_rate_per_hour = cfg.ot_applicable ? (cfg.ot_basis==="gross" ? (cfg.ot_rate_multiplier===2?-3:-1) : (cfg.ot_rate_multiplier===2?-4:-2)) : 0;
        row.absence_deduction_from = (["basic","gross","no_deduct"].includes(String(cfg.absence_basis||"").toLowerCase()) ? String(cfg.absence_basis).toLowerCase() : "no_deduct");
        row.working_days_per_month = cfg.working_days||26;
        row.pf_applicable = cfg.pf_applicable??true;
        row.esi_applicable = cfg.esi_applicable??true;
      }
      try {
        const {data,error} = await supabase.from("companies").insert(row).select().single();
        if(error) throw error;
        added++;
      } catch(e){ skipped.push(form.name+" ("+e.message+")"); }
    }
    await loadCompanies();
    setModal(null);
    const msg = `✅ Added ${added} company${added!==1?"ies":"y"}.${skipped.length?" Skipped: "+skipped.join(", "):""}`;
    alert(msg);
  };

  // ── Employee CRUD
  const saveEmployee = async (form) => {
    const validationError = validateEmployeeRecord(form, activeCompany?.config || {});
    if (validationError) { setFormMsg(validationError); return; }

    const planTier = activeCompany?.config?.plan_tier || "Free";
    const empLimit = PLAN_LIMITS[planTier] || 20;
    const currentCount = (employees[activeCompany?.id] || []).length;
    
    // Only check limit for new employees, not edits
    if (!form.id && currentCount >= empLimit) {
      alert(`You have reached the employee limit (${empLimit}) for the ${planTier} plan. Please contact the administrator to upgrade.`);
      return;
    }

    if(!form.employee_code || !form.employee_code.trim()){setFormMsg("Employee ID / Code is required.");return;}
    const code = form.employee_code.trim();
    const existing = (employees[activeCompany?.id] || []).find(e => String(e.employee_code || "").trim().toLowerCase() === code.toLowerCase() && e.id !== form.id);
    if(existing){setFormMsg("Employee ID must be unique. This ID is already in use.");return;}
    if(!form.name.trim()){setFormMsg("Name required.");return;}
    if(form.name.trim().length > 100){setFormMsg("Employee name cannot exceed 100 characters.");return;}
    if(form.father_name && form.father_name.trim().length > 100){setFormMsg("Father's name cannot exceed 100 characters.");return;}
    if(form.emergency_name && form.emergency_name.trim().length > 100){setFormMsg("Emergency contact name cannot exceed 100 characters.");return;}
    const panClean = (form.pan||"").trim().toUpperCase();
    if(panClean && !validatePAN(panClean)){setFormMsg("Invalid PAN format (Must be 10 characters: 5 letters, 4 numbers, 1 letter, e.g. ABCDE1234F).");return;}
    form.pan = panClean || null;
    if(form.phone && form.phone.length !== 10){setFormMsg("Phone number must be exactly 10 digits.");return;}
    if(form.emergency_phone && form.emergency_phone.length !== 10){setFormMsg("Emergency phone number must be exactly 10 digits.");return;}
    if(!form.dob){setFormMsg("Date of Birth is required.");return;}
    // DOB age validation: must be 18–60
    if(form.dob){
      const age=calcAge(form.dob);
      if(age===""||age<18){setFormMsg("Employee must be at least 18 years old.");return;}
      if(age>60){setFormMsg("Employee must be under 60 years old.");return;}
    }
    const dojCheck = validateDOJ(form.doj);
    if(!dojCheck.ok){setFormMsg(dojCheck.msg);return;}
    // DOJ must be DOB+18 to DOB+60
    if(form.dob&&form.doj){
      const dob=new Date(form.dob), doj=new Date(form.doj);
      const min18=new Date(dob); min18.setFullYear(dob.getFullYear()+18);
      const max60=new Date(dob); max60.setFullYear(dob.getFullYear()+60);
      if(doj<min18){setFormMsg("Date of Joining must be at least 18 years after Date of Birth.");return;}
      if(doj>=max60){setFormMsg("Date of Joining must be less than 60 years after Date of Birth.");return;}
    }
    // Previous experience validation
    if(form.dob){
      const dob=new Date(form.dob);
      const prevMonths=(parseInt(form.prev_exp_years)||0)*12+(parseInt(form.prev_exp_months)||0);
      if(prevMonths>0){
        const earliest18=new Date(dob); earliest18.setFullYear(dob.getFullYear()+18);
        const reference=form.doj&&validateDOJ(form.doj).ok?new Date(form.doj):new Date();
        const maxPrevMonths=Math.max(0,(reference.getFullYear()-earliest18.getFullYear())*12+(reference.getMonth()-earliest18.getMonth()));
        if(prevMonths>maxPrevMonths){setFormMsg("Previous experience exceeds what's possible given Date of Birth (work can only start at age 18).");return;}
      }
    }
    // Check all employee records; inactive employees retain their identity and history.
    if (form.pan) {
      const upperPan = String(form.pan).trim().toUpperCase();
      const duplicateMessage = record => record.is_active === false
        ? "This employee previously worked here or is inactive. Open their existing record to review. PAN is already registered; a duplicate cannot be created."
        : "Employee already exists. PAN is already registered; a duplicate cannot be created.";
      const allEmps = Object.values(employees).flat();
      const dupLocal = allEmps.find(e => e.pan && String(e.pan).trim().toUpperCase() === upperPan && e.id !== editTarget?.id);
      if (dupLocal) { setFormMsg(duplicateMessage(dupLocal)); return; }
      try {
        let q = supabase.from("employees").select("id,name,is_active,company_id").eq("pan", upperPan);
        if (editTarget?.id) q = q.neq("id", editTarget.id);
        const { data: dupRows, error: dupErr } = await q;
        if (dupErr) throw dupErr;
        if (dupRows?.length) { setFormMsg(duplicateMessage(dupRows[0])); return; }
      } catch (error) { setFormMsg("Duplicate check failed: " + error.message); return; }
    }
    const cid = activeCompany.id;

    const row = {
      company_id:       cid,
      name:             form.name.trim().slice(0, 100),
      father_name:      form.father_name ? form.father_name.trim().slice(0, 100) : null,
      pan:              form.pan ? form.pan.toUpperCase() : null,
      date_of_birth:     form.dob||null,
      age:              form.dob ? calcAge(form.dob) : null,
      gender:           form.gender||null,
      marital_status:   form.marital_status||null,
      employment_status:  form.employment_status||"confirmed",
      prev_exp_years:     parseInt(form.prev_exp_years)||0,
      prev_exp_months:    parseInt(form.prev_exp_months)||0,
      perm_address:     form.perm_address||null,
      perm_city:        form.perm_city||null,
      perm_state:       form.perm_state||null,
      perm_pincode:     form.perm_pincode||null,
      temp_address:     form.same_address?(form.perm_address||null):(form.temp_address||null),
      temp_city:        form.same_address?(form.perm_city||null):(form.temp_city||null),
      temp_state:       form.same_address?(form.perm_state||null):(form.temp_state||null),
      temp_pincode:     form.same_address?(form.perm_pincode||null):(form.temp_pincode||null),
      emergency_name:   form.emergency_name ? form.emergency_name.trim().slice(0, 100) : null,
      emergency_relation:form.emergency_relation||null,
      emergency_phone:  form.emergency_phone||null,
      date_of_joining:  form.doj||null,
      date_of_exit:     form.date_of_exit||null,
      phone:            form.phone||null,
      email:            form.email||null,
      department:       form.department||null,
      designation:      form.designation||null,
      basic_salary:     Math.round(parseFloat(form.basic_salary||form.basic||0) || 0),
      vpf_pct:          Math.round(parseFloat(form.vpf_pct||0) || 0),
      pension_applicable: form.pension_applicable!==false,
      tds_amount:       Math.round(parseFloat(form.tds_amount||0) || 0),
      uan:              form.uan||null,
      pf_account_number:form.pf_account||null,
      esi_id:           form.esi_id||null,
      bank_name:        form.bank_name||null,
      bank_account_number: form.bank_account||null,
      bank_ifsc:        form.bank_ifsc||null,
      bank_branch:      form.bank_branch||null,
      employee_code:    (form.employee_code||"TMP").trim(),
      custom_basic_pct: form.custom_basic_pct !== "" && form.custom_basic_pct !== undefined ? Math.round(parseFloat(form.custom_basic_pct) || 0) : null,
      custom_hra_pct:   form.custom_hra_pct !== "" && form.custom_hra_pct !== undefined ? Math.round(parseFloat(form.custom_hra_pct) || 0) : null,
      custom_special_allowance: form.custom_special_allowance !== "" && form.custom_special_allowance != null ? Math.round(parseFloat(form.custom_special_allowance) || 0) : null,
      esi_forced_until: form.esi_forced_until||null,
      login_pin:        null,
      is_active:        true,
    };
    try {
      if(modal==="addEmp" || !form.id){
        const {data,error} = await supabase.from("employees").insert(row).select().single();
        if(error) throw error;
        const photoToSave = form.emp_photo || "";
        const empId = data.id;
        const empCode = data.employee_code || row.employee_code;
        const currentPhotos = { ...(activeCompany?.config?.employee_photos || {}) };
        if (photoToSave) {
          currentPhotos[empId] = photoToSave;
          if (empCode) currentPhotos[empCode] = photoToSave;
        }
        const currentHierarchy = { ...(activeCompany?.config?.reporting_hierarchy || {}) };
        if (form.reporting_manager_id) {
          currentHierarchy[empId] = form.reporting_manager_id;
        } else {
          delete currentHierarchy[empId];
        }
        const updatedConfig = { 
          ...(activeCompany?.config || {}), 
          employee_photos: currentPhotos,
          reporting_hierarchy: currentHierarchy
        };
        await supabase.from("companies").update({ config: updatedConfig }).eq("id", cid);
        setActiveCompany(prev => ({ ...prev, config: updatedConfig }));
        setCompanies(prev => prev.map(c => c.id === cid ? { ...c, config: updatedConfig } : c));

        const ne = {id:data.id, company_id:cid, name:data.name, father_name:data.father_name||"", dob:data.date_of_birth||"", gender:data.gender||"", marital_status:data.marital_status||"", employment_status:data.employment_status||"confirmed", prev_exp_years:data.prev_exp_years||0, prev_exp_months:data.prev_exp_months||0,
        perm_address:data.perm_address||"", perm_city:data.perm_city||"", perm_state:data.perm_state||"", perm_pincode:data.perm_pincode||"",
        temp_address:data.temp_address||"", temp_city:data.temp_city||"", temp_state:data.temp_state||"", temp_pincode:data.temp_pincode||"",
        emergency_name:data.emergency_name||"", emergency_relation:data.emergency_relation||"", emergency_phone:data.emergency_phone||"", pan:data.pan||"", age:data.age||"", doj:data.date_of_joining||"", date_of_exit:data.date_of_exit||"", phone:data.phone||"", email:data.email||"", department:data.department||"", designation:data.designation||"", basic:data.basic_salary||0, basic_salary:data.basic_salary||0, vpf_pct:data.vpf_pct||0, pension_applicable:data.pension_applicable!==false, tds_amount:data.tds_amount||0, uan:data.uan||"", pf_account:data.pf_account_number||"", esi_id:data.esi_id||"", bank_name:data.bank_name||"", bank_account:data.bank_account_number||"", bank_ifsc:data.bank_ifsc||"", bank_branch:data.bank_branch||"", employee_code:data.employee_code||"", custom_basic_pct:data.custom_basic_pct, custom_hra_pct:data.custom_hra_pct, custom_special_allowance:data.custom_special_allowance, esi_forced_until:data.esi_forced_until||null, login_pin:null, is_active:data.is_active, emp_photo: photoToSave, reporting_manager_id: form.reporting_manager_id || ""};
        setEmployees(p=>({...p,[cid]:[...(p[cid]||[]),ne]}));
        logAudit(currentUser, "ADD_EMPLOYEE", {
          employee_id: data.id,
          name: data.name,
          employee_code: data.employee_code || form.employee_code || "",
          department: data.department || form.department || "",
          designation: data.designation || form.designation || "",
          company_id: cid
        });
        if (modal === "addEmp") {
          setModal("postAddEmp");
        } else {
          setView("employees");
          setActiveEmployee(null);
          setModal(null);
        }
      } else {
        const empId = form.id;
        const empCode = row.employee_code || editTarget?.employee_code;
        const photoToSave = form.emp_photo !== undefined ? form.emp_photo : (editTarget?.emp_photo || "");
        const employeePatch = { ...row };
        delete employeePatch.company_id;
        delete employeePatch.login_pin;
        const previousEmployee = (employees[cid] || []).find(employee => employee.id === empId) || editTarget;
        const photoChanges = { [empId]: photoToSave || null, ...(empCode ? { [empCode]: photoToSave || null } : {}) };
        const result = await applySalaryRevisions({ company: activeCompany, actor: currentUser, source: "Employee edit",
          changes: [{ employee: previousEmployee, patch: employeePatch }],
          configChanges: { employee_photos: photoChanges, reporting_hierarchy: { [empId]: form.reporting_manager_id || null } } });
        const updatedConfig = result.config;
        const newSal = Number(result.employees[0].basic_salary);
        setActiveCompany(previous => ({ ...previous, config: updatedConfig }));
        setCompanies(previous => previous.map(company => company.id === cid ? { ...company, config: updatedConfig } : company));
        const updated = {...(editTarget||{}), ...form, reporting_manager_id: form.reporting_manager_id || "", emp_photo: photoToSave, login_pin: null, pan: form.pan ? form.pan.toUpperCase() : (editTarget?.pan || ""), basic: newSal, basic_salary: newSal, id: form.id};
        setEmployees(p=>({...p,[cid]:p[cid].map(e=>e.id===form.id?updated:e)}));
        logAudit(currentUser, "EDIT_EMPLOYEE", {
          employee_id: form.id,
          name: form.name,
          employee_code: form.employee_code || editTarget?.employee_code || "",
          department: form.department || editTarget?.department || "",
          designation: form.designation || editTarget?.designation || "",
          company_id: cid
        });
        if(activeEmployee?.id===form.id) setActiveEmployee(updated);
        
        if (view === "employee_form") {
          setView("employees");
          setActiveEmployee(null);
        }
      }
      setModal(null);
    } catch(e) {
      setFormMsg("Save failed: "+e.message);
    }
  };
  const confirmDeleteEmp = async () => {
    const cid = activeCompany.id;
    try {
      // 1. Delete all R2 storage files & cached local photos/docs in the background (non-blocking)
      deleteEmployeeFilesAndStorage(deleteTarget, activeCompany).catch(e => console.warn("[Background R2 delete]:", e));

      // 2. Cascade delete database records (salary_records, attendance_records, leaves, update_requests, fnf, employees)
      await purgeEmployeeDatabaseRecords(cid, [deleteTarget.id]);

      // 3. Update React in-memory state
      setEmployees(p => ({
        ...p,
        [cid]: (p[cid] || []).filter(e => e.id !== deleteTarget.id).map(e => e.reporting_manager_id === deleteTarget.id ? { ...e, reporting_manager_id: "" } : e)
      }));
      logAudit(currentUser, "DELETE_EMPLOYEE", {
        employee_id: deleteTarget.id,
        name: deleteTarget.name,
        employee_code: deleteTarget.employee_code || "",
        department: deleteTarget.department || "",
        designation: deleteTarget.designation || "",
        company_id: cid
      });
      setSalaries(p => { const n = { ...p }; delete n[deleteTarget.id]; return n; });
      setAttendance(p => { const n = { ...p }; delete n[deleteTarget.id]; return n; });
      if (activeEmployee?.id === deleteTarget.id) {
        setView("employees");
        setActiveEmployee(null);
      }

      // 4. Clean company configuration
      const photos = { ...(activeCompany?.config?.employee_photos || {}) };
      const docs = { ...(activeCompany?.config?.employee_documents || {}) };
      const hikes = { ...(activeCompany?.config?.salary_hikes || {}) };
      const hierarchy = { ...(activeCompany?.config?.reporting_hierarchy || {}) };
      const fnf = { ...(activeCompany?.config?.fnf_settlements || {}) };
      let cfgChanged = false;

      const delId = deleteTarget.id;
      const delCode = deleteTarget.employee_code;

      if (photos[delId] || (delCode && photos[delCode])) {
        delete photos[delId];
        if (delCode) delete photos[delCode];
        cfgChanged = true;
      }
      if (docs[delId] || (delCode && docs[delCode])) {
        delete docs[delId];
        if (delCode) delete docs[delCode];
        cfgChanged = true;
      }
      if (hikes[delId] || (delCode && hikes[delCode])) {
        delete hikes[delId];
        if (delCode) delete hikes[delCode];
        cfgChanged = true;
      }
      if (fnf[delId] || (delCode && fnf[delCode])) {
        delete fnf[delId];
        if (delCode) delete fnf[delCode];
        cfgChanged = true;
      }
      if (hierarchy[delId] || (delCode && hierarchy[delCode])) {
        delete hierarchy[delId];
        if (delCode) delete hierarchy[delCode];
        cfgChanged = true;
      }
      Object.keys(hierarchy).forEach(k => {
        if (hierarchy[k] === delId || (delCode && hierarchy[k] === delCode)) {
          delete hierarchy[k];
          cfgChanged = true;
        }
      });

      if (cfgChanged) {
        const updatedConfig = {
          ...(activeCompany?.config || {}),
          employee_photos: photos,
          employee_documents: docs,
          salary_hikes: hikes,
          reporting_hierarchy: hierarchy,
          fnf_settlements: fnf
        };
        await supabase.from("companies").update({ config: updatedConfig }).eq("id", cid);
        setActiveCompany(p => p ? { ...p, config: updatedConfig } : p);
        setCompanies(p => p.map(c => c.id === cid ? { ...c, config: updatedConfig } : c));
      }

      setModal(null);
    } catch(e) {
      alert("Delete failed: " + e.message);
    }
  };

  const confirmDeleteMultipleEmps = async () => {
    const cid = activeCompany.id;
    try {
      const currentEmps = employees[cid] || [];
      const targetEmps = currentEmps.filter(e => selectedEmps.includes(e.id));

      // 1. Delete all R2 storage files & cached local photos/docs for all selected employees in background (non-blocking)
      deleteEmployeeFilesAndStorage(targetEmps, activeCompany).catch(e => console.warn("[Background R2 delete]:", e));

      // 2. Cascade delete database records in child tables and employees table
      await purgeEmployeeDatabaseRecords(cid, selectedEmps);

      // 3. Clean company configuration
      const photos = { ...(activeCompany?.config?.employee_photos || {}) };
      const docs = { ...(activeCompany?.config?.employee_documents || {}) };
      const hikes = { ...(activeCompany?.config?.salary_hikes || {}) };
      const hierarchy = { ...(activeCompany?.config?.reporting_hierarchy || {}) };
      const fnf = { ...(activeCompany?.config?.fnf_settlements || {}) };
      let cfgChanged = false;

      selectedEmps.forEach(id => {
        const emp = currentEmps.find(e => e.id === id);
        const code = emp?.employee_code;
        if (photos[id] || (code && photos[code])) {
          delete photos[id];
          if (code) delete photos[code];
          cfgChanged = true;
        }
        if (docs[id] || (code && docs[code])) {
          delete docs[id];
          if (code) delete docs[code];
          cfgChanged = true;
        }
        if (hikes[id] || (code && hikes[code])) {
          delete hikes[id];
          if (code) delete hikes[code];
          cfgChanged = true;
        }
        if (fnf[id] || (code && fnf[code])) {
          delete fnf[id];
          if (code) delete fnf[code];
          cfgChanged = true;
        }
        if (hierarchy[id] || (code && hierarchy[code])) {
          delete hierarchy[id];
          if (code) delete hierarchy[code];
          cfgChanged = true;
        }
      });

      // Clear any employees who had these managers in hierarchy
      Object.keys(hierarchy).forEach(k => {
        if (selectedEmps.includes(hierarchy[k])) {
          delete hierarchy[k];
          cfgChanged = true;
        }
      });

      if (cfgChanged) {
        const updatedConfig = {
          ...(activeCompany?.config || {}),
          employee_photos: photos,
          employee_documents: docs,
          salary_hikes: hikes,
          reporting_hierarchy: hierarchy,
          fnf_settlements: fnf
        };
        await supabase.from("companies").update({ config: updatedConfig }).eq("id", cid);
        setActiveCompany(p => p ? { ...p, config: updatedConfig } : p);
        setCompanies(p => p.map(c => c.id === cid ? { ...c, config: updatedConfig } : c));
      }

      // 4. Update React state
      setEmployees(p => ({
        ...p,
        [cid]: (p[cid] || []).filter(e => !selectedEmps.includes(e.id)).map(e => selectedEmps.includes(e.reporting_manager_id) ? { ...e, reporting_manager_id: "" } : e)
      }));
      setSalaries(p => {
        const n = { ...p };
        selectedEmps.forEach(id => delete n[id]);
        return n;
      });
      setAttendance(p => {
        const n = { ...p };
        selectedEmps.forEach(id => delete n[id]);
        return n;
      });
      if (activeEmployee && selectedEmps.includes(activeEmployee.id)) {
        setView("employees");
        setActiveEmployee(null);
      }
      setSelectedEmps([]);
      setModal(null);
    } catch(e) {
      alert("Delete failed: " + e.message);
    }
  };

  const handleSaveHike = async () => {
    if (!activeEmployee || !activeCompany || savingHike) return;
    const newSalary = Math.round(Number(hikeForm.newGross));
    if (newSalary === Number(activeEmployee.basic_salary ?? activeEmployee.basic ?? 0)) {
      alert("New salary is the same as the current salary. Enter a changed salary to record a revision.");
      return;
    }
    setSavingHike(true);
    try {
      const result = await applySalaryRevisions({ company: activeCompany, actor: currentUser,
        effectiveDate: hikeForm.date, source: "Employee salary revision",
        changes: [{ employee: activeEmployee, patch: { basic_salary: newSalary }, note: hikeForm.note }] });
      const updatedEmp = { ...activeEmployee, ...mapEmployeeRow(result.employees[0]) };
      setActiveEmployee(updatedEmp);
      setEmployees(previous => ({ ...previous, [activeCompany.id]: (previous[activeCompany.id] || []).map(employee => employee.id === updatedEmp.id ? updatedEmp : employee) }));
      setActiveCompany(previous => ({ ...previous, config: result.config }));
      setCompanies(previous => previous.map(company => company.id === activeCompany.id ? { ...company, config: result.config } : company));
      setHikeModal(false);
      logAudit(currentUser, "SALARY_HIKE", { company_id: activeCompany.id, employee_id: updatedEmp.id,
        old_salary: activeEmployee.basic_salary ?? activeEmployee.basic ?? 0, new_salary: newSalary, effective_date: hikeForm.date });
    } catch (error) { alert("Failed to record salary revision: " + error.message); }
    finally { setSavingHike(false); }
  };

  const handleConvertCandidateToEmployee = async (candidate) => {
    if (!activeCompany) throw new Error('Select a company first.');
    const createdRow = await convertJoinedCandidate(activeCompany, candidate.id);
    const created = mapEmployeeRow(createdRow);
    setEmployees(previous => ({ ...previous, [activeCompany.id]: [created, ...(previous[activeCompany.id] || []).filter(employee => employee.id !== created.id)] }));
    await loadCompanies();
    logAudit(currentUser, 'CONVERT_CANDIDATE', {company_id: activeCompany.id, candidate_id: candidate.id, employee_id: created.id});
    setActiveEmployee(created); setEmpTab('employee'); setView('employee_detail');
  };

  // ── Salary (employee detail)
  const saveSalary = async (form) => {
    try {
      await assertPayrollDraft(activeCompany.id, form);
      if (editTarget?.month && (editTarget.month !== form.month || editTarget.year !== form.year)) await assertPayrollDraft(activeCompany.id, editTarget);
    } catch (error) { setFormMsg("Save failed: " + error.message); return; }

    const eid = activeEmployee.id;
    const dup = (salaries[eid]||[]).find(s=>s.month===form.month&&s.year===form.year&&s.id!==editTarget?.id);
    if(dup){setFormMsg("Salary for this month already exists.");return;}
    const cfg = activeCompany?.config || DEFAULT_CONFIG;
    const empVpfPct = activeEmployee?.vpf_pct || 0;
    const empTds    = activeEmployee?.tds_amount || 0;
    const calc = calcSalaryFull({basic:form.basic,month:form.month,year:form.year,absent_days:form.absent_days||0,ot_hours:form.ot_hours||0,cfg,extra:{vpf_enabled:empVpfPct>0,vpf_pct:empVpfPct,tds_amount:empTds,emp_dob:activeEmployee?.date_of_birth,custom_basic_pct: activeEmployee?.custom_basic_pct, custom_hra_pct:activeEmployee?.custom_hra_pct, custom_special_allowance:activeEmployee?.custom_special_allowance}});
    const row = {
      employee_id:        eid,
      company_id:         activeCompany.id,
      month:              form.month,
      year:               form.year,
      basic:              calc.basic||0,
      days_absent:        form.absent_days||0,
      ot_hours:           form.ot_hours||0,
      hra:                calc.hra||0,
      da:                 calc.da||0,
      special_allowance:  calc.special||0,
      conveyance:         calc.conveyance||0,
      medical:            calc.medical||0,
      bonus:              calc.bonus||0,
      ot_amount:          calc.ot_pay||0,
      gross:              calc.gross||0,
      pf_employee:        calc.pf_emp||0,
      pf_employer:        calc.pf_er||0,
      esi_employee:       calc.esi_emp||0,
      esi_employer:       calc.esi_er||0,
      professional_tax:   calc.pt||0,
      absence_deduction:  calc.absence_deduction||0,
      vpf:                calc.vpf||0,
      tds:                calc.tds||0,
      net_pay:            calc.net||0,
      ctc:                calc.ctc||0,
      status:             "draft",
    };
    try {
      if(modal==="addSalary"){
        const {data,error} = await supabase.from("salary_records").insert(row).select().single();
        if(error) throw error;
        setSalaries(p=>({...p,[eid]:[...(p[eid]||[]),{id:data.id,...form,...calc}]}));
      } else {
        const {data, error} = await supabase.from("salary_records").update(row).eq("id",editTarget.id).eq("status", "draft").select("id");
        if(error) throw error;
        if (!data?.length) throw new Error("Salary record changed or is locked. Refresh before retrying.");
        setSalaries(p=>({...p,[eid]:p[eid].map(s=>s.id===editTarget.id?{...s,...form,...calc}:s)}));
      }
      setModal(null);
    } catch(e) {
      setFormMsg("Save failed: "+e.message);
    }
  };
  const confirmDeleteSalary = async () => {
    const eid = activeEmployee.id;
    try {
      await assertPayrollDraft(activeCompany.id, deleteTarget);
      const {data, error} = await supabase.from("salary_records").delete().eq("id",deleteTarget.id).eq("status", "draft").select("id");
      if(error) throw error;
      if (!data?.length) throw new Error("Salary record changed or is locked. Refresh before retrying.");
      setSalaries(p=>({...p,[eid]:p[eid].filter(s=>s.id!==deleteTarget.id)}));
      setModal(null);
    } catch(e) {
      alert("Delete failed: "+e.message);
    }
  };

  const handlePayslipStyleSaved = (companyId, style) => {
    setCompanies(previous=>previous.map(company=>company.id === companyId?{...company,config:{...company.config,payslip_style:style}}:company));
    setActiveCompany(previous=>previous?.id === companyId?{...previous,config:{...previous.config,payslip_style:style}}:previous);
    setEditTarget(previous=>previous?.id === companyId?{...previous,config:{...previous.config,payslip_style:style}}:previous);
  };

  // ── Save payroll run
  const savePayrollRun = async () => {
    if(!payMonth) return;
    const cfg = activeCompany?.config || DEFAULT_CONFIG;
    const wd = cfg.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (cfg.working_days || 26);

    // Compliance Check
    let hasComplianceIssue = false;
    for (let emp of eligibleEmps) {
      const s = payrollData[emp.id];
      if(!s) continue;
      const present = wd - (s.absent_days || 0);
      const totalEquivalentDays = present + ((s.ot_hours || 0) / 8);
      if (totalEquivalentDays > 31) {
        hasComplianceIssue = true;
        break;
      }
    }
    
    if (hasComplianceIssue) {
      if (!window.confirm("⚠️ COMPLIANCE WARNING\n\nOne or more employees have logged Overtime hours that push their total equivalent work days beyond 31 days for the month. This violates maximum legal working hour limits.\n\nDo you still want to proceed and save this payroll run?")) {
        return;
      }
    }

    const invalidEmployee = eligibleEmps.find(emp => validateEmployeeSalary(emp, cfg));
    if (invalidEmployee) { setFormMsg("Save failed: " + invalidEmployee.name + ": " + validateEmployeeSalary(invalidEmployee, cfg)); return; }
    const toUpsert = buildPayrollRows(eligibleEmps, payrollData, activeCompany.id, payMonth);
    try {
      if (!await payrollWorkflow.saveDraft(toUpsert)) return;
      // Bulk reload: one query for all company salaries instead of N per-employee requests
      await reloadCompanySalaries(activeCompany.id, companyEmps.map(employee => employee.id));
      setEditAttRow({});
      setFormMsg("Payroll saved for "+MONTHS[payMonth.month]+" "+payMonth.year+"!");
      setTimeout(()=>setFormMsg(""), 5000);
    } catch(e) {
      setFormMsg("Save failed: "+e.message);
      setTimeout(()=>setFormMsg(""), 5000);
    }
  };

  // ── Upload attendance Excel
  const handleAttUpload = (file) => {
    if(!file||!payMonth) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        
        if (!rawRows || !rawRows.length) return;
        
        let hIdx = rawRows.findIndex(r => r.some(cell => String(cell||"").toLowerCase().includes("name")));
        if (hIdx < 0) hIdx = 0;

        const headerRow = rawRows[hIdx].map(h => String(h || "").trim().toLowerCase());
        const empCodeCol = headerRow.findIndex(h => h.includes("id") || h.includes("code"));
        const nameCol = headerRow.findIndex(h => h.includes("name"));
        const absCol = headerRow.findIndex(h => h.includes("absent"));
        const otCol = headerRow.findIndex(h => h.includes("ot") || h.includes("overtime"));

        if (nameCol < 0 && empCodeCol < 0) {
          alert("Could not find Employee ID or Name column in Excel.");
          return;
        }

        const updates = {...editAttRow};
        for (let i = hIdx + 1; i < rawRows.length; i++) {
          const r = rawRows[i];
          const code = empCodeCol >= 0 ? String(r[empCodeCol]||"").trim() : "";
          const name = nameCol >= 0 ? String(r[nameCol]||"").trim() : "";
          const abs = absCol >= 0 ? parseFloat(r[absCol]) : 0;
          const ot = otCol >= 0 ? parseFloat(r[otCol]) : 0;
          
          if (!code && !name) continue;

          const emp = companyEmps.find(e => (e.employee_code === code || e.id === code) && e.name.toLowerCase() === name.toLowerCase());
          if(!emp) {
            alert(`Error at row ${i+1}: Could not find employee "${name}" with ID "${code}". Upload aborted.`);
            return;
          }
          
          updates[emp.id] = {
            ...updates[emp.id],
            absent_days: isNaN(abs) ? 0 : abs,
            ot_hours: isNaN(ot) ? 0 : ot
          };
        }
        setEditAttRow(updates);
        alert("✅ Attendance uploaded successfully! Please review the numbers on screen and click 'Save & Finalise' to save.");
      } catch(err) {
        alert("Failed to parse attendance Excel: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Financial Year helpers
  const availableFYs = useMemo(() => {
    let oldest = new Date();
    const currentM = oldest.getMonth() + 1;
    const currentY = oldest.getFullYear();
    let oldestStartYear = currentM <= 3 ? currentY - 1 : currentY;

    if (salaries && activeCompany) {
      Object.values(salaries).forEach(empSals => {
        (empSals||[]).forEach(s => {
          const sy = s.month <= 3 ? s.year - 1 : s.year;
          if (sy < oldestStartYear) oldestStartYear = sy;
        });
      });
    }

    const currentStartYear = currentM <= 3 ? currentY - 1 : currentY;
    const result = [];
    for (let y = currentStartYear; y >= oldestStartYear; y--) {
      result.push(`${y}-${y + 1}`);
    }
    return result;
  }, [salaries, activeCompany]);

  useEffect(() => {
    if (activeCompany && availableFYs.length > 0) {
      if (!selectedFY || !availableFYs.includes(selectedFY)) {
        setSelectedFY(availableFYs[0]);
      }
    }
  }, [activeCompany, availableFYs, selectedFY]);

  const availableMonths = useMemo(() => {
    if (!selectedFY) return [];
    const startYear = parseInt(selectedFY.split("-")[0]);
    const result = [];
    const now = new Date();
    const currentMonthIndex = now.getFullYear() * 12 + now.getMonth();

    const pushIfValid = (m, y) => {
      if (y * 12 + (m - 1) <= currentMonthIndex) {
        result.push({ month: m, year: y });
      }
    };

    for (let m = 4; m <= 12; m++) {
      pushIfValid(m, startYear);
    }
    for (let m = 1; m <= 3; m++) {
      pushIfValid(m, startYear + 1);
    }
    return result.reverse(); 
  }, [selectedFY]);

  // breadcrumb
  const crumbs = activeCompany ? (
    <div className="breadcrumb">
      {!isCompanyRole && (
        <>
          <span className="crumb" onClick={()=>safeNavigate(()=>{setView("companies");setActiveCompany(null);setActiveEmployee(null);})}>Companies</span>
          <span className="sep">›</span>
        </>
      )}
      <span className={view==="employees"||view==="payroll"||view==="dashboard"||view==="company_details"||view==="approvals"||view==="org"||view==="recruitment"||view==="offboarding"?"active":"crumb"} onClick={()=>{if(!isCompanyRole && (view==="employee_detail"||view==="payroll"||view==="dashboard"||view==="approvals"||view==="org"||view==="recruitment"||view==="offboarding")){setView("employees");setActiveEmployee(null);}}}>
        {activeCompany.name}
      </span>
      {view==="payroll"&&<><span className="sep">›</span><span className="active">Payroll</span></>}
      {view==="dashboard"&&<><span className="sep">›</span><span className="active">Dashboard</span></>}
      {view==="approvals"&&<><span className="sep">›</span><span className="active">Approvals</span></>}
      {view==="org"&&<><span className="sep">›</span><span className="active">Org & Culture</span></>}
      {view==="recruitment"&&<><span className="sep">›</span><span className="active">Recruitment</span></>}
      {view==="offboarding"&&<><span className="sep">›</span><span className="active">Off-Boarding</span></>}
      {view==="employee_detail"&&activeEmployee&&<><span className="sep">›</span><span className="active">{activeEmployee.name}</span></>}
    </div>
  ) : null;

  const headerBtn = null;

  if (workspaceError) return <><style>{css}</style><main className="login-page"><section className="login-card" role="alert"><h1 style={{fontSize:22}}>Workspace unavailable</h1><p style={{fontSize:14,lineHeight:1.7}}>{workspaceError}</p><button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button></section></main></>;

  if (workspace && (passwordSetup.active || (authReady && currentUser?.role==='employee' && currentUser.passwordChangeRequired))) return <><style>{css}</style><PasswordSetup tokenHash={passwordSetup.tokenHash} workspace={workspace} onDone={message=>{
    const url=new URL(window.location.href);url.searchParams.delete('password_setup');url.hash='';window.history.replaceState(null,'',url.pathname+url.search);
    setPasswordSetup({active:false,tokenHash:null});setCurrentUser(null);setLoginNotice(message);setAuthReady(true);
  }}/></>;

  if (authReady && currentUser?.passwordChangeRequired) return <><style>{css}</style><AdminPasswordChange workspace={workspace} user={currentUser} onSuccess={handleLogin} onLogout={handleLogout}/></>;

  if (!authReady || loading || isResolvingCompany) {
    return (
      <AttractiveWorkspaceLoader
        title="TrustWork Payroll Software"
        subtitle="Loading your workspace..."
      />
    );
  }

  if (!currentUser || (isPwaMode() && currentUser.role !== "employee")) {
    return (
      <>
        <style>{css}</style>
        <LoginPage onLogin={handleLogin} companies={companies} onInstallApp={handleInstallApp} workspace={workspace} notice={loginNotice}/>
        <PwaInstallGuideModal isOpen={showPwaGuide} onClose={() => setShowPwaGuide(false)} appName={workspace?.kind === 'company' ? workspace.companyName : 'TrustWork'}/>
      </>
    );
  }

  // ── Employee Portal
  if (currentUser.role === "employee") {
    return (
      <>
        <style>{css}</style>
        <EmployeePortal session={currentUser} onLogout={handleLogout} onInstallApp={handleInstallApp} onSessionChange={updated => { setCurrentUser(updated); localStorage.setItem("tw_current_user", JSON.stringify(updated)); if (!updated.forcePinChange) syncFileSession(updated).catch(() => {}); }} />
        <PwaInstallGuideModal isOpen={showPwaGuide} onClose={() => setShowPwaGuide(false)} appName={workspace?.kind === 'company' ? workspace.companyName : 'TrustWork'}/>
      </>
    );
  }

  const wrapCompanyLayout = (content) => {
    if (!isCompanyRole) return <div className="main">{content}</div>;
    const cfg = activeCompany.config || DEFAULT_CONFIG;
    return (
      <div className="company-layout-wrapper company-admin-layout" style={{gridTemplateColumns: `${sidebarOpen ? 280 : 60}px minmax(0, 1fr)`}}>
          <div className="company-sidebar-header" style={{ opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.2s" }}>
            <div className="company-sidebar-title" style={{display:"flex", alignItems:"center", gap:6}}>
              {cfg.logo_base64 ? <img src={cfg.logo_base64} alt="Logo" style={{width: 20, height: 20, borderRadius: 4, objectFit:"contain"}} /> : "🏢"} {activeCompany.name}
            </div>
          </div>
          <div className="company-header-bar">
            <div style={{display:"flex", alignItems:"center", gap: 12, paddingRight: 16, borderRight: "1px solid #e2e8f0"}}>
              <div style={{background:"#f8fafc", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700, color: "#475569", border: "1px solid #cbd5e1", display: "flex", alignItems: "center", gap: 6}}>
                <span style={{color: "#334155"}}>👤 {activeCompany?.login_username || currentUser?.login_username || "Admin"}</span>
                <span style={{color: "#94a3b8"}}>•</span>
                <span style={{color: "#4f46e5"}}>{cfg.plan_tier || "Free"} Plan</span>
              </div>
            </div>
            <div style={{flex: 1, display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", paddingLeft: 8}}>
              <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>Basic {cfg.custom_basic_enabled ? "Custom" : `${cfg.basic_pct !== undefined ? cfg.basic_pct : 50}% of Gross`}</span>
              <span className="chip chip-blue" style={{background:"#f1f5f9", borderColor:"#cbd5e1", color:"#334155", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>HRA {cfg.hra_customised ? "Custom" : (cfg.hra_type==="fixed"?"₹"+cfg.hra_value:(cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40))+"%")}</span>
              <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>Min Wage {(cfg.min_wage_enabled && cfg.min_wage) ? "₹" + cfg.min_wage : "None"}</span>
              {cfg.pf_applicable ? <span className="chip chip-green" style={{background:"#f0fdf4", borderColor:"#bbf7d0", color:"#166534", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>{cfg.pf_wage_cap!==false?"PF with cap":"PF"}</span> : <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>No PF</span>}
              {cfg.esi_applicable ? <span className="chip chip-green" style={{background:"#f0fdf4", borderColor:"#bbf7d0", color:"#166534", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>ESI ✓</span> : <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>No ESI</span>}
              <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>LWF {cfg.lwf_applicable ? "✓" : "No"}</span>
              {cfg.bonus ? <span className="chip chip-purple" style={{background:"#faf5ff", borderColor:"#e9d5ff", color:"#6b21a8", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>Bonus ✓</span> : <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>No Bonus</span>}
              {cfg.gratuity !== false ? <span className="chip chip-purple" style={{background:"#faf5ff", borderColor:"#e9d5ff", color:"#6b21a8", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>Gratuity ✓</span> : <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>No Gratuity</span>}
              {(cfg.absence_basis && cfg.absence_basis !== "no_deduct" && cfg.absence_deduction) ? <span className="chip chip-blue" style={{background:"#f1f5f9", borderColor:"#cbd5e1", color:"#334155", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>Abs. from {cfg.absence_basis==="gross"?"Gross":"Basic"} / {cfg.working_days === -1 ? "Calendar Days" : cfg.working_days + "d"}</span> : <span className="chip chip-gray" style={{background:"#f8fafc", borderColor:"#e2e8f0", color:"#475569", transition:"transform 0.2s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform="none"}>No Absence Deduct</span>}
            </div>
            {/* Header Right Actions */}
            <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
               <div
                 onClick={() => setCommandPaletteOpen(true)}
                 style={{
                   display: "flex",
                   alignItems: "center",
                   gap: 7,
                   background: "#f8fafc",
                   border: "1.5px solid #cbd5e1",
                   borderRadius: 20,
                   padding: "4px 12px",
                   cursor: "pointer",
                   transition: "all 0.2s ease",
                   userSelect: "none"
                 }}
                 onMouseEnter={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#fff"; }}
                 onMouseLeave={e => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = "#f8fafc"; }}
                 title="Quick Command & Tool Search (Ctrl + K)"
               >
                 <span style={{ fontSize: 12, color: "#64748b" }}>🔍</span>
                 <span style={{ fontSize: 12, color: "#334155", fontWeight: 700 }}>Quick Search</span>
                 <span style={{ fontSize: 10, fontWeight: 800, color: "#475569", background: "#e2e8f0", padding: "1px 5px", borderRadius: 4, fontFamily: "monospace" }}>Ctrl K</span>
               </div>
               {(pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews) > 0 && (
                 <button onClick={() => { setCompanyNav("approvals"); setView("approvals"); }} style={{background: "#fef2f2", border: "1.5px solid #fca5a5", color: "#dc2626", padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s", boxShadow: "0 2px 6px rgba(220,38,38,0.2)"}} title="Review approvals">
                   <span style={{fontSize: 14}}>🔔</span>
                   <span>Approvals</span>
                   <span style={{background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 900, padding: "2px 7px", borderRadius: 12, lineHeight: 1}}>
                     {pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews}
                   </span>
                 </button>
               )}
               <button onClick={() => setShowTour(true)} style={{background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.background="#dcfce7";e.currentTarget.style.transform="scale(1.02)"}} onMouseLeave={e=>{e.currentTarget.style.background="#f0fdf4";e.currentTarget.style.transform="none"}}>🧭 How to use</button>
               {headerBtn}
            </div>
          </div>

        <div className="company-sidebar admin-sidebar" style={{ width: sidebarOpen ? 280 : 60, transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)", overflow: "hidden", borderRight: sidebarOpen ? "1px solid #e2e8f0" : "none" }}>
          <div className="company-nav" style={{ opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.2s", pointerEvents: sidebarOpen ? "auto" : "none" }}>
            <CompanyNavSection id="settings" label="Settings" icon="⚙️" expanded={expandedSidebarGroup === "settings"} active={sidebarGroupFor(companyNav) === "settings"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "settings" ? null : "settings")}>

            <button className={`company-nav-item admin-nav-item ${companyNav==="profile"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("profile"); setView("company_details");}, "Profile")}>
              <span className="company-nav-icon">🏢</span> Company Profile
            </button>
            <button className={`company-nav-item admin-nav-item ${companyNav==="salary_structure"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("salary_structure"); setView("salary_structure");}, "Salary Structure")}>
              <span className="company-nav-icon">⚙️</span> Salary Structure
            </button>
            </CompanyNavSection>
            <CompanyNavSection id="employees" label="Employees" icon="👥" expanded={expandedSidebarGroup === "employees"} active={sidebarGroupFor(companyNav) === "employees"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "employees" ? null : "employees")}>

            <button className={`company-nav-item admin-nav-item ${companyNav==="employees"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("employees"); setView("employees");}, "Employees")}>
              <span className="company-nav-icon">👥</span> Employee Directory
            </button>
            <button className={`company-nav-item admin-nav-item ${companyNav==="joining_kit"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("joining_kit"); setView("joining_kit");}, "Joining Kit")}>
              <span className="company-nav-icon">🪪</span> Onboarding
            </button>
            </CompanyNavSection>
            {(activeCompany?.config?.attendance_enabled === true || activeCompany?.config?.leave_module_enabled === true) && (
            <CompanyNavSection id="attendance" label="Attendance & Leave" icon="⏱️" expanded={expandedSidebarGroup === "attendance"} active={sidebarGroupFor(companyNav) === "attendance"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "attendance" ? null : "attendance")}>

            {(activeCompany?.config?.attendance_enabled === true) && (
              <button className={`company-nav-item admin-nav-item ${companyNav==="attendance"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("attendance"); setView("attendance");}, "Attendance")} style={{display: "flex", alignItems: "center"}}>
                <span className="company-nav-icon">⏱️</span> Attendance
              </button>
            )}
            {activeCompany?.config?.leave_module_enabled === true && <button className={`company-nav-item admin-nav-item ${companyNav === "leave_management" ? "active" : ""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("leave_management");setApprovalsTab("leave_attendance");setView("approvals");}, "Leave Management")}><span className="company-nav-icon">🏖️</span> Leave Management</button>}
            </CompanyNavSection>
            )}
            <CompanyNavSection id="payroll" label="Payroll" icon="💰" expanded={expandedSidebarGroup === "payroll"} active={sidebarGroupFor(companyNav) === "payroll"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "payroll" ? null : "payroll")}>

            <button className={`company-nav-item admin-nav-item ${companyNav==="payroll"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("payroll"); openPayroll(activeCompany);}, "Run Payroll")}>
              <span className="company-nav-icon">💰</span> Run Payroll
            </button>
            {activeCompany?.config?.addon_bulk_hike && <button className="company-nav-item admin-nav-item" onClick={()=>safeNavigate(()=>{setFormMsg("");setModal("bulk_hike");}, "Salary Revisions")}><span className="company-nav-icon">📈</span> Salary Revisions</button>}
            </CompanyNavSection>
            <button className={`company-nav-item admin-nav-item ${companyNav==="dashboard"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("dashboard"); setView("dashboard");}, "Dashboard")}>
              <span className="company-nav-icon">📊</span> Dashboard
            </button>
            <button className={`company-nav-item admin-nav-item ${companyNav==="approvals"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("approvals"); setView("approvals");}, "Approvals")} style={{display: "flex", alignItems: "center"}}>
              <span className="company-nav-icon">⏳</span> Approvals
              {(pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews) > 0 && (
                <span style={{marginLeft: "auto", background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12, lineHeight: 1}}>{pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews}</span>
              )}
            </button>
            {activeCompany?.config?.reports_enabled !== false && (
            <CompanyNavSection id="reports" label="Reports" icon="📑" expanded={expandedSidebarGroup === "reports"} active={sidebarGroupFor(companyNav) === "reports"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "reports" ? null : "reports")}>

                      {activeCompany?.config?.reports_enabled !== false && (
                        <button
                          className={`company-nav-item admin-nav-item ${companyNav === "reports" ? "active" : ""}`}
                          onClick={() => safeNavigate(() => { setCompanyNav("reports"); setView("reports"); }, "Reports")}
                        >
                          <span className="company-nav-icon">📑</span>
                          <span>Reports</span>
                        </button>
                      )}
            </CompanyNavSection>
            )}
            {(() => {
              const items = [
                {nav:"org",label:"Org & Culture",icon:"🌳",enabled:activeCompany?.config?.org_culture_enabled === true},
                {nav:"recruitment",label:"Recruitment",icon:"🎯",enabled:activeCompany?.config?.recruitment_enabled === true},
                {nav:"offboarding",label:"Off-Boarding",icon:"🚪",enabled:activeCompany?.config?.offboarding_enabled === true},
              ].filter(item=>item.enabled);
              return items.length > 0 && <CompanyNavSection id="people" label="People" icon="👥" expanded={expandedSidebarGroup === "people"} active={sidebarGroupFor(companyNav) === "people"} onToggle={()=>setExpandedSidebarGroup(previous=>previous === "people" ? null : "people")}>
                {items.map(item=><button key={item.nav} className={`company-nav-item admin-nav-item ${companyNav === item.nav ? "active" : ""}`} onClick={()=>safeNavigate(()=>{setCompanyNav(item.nav);setView(item.nav);}, item.label)}><span className="company-nav-icon">{item.icon}</span>{item.label}</button>)}
              </CompanyNavSection>;
            })()}
            {activeCompany?.config?.training_enabled === true && <button className={`company-nav-item admin-nav-item ${companyNav==='training'?'active':''}`} onClick={()=>safeNavigate(()=>{setCompanyNav('training');setView('training');},'Training Hub')}><span className="company-nav-icon">🎓</span>Training Hub</button>}
            {activeCompany?.config?.doc_hub_enabled === true && <button className={`company-nav-item admin-nav-item ${companyNav==='doc_hub'?'active':''}`} onClick={()=>safeNavigate(()=>{setCompanyNav('doc_hub');setView('doc_hub');},'Doc Hub')}><span className="company-nav-icon">📁</span>Doc Hub</button>}
            <button className={`company-nav-item admin-nav-item ${companyNav==="security"?"active":""}`} onClick={()=>safeNavigate(()=>{setCompanyNav("security"); setView("security");}, "Security")}>
              <span className="company-nav-icon">🛡️</span> Security Center
            </button>
          </div>
          <div ref={profileMenuRef} style={{marginTop:"auto", padding:"16px 20px", borderTop:"1px solid #e2e8f0", display:"flex", flexDirection:"column", position: "relative"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding: "10px 14px", borderRadius: 12, transition: "all 0.2s ease", cursor: "pointer", background: profileMenuOpen ? "#f1f5f9" : "transparent"}}
                 onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                 onMouseEnter={e=>e.currentTarget.style.background="#f1f5f9"}
                 onMouseLeave={e=>e.currentTarget.style.background=profileMenuOpen ? "#f1f5f9" : "transparent"}>
              <div style={{display: "flex", alignItems: "center", gap: 10}}>
                <div style={{display: "flex", flexDirection: "column"}}>
                  <span style={{fontSize: 14, color: "#1e293b", fontWeight: 800, lineHeight: 1.2}}>{activeCompany?.login_username || currentUser?.login_username || "Admin"}</span>
                  <span style={{fontSize: 11, color: "#64748b", fontWeight: 600}}>System Admin</span>
                </div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{transition: "transform 0.2s", transform: profileMenuOpen ? "rotate(90deg)" : "none"}}>
                <line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </div>
            
            {profileMenuOpen && (
              <div style={{
                position: "absolute", bottom: "100%", left: 16, right: 16, marginBottom: 8, 
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, 
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)", padding: 6, zIndex: 100,
                display: "flex", flexDirection: "column", gap: 2
              }}>
                <button style={{color:"#4f46e5", fontSize: 13, fontWeight: 700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"flex-start", gap: 10, padding:"8px 12px", background:"transparent", border:"none", borderRadius:8, transition:"all 0.2s ease", width: "100%", textAlign: "left"}} onClick={() => {setModal("change_password"); setProfileMenuOpen(false);}}
                   onMouseEnter={e=>e.currentTarget.style.background="#e0e7ff"}
                   onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:14}}>🔑</span> Change Password
                </button>
                {currentUser?.role === 'company' && (
                  <button style={{color:"#0ea5e9", fontSize: 13, fontWeight: 700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"flex-start", gap: 10, padding:"8px 12px", background:"transparent", border:"none", borderRadius:8, transition:"all 0.2s ease", width: "100%", textAlign: "left"}} onClick={() => {setModal("change_login_id"); setProfileMenuOpen(false);}}
                     onMouseEnter={e=>e.currentTarget.style.background="#e0f2fe"}
                     onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{fontSize:14}}>👤</span> Change Login ID
                  </button>
                )}
                <div style={{height: 1, background: "#f1f5f9", margin: "2px 0"}} />
                <button style={{color:"#ef4444", fontSize: 13, fontWeight: 700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"flex-start", gap: 10, padding:"8px 12px", background:"transparent", border:"none", borderRadius:8, transition:"all 0.2s ease", width: "100%", textAlign: "left"}} onClick={handleLogout}
                   onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"}
                   onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:14}}>🚪</span> Log Out
                </button>
              </div>
            )}
          </div>
        </div>
        <button 
          className="sidebar-toggle-float"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: "absolute", top: "50%", left: (sidebarOpen ? 280 : 60) - 14, transform: "translateY(-50%)",
            width: 28, height: 28, borderRadius: "50%", background: "#fff", border: "1px solid #e2e8f0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 10, color: "#64748b", fontSize: 16, fontWeight: 800, transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#2563eb"; e.currentTarget.style.borderColor = "#bfdbfe"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.15)"; e.currentTarget.style.transform = "translateY(-50%) scale(1.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
        >
          {sidebarOpen ? '«' : '»'}
        </button>
        <div className="company-main-content">
          <div className="company-main-scroll">
            {view !== "approvals" && (pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews) > 0 && (
              <div style={{background: "linear-gradient(135deg, #fffbeb, #fef3c7)", border: "1.5px solid #fde68a", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, boxShadow: "0 2px 8px rgba(217,119,6,0.12)"}}>
                <div style={{display: "flex", alignItems: "center", gap: 12}}>
                  <div style={{width: 36, height: 36, borderRadius: "50%", background: "#fef08a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, border: "1px solid #facc15", flexShrink: 0}}>
                    🔔
                  </div>
                  <div>
                    <div style={{fontSize: 14, fontWeight: 800, color: "#92400e"}}>
                      Action Required: {pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews} Pending Request{(pendingApprovals.length + pendingLeaves.length + pendingRegularizations.length + pendingPayrollReviews) > 1 ? "s" : ""}
                    </div>
                    <div style={{fontSize: 12.5, color: "#b45309", marginTop: 2}}>
                      {[
                        pendingLeaves.length > 0 ? `${pendingLeaves.length} leave application${pendingLeaves.length > 1 ? "s" : ""}` : null,
                        pendingRegularizations.length > 0 ? `${pendingRegularizations.length} attendance regularization${pendingRegularizations.length > 1 ? "s" : ""}` : null,
                        pendingApprovals.length > 0 ? `${pendingApprovals.length} profile update${pendingApprovals.length > 1 ? "s" : ""}` : null
                      ].filter(Boolean).join(", ")} awaiting review.
                    </div>
                  </div>
                </div>
                <button onClick={() => { setCompanyNav("approvals"); setView("approvals"); }} style={{background: "#d97706", color: "#fff", border: "none", fontWeight: 800, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 4px rgba(217,119,6,0.2)", whiteSpace: "nowrap"}}>
                  Review Requests →
                </button>
              </div>
            )}
            {content}
          </div>
        </div>
      </div>
    );
  };

  return (<>
    <style>{css}</style>
    <div className="app">

      {!isCompanyRole && (
        <div className="header" style={{background:"#ffffff",borderBottom:"1.5px solid #cbd5e1",padding:"0 24px",height:64,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{display:"flex",alignItems:"center",gap:10,cursor:!isCompanyRole?"pointer":"default"}} onClick={()=>{if(!isCompanyRole){safeNavigate(()=>{setView("companies");setActiveCompany(null);setActiveEmployee(null);})}}}>
              <img src={LOGO} alt="TrustWork" style={{height:44,borderRadius:10,background:"#fff",padding:"2px 6px",border:"1px solid #cbd5e1"}} />
              <div style={{display:"flex",flexDirection:"column",gap:1}}>
                <span style={{fontFamily:"'Inter',sans-serif",fontWeight:900,fontSize:19,background:"linear-gradient(135deg,#2563eb,#4f46e5)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",lineHeight:1.1}}>TrustWork</span>
                <span style={{fontSize:9,fontWeight:700,color:"#64748b",letterSpacing:"1px",textTransform:"uppercase",lineHeight:1}}>Work Simplified</span>
              </div>
            </div>
            {activeCompany && (
              <div style={{display:"flex",alignItems:"center",gap:10,paddingLeft:14,borderLeft:"2px solid #cbd5e1"}}>
                <span style={{fontSize:22,fontWeight:900,color:"#0f172a",letterSpacing:"-0.5px",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}>
                  {activeCompany.config?.logo_base64 ? <img src={activeCompany.config.logo_base64} alt="Logo" style={{width: 28, height: 28, borderRadius: 6, objectFit:"contain"}} /> : "🏢"} {activeCompany.name}
                </span>
                {view==="payroll" && (
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,color:"#64748b",fontWeight:700}}>
                    <span style={{color:"#94a3b8"}}>›</span>
                    <span style={{color:"#2563eb"}}>Payroll</span>
                  </div>
                )}
                {view==="approvals" && (
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,color:"#64748b",fontWeight:700}}>
                    <span style={{color:"#94a3b8"}}>›</span>
                    <span style={{color:"#2563eb"}}>Approvals</span>
                  </div>
                )}
                {view==="employee_detail" && activeEmployee && (
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,color:"#64748b",fontWeight:700}}>
                    <span style={{color:"#94a3b8"}}>›</span>
                    <span style={{color:"#0f172a"}}>{activeEmployee.name}</span>
                  </div>
                )}
              </div>
            )}
            {!activeCompany && !isCompanyRole && (
              <div style={{display:"flex",alignItems:"center",gap:10,paddingLeft:14,borderLeft:"2px solid #cbd5e1"}}>
                <span style={{fontSize:18,fontWeight:800,color:"#0f172a"}}>Company Directory</span>
              </div>
            )}
          </div>
          {/* Universal Command Palette Trigger (Ctrl + K) */}
          <div
            onClick={() => setCommandPaletteOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              background: "#f8fafc",
              border: "1.5px solid #cbd5e1",
              borderRadius: 24,
              padding: "7px 16px",
              cursor: "pointer",
              width: 340,
              maxWidth: "100%",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              userSelect: "none"
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(59,130,246,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.03)"; }}
            title="Search features, tools, employees, and statutory formulas (Ctrl + K)"
          >
            <span style={{ fontSize: 13, color: "#64748b" }}>🔍</span>
            <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 500, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Search tools, POSH, Gratuity...
            </span>
            <span style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: "#475569",
              background: "#e2e8f0",
              border: "1px solid #cbd5e1",
              padding: "2px 6px",
              borderRadius: 6,
              fontFamily: "'SF Mono', Consolas, monospace",
              letterSpacing: "0.5px"
            }}>
              Ctrl K
            </span>
          </div>
          <div className="header-right" style={{display:"flex",alignItems:"center",gap:14, marginTop:"-4px"}}>
            <button
              onClick={() => setShowTour(true)}
              style={{background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="#dcfce7";e.currentTarget.style.transform="scale(1.02)"}}
              onMouseLeave={e=>{e.currentTarget.style.background="#f0fdf4";e.currentTarget.style.transform="none"}}
            >
              🧭 How to use
            </button>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
              <a href="mailto:payroll@trustwork.co.in" style={{fontSize:12.5,color:"#2563eb",fontWeight:700,textDecoration:"none"}}>📧 payroll@trustwork.co.in</a>
              <a href="tel:+919353010107" style={{fontSize:12.5,color:"#2563eb",fontWeight:600,textDecoration:"none"}}>📞 +91 93530 10107</a>
            </div>
            {headerBtn}
            {currentUser && (
              <div ref={userMenuRef} style={{position:"relative"}}>
                <button
                  onClick={()=>setShowUserMenu(p=>!p)}
                  style={{padding:"8px 12px",borderRadius:8,background:"#f1f5f9",border:"1px solid #cbd5e1",cursor:"pointer",fontSize:12,fontWeight:700,color:"#334155"}}
                  title={currentUser.username||"Account"}
                >
                  Account
                </button>
                {showUserMenu && (
                  <div style={{position:"absolute",right:0,top:"calc(100% + 8px)",background:"#141824",border:"1.5px solid #1e2130",borderRadius:12,padding:"6px",minWidth:180,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",zIndex:999}}>
                    <div style={{padding:"8px 12px 10px",borderBottom:"1px solid #1e2130",marginBottom:4,fontSize:11,color:"#6b7a9e",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8}}>
                      {currentUser.username||"Account"}
                    </div>
                    <button
                      onClick={()=>{ setShowUserMenu(false); setFormMsg(""); setModal("change_password"); }}
                      style={{width:"100%",background:"none",border:"none",borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:600,color:"#e8eaf0",cursor:"pointer",display:"flex",alignItems:"center",gap:9,textAlign:"left"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#1a2035"}
                      onMouseLeave={e=>e.currentTarget.style.background="none"}
                    >
                      🔑 Change Password
                    </button>
                    <button
                      onClick={()=>{ setShowUserMenu(false); handleLogout(); }}
                      style={{width:"100%",background:"none",border:"none",borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:600,color:"#f76f6f",cursor:"pointer",display:"flex",alignItems:"center",gap:9,textAlign:"left"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#2a1a1a"}
                      onMouseLeave={e=>e.currentTarget.style.background="none"}
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {adminToast && (
        <div style={{
          position: "fixed",
          top: 24,
          right: 24,
          background: "#065f46",
          color: "#fff",
          padding: "14px 22px",
          borderRadius: 10,
          zIndex: 99999,
          fontWeight: 700,
          fontSize: 14,
          boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>
          <span>✅</span>
          <span>{adminToast}</span>
        </div>
      )}

      {wrapCompanyLayout(
        <>
          {dbError && (
            <div style={{background:"#fef2f2",border:"1.5px solid #fca5a5",color:"#991b1b",padding:"12px 16px",borderRadius:8,marginBottom:16,fontSize:13,fontWeight:500}}>
              ⚠️ {dbError}
            </div>
          )}

      {/* ── VIEW: COMPANIES */}
      {view==="companies" && (
        <>
          <div className="page-header">
            <div><div className="page-title">Companies</div><div className="page-sub">Manage companies, salary structure & run monthly payroll</div></div>
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:10}}>
            <div className="stats-bar" style={{marginBottom:0}}>
              <div className="stat-chip"><div className="stat-num">{companies.length}</div><div className="stat-label">Companies</div></div>
              <div className="stat-chip"><div className="stat-num">{Object.values(employees).flat().length}</div><div className="stat-label">Employees</div></div>
              {(allPendingApprovals.length + allPendingLeaves.length) > 0 && (
                <div className="stat-chip" style={{borderColor:"#fca5a5",background:"#fef2f2"}}>
                  <div className="stat-num" style={{color:"#dc2626"}}>{allPendingApprovals.length + allPendingLeaves.length}</div>
                  <div className="stat-label" style={{color:"#b91c1c"}}>Approvals</div>
                </div>
              )}
              <div className="stat-chip" onClick={() => setView("security")} style={{cursor:"pointer", border:"1px solid #bfdbfe", background:"#f0f7ff"}} title="Open Security & Threat Center">
                <div className="stat-num" style={{color:"#1d4ed8", fontSize:18, display:"flex", alignItems:"center", gap:6}}>
                  <span>🛡️</span> Security
                </div>
                <div className="stat-label" style={{color:"#1e40af"}}>Threat Center</div>
              </div>
              <div className="stat-chip" onClick={() => { setView("audit_logs"); setCompanyNav("audit_logs"); }} style={{cursor:"pointer", border:"1px solid #cbd5e1", background:"#f8fafc"}} title="Open Complete System Activity & Audit Trail">
                <div className="stat-num" style={{color:"#0f172a", fontSize:18, display:"flex", alignItems:"center", gap:6}}>
                  <span>📜</span> Activity Log
                </div>
                <div className="stat-label" style={{color:"#475569"}}>Audit Trail</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {/* Export All companies */}
              {companies.length>0&&<>
                <button className="btn btn-ghost btn-sm" onClick={()=>exportAllCompaniesExcel(companies)} title="Export all companies to Excel">⬇ Export</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>exportAllCompaniesPDF(companies,employees)} title="Export all companies to PDF">📄 PDF</button>
                <div style={{width:1,height:20,background:"#1e2130"}}/>
              </>}
              {/* Templates dropdown area */}
              <div style={{position:"relative",display:"inline-block"}} className="tmpl-wrap">
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowTmplMenu(p=>!p)} title="Download blank templates">⬇ Template ▾</button>
                {showTmplMenu&&(
                  <div style={{position:"absolute",top:"100%",right:0,marginTop:4,background:"#0f1117",border:"1.5px solid #1e2a45",borderRadius:12,minWidth:270,zIndex:200,boxShadow:"0 12px 40px #000a",overflow:"hidden"}}
                    onMouseLeave={()=>setShowTmplMenu(false)}>
                    {/* Dropdown header with logo */}
                    <div style={{background:"linear-gradient(135deg,#1a3258,#0f1117)",padding:"10px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:"1px solid #1e2a45"}}>
                      <img src={LOGO} alt="TrustWork" style={{height:28,borderRadius:6,background:"#fff",padding:"1px 5px"}} />
                      <div>
                        <div style={{fontSize:11,fontWeight:800,color:"#4f8ef7",letterSpacing:.5}}>TrustWork Templates</div>
                        <div style={{fontSize:9,color:"#6b7a9e",marginTop:1}}>Pre-formatted Excel sheets</div>
                      </div>
                    </div>
                    {/* Single template row */}
                    <div style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:11,borderTop:"1px solid #1e2a45"}}
                      onClick={()=>{downloadCompanyTemplate();setShowTmplMenu(false);}}
                      onMouseEnter={e=>e.currentTarget.style.background="#161d2e"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#1a3258,#4f8ef7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>🏢</div>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:"#e8eaf0"}}>Single Company</div>
                        <div style={{fontSize:10,color:"#6b7a9e",marginTop:1}}>Add or update one company</div>
                      </div>
                      <div style={{marginLeft:"auto",fontSize:10,color:"#4f8ef7",fontWeight:700}}>XLSX ↓</div>
                    </div>
                    {/* Bulk template row */}
                    <div style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:11,borderTop:"1px solid #1e2a45"}}
                      onClick={()=>{downloadBulkCompanyTemplate();setShowTmplMenu(false);}}
                      onMouseEnter={e=>e.currentTarget.style.background="#161d2e"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#1e3a20,#3cba54)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>📊</div>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:"#e8eaf0"}}>Bulk Companies</div>
                        <div style={{fontSize:10,color:"#6b7a9e",marginTop:1}}>Upload many companies at once</div>
                      </div>
                      <div style={{marginLeft:"auto",fontSize:10,color:"#4f8ef7",fontWeight:700}}>XLSX ↓</div>
                    </div>
                  </div>
                )}
              </div>
              {/* Upload (single or bulk) */}
              <button className="btn btn-ghost btn-sm" onClick={()=>companyUploadRef.current?.click()} title="Upload one or many companies from Excel">⬆ Upload</button>
              <input ref={companyUploadRef} type="file" accept=".xlsx" style={{display:"none"}}
                onChange={e=>{handleCompanyExcelUpload(e.target.files[0]);e.target.value="";}} />
              <div style={{width:1,height:20,background:"#1e2130"}}/>
              <button className="btn btn-primary btn-sm" onClick={()=>{setEditTarget(null);setFormMsg("");setModal("addCompany");}}>＋ Add Company</button>
              <button className="btn btn-ghost btn-sm" style={{border:"1px solid #4f8ef7", color:"#4f8ef7", marginLeft: 8}} onClick={()=>setModal("testRunner")}>🧪 System Tests</button>
            </div>
          </div>
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input className="search-input" placeholder="Search company…" value={companySearch} onChange={e=>setCompanySearch(e.target.value)} />
          </div>
          <div className="company-grid">
            {filteredCompanies.map(c=>{
              const cfg=c.config||DEFAULT_CONFIG;
              const empCount=(employees[c.id]||[]).length;
              const pendingLeavesCount = allPendingLeaves.filter(l => l.company_id === c.id).length;
              const pendingApprovalsCount = allPendingApprovals.filter(a => a.company_id === c.id).length;
              const pendingRegsCount = allPendingRegularizations.filter(r => r.company_id === c.id).length;
              const companyPendingTotal = pendingLeavesCount + pendingApprovalsCount + pendingRegsCount;
              return(
              <div className="company-card" key={c.id} onClick={()=>openCompany(c)}>
                <div className="card-top">
                  <div className="card-icon" title="View company details" onClick={e=>{e.stopPropagation();setEditTarget(c);setModal("viewCompany");}}>🏢</div>
                    <div style={{display:"flex",alignItems:"center",gap:6}} onClick={e=>e.stopPropagation()}>
                      {companyPendingTotal > 0 && (
                        <div style={{background:"#dc2626",borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:800,color:"#fff",boxShadow:"0 2px 6px rgba(220,38,38,0.35)",display:"flex",alignItems:"center",gap:4}} title={`${companyPendingTotal} pending request(s) (${pendingLeavesCount} leave, ${pendingRegsCount} attendance reg, ${pendingApprovalsCount} profile)`}>
                          ⏳ {companyPendingTotal} Pending
                        </div>
                      )}
                      <div style={{background:"#1a2540",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,color:"#10b981"}}>{cfg.plan_tier || "Free"}</div>
                      <div style={{background:"#1a2540",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,color:"#4f8ef7"}}>Employees: {empCount}</div>
                      {!c.is_active && <div style={{background:"#451a1a",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,color:"#f87171"}}>Inactive</div>}
                      <button className="icon-btn edit" onClick={()=>{setActiveCompany(c);setView("company_details");}} title="Edit Company Details">✏️</button>
                      {c.is_active ? (
                        <button className="icon-btn del" style={{background:"#f59e0b22", color:"#f59e0b", border:"1px solid #f59e0b33"}} onClick={()=>{setDeleteTarget(c);setModal("deleteCompany");}} title="Deactivate Company">⏸</button>
                      ) : (
                        <div style={{display:"flex",gap:4}}>
                          <button className="icon-btn" style={{background:"#10b98122", color:"#10b981", border:"1px solid #10b98133"}} onClick={()=>{setDeleteTarget(c);setModal("restoreCompany");}} title="Reactivate Company">↺</button>
                          <button className="icon-btn del" style={{background:"#ef444422", color:"#ef4444", border:"1px solid #ef444433"}} onClick={()=>{setDeleteTarget(c);setModal("hardDeleteCompany");}} title="Permanently Delete Company">💥</button>
                        </div>
                      )}
                    </div>
                </div>
                <div className="card-name">{c.name}</div>
                {c.gst&&<div className="card-tin">GST: {c.gst}</div>}
                <div className="card-meta">
                  <div className="card-meta-row" style={{flexWrap:"wrap",gap:8}}>
                    {c.contact&&<span style={{display:"flex",alignItems:"center",gap:3}}><span>👤</span><span>{c.contact}</span></span>}
                    {c.phone&&<span style={{display:"flex",alignItems:"center",gap:3}}><span>📞</span><span>{c.phone}</span></span>}
                    {c.email&&<span style={{display:"flex",alignItems:"center",gap:3}}><span>✉️</span><span style={{fontSize:11}}>{c.email}</span></span>}
                  </div>
                  {c.address&&<div className="card-meta-row"><span>📍</span><span>{c.address}</span></div>}
                </div>

                <div className="card-footer">
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
                    <button className="btn btn-ghost btn-xs" onClick={()=>exportSingleCompanyExcel(c)} title="Export company details as Excel">⬇ Export</button>
                    <button className="btn btn-ghost btn-xs" onClick={()=>exportSingleCompanyPDF(c,empCount)} title="Download PDF company profile">📄 PDF</button>
                    <button className="btn btn-ghost btn-xs" onClick={()=>openPayroll(c)}>💰 Payroll</button>
                    <button
                      className="btn btn-ghost btn-xs"
                      style={{
                        background: cfg.attendance_enabled ? "#eff6ff" : "#f8fafc",
                        color: cfg.attendance_enabled ? "#1d4ed8" : "#64748b",
                        borderColor: cfg.attendance_enabled ? "#bfdbfe" : "#cbd5e1",
                        fontWeight: 700
                      }}
                      onClick={async () => {
                        const nextVal = !cfg.attendance_enabled;
                        const nextCfg = { ...cfg, attendance_enabled: nextVal };
                        const { error } = await supabase.from("companies").update({ config: nextCfg }).eq("id", c.id);
                        if (!error) {
                          setCompanies(prev => prev.map(comp => comp.id === c.id ? { ...comp, config: nextCfg } : comp));
                        }
                      }}
                      title={`Attendance Module is currently ${cfg.attendance_enabled ? "Enabled" : "Disabled"}. Click to toggle for this company.`}
                    >
                      ⏱️ Attendance: {cfg.attendance_enabled ? "ON" : "OFF"}
                    </button>
                    <button className="btn btn-ghost btn-xs" style={companyPendingTotal > 0 ? {color:"#dc2626",borderColor:"#fca5a5",fontWeight:700,background:"#fef2f2"} : {}} onClick={()=>{setActiveCompany(c);setCompanyNav("approvals");setView("approvals");loadEmployees(c.id);}}>
                      ⏳ Approvals{companyPendingTotal > 0 ? ` (${companyPendingTotal})` : ""}
                    </button>
                    <button className="btn btn-ghost btn-xs" style={{color:"#64748b"}} onClick={()=>{setActiveCompany(c);setModal("cleanStaleData");}} title="Scan and purge orphaned database records left by deleted employees (Super Admin)">
                      🧹 Clean DB
                    </button>
                  </div>
                </div>
              </div>);
            })}
          </div>
        </>
      )}

      {/* ── VIEW: JOINING KIT */}
      {view==="joining_kit" && activeCompany && (
        <JoiningKitPage
          key={activeCompany.id}
          employees={companyEmps}
          company={activeCompany}
          onSave={saveJoiningKitEmployee}
          onDirtyChange={setHasUnsavedChanges}
          onBack={() => setView("employees")}
          isCompanyRole={isCompanyRole}
          initialTab={joiningKitTab}
          onRefresh={async () => {
            if (activeCompany?.id) {
              await Promise.all([
                loadEmployees(activeCompany.id),
                loadCompanies()
              ]);
              setAdminToast("Joining kit records refreshed!");
            }
          }}
        />
      )}

      {/* ── VIEW: COMPANY DETAILS */}
      {view==="company_details" && activeCompany && (
        <CompanyDetailsPage
          company={activeCompany}
          onSave={saveCompany}
          onBack={()=>{setView("companies");setActiveCompany(null);}}
          msg={formMsg}
          setMsg={setFormMsg}
          isCompanyRole={isCompanyRole}
          setModal={setModal}
          onDirtyChange={setHasUnsavedChanges}
        />
      )}

      {view==="employee_form" && activeCompany && (
        <EmployeeFormPage
          initial={editTarget}
          currentUser={currentUser}
          company={activeCompany}
          companyEmps={employees[activeCompany?.id] || []}
          onSave={saveEmployee}
          onBack={() => {setView("employees"); setActiveEmployee(null);}}
          msg={formMsg}
          setMsg={setFormMsg}
        />
      )}

      {/* ── VIEW: EMPLOYEES */}
      {view==="employees" && activeCompany && (
        <>
          {!isCompanyRole && (
            <>
              <div className="back-btn" onClick={goBack}>← Back</div>
              <div className="company-banner">
                <div className="banner-icon">🏢</div>
                <div className="banner-info" style={{flex:1}}>
                  <div className="banner-name">
                    {activeCompany.name}
                    <span style={{fontSize:12, fontWeight:700, padding:"2px 8px", borderRadius:4, background:"#e2e8f0", color:"#334155", marginLeft:12, verticalAlign:"middle"}}>
                      {activeCompany.config?.plan_tier || "Free"} Plan (Max {PLAN_LIMITS[activeCompany.config?.plan_tier || "Free"] || 20} Emp)
                    </span>
                  </div>
                  <div className="banner-details">
                    {activeCompany.contact&&<div className="banner-detail">👤 {activeCompany.contact}</div>}
                    {activeCompany.phone&&<div className="banner-detail">📞 {activeCompany.phone}</div>}
                  </div>
                  {/* Salary config chips — visible before running payroll */}
                  {(()=>{const cfg=activeCompany.config||DEFAULT_CONFIG; return(
                    <div className="config-chips" style={{marginTop:10}}>
                      <span className="chip chip-gray">Basic {cfg.custom_basic_enabled ? "Custom" : `${cfg.basic_pct !== undefined ? cfg.basic_pct : 50}%`}</span>{" "}
                      <span className="chip chip-blue">HRA {cfg.hra_customised ? "Custom" : (cfg.hra_type==="fixed"?"₹"+cfg.hra_value:(cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct||40))+"%")}</span>{" "}
                      {cfg.pf_applicable
                        ? <span className="chip chip-green">{cfg.pf_wage_cap!==false?"PF with cap":"PF"}</span>
                        : <span className="chip chip-gray">No PF</span>
                      }{" "}
                      {cfg.esi_applicable
                        ? <span className="chip chip-green">ESI ✓</span>
                        : <span className="chip chip-gray">No ESI</span>
                      }{" "}
                      {cfg.special_allowance&&<span className="chip chip-blue">Sp. Allow {cfg.special_allowance_type==="pct"?cfg.special_allowance_value+"%":"₹"+cfg.special_allowance_value}</span>}{" "}
                      {cfg.da&&<span className="chip chip-blue">DA {cfg.da_pct}%</span>}{" "}
                      {cfg.lta&&<span className="chip chip-blue">LTA {cfg.lta_pct}%</span>}{" "}
                      {cfg.conveyance&&<span className="chip chip-yellow">Conv ₹{cfg.conveyance_amount}</span>}{" "}
                      {cfg.medical&&<span className="chip chip-yellow">Medical ₹{cfg.medical_amount}</span>}{" "}
                      {cfg.bonus&&<span className="chip chip-yellow">Bonus {cfg.bonus_pct}% ({cfg.bonus_months?.length === 12 ? "Monthly" : (cfg.bonus_months?.length ? cfg.bonus_months.map(m=>MONTHS[m]?.slice(0,3)).join(",") : "Mar,Oct")})</span>}{" "}
                      {cfg.gratuity !== false && <span className="chip chip-yellow">Gratuity 4.81%</span>}{" "}
                      {cfg.ot_applicable&&<span className="chip chip-yellow">OT ({cfg.ot_basis==="gross"?"Gross":"Basic+DA"} @ {cfg.ot_rate_multiplier===2?"2x":"1x"})</span>}{" "}
                      {(cfg.absence_basis && cfg.absence_basis !== "no_deduct" && cfg.absence_deduction)
                        ? <span className="chip chip-blue">Abs. from {cfg.absence_basis==="gross"?"Gross":"Basic"} / {cfg.working_days === -1 ? "Calendar Days" : cfg.working_days + "d"}</span>
                        : <span className="chip chip-gray">No Absence Deduct</span>}{" "}
                      {cfg.tds_applicable&&<span className="chip chip-orange">TDS ✓</span>}
                    </div>
                  );})()} 
                </div>
              </div>
            </>
          )}
            {!isCompanyRole && (
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignSelf:"flex-start"}}>
                {companyEmps.length > 0 ? (
                  <button className="btn btn-primary btn-sm" onClick={()=>openPayroll(activeCompany)}>💰 Run Payroll</button>
                ) : (
                  <button className="btn btn-primary btn-sm" onClick={()=>{setEditTarget(null);setFormMsg("");setView("employee_form");}}>＋ Add Employee to Run Payroll</button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={openJoiningKit}>📋 Joining KIT</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setView("dashboard")}>📊 Dashboard</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setCompanyNav("audit_logs");setView("audit_logs");}}>📜 Activity Log</button>
                <button className="btn btn-ghost btn-sm" style={(pendingApprovals.length + pendingLeaves.length) > 0 ? {borderColor:"#fca5a5",background:"#fef2f2",color:"#dc2626",fontWeight:700} : {}} onClick={()=>{setCompanyNav("approvals");setView("approvals");}}>
                  ⏳ Approvals
                  {(pendingApprovals.length + pendingLeaves.length) > 0 && (
                    <span style={{marginLeft:6,background:"#dc2626",color:"#fff",fontSize:11,fontWeight:800,padding:"1px 7px",borderRadius:12}}>
                      {pendingApprovals.length + pendingLeaves.length}
                    </span>
                  )}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeCompany);setFormMsg("");setModal("salaryConfig");}}>＋ Add Salary Structure</button>
                <button className="btn btn-ghost btn-sm" title="Export current salary structure as Excel" onClick={()=>downloadSalaryStructureTemplate(activeCompany)}>⬇ Export</button>
                <button className="btn btn-ghost btn-sm" title="Download blank salary structure template" onClick={()=>downloadSalaryStructureTemplate(null)}>📋 Template</button>
                <button className="btn btn-ghost btn-sm" title="Upload salary structure from Excel" onClick={()=>{ const inp=document.createElement('input');inp.type='file';inp.accept='.xlsx,.xls';inp.onchange=e=>handleSalaryStructureUpload(e.target.files[0]);inp.click(); }}>⬆ Upload</button>
              </div>
            )}
            {!isCompanyRole && (pendingApprovals.length + pendingLeaves.length) > 0 && (
              <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:12,padding:"14px 18px",marginBottom:6,width:"100%",boxSizing:"border-box",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,boxShadow:"0 2px 6px rgba(217,119,6,0.08)"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:24}}>⏳</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:800,color:"#92400e"}}>Action Required: {pendingApprovals.length + pendingLeaves.length} Pending Approval{pendingApprovals.length + pendingLeaves.length > 1 ? "s" : ""}</div>
                    <div style={{fontSize:12.5,color:"#b45309",marginTop:2}}>
                      {pendingLeaves.length > 0 && `${pendingLeaves.length} leave application${pendingLeaves.length > 1 ? "s" : ""}`}
                      {pendingLeaves.length > 0 && pendingApprovals.length > 0 && " and "}
                      {pendingApprovals.length > 0 && `${pendingApprovals.length} profile update${pendingApprovals.length > 1 ? "s" : ""}`} awaiting review.
                    </div>
                  </div>
                </div>
                <button className="btn btn-sm" style={{background:"#d97706",color:"#fff",border:"none",fontWeight:700,borderRadius:8,padding:"8px 16px",cursor:"pointer",whiteSpace:"nowrap"}} onClick={()=>{setCompanyNav("approvals");setView("approvals");}}>
                  Review Requests →
                </button>
              </div>
            )}
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input className="search-input" placeholder="Search employee…" value={empSearch} onChange={e=>setEmpSearch(e.target.value)} />
          </div>
          <div className="table-wrap">
            <div className="table-header">
              <div className="table-title">Employees ({filteredEmps.length})</div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <input type="file" id="empUploadInput" accept=".xlsx,.xls" style={{display:"none"}}
                  onChange={e=>{handleEmployeeExcelUpload(e.target.files[0]);e.target.value="";}} />
                <button className="btn btn-ghost btn-sm" title="Download employee data as Excel" onClick={()=>exportEmployeesExcel(employees[activeCompany.id]||[],activeCompany.name)}>⬇ Export</button>
                <button className="btn btn-ghost btn-sm" title="Download blank employee template" onClick={()=>downloadEmployeeTemplate(activeCompany?.name || "")}>📋 Template</button>
                <button className="btn btn-ghost btn-sm" title="Upload employees from Excel" onClick={()=>document.getElementById("empUploadInput").click()}>⬆ Upload</button>
                {!isCompanyRole && currentUser?.role === "admin" && (
                  <button className="btn btn-ghost btn-sm" title="Scan & Purge Stale DB Entries and Storage (Super Admin Only)" onClick={()=>setModal("cleanStaleData")} style={{color:"#64748b"}}>🧹 Clean Stale DB</button>
                )}
                {activeCompany?.config?.addon_bulk_hike && (
                  <>
                    <button className="btn btn-ghost btn-sm" style={{fontWeight:700,color:"#2563eb",borderColor:"#bfdbfe",background:"#eff6ff"}} onClick={() => { setFormMsg(""); setModal("bulk_hike"); }} title="Bulk Salary Hikes & Revision Excel">📈 Bulk Salary Hike</button>
                    <button className="btn btn-ghost btn-sm" style={{fontWeight:700,color:"#059669",borderColor:"#a7f3d0",background:"#ecfdf5"}} onClick={() => { setFormMsg(""); setModal("hike_report"); }} title="View Complete Company Salary Hike & Appraisal Report">📜 Hike Report</button>
                  </>
                )}
                {selectedEmps.length > 0 && (
                  <><BulkEmployeeInvitations key={activeCompany.id} company={activeCompany} employees={companyEmps.filter(employee=>selectedEmps.includes(employee.id))}/><button className="btn btn-danger btn-sm" onClick={()=>setModal("deleteMultipleEmps")}>🗑 Delete Selected ({selectedEmps.length})</button></>
                )}
                {(() => {
                  const planTier = activeCompany?.config?.plan_tier || "Free";
                  const empLimit = PLAN_LIMITS[planTier] || 20;
                  const currentCount = companyEmps.length;
                  const limitReached = currentCount >= empLimit;
                  return (
                    <>
                      <span style={{fontSize:12, fontWeight:700, color:limitReached?"#ef4444":"#64748b", alignSelf:"center", marginRight:4}}>
                        {planTier} Plan ({currentCount}/{empLimit})
                      </span>
                      <button className="btn btn-primary btn-sm" onClick={()=>{
                        if(limitReached) {
                          alert(`You have reached the employee limit (${empLimit}) for the ${planTier} plan. Please contact the administrator to upgrade.`);
                        } else {
                          setEditTarget(null);setFormMsg("");setView("employee_form");
                        }
                      }}>＋ Add Employee</button>
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={{overflowX:"auto"}}>
            <table>
              <thead><tr>
                <th style={{width: 40, textAlign: "center"}}>
                  <input type="checkbox" 
                    checked={filteredEmps.length > 0 && selectedEmps.length === filteredEmps.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedEmps(filteredEmps.map(emp => emp.id));
                      else setSelectedEmps([]);
                    }}
                  />
                </th>
                <th style={{cursor:"pointer", userSelect:"none", width: 100}} onClick={()=>setEmpSort(p=>({key:'employee_code',dir:p.key==='employee_code'&&p.dir==='asc'?'desc':'asc'}))} title="Click to sort by Employee ID">Emp ID {empSort.key==='employee_code'?(empSort.dir==='asc'?'↑':'↓'):''}</th>
                <th style={{cursor:"pointer", userSelect:"none"}} onClick={()=>setEmpSort(p=>({key:'name',dir:p.key==='name'&&p.dir==='asc'?'desc':'asc'}))}>Name {empSort.key==='name'?(empSort.dir==='asc'?'↑':'↓'):''}</th>
                <th style={{textAlign:"center", width: 30, padding: 0}}>
                  <button style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={() => setShowPersonalBreakdown(p => !p)} title="Toggle Personal Info">
                    {showPersonalBreakdown ? "−" : "＋"}
                  </button>
                </th>
                {showPersonalBreakdown && <th style={{width: 110}}>PAN</th>}
                {showPersonalBreakdown && <th style={{textAlign:"center", width: 60}}>Age</th>}
                {showPersonalBreakdown && <th style={{textAlign:"center", cursor:"pointer", userSelect:"none", width: 100}} onClick={()=>setEmpSort(p=>({key:'doj',dir:p.key==='doj'&&p.dir==='asc'?'desc':'asc'}))}>DOJ {empSort.key==='doj'?(empSort.dir==='asc'?'↑':'↓'):''}</th>}
                <th style={{textAlign:"center", width: 30, padding: 0}}>
                  <button style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={() => setShowSalaryBreakdown(p => !p)} title="Toggle Salary Breakdown">
                    {showSalaryBreakdown ? "−" : "＋"}
                  </button>
                </th>
                {showSalaryBreakdown && <th style={{textAlign:"right", cursor:"pointer", userSelect:"none"}} onClick={()=>setEmpSort(p=>({key:'real_basic',dir:p.key==='real_basic'&&p.dir==='asc'?'desc':'asc'}))}>Basic {empSort.key==='real_basic'?(empSort.dir==='asc'?'↑':'↓'):''}</th>}
                {showSalaryBreakdown && <th style={{textAlign:"right"}}>HRA</th>}
                <th style={{textAlign:"center", width: 30, padding: 0}}>
                  <button style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={() => setShowStatBreakdown(p => !p)} title="Toggle PF/ESI Breakdown">
                    {showStatBreakdown ? "−" : "＋"}
                  </button>
                </th>
                {showStatBreakdown && <th style={{textAlign:"right", color:"#0369a1"}}>PF (Emp/Er)</th>}
                {showStatBreakdown && <th style={{textAlign:"right", color:"#0f766e"}}>ESI (Emp/Er)</th>}
                <th style={{textAlign:"center", width: 30, padding: 0}}>
                  <button style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={() => setShowAllowancesBreakdown(p => !p)} title="Toggle Other Components Breakdown">
                    {showAllowancesBreakdown ? "−" : "＋"}
                  </button>
                </th>
                {showAllowancesBreakdown && activeCompany?.config?.vpf_tds_enabled && <th style={{textAlign:"right"}}>VPF</th>}
                {showAllowancesBreakdown && activeCompany?.config?.vpf_tds_enabled && <th style={{textAlign:"right"}}>TDS</th>}
                {showAllowancesBreakdown && activeCompany?.config?.bonus && <th style={{textAlign:"right", color:"#6b21a8"}}>{activeCompany?.config?.bonus_display_name || "Bonus"}</th>}
                {showAllowancesBreakdown && activeCompany?.config?.custom_special_allowance_enabled && <th style={{textAlign:"right", color:"#ca8a04"}}>{activeCompany?.config?.custom_special_display_name || "Special Allowance"}</th>}
                <th style={{textAlign:"center", width: 30, padding: 0}}>
                  <button style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={() => setShowTotalBreakdown(p => !p)} title="Toggle Net/CTC Breakdown">
                    {showTotalBreakdown ? "−" : "＋"}
                  </button>
                </th>
                <th style={{textAlign:"right", cursor:"pointer", userSelect:"none", background:"#f1f5f9"}} onClick={()=>setEmpSort(p=>({key:'basic',dir:p.key==='basic'&&p.dir==='asc'?'desc':'asc'}))}>Gross {empSort.key==='basic'?(empSort.dir==='asc'?'↑':'↓'):''}</th>
                {showTotalBreakdown && <th style={{textAlign:"right", color:"#16a34a", cursor:"pointer", userSelect:"none", background:"#f0fdf4"}} onClick={()=>setEmpSort(p=>({key:'net',dir:p.key==='net'&&p.dir==='asc'?'desc':'asc'}))}>Net {empSort.key==='net'?(empSort.dir==='asc'?'↑':'↓'):''}</th>}
                {showTotalBreakdown && <th style={{textAlign:"right", color:"#9333ea", cursor:"pointer", userSelect:"none", background:"#faf5ff"}} onClick={()=>setEmpSort(p=>({key:'ctc',dir:p.key==='ctc'&&p.dir==='asc'?'desc':'asc'}))}>CTC {empSort.key==='ctc'?(empSort.dir==='asc'?'↑':'↓'):''}</th>}
                <th style={{textAlign:"center"}}>Actions</th>
                <th style={{width: "100%"}}></th>
              </tr></thead>
              <tbody>
                {filteredEmps.length===0
                  ? <tr><td colSpan={20} style={{textAlign:"center",padding:"36px",color:"#2a3050"}}>No employees yet.</td></tr>
                  : filteredEmps.slice(0, visibleEmpCount).map(e=>(
                    <tr key={e.id} className="emp-row">
                      <td style={{textAlign: "center"}}>
                        <input type="checkbox"
                          checked={selectedEmps.includes(e.id)}
                          onChange={(ev) => {
                            if (ev.target.checked) setSelectedEmps(prev => [...prev, e.id]);
                            else setSelectedEmps(prev => prev.filter(id => id !== e.id));
                          }}
                        />
                      </td>
                      <td className="td-emp-id" style={{color:"#64748b", fontWeight: 600, fontSize: 12}}>
                        {e.employee_code || "—"}
                      </td>
                      <td className="td-name" onClick={()=>openEmployee(e)}>
                        {(() => {
                          const photo = e.emp_photo || activeCompany?.config?.employee_photos?.[e.id] || activeCompany?.config?.employee_photos?.[e.employee_code] || localStorage.getItem("tw_photo_" + e.id) || (e.employee_code ? localStorage.getItem("tw_photo_" + e.employee_code) : "") || "";
                          return photo ? (
                            <div style={{display: "inline-flex", alignItems: "center", gap: 8, verticalAlign: "middle"}}>
                              <img src={photo} alt={e.name} style={{width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1.5px solid #cbd5e1"}} />
                              <span>{e.name}</span>
                            </div>
                          ) : (
                            <span>{e.name}</span>
                          );
                        })()}
                        {e.date_of_exit && <span style={{marginLeft:8,fontSize:10,background:"#fee2e2",color:"#b91c1c",padding:"2px 6px",borderRadius:4,border:"1px solid #fecaca",verticalAlign:"middle"}}>Resigned</span>}
                        {e.vpf_pct > 0 && <span style={{marginLeft:8,fontSize:10,background:"#fef3c7",color:"#d97706",padding:"2px 6px",borderRadius:4,border:"1px solid #fde68a",verticalAlign:"middle"}}>VPF: {fmtINR(e.vpf_pct)}</span>}
                        {e.tds_amount > 0 && <span style={{marginLeft:8,fontSize:10,background:"#e0e7ff",color:"#4338ca",padding:"2px 6px",borderRadius:4,border:"1px solid #c7d2fe",verticalAlign:"middle"}}>TDS: {fmtINR(e.tds_amount)}</span>}
                        {(() => {
                          const lock = getLocalEmployeeLockStatus(e.id, e.pan);
                          if (!lock) return null;
                          return (
                            <AccountLockBadge
                              lockInfo={lock}
                              onUnlock={async () => {
                                const res = await unlockEmployeeAccount(supabase, activeCompany?.id, e.id, e.pan, currentUser?.username || "Admin", activeCompany);
                                if (res.ok) {
                                  alert(`Account unlocked for ${e.name}`);
                                  loadEmployees(activeCompany.id);
                                }
                              }}
                            />
                          );
                        })()}
                      </td>
                      <td style={{padding: 0, width: 30}}></td>
                      {showPersonalBreakdown && <td className="td-pan">{e.pan}</td>}
                      {showPersonalBreakdown && <td style={{textAlign:"center"}}>{e.dob ? calcAge(e.dob) : (e.age||"—")}</td>}
                      {showPersonalBreakdown && <td style={{textAlign:"center"}}>{fmtDate(e.doj)}</td>}
                      <td style={{padding: 0, width: 30}}></td>
                      {showSalaryBreakdown && (() => {
                        const s = calcSalaryFull({ basic: e.basic || 0, month: 1, year: 2026, cfg: activeCompany?.config || {}, emp_id: e.id, extra: e });
                        return (
                          <>
                            <td className="td-num" style={{color:"#475569", fontSize:12}}>{fmtINR(s.basic)}</td>
                            <td className="td-num" style={{color:"#475569", fontSize:12}}>{fmtINR(s.hra)}</td>
                          </>
                        );
                      })()}
                      <td style={{padding: 0, width: 30}}></td>
                      {showStatBreakdown && (() => {
                        const s = calcSalaryFull({ basic: e.basic || 0, month: 1, year: 2026, cfg: activeCompany?.config || {}, emp_id: e.id, extra: e });
                        return (
                          <>
                            <td className="td-num" style={{color:"#0369a1", fontSize:12}}>{fmtINR(s.pf_emp)} / {fmtINR(s.pf_er)}</td>
                            <td className="td-num" style={{color:"#0f766e", fontSize:12}}>{fmtINR(s.esi_emp)} / {fmtINR(s.esi_er)}</td>
                          </>
                        );
                      })()}
                      <td style={{padding: 0, width: 30}}></td>
                      {showAllowancesBreakdown && (() => {
                        const s = calcSalaryFull({ basic: e.basic || 0, month: 1, year: 2026, cfg: activeCompany?.config || {}, emp_id: e.id, extra: e });
                        const potentialBonus = (s.basic + s.da <= 21000) ? Math.round(s.basic * (activeCompany?.config?.bonus_pct || 0) / 100) : 0;
                        return (
                          <>
                            {activeCompany?.config?.vpf_tds_enabled && <td className="td-num" style={{color:"#d97706", fontSize:12}}>{e.vpf_pct > 0 ? fmtINR(e.vpf_pct) : "—"}</td>}
                            {activeCompany?.config?.vpf_tds_enabled && <td className="td-num" style={{color:"#4338ca", fontSize:12}}>{e.tds_amount > 0 ? fmtINR(e.tds_amount) : "—"}</td>}
                            {activeCompany?.config?.bonus && <td className="td-num" style={{color:"#6b21a8", fontSize:12}}>{fmtINR(s.bonus || potentialBonus)}</td>}
                            {activeCompany?.config?.custom_special_allowance_enabled && <td className="td-num" style={{color:"#ca8a04", fontSize:12}}>{fmtINR(s.special)}</td>}
                          </>
                        );
                      })()}
                      <td style={{padding: 0, width: 30}}></td>
                      <td className="td-num" style={{color:"#1e293b",fontWeight:600, background:"#f1f5f9"}}>{fmtINR(e.basic||0)}</td>
                      {showTotalBreakdown && (() => {
                        const s = calcSalaryFull({ basic: e.basic || 0, month: 1, year: 2026, cfg: activeCompany?.config || {}, emp_id: e.id, extra: e });
                        return (
                          <>
                            <td className="td-num" style={{color:"#16a34a", fontWeight: 600, background:"#f0fdf4"}}>{fmtINR(s.net)}</td>
                            <td className="td-num" style={{color:"#9333ea", fontWeight: 600, background:"#faf5ff"}}>{fmtINR(s.ctc)}</td>
                          </>
                        );
                      })()}
                      <td><div className="td-actions" style={{display: "flex", flexWrap: "nowrap", gap: 4, justifyContent:"center"}}>
                        <button className="btn btn-ghost btn-xs" title="View Employee" style={{fontSize: 11, padding: "2px 6px"}} onClick={()=>openEmployee(e)}>👁️</button>
                        <button className="btn btn-ghost btn-xs" title="Edit Employee" style={{fontSize: 11, padding: "2px 6px"}} onClick={()=>{setEditTarget(e);setFormMsg("");setView("employee_form");}}>✏️</button>
                        <button className="btn btn-danger btn-xs" title="Delete Employee" style={{fontSize: 11, padding: "2px 6px"}} onClick={()=>{setDeleteTarget(e);setModal("deleteEmp");}}>🗑️</button>
                      </div></td>
                      <td></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            </div>
            {visibleEmpCount < filteredEmps.length && (
              <div style={{display: "flex", justifyContent: "flex-end", padding: "12px 16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", borderRadius: "0 0 14px 14px"}}>
                <button className="btn btn-ghost btn-sm" style={{color: "#2563eb", fontWeight: 700, background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8}} onClick={() => setVisibleEmpCount(v => v + 10)}>
                  ＋ Load 10 More
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── VIEW: PAYROLL RUN */}
      {view==="payroll" && activeCompany && (
        <>
          {!isCompanyRole && (
            <div className="back-btn" onClick={goBack}>
              ← Back to Companies
            </div>
          )}
          <div className="page-header">
            <div>
              <div className="page-title">Monthly Payroll</div>
              <div className="page-sub">Select a month, enter attendance, review & export</div>
            </div>
          </div>

          {/* Month selector */}
          {!payMonth && (
            <>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 12}}>
                <div style={{fontSize:13,color:"#9ba8c8",fontWeight:600}}>SELECT PAYROLL MONTH</div>
                {availableFYs.length > 0 && (
                  <select className="config-input" style={{width: 180, padding: "6px 12px", fontSize: 13, height: "auto", margin: 0}} value={selectedFY||""} onChange={e => setSelectedFY(e.target.value)}>
                    {availableFYs.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                  </select>
                )}
              </div>
              <div className="payroll-grid">
                {availableMonths.map(({month,year})=>{
                  const done = companyEmps.some(e=>(salaries[e.id]||[]).find(s=>s.month===month&&s.year===year));
                  const isMonthLocked = companyEmps.some(e=>{
                    const s = (salaries[e.id]||[]).find(s=>s.month===month&&s.year===year);
                    return s && s.status === "locked";
                  });
                  return(
                  <div key={month+"-"+year} className={"month-card"+(payMonth?.month===month&&payMonth?.year===year?" selected":"")}
                    onClick={()=>{setPayMonth({month,year});setEditAttRow({});}}>
                    <div>
                      <div className="month-card-title">{MONTHS[month]} {year}{isMonthLocked ? " 🔒" : ""}</div>
                      <div className="month-card-sub">{companyEmps.length} employees · {activeCompany?.config?.payroll_workflows?.[`${year}-${String(month).padStart(2, "0")}`]?.stage || (isMonthLocked ? "locked" : done ? "draft" : "not started")}</div>
                    </div>
                    <div style={{fontSize:14, opacity:payMonth?.month===month&&payMonth?.year===year?1:0.3, transition:"opacity 0.3s"}}>🗓️</div>
                  </div>);
                })}
              </div>
            </>
          )}

          {/* Payroll run for selected month */}
          {payMonth && (
            <>
              {formMsg && (
                <div className={formMsg.includes("failed")?"err-msg":"success-msg"} style={{marginBottom: 16}}>
                  {formMsg.includes("failed")?"⚠️":"✅"} {formMsg}
                </div>
              )}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18,flexWrap:"wrap"}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setPayMonth(null);setEditAttRow({});}}>← Change Month</button>
                <div style={{fontFamily:"'Inter',sans-serif",fontWeight:700,fontSize:17,color:"#1e293b"}}>{MONTHS[payMonth.month]} {payMonth.year}</div>
              </div>

              <PayrollWorkflowPanel key={`${activeCompany.id}-${payMonth.year}-${payMonth.month}`} workflow={payrollWorkflow} onSave={savePayrollRun} employeeCount={eligibleEmps.length} />
              <PayslipGroupNotification key={`group-${activeCompany.id}-${payMonth.year}-${payMonth.month}`} company={activeCompany} month={payMonth.month} year={payMonth.year} published={["locked", "paid"].includes(payrollWorkflow.stage)} blocked={payrollWorkflow.loading || payrollWorkflow.busy || Boolean(payrollWorkflow.error || payrollWorkflow.metadata?.operation) || !eligibleEmps.length} />

              {/* Summary totals */}
              {payrollTotals && (
                <div className="sum-row">
                  <div className="sum-card"><div className="sum-label">Employees</div><div className="sum-val">{eligibleEmps.length}</div></div>
                  <div className="sum-card"><div className="sum-label">Total Gross</div><div className="sum-val blue">{fmtINR(payrollTotals.gross)}</div></div>
                  <div className="sum-card"><div className="sum-label">Net Payable</div><div className="sum-val green">{fmtINR(payrollTotals.net)}</div></div>
                  <div className="sum-card"><div className="sum-label">PF (both)</div><div className="sum-val yellow">{fmtINR(payrollTotals.pf)}</div></div>
                  <div className="sum-card"><div className="sum-label">ESI (both)</div><div className="sum-val yellow">{fmtINR(payrollTotals.esi)}</div></div>
                  <div className="sum-card"><div className="sum-label">Total CTC</div><div className="sum-val">{fmtINR(payrollTotals.ctc)}</div></div>
                </div>
              )}

              {/* Attendance upload */}
              <div className="table-wrap" style={{marginBottom:14}}>
                <div className="table-header">
                  <div className="table-title">📋 Payroll — {MONTHS[payMonth.month]} {payMonth.year}</div>
                  <div style={{display:"flex",gap:8}}>
                    {!isPayrollLocked && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={async ()=>{
                      try {
                        const ExcelJS = (await import("exceljs")).default;
                        const wb = new ExcelJS.Workbook();
                        const ws = wb.addWorksheet("Attendance");

                        const headers = ["Employee ID", "Name", "Absent Days"];
                        if (activeCompany.config?.ot_applicable) headers.push("OT Hours");

                        ws.addRow([]);
                        ws.addRow([]);
                        ws.addRow([]);

                        const border = {
                          top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                          left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                          bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                          right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
                        };

                        const headerRow = ws.addRow(headers);
                        headerRow.font = { bold: true, color: { argb: 'FF0F172A' } };
                        headerRow.eachCell(cell => {
                          cell.border = border;
                          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
                        });

                        ws.views = [{ state: "frozen", xSplit: 2, ySplit: 4 }];

                        headers.forEach((h, i) => {
                          ws.getColumn(i + 1).width = Math.floor(Math.max(h.length + 2, 20) * 1.1);
                        });

                        eligibleEmps.forEach((e, idx) => {
                          const rData = [e.employee_code || "", e.name || "", 0];
                          if (activeCompany.config?.ot_applicable) rData.push(0);
                          const row = ws.addRow(rData);
                          const bgColor = idx % 2 === 0 ? 'FFFFFFFF' : 'FFF8FAFC';
                          row.eachCell({ includeEmpty: true }, (cell) => {
                            cell.border = border;
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                          });
                        });

                        const buffer = await wb.xlsx.writeBuffer();
                        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "Attendance_Template_"+MONTHS[payMonth.month]+"_"+payMonth.year+".xlsx";
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch(err) {
                        alert("Template error: " + err.message);
                      }
                    }}>⬇ Template</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>fileRef.current?.click()}>⬆ Upload Excel</button>
                    <input ref={fileRef} type="file" accept=".xlsx, .xls" style={{display:"none"}} onChange={e=>{handleAttUpload(e.target.files[0]);e.target.value="";}} />
                    
                    <button 
                      className="btn btn-primary btn-sm" 
                      style={{marginLeft: 12, background: "#8b5cf6", borderColor: "#7c3aed"}}
                      onClick={async () => {
                        if (!eligibleEmps || eligibleEmps.length === 0) return;
                        if (!window.confirm(`Notify ${eligibleEmps.length} employees about their published payslip? Only locked payslips can be notified; repeats are skipped.`)) return;
                        let current = 0, errors = 0, accepted = 0, skipped = 0, review = 0;
                        const failures = [];
                        const total = eligibleEmps.length;
                        setEmailSendingProgress({ current, total, errors, accepted, skipped, review, done: false });
                        for (const employee of eligibleEmps) {
                          try {
                            const result = await companyEmail(activeCompany.id, 'send', { kind: 'payslip', employee_id: employee.id, month: payMonth.month, year: payMonth.year });
                            if (result.duplicate && ['accepted','delivered'].includes(result.delivery?.status)) skipped += 1;
                            else if (result.duplicate) review += 1;
                            else if (result.delivery?.status === 'accepted') accepted += 1;
                            else review += 1;
                          } catch (error) { errors += 1; if (failures.length < 20) failures.push({ name: employee.name, message: error.message }); }
                          current += 1;
                          setEmailSendingProgress({ current, total, errors, accepted, skipped, review, failures: [...failures], done: current === total });
                        }
                      }}
                      disabled={emailSendingProgress && !emailSendingProgress.done}
                    >
                      {emailSendingProgress && !emailSendingProgress.done ? "Sending..." : "📧 Notify All Employees"}
                    </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Email submission progress */}
                {emailSendingProgress && (
                  <div style={{marginBottom: 20, padding: 16, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10}}>
                    <div style={{display: "flex", justifyContent: "space-between", marginBottom: 8}}>
                      <div style={{fontWeight: 600, color: "#334155"}}>
                        {emailSendingProgress.done ? "✅ Sending Complete" : "🚀 Sending Emails..."}
                      </div>
                      <div style={{color: "#64748b", fontSize: 13, fontWeight: 700}}>
                        {emailSendingProgress.current} / {emailSendingProgress.total}
                      </div>
                    </div>
                    
                    <div style={{height: 12, background: "#cbd5e1", borderRadius: 6, overflow: "hidden", marginBottom: 8}}>
                      <div style={{
                        height: "100%", 
                        background: emailSendingProgress.done ? "#10b981" : "#3b82f6", 
                        width: `${(emailSendingProgress.current / emailSendingProgress.total) * 100}%`,
                        transition: "width 0.3s ease"
                      }}></div>
                    </div>
                    
                    {emailSendingProgress.done && (
                      <div style={{fontSize: 13, color: "#475569", display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                        <span style={{color: "#10b981", fontWeight: 700}}>✅ {emailSendingProgress.accepted || 0} Accepted by email service</span>
                        <span style={{color:"#64748b",fontWeight:600}}>{emailSendingProgress.skipped || 0} already processed · {emailSendingProgress.review || 0} need review</span>
                        {emailSendingProgress.errors > 0 && <span style={{color: "#ef4444", fontWeight: 700}}>❌ {emailSendingProgress.errors} Failed (see delivery history or check published payslips)</span>}
                        <button className="btn btn-ghost btn-xs" style={{marginLeft: "auto"}} onClick={() => setEmailSendingProgress(null)}>Dismiss</button>
                      </div>
                    )}
                    {Boolean(emailSendingProgress.failures?.length) && <details style={{marginTop:12,fontSize:12,lineHeight:1.8}}><summary>Employee notification errors (first 20)</summary><ul>{emailSendingProgress.failures.map((item,index)=><li key={index}><strong>{item.name}:</strong> {item.message}</li>)}</ul></details>}
                  </div>
                )}
                <div style={{overflowX:"auto"}}>
                <table>
                  <thead><tr>
                    <th
                      style={{ width: 44, textAlign: "center", cursor: "pointer", userSelect: "none" }}
                      onClick={() => setPayrollRunSort(p => ({ key: 'employee_code', dir: p.key === 'employee_code' && p.dir === 'asc' ? 'desc' : 'asc' }))}
                      title="Click to sort by Employee ID"
                    >
                      # {payrollRunSort.key === 'employee_code' ? (payrollRunSort.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => setPayrollRunSort(p => ({ key: 'name', dir: p.key === 'name' && p.dir === 'asc' ? 'desc' : 'asc' }))}
                      title="Click to sort by Employee Name"
                    >
                      Employee {payrollRunSort.key === 'name' ? (payrollRunSort.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{textAlign:"center", width: 30, padding: 0}}>
                      <button type="button" style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={(e)=>{e.stopPropagation(); setShowSalaryBreakdown(p => !p);}} title="Toggle Salary Breakdown">
                        {showSalaryBreakdown ? "−" : "＋"}
                      </button>
                    </th>
                    {showSalaryBreakdown && <th style={{textAlign:"right"}}>{"Basic " + (activeCompany.config?.custom_basic_enabled ? "(Custom)" : "(" + (activeCompany.config?.basic_pct !== undefined ? activeCompany.config.basic_pct : 50) + "%)")}</th>}
                    {showSalaryBreakdown && <th style={{textAlign:"right"}}>{"HRA " + (activeCompany.config?.hra_customised ? "(Custom)" : (activeCompany.config?.hra_type==="fixed" ? "(Fixed)" : "(" + (activeCompany.config?.hra_value !== undefined ? activeCompany.config?.hra_value : (activeCompany.config?.hra_pct ?? 40)) + "%)"))}</th>}
                    {!isNoDeduct && <th style={{textAlign:"center"}}>Working Days</th>}
                    {!isNoDeduct && <th style={{textAlign:"center"}}>Absent Days</th>}
                    {activeCompany.config?.ot_applicable&&<th style={{textAlign:"center"}}>OT Hours</th>}
                    <th style={{textAlign:"center", width: 30, padding: 0}}>
                      <button type="button" style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={(e)=>{e.stopPropagation(); setShowStatBreakdown(p => !p);}} title="Toggle PF/ESI Breakdown">
                        {showStatBreakdown ? "−" : "＋"}
                      </button>
                    </th>
                    {showStatBreakdown && activeCompany.config?.pf_applicable && <th style={{textAlign:"right", color:"#0369a1"}}>PF (Emp/Er)</th>}
                    {showStatBreakdown && activeCompany.config?.esi_applicable && <th style={{textAlign:"right", color:"#0f766e"}}>ESI (Emp/Er)</th>}
                    {showStatBreakdown && <th style={{textAlign:"right"}}>PT</th>}
                    <th style={{textAlign:"center", width: 30, padding: 0}}>
                      <button type="button" style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={(e)=>{e.stopPropagation(); setShowAllowancesBreakdown(p => !p);}} title="Toggle Other Components Breakdown">
                        {showAllowancesBreakdown ? "−" : "＋"}
                      </button>
                    </th>
                    {showAllowancesBreakdown && !isNoDeduct && <th style={{textAlign:"right", color:"#dc2626"}}>Abs. Ded</th>}
                    {showAllowancesBreakdown && (activeCompany?.config?.da || activeCompany?.config?.special_allowance || activeCompany?.config?.custom_special_allowance_enabled) && <th style={{textAlign:"right", color:"#ca8a04"}}>Sp. Allow</th>}
                    {showAllowancesBreakdown && activeCompany?.config?.vpf_tds_enabled && <th style={{textAlign:"right", color:"#d97706"}}>VPF</th>}
                    {showAllowancesBreakdown && (activeCompany?.config?.vpf_tds_enabled || activeCompany?.config?.tds_applicable) && <th style={{textAlign:"right", color:"#4338ca"}}>TDS</th>}
                    {showAllowancesBreakdown && activeCompany?.config?.bonus && <th style={{textAlign:"right", color:"#6b21a8"}}>{activeCompany?.config?.bonus_display_name || "Bonus"}</th>}
                    <th style={{textAlign:"center", width: 30, padding: 0}}>
                      <button type="button" style={{background:"transparent",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,color:"#2563eb",width:"100%",height:"100%"}} onClick={(e)=>{e.stopPropagation(); setShowTotalBreakdown(p => !p);}} title="Toggle Net/CTC Breakdown">
                        {showTotalBreakdown ? "−" : "＋"}
                      </button>
                    </th>
                    <th style={{textAlign:"right", background:"#f1f5f9"}}>Gross</th>
                    <th style={{textAlign:"right", color:"#16a34a", background:"#f0fdf4"}}>Net Pay</th>
                    {showTotalBreakdown && <th style={{textAlign:"right", color:"#9333ea", background:"#faf5ff"}}>CTC</th>}
                    <th style={{textAlign:"center"}}>Slip</th>
                    <th style={{width: "100%"}}></th>
                  </tr></thead>
                  <tbody>
                    {eligibleEmps.map((e,i)=>{
                      const s=payrollData[e.id]||{};
                      const att=editAttRow[e.id]||{};
                      const absDays=att.absent_days!==undefined?att.absent_days:(s.absent_days||0);
                      const otH=att.ot_hours!==undefined?att.ot_hours:(s.ot_hours||0);
                      return(
                      <tr key={e.id}>
                        <td style={{color:"#6b7a9e", textAlign: "center"}}>{i+1}</td>
                        <td className="td-name" onClick={()=>openEmployee(e)}>
                          {e.name}
                          {e.date_of_exit && <span style={{marginLeft:8,fontSize:10,background:"#fee2e2",color:"#b91c1c",padding:"2px 6px",borderRadius:4,border:"1px solid #fecaca",verticalAlign:"middle"}}>Resigned</span>}
                          {e.vpf_pct > 0 && <span style={{marginLeft:8,fontSize:10,background:"#fef3c7",color:"#d97706",padding:"2px 6px",borderRadius:4,border:"1px solid #fde68a",verticalAlign:"middle"}}>VPF: {fmtINR(e.vpf_pct)}</span>}
                          {e.tds_amount > 0 && <span style={{marginLeft:8,fontSize:10,background:"#e0e7ff",color:"#4338ca",padding:"2px 6px",borderRadius:4,border:"1px solid #c7d2fe",verticalAlign:"middle"}}>TDS: {fmtINR(e.tds_amount)}</span>}
                        </td>
                        <td style={{padding: 0, width: 30}}></td>
                        {showSalaryBreakdown && <td className="td-num" style={{color:"#475569", fontSize:12}}>{fmtINR(s.basic||0)}</td>}
                        {showSalaryBreakdown && <td className="td-num" style={{color:"#475569", fontSize:12}}>{fmtINR(s.hra||0)}</td>}
                        {!isNoDeduct && (
                          <td className="td-center">{activeCompany.config?.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (activeCompany.config?.working_days||26)} {e._status.lwd_absent>0&&<span style={{fontSize:10,color:"#dc2626",display:"block"}}>(LWD enforced)</span>}</td>
                        )}
                        {!isNoDeduct && (
                          <td className="td-center">
                            {isPayrollLocked ? <span style={{fontWeight:500,color:"#475569"}}>{absDays}</span> : (
                              <div className={"att-ctrl"+(absDays>0?" has-val":"")}>
                                <button className="att-step" onClick={()=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],absent_days:Math.max(0,(att.absent_days!==undefined?att.absent_days:(s.absent_days||0))-1)}}))} tabIndex={-1}>−</button>
                                <input className="att-input" type="number" min="0" max="31" value={absDays}
                                  onChange={ev=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],absent_days:parseInt(ev.target.value)||0}}))}
                                  onFocus={ev=>ev.target.select()} />
                                <button className="att-step" onClick={()=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],absent_days:Math.min(31,(att.absent_days!==undefined?att.absent_days:(s.absent_days||0))+1)}}))} tabIndex={-1}>+</button>
                              </div>
                            )}
                          </td>
                        )}
                        {activeCompany.config?.ot_applicable&&(
                          <td className="td-center">
                            <div className={"att-ctrl"+(otH>0?" ot-val":"")}>
                              <button className="att-step" onClick={()=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],ot_hours:Math.max(0,parseFloat(((att.ot_hours!==undefined?att.ot_hours:(s.ot_hours||0))-0.5).toFixed(1)))}}))} tabIndex={-1}>−</button>
                              <input className="att-input" type="number" min="0" value={otH}
                                onChange={ev=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],ot_hours:parseFloat(ev.target.value)||0}}))}
                                onFocus={ev=>ev.target.select()} />
                              <button className="att-step" onClick={()=>setEditAttRow(p=>({...p,[e.id]:{...p[e.id],ot_hours:parseFloat(((att.ot_hours!==undefined?att.ot_hours:(s.ot_hours||0))+0.5).toFixed(1))}}))} tabIndex={-1}>+</button>
                            </div>
                          </td>
                        )}
                        <td style={{padding: 0, width: 30}}></td>
                        {showStatBreakdown && activeCompany.config?.pf_applicable && (
                          <td className="td-num" style={{color:"#0369a1", fontSize:12}}>{fmtINR(s.pf_emp||0)} / {fmtINR(s.pf_er !== undefined ? s.pf_er : (s.pf_employer||0))}</td>
                        )}
                        {showStatBreakdown && activeCompany.config?.esi_applicable && (
                          <td className="td-num" style={{color:"#0f766e", fontSize:12}}>{fmtINR(s.esi_emp||0)} / {fmtINR(s.esi_er !== undefined ? s.esi_er : (s.esi_employer||0))}</td>
                        )}
                        {showStatBreakdown && (
                          <td className="td-num" style={{color:"#475569", fontSize:12}}>{fmtINR(s.pt||0)}</td>
                        )}
                        <td style={{padding: 0, width: 30}}></td>
                        {showAllowancesBreakdown && !isNoDeduct && (
                          <td className="td-num" style={{color:"#dc2626", fontSize:12}}>{s.absence_deduction>0 ? "−"+fmtINR(s.absence_deduction) : "—"}</td>
                        )}
                        {showAllowancesBreakdown && (activeCompany?.config?.da || activeCompany?.config?.special_allowance || activeCompany?.config?.custom_special_allowance_enabled) && (
                          <td className="td-num" style={{color:"#ca8a04", fontSize:12}}>{fmtINR(s.special||0)}</td>
                        )}
                        {showAllowancesBreakdown && activeCompany?.config?.vpf_tds_enabled && (
                          <td className="td-num" style={{color:"#d97706", fontSize:12}}>{s.vpf > 0 ? fmtINR(s.vpf) : "—"}</td>
                        )}
                        {showAllowancesBreakdown && (activeCompany?.config?.vpf_tds_enabled || activeCompany?.config?.tds_applicable) && (
                          <td className="td-num" style={{color:"#4338ca", fontSize:12}}>{s.tds > 0 ? fmtINR(s.tds) : "—"}</td>
                        )}
                        {showAllowancesBreakdown && activeCompany?.config?.bonus && (
                          <td className="td-num" style={{color:"#6b21a8", fontSize:12}}>{fmtINR(s.bonus||0)}</td>
                        )}
                        <td style={{padding: 0, width: 30}}></td>
                        <td className="td-num" style={{color:"#1e293b",fontWeight:600, background:"#f1f5f9"}}>{fmtINR(s.gross||0)}</td>
                        <td className="td-net" style={{fontWeight:600, background:"#f0fdf4"}}>{fmtINR(s.net||0)}</td>
                        {showTotalBreakdown && (
                          <td className="td-num" style={{color:"#9333ea", fontWeight:600, background:"#faf5ff"}}>{fmtINR(s.ctc||0)}</td>
                        )}
                        <td>
                          <div style={{display: "flex", gap: 6, alignItems: "center", justifyContent:"center"}}>
                            <button className="btn btn-ghost btn-xs" onClick={()=>{
                              const pDays = (activeCompany.config?.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (activeCompany.config?.working_days||26)) - absDays;
                              exportPDF(e,{...s,present_days:pDays,absent_days:absDays,ot_hours:otH},activeCompany);
                            }}>📄 PDF</button>
                            <button className="btn btn-ghost btn-xs" title={`Send payslip to ${e.name} (${e.email || 'No email set'})`} onClick={() => {
                              const pDays = (activeCompany.config?.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (activeCompany.config?.working_days||26)) - absDays;
                              handleOpenSendPayslipEmail(e, {...s, present_days: pDays, absent_days: absDays, ot_hours: otH}, payMonth);
                            }} style={{fontSize: 14, padding: "0 4px"}}>
                              📧
                            </button>
                          </div>
                        </td>
                        <td></td>
                      </tr>);
                    })}
                    {payrollTotals && (() => {
                      let colSpanBeforeGross = 2; // # and Employee
                      colSpanBeforeGross += 1; // [＋] Salary
                      if (showSalaryBreakdown) colSpanBeforeGross += 2; // Basic, HRA
                      if (!isNoDeduct) colSpanBeforeGross += 2; // Working Days, Absent Days
                      if (activeCompany.config?.ot_applicable) colSpanBeforeGross += 1; // OT Hours
                      colSpanBeforeGross += 1; // [＋] Stat
                      if (showStatBreakdown) {
                        if (activeCompany.config?.pf_applicable) colSpanBeforeGross += 1;
                        if (activeCompany.config?.esi_applicable) colSpanBeforeGross += 1;
                        colSpanBeforeGross += 1; // PT
                      }
                      colSpanBeforeGross += 1; // [＋] Allowances
                      if (showAllowancesBreakdown) {
                        if (!isNoDeduct) colSpanBeforeGross += 1;
                        if (activeCompany?.config?.da || activeCompany?.config?.special_allowance || activeCompany?.config?.custom_special_allowance_enabled) colSpanBeforeGross += 1;
                        if (activeCompany?.config?.vpf_tds_enabled) colSpanBeforeGross += 1;
                        if (activeCompany?.config?.vpf_tds_enabled || activeCompany?.config?.tds_applicable) colSpanBeforeGross += 1;
                        if (activeCompany?.config?.bonus) colSpanBeforeGross += 1;
                      }
                      colSpanBeforeGross += 1; // [＋] Total

                      return (
                        <tr className="tot-row">
                          <td colSpan={colSpanBeforeGross} style={{textAlign:"right",paddingRight:"20px",color:"#fff",fontWeight:700}}>TOTAL</td>
                          <td className="td-num" style={{textAlign:"right"}}>{fmtINR(payrollTotals.gross)}</td>
                          <td className="td-net" style={{textAlign:"right",color:"#6fcf97"}}>{fmtINR(payrollTotals.net)}</td>
                          {showTotalBreakdown && <td className="td-num" style={{textAlign:"right",color:"#d8b4fe"}}>{fmtINR(payrollTotals.ctc)}</td>}
                          <td></td>
                          <td></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Export panel */}
              <div className="table-wrap">
                <div className="table-header"><div className="table-title">📤 Export & Deliverables</div></div>
                <div style={{padding:"16px 20px",display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                  <button className="btn btn-primary" onClick={()=>{
                    const cWd = activeCompany.config?.working_days === -1 ? new Date(payMonth.year, payMonth.month, 0).getDate() : (activeCompany.config?.working_days||26);
                    eligibleEmps.forEach(e=>exportPDF(e,{...payrollData[e.id],present_days:cWd-(payrollData[e.id]?.absent_days||0)},activeCompany));
                  }}>📄 All Salary Slips (PDF)</button>
                  <button className="btn btn-ghost" onClick={async ()=>{ await exportMasterCSV(eligibleEmps,payrollData,activeCompany,payMonth.month,payMonth.year); }}>📊 Master Excel</button>
                  {/* 
                  {activeCompany.config?.pf_applicable&&(
                    <button className="btn btn-ghost" onClick={()=>exportPFChallan(companyEmps,payrollData,pfStore,activeCompany,payMonth.month,payMonth.year)}>🏦 PF Challan</button>
                  )}
                  {activeCompany.config?.esi_applicable&&(
                    <button className="btn btn-ghost" onClick={()=>exportESIChallan(companyEmps,payrollData,esiStore,activeCompany,payMonth.month,payMonth.year)}>🏥 ESI Challan</button>
                  )}
                  <button className="btn btn-success" onClick={()=>exportInvoice(activeCompany,payMonth.month,payMonth.year,companyEmps,payrollData)}>🧾 Invoice</button>
                  */}
                  {["locked", "paid"].includes(payrollWorkflow.stage) && (
                    <>
                      <button className="btn btn-ghost" onClick={()=>exportESIMcTemplate(companyEmps,payrollData,esiStore,activeCompany,payMonth.month,payMonth.year)}>⬇ MC_Template (ESI)</button>
                      <button className="btn btn-ghost" onClick={()=>exportPFWagedata(companyEmps,payrollData,pfStore,activeCompany,payMonth.month,payMonth.year)}>⬇ Wagedata (PF)</button>
                      <button className="btn btn-ghost" onClick={()=>exportPFWagedataText(companyEmps,payrollData,pfStore,activeCompany,payMonth.month,payMonth.year)} title="Generates a TXT file without headers using #~# delimiter, ready for EPFO upload">⬇ Ready to Upload (PF)</button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── VIEW: SALARY STRUCTURE */}
      {view==="salary_structure" && activeCompany && (
        <SalaryConfigPage 
          company={activeCompany}
          onStyleSaved={handlePayslipStyleSaved}
          onSave={async (updated) => {
            const err = await saveCompany(updated);
            if (err) setFormMsg(err);
            else { 
              setFormMsg("Salary structure saved successfully!"); 
              setHasUnsavedChanges(false);
              setTimeout(()=>setFormMsg(""), 3000); 
            }
          }}
          onBack={() => safeNavigate(() => { setView("company_details"); setCompanyNav("profile"); })}
          isCompanyRole={isCompanyRole}
          msg={formMsg}
          setMsg={setFormMsg}
          onDirtyChange={setHasUnsavedChanges}
        />
      )}

      {/* ── VIEW: APPROVALS */}
      {view === "approvals" && activeCompany && (
        <div>
          {!isCompanyRole && <div className="back-btn" onClick={goBack}>← Back</div>}
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16}}>
            <div>
              <div className="page-title" style={{marginBottom: 4}}>{companyNav === "leave_management" ? "🏖️ Leave Management" : "⏳ Approvals"}</div>
              <div className="page-sub" style={{marginBottom: 0}}>{companyNav === "leave_management" ? "Manage leave requests, balances, allotments and history." : "Review leave, attendance corrections, profile updates and certificate requests."}</div>
            </div>
            <button
              className="btn btn-ghost"
              style={{display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "8px 16px", borderRadius: 8, background: "#f8fafc", border: "1.5px solid #cbd5e1", cursor: "pointer", fontWeight: 700, color: "#1e293b", boxShadow: "0 1px 2px rgba(0,0,0,0.05)"}}
              onClick={async () => {
                try {
                  if (activeCompany?.id) {
                    await Promise.all([
                      loadEmployees(activeCompany.id),
                      refreshApprovals(),
                      loadCompanies()
                    ]);
                    setAdminToast("Approvals and employee records refreshed!");
                  }
                } catch(e) {
                  console.error("Refresh error:", e);
                }
              }}
              title="Reload approvals and employee records from database"
            >
              🔄 Refresh Data
            </button>
          </div>

          {companyNav !== "leave_management" && Object.entries(activeCompany?.config?.payroll_workflows || {}).filter(([, run]) => run.stage === "review").map(([period, run]) => (
            <div key={period} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",padding:16,background:"#f0fdfa",border:"1px solid #99f6e4",borderRadius:10,marginBottom:12}}>
              <div><strong style={{color:"#115e59"}}>Payroll awaiting approval · {period}</strong><div style={{fontSize:12,color:"#64748b",marginTop:4}}>Submitted by {run.updated_by}</div></div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={()=>{const [year, month] = period.split("-").map(Number);setCompanyNav("payroll");setView("payroll");setEditAttRow({});setPayMonth({year,month});}}>Review Payroll</button>
            </div>
          ))}

          {/* Suboptions Navigation Tabs */}
          {companyNav !== "leave_management" && <div style={{display: "flex", gap: 8, borderBottom: "2px solid #e2e8f0", marginBottom: 24}}>
            <button
              onClick={() => setApprovalsTab("leave_attendance")}
              style={{
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 700,
                border: "none",
                borderBottom: approvalsTab === "leave_attendance" ? "3px solid #2563eb" : "3px solid transparent",
                background: approvalsTab === "leave_attendance" ? "#eff6ff" : "transparent",
                color: approvalsTab === "leave_attendance" ? "#1d4ed8" : "#64748b",
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: -2,
                transition: "all 0.15s ease"
              }}
            >
              <span>🏖️ Leave & Attendance</span>
              {(pendingLeaves.length + pendingRegularizations.length) > 0 && (
                <span style={{background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12}}>
                  {pendingLeaves.length + pendingRegularizations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setApprovalsTab("profile")}
              style={{
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 700,
                border: "none",
                borderBottom: approvalsTab === "profile" ? "3px solid #2563eb" : "3px solid transparent",
                background: approvalsTab === "profile" ? "#eff6ff" : "transparent",
                color: approvalsTab === "profile" ? "#1d4ed8" : "#64748b",
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: -2,
                transition: "all 0.15s ease"
              }}
            >
              <span>👤 Profile Updates</span>
              {pendingApprovals.filter(r => r.requested_data?.request_type !== "certificate").length > 0 && (
                <span style={{background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12}}>
                  {pendingApprovals.filter(r => r.requested_data?.request_type !== "certificate").length}
                </span>
              )}
            </button>
            <button
              onClick={() => setApprovalsTab("certificates")}
              style={{
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 700,
                border: "none",
                borderBottom: approvalsTab === "certificates" ? "3px solid #2563eb" : "3px solid transparent",
                background: approvalsTab === "certificates" ? "#eff6ff" : "transparent",
                color: approvalsTab === "certificates" ? "#1d4ed8" : "#64748b",
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: -2,
                transition: "all 0.15s ease"
              }}
            >
              <span>📜 Certificate Requests</span>
              {pendingApprovals.filter(r => r.requested_data?.request_type === "certificate").length > 0 && (
                <span style={{background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12}}>
                  {pendingApprovals.filter(r => r.requested_data?.request_type === "certificate").length}
                </span>
              )}
            </button>
          </div>}
          
          {(() => {
            const pendingProfiles = pendingApprovals.filter(r => r.requested_data?.request_type !== "certificate");
            const pendingCertificates = pendingApprovals.filter(r => r.requested_data?.request_type === "certificate");
            const companyEmps = employees[activeCompany.id] || [];

            // Filter company-wide leaves
            const filteredCompanyLeaves = allCompanyLeaves.filter(req => {
              if (adminLeaveFilterEmp !== "all" && String(req.employee_id) !== String(adminLeaveFilterEmp)) return false;
              if (adminLeaveFilterStatus !== "all" && req.status !== adminLeaveFilterStatus) return false;
              if (adminLeaveFilterYear !== "all") {
                const yr = new Date(req.start_date).getFullYear().toString();
                if (yr !== adminLeaveFilterYear) return false;
              }
              return true;
            });

            const companyLeaveYears = Array.from(new Set([
              String(new Date().getFullYear()),
              ...allCompanyLeaves.map(r => new Date(r.start_date).getFullYear().toString())
            ])).sort().reverse();
            
            const renderEmptyApprovalState = (title, subtitle) => (
              <div style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "36px 24px",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
              }}>
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  border: "1.5px solid #a7f3d0",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.12)"
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div style={{fontWeight: 700, fontSize: 16, color: "#1e293b"}}>{title}</div>
                <div style={{color: "#64748b", marginTop: 4, fontSize: 13}}>{subtitle}</div>
              </div>
            );

            return (
              <div style={{display: "flex", flexDirection: "column", gap: 32}}>
                
                {/* ─── TAB 1: LEAVE & ATTENDANCE APPROVAL ─── */}
                {(companyNav === "leave_management" || approvalsTab === "leave_attendance") && (
                  <div>
                    {(activeCompany.config?.leave_module_enabled !== true) ? (
                      <div style={{background:"#fff", border:"1px solid #e2e8f0", borderRadius: 12, padding: 32, textAlign: "center", color: "#64748b"}}>
                        Leave module is currently disabled for this company. You can enable it in Company Profile &gt; ✨ Add-on Features.
                      </div>
                    ) : (
                      <div style={{display: "flex", flexDirection: "column", gap: 32}}>
                        <div>
                          <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                            <span>🏖️ Pending Leave Requests</span>
                            <span style={{fontSize:12,fontWeight:700,background:pendingLeaves.length > 0 ? "#fee2e2" : "#f1f5f9",color:pendingLeaves.length > 0 ? "#dc2626" : "#64748b",padding:"2px 8px",borderRadius:12}}>
                              {pendingLeaves.length}
                            </span>
                          </div>

                          {pendingLeaves.length === 0 ? (
                            renderEmptyApprovalState("No pending leave requests", "All employee leave applications have been reviewed.")
                          ) : (
                            <div style={{display: "flex", flexDirection: "column", gap: 14}}>
                              {pendingLeaves.map(req => {
                                const empFromStore = (employees[activeCompany?.id] || []).find(e => e.id === req.employee_id) || {};
                                const empObj = { id: req.employee_id, ...empFromStore, ...(req.employees || {}) };
                                const empCode = empObj.employee_code || "N/A";
                                const empName = empObj.name || "Employee";

                                // Accurate Quota & Balance Calculation using unified helper
                                const currentYear = new Date(req.start_date || new Date()).getFullYear();
                                const m = getEmployeeLeaveMetrics(empObj, activeCompany?.config, allCompanyLeaves, req.leave_type, currentYear, req.id);

                                const totalQuota = (m && typeof m.totalQuota === "number" && !isNaN(m.totalQuota)) ? m.totalQuota : (parseFloat(m?.totalQuota) || 0);
                                const rawBal = (m && typeof m.remainingBalance === "number" && !isNaN(m.remainingBalance))
                                  ? m.remainingBalance
                                  : (m && typeof m.availableBalBeforeReview === "number" && !isNaN(m.availableBalBeforeReview))
                                    ? m.availableBalBeforeReview
                                    : (m && typeof m.balanceBeforePending === "number" && !isNaN(m.balanceBeforePending))
                                      ? m.balanceBeforePending
                                      : 0;
                                const currentBal = Math.max(0, rawBal);
                                const reqDays = parseFloat(req.days) || 0;
                                const balAfter = Math.max(0, currentBal - reqDays);
                                const exceedsQuota = reqDays > currentBal;

                                return (
                                  <div key={req.id} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 22, boxShadow: "0 1px 3px rgba(0,0,0,0.05)"}}>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 16}}>
                                      <div>
                                        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                                          <span style={{fontWeight: 800, fontSize: 17, color: "#0f172a"}}>{empName}</span>
                                          <span style={{fontSize: 12, fontWeight: 700, padding: "3px 9px", background: "#f1f5f9", color: "#334155", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                                            ID: {empCode}
                                          </span>
                                          <span style={{fontSize: 12, fontWeight: 700, padding: "3px 10px", background: "#eff6ff", color: "#2563eb", borderRadius: 12, border: "1px solid #bfdbfe"}}>
                                            {req.leave_type}
                                          </span>
                                        </div>
                                        <div style={{fontSize: 13, color: "#475569", marginTop: 6}}>
                                          📅 <strong>{new Date(req.start_date).toLocaleDateString()}</strong> to <strong>{new Date(req.end_date).toLocaleDateString()}</strong> ({req.days} {parseFloat(req.days) === 1 ? "day" : "days"})
                                        </div>
                                      </div>

                                      <div style={{display: "flex", gap: 10}}>
                                        <button onClick={async () => {
                                          if (!window.confirm("Reject this leave request?")) return;
                                          const note = window.prompt("Optional reason for rejection (visible to employee):") || null;
                                          const {error} = await supabase.from("employee_leaves").update({
                                            status: 'rejected',
                                            rejection_reason: note,
                                            reviewed_at: new Date().toISOString(),
                                            reviewed_by: currentUser.username || currentUser.role
                                          }).eq("id", req.id);
                                          if(error) alert(error.message); else {
                                            setPendingLeaves(p => p.filter(x => x.id !== req.id));
                                            setAllPendingLeaves(p => p.filter(x => x.id !== req.id));
                                            setAllCompanyLeaves(p => [{...req, status: 'rejected', rejection_reason: note, reviewed_at: new Date().toISOString()}, ...p.filter(x => x.id !== req.id)]);
                                            logAudit(currentUser, "ADMIN_REJECTED_LEAVE", { employee_id: req.employee_id, leave_id: req.id, reason: note });
                                          }
                                        }} style={{background:"#fef2f2", color:"#dc2626", border:"1.5px solid #fecaca", borderRadius:8, padding:"8px 18px", cursor:"pointer", fontWeight:700, fontSize:13, transition:"all 0.15s"}}>Reject</button>
                                        <button onClick={async () => {
                                          if (!window.confirm("Approve this leave request?")) return;
                                          const {error} = await supabase.from("employee_leaves").update({
                                            status: 'approved',
                                            reviewed_at: new Date().toISOString(),
                                            reviewed_by: currentUser.username || currentUser.role
                                          }).eq("id", req.id);
                                          if(error) alert(error.message); else {
                                            setPendingLeaves(p => p.filter(x => x.id !== req.id));
                                            setAllPendingLeaves(p => p.filter(x => x.id !== req.id));
                                            setAllCompanyLeaves(p => [{...req, status: 'approved', reviewed_at: new Date().toISOString()}, ...p.filter(x => x.id !== req.id)]);
                                            logAudit(currentUser, "ADMIN_APPROVED_LEAVE", { employee_id: req.employee_id, leave_id: req.id, days: req.days, leave_type: req.leave_type });
                                          }
                                        }} style={{background:"#16a34a", color:"#ffffff", border:"1px solid #15803d", borderRadius:8, padding:"8px 20px", cursor:"pointer", fontWeight:700, fontSize:13, boxShadow:"0 1px 2px rgba(0,0,0,0.1)", transition:"all 0.15s"}}>Approve</button>
                                      </div>
                                    </div>

                                    {/* Detailed Quota & Balance Metric Cards */}
                                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, background: "#f8fafc", padding: "12px 16px", borderRadius: 10, border: "1px solid #e2e8f0"}}>
                                      <div>
                                        <div style={{fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"}}>Employee ID</div>
                                        <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginTop: 2}}>{empCode}</div>
                                      </div>
                                      <div>
                                        <div style={{fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"}}>Total Leave ({currentYear})</div>
                                        <div style={{fontSize: 15, fontWeight: 800, color: "#1e293b", marginTop: 2}}>{totalQuota} <span style={{fontSize: 12, fontWeight: 600, color: "#64748b"}}>days</span></div>
                                      </div>
                                      <div>
                                        <div style={{fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"}}>Leave Balance</div>
                                        <div style={{fontSize: 15, fontWeight: 800, color: currentBal > 0 ? "#16a34a" : "#dc2626", marginTop: 2}}>
                                          {currentBal} <span style={{fontSize: 12, fontWeight: 600, color: "#64748b"}}>days</span>
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"}}>Requested Days</div>
                                        <div style={{fontSize: 15, fontWeight: 800, color: "#2563eb", marginTop: 2}}>{req.days} <span style={{fontSize: 12, fontWeight: 600, color: "#64748b"}}>days</span></div>
                                      </div>
                                      <div>
                                        <div style={{fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"}}>Balance After</div>
                                        <div style={{fontSize: 15, fontWeight: 800, color: exceedsQuota ? "#dc2626" : "#475569", marginTop: 2}}>
                                          {balAfter} <span style={{fontSize: 12, fontWeight: 600, color: "#64748b"}}>days</span>
                                        </div>
                                      </div>
                                    </div>

                                    {exceedsQuota && (
                                      <div style={{marginTop: 10, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, color: "#dc2626", fontWeight: 600, display: "flex", alignItems: "center", gap: 6}}>
                                        <span>⚠️</span>
                                        <span>Notice: This request exceeds the employee's current balance by {Math.max(0, reqDays - currentBal).toFixed(1)} days.</span>
                                      </div>
                                    )}

                                    {req.reason && (
                                      <div style={{marginTop: 12, fontSize: 13, color: "#475569", background: "#ffffff", padding: "8px 12px", borderRadius: 6, border: "1px solid #f1f5f9"}}>
                                        <span style={{fontWeight: 700, color: "#334155"}}>Reason:</span> <span style={{fontStyle: "italic"}}>"{req.reason}"</span>
                                      </div>
                                    )}

                                    <div style={{fontSize: 11, color: "#94a3b8", marginTop: 10}}>
                                      Applied on: {new Date(req.created_at).toLocaleString()}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* ─── PENDING ATTENDANCE REGULARIZATIONS ─── */}
                        {(activeCompany.config?.attendance_enabled === true) && (
                          <div style={{marginTop: 8}}>
                            <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                              <span>🔄 Pending Attendance Regularizations</span>
                              <span style={{fontSize:12,fontWeight:700,background:pendingRegularizations.length > 0 ? "#fef3c7" : "#f1f5f9",color:pendingRegularizations.length > 0 ? "#b45309" : "#64748b",padding:"2px 8px",borderRadius:12}}>
                                {pendingRegularizations.length}
                              </span>
                            </div>

                            {pendingRegularizations.length === 0 ? (
                              renderEmptyApprovalState("No pending attendance regularizations", "All employee punch regularization requests have been reviewed.")
                            ) : (
                              <div style={{display: "flex", flexDirection: "column", gap: 16}}>
                                {pendingRegularizations.map(reg => {
                                  const regDateStr = reg.date ? new Date(reg.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : reg.date;
                                  const punchInStr = reg.punch_in ? new Date(reg.punch_in).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true }) : "None";
                                  const punchOutStr = reg.punch_out ? new Date(reg.punch_out).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true }) : "None";

                                  return (
                                    <div key={reg.id} style={{background: "#fff", border: "1.5px solid #fed7aa", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(249,115,22,0.08)"}}>
                                      <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 12}}>
                                        <div>
                                          <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                                            <span style={{fontWeight: 800, fontSize: 16, color: "#0f172a"}}>{reg.employees?.name || "Employee"}</span>
                                            <span style={{fontSize: 12, fontWeight: 700, background: "#fef3c7", color: "#9a3412", padding: "2px 8px", borderRadius: 6}}>
                                              {reg.employees?.employee_code || "EMP"}
                                            </span>
                                            {reg.employees?.department && (
                                              <span style={{fontSize: 12, color: "#64748b"}}>• {reg.employees.department}</span>
                                            )}
                                          </div>
                                          <div style={{color: "#475569", fontSize: 13, marginTop: 4, fontWeight: 600}}>
                                            📅 Requested Date: <span style={{color: "#1e293b", fontWeight: 700}}>{regDateStr}</span>
                                          </div>
                                        </div>

                                        <div style={{display: "flex", gap: 8}}>
                                          <button
                                            onClick={async () => {
                                              const { error } = await supabase.from("attendance_records")
                                                .update({
                                                  regularization_status: "rejected",
                                                  updated_at: new Date().toISOString()
                                                })
                                                .eq("id", reg.id);
                                              if (error) alert("Error rejecting regularization: " + error.message);
                                              else {
                                                setPendingRegularizations(p => p.filter(x => x.id !== reg.id));
                                                setAllPendingRegularizations(p => p.filter(x => x.id !== reg.id));
                                                logAudit(currentUser, "ADMIN_REJECTED_REGULARIZATION", { employee_id: reg.employee_id, date: reg.date });
                                                refreshApprovals();
                                              }
                                            }}
                                            style={{background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontWeight:700, fontSize:13}}
                                          >
                                            Reject
                                          </button>
                                          <button
                                            onClick={async () => {
                                              const { error } = await supabase.from("attendance_records")
                                                .update({
                                                  regularization_status: "approved",
                                                  status: "regularized",
                                                  updated_at: new Date().toISOString()
                                                })
                                                .eq("id", reg.id);
                                              if (error) alert("Error approving regularization: " + error.message);
                                              else {
                                                setPendingRegularizations(p => p.filter(x => x.id !== reg.id));
                                                setAllPendingRegularizations(p => p.filter(x => x.id !== reg.id));
                                                logAudit(currentUser, "ADMIN_APPROVED_REGULARIZATION", { employee_id: reg.employee_id, date: reg.date });
                                                refreshApprovals();
                                              }
                                            }}
                                            style={{background:"#16a34a", color:"#ffffff", border:"1px solid #15803d", borderRadius:8, padding:"8px 18px", cursor:"pointer", fontWeight:700, fontSize:13, boxShadow:"0 1px 2px rgba(0,0,0,0.1)"}}
                                          >
                                            Approve as Regularized
                                          </button>
                                        </div>
                                      </div>

                                      {/* Reason & Punch Info */}
                                      <div style={{background: "#fff7ed", border: "1px dashed #fdba74", borderRadius: 8, padding: 12, marginBottom: 10}}>
                                        <div style={{fontSize: 11, fontWeight: 700, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4}}>
                                          Employee's Stated Reason:
                                        </div>
                                        <div style={{fontSize: 13, color: "#431407", fontStyle: "italic"}}>
                                          "{reg.regularization_reason || "Missed punch regularization requested"}"
                                        </div>
                                      </div>

                                      <div style={{display: "flex", gap: 16, fontSize: 12, color: "#64748b", flexWrap: "wrap", alignItems: "center"}}>
                                        <span>Current Status: <strong style={{color: "#1e293b", textTransform: "capitalize"}}>{reg.status || "Absent / Missed"}</strong></span>
                                        <span>Recorded In: <strong style={{color: "#1e293b"}}>{punchInStr}</strong></span>
                                        <span>Recorded Out: <strong style={{color: "#1e293b"}}>{punchOutStr}</strong></span>
                                        <span style={{marginLeft: "auto", fontSize: 11, color: "#94a3b8"}}>ID: {reg.id?.substring(0, 8)}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ─── EMPLOYEE LEAVE BALANCES OVERVIEW ─── */}
                        <div className="profile-accordion-card">
                          <div
                            className="profile-accordion-header indigo"
                            onClick={() => setShowEmpLeaveBalances(!showEmpLeaveBalances)}
                            title={showEmpLeaveBalances ? "Click to collapse Leave Balances" : "Click to expand Leave Balances"}
                          >
                            <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                              <span style={{fontSize: 16}}>👥</span>
                              <span style={{fontSize: 13, fontWeight: 800, color: "#4338ca", letterSpacing: 0.5}}>EMPLOYEE LEAVE BALANCES & ALLOTMENT</span>
                              <span className="profile-accordion-tag" style={{background: "#e0e7ff", color: "#3730a3"}}>
                                {companyEmps.length} Employees · Year {new Date().getFullYear()}
                              </span>
                            </div>
                            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                              <button
                                className="btn btn-sm"
                                onClick={(e) => { e.stopPropagation(); downloadLeaveBalancesExcel(); }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  background: "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(241, 245, 249, 0.9) 50%, rgba(226, 232, 240, 0.95) 100%)",
                                  color: "#1e293b",
                                  border: "1.5px solid #cbd5e1",
                                  borderRadius: 8,
                                  padding: "5px 12px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                                  backdropFilter: "blur(8px)",
                                  WebkitBackdropFilter: "blur(8px)",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = "linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%)";
                                  e.currentTarget.style.borderColor = "#94a3b8";
                                  e.currentTarget.style.transform = "translateY(-1px)";
                                  e.currentTarget.style.boxShadow = "0 3px 8px rgba(15,23,42,0.08), inset 0 1px 0 #ffffff";
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(241, 245, 249, 0.9) 50%, rgba(226, 232, 240, 0.95) 100%)";
                                  e.currentTarget.style.borderColor = "#cbd5e1";
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)";
                                }}
                                title="Download all employee leave balances and allotments to Excel"
                              >
                                <span>📥</span>
                                <span>Export Excel</span>
                              </button>
                              <button
                                className="profile-accordion-btn indigo"
                                onClick={(e) => { e.stopPropagation(); setShowEmpLeaveBalances(!showEmpLeaveBalances); }}
                              >
                                <span>{showEmpLeaveBalances ? "Collapse" : "Expand"}</span>
                                <span style={{fontSize: 14, lineHeight: 1}}>{showEmpLeaveBalances ? "−" : "＋"}</span>
                              </button>
                            </div>
                          </div>

                          {showEmpLeaveBalances && (
                            <div style={{padding: 20}}>
                              {(() => {
                                const filteredEmps = companyEmps.filter(e => {
                                  if (!adminEmpBalSearch.trim()) return true;
                                  const q = adminEmpBalSearch.toLowerCase();
                                  return (e.name || "").toLowerCase().includes(q) || (e.employee_code || "").toLowerCase().includes(q) || (e.department || "").toLowerCase().includes(q);
                                });

                                const sortedFilteredEmps = [...filteredEmps].sort((a, b) => {
                                  const nameA = (a.name || "").trim().toLowerCase();
                                  const nameB = (b.name || "").trim().toLowerCase();
                                  return adminEmpBalSortDir === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                                });

                                const cfgLeaveTypes = (activeCompany.config?.leave_types && activeCompany.config?.leave_types.length > 0)
                                  ? activeCompany.config.leave_types
                                  : [{ name: "Casual Leave", days: 12 }, { name: "Sick Leave", days: 6 }];

                                return (
                                  <>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16}}>
                                      <div style={{fontSize: 12, color: "#64748b"}}>
                                        Annual allotted quota, approved days taken, and current remaining balance for Year {new Date().getFullYear()}.
                                      </div>
                                      <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                                        <input
                                          type="text"
                                          placeholder="🔍 Search employee name or ID..."
                                          value={adminEmpBalSearch}
                                          onChange={e => setAdminEmpBalSearch(e.target.value)}
                                          className="config-input"
                                          style={{width: 240, height: 32, padding: "2px 10px", fontSize: 12, margin: 0}}
                                        />
                                        <button
                                          onClick={() => downloadLeaveBalancesExcel(sortedFilteredEmps)}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            background: "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(241, 245, 249, 0.9) 50%, rgba(226, 232, 240, 0.95) 100%)",
                                            color: "#1e293b",
                                            border: "1.5px solid #cbd5e1",
                                            borderRadius: 8,
                                            padding: "6px 14px",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: "pointer",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                                            backdropFilter: "blur(8px)",
                                            WebkitBackdropFilter: "blur(8px)",
                                            transition: "all 0.15s ease"
                                          }}
                                          onMouseEnter={e => {
                                            e.currentTarget.style.background = "linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%)";
                                            e.currentTarget.style.borderColor = "#94a3b8";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                            e.currentTarget.style.boxShadow = "0 3px 8px rgba(15,23,42,0.08), inset 0 1px 0 #ffffff";
                                          }}
                                          onMouseLeave={e => {
                                            e.currentTarget.style.background = "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(241, 245, 249, 0.9) 50%, rgba(226, 232, 240, 0.95) 100%)";
                                            e.currentTarget.style.borderColor = "#cbd5e1";
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)";
                                          }}
                                          title="Export employee leave allotments, used leaves, and balances to Excel"
                                        >
                                          <span>📥</span>
                                          <span>Download Excel</span>
                                        </button>
                                      </div>
                                    </div>

                                    {sortedFilteredEmps.length === 0 ? (
                                      <div style={{padding: 24, textAlign: "center", color: "#64748b", fontSize: 13}}>
                                        {companyEmps.length === 0 ? "No employees found in this company." : "No employees match your search."}
                                      </div>
                                    ) : (
                                  <div style={{overflowX: "auto"}}>
                                    <table style={{width: "100%", borderCollapse: "collapse", fontSize: 13}}>
                                      <thead>
                                        <tr style={{background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", textAlign: "left"}}>
                                          <th
                                            onClick={() => setAdminEmpBalSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                            style={{padding: "10px 14px", fontWeight: 700, color: "#1e293b", cursor: "pointer", userSelect: "none"}}
                                            title="Click to sort by Employee Name"
                                          >
                                            <div style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                                              <span>Employee</span>
                                              <span style={{fontSize: 11, color: "#4f46e5", background: "#eef2ff", padding: "1px 6px", borderRadius: 4, fontWeight: 800}}>
                                                {adminEmpBalSortDir === 'asc' ? 'A → Z ▲' : 'Z → A ▼'}
                                              </span>
                                            </div>
                                          </th>
                                          <th style={{padding: "10px 14px", fontWeight: 700, color: "#475569"}}>Joining Date</th>
                                          {cfgLeaveTypes.map(lt => (
                                            <th key={lt.name} style={{padding: "10px 14px", fontWeight: 700, color: "#475569", textAlign: "center"}}>
                                              {lt.name}
                                              <div style={{fontSize: 10, fontWeight: 600, color: "#94a3b8"}}>Allotted / Used / Bal</div>
                                            </th>
                                          ))}
                                          <th style={{padding: "10px 14px", fontWeight: 700, color: "#475569", textAlign: "right"}}>History</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sortedFilteredEmps.map(emp => {
                                          const dojStr = emp.date_of_joining || emp.doj;
                                          const formattedDoj = dojStr ? new Date(dojStr).toLocaleDateString() : "—";
                                          return (
                                            <tr key={emp.id} style={{borderBottom: "1px solid #f1f5f9"}}>
                                              <td style={{padding: "8px 14px",whiteSpace:"nowrap"}}>
                                                <span style={{fontSize:12,color:"#64748b",marginRight:8}}>{emp.employee_code || "N/A"}</span>
                                                <span style={{fontWeight:700,color:"#0f172a"}}>{emp.name}</span>
                                              </td>
                                              <td style={{padding: "8px 14px", color: "#64748b", fontSize: 12, whiteSpace: "nowrap"}}>
                                                {formattedDoj}
                                              </td>
                                              {cfgLeaveTypes.map(lt => {
                                                const m = getEmployeeLeaveMetrics(emp, activeCompany.config, allCompanyLeaves, lt.name, new Date().getFullYear());
                                                return (
                                                  <td key={lt.name} style={{padding: "8px 14px", textAlign: "center"}}>
                                                    <div
                                                      title={`Allotted: ${m.totalQuota}d | Approved Used: ${m.approvedUsed}d | Balance: ${m.remainingBalance}d${m.pendingUsed > 0 ? ` | Pending: ${m.pendingUsed}d` : ''}`}
                                                      style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        background: "#f8fafc",
                                                        padding: "4px 10px",
                                                        borderRadius: 8,
                                                        border: "1px solid #e2e8f0",
                                                        fontSize: 12
                                                      }}
                                                    >
                                                      <span title="Allotted" style={{fontWeight: 700, color: "#1e293b"}}>{m.totalQuota}</span>
                                                      <span style={{color: "#cbd5e1"}}>/</span>
                                                      <span title="Used" style={{color: "#64748b", fontWeight: 600}}>{m.approvedUsed}</span>
                                                      <span style={{color: "#cbd5e1"}}>/</span>
                                                      <span style={{
                                                        fontWeight: 800,
                                                        padding: "1px 6px",
                                                        borderRadius: 4,
                                                        background: m.remainingBalance > 0 ? "#ecfdf5" : "#fef2f2",
                                                        color: m.remainingBalance > 0 ? "#059669" : "#dc2626"
                                                      }}>
                                                        {m.remainingBalance}
                                                      </span>
                                                      {m.pendingUsed > 0 && (
                                                        <span title={`Pending Approval: ${m.pendingUsed}d`} style={{fontSize: 10, fontWeight: 700, background: "#fef9c3", color: "#854d0e", padding: "1px 4px", borderRadius: 4, marginLeft: 2}}>
                                                          ({m.pendingUsed}p)
                                                        </span>
                                                      )}
                                                    </div>
                                                  </td>
                                                );
                                              })}
                                              <td style={{padding: "8px 14px", textAlign: "right"}}>
                                                <button
                                                  type="button"
                                                  title="Open leave requests for this employee; reset shows all employees"
                                                  onClick={() => viewEmployeeLeaveRecords(emp.id)}
                                                  style={{
                                                    background: adminLeaveFilterEmp === String(emp.id) ? "#eff6ff" : "#f8fafc",
                                                    border: adminLeaveFilterEmp === String(emp.id) ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                                                    color: adminLeaveFilterEmp === String(emp.id) ? "#2563eb" : "#475569",
                                                    borderRadius: 6,
                                                    padding: "4px 10px",
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    cursor: "pointer"
                                                  }}
                                                >
                                                  {adminLeaveFilterEmp === String(emp.id) ? "Reset Filter ✕" : "View Leave Requests"}
                                                </button>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                            </div>
                          )}
                        </div>

                        {/* Company-Wide Leave Records Audit Section */}
                        <div ref={companyLeaveRecordsRef} className="profile-accordion-card" style={{marginTop: 8,scrollMarginTop:24}}>
                          <div
                            className="profile-accordion-header slate"
                            onClick={() => setShowCompanyLeaveRecords(!showCompanyLeaveRecords)}
                            title={showCompanyLeaveRecords ? "Click to collapse Company Leave Records" : "Click to expand Company Leave Records"}
                          >
                            <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                              <span style={{fontSize: 16}}>📋</span>
                              <span style={{fontSize: 13, fontWeight: 800, color: "#334155", letterSpacing: 0.5}}>COMPANY LEAVE RECORDS</span>
                              <span className="profile-accordion-tag" style={{background: "#e2e8f0", color: "#1e293b"}}>
                                {filteredCompanyLeaves.length} Records
                              </span>
                              {adminLeaveFilterEmp !== "all" && (
                                <span style={{fontSize:12,color:"#2563eb",fontWeight:600}}>
                                  For {companyEmps.find(emp => String(emp.id) === String(adminLeaveFilterEmp))?.name || "selected employee"}
                                </span>
                              )}
                            </div>
                            <button
                              className="profile-accordion-btn slate"
                              onClick={(e) => { e.stopPropagation(); setShowCompanyLeaveRecords(!showCompanyLeaveRecords); }}
                            >
                              <span>{showCompanyLeaveRecords ? "Collapse" : "Expand"}</span>
                              <span style={{fontSize: 14, lineHeight: 1}}>{showCompanyLeaveRecords ? "−" : "＋"}</span>
                            </button>
                          </div>

                          {showCompanyLeaveRecords && (
                            <div style={{padding: 20}}>
                              <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16}}>
                                <div style={{display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"}}>
                                  <HRNotificationAction company={activeCompany} label="Notify selected leave decisions" targets={filteredCompanyLeaves.filter(request=>selectedLeaveRecordIds.includes(request.id)&&['approved','rejected'].includes(request.status)).map(request=>({kind:'leave_decision',record_id:request.id,employee_id:request.employee_id,label:request.employees?.name||'Employee'}))}/>
                                  {filteredCompanyLeaves.length > 0 && (
                                    <label style={{display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#475569", cursor: "pointer", userSelect: "none"}}>
                                      <input
                                        type="checkbox"
                                        checked={selectedLeaveRecordIds.length > 0 && selectedLeaveRecordIds.length === filteredCompanyLeaves.length}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedLeaveRecordIds(filteredCompanyLeaves.map(r => r.id));
                                          } else {
                                            setSelectedLeaveRecordIds([]);
                                          }
                                        }}
                                      />
                                      Select All
                                    </label>
                                  )}
                                  {selectedLeaveRecordIds.length > 0 && (
                                    <button
                                      className="btn btn-sm"
                                      style={{background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 700, padding: "4px 12px", fontSize: 12}}
                                      onClick={async () => {
                                        if (!window.confirm(`Are you sure you want to delete ${selectedLeaveRecordIds.length} selected leave record(s)?`)) return;
                                        const count = selectedLeaveRecordIds.length;
                                        const ids = [...selectedLeaveRecordIds];
                                        const { error } = await supabase.from("employee_leaves").delete().in("id", selectedLeaveRecordIds);
                                        if (error) alert("Error deleting records: " + error.message);
                                        else {
                                          logAudit(currentUser, "ADMIN_BULK_DELETED_LEAVE_RECORDS", {
                                            record_count: count,
                                            leave_ids: ids
                                          });
                                          setSelectedLeaveRecordIds([]);
                                          refreshApprovals();
                                        }
                                      }}
                                    >
                                      🗑️ Delete Selected ({selectedLeaveRecordIds.length})
                                    </button>
                                  )}
                                </div>

                                <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                                  <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={adminLeaveFilterEmp} onChange={e => setAdminLeaveFilterEmp(e.target.value)}>
                                    <option value="all">All Employees</option>
                                    {[...companyEmps].sort((a,b) => {
                                      const valA = (a.employee_code ?? a.emp_id ?? a.employee_id ?? '').toString().trim();
                                      const valB = (b.employee_code ?? b.emp_id ?? b.employee_id ?? '').toString().trim();
                                      if (!valA && !valB) return (a.name || '').localeCompare(b.name || '');
                                      if (!valA) return 1;
                                      if (!valB) return -1;
                                      const cmp = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
                                      return cmp !== 0 ? cmp : (a.name || '').localeCompare(b.name || '');
                                    }).map(emp => <option key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</option>)}
                                  </select>
                                  <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={adminLeaveFilterStatus} onChange={e => setAdminLeaveFilterStatus(e.target.value)}>
                                    <option value="all">All Statuses</option>
                                    <option value="approved">Approved</option>
                                    <option value="pending">Pending</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="cancelled">Cancelled</option>
                                  </select>
                                  <select className="config-input" style={{width: "auto", height: 32, padding: "2px 8px", fontSize: 12, margin: 0}} value={adminLeaveFilterYear} onChange={e => setAdminLeaveFilterYear(e.target.value)}>
                                    <option value="all">All Years</option>
                                    {companyLeaveYears.map(yr => <option key={yr} value={yr}>{yr}</option>)}
                                  </select>
                                  {(adminLeaveFilterEmp !== "all" || adminLeaveFilterStatus !== "all" || adminLeaveFilterYear !== "all") && (
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                                      setAdminLeaveFilterEmp("all");
                                      setAdminLeaveFilterStatus("all");
                                      setAdminLeaveFilterYear("all");
                                      setSelectedLeaveRecordIds([]);
                                    }}>Reset Filters ✕</button>
                                  )}
                                </div>
                              </div>

                              {filteredCompanyLeaves.length === 0 ? (
                                <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, textAlign: "center", color: "#64748b"}}>
                                  No leave records match the selected filters.
                                </div>
                              ) : (
                                <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                                  {filteredCompanyLeaves.map(req => {
                                    const statusBg = req.status === "approved" ? "#dcfce7" : req.status === "rejected" ? "#fee2e2" : req.status === "cancelled" ? "#f1f5f9" : "#fef9c3";
                                    const statusColor = req.status === "approved" ? "#16a34a" : req.status === "rejected" ? "#dc2626" : req.status === "cancelled" ? "#64748b" : "#ca8a04";

                                    const empFromStore = (employees[activeCompany?.id] || []).find(e => e.id === req.employee_id) || {};
                                    const empObj = { ...empFromStore, ...(req.employees || {}) };
                                    const reqYear = new Date(req.start_date || new Date()).getFullYear();
                                    const m = getEmployeeLeaveMetrics(empObj, activeCompany?.config, allCompanyLeaves, req.leave_type, reqYear);
                                    const isSelected = selectedLeaveRecordIds.includes(req.id);

                                    return (
                                      <div key={req.id} style={{background: isSelected ? "#f8fafc" : "#fff", border: isSelected ? "1.5px solid #93c5fd" : "1px solid #e2e8f0", borderRadius: 10, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12}}>
                                        <div style={{display: "flex", alignItems: "flex-start", gap: 12}}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                              if (e.target.checked) setSelectedLeaveRecordIds(prev => [...prev, req.id]);
                                              else setSelectedLeaveRecordIds(prev => prev.filter(x => x !== req.id));
                                            }}
                                            style={{marginTop: 4, cursor: "pointer"}}
                                          />
                                          <div>
                                            <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 4}}>
                                              <span style={{fontWeight: 700, color: "#0f172a", fontSize: 15}}>{req.employees?.name}</span>
                                              <span style={{fontSize: 12, color: "#64748b", fontWeight: 600}}>({req.employees?.employee_code})</span>
                                              <span style={{fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 12, background: statusBg, color: statusColor, textTransform: "uppercase"}}>{req.status}</span>
                                              {['approved','rejected'].includes(req.status)&&<HRNotificationAction company={activeCompany} targets={[{kind:'leave_decision',record_id:req.id,employee_id:req.employee_id,label:req.employees?.name||'Employee'}]}/>}
                                            </div>
                                            <div style={{fontSize: 13, color: "#334155"}}>
                                              <strong>{req.days} days</strong> • {req.leave_type} ({new Date(req.start_date).toLocaleDateString()} to {new Date(req.end_date).toLocaleDateString()})
                                            </div>

                                            {/* Allotted Quota and Current Leave Balance for this record's employee */}
                                            <div style={{display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap", fontSize: 12}}>
                                              <span style={{background: "#f1f5f9", padding: "2px 8px", borderRadius: 6, color: "#475569", fontWeight: 700, border: "1px solid #e2e8f0"}}>
                                                Allotted: <strong>{m.totalQuota}d</strong>
                                              </span>
                                              <span style={{background: m.remainingBalance > 0 ? "#ecfdf5" : "#fef2f2", padding: "2px 8px", borderRadius: 6, color: m.remainingBalance > 0 ? "#059669" : "#dc2626", fontWeight: 700, border: m.remainingBalance > 0 ? "1px solid #a7f3d0" : "1px solid #fecaca"}}>
                                                Balance: <strong>{m.remainingBalance}d left</strong>
                                              </span>
                                              <span style={{color: "#64748b", fontSize: 11}}>
                                                (Total {m.approvedUsed}d approved in {reqYear})
                                              </span>
                                            </div>

                                            {req.reason && <div style={{fontSize: 12, color: "#64748b", marginTop: 4}}>Reason: "{req.reason}"</div>}
                                            {req.rejection_reason && <div style={{fontSize: 12, color: "#dc2626", marginTop: 2}}>Rejection note: "{req.rejection_reason}"</div>}
                                          </div>
                                        </div>
                                        <div style={{display: "flex", alignItems: "center", gap: 16}}>
                                          <div style={{fontSize: 11, color: "#94a3b8", textAlign: "right"}}>
                                            <div>Applied: {new Date(req.created_at).toLocaleDateString()}</div>
                                            {req.reviewed_at && <div>Reviewed: {new Date(req.reviewed_at).toLocaleDateString()}</div>}
                                          </div>
                                          <button
                                            title="Delete this leave record"
                                            onClick={async () => {
                                              if (!window.confirm(`Delete leave record for ${req.employees?.name || 'employee'}?`)) return;
                                              const { error } = await supabase.from("employee_leaves").delete().eq("id", req.id);
                                              if (error) alert("Error deleting record: " + error.message);
                                              else {
                                                logAudit(currentUser, "ADMIN_DELETED_LEAVE_RECORD", {
                                                  leave_id: req.id,
                                                  employee_id: req.employee_id,
                                                  employee_name: req.employees?.name,
                                                  leave_type: req.leave_type,
                                                  days: req.days,
                                                  start_date: req.start_date,
                                                  end_date: req.end_date
                                                });
                                                setSelectedLeaveRecordIds(prev => prev.filter(x => x !== req.id));
                                                refreshApprovals();
                                              }
                                            }}
                                            style={{background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 6, padding: "6px 10px", fontSize: 13, cursor: "pointer", transition: "all 0.15s"}}
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                      </div>
                    )}
                  </div>
                )}

                {/* ─── TAB 2: PROFILE UPDATES ─── */}
                {companyNav !== "leave_management" && approvalsTab === "profile" && (
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                      <span>👤 Pending Profile Updates</span>
                      <span style={{fontSize:12,fontWeight:700,background:pendingProfiles.length > 0 ? "#fee2e2" : "#f1f5f9",color:pendingProfiles.length > 0 ? "#dc2626" : "#64748b",padding:"2px 8px",borderRadius:12}}>
                        {pendingProfiles.length}
                      </span>
                    </div>

                    {pendingProfiles.length === 0 ? (
                      renderEmptyApprovalState("No pending requests", "All employee updates have been reviewed.")
                    ) : (
                      <div style={{display: "flex", flexDirection: "column", gap: 16}}>
                        {pendingProfiles.map(req => (
                          <div key={req.id} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 16}}>
                              <div>
                                <div style={{fontWeight: 700, fontSize: 16, color: "#0f172a"}}>{req.employees?.name}</div>
                                <div style={{color: "#64748b", fontSize: 12, marginTop: 2}}>{req.employees?.employee_code} • Requested on {new Date(req.created_at).toLocaleString()}</div>
                              </div>
                              <div style={{display: "flex", gap: 8}}>
                                <button onClick={async () => {
                                  const {error} = await supabase.from("employee_update_requests").update({status: 'rejected', reviewed_at: new Date().toISOString()}).eq("id", req.id);
                                  if(error) alert(error.message); else {
                                    setPendingApprovals(p => p.filter(x => x.id !== req.id));
                                    setAllPendingApprovals(p => p.filter(x => x.id !== req.id));
                                    setRecentApprovals(p => [{...req, status: 'rejected', reviewed_at: new Date().toISOString()}, ...p].slice(0, 10));
                                    logAudit(currentUser, "ADMIN_REJECTED_UPDATE", { employee_id: req.employee_id, request_id: req.id });
                                  }
                                }} style={{background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontWeight:600, fontSize:13}}>Reject</button>
                                <button onClick={async () => {
                                  try {
                                    const rawData = { ...(req.requested_data || {}) };
                                    const {
                                      emp_photo,
                                      emp_aadhaar_front_doc,
                                      emp_aadhaar_back_doc,
                                      emp_aadhaar_doc,
                                      emp_pan_doc,
                                      emp_bank_doc,
                                      ...otherData
                                    } = rawData;

                                    // Map alias fields to DB columns
                                    const empUpdatePayload = {};
                                    const ignoreKeys = new Set([
                                      'id', 'company_id', 'created_at', 'updated_at', 'status', 'request_type',
                                      'rejection_reason', 'requested_date', 'cert_type', 'purpose', 'addressed_to',
                                      'current_address', 'permanent_address', 'prev_esi'
                                    ]);

                                    for (const [key, val] of Object.entries(otherData)) {
                                      if (ignoreKeys.has(key)) continue;
                                      if (key === 'dob') {
                                        empUpdatePayload.date_of_birth = val;
                                      } else if (key === 'doj') {
                                        empUpdatePayload.date_of_joining = val;
                                      } else if (key === 'bank_account') {
                                        empUpdatePayload.bank_account_number = val;
                                      } else if (key === 'pf_account') {
                                        empUpdatePayload.pf_account_number = val;
                                      } else {
                                        empUpdatePayload[key] = val;
                                      }
                                    }

                                    if (Object.keys(empUpdatePayload).length > 0) {
                                      const { error: e1 } = await supabase.from("employees").update(empUpdatePayload).eq("id", req.employee_id);
                                      if (e1) {
                                        alert("Error updating employee details: " + e1.message);
                                        return;
                                      }
                                    }

                                    const empCode = req.employees?.employee_code;
                                    const { data: coRow } = await supabase.from("companies").select("config").eq("id", activeCompany.id).single();
                                    const currentConfig = (coRow && typeof coRow.config === 'object') ? coRow.config : (activeCompany?.config || {});
                                    const photos = { ...(currentConfig.employee_photos || {}) };
                                    const docs = { ...(currentConfig.employee_documents || {}) };
                                    let configChanged = false;

                                    if (emp_photo !== undefined) {
                                      if (emp_photo) {
                                        photos[req.employee_id] = emp_photo;
                                        if (empCode) photos[empCode] = emp_photo;
                                      } else {
                                        delete photos[req.employee_id];
                                        if (empCode) delete photos[empCode];
                                      }
                                      configChanged = true;
                                    }

                                    const empDocs = {};
                                    if (emp_aadhaar_front_doc !== undefined || emp_aadhaar_doc !== undefined) {
                                      empDocs.emp_aadhaar_front_doc = emp_aadhaar_front_doc || emp_aadhaar_doc || "";
                                    }
                                    if (emp_aadhaar_back_doc !== undefined) {
                                      empDocs.emp_aadhaar_back_doc = emp_aadhaar_back_doc || "";
                                    }
                                    if (emp_pan_doc !== undefined) {
                                      empDocs.emp_pan_doc = emp_pan_doc || "";
                                    }
                                    if (emp_bank_doc !== undefined) {
                                      empDocs.emp_bank_doc = emp_bank_doc || "";
                                    }

                                    if (Object.keys(empDocs).length > 0) {
                                      docs[req.employee_id] = { ...(docs[req.employee_id] || {}), ...empDocs };
                                      if (empCode) docs[empCode] = { ...(docs[empCode] || {}), ...empDocs };
                                      configChanged = true;
                                    }

                                    if (configChanged) {
                                      const updatedConfig = {
                                        ...currentConfig,
                                        employee_photos: photos,
                                        employee_documents: docs
                                      };
                                      await supabase.from("companies").update({ config: updatedConfig }).eq("id", activeCompany.id);
                                      try {
                                        localStorage.setItem('co_config_' + activeCompany.id, JSON.stringify(updatedConfig));
                                      } catch(e) {}
                                    }

                                    const { error: reqErr } = await supabase.from("employee_update_requests").update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq("id", req.id);
                                    if (reqErr) {
                                      alert("Error updating request: " + reqErr.message);
                                      return;
                                    }

                                    logAudit(currentUser, "ADMIN_APPROVED_UPDATE", { employee_id: req.employee_id, request_id: req.id, data: req.requested_data, company_id: req.company_id });

                                    // Clear local draft for this employee so employee portal doesn't retain old draft
                                    try {
                                      localStorage.removeItem(`trustwork_jk_draft_${req.employee_id}`);
                                    } catch(e) {}

                                    // Persist current admin navigation state and toast in sessionStorage before reloading
                                    const empName = req.employees?.name || 'employee';
                                    sessionStorage.setItem("tw_active_company_id", activeCompany.id);
                                    sessionStorage.setItem("tw_active_view", "approvals");
                                    sessionStorage.setItem("tw_active_company_nav", "approvals");
                                    sessionStorage.setItem("tw_active_approvals_tab", "profile");
                                    sessionStorage.setItem("tw_toast_msg", `Profile & Joining Kit update approved for ${empName}! All records updated.`);

                                    // Reload window so all admin views, employee lists, and document caches are cleanly refreshed
                                    window.location.reload();
                                  } catch (err) {
                                    console.error("Error during approval:", err);
                                    alert("An error occurred while approving: " + err.message);
                                  }
                                }} style={{background:"#dcfce7", color:"#16a34a", border:"1px solid #bbf7d0", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontWeight:600, fontSize:13}}>Approve & Apply</button>
                              </div>
                            </div>
                            <div style={{background: "#f8fafc", padding: 12, borderRadius: 8, fontSize: 13, border: "1px dashed #cbd5e1"}}>
                              <details>
                                <summary style={{fontWeight: 600, color: "#334155", cursor: "pointer"}}>View requested changes</summary>
                                <div style={{marginTop: 10, display: "flex", flexDirection: "column", gap: "8px"}}>
                                  {Object.entries(req.requested_data || {}).map(([k, v]) => (
                                    <div key={k} style={{display: "flex", gap: "10px", paddingBottom: "8px", borderBottom: "1px solid #e2e8f0"}}>
                                      <strong style={{minWidth: "160px", color: "#1e293b", textTransform: "capitalize"}}>{k.replace(/_/g, ' ')}:</strong>
                                      <div style={{display: "flex", alignItems: "center", gap: 12, color: "#475569", wordBreak: "break-all"}}>
                                        <span style={{textDecoration: "line-through", color: "#94a3b8"}}>
                                          {typeof req.employees?.[k] === 'object' && req.employees?.[k] !== null
                                            ? (Array.isArray(req.employees[k]) ? `${req.employees[k].length} items` : "Previous Details")
                                            : ((typeof req.employees?.[k] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(req.employees[k])) ? req.employees[k].split('-').reverse().join('-') : (String(req.employees?.[k] || "") || <em style={{color: "#cbd5e1"}}>Empty</em>))}
                                        </span>
                                        <span>➡️</span>
                                        <span style={{color: "#16a34a", fontWeight: 600}}>
                                          {typeof v === 'object' && v !== null 
                                            ? (Array.isArray(v) ? `${v.length} items updated` : "New Details") 
                                            : (typeof v === 'string' && (v.startsWith('data:') || v.startsWith('/api/files/')) ? (k === 'emp_photo' ? <img src={v} alt="Photo" style={{width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', verticalAlign: 'middle', border: '1px solid #cbd5e1'}} /> : (v.toLowerCase().endsWith('.pdf') ? <a href={v} target="_blank" rel="noreferrer" style={{color: '#2563eb', fontWeight: 600}}>📄 View PDF</a> : <a href={v} target="_blank" rel="noreferrer" style={{color: '#2563eb', fontWeight: 600}}>📎 View Document</a>)) : ((typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) ? v.split('-').reverse().join('-') : (String(v) || <em style={{color: "#94a3b8"}}>Empty</em>)))}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {recentApprovals.length > 0 && (
                      <div style={{marginTop: 48}}>
                        <HRNotificationAction company={activeCompany} label="Email reviewed requests" targets={recentApprovals.filter(request=>['approved','rejected'].includes(request.status)).map(request=>({kind:request.requested_data?.request_type==='certificate'?'certificate_decision':'profile_decision',record_id:request.id,employee_id:request.employee_id,label:request.employees?.name||'Employee'}))}/>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16}}>
                          <div>
                            <div className="page-title" style={{marginBottom: 8, fontSize: 18}}>📋 Recent Profile Approvals & Rejections</div>
                            <div className="page-sub">Last 10 processed employee profile update requests</div>
                          </div>
                          <button 
                            onClick={async () => {
                              if (!window.confirm("Are you sure you want to clear the recent history? This cannot be undone.")) return;
                              const { error } = await supabase.from("employee_update_requests").delete().eq("company_id", activeCompany.id).in("status", ["approved", "rejected"]);
                              if (error) alert("Failed to clear history: " + error.message);
                              else setRecentApprovals([]);
                            }} 
                            style={{background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontWeight:600, fontSize:13}}
                          >
                            🗑 Clear History
                          </button>
                        </div>
                        <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                          {recentApprovals.map(req => (
                            <div key={req.id} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                              <div>
                                <div style={{fontWeight: 700, fontSize: 15, color: "#0f172a"}}>{req.employees?.name}</div>
                                <div style={{color: "#64748b", fontSize: 12, marginTop: 2}}>
                                  {req.employees?.employee_code} • Reviewed on {new Date(req.reviewed_at || req.created_at).toLocaleString()}
                                </div>
                                {req.requested_data?.request_type === "certificate" ? (
                                  <div style={{color: "#475569", fontSize: 12, marginTop: 6, padding: "4px 8px", background: "#eff6ff", borderRadius: 4, display: "inline-block", border: "1px solid #bfdbfe"}}>
                                    <span style={{fontWeight: 700, color: "#1d4ed8"}}>📜 Certificate:</span> {req.requested_data.cert_type === "salary" ? "Salary Certificate" : (req.requested_data.cert_type === "employment" ? "Employment Certificate" : "Combined Salary & Employment Certificate")} — {req.requested_data.purpose || "Verification"}
                                  </div>
                                ) : (
                                  req.requested_data && Object.keys(req.requested_data).length > 0 && (
                                    <div style={{color: "#475569", fontSize: 12, marginTop: 6, padding: "4px 8px", background: "#f8fafc", borderRadius: 4, display: "inline-block", border: "1px solid #e2e8f0"}}>
                                      <span style={{fontWeight: 700}}>Updated:</span> {Object.keys(req.requested_data).filter(k => k !== "type").map(k => k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).join(', ')}
                                    </div>
                                  )
                                )}
                              </div>
                              <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                <span style={{
                                  display: "inline-block", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                                  background: req.status === "approved" ? "#dcfce7" : "#fef2f2",
                                  color: req.status === "approved" ? "#16a34a" : "#dc2626"
                                }}>
                                  {req.status === "approved" ? "Approved" : "Rejected"}
                                </span>
                                <HRNotificationAction company={activeCompany} label={req.requested_data?.request_type==='certificate'&&req.status==='approved'?'Email download notice':'Send notification'} targets={[{kind:req.requested_data?.request_type==='certificate'?'certificate_decision':'profile_decision',record_id:req.id,employee_id:req.employee_id,label:req.employees?.name||'Employee'}]}/>
                                {req.status === "approved" && req.requested_data?.request_type === "certificate" && (
                                  <button
                                    onClick={() => {
                                      const emp = req.employees || companyEmps.find(e => e.id === req.employee_id) || {};
                                      generateCertificatePDF({
                                        employee: emp,
                                        company: activeCompany,
                                        certType: req.requested_data?.cert_type || "combined",
                                        purpose: req.requested_data?.purpose || "Verification",
                                        addressedTo: req.requested_data?.addressed_to || "To Whom It May Concern",
                                        refNo: `CERT-${activeCompany?.id?.substring(0,4)?.toUpperCase() || "TW"}-${new Date(req.created_at || Date.now()).getFullYear()}-${String(req.id || "").substring(0,4).toUpperCase()}`,
                                        issueDate: req.reviewed_at ? new Date(req.reviewed_at).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]
                                      });
                                    }}
                                    style={{background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 8px", fontSize: 11.5, cursor: "pointer", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4}}
                                    title="Re-download issued certificate PDF"
                                  >
                                    📄 PDF
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── TAB 3: CERTIFICATE REQUESTS ─── */}
                {companyNav !== "leave_management" && approvalsTab === "certificates" && (
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:"#1e293b",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                      <span>📜 Pending Certificate Requests</span>
                      <span style={{fontSize:12,fontWeight:700,background:pendingCertificates.length > 0 ? "#fee2e2" : "#f1f5f9",color:pendingCertificates.length > 0 ? "#dc2626" : "#64748b",padding:"2px 8px",borderRadius:12}}>
                        {pendingCertificates.length}
                      </span>
                    </div>

                    {pendingCertificates.length === 0 ? (
                      renderEmptyApprovalState("No pending certificate requests", "All employee certificate applications have been reviewed.")
                    ) : (
                      <div style={{display: "flex", flexDirection: "column", gap: 16}}>
                        {pendingCertificates.map(req => {
                          const emp = req.employees || companyEmps.find(e => e.id === req.employee_id) || {};
                          const reqData = req.requested_data || {};
                          const certTypeLabel = reqData.cert_type === "salary" 
                            ? "💰 Salary Certificate" 
                            : reqData.cert_type === "employment" 
                              ? "💼 Employment Certificate" 
                              : "📜 Combined Salary & Employment Certificate";

                          return (
                            <div key={req.id} style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20}}>
                              <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 16}}>
                                <div>
                                  <div style={{fontWeight: 700, fontSize: 16, color: "#0f172a"}}>{emp.name || "Employee"}</div>
                                  <div style={{color: "#64748b", fontSize: 12, marginTop: 2}}>
                                    {emp.employee_code || "—"} • {emp.designation || "Staff"} ({emp.department || "General"}) • Requested on {new Date(req.created_at).toLocaleString()}
                                  </div>
                                </div>
                                <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                                  {/* Preview Button */}
                                  <button
                                    onClick={() => {
                                      generateCertificatePDF({
                                        employee: emp,
                                        company: activeCompany,
                                        certType: reqData.cert_type || "combined",
                                        purpose: reqData.purpose || "Verification",
                                        addressedTo: reqData.addressed_to || "To Whom It May Concern",
                                        refNo: `PREVIEW-${activeCompany?.id?.substring(0,4)?.toUpperCase() || "TW"}-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
                                        issueDate: new Date().toISOString().split("T")[0]
                                      });
                                    }}
                                    style={{background:"#f8fafc", color:"#334155", border:"1px solid #cbd5e1", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontWeight:600, fontSize:13, display: "inline-flex", alignItems: "center", gap: 4}}
                                    title="Preview generated PDF before approving"
                                  >
                                    👁️ Preview PDF
                                  </button>

                                  {/* Reject Button */}
                                  <button
                                    onClick={async () => {
                                      const reason = window.prompt("Reason for rejecting certificate request (optional):");
                                      const updatedData = { ...reqData, rejection_reason: reason || "Rejected by HR" };
                                      const { error } = await supabase.from("employee_update_requests").update({
                                        status: 'rejected',
                                        requested_data: updatedData,
                                        reviewed_at: new Date().toISOString()
                                      }).eq("id", req.id);
                                      if (error) alert(error.message);
                                      else {
                                        setPendingApprovals(p => p.filter(x => x.id !== req.id));
                                        setAllPendingApprovals(p => p.filter(x => x.id !== req.id));
                                        setRecentApprovals(p => [{ ...req, status: 'rejected', requested_data: updatedData, reviewed_at: new Date().toISOString() }, ...p].slice(0, 10));
                                        logAudit(currentUser, "ADMIN_REJECTED_CERTIFICATE", { employee_id: req.employee_id, request_id: req.id, reason });
                                      }
                                    }}
                                    style={{background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontWeight:600, fontSize:13}}
                                  >
                                    ❌ Reject
                                  </button>

                                  {/* Approve Button */}
                                  <button
                                    onClick={async () => {
                                      const { error } = await supabase.from("employee_update_requests").update({
                                        status: 'approved',
                                        reviewed_at: new Date().toISOString()
                                      }).eq("id", req.id);
                                      if (error) alert(error.message);
                                      else {
                                        setPendingApprovals(p => p.filter(x => x.id !== req.id));
                                        setAllPendingApprovals(p => p.filter(x => x.id !== req.id));
                                        setRecentApprovals(p => [{ ...req, status: 'approved', reviewed_at: new Date().toISOString() }, ...p].slice(0, 10));
                                        logAudit(currentUser, "ADMIN_APPROVED_CERTIFICATE", { employee_id: req.employee_id, request_id: req.id, cert_type: reqData.cert_type });
                                      }
                                    }}
                                    style={{background:"#dcfce7", color:"#16a34a", border:"1px solid #bbf7d0", borderRadius:6, padding:"6px 12px", cursor:"pointer", fontWeight:600, fontSize:13}}
                                  >
                                    ✅ Approve & Issue
                                  </button>
                                </div>
                              </div>

                              {/* Details Card */}
                              <div style={{background: "#f8fafc", padding: 14, borderRadius: 8, fontSize: 13, border: "1px solid #e2e8f0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12}}>
                                <div>
                                  <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Certificate Type</div>
                                  <div style={{fontSize: 13.5, fontWeight: 700, color: "#1e293b", marginTop: 2}}>{certTypeLabel}</div>
                                </div>
                                <div>
                                  <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Stated Purpose</div>
                                  <div style={{fontSize: 13.5, fontWeight: 600, color: "#0f766e", marginTop: 2}}>{reqData.purpose || "General Verification"}</div>
                                </div>
                                <div>
                                  <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Addressed To</div>
                                  <div style={{fontSize: 13.5, fontWeight: 600, color: "#334155", marginTop: 2}}>{reqData.addressed_to || "To Whom It May Concern"}</div>
                                </div>
                                <div>
                                  <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Current Monthly Gross</div>
                                  <div style={{fontSize: 13.5, fontWeight: 700, color: "#16a34a", marginTop: 2}}>{fmtINR(emp.basic_salary || emp.basic || 0)}/mo</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            );
          })()}
        </div>
      )}

      {/* ── VIEW: REPORTS & COMPLIANCE HUB */}
      {view === "reports" && activeCompany && (activeCompany?.config?.reports_enabled !== false) && (
        <AdminReportsHub
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          isCompanyRole={isCompanyRole}
          supabase={supabase}
          getEmployeeLeaveMetrics={getEmployeeLeaveMetrics}
          salaries={salaries}
          onDownloadMasterExcel={handleDownloadMasterExcelForMonth}
          onBack={() => safeNavigate(() => { setCompanyNav("dashboard"); setView("dashboard"); }, "Dashboard")}
          onGenerateCertificate={generateCertificatePDF}
          onGenerateIncrementLetter={generateIncrementLetterPDF}
          onGenerateAppointmentLetter={generateAppointmentLetterPDF}
          onOpenCertificateModal={(emp) => { setActiveEmployee(emp); setModal("issueCertificate"); }}
        />
      )}

      {view === 'doc_hub' && activeCompany && ['admin','company'].includes(currentUser?.role) && <DocHub key={activeCompany.id} company={activeCompany} currentUser={currentUser} employees={companyEmps} onDirtyChange={setHasUnsavedChanges}/>}

      {/* ── VIEW: TRAINING & COMPLIANCE HUB ── */}
      {view === "training" && activeCompany && (activeCompany?.config?.training_enabled === true) && (
        <TrainingHub
          key={activeCompany.id}
          onDirtyChange={setHasUnsavedChanges}
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          onUpdateCompanyConfig={async (updatedCfg) => {
            const newCo = { ...activeCompany, config: updatedCfg };
            const { error } = await supabase.from("companies").update({ config: updatedCfg }).eq("id", activeCompany.id);
            if (error) throw new Error(error.message);
            setActiveCompany(newCo);
            setCompanies(prev => prev.map(c => c.id === activeCompany.id ? newCo : c));
          }}
        />
      )}

      {/* ── VIEW: ATTENDANCE */}
      {view === "attendance" && activeCompany && (
        <AdminAttendanceDashboard
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          session={{ companyId: activeCompany.id, role: currentUser?.role || "company" }}
          onBack={() => safeNavigate(() => { setCompanyNav("dashboard"); setView("dashboard"); }, "Dashboard")}
          isCompanyRole={isCompanyRole}
          onUpdateCompanyConfig={async (updatedCfg) => {
            const newCo = { ...activeCompany, config: updatedCfg };
            setActiveCompany(newCo);
            setCompanies(prev => prev.map(c => c.id === activeCompany.id ? newCo : c));
          }}
        />
      )}

      {/* ── VIEW: ORG & CULTURE */}
      {view === "org" && activeCompany && (activeCompany?.config?.org_culture_enabled === true) && (
        <OrgAndCultureDashboard
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          isCompanyRole={isCompanyRole}
          initialTab={orgTab}
          onUpdateCompanyConfig={async (updatedCfg) => {
            const newCo = { ...activeCompany, config: updatedCfg };
            setActiveCompany(newCo);
            setCompanies(prev => prev.map(c => c.id === activeCompany.id ? newCo : c));
          }}
          onEditEmployee={(emp) => {
            setEditTarget(emp);
            setModal("editEmp");
            setView("employee_form");
          }}
          onSelectEmployee={(emp) => {
            setActiveEmployee(emp);
            setView("employee_detail");
          }}
        />
      )}

      {/* ── VIEW: RECRUITMENT ── */}
      {view === "recruitment" && activeCompany && (activeCompany?.config?.recruitment_enabled === true) && (
        <RecruitmentDashboard
          key={activeCompany.id}
          onGenerateOffer={generateOfferLetterPDF}
          onDirtyChange={setHasUnsavedChanges}
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          onUpdateCompanyConfig={async (updatedCfg) => {
            const newCo = { ...activeCompany, config: updatedCfg };
            setActiveCompany(newCo);
            setCompanies(prev => prev.map(c => c.id === activeCompany.id ? newCo : c));
          }}
          onConvertCandidateToEmployee={handleConvertCandidateToEmployee}
        />
      )}

      {/* ── VIEW: OFFBOARDING & FNF ── */}
      {view === "offboarding" && activeCompany && (activeCompany?.config?.offboarding_enabled === true) && (
        <OffboardingDashboard
          key={activeCompany.id}
          onSettlementPdf={generateFnFPdfDoc}
          onRelievingPdf={generateRelievingLetterPDF}
          onDirtyChange={setHasUnsavedChanges}
          company={activeCompany}
          employees={companyEmps}
          currentUser={currentUser}
          initialTab={offboardingTab}
          onUpdateCompanyConfig={async (updatedCfg) => {
            const newCo = { ...activeCompany, config: updatedCfg };
            setActiveCompany(newCo);
            setCompanies(prev => prev.map(c => c.id === activeCompany.id ? newCo : c));
          }}
          onUpdateEmployee={(updatedEmp) => {
            setEmployees(prev => ({
              ...prev,
              [activeCompany.id]: (prev[activeCompany.id] || []).map(e => e.id === updatedEmp.id ? updatedEmp : e)
            }));
            if (activeEmployee?.id === updatedEmp.id) {
              setActiveEmployee(updatedEmp);
            }
          }}
          onSelectEmployee={(emp) => {
            setActiveEmployee(emp);
            setView("employee_detail");
          }}
        />
      )}

      {/* ── VIEW: AUDIT LOGS */}
      {view === "audit_logs" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div><div className="page-title">Activity Log</div><div className="page-sub">Review recent company, employee and administrative actions. Up to 500 records are loaded.</div></div>
            <button type="button" className="btn btn-ghost" disabled={auditLoading} onClick={fetchActivityLogs}>{auditLoading ? "Refreshing…" : "Refresh logs"}</button>
          </div>
          {auditError && <div className="err-msg" role="alert">{auditError}</div>}
          <LogViewer title="Activity log" logs={auditLogs.filter(log => {
            if (isCompanyRole || currentUser?.role === "company") return log.company_id === (currentUser.companyId || activeCompany?.id);
            return superAdminLogCompanyFilter === "all" || (superAdminLogCompanyFilter === "platform" ? !log.company_id : log.company_id === superAdminLogCompanyFilter);
          })} companies={companies} showCompany={!isCompanyRole} onClear={clearActivityLogs} clearing={clearingActivityLogs}
            scopeLabel={isCompanyRole ? activeCompany?.name || "this company" : "the selected company/platform scope"}
            scopeControl={!isCompanyRole && <label>Company<select value={superAdminLogCompanyFilter} onChange={event => setSuperAdminLogCompanyFilter(event.target.value)}><option value="all">All companies and platform</option><option value="platform">Platform only</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label>}
          />
        </div>
      )}

      {/* ── VIEW: SECURITY & LOGIN THREAT MONITOR ── */}
      {view === "security" && (
        <SecurityThreatDashboard
          supabase={supabase}
          activeCompany={activeCompany}
          companies={companies}
          isMasterAdmin={!isCompanyRole}
          currentUser={currentUser}
          onBack={goBack}
          onOpenActivityLog={() => { setCompanyNav("audit_logs"); setView("audit_logs"); }}
        />
      )}

      {/* ── VIEW: DASHBOARD */}
      {view === "dashboard" && activeCompany && (
        <>
          {!isCompanyRole && (
            <div className="back-btn" onClick={() => { setDashMonth(null); setView("employees"); }}>← Back to Employees</div>
          )}
          <div className="page-header" style={{marginBottom: 20}}>
            <div>
              <div className="page-title">
                Executive Dashboard
              </div>
              <div className="page-sub">Visualize payroll metrics, financial outflow, and compliance exceptions.</div>
            </div>
          </div>

          <div className="tab-nav" aria-label="Dashboard views">
            <button type="button" className={`tab-btn${dashboardTab === 'overview' ? ' active' : ''}`} aria-pressed={dashboardTab === 'overview'} onClick={() => setDashboardTab('overview')}>Overview</button>
            <button type="button" className={`tab-btn${dashboardTab === 'charts' ? ' active' : ''}`} aria-pressed={dashboardTab === 'charts'} onClick={() => setDashboardTab('charts')}>Charts</button>
          </div>
          {(dashMonth || (availableMonths.length > 0 ? availableMonths[0] : null)) && (()=>{
            const activeDashMonth = availableMonths.find(item => item.month === dashMonth?.month && item.year === dashMonth?.year) || availableMonths[0];
            const month = activeDashMonth.month, year = activeDashMonth.year;
            const summary = summarizeDashboardMonth({ employees: companyEmps, salaries, config: activeCompany.config || DEFAULT_CONFIG, month, year });
            const { summAbs, summOtH, summOtP, summPfElig, summEsiElig, summGross, summNet, summEmpPf, summEmpEsi, summGratuity, summErEsi, otViolators, noPfList, noEsiList, epsExemptedList, onboarded, exited, employeeRows, totalErPfExpenditure, totalExpenditure } = summary;

            const listDefinitions = {
              employees: { title: 'Employees in payroll month', rows: employeeRows, column: 'Employment dates', value: row => `${fmtDate(row.employee.doj)} – ${row.employee.date_of_exit ? fmtDate(row.employee.date_of_exit) : 'Continuing'}` },
              joined: { title: 'Joined this month', rows: employeeRows.filter(row => row.joined), column: 'Date of joining', value: row => fmtDate(row.employee.doj) },
              exited: { title: 'Exited this month', rows: employeeRows.filter(row => row.exited), column: 'Last working day', value: row => fmtDate(row.employee.date_of_exit) },
              absent: { title: 'Employees with absent days', rows: employeeRows.filter(row => row.absent > 0), column: 'Absent days', value: row => row.absent.toFixed(1) },
              pfEligible: { title: 'Employees contributing to PF', rows: employeeRows.filter(row => row.pf > 0), column: 'Employee PF', value: row => fmtINR(row.pf) },
              pfExempt: { title: 'Employees without PF contributions', rows: employeeRows.filter(row => row.pf <= 0), column: 'Reason', value: row => noPfList.find(item => item.id === row.employee.id)?.reason || '—' },
              esiEligible: { title: 'Employees contributing to ESI', rows: employeeRows.filter(row => row.esi > 0), column: 'Employee ESI', value: row => fmtINR(row.esi) },
              esiExempt: { title: 'Employees without ESI contributions', rows: employeeRows.filter(row => row.esi <= 0), column: 'Reason', value: row => noEsiList.find(item => item.id === row.employee.id)?.reason || '—' },
              otHours: { title: 'Employees with overtime', rows: employeeRows.filter(row => row.otHours > 0), column: 'OT hours', value: row => row.otHours.toFixed(1) },
              otPay: { title: 'Employees with overtime pay', rows: employeeRows.filter(row => row.otPay > 0), column: 'OT pay', value: row => fmtINR(row.otPay) },
            };
            const selectedList = listDefinitions[dashEmployeeList];
            const openEmployee = employee => safeNavigate(() => { setActiveEmployee(employee); setEmpTab('employee'); setCompanyNav('employees'); setView('employee_detail'); }, 'Employee Details');
            const drilldownProps = key => ({
              role: 'button', tabIndex: 0, 'aria-label': `View ${listDefinitions[key].title.toLowerCase()}`, 'aria-controls': 'dashboard-employee-list',
              onClick: () => { setDashEmployeeList(key); requestAnimationFrame(() => { const panel = document.getElementById('dashboard-employee-list'); panel?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }); panel?.focus(); }); },
              onKeyDown: event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); } },
            });

            return (
              <div className="dash-container" style={{animation: "fadeIn 0.3s ease"}}>
                <DashboardComparison employees={companyEmps} salaries={salaries} config={activeCompany.config || DEFAULT_CONFIG} current={summary} financialYears={availableFYs} controls={<>
                  {availableFYs.length > 0 && <label>Financial year<select aria-label="Dashboard financial year" value={selectedFY||availableFYs[0]||""} onChange={event=>{setSelectedFY(event.target.value);setDashMonth(null);setDashEmployeeList(null);}}>{availableFYs.map(fy=><option key={fy} value={fy}>FY {fy}</option>)}</select></label>}
                  <label>Month<select aria-label="Dashboard month" value={`${year}-${month}`} onChange={event=>{const chosen=availableMonths.find(item=>`${item.year}-${item.month}`===event.target.value);if(chosen){setDashMonth(chosen);setDashEmployeeList(null);}}}>
                    {availableMonths.map(item=>{
                      const workingDays=activeCompany.config?.working_days===-1?new Date(item.year,item.month,0).getDate():(activeCompany.config?.working_days||26);
                      const count=companyEmps.filter(employee=>getEmployeeMonthStatus(employee,item.month,item.year,workingDays).eligible).length;
                      const locked=companyEmps.some(employee=>(salaries[employee.id]||[]).some(record=>record.month===item.month&&record.year===item.year&&record.status==='locked'));
                      return <option key={`${item.year}-${item.month}`} value={`${item.year}-${item.month}`}>{MONTHS[item.month]} {item.year} · Employees: {count}{locked?' 🔒':''}</option>;
                    })}
                  </select></label>
                </>}/>
                {dashboardTab === 'charts' ? (
                  <DashboardCharts employees={companyEmps} salaries={salaries} config={activeCompany.config || DEFAULT_CONFIG} months={availableMonths} selectedMonth={activeDashMonth} onSelectMonth={value => { setDashMonth(value); setDashEmployeeList(null); }} />
                ) : <>
                {/* Unified Executive Summary Banner */}
                <div className="dash-section-card" style={{marginBottom:24}}>
                  <div className="dash-summary-grid">
                    
                    {/* Financial Outflow (1st column - compact) */}
                    <div className="dash-summary-financial" style={{display:"flex", flexDirection:"column"}}>
                      <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                        <span style={{fontSize:18}}>💰</span> Financial Outflow
                      </div>
                      <div style={{display:"flex", flexDirection:"column", gap:8, height: "100%"}}>
                        <div className="dash-stat-pill" style={{flex:1, background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#0369a1", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Cost to Company</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#0284c7", whiteSpace:"nowrap"}}>{fmtINR(totalExpenditure)}</div>
                        </div>
                        <div className="dash-stat-pill" style={{flex:1, background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#047857", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Net Paid</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#059669", whiteSpace:"nowrap"}}>{fmtINR(summNet)}</div>
                        </div>
                        <div className="dash-stat-pill" style={{flex:1, background:"#f8fafc", border:"1px solid #cbd5e1", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#334155", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Gross Paid</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#1e293b", whiteSpace:"nowrap"}}>{fmtINR(summGross)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Statutory Payments (Middle column - expanded) */}
                    <div className="dash-summary-statutory" style={{display:"flex", flexDirection:"column"}}>
                      <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                        <span style={{fontSize:18}}>🏛</span> Statutory Payments
                      </div>
                      <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
                        <div className="dash-stat-pill" style={{flex:"1 1 200px", minWidth: 190, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:12}}>
                          <div style={{fontSize:13, color:"#d97706", fontWeight:800, borderBottom:"1px dashed #fcd34d", paddingBottom:6, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:4}}>
                            <span>Total PF</span>
                            <span style={{fontSize:13, fontWeight:800}}>{fmtINR(totalErPfExpenditure + summEmpPf)}</span>
                          </div>
                          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4, gap:6}}>
                            <span style={{fontSize:11, color:"#b45309", fontWeight:600, whiteSpace:"nowrap"}}>Employer Share</span>
                            <span style={{fontSize:12, fontWeight:700, color:"#92400e", whiteSpace:"nowrap"}}>{fmtINR(totalErPfExpenditure)}</span>
                          </div>
                          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:6}}>
                            <span style={{fontSize:11, color:"#b45309", fontWeight:600, whiteSpace:"nowrap"}}>Employee Share</span>
                            <span style={{fontSize:12, fontWeight:700, color:"#92400e", whiteSpace:"nowrap"}}>{fmtINR(summEmpPf)}</span>
                          </div>
                        </div>
                        <div className="dash-stat-pill" style={{flex:"1 1 200px", minWidth: 190, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:12}}>
                          <div style={{fontSize:13, color:"#059669", fontWeight:800, borderBottom:"1px dashed #86efac", paddingBottom:6, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:4}}>
                            <span>Total ESI</span>
                            <span style={{fontSize:13, fontWeight:800}}>{fmtINR(summErEsi + summEmpEsi)}</span>
                          </div>
                          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4, gap:6}}>
                            <span style={{fontSize:11, color:"#166534", fontWeight:600, whiteSpace:"nowrap"}}>Employer Share</span>
                            <span style={{fontSize:12, fontWeight:700, color:"#14532d", whiteSpace:"nowrap"}}>{fmtINR(summErEsi)}</span>
                          </div>
                          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:6}}>
                            <span style={{fontSize:11, color:"#166534", fontWeight:600, whiteSpace:"nowrap"}}>Employee Share</span>
                            <span style={{fontSize:12, fontWeight:700, color:"#14532d", whiteSpace:"nowrap"}}>{fmtINR(summEmpEsi)}</span>
                          </div>
                        </div>
                        {summGratuity > 0 && (
                          <div className="dash-stat-pill" style={{flex:"1 1 200px", minWidth: 190, background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:12}}>
                            <div style={{fontSize:13, color:"#6d28d9", fontWeight:800, borderBottom:"1px dashed #c4b5fd", paddingBottom:6, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:4}}>
                              <span>Total Gratuity</span>
                              <span style={{fontSize:13, fontWeight:800}}>{fmtINR(summGratuity)}</span>
                            </div>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4, gap:6}}>
                              <span style={{fontSize:11, color:"#5b21b6", fontWeight:600, whiteSpace:"nowrap"}}>Employer Share</span>
                              <span style={{fontSize:12, fontWeight:700, color:"#4c1d95", whiteSpace:"nowrap"}}>{fmtINR(summGratuity)}</span>
                            </div>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:6}}>
                              <span style={{fontSize:11, color:"#5b21b6", fontWeight:600, whiteSpace:"nowrap"}}>Employee Share</span>
                              <span style={{fontSize:12, fontWeight:700, color:"#4c1d95", whiteSpace:"nowrap"}}>{fmtINR(0)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="dash-stat-pill" style={{marginTop: 12, padding: "10px 14px", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8, fontSize: 13, color: "#475569", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap"}}>
                        <span style={{lineHeight: 1.4}}>Total Employer Statutory Contribution (PF + ESI + Gratuity)</span>
                        <span style={{color: "#0f172a", fontSize: 15, fontWeight: 800, whiteSpace: "nowrap"}}>{fmtINR(totalErPfExpenditure + summErEsi + summGratuity)}</span>
                      </div>
                    </div>

                    {/* Employee Lifecycle (3rd column - compact) */}
                    <div className="dash-summary-lifecycle" style={{display:"flex", flexDirection:"column"}}>
                      <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                        <span style={{fontSize:18}}>🔄</span> Employee Lifecycle
                      </div>
                      <div style={{display:"flex", flexDirection:"column", gap:8, height: "100%"}}>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("employees")} style={{flex:1, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#1e3a8a", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Employees this month</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#3b82f6"}}>{employeeRows.length}</div>
                        </div>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("joined")} style={{flex:1, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Onboard</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#10b981"}}>{onboarded.length}</div>
                        </div>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("exited")} style={{flex:1, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8}}>
                          <div style={{fontSize:11, color:"#991b1b", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap"}}>Exited</div>
                          <div style={{fontSize:17, fontWeight:800, color:"#ef4444"}}>{exited.length}</div>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>

                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginBottom: 24}}>
                  {/* Headcount */}
                  <div className="dash-section-card" style={{display:"flex", flexDirection:"column"}}>
                    <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                      <span style={{fontSize:18}}>👥</span> Headcount & Attendance
                    </div>
                    <div style={{display:"flex", gap:12, height: "100%"}}>
                      <div className="dash-stat-pill dash-drilldown" {...drilldownProps("employees")} style={{flex:1, background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                        <div style={{fontSize:11, color:"#5b21b6", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, textAlign:"center"}}>Employees this month</div>
                        <div style={{fontSize:28, fontWeight:800, color:"#6d28d9", marginTop:4}}>{employeeRows.length}</div>
                      </div>
                      <div className="dash-stat-pill dash-drilldown" {...drilldownProps("absent")} style={{flex:1, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                        <div style={{fontSize:11, color:"#991b1b", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, textAlign:"center"}}>Absent Days</div>
                        <div style={{fontSize:28, fontWeight:800, color:"#ef4444", marginTop:4}}>{summAbs.toFixed(1)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Statutory Eligibility */}
                  <div className="dash-section-card" style={{display:"flex", flexDirection:"column"}}>
                    <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                      <span style={{fontSize:18}}>📋</span> Statutory Eligibility
                    </div>
                    <div style={{display:"flex", flexDirection:"column", gap:8, height:"100%"}}>
                      <div style={{display:"flex", gap:8, flex:1}}>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("pfEligible")} style={{flex:1, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                          <div style={{fontSize:11, color:"#166534", fontWeight:700, textTransform:"uppercase"}}>PF Elig.</div>
                          <div style={{fontSize:16, fontWeight:800, color:"#10b981"}}>{summPfElig}</div>
                        </div>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("pfExempt")} style={{flex:1, background:"#fff1f2", border:"1px solid #fecdd3", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                          <div style={{fontSize:11, color:"#9f1239", fontWeight:700, textTransform:"uppercase"}}>PF Inelig.</div>
                          <div style={{fontSize:16, fontWeight:800, color:"#e11d48"}}>{noPfList.length}</div>
                        </div>
                      </div>
                      <div style={{display:"flex", gap:8, flex:1}}>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("esiEligible")} style={{flex:1, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                          <div style={{fontSize:11, color:"#166534", fontWeight:700, textTransform:"uppercase"}}>ESI Elig.</div>
                          <div style={{fontSize:16, fontWeight:800, color:"#10b981"}}>{summEsiElig}</div>
                        </div>
                        <div className="dash-stat-pill dash-drilldown" {...drilldownProps("esiExempt")} style={{flex:1, background:"#fff1f2", border:"1px solid #fecdd3", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                          <div style={{fontSize:11, color:"#9f1239", fontWeight:700, textTransform:"uppercase"}}>ESI Inelig.</div>
                          <div style={{fontSize:16, fontWeight:800, color:"#e11d48"}}>{noEsiList.length}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Overtime Metrics */}
                  <div className="dash-section-card" style={{display:"flex", flexDirection:"column"}}>
                    <div style={{fontSize:13, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:0.5, marginBottom:16, display:"flex", alignItems:"center", gap:8}}>
                      <span style={{fontSize:18}}>⏱️</span> Overtime Metrics
                    </div>
                    <div style={{display:"flex", flexDirection:"column", gap:10, height:"100%"}}>
                      <div className="dash-stat-pill dash-drilldown" {...drilldownProps("otHours")} style={{flex:1, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div style={{fontSize:12, color:"#b45309", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5}}>OT Hours</div>
                        <div style={{fontSize:20, fontWeight:800, color:"#d97706"}}>{summOtH.toFixed(1)} hrs</div>
                      </div>
                      <div className="dash-stat-pill dash-drilldown" {...drilldownProps("otPay")} style={{flex:1, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div style={{fontSize:12, color:"#b45309", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5}}>OT Paid</div>
                        <div style={{fontSize:20, fontWeight:800, color:"#d97706"}}>{fmtINR(summOtP)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedList && (
                  <section id="dashboard-employee-list" tabIndex={-1} className="dash-section-card" aria-labelledby="dashboard-employee-list-title" style={{marginBottom:24}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,marginBottom:12}}>
                      <h3 id="dashboard-employee-list-title" style={{margin:0,fontSize:16}}>{selectedList.title} — {MONTHS[month]} {year} ({selectedList.rows.length})</h3>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDashEmployeeList(null)}>Close list</button>
                    </div>
                    <p style={{fontSize:12,color:'#64748b'}}>Includes employees eligible for payroll in this month, including joiners and leavers. Absent days include adjustments for joining and leaving dates.</p>
                    {selectedList.rows.length === 0 ? <p>No employees match this figure for the selected month.</p> : (
                      <div style={{overflowX:'auto'}}>
                        <table className="dash-table">
                          <thead><tr><th>Employee code</th><th>Employee name</th><th>{selectedList.column}</th></tr></thead>
                          <tbody>{selectedList.rows.map(row => <tr key={row.employee.id}>
                            <td>{row.employee.employee_code || '—'}</td>
                            <td><button type="button" className="dash-employee-link" onClick={() => openEmployee(row.employee)}>{row.employee.name}</button></td>
                            <td>{selectedList.value(row)}</td>
                          </tr>)}</tbody>
                        </table>
                      </div>
                    )}
                  </section>
                )}

                <h3 style={{fontSize: 16, fontWeight: 700, color: "#334155", marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #cbd5e1"}}>Exception & Compliance Reports</h3>
                
                <div style={{display:"flex", flexDirection:"column", gap: 12}}>
                  {/* Salary Revisions & Appraisal History Accordion */}
                  {(() => {
                    const hikesMap = activeCompany?.config?.salary_hikes || {};
                    const totalHikesCount = Object.values(hikesMap).reduce((acc, l) => acc + (l||[]).length, 0);
                    return (
                      <div className="dash-accordion" style={{border: "1px solid #cbd5e1"}}>
                        <div className="dash-accordion-header purple" onClick={()=>setShowDashSection(p=>p==="hikes"?"":"hikes")}>
                          <div style={{fontWeight: 700, color: "#6d28d9", display: "flex", alignItems: "center", gap: 8}}>
                            <span>📈</span> Salary Revisions &amp; Appraisal History — {totalHikesCount} Logged
                          </div>
                          <div className="dash-accordion-btn purple">
                            <span>{showDashSection==="hikes" ? "Collapse" : "Expand"}</span>
                            <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="hikes" ? "−" : "＋"}</span>
                          </div>
                        </div>
                        {showDashSection==="hikes" && (
                          <div style={{padding: 16}}>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:12}}>
                              <div style={{fontSize:13, color:"#475569"}}>
                                Track complete organization-wide compensation progression, appraisal percentage revisions, and historical audit logs.
                              </div>
                              <button 
                                type="button" 
                                className="btn btn-primary btn-sm" 
                                style={{background:"#6d28d9", borderColor:"#6d28d9", color:"#fff", fontWeight:700, display:"inline-flex", alignItems:"center", gap:6}}
                                onClick={()=>setModal("hike_report")}
                              >
                                <span>📜</span> Open Complete Hike Audit Report &amp; Excel →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Employee Lifecycle Accordion */}
                  <div className="dash-accordion" style={{border: "1px solid #cbd5e1"}}>
                    <div className="dash-accordion-header lifecycle" onClick={()=>setShowDashSection(p=>p==="lifecycle"?"":"lifecycle")}>
                      <div style={{fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 8}}>
                        <span>🔄</span> Employee Lifecycle Changes — {onboarded.length} Joined, {exited.length} Left
                      </div>
                      <div className="dash-accordion-btn">
                        <span>{showDashSection==="lifecycle" ? "Collapse" : "Expand"}</span>
                        <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="lifecycle" ? "−" : "＋"}</span>
                      </div>
                    </div>
                    {showDashSection==="lifecycle" && (
                      <div style={{padding: 16}}>
                        <div style={{display:"flex", gap:20, flexWrap:"wrap"}}>
                          <div style={{flex:1, minWidth:250}}>
                            <div style={{fontWeight:700, color:"#065f46", marginBottom:8}}>🟢 Onboarded ({onboarded.length})</div>
                            {onboarded.length === 0 ? <div style={{color:"#64748b"}}>No new joiners this month.</div> : (
                              <table className="dash-table">
                                <thead><tr><th>Name</th><th>Date of Joining</th></tr></thead>
                                <tbody>
                                  {onboarded.map((v,i)=><tr key={i}><td><button type="button" className="dash-employee-link" onClick={() => openEmployee(companyEmps.find(employee => employee.id === v.id))}>{v.name}</button></td><td>{fmtDate(v.doj)}</td></tr>)}
                                </tbody>
                              </table>
                            )}
                          </div>
                          <div style={{flex:1, minWidth:250}}>
                            <div style={{fontWeight:700, color:"#991b1b", marginBottom:8}}>🔴 Exited ({exited.length})</div>
                            {exited.length === 0 ? <div style={{color:"#64748b"}}>No exits this month.</div> : (
                              <table className="dash-table">
                                <thead><tr><th>Name</th><th>Last Working Day</th></tr></thead>
                                <tbody>
                                  {exited.map((v,i)=><tr key={i}><td><button type="button" className="dash-employee-link" onClick={() => openEmployee(companyEmps.find(employee => employee.id === v.id))}>{v.name}</button></td><td>{fmtDate(v.date_of_exit)}</td></tr>)}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* OT Violations Accordion */}
                  <div className="dash-accordion" style={{border: "1px solid "+(otViolators.length>0?"#fca5a5":"#a7f3d0")}}>
                    <div className={"dash-accordion-header " + (otViolators.length>0?"compliance-warn":"compliance-ok")} onClick={()=>setShowDashSection(p=>p==="ot"?"":"ot")}>
                      <div style={{fontWeight: 700, color: otViolators.length>0?"#991b1b":"#065f46", display:"flex", alignItems:"center", gap: 8}}>
                        <span>{otViolators.length>0?"⚠️":"✅"}</span> Compliance Violations (OT &gt; 31 Eq. Days) — {otViolators.length} Employees
                      </div>
                      <div className="dash-accordion-btn">
                        <span>{showDashSection==="ot" ? "Collapse" : "Expand"}</span>
                        <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="ot" ? "−" : "＋"}</span>
                      </div>
                    </div>
                    {showDashSection==="ot" && (
                      <div style={{padding: 16}}>
                        {otViolators.length === 0 ? <div style={{color:"#64748b"}}>All compliance checks passed. No violations.</div> : (
                          <table className="dash-table">
                            <thead><tr><th>Employee Name</th><th>Equivalent Days Worked</th><th>Reason</th></tr></thead>
                            <tbody>
                              {otViolators.map((v,i)=>(
                                <tr key={i}>
                                  <td><button type="button" className="dash-employee-link" onClick={() => openEmployee(companyEmps.find(employee => employee.id === v.id))}>{v.name}</button></td>
                                  <td style={{color:"#dc2626", fontWeight:600}}>{v.days.toFixed(1)} days</td>
                                  <td style={{color:"#64748b"}}>Exceeds maximum legal 31-day limit for a single month.</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>

                  {/* EPS Exemptions Accordion */}
                  <div className="dash-accordion" style={{border: "1px solid "+(epsExemptedList.length>0?"#fde68a":"#cbd5e1")}}>
                    <div className={"dash-accordion-header " + (epsExemptedList.length>0?"eps-active":"eps-none")} onClick={()=>setShowDashSection(p=>p==="eps"?"":"eps")}>
                      <div style={{fontWeight: 700, color: epsExemptedList.length>0?"#92400e":"#1e293b", display: "flex", alignItems: "center", gap: 8}}>
                        <span>⚠️</span> EPS Exempted (Age 58+) — {epsExemptedList.length} Employees
                      </div>
                      <div className="dash-accordion-btn">
                        <span>{showDashSection==="eps" ? "Collapse" : "Expand"}</span>
                        <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="eps" ? "−" : "＋"}</span>
                      </div>
                    </div>
                    {showDashSection==="eps" && (
                      <div style={{padding: 16}}>
                        {epsExemptedList.length === 0 ? <div style={{color:"#64748b"}}>No employees are currently exempted from EPS.</div> : (
                          <>
                            <div style={{fontSize:13, color:"#64748b", marginBottom:12}}>These employees are 58 or older. Under EPF rules, they no longer contribute to the Pension scheme. The employer's full 12% contribution is routed to the EPF bucket.</div>
                            <table className="dash-table">
                              <thead><tr><th>Employee Name</th><th>Status</th></tr></thead>
                              <tbody>
                                {epsExemptedList.map((employee,i)=>(
                                  <tr key={i}>
                                    <td><button type="button" className="dash-employee-link" onClick={() => openEmployee(employee)}>{employee.name}</button></td>
                                    <td style={{color:"#b45309"}}>EPS Disabled (100% to EPF)</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* PF Exemptions Accordion */}
                  <div className="dash-accordion" style={{border: "1px solid #cbd5e1"}}>
                    <div className="dash-accordion-header pf-inelig" onClick={()=>setShowDashSection(p=>p==="pf"?"":"pf")}>
                      <div style={{fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 8}}>
                        Employees Not Eligible for PF — {noPfList.length} Employees
                      </div>
                      <div className="dash-accordion-btn">
                        <span>{showDashSection==="pf" ? "Collapse" : "Expand"}</span>
                        <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="pf" ? "−" : "＋"}</span>
                      </div>
                    </div>
                    {showDashSection==="pf" && (
                      <div style={{padding: 16}}>
                        {noPfList.length === 0 ? <div style={{color:"#64748b"}}>All employees are eligible for PF.</div> : (
                          <table className="dash-table">
                            <thead><tr><th>Employee Name</th><th>Reason for Exemption</th></tr></thead>
                            <tbody>
                              {noPfList.map((v,i)=>(
                                <tr key={i}>
                                  <td><button type="button" className="dash-employee-link" onClick={() => openEmployee(companyEmps.find(employee => employee.id === v.id))}>{v.name}</button></td>
                                  <td style={{color:"#64748b"}}>{v.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ESI Exemptions Accordion */}
                  <div className="dash-accordion" style={{border: "1px solid #cbd5e1"}}>
                    <div className="dash-accordion-header esi-inelig" onClick={()=>setShowDashSection(p=>p==="esi"?"":"esi")}>
                      <div style={{fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 8}}>
                        Employees Not Eligible for ESI — {noEsiList.length} Employees
                      </div>
                      <div className="dash-accordion-btn">
                        <span>{showDashSection==="esi" ? "Collapse" : "Expand"}</span>
                        <span style={{fontSize: 13, lineHeight: 1}}>{showDashSection==="esi" ? "−" : "＋"}</span>
                      </div>
                    </div>
                    {showDashSection==="esi" && (
                      <div style={{padding: 16}}>
                        {noEsiList.length === 0 ? <div style={{color:"#64748b"}}>All employees are eligible for ESI.</div> : (
                          <table className="dash-table">
                            <thead><tr><th>Employee Name</th><th>Reason for Exemption</th></tr></thead>
                            <tbody>
                              {noEsiList.map((v,i)=>(
                                <tr key={i}>
                                  <td><button type="button" className="dash-employee-link" onClick={() => openEmployee(companyEmps.find(employee => employee.id === v.id))}>{v.name}</button></td>
                                  <td style={{color:"#64748b"}}>{v.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </>}
              </div>
            );
          })()}
        </>
      )}

      {/* ── VIEW: EMPLOYEE DETAIL */}
      {view==="employee_detail" && activeEmployee && (()=>{
        const eid=activeEmployee.id;
        const esi=esiStore[eid]||{esi_id:"",dispensary:"",members:[]};
        const pf=pfStore[eid]||{uan:"",pf_account:"",nominee:""};
        const pfSals=[...empSalaries].reverse().slice(0,12);
        const esiSals=empSalaries.filter(s=>s.esi_emp>0).slice(0,12);
        const last=empSalaries[0];
        return(<>
          <div className="back-btn" onClick={goBack}>← Back to Employees</div>
          <div className="emp-banner">
            {(() => {
              const photo = activeEmployee.emp_photo || activeCompany?.config?.employee_photos?.[activeEmployee.id] || activeCompany?.config?.employee_photos?.[activeEmployee.employee_code] || localStorage.getItem("tw_photo_" + activeEmployee.id) || (activeEmployee.employee_code ? localStorage.getItem("tw_photo_" + activeEmployee.employee_code) : "") || "";
              return photo ? (
                <img src={photo} alt={activeEmployee.name} style={{width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.1)"}} />
              ) : null;
            })()}
            <div style={{flex:1}}>
              <div className="emp-info-name">
                {activeEmployee.name}
                {activeEmployee.date_of_exit && <span style={{marginLeft:12,fontSize:12,background:"#fee2e2",color:"#b91c1c",padding:"4px 8px",borderRadius:6,border:"1px solid #fecaca",verticalAlign:"middle"}}>Resigned (LWD: {fmtDate(activeEmployee.date_of_exit)})</span>}
              </div>
              <div className="emp-info-details">
                <div className="emp-info-detail">Emp ID: <strong>{activeEmployee.employee_code}</strong></div>
                <div className="emp-info-detail">PAN: <strong>{activeEmployee.pan}</strong></div>
                <div className="emp-info-detail">DOJ: <strong>{fmtDate(activeEmployee.doj || activeEmployee.date_of_joining)}</strong></div>
                <div className="emp-info-detail">Company: <strong>{activeCompany?.name}</strong></div>
                <div className="emp-info-detail">Gross: <strong>{fmtINR(activeEmployee.basic_salary || activeEmployee.basic || 0)}</strong></div>
              </div>
            </div>
            <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap"}}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const codeClean = String(activeEmployee.employee_code || activeEmployee.id || "001").replace(/[^a-zA-Z0-9]/g, "");
                  const defaultRef = activeEmployee.appointment_ref_no || activeEmployee.ref_no || `REF/APT/${codeClean}/${new Date().getFullYear()}`;
                  const userRef = window.prompt("Enter Reference Number for Appointment Letter (leave blank to omit):", defaultRef);
                  if (userRef === null) return;
                  generateAppointmentLetterPDF(activeEmployee, activeCompany, userRef);
                }}
                title="Generate Formal Appointment Letter PDF"
              >
                📄 Appointment Letter
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setModal("issueCertificate");
                }}
                title="Issue Official Salary / Employment Certificate PDF"
              >
                📜 Issue Certificate
              </button>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
            </div>
          </div>

          {last&&<div className="sal-summary">
            <div className="sal-card"><div className="sal-card-label">Last Month</div><div className="sal-card-val blue">{MONTHS[last.month].slice(0,3)} {last.year}</div></div>
            <div className="sal-card"><div className="sal-card-label">Gross</div><div className="sal-card-val">{fmtINR(last.gross)}</div></div>
            <div className="sal-card"><div className="sal-card-label">Net Pay</div><div className="sal-card-val green">{fmtINR(last.net)}</div></div>
            <div className="sal-card"><div className="sal-card-label">PF Total</div><div className="sal-card-val">{fmtINR(last.pf_emp+(last.epf_er||0)+(last.eps||0))}</div></div>
            <div className="sal-card"><div className="sal-card-label">Records</div><div className="sal-card-val">{empSalaries.length} months</div></div>
          </div>}

          <div className="tab-nav">
            {(()=>{
              const esiMissing = activeCompany?.config?.esi_applicable && !esi.esi_id;
              const pfMissing  = activeCompany?.config?.pf_applicable  && !pf.uan;
              const bankMissing = !activeEmployee.bank_account;
              const warnMap = {esi:esiMissing, pf:pfMissing, profile:bankMissing};
              const tabs = [["employee","🧑 Employee"],["salary","💰 Salary"],["esi","🏥 ESI"],["pf","🏦 PF"],["profile","👤 Profile"]];
              if (activeCompany?.config?.leave_module_enabled === true) {
                tabs.push(["leaves", "🏖️ Leaves"]);
              }
              if (activeCompany?.config?.org_culture_enabled === true) {
                tabs.push(["org", "🌳 Team & Hierarchy"]);
              }
              return tabs.map(([k,lbl])=>(
                <button key={k} className={"tab-btn"+(empTab===k?" active":"")+(warnMap[k]?" incomplete":"")} onClick={()=>setEmpTab(k)}>{lbl}</button>
              ));
            })()}
          </div>

          {/* EMPLOYEE TAB */}
          {empTab==="employee"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">🧑 Employee Overview</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
                </div>
                <div className="info-grid">
                  <div className="info-field"><div className="info-label">Full Name</div><div className="info-val">{activeEmployee.name||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Father's Name</div><div className="info-val">{activeEmployee.father_name||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">Employee Code</div><div className="info-val mono">{activeEmployee.employee_code||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Department</div><div className="info-val">{activeEmployee.department||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Designation</div><div className="info-val">{activeEmployee.designation||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Date of Joining</div><div className="info-val">{activeEmployee.doj?new Date(activeEmployee.doj).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}):<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Phone</div><div className="info-val">{activeEmployee.phone||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Email</div><div className="info-val">{activeEmployee.email||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Gender</div><div className="info-val" style={{textTransform:"capitalize"}}>{activeEmployee.gender||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Employment Status</div><div className="info-val"><EmpStatusBadge status={activeEmployee.employment_status} /></div></div>
                  <div className="info-field"><div className="info-label">Gross Salary</div><div className="info-val" style={{color:"#6fcf97",fontFamily:"monospace"}}>{fmtINR(activeEmployee.basic||0)}</div></div>
                  <div className="info-field"><div className="info-label">PAN</div><div className="info-val mono">{activeEmployee.pan||<span className="na">—</span>}</div></div>
                  {(() => {
                    const mgrId = activeCompany?.config?.reporting_hierarchy?.[activeEmployee.id] || (activeEmployee.employee_code && activeCompany?.config?.reporting_hierarchy?.[activeEmployee.employee_code]);
                    const mgr = companyEmps.find(e => e.id === mgrId || (e.employee_code && e.employee_code === mgrId));
                    const reports = companyEmps.filter(e => {
                      const m = activeCompany?.config?.reporting_hierarchy?.[e.id] || (e.employee_code && activeCompany?.config?.reporting_hierarchy?.[e.employee_code]);
                      return m === activeEmployee.id || (activeEmployee.employee_code && m === activeEmployee.employee_code);
                    });
                    return (
                      <>
                        <div className="info-field">
                          <div className="info-label">Reporting Manager</div>
                          <div className="info-val">
                            {mgr ? (
                              <span
                                onClick={() => setActiveEmployee(mgr)}
                                style={{ color: "#2563eb", cursor: "pointer", fontWeight: 700, textDecoration: "underline" }}
                                title="Click to view manager profile"
                              >
                                👔 {mgr.name} ({mgr.designation || "Manager"})
                              </span>
                            ) : (
                              <span className="na">No direct manager</span>
                            )}
                          </div>
                        </div>
                        <div className="info-field">
                          <div className="info-label">Direct Reports</div>
                          <div className="info-val">
                            {reports.length > 0 ? (
                              <span style={{ color: "#16a34a", fontWeight: 700 }}>
                                👥 {reports.length} {reports.length === 1 ? "direct report" : "direct reports"}
                              </span>
                            ) : (
                              <span className="na">0 direct reports</span>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">🚨 Emergency Contact</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
                </div>
                <div className="info-grid">
                  <div className="info-field"><div className="info-label">Name</div><div className="info-val">{activeEmployee.emergency_name||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">Relation</div><div className="info-val" style={{textTransform:"capitalize"}}>{activeEmployee.emergency_relation||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Phone</div><div className="info-val mono">{activeEmployee.emergency_phone||<span className="na">Not set</span>}</div></div>
                </div>
              </div>
            </div>
          )}

          {/* Salary tab structure begins */}

          {empTab==="salary"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Career & Salary Revision History */}
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">📈 Career & Compensation Progression</div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setHikeForm({
                        date: salaryToday(),
                        newGross: activeEmployee.basic_salary || activeEmployee.basic || "",
                        note: "Annual Appraisal"
                      });
                      setHikeModal(true);
                    }}
                  >
                    ＋ Record Salary Revision
                  </button>
                </div>

                <div style={{padding:"6px 0"}}>
                  {(() => {
                    const hikes = activeCompany?.config?.salary_hikes?.[activeEmployee.id] || [];
                    const currentGross = parseFloat(activeEmployee.basic_salary || activeEmployee.basic || 0);

                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:14}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,background:"#f8fafc",padding:"12px 16px",borderRadius:8,border:"1px solid #e2e8f0"}}>
                          <div>
                            <span style={{fontSize:12,color:"#64748b"}}>Current Compensation:</span>{" "}
                            <strong style={{fontSize:16,color:"#16a34a"}}>{fmtINR(currentGross)}/month</strong>
                            <span style={{fontSize:12,color:"#64748b",marginLeft:8}}>({fmtINR(currentGross * 12)} Annual CTC)</span>
                          </div>
                          {activeEmployee.doj && (
                            <div style={{fontSize:12,color:"#64748b"}}>
                              Joined on <strong>{fmtDate(activeEmployee.doj)}</strong>
                            </div>
                          )}
                        </div>

                        {hikes.length === 0 ? (
                          <div style={{fontSize:12.5,color:"#64748b",padding:"12px 16px",background:"#fdfdfe",borderRadius:8,border:"1px dashed #cbd5e1"}}>
                            No prior salary revisions logged. Click <strong>"＋ Record Salary Revision"</strong> to log promotions or appraisals.
                          </div>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",paddingLeft:12}}>
                            {sortSalaryRevisions(hikes).map((h, idx) => (
                              <div key={idx} className="hike-timeline-item">
                                <div className="hike-timeline-dot" />
                                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                                  <strong style={{fontSize:13.5,color:"#1e293b"}}>{h.note || "Salary Revision"}</strong>
                                  <span style={{
                                    fontSize:11,
                                    fontWeight:800,
                                    padding:"1px 8px",
                                    borderRadius:10,
                                    background: h.hike_pct >= 0 ? "#f0fdf4" : "#fef2f2",
                                    color: h.hike_pct >= 0 ? "#16a34a" : "#dc2626",
                                    border: `1px solid ${h.hike_pct >= 0 ? "#bbf7d0" : "#fecaca"}`
                                  }}>
                                    {h.hike_pct >= 0 ? `+${h.hike_pct}%` : `${h.hike_pct}%`}
                                  </span>
                                  <span style={{fontSize:11.5,color:"#94a3b8"}}>Effective {fmtDate(h.effective_date || h.date)}</span>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    style={{padding: "2px 8px", fontSize: 11.5, marginLeft: "auto", border: "1px solid #cbd5e1", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4}}
                                    onClick={() => generateIncrementLetterPDF({ employee: activeEmployee, company: activeCompany, hikeRecord: h })}
                                    title="Download Official Increment / Appraisal Letter"
                                  >
                                    📄 Increment Letter
                                  </button>
                                </div>
                                <div style={{fontSize:12.5,color:"#475569",marginTop:4}}>
                                  Revised from <span style={{textDecoration:"line-through",color:"#94a3b8"}}>{fmtINR(h.old_salary)}</span> to <strong style={{color:"#16a34a"}}>{fmtINR(h.new_salary)}/mo</strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="table-wrap">
                <div className="table-header">
                  <div className="table-title">Salary History ({empSalaries.length})</div>
                  <button className="btn btn-primary btn-sm" onClick={()=>{setEditTarget(null);setFormMsg("");setModal("addSalary");}}>＋ Add Month</button>
                </div>
              <div style={{overflowX:"auto"}}>
              <table>
                <thead><tr>
                  <th>Month</th><th style={{textAlign:"right"}}>{"Basic " + (activeCompany?.config?.custom_basic_enabled ? "(Custom)" : "(" + (activeCompany?.config?.basic_pct !== undefined ? activeCompany?.config?.basic_pct : 50) + "%)")}</th><th style={{textAlign:"right"}}>{"HRA " + (activeCompany?.config?.hra_customised ? "(Custom)" : (activeCompany?.config?.hra_type==="fixed" ? "(Fixed)" : "(" + (activeCompany?.config?.hra_value !== undefined ? activeCompany?.config?.hra_value : (activeCompany?.config?.hra_pct ?? 40)) + "%)"))}</th>
                  {activeCompany?.config?.da&&<th style={{textAlign:"right"}}>DA</th>}
                  <th style={{textAlign:"right"}}>Other Allow.</th>
                  {activeCompany?.config?.conveyance&&<th style={{textAlign:"right"}}>Conv.</th>}
                  {activeCompany?.config?.medical&&<th style={{textAlign:"right"}}>Medical</th>}
                  <th style={{textAlign:"right"}}>Abs.Ded</th><th style={{textAlign:"right"}}>Gross</th>
                  {activeCompany?.config?.pf_applicable&&<th style={{textAlign:"right"}}>PF</th>}
                  {activeCompany?.config?.esi_applicable&&<th style={{textAlign:"right"}}>ESI</th>}
                  <th style={{textAlign:"right"}}>PT</th>
                  {activeCompany?.config?.tds_applicable&&<th style={{textAlign:"right"}}>TDS</th>}
                  <th className="td-net">Net Pay</th><th style={{textAlign:"right"}}>CTC</th>
                  <th>Actions</th>
                </tr></thead>
                <tbody>
                  {empSalaries.length===0
                    ? <tr><td colSpan={16} style={{textAlign:"center",padding:"36px",color:"#2a3050"}}>No records.</td></tr>
                    : empSalaries.map(s=>(
                      <tr key={s.id}>
                        <td className="td-month">{MONTHS[s.month].slice(0,3)} {s.year}</td>
                        <td className="td-num">{fmtINR(s.basic)}</td>
                        <td className="td-num">{fmtINR(s.hra)}</td>
                        {activeCompany?.config?.da&&<td className="td-num">{fmtINR(s.da||0)}</td>}
                        <td className="td-num">{fmtINR(s.special)}</td>
                        {activeCompany?.config?.conveyance&&<td className="td-num">{fmtINR(s.conveyance)}</td>}
                        {activeCompany?.config?.medical&&<td className="td-num">{fmtINR(s.medical)}</td>}
                        <td className="td-num" style={{color:"#f76f6f"}}>{s.absence_deduction>0?"−"+fmtINR(s.absence_deduction):"—"}</td>
                        <td className="td-num" style={{color:"#1e293b",fontWeight:600}}>{fmtINR(s.gross)}</td>
                        {activeCompany?.config?.pf_applicable&&<td className="td-num">{fmtINR(s.pf_emp)}</td>}
                        {activeCompany?.config?.esi_applicable&&<td className="td-num">{s.esi_emp>0?fmtINR(s.esi_emp):"—"}</td>}
                        <td className="td-num">{fmtINR(s.pt)}</td>
                        {activeCompany?.config?.tds_applicable&&<td className="td-num">{fmtINR(s.tds)}</td>}
                        <td className="td-net">{fmtINR(s.net)}</td>
                        <td className="td-num" style={{color:"#9aa0bb"}}>{fmtINR(s.ctc)}</td>
                        <td><div className="td-actions" style={{display: "flex", gap: 4}}>
                          <button className="btn btn-ghost btn-xs" onClick={()=>{setEditTarget({...s});setFormMsg("");setModal("editSalary");}}>Edit</button>
                          <button className="btn btn-primary btn-xs" onClick={()=>{
                            const pDays = (activeCompany?.config?.working_days === -1 ? new Date(s.year||2026, s.month||1, 0).getDate() : (activeCompany?.config?.working_days||26)) - (s.absent_days||0);
                            exportPDF(activeEmployee,{...s,present_days:pDays},activeCompany);
                          }}>PDF</button>
                          <button className="btn btn-secondary btn-xs" title={`Send secure payslip to ${activeEmployee.name}`} onClick={() => {
                            const pDays = (activeCompany?.config?.working_days === -1 ? new Date(s.year||2026, s.month||1, 0).getDate() : (activeCompany?.config?.working_days||26)) - (s.absent_days||0);
                            handleOpenSendPayslipEmail(activeEmployee, {...s, present_days: pDays, absent_days: s.absent_days || 0, month: s.month, year: s.year}, { month: s.month, year: s.year });
                          }} style={{padding: "0 6px", fontSize: 13}}>📧</button>
                          <button className="btn btn-danger btn-xs" onClick={()=>{setDeleteTarget(s);setModal("deleteSalary");}}>Del</button>
                        </div></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
              </div>
            </div>
            </div>
          )}

          {/* ESI TAB */}
          {empTab==="esi"&&(
            <>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">🏥 ESI Registration</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setFormMsg("");setModal("editESI");}}>Edit</button>
                </div>
                {!activeCompany?.config?.esi_applicable
                  ? <div style={{color:"#9ba8c8",fontSize:13}}>ESI is not applicable for {activeCompany?.name}.</div>
                  : <div className="info-grid">
                      <div className="info-field"><div className="info-label">ESI Number</div><div className="info-val mono">{activeEmployee.esi_id||<span className="na">Not set</span>}</div></div>
                    </div>
                }
              </div>
              {esiSals.length>0&&(
                <div className="table-wrap">
                  <div className="table-header"><div className="table-title">ESI Contributions (last {esiSals.length} months)</div></div>
                  <div style={{overflowX:"auto"}}>
                  <table>
                    <thead><tr><th>Month</th><th style={{textAlign:"right"}}>Gross</th><th style={{textAlign:"right"}}>Emp (0.75%)</th><th style={{textAlign:"right"}}>Employer (3.25%)</th><th style={{textAlign:"right"}}>Total</th></tr></thead>
                    <tbody>
                      {esiSals.map(s=>(
                        <tr key={s.id}>
                          <td className="td-month">{MONTHS[s.month].slice(0,3)} {s.year}</td>
                          <td className="td-num">{fmtINR(s.gross)}</td>
                          <td className="td-num" style={{color:"#f76f6f"}}>{fmtINR(s.esi_emp)}</td>
                          <td className="td-num" style={{color:"#e8a93a"}}>{fmtINR(s.esi_er)}</td>
                          <td className="td-num" style={{color:"#e8eaf0",fontWeight:700}}>{fmtINR(s.esi_emp+s.esi_er)}</td>
                        </tr>
                      ))}
                      <tr className="tot-row">
                        <td style={{textAlign:"left"}}>Total</td>
                        <td>{fmtINR(esiSals.reduce((a,s)=>a+s.gross,0))}</td>
                        <td style={{color:"#f76f6f"}}>{fmtINR(esiSals.reduce((a,s)=>a+s.esi_emp,0))}</td>
                        <td style={{color:"#e8a93a"}}>{fmtINR(esiSals.reduce((a,s)=>a+s.esi_er,0))}</td>
                        <td style={{color:"#6fcf97"}}>{fmtINR(esiSals.reduce((a,s)=>a+s.esi_emp+s.esi_er,0))}</td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* PROFILE TAB */}
          {empTab==="profile"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">👤 Personal Details</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
                </div>
                <div className="info-grid">
                  <div className="info-field"><div className="info-label">Full Name</div><div className="info-val">{activeEmployee.name||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Father's Name</div><div className="info-val">{activeEmployee.father_name||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">PAN</div><div className="info-val mono">{activeEmployee.pan||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Date of Birth</div><div className="info-val">{activeEmployee.dob?new Date(activeEmployee.dob).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}):<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Age</div><div className="info-val">{activeEmployee.dob?(calcAge(activeEmployee.dob)+" yrs"):(activeEmployee.age||<span className="na">—</span>)}</div></div>
                  <div className="info-field"><div className="info-label">Date of Joining</div><div className="info-val">{activeEmployee.doj?new Date(activeEmployee.doj).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}):<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Phone</div><div className="info-val">{activeEmployee.phone||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Email</div><div className="info-val">{activeEmployee.email||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Department</div><div className="info-val">{activeEmployee.department||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Designation</div><div className="info-val">{activeEmployee.designation||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Gender</div><div className="info-val" style={{textTransform:"capitalize"}}>{activeEmployee.gender||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Marital Status</div><div className="info-val" style={{textTransform:"capitalize"}}>{activeEmployee.marital_status||<span className="na">—</span>}</div></div>
                  <div className="info-field"><div className="info-label">Employment Status</div><div className="info-val"><EmpStatusBadge status={activeEmployee.employment_status} /></div></div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">🏦 Bank Details</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
                </div>
                <div className="info-grid">
                  <div className="info-field"><div className="info-label">Bank Name</div><div className="info-val">{activeEmployee.bank_name||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">Account Number</div><div className="info-val mono">{activeEmployee.bank_account||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">IFSC Code</div><div className="info-val mono">{activeEmployee.bank_ifsc||<span className="na">Not set</span>}</div></div>
                  <div className="info-field"><div className="info-label">Branch</div><div className="info-val">{activeEmployee.bank_branch||<span className="na">Not set</span>}</div></div>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">📋 Statutory Numbers</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>Edit</button>
                </div>
                {(()=>{
                  const eid=activeEmployee.id;
                  const pf=pfStore[eid]||{};
                  const esi=esiStore[eid]||{};
                  const uan=activeEmployee.uan||pf.uan||"";
                  const esiId=activeEmployee.esi_id||esi.esi_id||"";
                  return(
                  <div className="info-grid">
                    <div className="info-field"><div className="info-label">UAN (PF)</div><div className="info-val mono">{uan||<span className="na">—</span>}</div></div>
                    <div className="info-field"><div className="info-label">ESI Number</div><div className="info-val mono">{esiId||<span className="na">—</span>}</div></div>
                  </div>);
                })()}
              </div>
              {(activeEmployee.family_members && activeEmployee.family_members.length > 0) && (
                <div className="info-card">
                  <div className="info-card-header">
                    <div className="info-card-title">👨‍👩‍👧 Family Members ({activeEmployee.family_members.length})</div>
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                    {activeEmployee.family_members.map((fm, fidx) => (
                      <div key={fidx} style={{display:'flex', alignItems:'center', gap:12, background:'#f8fafc', padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0'}}>
                        {fm.photo ? (
                          <img src={fm.photo} alt={fm.name} style={{width:40, height:40, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'1.5px solid #3b82f6'}} />
                        ) : (
                          null
                        )}
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13, fontWeight:700, color:'#1e293b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{fm.name}</div>
                          <div style={{fontSize:11.5, color:'#64748b'}}>{fm.relationship || 'Member'} {fm.dob ? `• ${calcAge(fm.dob)} yrs` : ''}</div>
                          {fm.residing && <div style={{fontSize:11, color:'#94a3b8'}}>Residing: {fm.residing}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Documents tab removed */}

          {empTab==="pf"&&(
            <>
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-title">🏦 PF Registration</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setFormMsg("");setModal("editPF");}}>Edit</button>
                </div>
                {!activeCompany?.config?.pf_applicable
                  ? <div style={{color:"#9ba8c8",fontSize:13}}>PF is not applicable for {activeCompany?.name}.</div>
                  : <div className="info-grid">
                      <div className="info-field"><div className="info-label">UAN (PF)</div><div className="info-val mono">{pf.uan||activeEmployee.uan||<span className="na">Not set</span>}</div></div>
                    </div>
                }
              </div>
              {pfSals.length>0&&(()=>{
                let running=0;
                return(
                <div className="table-wrap">
                  <div className="table-header">
                    <div className="table-title">PF Contributions (last {pfSals.length} months)</div>
                    <div style={{fontSize:12,color:"#6fcf97",fontWeight:700}}>Corpus: {fmtINR(pfSals.reduce((a,s)=>a+s.pf_emp+(s.epf_er||0)+(s.eps||0),0))}</div>
                  </div>
                  <div style={{overflowX:"auto"}}>
                  <table>
                    <thead><tr><th>Month</th><th style={{textAlign:"right"}}>PF Wage</th><th style={{textAlign:"right"}}>Emp (12%)</th><th style={{textAlign:"right"}}>EPF (3.67%)</th><th style={{textAlign:"right"}}>EPS (8.33%)</th><th style={{textAlign:"right"}}>EDLI (0.5%)</th><th style={{textAlign:"right"}}>EPF Admin (0.5%)</th><th style={{textAlign:"right"}}>Monthly Total</th><th style={{textAlign:"right"}}>Employer Total</th><th style={{textAlign:"right"}}>Running Total</th></tr></thead>
                    <tbody>
                      {pfSals.map(s=>{
                        const pw=s.pfWage??Math.min(s.basic,25000);
                        const edli=Math.round(Math.min(pw,25000)*0.005);  // EDLI capped at 25K
                        const epfAdmin=Math.round(pw*0.005);              // EPF Admin — no cap
                        const erTotal=(s.epf_er||0)+(s.eps||0)+edli+epfAdmin;
                        const monthlyTotal=s.pf_emp+erTotal;
                        running+=monthlyTotal;
                        return(
                        <tr key={s.id}>
                          <td className="td-month">{MONTHS[s.month].slice(0,3)} {s.year}</td>
                          <td className="td-num">{fmtINR(pw)}</td>
                          <td className="td-num" style={{color:"#f76f6f"}}>{fmtINR(s.pf_emp)}</td>
                          <td className="td-num" style={{color:"#e8a93a"}}>{fmtINR(s.epf_er||0)}</td>
                          <td className="td-num" style={{color:"#4f8ef7"}}>{fmtINR(s.eps||0)}</td>
                          <td className="td-num" style={{color:"#9ba8c8"}}>{fmtINR(edli)}</td>
                          <td className="td-num" style={{color:"#9ba8c8"}}>{fmtINR(epfAdmin)}</td>
                          <td className="td-num" style={{color:"#e8eaf0",fontWeight:600}}>{fmtINR(monthlyTotal)}</td>
                          <td className="td-num" style={{color:"#e8a93a",fontWeight:600}}>{fmtINR(erTotal)}</td>
                          <td className="td-num" style={{color:"#6fcf97",fontWeight:600}}>{fmtINR(running)}</td>
                        </tr>
                      );})}
                      <tr className="tot-row">
                        <td style={{textAlign:"left"}}>Total</td><td></td>
                        <td style={{color:"#f76f6f"}}>{fmtINR(pfSals.reduce((a,s)=>a+s.pf_emp,0))}</td>
                        <td style={{color:"#e8a93a"}}>{fmtINR(pfSals.reduce((a,s)=>a+(s.epf_er||0),0))}</td>
                        <td style={{color:"#4f8ef7"}}>{fmtINR(pfSals.reduce((a,s)=>a+(s.eps||0),0))}</td>
                        <td style={{color:"#9ba8c8"}}>{fmtINR(pfSals.reduce((a,s)=>{const pw=s.pfWage??Math.min(s.basic,25000);return a+Math.round(Math.min(pw,25000)*0.005);},0))}</td>
                        <td style={{color:"#9ba8c8"}}>{fmtINR(pfSals.reduce((a,s)=>{const pw=s.pfWage??Math.min(s.basic,25000);return a+Math.round(pw*0.005);},0))}</td>
                        <td style={{color:"#e8eaf0"}}>{fmtINR(pfSals.reduce((a,s)=>{const pw=s.pfWage??Math.min(s.basic,25000);const edli=Math.round(Math.min(pw,25000)*0.005);const epfAdmin=Math.round(pw*0.005);return a+s.pf_emp+(s.epf_er||0)+(s.eps||0)+edli+epfAdmin;},0))}</td>
                        <td style={{color:"#e8a93a"}}>{fmtINR(pfSals.reduce((a,s)=>{const pw=s.pfWage??Math.min(s.basic,25000);const edli=Math.round(Math.min(pw,25000)*0.005);const epfAdmin=Math.round(pw*0.005);return a+(s.epf_er||0)+(s.eps||0)+edli+epfAdmin;},0))}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>);
              })()}
            </>
          )}

          {empTab==="leaves" && activeCompany?.config?.leave_module_enabled === true && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div className="info-card">
                <div className="info-card-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div className="info-card-title">🏖️ Leave Balances &amp; Quota ({new Date().getFullYear()})</div>
                </div>
                {(()=>{
                  const metrics = getEmployeeLeaveMetrics(activeEmployee, activeCompany?.config, allCompanyLeaves, null, new Date().getFullYear());
                  return (
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))",gap:12,marginTop:12}}>
                      {metrics.map(m => (
                        <div key={m.leaveType} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:14}}>
                          <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",marginBottom:4}}>{m.leaveType}</div>
                          <div style={{fontSize:22,fontWeight:800,color: m.remainingBalance > 0 ? "#0f172a" : "#dc2626"}}>{m.remainingBalance} <span style={{fontSize:12,color:"#64748b",fontWeight:600}}>/ {m.totalQuota} days</span></div>
                          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>Used: {m.approvedUsed} days {m.pendingUsed > 0 ? `(${m.pendingUsed}d pending)` : ""}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="info-card">
                <div className="info-card-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div className="info-card-title">📜 Leave Request History</div>
                </div>
                {(()=>{
                  const empLeavesList = allCompanyLeaves.filter(l => l.employee_id === activeEmployee.id);
                  if (empLeavesList.length === 0) {
                    return <div style={{padding:"24px",textAlign:"center",color:"#64748b",fontSize:13}}>No leave requests found for this employee.</div>;
                  }
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:12}}>
                      {empLeavesList.map(req => {
                        const statusBg = req.status === "approved" ? "#f1f5f9" : req.status === "rejected" ? "#fee2e2" : req.status === "cancelled" ? "#f8fafc" : "#fef9c3";
                        const statusColor = req.status === "approved" ? "#334155" : req.status === "rejected" ? "#dc2626" : req.status === "cancelled" ? "#94a3b8" : "#b45309";
                        const statusBorder = req.status === "approved" ? "1px solid #cbd5e1" : req.status === "rejected" ? "1px solid #fecaca" : req.status === "cancelled" ? "1px solid #e2e8f0" : "1px solid #fef08a";
                        return (
                          <div key={req.id} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                                <strong style={{color:"#0f172a",fontSize:14}}>{req.leave_type}</strong>
                                <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:10,background:statusBg,color:statusColor,border:statusBorder,textTransform:"uppercase"}}>{req.status}</span>
                              </div>
                              <div style={{fontSize:12,color:"#475569"}}>
                                {new Date(req.start_date).toLocaleDateString()} to {new Date(req.end_date).toLocaleDateString()} ({req.days} days)
                              </div>
                              {req.reason && <div style={{fontSize:11,color:"#64748b",marginTop:2}}>Reason: "{req.reason}"</div>}
                              {req.status === "rejected" && req.rejection_reason && <div style={{fontSize:11,color:"#dc2626",marginTop:2}}>Rejection note: "{req.rejection_reason}"</div>}
                            </div>
                            <div style={{fontSize:11,color:"#94a3b8",textAlign:"right"}}>
                              Applied: {new Date(req.created_at).toLocaleDateString()}
                              {req.reviewed_by && <div>By: {req.reviewed_by}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ORG & TEAM TAB */}
          {empTab==="org" && activeCompany?.config?.org_culture_enabled === true && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {(()=>{
                const reportingHierarchy = activeCompany?.config?.reporting_hierarchy || {};
                const mgrId = reportingHierarchy[activeEmployee.id] || reportingHierarchy[activeEmployee.employee_code];
                const manager = companyEmps.find(e => e.id === mgrId || e.employee_code === mgrId);
                const directReports = companyEmps.filter(e => reportingHierarchy[e.id] === activeEmployee.id || reportingHierarchy[e.employee_code] === activeEmployee.id || reportingHierarchy[e.id] === activeEmployee.employee_code);
                const peers = companyEmps.filter(e => {
                  if (e.id === activeEmployee.id) return false;
                  const eMgr = reportingHierarchy[e.id] || reportingHierarchy[e.employee_code];
                  if (mgrId && eMgr && eMgr === mgrId) return true;
                  if (activeEmployee.department && e.department && e.department.toLowerCase() === activeEmployee.department.toLowerCase()) return true;
                  return false;
                });

                return (
                  <>
                    {/* Manager Card */}
                    <div className="info-card">
                      <div className="info-card-header">
                        <div className="info-card-title">👔 Reporting Manager</div>
                        <button className="btn btn-ghost btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>
                          Change Manager
                        </button>
                      </div>
                      {manager ? (
                        <div style={{display:"flex",alignItems:"center",gap:16,padding:"14px 18px",background:"#f8fafc",borderRadius:10,border:"1px solid #e2e8f0"}}>
                          {(()=>{
                            const photo = manager.emp_photo || activeCompany?.config?.employee_photos?.[manager.id] || activeCompany?.config?.employee_photos?.[manager.employee_code] || localStorage.getItem("tw_photo_" + manager.id) || (manager.employee_code ? localStorage.getItem("tw_photo_" + manager.employee_code) : "");
                            return photo ? (
                              <img src={photo} alt={manager.name} style={{width:50,height:50,borderRadius:"50%",objectFit:"cover",border:"2px solid #cbd5e1",flexShrink:0}} />
                            ) : null;
                          })()}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:15,fontWeight:700,color:"#0f172a"}}>{manager.name}</div>
                            <div style={{fontSize:13,color:"#64748b",marginTop:1}}>{manager.designation || "Manager"} · {manager.department || "General"}</div>
                            <div style={{fontSize:12,color:"#94a3b8",marginTop:4,display:"flex",gap:16,flexWrap:"wrap"}}>
                              {manager.email && <span>✉ {manager.email}</span>}
                              {manager.phone && <span>📞 {manager.phone}</span>}
                              <span>ID: <code style={{fontFamily:"monospace",fontWeight:600}}>{manager.employee_code}</code></span>
                            </div>
                          </div>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={()=>{setActiveEmployee(manager);setEmpTab("employee");}}
                            style={{whiteSpace:"nowrap"}}
                          >
                            View Profile →
                          </button>
                        </div>
                      ) : (
                        <div style={{padding:"16px",background:"#f0fdf4",borderRadius:10,border:"1px solid #bbf7d0",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
                          <div>
                            <div style={{fontWeight:700,color:"#166534",fontSize:14}}>No direct manager</div>
                            <div style={{fontSize:12.5,color:"#15803d",marginTop:2}}>
                              No reporting manager is assigned to this employee.
                            </div>
                          </div>
                          <button className="btn btn-secondary btn-sm" onClick={()=>{setEditTarget(activeEmployee);setFormMsg("");setView("employee_form");}}>
                            Assign Manager
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Direct Reports Card */}
                    <div className="info-card">
                      <div className="info-card-header">
                        <div className="info-card-title">👥 Direct Reports ({directReports.length})</div>
                        <span style={{fontSize:12,color:"#64748b"}}>Team members reporting to {activeEmployee.name}</span>
                      </div>
                      {directReports.length === 0 ? (
                        <div style={{padding:"16px",color:"#64748b",fontSize:13,background:"#f8fafc",borderRadius:8,border:"1px dashed #cbd5e1",textAlign:"center"}}>
                          No direct reports currently reporting to this employee.
                        </div>
                      ) : (
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))",gap:12}}>
                          {directReports.map(rep => {
                            const photo = rep.emp_photo || activeCompany?.config?.employee_photos?.[rep.id] || activeCompany?.config?.employee_photos?.[rep.employee_code] || localStorage.getItem("tw_photo_" + rep.id) || (rep.employee_code ? localStorage.getItem("tw_photo_" + rep.employee_code) : "");
                            return (
                              <div key={rep.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"#ffffff",borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
                                {photo ? (
                                  <img src={photo} alt={rep.name} style={{width:40,height:40,borderRadius:"50%",objectFit:"cover",flexShrink:0}} />
                                ) : null}
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rep.name}</div>
                                  <div style={{fontSize:11.5,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rep.designation || "Employee"} · {rep.department || "General"}</div>
                                </div>
                                <button className="btn btn-ghost btn-xs" onClick={()=>{setActiveEmployee(rep);setEmpTab("employee");}}>
                                  Profile
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Team Peers Card */}
                    <div className="info-card">
                      <div className="info-card-header">
                        <div className="info-card-title">🤝 Teammates & Peers ({peers.length})</div>
                        <span style={{fontSize:12,color:"#64748b"}}>Colleagues in {activeEmployee.department || "same department"} or under the same manager</span>
                      </div>
                      {peers.length === 0 ? (
                        <div style={{padding:"16px",color:"#64748b",fontSize:13,background:"#f8fafc",borderRadius:8,border:"1px dashed #cbd5e1",textAlign:"center"}}>
                          No peers found in this department or team.
                        </div>
                      ) : (
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))",gap:12}}>
                          {peers.map(peer => {
                            const photo = peer.emp_photo || activeCompany?.config?.employee_photos?.[peer.id] || activeCompany?.config?.employee_photos?.[peer.employee_code] || localStorage.getItem("tw_photo_" + peer.id) || (peer.employee_code ? localStorage.getItem("tw_photo_" + peer.employee_code) : "");
                            return (
                              <div key={peer.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"#ffffff",borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
                                {photo ? (
                                  <img src={photo} alt={peer.name} style={{width:40,height:40,borderRadius:"50%",objectFit:"cover",flexShrink:0}} />
                                ) : null}
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:13.5,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{peer.name}</div>
                                  <div style={{fontSize:11.5,color:"#64748b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{peer.designation || "Employee"} · {peer.department || "General"}</div>
                                </div>
                                <button className="btn btn-ghost btn-xs" onClick={()=>{setActiveEmployee(peer);setEmpTab("employee");}}>
                                  Profile
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>);
      })()}

      {/* ── MODALS ── */}
      {/* ── Record Salary Revision / Hike Modal ── */}
      {hikeModal && activeEmployee && (
        <div className="overlay" onClick={() => !savingHike && setHikeModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-title">📈 Record Salary Revision</div>
            <div className="modal-sub" style={{ color: "#9ba8c8", marginBottom: 16 }}>
              Log an appraisal increment, promotion hike, or salary revision for <strong>{activeEmployee.name}</strong>.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "#0f1420", padding: "12px 16px", borderRadius: 8, border: "1px solid #1e293b" }}>
                <div style={{ color: "#9ba8c8", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>Current Gross / Basic</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#6fcf97", marginTop: 2 }}>
                  {fmtINR(parseFloat(activeEmployee.basic_salary || activeEmployee.basic || 0))}/month
                  <span style={{ fontSize: 12, color: "#9ba8c8", marginLeft: 8, fontWeight: 400 }}>
                    ({fmtINR(parseFloat(activeEmployee.basic_salary || activeEmployee.basic || 0) * 12)} Annual CTC)
                  </span>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#9ba8c8", display: "block", marginBottom: 4 }}>
                  Effective from * (current month, payroll in draft)
                </label>
                <input
                  type="date"
                  className="input"
                  min={`${salaryToday().slice(0, 7)}-01`}
                  max={salaryToday()}
                  value={hikeForm.date || ""}
                  onChange={e => setHikeForm(prev => ({ ...prev, date: e.target.value }))}
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#9ba8c8", display: "block", marginBottom: 4 }}>
                  New Gross / Basic Salary (₹/month) *
                </label>
                <input
                  type="number"
                  className="input"
                  placeholder="e.g. 75000"
                  value={hikeForm.newGross || ""}
                  onChange={e => setHikeForm(prev => ({ ...prev, newGross: e.target.value }))}
                  style={{ width: "100%" }}
                />
                {(() => {
                  const cur = parseFloat(activeEmployee.basic_salary || activeEmployee.basic || 0);
                  const nxt = parseFloat(hikeForm.newGross || 0);
                  if (!isNaN(nxt) && nxt > 0 && cur > 0) {
                    const diff = nxt - cur;
                    const pct = Number(((diff / cur) * 100).toFixed(1));
                    return (
                      <div style={{ marginTop: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: "#9ba8c8" }}>Calculated Hike:</span>
                        <span style={{
                          fontWeight: 700,
                          color: pct >= 0 ? "#6fcf97" : "#f76f6f",
                          background: pct >= 0 ? "rgba(111, 207, 151, 0.15)" : "rgba(247, 111, 111, 0.15)",
                          padding: "2px 8px",
                          borderRadius: 4
                        }}>
                          {pct >= 0 ? `+${pct}%` : `${pct}%`} ({pct >= 0 ? `+${fmtINR(diff)}` : fmtINR(diff)})
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#9ba8c8", display: "block", marginBottom: 4 }}>
                  Revision Reason / Appraisal Note
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. Annual Appraisal FY26, Senior Promotion, Market Adjustment"
                  value={hikeForm.note || ""}
                  onChange={e => setHikeForm(prev => ({ ...prev, note: e.target.value }))}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className="modal-footer" style={{ marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setHikeModal(false)} disabled={savingHike}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveHike} disabled={savingHike}>
                {savingHike ? "Saving Revision..." : "✅ Apply & Save Revision"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Upload Preview Modal */}
      {modal==="uploadBulkPreview"&&editTarget&&Array.isArray(editTarget)&&(
        <div className="overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:680}}>
            <div className="modal-title">📊 Bulk Upload Preview — {editTarget.length} Companies</div>
            <div className="modal-sub" style={{color:"#9ba8c8",marginBottom:16}}>
              Review all companies before importing. Duplicates will be skipped automatically.
            </div>
            {formMsg&&<div className="err-msg" style={{marginBottom:12}}>⚠ {formMsg}</div>}
            <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
              {editTarget.map((co,i)=>(
                <div key={i} style={{background:"#0f1420",borderRadius:10,padding:"12px 16px",display:"flex",gap:16,alignItems:"flex-start",flexWrap:"wrap"}}>
                  <div style={{minWidth:180,flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#e8eaf0"}}>{co.name}</div>
                    {co.gst&&<div style={{fontSize:11,color:"#4f8ef7",fontFamily:"monospace"}}>{co.gst}</div>}
                    {co.contact&&<div style={{fontSize:11,color:"#9ba8c8",marginTop:2}}>👤 {co.contact}{co.phone?" · "+co.phone:""}</div>}
                    {co.email&&<div style={{fontSize:11,color:"#9ba8c8"}}>✉ {co.email}</div>}
                    {!co.name&&<div style={{fontSize:11,color:"#f76f6f"}}>⚠ Missing name — will be skipped</div>}
                  </div>
                  <div style={{flex:1}}>
                    {co.config
                      ? <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                          {[`HRA ${co.config.hra_type==="fixed"?"₹"+co.config.hra_value:co.config.hra_value+"%"}`,
                            co.config.pf_applicable?"PF ✓":"No PF",
                            co.config.esi_applicable?"ESI ✓":"No ESI",
                            co.config.conveyance?`Conv ₹${co.config.conveyance_amount}`:"",
                            co.config.medical?`Med ₹${co.config.medical_amount}`:"",
                          ].filter(Boolean).map(t=>(
                            <span key={t} style={{background:"#1a2540",borderRadius:20,padding:"2px 8px",fontSize:10,color:"#9ba8c8"}}>{t}</span>
                          ))}
                        </div>
                      : <span style={{fontSize:11,color:"#6b7a9e",fontStyle:"italic"}}>No salary structure</span>
                    }
                  </div>
                  {companies.find(c=>c.name.toLowerCase()===co.name?.toLowerCase())&&(
                    <span style={{background:"#2a1a0a",color:"#e8a93a",border:"1px solid #3a2a0a",borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>⚠ Duplicate</span>
                  )}
                </div>
              ))}
            </div>
            <div style={{fontSize:11,color:"#6b7a9e",marginTop:12}}>
              {editTarget.filter(c=>companies.find(e=>e.name.toLowerCase()===c.name?.toLowerCase())).length>0&&
                <span style={{color:"#e8a93a"}}>⚠ Orange-tagged companies already exist and will be skipped. </span>
              }
              {editTarget.filter(c=>c.name?.trim()).length} valid companies will be added.
            </div>
            <div className="modal-footer" style={{marginTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>saveBulkCompanies(editTarget)}>
                ✅ Import {editTarget.filter(c=>c.name&&!companies.find(e=>e.name.toLowerCase()===c.name.toLowerCase())).length} Companies
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upload Company Preview Modal */}
      {modal==="uploadCompanyPreview"&&editTarget&&Array.isArray(editTarget)&&(()=>{
        const co=editTarget[0]||{};
        return(
        <div className="overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
            <div className="modal-title">📋 Review Company from Excel</div>
            <div className="modal-sub" style={{color:"#9ba8c8",marginBottom:16}}>Check details before adding to TrustWork</div>
            {formMsg&&<div className="err-msg" style={{marginBottom:12}}>⚠ {formMsg}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:"#0f1420",borderRadius:10,padding:"14px 18px"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#4f8ef7",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Basic Information</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[["Company Name",co.name],["GST",co.gst||"—"],["Address",co.address||"—"],["Contact",co.contact||"—"],["Phone",co.phone||"—"],["Email",co.email||"—"]].map(([l,v])=>(
                    <div key={l}><div style={{fontSize:10,color:"#6b7a9e"}}>{l}</div><div style={{fontSize:13,color:"#e8eaf0",fontWeight:600}}>{v}</div></div>
                  ))}
                </div>
              </div>
              <div style={{background:"#0f1420",borderRadius:10,padding:"14px 18px"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#4f8ef7",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Salary Structure</div>
                {co.config
                  ? <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {[`HRA ${co.config.hra_type==="fixed"?"₹"+co.config.hra_value:co.config.hra_value+"%"}`,co.config.pf_applicable?"PF ✓":"No PF",co.config.esi_applicable?"ESI ✓":"No ESI",
                        co.config.conveyance?`Conv ₹${co.config.conveyance_amount}`:"No Conv",
                        co.config.medical?`Medical ₹${co.config.medical_amount}`:"No Medical",
                        `${co.config.working_days}d`].map(t=>(
                        <span key={t} style={{background:"#1a2540",borderRadius:20,padding:"3px 10px",fontSize:11,color:"#9ba8c8"}}>{t}</span>
                      ))}
                    </div>
                  : <div style={{color:"#6b7a9e",fontSize:12}}>Not configured — can be set later via ⚙ Salary Structure</div>
                }
              </div>
            </div>
            <div className="modal-footer" style={{marginTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={async()=>{
                if(!co.name?.trim()){ setFormMsg("Company Name is required."); return; }
                if(co.name.trim().length > 100){ setFormMsg("Company name cannot exceed 100 characters."); return; }
                if(co.contact && co.contact.trim().length > 100){ setFormMsg("Contact person name cannot exceed 100 characters."); return; }
                const cfg2 = co.config||null;
                const hasSal2 = cfg2 && (cfg2._configured===true||cfg2.hra_value!==undefined||cfg2.hra_pct!==undefined||cfg2.pf_applicable!==undefined);
                const row2 = {
                  name: co.name.trim().slice(0, 100), tin: co.gst||null, address: co.address||null,
                  contact_person: co.contact ? co.contact.trim().slice(0, 100) : null, contact_email: co.email||null, phone: co.phone||null, is_active: true,
                };
                if(hasSal2){
                  row2.hra_percent            = (cfg2.hra_type==="fixed") ? -(cfg2.hra_value||0) : (cfg2.hra_value!==undefined?cfg2.hra_value:(cfg2.hra_pct||40));
                  row2.special_allowance      = cfg2.special_allowance && cfg2.special_allowance_type==="fixed" ? (cfg2.special_allowance_value||0) : 0;
                  row2.special_allowance_pct  = cfg2.special_allowance && cfg2.special_allowance_type==="pct"   ? (cfg2.special_allowance_value||0) : 0;
                  row2.conveyance_allowance   = cfg2.conveyance ? (cfg2.conveyance_amount||0) : 0;
                  row2.medical_allowance      = cfg2.medical    ? (cfg2.medical_amount||0)    : 0;
                  row2.bonus_percent          = cfg2.bonus      ? (cfg2.bonus_pct||0)         : 0;
                  row2.bonus_months           = cfg2.bonus && cfg2.bonus_months?.length ? cfg2.bonus_months.join(",") : null;
                  row2.ot_rate_per_hour       = cfg2.ot_applicable ? (cfg2.ot_basis==="gross" ? (cfg2.ot_rate_multiplier===2?-3:-1) : (cfg2.ot_rate_multiplier===2?-4:-2)) : 0;
                  row2.absence_deduction_from = (["basic","gross","no_deduct"].includes(String(cfg2.absence_basis||"").toLowerCase()) ? String(cfg2.absence_basis).toLowerCase() : "no_deduct");
                  row2.working_days_per_month = cfg2.working_days||26;
                  row2.pf_applicable          = cfg2.pf_applicable??true;
                  row2.esi_applicable         = cfg2.esi_applicable??true;
                }
                try {
                  const {data:d2,error:e2} = await supabase.from("companies").insert(row2).select().single();
                  if(e2) throw e2;
                  setCompanies(p=>[...p,{id:d2.id,name:d2.name,gst:d2.tin||"",address:d2.address||"",contact:d2.contact_person||"",email:d2.contact_email||"",config:{...DEFAULT_CONFIG,...cfg2}}]);
                  setEmployees(p=>({...p,[d2.id]:[]}));
                  setModal(null); setEditTarget(null);
                } catch(err2){ setFormMsg("Save failed: "+err2.message); }
              }}>✅ Confirm & Add Company</button>
            </div>
          </div>
        </div>
        );
      })()}
      {modal==="uploadCompanyError"&&(
        <div className="overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:420,textAlign:"center"}}>
            <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
            <div className="modal-title">Upload Failed</div>
            <div style={{color:"#f76f6f",fontSize:13,margin:"12px 0 20px"}}>{formMsg}</div>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setModal(null);setTimeout(()=>companyUploadRef.current?.click(),100);}}>Try Again</button>
            </div>
          </div>
        </div>
      )}

      {(modal==="addCompany"||modal==="editCompany")&&<CompanyModal initial={modal==="editCompany"?editTarget:null} onSave={saveCompany} onClose={()=>setModal(null)} msg={formMsg} setMsg={setFormMsg} />}
      {modal==="salaryConfig"&&editTarget&&<SalaryConfigModal initial={editTarget} onStyleSaved={handlePayslipStyleSaved} onSave={saveCompany} onClose={()=>setModal(null)} msg={formMsg} setMsg={setFormMsg} />}
      
      {modal==="viewCompany"&&editTarget&&(()=>{
        const co=editTarget;
        const Row=({label,value})=>value?(
          <div style={{display:"flex",flexDirection:"column",gap:2,background:"#0f1117",border:"1.5px solid #1e2130",borderRadius:10,padding:"10px 13px"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#6b7a9e",textTransform:"uppercase",letterSpacing:.6}}>{label}</div>
            <div style={{fontSize:13,color:"#e8eaf0",fontWeight:600}}>{value}</div>
          </div>
        ):null;
        return(
        <div className="overlay" onClick={()=>{setModal(null);setEditTarget(null);}}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:640}}>
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <div style={{width:44,height:44,background:"linear-gradient(135deg,#1a2540,#22305a)",border:"1.5px solid #2d3a5e",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>🏢</div>
              <div>
                <div className="modal-title" style={{marginBottom:0}}>{co.name}</div>
              </div>
              <button className="btn btn-ghost btn-xs" style={{marginLeft:"auto"}} onClick={()=>{setModal(null);setActiveCompany(co);setView("company_details");}}>✏️ Edit</button>
            </div>

            {/* Company Info */}
            <div style={{fontSize:10,fontWeight:700,color:"#4f8ef7",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>📋 Company Info</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
              <Row label="GST Number" value={co.gst||null} />
              <Row label="Contact Person" value={co.contact||null} />
              <Row label="Phone" value={co.phone||null} />
              <Row label="Email" value={co.email||null} />
              <Row label="Address" value={co.address||null} />
              <Row label="Website" value={co.website||null} />
            </div>

            {/* Statutory Registrations */}
            <div style={{fontSize:10,fontWeight:700,color:"#7c5cfc",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🏛 Statutory Registrations</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8,marginBottom:14}}>
              {[["PF Registration No.",co.pf_number],["ESI Code",co.esi_code],["PT Registration",co.pt_reg]].map(([label,val])=>(
                <div key={label} style={{background:"#0f1117",border:"1.5px solid #1e2130",borderRadius:10,padding:"10px 13px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#6b7a9e",textTransform:"uppercase",letterSpacing:.6,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:13,fontFamily:"monospace",color:val?"#e8eaf0":"#2a3050",fontStyle:val?"normal":"italic"}}>{val||"Not registered"}</div>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{marginTop:4}}>
              <button className="btn btn-ghost" onClick={()=>{setModal(null);setEditTarget(null);}}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setModal(null);setActiveCompany(co);setView("company_details");}}>✏️ Edit Company</button>
            </div>
          </div>
        </div>
        );
      })()}
      {modal==="postAddEmp"&&(
        <div className="overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{textAlign:"center",maxWidth:400}}>
            <div style={{fontSize:40,marginBottom:12}}>✅</div>
            <div className="modal-title" style={{marginBottom:8}}>Employee Added!</div>
            <div style={{fontSize:13,color:"#9ba8c8",marginBottom:20,lineHeight:1.6}}>
              Don't forget to complete the employee profile:<br/>
              <span style={{color:"#e8a93a",fontWeight:600}}>Bank Details</span> in the Profile tab<br/>
              <span style={{color:"#f76f6f",fontWeight:600}}>PF & ESI details</span> in the PF / ESI tabs
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Later</button>
              <button className="btn btn-primary" onClick={()=>{
                const cid=activeCompany?.id;
                const emps=employees[cid]||[];
                const newest=emps[emps.length-1];
                if(newest){ openEmployee(newest); setEmpTab("profile"); }
                setModal(null);
              }}>Go to Profile →</button>
            </div>
          </div>
        </div>
      )}
      {(modal==="addSalary"||modal==="editSalary")&&<SalaryModal initial={modal==="editSalary"?editTarget:null} cfg={activeCompany?.config} employee={activeEmployee} onSave={saveSalary} onClose={()=>setModal(null)} msg={formMsg} setMsg={setFormMsg} isEdit={modal==="editSalary"} />}
      {modal === "issueCertificate" && activeEmployee && (
        <CertificateModal
          isOpen={true}
          onClose={() => setModal(null)}
          employee={activeEmployee}
          company={activeCompany}
          mode="admin_generate"
        />
      )}
      {modal==="editESI"&&activeEmployee&&(()=>{
        const eid=activeEmployee.id;
        const cur=esiStore[eid]||{esi_id:"",dispensary:"",members:[]};
        return <ESIPFModal type="esi" initial={cur} msg={formMsg} setMsg={setFormMsg} onClose={()=>setModal(null)}
          onSave={async data=>{
            setEsiStore(p=>({...p,[eid]:data}));
            // Also write esi_id back to employee record
            if(data.esi_id){
              await supabase.from("employees").update({esi_id:data.esi_id}).eq("id",eid);
              const upd={...activeEmployee,esi_id:data.esi_id};
              setActiveEmployee(upd);
              setEmployees(p=>({...p,[activeCompany.id]:(p[activeCompany.id]||[]).map(e=>e.id===eid?upd:e)}));
            }
            setModal(null);
          }}/>;
      })()}
      {modal==="editPF"&&activeEmployee&&(()=>{
        const eid=activeEmployee.id;
        const cur=pfStore[eid]||{uan:"",pf_account:"",nominee:""};
        return <ESIPFModal type="pf" initial={cur} msg={formMsg} setMsg={setFormMsg} onClose={()=>setModal(null)}
          onSave={async data=>{
            setPfStore(p=>({...p,[eid]:data}));
            // Sync uan + pf_account_number back to employee record
            const updates={};
            if(data.uan) updates.uan=data.uan;
            if(data.pf_account) updates.pf_account_number=data.pf_account;
            if(Object.keys(updates).length){
              await supabase.from("employees").update(updates).eq("id",eid);
              const upd={...activeEmployee,...updates,pf_account:data.pf_account||activeEmployee.pf_account};
              setActiveEmployee(upd);
              setEmployees(p=>({...p,[activeCompany.id]:(p[activeCompany.id]||[]).map(e=>e.id===eid?upd:e)}));
            }
            setModal(null);
          }}/>;
      })()}
      {/* ── Upload Employees Preview */}
      {/* ── Upload Employees Preview (Smart Dry-Run) */}
      {modal==="uploadEmpPreview"&&editTarget&&Array.isArray(editTarget)&&(()=>{
        const cfg = activeCompany?.config || DEFAULT_CONFIG;
        const curMonth = new Date().getMonth() + 1;
        const curYear = new Date().getFullYear();

        let previewPlan = [], importPlanError = "";
        try { previewPlan = planEmployeeImport(editTarget, employees[activeCompany?.id] || []); }
        catch (error) { importPlanError = error.message; }

        // Calculate dry-run using the same preserved salary fields as the update.
        const dryRuns = editTarget.map((uploaded, index) => {
          const existing = previewPlan[index]?.existing;
          const emp = existing ? { ...existing, ...employeeSalaryPatch(uploaded), basic: uploaded.basic } : uploaded;
          const empGross = Math.round(parseFloat(emp.basic) || 0);
          const calc = calcSalaryFull({
            basic: empGross,
            month: curMonth,
            year: curYear,
            absent_days: 0,
            ot_hours: 0,
            cfg,
            extra: {
              custom_basic_pct: emp.custom_basic_pct,
              custom_hra_pct: emp.custom_hra_pct,
              custom_special_allowance: emp.custom_special_allowance,
              vpf_enabled: (emp.vpf_pct || 0) > 0,
              vpf_pct: emp.vpf_pct || 0,
              tds_amount: emp.tds_amount || 0,
              emp_dob: emp.dob,
              esi_forced_until: emp.esi_forced_until
            }
          });
          const hasBudgetIssue = calc.gross > empGross;
          const isUnderMinWage = cfg.min_wage_enabled && cfg.min_wage > 0 && empGross < cfg.min_wage;
          return { emp: { ...emp, basic: empGross }, calc, hasBudgetIssue, isUnderMinWage };
        });

        const rowValidationErrors = editTarget.map((emp, index) => {
          const existing = previewPlan[index]?.existing;
          return existing ? validateEmployeeSalary({ ...existing, ...employeeSalaryPatch(emp) }, cfg) : validateEmployeeRecord(emp, cfg);
        });
        const hasAnyErrors = !!importPlanError || rowValidationErrors.some(Boolean);
        const totalGross = Math.round(dryRuns.reduce((sum, d) => sum + (d.emp.basic || 0), 0));
        const totalNet = Math.round(dryRuns.reduce((sum, d) => sum + (d.calc.net || 0), 0));
        const allEmpsFlat = Object.values(employees).flat();
        const dupes = editTarget.filter(e=>allEmpsFlat.find(x=>x.is_active !== false && x.pan&&e.pan&&String(x.pan).toUpperCase().trim()===String(e.pan).toUpperCase().trim()));
        const dupCodes = editTarget.filter(e=>(employees[activeCompany?.id]||[]).find(x=>x.is_active !== false && x.employee_code&&e.employee_code&&String(x.employee_code).trim().toLowerCase()===String(e.employee_code).trim().toLowerCase()));

        const seenPansInFile = new Set();
        const seenCodesInFile = new Set();
        const internalDupPans = editTarget.filter(e => {
          if (!e.pan) return false;
          const p = String(e.pan).toUpperCase().trim();
          if (seenPansInFile.has(p)) return true;
          seenPansInFile.add(p);
          return false;
        });
        const internalDupCodes = editTarget.filter(e => {
          if (!e.employee_code) return false;
          const c = String(e.employee_code).trim().toLowerCase();
          if (seenCodesInFile.has(c)) return true;
          seenCodesInFile.add(c);
          return false;
        });

        return (
          <div className="overlay" onClick={()=>setModal(null)}>
            <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:980, width:"95%"}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10, marginBottom:12}}>
                <div>
                  <div className="modal-title" style={{display:"flex", alignItems:"center", gap:8}}>
                    <span>👥 Excel Upload Preview &amp; Salary Dry-Run</span>
                  </div>
                  <div className="modal-sub" style={{color:"#64748b"}}>
                    {editTarget.length} employee(s) parsed. Simulated using active company rules for <strong>{activeCompany?.name || "Company"}</strong>.
                  </div>
                </div>
                <div style={{display:"flex", gap:16, background:"#f8fafc", padding:"8px 16px", borderRadius:8, border:"1px solid #e2e8f0"}}>
                  <div>
                    <div style={{fontSize:11, color:"#64748b", fontWeight:600}}>TOTAL GROSS</div>
                    <div style={{fontSize:16, fontWeight:800, color:"#1e293b"}}>₹{Math.round(totalGross).toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{borderLeft:"1px solid #cbd5e1", paddingLeft:16}}>
                    <div style={{fontSize:11, color:"#16a34a", fontWeight:600}}>EST. TAKE-HOME</div>
                    <div style={{fontSize:16, fontWeight:800, color:"#16a34a"}}>₹{Math.round(totalNet).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              </div>

              {/* Active Rules Badges */}
              <div style={{display:"flex", gap:8, flexWrap:"wrap", marginBottom:12, fontSize:11.5}}>
                <span style={{background:"#eff6ff", color:"#1d4ed8", padding:"3px 8px", borderRadius:6, fontWeight:600, border:"1px solid #bfdbfe"}}>
                  🏛️ Basic: {cfg.basic_pct !== undefined ? cfg.basic_pct : 50}% {cfg.min_wage_enabled ? `(Floor ₹${Math.round(cfg.min_wage || 0).toLocaleString("en-IN")})` : ""}
                </span>
                <span style={{background:(cfg.bonus ? "#faf5ff" : "#f1f5f9"), color:(cfg.bonus ? "#7e22ce" : "#64748b"), padding:"3px 8px", borderRadius:6, fontWeight:600, border:(cfg.bonus ? "1px solid #e9d5ff" : "1px solid #e2e8f0")}}>
                  🎁 Bonus: {cfg.bonus ? `${cfg.bonus_pct || 8.33}% Monthly` : "Disabled"}
                </span>
                <span style={{background:(cfg.pf_applicable !== false ? "#f0fdf4" : "#f1f5f9"), color:(cfg.pf_applicable !== false ? "#15803d" : "#64748b"), padding:"3px 8px", borderRadius:6, fontWeight:600, border:(cfg.pf_applicable !== false ? "1px solid #bbf7d0" : "1px solid #e2e8f0")}}>
                  🛡️ EPF: {cfg.pf_applicable !== false ? "12%" : "Off"}
                </span>
                <span style={{background:(cfg.esi_applicable !== false ? "#f0fdfa" : "#f1f5f9"), color:(cfg.esi_applicable !== false ? "#0f766e" : "#64748b"), padding:"3px 8px", borderRadius:6, fontWeight:600, border:(cfg.esi_applicable !== false ? "1px solid #99f6e4" : "1px solid #e2e8f0")}}>
                  🏥 ESI: {cfg.esi_applicable !== false ? "0.75%" : "Off"}
                </span>
              </div>

              {dupes.length > 0 && (
                <div style={{background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b45309", marginBottom:10}}>
                  ⚠️ Existing PAN match found in company roster: {dupes.map(d => `${d.name} (${d.pan})`).join(", ")}. Matching Employee IDs with the same PAN will update salary fields only. A PAN belonging to a different Employee ID will be skipped.
                </div>
              )}

              {dupCodes.length > 0 && (
                <div style={{background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b45309", marginBottom:10}}>
                  ⚠️ Existing Employee ID match found in roster: {dupCodes.map(d => `${d.name} (${d.employee_code})`).join(", ")}. These will update salary fields only when PAN also matches. Onboarding details are preserved.
                </div>
              )}

              {internalDupPans.length > 0 && (
                <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b91c1c", marginBottom:10}}>
                  ⚠️ Duplicate PAN found within this Excel file: {internalDupPans.map(d => `${d.name} (${d.pan})`).join(", ")}.
                </div>
              )}

              {internalDupCodes.length > 0 && (
                <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b91c1c", marginBottom:10}}>
                  ⚠️ Duplicate Employee ID found within this Excel file: {internalDupCodes.map(d => `${d.name} (${d.employee_code})`).join(", ")}.
                </div>
              )}

              {hasAnyErrors && (
                <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b91c1c", marginBottom:10}}>
                  Import blocked. Correct these rows and upload again:
                  {importPlanError && <div>{importPlanError}</div>}
                  {rowValidationErrors.map((error, index) => error && <div key={index} style={{marginTop:4}}>Row {index + 1} ({editTarget[index].name}): {error}</div>)}
                </div>
              )}

              <div style={{maxHeight:360, overflowY:"auto", borderRadius:8, border:"1px solid #e2e8f0"}}>
                <table style={{width:"100%", fontSize:12, borderCollapse:"collapse", background:"#fff"}}>
                  <thead>
                    <tr style={{background:"#f8fafc", position:"sticky", top:0, borderBottom:"1px solid #cbd5e1"}}>
                      <th style={{padding:"8px 10px", textAlign:"left", color:"#475569", fontWeight:700}}>#</th>
                      <th style={{padding:"8px 10px", textAlign:"left", color:"#475569", fontWeight:700}}>Employee</th>
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#475569", fontWeight:700}}>Gross</th>
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#475569", fontWeight:700}}>Basic</th>
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#475569", fontWeight:700}}>HRA</th>
                      {cfg.bonus && <th style={{padding:"8px 10px", textAlign:"right", color:"#7e22ce", fontWeight:700}}>Bonus</th>}
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#ca8a04", fontWeight:700}}>Special / Other</th>
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#dc2626", fontWeight:700}}>Deductions</th>
                      <th style={{padding:"8px 10px", textAlign:"right", color:"#16a34a", fontWeight:700, background:"#f0fdf4"}}>Take-Home</th>
                      <th style={{padding:"8px 10px", textAlign:"center", color:"#475569", fontWeight:700}}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRuns.map(({emp: e, calc: c, hasBudgetIssue, isUnderMinWage}, i) => (
                      <tr key={i} style={{borderBottom:"1px solid #f1f5f9", background: hasBudgetIssue || isUnderMinWage ? "#fff1f2" : (i % 2 === 0 ? "#ffffff" : "#f8fafc")}}>
                        <td style={{padding:"8px 10px", color:"#64748b"}}>{i + 1}</td>
                        <td style={{padding:"8px 10px"}}>
                          <strong style={{color:"#0f172a"}}>{e.name}</strong>
                          <div style={{fontSize:11, color:"#64748b", fontFamily:"monospace"}}>{e.employee_code} • {e.pan || "No PAN"}</div>
                        </td>
                        <td style={{padding:"8px 10px", textAlign:"right", fontWeight:700, color:"#0f172a"}}>₹{Math.round(e.basic || 0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#334155"}}>₹{Math.round(c.basic || 0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#334155"}}>₹{Math.round(c.hra || 0).toLocaleString("en-IN")}</td>
                        {cfg.bonus && <td style={{padding:"8px 10px", textAlign:"right", color:"#7e22ce", fontWeight:600}}>₹{Math.round(c.bonus || 0).toLocaleString("en-IN")}</td>}
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#ca8a04"}}>₹{Math.round(c.special || 0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#dc2626"}}>−₹{Math.round(c.total_deductions || 0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#16a34a", fontWeight:800, background:"#f0fdf4"}}>₹{Math.round(c.net || 0).toLocaleString("en-IN")}</td>
                        <td style={{padding:"8px 10px", textAlign:"center"}}>
                          {hasBudgetIssue ? (
                            <span style={{color:"#dc2626", fontWeight:700, fontSize:11}} title="Allowances exceed target gross">⚠️ Over Budget</span>
                          ) : isUnderMinWage ? (
                            <span style={{color:"#ea580c", fontWeight:700, fontSize:11}} title="Below minimum wage floor">⚠️ Below Min</span>
                          ) : (
                            <span style={{color:"#16a34a", fontWeight:700, fontSize:11}}>✓ Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="modal-footer" style={{marginTop:16, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <button className="btn btn-ghost" onClick={()=>setModal(null)} disabled={!!uploadProgress}>Cancel</button>
                <div style={{display:"flex", gap:10, alignItems:"center"}}>
                  <button className="btn btn-primary" onClick={()=>saveBulkEmployees(editTarget)} disabled={!!uploadProgress || hasAnyErrors}>
                    {uploadProgress ? "⏳ Processing Import..." : `✅ Confirm: Add ${previewPlan.filter(item => !item.existing).length}, Update ${previewPlan.filter(item => item.existing).length}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ── Bulk Upload Live Progress Overlay */}
      {uploadProgress && (
        <div className="overlay" style={{zIndex: 99999}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth: 480, textAlign: "center", padding: "32px 28px", borderRadius: 16, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"}}>
            <div style={{fontSize: 40, marginBottom: 12}}>👥</div>
            <div style={{fontSize: 19, fontWeight: 800, color: "#0f172a", marginBottom: 6}}>
              Importing Employees ({uploadProgress.current}/{uploadProgress.total})
            </div>
            <div style={{fontSize: 13, color: "#64748b", marginBottom: 20, height: 20, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
              {uploadProgress.name ? `Processing: ${uploadProgress.name}` : "Preparing upload batch..."}
            </div>
            {/* Progress bar container */}
            <div style={{width: "100%", height: 12, background: "#e2e8f0", borderRadius: 6, overflow: "hidden", marginBottom: 12}}>
              <div 
                style={{
                  width: `${Math.round((uploadProgress.current / (uploadProgress.total || 1)) * 100)}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #3b82f6, #1d4ed8)",
                  borderRadius: 6,
                  transition: "width 0.2s ease-in-out"
                }}
              />
            </div>
            <div style={{display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: "#64748b"}}>
              <span>{Math.round((uploadProgress.current / (uploadProgress.total || 1)) * 100)}%</span>
              <span style={{color: "#2563eb"}}>Do not close or refresh this tab</span>
            </div>
          </div>
        </div>
      )}
      {modal==="uploadEmpDone"&&(
        <div className="overlay" onClick={()=>{setModal(null);setFormMsg("");setUploadLog("");setCopiedLog(false);}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:660, width:"95%", borderRadius:14}}>
            <div style={{display:"flex", alignItems:"flex-start", gap:14, marginBottom:16}}>
              <div style={{fontSize:32, background: formMsg.includes("failed") || formMsg.includes("Error") || formMsg.includes("Issues") ? "#fef3c7" : "#dcfce7", padding:"8px 12px", borderRadius:12, border:`1px solid ${formMsg.includes("failed") || formMsg.includes("Error") ? "#fde68a" : "#bbf7d0"}`, flexShrink:0}}>
                {formMsg.includes("failed") || formMsg.includes("Error") || formMsg.includes("Issues") ? "⚠️" : "✅"}
              </div>
              <div>
                <div style={{fontSize:18, fontWeight:800, color:"#0f172a"}}>
                  {formMsg.includes("failed") ? "Bulk Upload Unsuccessful" : "Upload Processing Complete"}
                </div>
                <div style={{fontSize:13, color:"#64748b", marginTop:2}}>
                  {formMsg.includes("added") ? "Employee records have been synchronized with your database." : "Review error details below to resolve rejected records."}
                </div>
              </div>
            </div>

            <div style={{background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 16px", fontSize:13, marginBottom:14, whiteSpace:"pre-wrap", lineHeight:1.5, color:"#334155"}}>
              {formMsg}
            </div>

            {uploadLog && (
              <div style={{marginBottom:16}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
                  <div style={{fontSize:11.5, fontWeight:800, color:"#475569", textTransform:"uppercase", letterSpacing:0.5}}>📋 Diagnostic Activity Log</div>
                  <div style={{display:"flex", gap:8}}>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{fontSize:11.5, padding:"4px 10px", display:"flex", alignItems:"center", gap:4}}
                      onClick={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(uploadLog);
                          setCopiedLog(true);
                          setTimeout(() => setCopiedLog(false), 3000);
                        }
                      }}
                    >
                      {copiedLog ? "✅ Copied to Clipboard" : "📋 Copy Log"}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{fontSize:11.5, padding:"4px 10px", display:"flex", alignItems:"center", gap:4}}
                      onClick={() => {
                        const blob = new Blob([uploadLog], { type: "text/plain;charset=utf-8" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `TrustWork_Employee_Upload_Log_${new Date().toISOString().slice(0, 10)}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      ⬇ Download Log (.txt)
                    </button>
                  </div>
                </div>
                <pre style={{
                  background:"#0f172a",
                  color:"#e2e8f0",
                  padding:"12px 14px",
                  borderRadius:8,
                  fontSize:11.5,
                  maxHeight:180,
                  overflowY:"auto",
                  fontFamily:"monospace",
                  whiteSpace:"pre-wrap",
                  lineHeight:1.45,
                  margin:0
                }}>
                  {uploadLog}
                </pre>
              </div>
            )}

            <div style={{display:"flex", justifyContent:"flex-end", gap:10}}>
              <button className="btn btn-primary" onClick={()=>{setModal(null);setFormMsg("");setUploadLog("");setCopiedLog(false);}}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {(modal==="bulk_hike" || modal==="hike_report") && (
        <BulkHikeModal 
          activeCompany={activeCompany} 
          setActiveCompany={setActiveCompany} 
          setCompanies={setCompanies} 
          companyEmps={companyEmps} 
          setEmployees={setEmployees} 
          currentUser={currentUser} 
          initialTab={modal==="hike_report" ? "report" : "upload"}
          onClose={()=>setModal(null)} 
        />
      )}
      {modal==="uploadEmpError"&&(
        <div className="overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:660, width:"95%", borderRadius:14}}>
            <div style={{display:"flex", alignItems:"flex-start", gap:14, marginBottom:16}}>
              <div style={{fontSize:32, background:"#fee2e2", padding:"8px 12px", borderRadius:12, border:"1px solid #fecaca", flexShrink:0}}>⚠️</div>
              <div>
                <div style={{fontSize:18, fontWeight:800, color:"#991b1b"}}>Employee Template Upload Failed</div>
                <div style={{fontSize:13, color:"#64748b", marginTop:2}}>The Excel file could not be parsed or imported due to validation errors.</div>
              </div>
            </div>

            {/* Error Message Box */}
            <div style={{background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"12px 16px", color:"#b91c1c", fontSize:13, fontWeight:600, marginBottom:14, lineHeight:1.5}}>
              {formMsg}
            </div>

            {/* Diagnostic Log Box with Copy & Download */}
            {uploadLog && (
              <div style={{marginBottom:16}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
                  <div style={{fontSize:11.5, fontWeight:800, color:"#475569", textTransform:"uppercase", letterSpacing:0.5}}>📋 Diagnostic Error Log</div>
                  <div style={{display:"flex", gap:8}}>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{fontSize:11.5, padding:"4px 10px", display:"flex", alignItems:"center", gap:4}}
                      onClick={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(uploadLog);
                          setCopiedLog(true);
                          setTimeout(() => setCopiedLog(false), 3000);
                        }
                      }}
                    >
                      {copiedLog ? "✅ Copied to Clipboard" : "📋 Copy Log"}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{fontSize:11.5, padding:"4px 10px", display:"flex", alignItems:"center", gap:4}}
                      onClick={() => {
                        const blob = new Blob([uploadLog], { type: "text/plain;charset=utf-8" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `TrustWork_Employee_Upload_Error_Log_${new Date().toISOString().slice(0, 10)}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      ⬇ Download Log (.txt)
                    </button>
                  </div>
                </div>
                <pre style={{
                  background:"#0f172a",
                  color:"#e2e8f0",
                  padding:"12px 14px",
                  borderRadius:8,
                  fontSize:11.5,
                  maxHeight:180,
                  overflowY:"auto",
                  fontFamily:"monospace",
                  whiteSpace:"pre-wrap",
                  lineHeight:1.45,
                  margin:0
                }}>
                  {uploadLog}
                </pre>
              </div>
            )}

            {/* Troubleshooting Checklist */}
            <div style={{background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"12px 16px", marginBottom:18, fontSize:12, color:"#475569", lineHeight:1.5}}>
              <div style={{fontWeight:800, color:"#0f172a", marginBottom:6, fontSize:12.5}}>💡 Troubleshooting Tips:</div>
              <ul style={{margin:0, paddingLeft:18, display:"flex", flexDirection:"column", gap:4}}>
                <li><strong>Date of Birth &amp; Joining:</strong> Must be entered as <code>DD-MM-YYYY</code> (e.g. <code>15-05-1995</code>).</li>
                <li><strong>PAN Number:</strong> Must be exactly 10 alphanumeric characters (e.g. <code>ABCDE1234F</code>).</li>
                <li><strong>Gross Monthly Salary:</strong> Must be a valid positive number (e.g. <code>35000</code>).</li>
                <li><strong>Employee ID:</strong> Must be unique and not match any existing employee code in the company.</li>
              </ul>
            </div>

            <div style={{display:"flex", justifyContent:"flex-end", gap:10}}>
              <button className="btn btn-primary" onClick={()=>{setModal(null); setFormMsg(""); setUploadLog(""); setCopiedLog(false);}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {modal==="deleteCompany"&&deleteTarget&&<ConfirmModal icon="⚠️" title="Deactivate Company?" text={"Deactivate "+deleteTarget.name+"? Employees won't be able to log in, but data is kept safe."} confirmText="Yes, Deactivate" onConfirm={confirmDeleteCompany} onClose={()=>setModal(null)} />}
      {modal==="restoreCompany"&&deleteTarget&&<ConfirmModal icon="↺" title="Reactivate Company?" text={"Reactivate "+deleteTarget.name+"? Employees will regain access."} confirmText="Yes, Reactivate" onConfirm={confirmRestoreCompany} onClose={()=>setModal(null)} />}
      {modal==="deleteEmp"&&deleteTarget&&<ConfirmModal icon="⚠️" title="Remove Employee?" text={"Remove "+deleteTarget.name+"? All salary records, attendance, leaves, uploaded documents, and Cloudflare storage files will be permanently purged."} onConfirm={confirmDeleteEmp} onClose={()=>setModal(null)} />}
      {modal==="deleteMultipleEmps"&&selectedEmps.length>0&&<ConfirmModal icon="⚠️" title="Remove Selected Employees?" text={`Remove ${selectedEmps.length} selected employees? All salary records, attendance, leaves, uploaded documents, and Cloudflare storage files will be permanently purged.`} onConfirm={confirmDeleteMultipleEmps} onClose={()=>setModal(null)} />}
      {modal==="cleanStaleData" && activeCompany && (
        <CleanStaleDataModal
          activeCompany={activeCompany}
          onClose={() => {
            setModal(null);
            if (view === "companies") setActiveCompany(null);
          }}
          onRefreshEmployees={() => {
            if (activeCompany?.id) loadEmployees(activeCompany.id);
          }}
        />
      )}
      {modal==="deleteSalary"&&deleteTarget&&<ConfirmModal icon="🗑" title="Delete Record?" text={"Delete salary for "+MONTHS[deleteTarget.month]+" "+deleteTarget.year+"?"} onConfirm={confirmDeleteSalary} onClose={()=>setModal(null)} />}
      {modal==="payrollSaved"&&<AlertModal icon="✅" title="Success!" text={formMsg} onClose={()=>setModal(null)} />}
      {modal==="payrollError"&&<AlertModal icon="❌" title="Error!" text={formMsg} onClose={()=>setModal(null)} />}
      {modal==="change_password"&&<ChangePasswordModal currentUser={currentUser} activeCompany={activeCompany} companies={companies} setCompanies={setCompanies} setActiveCompany={setActiveCompany} onClose={()=>setModal(null)} />}
      {modal==="change_login_id"&&<ChangeLoginIDModal currentUser={currentUser} activeCompany={activeCompany} companies={companies} setCompanies={setCompanies} setActiveCompany={setActiveCompany} onClose={()=>setModal(null)} />}
      {modal==="testRunner"&&<SystemTestRunner onClose={()=>setModal(null)} />}
      {modal==="sendPayslipEmail" && emailModalData && <SendPayslipEmailModal data={emailModalData} onClose={() => { setModal(null); setEmailModalData(null); }}/>}
      </>)}
    </div>
      {showTour && <WelcomeTourModal onClose={() => setShowTour(false)} />}
      <CommandPaletteModal
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        activeCompany={activeCompany}
        companies={companies}
        employees={companyEmps}
        onNavigate={handleCommandNavigate}
        isMasterAdmin={!isCompanyRole && currentUser?.role === "admin"}
      />
  </>);
}

// ── Confirm Modal
function ConfirmModal({icon,title,text,onConfirm,onClose,confirmText="Yes, Delete"}){
  const [busy, setBusy] = useState(false);
  return(
    <div className="overlay" onClick={busy ? undefined : onClose}>
      <div className="modal confirm-modal" onClick={e=>e.stopPropagation()}>
        <div className="confirm-icon">{icon}</div>
        <div className="confirm-title">{title}</div>
        <div className="confirm-text">{text}</div>
        <div className="confirm-btns">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button 
            className="btn btn-danger" 
            onClick={async (e) => {
              if (busy) return;
              setBusy(true);
              try {
                await onConfirm(e);
              } finally {
                setBusy(false);
              }
            }} 
            disabled={busy}
          >
            {busy ? "⏳ Deleting..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertModal({icon,title,text,onClose}){
  return(
    <div className="overlay" onClick={onClose}>
      <div className="modal confirm-modal" onClick={e=>e.stopPropagation()}>
        <div className="confirm-icon">{icon}</div>
        <div className="confirm-title">{title}</div>
        <div className="confirm-text">{text}</div>
        <div className="confirm-btns" style={{justifyContent:"center"}}>
          <button className="btn btn-primary" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

// ── Company Modal (with salary config)
// ── Company Modal — 10 fields, no salary structure
function CompanyModal({initial,onSave,onClose,msg,setMsg}){
  const [showMore, setShowMore] = useState(false);
  const [form,setForm] = useState({
    name:"", phone:"", login_username:"", login_password:"",
    gst:"", address:"", website:"", contact:"", email:"",
    pf_number:"", esi_code:"", pt_reg:"",
    plan_tier: initial?.plan_tier || initial?.config?.plan_tier || "Basic",
    ...initial,
  });
  const set = k => v => {
    let val = v;
    if (typeof val === 'string' && (k === 'name' || k === 'contact')) {
      val = val.slice(0, 100);
    }
    setForm(p=>({...p,[k]:val}));
    setMsg("");
  };

  const handleSave = () => {
    if(!form.name.trim()){setMsg("Company name is required.");return;}
    if(form.name.trim().length > 100){setMsg("Company name cannot exceed 100 characters.");return;}
    if(form.contact && form.contact.trim().length > 100){setMsg("Contact person name cannot exceed 100 characters.");return;}

    const phone10 = getPhone10(form.phone);
    if(!phone10 || phone10.length !== 10){
      setMsg("Phone number is required. Enter 10-digit number after +91.");return;
    }
    if(!/^[6-9][0-9]{9}$/.test(phone10)){
      setMsg("Invalid phone number. Must be a 10-digit Indian mobile number starting with 6–9.");return;
    }

    if(form.email?.trim() && !isValidEmail(form.email)){
      setMsg("Invalid email address format (e.g. hello@company.com).");return;
    }

    if(!form.login_username?.trim()){setMsg("Login username is required.");return;}
    if(!initial && !form.login_password){setMsg("A temporary administrator password is required.");return;}
    if(form.login_password && form.login_password.length < 12){setMsg("Use at least 12 characters for the temporary password.");return;}
    if(!initial && !isValidEmail(form.login_username)){setMsg("Enter the administrator email address.");return;}

    const chosenPlan = form.plan_tier || initial?.config?.plan_tier || initial?.plan_tier || "Basic";
    onSave({
      ...form, 
      phone: `+91 ${phone10}`,
      name: form.name.trim().slice(0, 100), 
      contact: form.contact ? form.contact.trim().slice(0, 100) : "",
      login_username: form.login_username.trim(), 
      login_password: form.login_password || '',
      gst: form.gst?.trim().toUpperCase()||"", 
      plan_tier: chosenPlan,
      config: { ...(initial?.config || {}), ...(form.config || {}), plan_tier: chosenPlan }
    });
  };

  const currentPhone10 = getPhone10(form.phone);
  const emailVal = (form.email || "").trim();
  const isEmailValid = isValidEmail(emailVal);
  const emailHasError = emailVal.length > 0 && !isEmailValid;

  return(
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-title">{initial?"Edit Company":"Add Company"}</div>
        {msg&&<div className="err-msg" style={{marginTop:8}}>⚠️ {msg}</div>}

        <div style={{marginTop:16,marginBottom:6,fontSize:10,fontWeight:700,color:"#4f8ef7",textTransform:"uppercase",letterSpacing:1}}>🏢 Mandatory Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div className="config-field" style={{gridColumn:"1/-1"}}>
            <div className="config-label">Company Name <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} /></div>
            <input className="config-input" maxLength={100} value={form.name||""} onChange={e=>set("name")(e.target.value.slice(0, 100))} placeholder="TrustWork Solutions" />
          </div>
          <div className="config-field">
            <div className="config-label">Contact Person</div>
            <input className="config-input" maxLength={100} value={form.contact||""} onChange={e=>set("contact")(e.target.value.slice(0, 100))} placeholder="John Doe" />
          </div>
          <div className="config-field">
            <div className="config-label">Phone <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} /></div>
            <div style={{display:"flex", gap:6, alignItems:"center"}}>
              <div style={{background:"#f1f5f9",border:"1.5px solid #cbd5e1",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#475569",fontWeight:700,display:"flex",alignItems:"center",userSelect:"none"}}>+91</div>
              <input 
                type="tel"
                className="config-input" 
                value={currentPhone10} 
                onChange={e=>{
                  const v = getPhone10(e.target.value);
                  setForm(p=>({...p, phone: v ? `+91 ${v}` : ""}));
                  setMsg("");
                }} 
                placeholder="93530 10107" 
                style={{fontFamily:"monospace"}} 
                maxLength={16} 
              />
            </div>
            {currentPhone10.length > 0 && currentPhone10.length < 10 && (
              <div style={{fontSize:10,color:"#ef4444",marginTop:3,fontWeight:600}}>
                ⚠️ {currentPhone10.length}/10 digits (enter 10-digit mobile)
              </div>
            )}
          </div>
          <div className="config-field" style={{gridColumn:"1/-1"}}>
            <div className="config-label">Email</div>
            <div style={{position:"relative"}}>
              <input 
                className="config-input" 
                type="email" 
                value={form.email||""} 
                onChange={e=>set("email")(e.target.value.replace(/\s/g, ''))} 
                placeholder="hello@company.com" 
                style={{
                  paddingRight: emailHasError ? 32 : 12,
                  borderColor: emailHasError ? "#ef4444" : undefined
                }}
              />
              {emailHasError && (
                <span style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 13,
                  pointerEvents: "none"
                }}>
                  ⚠️
                </span>
              )}
            </div>
            {emailHasError && (
              <div style={{
                fontSize: 10,
                marginTop: 3,
                fontWeight: 600,
                color: "#ef4444"
              }}>
                ⚠️ Invalid email format (e.g. hello@company.com)
              </div>
            )}
          </div>
        </div>

        <div style={{marginTop:16,marginBottom:6,fontSize:10,fontWeight:700,color:"#7c5cfc",textTransform:"uppercase",letterSpacing:1}}>🔑 Login Credentials</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div className="config-field">
            <div className="config-label">Admin Email <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} /></div>
            <input className="config-input" value={form.login_username||""} onChange={e=>set("login_username")(e.target.value)} placeholder="hr@company.com" />
          </div>
          <div className="config-field">
            <div className="config-label">Temporary Password <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} /></div>
            <input className="config-input" type="password" autoComplete="new-password" value={form.login_password||""} onChange={e=>set("login_password")(e.target.value)} placeholder="Password" />
          </div>
        </div>

        <div style={{marginTop:16,marginBottom:6,fontSize:10,fontWeight:700,color:"#0284c7",textTransform:"uppercase",letterSpacing:1}}>📦 Subscription Plan</div>
        <div className="config-field">
          <div className="config-label">Plan Tier & Employee Limit</div>
          <select
            className="config-input"
            value={form.plan_tier || "Basic"}
            onChange={e => {
              const val = e.target.value;
              setForm(p => ({ ...p, plan_tier: val, config: { ...(p.config || {}), plan_tier: val } }));
            }}
            style={{fontWeight: 700, cursor: "pointer"}}
          >
            <option value="Free">Free (up to 5 employees)</option>
            <option value="Basic">Basic (up to 25 employees)</option>
            <option value="Pro">Pro (up to 50 employees)</option>
            <option value="Enterprise">Enterprise (up to 3,000 employees)</option>
            <option value="Enterprise Plus">Enterprise Plus (up to 10,000 employees)</option>
            <option value="Ultimate">Ultimate (unlimited / 100,000 employees)</option>
          </select>
        </div>

        <div style={{marginTop:16}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowMore(!showMore)}>
            {showMore ? "➖ Hide Additional Details" : "➕ Add More Details (Optional)"}
          </button>
        </div>

        {showMore && (
          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #e2e8f0"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Additional Info</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="config-field">
                <div className="config-label">Address</div>
                <input className="config-input" value={form.address||""} onChange={e=>set("address")(e.target.value)} placeholder="City, State" />
              </div>
              <div className="config-field">
                <div className="config-label">Website</div>
                <input className="config-input" value={form.website||""} onChange={e=>set("website")(e.target.value)} placeholder="www.company.com" />
              </div>
              <div className="config-field">
                <div className="config-label">GST Number</div>
                <input className="config-input" value={form.gst||""} onChange={e=>set("gst")(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} placeholder="22AAAAA0000A1Z5" style={{fontFamily:"monospace"}} maxLength={15} />
              </div>
              <div className="config-field">
                <div className="config-label">PF Number</div>
                <input className="config-input" value={form.pf_number||""} onChange={e=>set("pf_number")(e.target.value)} placeholder="MH/BAN/0012345" style={{fontFamily:"monospace"}} />
              </div>
              <div className="config-field">
                <div className="config-label">ESI Code</div>
                <input className="config-input" value={form.esi_code||""} onChange={e=>set("esi_code")(e.target.value)} placeholder="31-00-123456" style={{fontFamily:"monospace"}} />
              </div>
              <div className="config-field">
                <div className="config-label">PT Registration</div>
                <input className="config-input" value={form.pt_reg||""} onChange={e=>set("pt_reg")(e.target.value)} placeholder="PT/MH/XXXX" style={{fontFamily:"monospace"}} />
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer" style={{marginTop:18}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>{initial?"💾 Save Changes":"➕ Add Company"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Hike Modal & Company Hike Audit Report
function BulkHikeModal({ activeCompany, setActiveCompany, setCompanies, companyEmps, setEmployees, currentUser, initialTab = "upload", onClose }) {
  const [tab, setTab] = useState(initialTab || "upload");
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const saveInFlightRef = useRef(false);
  const [effectiveDate, setEffectiveDate] = useState(() => salaryToday());
  const [reportSearch, setReportSearch] = useState("");

  // Extract all historical hikes across the company
  const allHikes = useMemo(() => {
    const hikesMap = activeCompany?.config?.salary_hikes || {};
    const list = [];
    Object.entries(hikesMap).forEach(([empId, empHikes]) => {
      const emp = (companyEmps || []).find(e => e.id === empId || e.employee_code === empId);
      sortSalaryRevisions(empHikes || []).forEach(h => {
        list.push({
          id: empId,
          empCode: emp?.employee_code || empId,
          name: emp?.name || "Employee",
          department: emp?.department || "—",
          designation: emp?.designation || "—",
          date: h.effective_date || h.date || "",
          oldSalary: h.old_salary || 0,
          newSalary: h.new_salary || 0,
          increment: (h.new_salary || 0) - (h.old_salary || 0),
          hikePct: h.hike_pct !== undefined ? h.hike_pct : 0,
          note: h.note || "Salary Revision"
        });
      });
    });
    // Sort descending by date
    list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return list;
  }, [activeCompany?.config?.salary_hikes, companyEmps]);

  const filteredHikes = useMemo(() => {
    if (!reportSearch.trim()) return allHikes;
    const q = reportSearch.toLowerCase().trim();
    return allHikes.filter(h => 
      h.name.toLowerCase().includes(q) ||
      h.empCode.toLowerCase().includes(q) ||
      h.department.toLowerCase().includes(q) ||
      h.designation.toLowerCase().includes(q) ||
      h.note.toLowerCase().includes(q)
    );
  }, [allHikes, reportSearch]);

  const totalIncrementAmount = useMemo(() => {
    return allHikes.reduce((sum, h) => sum + (h.increment > 0 ? h.increment : 0), 0);
  }, [allHikes]);

  const avgHikePct = useMemo(() => {
    if (allHikes.length === 0) return 0;
    const sum = allHikes.reduce((acc, h) => acc + (h.hikePct || 0), 0);
    return Number((sum / allHikes.length).toFixed(1));
  }, [allHikes]);

  const uniqueEmployeesCount = useMemo(() => {
    return new Set(allHikes.map(h => h.id)).size;
  }, [allHikes]);

  const handleDownloadHikeReport = () => {
    if (allHikes.length === 0) {
      alert("No salary revisions logged yet.");
      return;
    }

    const data = allHikes.map((h, idx) => ({
      "Sl No": idx + 1,
      "Employee Code": h.empCode,
      "Employee Name": h.name,
      "Department": h.department,
      "Designation": h.designation,
      "Effective Date": h.date ? fmtDate(h.effective_date || h.date) : "—",
      "Previous Gross (₹)": h.oldSalary,
      "Revised Gross (₹)": h.newSalary,
      "Monthly Increment (₹)": h.increment,
      "Hike %": (h.hikePct >= 0 ? `+${h.hikePct}%` : `${h.hikePct}%`),
      "Note / Reason": h.note
    }));

    const ws = XLSX.utils.json_to_sheet(data, { origin: "A4" });

    // Title rows
    XLSX.utils.sheet_add_aoa(ws, [
      [`${activeCompany.name} - Complete Salary Revision & Appraisal Audit Report`],
      [`Generated on: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} | Total Revisions: ${allHikes.length} | Unique Employees: ${uniqueEmployeesCount}`],
      []
    ], { origin: "A1" });

    // Column widths
    ws['!cols'] = [
      { wch: 8 },  // Sl No
      { wch: 18 }, // Employee Code
      { wch: 28 }, // Employee Name
      { wch: 20 }, // Department
      { wch: 20 }, // Designation
      { wch: 16 }, // Effective Date
      { wch: 20 }, // Previous Gross
      { wch: 20 }, // Revised Gross
      { wch: 22 }, // Monthly Increment
      { wch: 14 }, // Hike %
      { wch: 32 }, // Note / Reason
    ];

    // Freeze panes
    ws['!views'] = [{
      state: 'frozen',
      xSplit: 0,
      ySplit: 4,
      topLeftCell: 'A5',
      activePane: 'bottomLeft'
    }];

    // Style headers and data
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerAddr = XLSX.utils.encode_cell({ r: 3, c: C });
      if (ws[headerAddr]) {
        ws[headerAddr].s = {
          fill: { fgColor: { rgb: "E2E8F0" } },
          font: { bold: true, color: { rgb: "0F172A" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      for (let R = 4; R <= range.e.r; ++R) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) continue;
        if (C >= 6 && C <= 8) { // Currency columns
          ws[addr].s = { alignment: { horizontal: "right" } };
        } else if (C === 0 || C === 5 || C === 9) { // Sl No, Date, Hike %
          ws[addr].s = { alignment: { horizontal: "center" } };
        }
      }
    }

    if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14, color: { rgb: "0F172A" } } };
    if (ws["A2"]) ws["A2"].s = { font: { italic: true, sz: 10, color: { rgb: "64748B" } } };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salary Hikes Report");
    XLSX.writeFile(wb, `${activeCompany.name}_Salary_Hikes_Report.xlsx`);
  };

  const handleDownload = () => {
    // Only include employees who are active / not relieved
    const list = (companyEmps || []).filter(e => e.is_active !== false && e.employment_status !== "relieved");
    const targetList = list.length > 0 ? list : (companyEmps || []);
    const data = targetList.map(emp => ({
      "Employee Code": emp.employee_code || emp.id,
      "Name": emp.name,
      "Current Salary": emp.basic_salary !== undefined ? emp.basic_salary : (emp.basic || 0),
      "New Salary": ""
    }));
    
    // Generate sheet starting at A4
    const ws = XLSX.utils.json_to_sheet(data, { origin: "A4" });
    
    // Add 3 title rows at the top
    XLSX.utils.sheet_add_aoa(ws, [
      [],
      ["Instructions: Fill out the 'New Salary' column. Do NOT modify Employee Code or Current Salary."],
      []
    ], { origin: "A1" });

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Employee Code
      { wch: 35 }, // Name
      { wch: 20 }, // Current Salary
      { wch: 20 }, // New Salary
    ];

    // Freeze panes (freeze first 4 rows)
    ws['!views'] = [{
      state: 'frozen',
      xSplit: 0,
      ySplit: 4,
      topLeftCell: 'A5',
      activePane: 'bottomLeft'
    }];

    // Style the sheet using xlsx-js-style
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerAddr = XLSX.utils.encode_cell({ r: 3, c: C });
      if (ws[headerAddr]) {
        ws[headerAddr].s = {
          fill: { fgColor: { rgb: "E2E8F0" } },
          font: { bold: true, color: { rgb: "1E293B" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
      
      // Style data cells
      for (let R = 4; R <= range.e.r; ++R) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) continue;
        if (C >= 2) { // Salary columns
          ws[addr].s = { alignment: { horizontal: "right" } };
        }
      }
    }
    
    // Style titles
    if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14, color: { rgb: "3B82F6" } } };
    if (ws["A2"]) ws["A2"].s = { font: { italic: true, color: { rgb: "64748B" } } };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Hikes");
    XLSX.writeFile(wb, `${activeCompany.name}_Salary_Hikes.xlsx`);
  };

  const handleUpload = (f) => {
    setFile(f);
    setMsg("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        
        // Find header row dynamically
        const jsonRaw = XLSX.utils.sheet_to_json(ws, { header: 1 });
        let headerRowIdx = -1;
        for (let i = 0; i < jsonRaw.length; i++) {
          if (jsonRaw[i] && (jsonRaw[i].includes("Employee Code") || jsonRaw[i].includes("Name"))) {
            headerRowIdx = i;
            break;
          }
        }
        
        if (headerRowIdx === -1) {
          setMsg("Could not find a valid header row containing 'Employee Code' or 'Name'. Please use the exact downloaded template.");
          return;
        }

        const json = XLSX.utils.sheet_to_json(ws, { range: headerRowIdx });
        
        const validHikes = [];
        for (let i = 0; i < json.length; i++) {
          const row = json[i];
          const code = String(row["Employee Code"] || "").trim();
          const name = String(row["Name"] || "").trim();
          const oldSal = Math.round(parseFloat(row["Current Salary"]) || 0);
          const newSal = Math.round(parseFloat(row["New Salary"]) || 0);

          if (!newSal || isNaN(newSal) || newSal <= 0) continue;

          const emp = (companyEmps || []).find(e => 
            (String(e.employee_code || "").trim().toLowerCase() === code.toLowerCase() || String(e.id || "").trim().toLowerCase() === code.toLowerCase()) &&
            String(e.name || "").trim().toLowerCase() === name.toLowerCase()
          ) || (companyEmps || []).find(e => 
            (String(e.employee_code || "").trim().toLowerCase() === code.toLowerCase() || String(e.id || "").trim().toLowerCase() === code.toLowerCase())
          );

          if (!emp) {
            setMsg(`Error at row ${headerRowIdx + 2 + i}: Could not find employee "${name}" with ID "${code}".`);
            return;
          }
          const currentBasic = Math.round(parseFloat(emp.basic_salary !== undefined ? emp.basic_salary : (emp.basic || 0)) || 0);
          if (Math.abs(currentBasic - oldSal) > 0.5) {
            setMsg(`Error at row ${headerRowIdx + 2 + i} for ${name}: Current Salary in Excel (₹${oldSal}) does not match current record in database (₹${currentBasic}). Please download a fresh template!`);
            return;
          }
          
          if (validHikes.some(hike => hike.id === emp.id)) {
            setMsg(`Employee ${code} appears more than once. Remove duplicate rows and upload again.`);
            return;
          }
          if (newSal === currentBasic) continue;
          validHikes.push({
            id: emp.id,
            name: emp.name,
            oldSalary: currentBasic,
            newSalary: newSal
          });
        }
        
        if (validHikes.length === 0) {
          setMsg("No valid new salaries found in the file. Please fill out numbers in the 'New Salary' column.");
        } else {
          setPreview(validHikes);
          setStep(2);
        }
      } catch (err) {
        setMsg("Failed to parse Excel: " + err.message);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const applyHikes = async () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setSaving(true);
    setMsg("");
    try {
      const result = await applySalaryRevisions({ company: activeCompany, actor: currentUser, effectiveDate, source: "Bulk salary revision",
        changes: preview.map(hike => {
          const employee = (companyEmps || []).find(employee => employee.id === hike.id);
          if (!employee) throw new Error("Employee no longer available. Refresh and retry.");
          return { employee: { ...employee, basic_salary: hike.oldSalary }, patch: { basic_salary: hike.newSalary }, note: "Bulk Salary Revision" };
        }) });
      if (setActiveCompany) setActiveCompany(previous => ({ ...previous, config: result.config }));
      if (setCompanies) setCompanies(previous => previous.map(company => company.id === activeCompany.id ? { ...company, config: result.config } : company));
      const saved = new Map(result.employees.map(employee => [employee.id, mapEmployeeRow(employee)]));
      setEmployees(previous => ({ ...previous, [activeCompany.id]: (previous[activeCompany.id] || []).map(employee =>
        saved.has(employee.id) ? { ...employee, ...saved.get(employee.id) } : employee) }));
      logAudit(currentUser, "BULK_SALARY_HIKE", { company_id: activeCompany.id, count: preview.length, effective_date: effectiveDate,
        hikes: preview.map(hike => ({ id: hike.id, old_salary: hike.oldSalary, new_salary: hike.newSalary })) });
      alert(`Successfully updated ${preview.length} salaries and their revision history, effective ${fmtDate(effectiveDate)}.`);
      onClose();
    } catch (error) { setMsg("Failed to apply hikes: " + error.message); }
    finally { saveInFlightRef.current = false; setSaving(false); }
  };

  return (
    <div className="overlay" onClick={() => !saving && onClose()}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth: tab === "report" ? 880 : 620, width: "100%", transition: "max-width 0.25s ease"}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 14}}>
          <div className="modal-title" style={{margin:0}}>
            {tab === "report" ? "📜 Company Salary Revision & Appraisal Report" : "📈 Bulk Salary Hikes"}
          </div>
          <button className="btn btn-ghost btn-xs" onClick={onClose} disabled={saving} style={{fontSize:16,lineHeight:1,padding:"3px 8px"}}>✕</button>
        </div>

        {/* Top Tab Switcher */}
        <div style={{display:"flex",gap:8,marginBottom:18,borderBottom:"1px solid #e2e8f0",paddingBottom:10}}>
          <button 
            type="button"
            className={`btn btn-sm ${tab==="upload"?"btn-primary":"btn-ghost"}`} 
            onClick={()=>{ setTab("upload"); setMsg(""); }}
            style={{fontWeight:700,borderRadius:8}}
          >
            📈 Mass Salary Hike (Excel)
          </button>
          <button 
            type="button"
            className={`btn btn-sm ${tab==="report"?"btn-primary":"btn-ghost"}`} 
            onClick={()=>{ setTab("report"); setMsg(""); }}
            style={{fontWeight:700,borderRadius:8,display:"inline-flex",alignItems:"center",gap:6}}
          >
            <span>📜 Company Hike Report</span>
            <span style={{background:tab==="report"?"rgba(255,255,255,0.3)":"#e2e8f0",color:tab==="report"?"#fff":"#475569",fontSize:11,padding:"1px 7px",borderRadius:10,fontWeight:800}}>
              {allHikes.length}
            </span>
          </button>
        </div>

        {msg && <div className="err-msg" style={{marginBottom:16}}>⚠️ {msg}</div>}
        
        {/* TAB 1: UPLOAD & BULK EXCEL */}
        {tab === "upload" && step === 1 && (
          <div>
            <div style={{marginBottom:16, fontSize:13, color:"#64748b"}}>
              Download the template, fill out the "New Salary" column, and upload it back. The system will strictly verify that the old salaries haven't changed in the meantime to prevent data corruption.
            </div>
            <div style={{display:"flex", gap:16, marginBottom:20}}>
              <div style={{flex:1, padding:24, border:"2px dashed #e2e8f0", borderRadius:12, textAlign:"center"}}>
                <div style={{fontSize:24, marginBottom:8}}>⬇️</div>
                <div style={{fontWeight:600, marginBottom:8}}>Step 1: Get Template</div>
                <button className="btn btn-ghost btn-sm" onClick={handleDownload}>Download Excel</button>
              </div>
              <div style={{flex:1, padding:24, border:"2px dashed #e2e8f0", borderRadius:12, textAlign:"center"}}>
                <div style={{fontSize:24, marginBottom:8}}>⬆️</div>
                <div style={{fontWeight:600, marginBottom:8}}>Step 2: Upload Data</div>
                <button className="btn btn-ghost btn-sm" onClick={() => { const inp=document.createElement('input');inp.type='file';inp.accept='.xlsx,.xls';inp.onchange=e=>handleUpload(e.target.files[0]);inp.click(); }}>Upload Excel</button>
              </div>
            </div>
          </div>
        )}

        {tab === "upload" && step === 2 && (
          <div>
            <div style={{marginBottom:12, fontSize:13, color:"#166534", background:"#f0fdf4", padding:"8px 12px", borderRadius:6, border:"1px solid #86efac"}}>
              ✅ Validation passed! Previewing hikes for {preview.length} employees.
            </div>
            <label style={{display:"block",fontSize:12,fontWeight:700,color:"#475569",marginBottom:12}}>
              Effective from *
              <input type="date" className="config-input" value={effectiveDate} min={`${salaryToday().slice(0,7)}-01`} max={salaryToday()}
                disabled={saving} onChange={event => setEffectiveDate(event.target.value)} />
              <span style={{fontWeight:400}}>Current month only. Payroll must still be in draft.</span>
            </label>
            <div style={{maxHeight:300, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8, marginBottom:16}}>
              <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                <thead style={{background:"#f8fafc", position:"sticky", top:0}}>
                  <tr>
                    <th style={{padding:8, textAlign:"left", borderBottom:"1px solid #e2e8f0"}}>Name</th>
                    <th style={{padding:8, textAlign:"right", borderBottom:"1px solid #e2e8f0"}}>Old Salary</th>
                    <th style={{padding:8, textAlign:"right", borderBottom:"1px solid #e2e8f0"}}>New Salary</th>
                    <th style={{padding:8, textAlign:"right", borderBottom:"1px solid #e2e8f0"}}>Hike %</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map(h => (
                    <tr key={h.id} style={{borderBottom:"1px solid #e2e8f0"}}>
                      <td style={{padding:8}}>{h.name}</td>
                      <td style={{padding:8, textAlign:"right"}}>₹{Math.round(h.oldSalary || 0).toLocaleString("en-IN")}</td>
                      <td style={{padding:8, textAlign:"right", color:"#16a34a", fontWeight:600}}>₹{Math.round(h.newSalary || 0).toLocaleString("en-IN")}</td>
                      <td style={{padding:8, textAlign:"right"}}>
                        {h.oldSalary > 0 ? `${(((h.newSalary - h.oldSalary) / h.oldSalary) * 100).toFixed(1)}%` : "Initial salary"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: AUDIT REPORT & FULL HISTORY */}
        {tab === "report" && (
          <div>
            {/* KPI Summary Banner */}
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:16}}>
              <div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase"}}>Total Revisions</div>
                <div style={{fontSize:20,fontWeight:800,color:"#1e293b",marginTop:2}}>{allHikes.length}</div>
              </div>
              <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#1d4ed8",textTransform:"uppercase"}}>Employees Impacted</div>
                <div style={{fontSize:20,fontWeight:800,color:"#2563eb",marginTop:2}}>{uniqueEmployeesCount}</div>
              </div>
              <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#15803d",textTransform:"uppercase"}}>Average Hike</div>
                <div style={{fontSize:20,fontWeight:800,color:"#16a34a",marginTop:2}}>+{avgHikePct}%</div>
              </div>
              <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#7e22ce",textTransform:"uppercase"}}>Monthly Outflow Added</div>
                <div style={{fontSize:20,fontWeight:800,color:"#9333ea",marginTop:2}}>+{fmtINR(totalIncrementAmount)}</div>
              </div>
            </div>

            {/* Actions Bar: Search + Export Button */}
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:12, flexWrap:"wrap"}}>
              <div style={{flex:1, minWidth:220, position:"relative"}}>
                <input 
                  type="text" 
                  className="search-input" 
                  placeholder="Search by employee name, code, dept, note..." 
                  value={reportSearch} 
                  onChange={e => setReportSearch(e.target.value)} 
                  style={{margin:0, padding:"8px 12px", width:"100%", fontSize:13, boxSizing:"border-box"}}
                />
              </div>
              <button 
                type="button"
                className="btn btn-primary btn-sm" 
                onClick={handleDownloadHikeReport}
                disabled={allHikes.length === 0}
                style={{display:"inline-flex",alignItems:"center",gap:6,fontWeight:700,padding:"8px 16px"}}
              >
                <span>⬇️</span> Download Full Report (.xlsx)
              </button>
            </div>

            {/* Revisions Table */}
            {allHikes.length === 0 ? (
              <div style={{padding:36,textAlign:"center",background:"#f8fafc",borderRadius:10,border:"1px dashed #cbd5e1",color:"#64748b"}}>
                <div style={{fontSize:32,marginBottom:8}}>📊</div>
                <div style={{fontWeight:700,fontSize:14,color:"#334155"}}>No Salary Revisions Recorded Yet</div>
                <div style={{fontSize:12.5,marginTop:4}}>
                  Use the <strong>"Mass Salary Hike"</strong> tab to upload Excel increments or log appraisals in employee profiles.
                </div>
              </div>
            ) : filteredHikes.length === 0 ? (
              <div style={{padding:24,textAlign:"center",background:"#f8fafc",borderRadius:10,border:"1px dashed #cbd5e1",color:"#64748b",fontSize:13}}>
                No revisions match "<strong>{reportSearch}</strong>".
              </div>
            ) : (
              <div style={{maxHeight:330, overflow:"auto", border:"1px solid #e2e8f0", borderRadius:8}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12.5}}>
                  <thead style={{background:"#f8fafc", position:"sticky", top:0, zIndex:1}}>
                    <tr>
                      <th style={{padding:"8px 10px", textAlign:"left", borderBottom:"1px solid #e2e8f0"}}>Effective Date</th>
                      <th style={{padding:"8px 10px", textAlign:"left", borderBottom:"1px solid #e2e8f0"}}>Employee</th>
                      <th style={{padding:"8px 10px", textAlign:"left", borderBottom:"1px solid #e2e8f0"}}>Department</th>
                      <th style={{padding:"8px 10px", textAlign:"right", borderBottom:"1px solid #e2e8f0"}}>Previous Salary</th>
                      <th style={{padding:"8px 10px", textAlign:"right", borderBottom:"1px solid #e2e8f0"}}>Revised Salary</th>
                      <th style={{padding:"8px 10px", textAlign:"center", borderBottom:"1px solid #e2e8f0"}}>Hike %</th>
                      <th style={{padding:"8px 10px", textAlign:"left", borderBottom:"1px solid #e2e8f0"}}>Note / Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHikes.map((h, i) => (
                      <tr key={i} style={{borderBottom:"1px solid #f1f5f9", background: i%2===0?"#fff":"#fafafa"}}>
                        <td style={{padding:"8px 10px", whiteSpace:"nowrap", color:"#475569"}}>{h.date ? fmtDate(h.effective_date || h.date) : "—"}</td>
                        <td style={{padding:"8px 10px"}}>
                          <div style={{fontWeight:700, color:"#0f172a"}}>{h.name}</div>
                          <div style={{fontSize:11, color:"#94a3b8"}}>{h.empCode}</div>
                        </td>
                        <td style={{padding:"8px 10px", color:"#475569"}}>{h.department}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", color:"#94a3b8", textDecoration:"line-through"}}>{fmtINR(h.oldSalary)}</td>
                        <td style={{padding:"8px 10px", textAlign:"right", fontWeight:700, color:"#16a34a"}}>{fmtINR(h.newSalary)}</td>
                        <td style={{padding:"8px 10px", textAlign:"center"}}>
                          <span style={{
                            fontSize:11,
                            fontWeight:800,
                            padding:"1px 7px",
                            borderRadius:10,
                            background: h.hikePct >= 0 ? "#f0fdf4" : "#fef2f2",
                            color: h.hikePct >= 0 ? "#16a34a" : "#dc2626",
                            border: `1px solid ${h.hikePct >= 0 ? "#bbf7d0" : "#fecaca"}`
                          }}>
                            {h.hikePct >= 0 ? `+${h.hikePct}%` : `${h.hikePct}%`}
                          </span>
                        </td>
                        <td style={{padding:"8px 10px", color:"#334155", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}} title={h.note}>{h.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="modal-footer" style={{marginTop:18, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <div>
            {tab === "report" && (
              <span style={{fontSize:12, color:"#64748b"}}>Showing {filteredHikes.length} of {allHikes.length} revision records</span>
            )}
          </div>
          <div style={{display:"flex", gap:8}}>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Close</button>
            {tab === "upload" && step === 2 && (
              <button className="btn btn-primary" onClick={applyHikes} disabled={saving}>
                {saving ? "Saving..." : `Confirm & Apply ${preview.length} Hikes`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Company Details Page
function CompanyDetailsPage({company, onSave, onBack, msg, setMsg, isCompanyRole, setModal, onDirtyChange}){
  const formatPreferredTime = (value) => {
    const [hour, minute] = value.split(":").map(Number);
    return `${String(hour % 12 || 12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const initialForm = {
    name:"", gst:"", address:"", website:"", contact:"", phone:"", email:"",
    pf_number:"", esi_code:"", pt_reg:"",
    office_lat:"", office_lng:"",
    ...(company||{}),
  };
  const [form, setForm] = useState(initialForm);
  const [hasSalary, setHasSalary] = useState(!!(company?.config));
  const [cfg, setCfg] = useState({...DEFAULT_CONFIG,...(company?.config||{})});
  const [showBasicInfo, setShowBasicInfo] = useState(true);
  const [showStatutory, setShowStatutory] = useState(false);
  const [showDepartments, setShowDepartments] = useState(false);
  const [showLeaveSettings, setShowLeaveSettings] = useState(false);
  const [showAttendanceSettings, setShowAttendanceSettings] = useState(false);
  const [showAddLeaveModal, setShowAddLeaveModal] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [newLeaveName, setNewLeaveName] = useState("");
  const [newLeaveDays, setNewLeaveDays] = useState(12);
  const [addLeaveError, setAddLeaveError] = useState("");
  const [deptDesigModal, setDeptDesigModal] = useState(null); // { type: 'department' | 'designation', value: '', error: '' }
  const set = k => v => {
    let val = v;
    if (typeof val === 'string' && (k === 'name' || k === 'contact')) val = val.slice(0, 100);
    setForm(p=>({...p,[k]:val})); setMsg(""); if(onDirtyChange) onDirtyChange(true);
  };
  const setCf = k => v => { setCfg(p=>({...p,[k]:v})); setMsg(""); if(onDirtyChange) onDirtyChange(true); };

  const handleSaveCompanyDeptDesig = (e) => {
    if (e) e.preventDefault();
    if (!deptDesigModal) return;
    const val = (deptDesigModal.value || "").trim();
    const type = deptDesigModal.type;
    if (!val) {
      setDeptDesigModal(prev => ({ ...prev, error: `Please enter a valid ${type === "department" ? "department name" : "designation title"}.` }));
      return;
    }
    if (type === "department") {
      const d = [...(cfg.departments || [])];
      if (!d.some(x => x.toLowerCase() === val.toLowerCase())) {
        d.push(val);
        setCf("departments")(d);
      }
    } else {
      const d = [...(cfg.designations || [])];
      if (!d.some(x => x.toLowerCase() === val.toLowerCase())) {
        d.push(val);
        setCf("designations")(d);
      }
    }
    setDeptDesigModal(null);
  };
  const handleSave = () => {
    if (!form.name?.trim()) { setMsg("Company Name is required."); return; }
    if (form.name.trim().length > 100) { setMsg("Company name cannot exceed 100 characters."); return; }
    if (form.contact && form.contact.trim().length > 100) { setMsg("Contact person name cannot exceed 100 characters."); return; }
    
    // Validate primary phone
    const phone10 = getPhone10(form.phone);
    if (!phone10 || phone10.length !== 10) {
      setMsg("Phone number is required. Enter 10-digit number after +91.");
      return;
    }
    if (!/^[6-9][0-9]{9}$/.test(phone10)) {
      setMsg("Invalid phone number. Must be a 10-digit Indian mobile number starting with 6–9.");
      return;
    }

    if (form.email?.trim() && !isValidEmail(form.email)) {
      setMsg("Invalid email address format (e.g. contact@company.com).");
      return;
    }

    if (form.website?.trim() && !/^https?:\/\/.+\..+/i.test(form.website.trim()) && !/^[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/i.test(form.website.trim())) {
      setMsg("Invalid website URL format (e.g. https://company.com).");
      return;
    }

    // Validate additional contacts if provided
    for (let i = 0; i < (cfg.additional_contacts || []).length; i++) {
      const ac = cfg.additional_contacts[i];
      if (ac.name && ac.name.trim().length > 100) {
        setMsg(`Contact person name cannot exceed 100 characters for Contact Person ${i+2}.`);
        return;
      }
      if (ac.phone) {
        const ac10 = getPhone10(ac.phone);
        if (ac10.length !== 10 || !/^[6-9][0-9]{9}$/.test(ac10)) {
          setMsg(`Invalid phone number for Contact Person ${i+2}. Must be a 10-digit Indian mobile number starting with 6–9.`);
          return;
        }
      }
      if (ac.email?.trim() && !isValidEmail(ac.email)) {
        setMsg(`Invalid email format for Contact Person ${i+2} (e.g. contact@company.com).`);
        return;
      }
    }

    onSave({
      ...form,
      name: form.name.trim().slice(0, 100),
      contact: form.contact ? form.contact.trim().slice(0, 100) : "",
      phone: `+91 ${phone10}`,
      config: (hasSalary || cfg) ? {...cfg,_configured:true} : null
    });
    if(onDirtyChange) onDirtyChange(false);
  };

  return(
    <div>
      {!isCompanyRole && (
        <div className="back-btn" onClick={onBack}>← Back to Companies</div>
      )}
      <div className="page-header" style={{marginBottom:20}}>
        <div style={{display:"flex", alignItems:"center", gap: 16}}>
          <div style={{position:"relative"}}>
            {logoUploading ? (
              <div style={{width: 56, height: 56, borderRadius: 12, border: "1px solid #cbd5e1", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22}}>
                ⏳
              </div>
            ) : cfg.logo_base64 ? (
              <a href={cfg.logo_base64} target="_blank" rel="noreferrer noopener" title="View company logo full size" style={{display: "block"}}>
                <div style={{position:"relative", width: 56, height: 56, borderRadius: 12, border: "1px solid #e2e8f0", overflow: "visible", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"}}>
                  <img src={cfg.logo_base64} alt="Company Logo" style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 12}} />
                </div>
              </a>
            ) : (
              <div style={{width: 56, height: 56, borderRadius: 12, border: "2px dashed #cbd5e1", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#94a3b8"}}>
                🏢
              </div>
            )}
            <input type="file" accept="image/png, image/jpeg" id="logoUpload" style={{display:"none"}} disabled={logoUploading} onChange={async e => {
              const file = e.target.files[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) {
                setMsg("Error: Image size must be less than 5MB.");
                return;
              }
              if (!['image/jpeg', 'image/png'].includes(file.type)) {
                setMsg("Error: Only JPG and PNG formats are supported.");
                return;
              }
              setLogoUploading(true);
              setMsg("");
              try {
                const reader = new FileReader();
                const dataUrl = await new Promise((resolve, reject) => {
                  reader.onload = (event) => {
                    const img = new Image();
                    img.onload = () => {
                      const canvas = document.createElement("canvas");
                      let width = img.width;
                      let height = img.height;
                      const maxSize = 200;
                      if (width > height) {
                        if (width > maxSize) { height *= maxSize / width; width = maxSize; }
                      } else {
                        if (height > maxSize) { width *= maxSize / height; height = maxSize; }
                      }
                      canvas.width = width;
                      canvas.height = height;
                      const ctx = canvas.getContext("2d");
                      ctx.drawImage(img, 0, 0, width, height);
                      // jsPDF strictly requires PNG or JPEG (WebP not supported in doc.addImage)
                      resolve(canvas.toDataURL("image/png"));
                    };
                    img.onerror = () => reject(new Error("Failed to process image"));
                    img.src = event.target.result;
                  };
                  reader.onerror = () => reject(new Error("Failed to read file"));
                  reader.readAsDataURL(file);
                });

                const coIdent = (company?.code || form?.name || company?.name || 'Company').trim().replace(/[^A-Za-z0-9_-]+/g, '-');
                const r2Url = await uploadToR2(dataUrl, 'logos', `${coIdent}_Logo`);
                if (r2Url) {
                  setCf("logo_base64")(r2Url);
                  cacheLogoImage(r2Url);
                } else {
                  setMsg("Error: Failed to upload logo to storage.");
                }
              } catch (err) {
                setMsg("Error: Could not process company logo.");
              } finally {
                setLogoUploading(false);
                e.target.value = "";
              }
            }} />
          </div>
          <div>
            <div className="page-title" style={{display:"flex", alignItems:"center", gap: 12}}>
              {form.name||company.name}
              <div style={{display:"flex", alignItems:"center", gap: 8}}>
                <label htmlFor="logoUpload" title={`${form.name || company.name || 'Company'} logo will be used across all documents (letters, salary slips, etc.)`} style={{cursor: logoUploading ? "not-allowed" : "pointer", opacity: logoUploading ? 0.6 : 1, padding:"4px 8px", fontSize:11, color:"#3b82f6", background:"#eff6ff", border:"1.5px dashed #93c5fd", borderRadius: 6, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4}}>
                  {logoUploading ? "⏳ Uploading..." : "⬆ Upload Logo"}
                </label>
                <span style={{fontSize: 10, color: "#94a3b8", fontWeight: 600, fontStyle: "italic"}}>(Max 5MB, PNG/JPG)</span>
              </div>
            </div>
            <div className="page-sub" style={{display:"flex", gap: 8, alignItems: "center", flexWrap:"wrap"}}>
              <span>Edit company details</span>
              {(form.contact || form.phone || form.email) && (
                <>
                  <span style={{color:"#cbd5e1"}}>|</span>
                  {form.contact && <span>👤 {form.contact}</span>}
                  {form.phone && <span>📞 {form.phone}</span>}
                  {form.email && <span>✉️ {form.email}</span>}
                </>
              )}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>exportSingleCompanyExcel(company)}>⬇ Excel</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>exportSingleCompanyPDF(company,0)}>📄 PDF</button>
        </div>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:16}}>

        {/* ── Basic Information */}
        <div className="profile-accordion-card">
          <div
            className="profile-accordion-header blue"
            onClick={() => setShowBasicInfo(!showBasicInfo)}
            title={showBasicInfo ? "Click to collapse Basic Information" : "Click to expand Basic Information"}
          >
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <span style={{fontSize: 16}}>📋</span>
              <span style={{fontSize: 13, fontWeight: 800, color: "#1d4ed8", letterSpacing: 0.5}}>BASIC INFORMATION</span>
              <span className="profile-accordion-tag" style={{background: "#dbeafe", color: "#1e40af"}}>General</span>
            </div>
            <button
              className="profile-accordion-btn blue"
              onClick={(e) => { e.stopPropagation(); setShowBasicInfo(!showBasicInfo); }}
            >
              <span>{showBasicInfo ? "Collapse" : "Expand"}</span>
              <span style={{fontSize: 14, lineHeight: 1}}>{showBasicInfo ? "−" : "＋"}</span>
            </button>
          </div>
          {showBasicInfo && (
            <div style={{padding: 24, display: "flex", flexDirection: "column", gap: 16}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,display:"flex",alignItems:"center",gap:4}}>Company Name <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} /></div>
                <input className="config-input" type="text" maxLength={100} value={form.name||""}
                  onChange={e=>set("name")(e.target.value.slice(0, 100))} placeholder="Acme Technologies Pvt Ltd"
                  style={{}} />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>GST Number</div>
                <input className="config-input" type="text" value={form.gst||""}
                  onChange={e=>set("gst")(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                  style={{fontFamily:"monospace",letterSpacing:1}} />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Address</div>
                <input className="config-input" type="text" value={form.address||""}
                  onChange={e=>set("address")(e.target.value)} placeholder="City, State"
                  style={{}} />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Website</div>
                <input className="config-input" type="text" value={form.website||""}
                  onChange={e=>set("website")(e.target.value)} placeholder="https://company.com"
                  style={{}} />
              </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Contact Person</div>
                <input className="config-input" type="text" maxLength={100} value={form.contact||""}
                  onChange={e=>set("contact")(e.target.value.slice(0, 100))} placeholder="John Doe" />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,display:"flex",alignItems:"center",gap:4}}>
                  Phone <span className="status-dot red" style={{marginLeft:4,verticalAlign:"middle"}} title="Mandatory: 10-digit Indian mobile number" />
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <div style={{background:"#f1f5f9",border:"1.5px solid #cbd5e1",borderRadius:8,padding:"8px 12px",fontSize:13,color:"#475569",fontWeight:700,display:"flex",alignItems:"center",gap:4,userSelect:"none"}}>
                    <span>🇮🇳</span>
                    <span>+91</span>
                  </div>
                  <input 
                    type="tel"
                    className="config-input" 
                    value={getPhone10(form.phone)} 
                    onChange={e=>{
                      const v = getPhone10(e.target.value);
                      set("phone")(v ? `+91 ${v}` : "");
                    }} 
                    placeholder="93530 10107" 
                    style={{fontFamily:"monospace",letterSpacing:0.5}} 
                    maxLength={16} 
                  />
                </div>
                {getPhone10(form.phone).length > 0 && getPhone10(form.phone).length < 10 && (
                  <div style={{fontSize: 10, color: "#ef4444", marginTop: 3, fontWeight: 600}}>
                    ⚠️ {getPhone10(form.phone).length}/10 digits (enter 10-digit mobile)
                  </div>
                )}
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Email</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{position:"relative",flex:1,minWidth:0}}>
                  <input className="config-input" type="email" value={form.email||""}
                    onChange={e=>set("email")(e.target.value.replace(/\s/g, ''))} placeholder="contact@company.com" 
                    style={{
                      paddingRight: (form.email||"").trim() && !isValidEmail(form.email) ? 32 : 12,
                      borderColor: (form.email||"").trim() && !isValidEmail(form.email) ? "#ef4444" : undefined
                    }}
                  />
                  {(form.email||"").trim() && !isValidEmail(form.email) && (
                    <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:13,pointerEvents:"none"}}>
                      ⚠️
                    </span>
                  )}
                </div>
                <button type="button" className="btn btn-ghost btn-xs" title="Add another contact" aria-label="Add another contact" style={{width:36,height:38,flexShrink:0,padding:0,display:"grid",placeItems:"center",border:"1px solid #cbd5e1",borderRadius:8,background:"linear-gradient(145deg,#fff,#f1f5f9)",color:"#2563eb",fontSize:22,lineHeight:1}} onClick={()=>setCf("additional_contacts")([...(cfg.additional_contacts||[]),{name:"",phone:"",email:""}])}>+</button>
                </div>
                {(form.email||"").trim() && !isValidEmail(form.email) && (
                  <div style={{fontSize:10,marginTop:3,fontWeight:600,color:"#ef4444"}}>
                    ⚠️ Invalid email format (e.g. contact@company.com)
                  </div>
                )}
              </div>
          </div>
          {(cfg.additional_contacts || []).map((ac, idx) => (
            <div key={idx} style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12, position: "relative", marginTop: 4}}>
              <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Contact Person {idx+2}</div>
                  <input className="config-input" type="text" maxLength={100} value={ac.name||""}
                    onChange={e=>{
                      const arr = [...(cfg.additional_contacts||[])];
                      arr[idx].name = e.target.value.slice(0, 100);
                      setCf("additional_contacts")(arr);
                    }} placeholder="Additional Contact" />
                </div>
              <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Phone</div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <div style={{background:"#f1f5f9",border:"1.5px solid #cbd5e1",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#475569",fontWeight:700,display:"flex",alignItems:"center",userSelect:"none"}}>+91</div>
                    <input 
                      type="tel"
                      className="config-input" 
                      value={getPhone10(ac.phone)} 
                      onChange={e=>{
                        const v = getPhone10(e.target.value);
                        const arr = [...(cfg.additional_contacts||[])];
                        arr[idx].phone = v ? `+91 ${v}` : "";
                        setCf("additional_contacts")(arr);
                      }} 
                      placeholder="93530 10107" 
                      style={{fontFamily:"monospace"}} 
                      maxLength={16} 
                    />
                  </div>
                  {getPhone10(ac.phone).length > 0 && getPhone10(ac.phone).length < 10 && (
                    <div style={{fontSize: 10, color: "#ef4444", marginTop: 3, fontWeight: 600}}>
                      ⚠️ {getPhone10(ac.phone).length}/10 digits
                    </div>
                  )}
                </div>
              <div className="config-field" style={{position:"relative", display:"flex", alignItems:"flex-end", gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Email</div>
                    <div style={{position:"relative"}}>
                      <input className="config-input" type="email" value={ac.email||""}
                        onChange={e=>{
                          const arr = [...(cfg.additional_contacts||[])];
                          arr[idx].email = e.target.value.replace(/\s/g, '');
                          setCf("additional_contacts")(arr);
                        }} placeholder="contact@company.com" 
                        style={{
                          paddingRight: (ac.email||"").trim() && !isValidEmail(ac.email) ? 32 : 12,
                          borderColor: (ac.email||"").trim() && !isValidEmail(ac.email) ? "#ef4444" : undefined
                        }}
                      />
                      {(ac.email||"").trim() && !isValidEmail(ac.email) && (
                        <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:13,pointerEvents:"none"}}>
                          ⚠️
                        </span>
                      )}
                    </div>
                    {(ac.email||"").trim() && !isValidEmail(ac.email) && (
                      <div style={{fontSize:10,marginTop:3,fontWeight:600,color:"#ef4444"}}>
                        ⚠️ Invalid email
                      </div>
                    )}
                  </div>
                  <button className="btn btn-ghost btn-xs" style={{height: 38, color: "#ef4444", padding: "0 8px", background: "#fef2f2", border: "1px solid #fecaca"}} title="Remove Contact" onClick={() => {
                      const arr = [...(cfg.additional_contacts||[])];
                      arr.splice(idx, 1);
                      setCf("additional_contacts")(arr);
                  }}>🗑️</button>
                </div>
            </div>
          ))}

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12, marginTop: 4}}>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}} title="Optional prefix for auto-generating employee IDs (e.g. EMP-)">Emp ID Prefix</div>
                <input className="config-input" type="text" value={cfg.emp_id_prefix||""}
                  onChange={e=>setCf("emp_id_prefix")(e.target.value)} placeholder="e.g. EMP-" />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}} title="The starting number for auto-generating employee IDs (e.g. 1001)">Emp ID Start Number</div>
                <input className="config-input" type="number" value={cfg.emp_id_start_number||""}
                  onChange={e=>setCf("emp_id_start_number")(e.target.value)} placeholder="e.g. 1001" />
              </div>
          </div>

            </div>
          )}
        </div>

        {/* ── Subscription Plan Tier (SuperAdmin Only) */}
        {!isCompanyRole && (
          <div style={{background:"#ffffff",border:"1px solid #e2e8f0",borderRadius:14,padding:24,display:"flex",flexDirection:"column",gap:14,boxShadow:"0 1px 3px rgba(0,0,0,0.05)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#3b82f6",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>📦 Subscription Plan Tier</div>
                <div style={{fontSize:12,color:"#64748b"}}>Controls the company's active employee capacity limit.</div>
              </div>
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:"#f1f5f9",padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:700,color:"#0f172a",border:"1px solid #e2e8f0"}}>
                Active Plan: <span style={{color:"#4f46e5",fontWeight:800}}>{cfg.plan_tier || "Free"}</span> <span style={{color:"#64748b",fontWeight:500}}>({(PLAN_LIMITS[cfg.plan_tier || "Free"] || 5).toLocaleString("en-IN")} max employees)</span>
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12,marginTop:4}}>
              <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Select Plan Tier</div>
                <select
                  className="config-input"
                  value={cfg.plan_tier || "Free"}
                  onChange={e => setCf("plan_tier")(e.target.value)}
                  style={{fontWeight:700,cursor:"pointer",fontSize:13,background:"#f8fafc",border:"1.5px solid #cbd5e1",padding:"8px 12px",borderRadius:8}}
                >
                  <option value="Free">Free (up to 5 employees)</option>
                  <option value="Basic">Basic (up to 25 employees)</option>
                  <option value="Pro">Pro (up to 50 employees)</option>
                  <option value="Enterprise">Enterprise (up to 3,000 employees)</option>
                  <option value="Enterprise Plus">Enterprise Plus (up to 10,000 employees)</option>
                  <option value="Ultimate">Ultimate (unlimited / 100,000 employees)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── Add-on Features */}
        {!isCompanyRole && <CompanyAddonsPanel config={cfg} onChange={(key, value) => setCf(key)(value)}/>}

        {!isCompanyRole && <AdminAccessPanel company={company}/>}
        {!isCompanyRole && <RecruitmentAISettings key={company.id} company={company}/>}
        <EmployeeEmailPolicy key={`email-policy-${company.id}`} company={company}/>

        {/* ── Statutory Registration */}
        <div className="profile-accordion-card">
          <div
            className="profile-accordion-header purple"
            onClick={() => setShowStatutory(!showStatutory)}
            title={showStatutory ? "Click to collapse Statutory Registration" : "Click to expand Statutory Registration"}
          >
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <span style={{fontSize: 16}}>🏛</span>
              <span style={{fontSize: 13, fontWeight: 800, color: "#6d28d9", letterSpacing: 0.5}}>STATUTORY REGISTRATION</span>
              <span className="profile-accordion-tag" style={{background: "#f3e8ff", color: "#6b21a8"}}>Compliance</span>
            </div>
            <button
              className="profile-accordion-btn purple"
              onClick={(e) => { e.stopPropagation(); setShowStatutory(!showStatutory); }}
            >
              <span>{showStatutory ? "Collapse" : "Expand"}</span>
              <span style={{fontSize: 14, lineHeight: 1}}>{showStatutory ? "−" : "＋"}</span>
            </button>
          </div>
          {showStatutory && (
            <div style={{padding: 24}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
                <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>PF Registration Number</div>
                  <input className="config-input" type="text" value={form.pf_number||""}
                    onChange={e=>set("pf_number")(e.target.value)} placeholder="MH/BAN/0012345"
                    style={{fontFamily:"monospace",letterSpacing:1}} />
                </div>
                <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>ESI Code</div>
                  <input className="config-input" type="text" value={form.esi_code||""}
                    onChange={e=>set("esi_code")(e.target.value)} placeholder="31-00-123456-000-0001"
                    style={{fontFamily:"monospace",letterSpacing:1}} />
                </div>
                <div className="config-field">
                  <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>PT Registration No.</div>
                  <input className="config-input" type="text" value={form.pt_reg||""}
                    onChange={e=>set("pt_reg")(e.target.value)} placeholder="PT/MH/XXXX"
                    style={{fontFamily:"monospace",letterSpacing:1}} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Office Location */}
        <div style={{display: "none", background:"#ffffff",border:"1px solid #e2e8f0",borderRadius:14,padding:24,flexDirection:"column",gap:16,boxShadow:"0 1px 3px rgba(0,0,0,0.05)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#3b82f6",textTransform:"uppercase",letterSpacing:1}}>📍 Office Location (for Attendance)</div>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(pos => {
                  set("office_lat")(pos.coords.latitude.toFixed(6));
                  set("office_lng")(pos.coords.longitude.toFixed(6));
                }, err => alert("Could not get location: " + err.message));
              } else alert("Geolocation not supported by this browser.");
            }}>🎯 Pick Current Location</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Latitude</div>
                <input className="config-input" type="number" value={form.office_lat||""}
                  onChange={e=>set("office_lat")(e.target.value)} placeholder="12.9716"
                  style={{}} />
              </div>
            <div className="config-field">
                <div style={{fontSize:11,fontWeight:700,color:"#0f172a",textTransform:"uppercase",letterSpacing:.5}}>Longitude</div>
                <input className="config-input" type="number" value={form.office_lng||""}
                  onChange={e=>set("office_lng")(e.target.value)} placeholder="77.5946"
                  style={{}} />
              </div>
          </div>
          <div style={{fontSize:12,color:"#64748b"}}>Used by the attendance app to verify employee check-in proximity.</div>
        </div>

        {/* ── Departments & Designations */}
        <div className="profile-accordion-card">
          <div
            className="profile-accordion-header amber"
            onClick={() => setShowDepartments(!showDepartments)}
            title={showDepartments ? "Click to collapse Departments & Designations" : "Click to expand Departments & Designations"}
          >
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <span style={{fontSize: 16}}>🏢</span>
              <span style={{fontSize: 13, fontWeight: 800, color: "#b45309", letterSpacing: 0.5}}>DEPARTMENTS & DESIGNATIONS</span>
              <span className="profile-accordion-tag" style={{background: "#fef3c7", color: "#92400e"}}>
                {(cfg.departments||[]).length} Depts · {(cfg.designations||[]).length} Desigs
              </span>
            </div>
            <button
              className="profile-accordion-btn amber"
              onClick={(e) => { e.stopPropagation(); setShowDepartments(!showDepartments); }}
            >
              <span>{showDepartments ? "Collapse" : "Expand"}</span>
              <span style={{fontSize: 14, lineHeight: 1}}>{showDepartments ? "−" : "＋"}</span>
            </button>
          </div>
          {showDepartments && (
            <div style={{padding: 24}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))",gap:24}}>
            
            {/* Departments */}
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#1e293b",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                Departments
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => {
                  setDeptDesigModal({ type: "department", value: "", error: "" });
                }}>+ Add</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:150,overflow:"auto",border:"1px solid #e2e8f0",padding:12,borderRadius:8}}>
                {(cfg.departments||[]).length === 0 && <div style={{fontSize:12,color:"#94a3b8",fontStyle:"italic"}}>No departments added.</div>}
                {(cfg.departments||[]).map(dept => (
                  <div key={dept} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,color:"#475569"}}>
                    {dept}
                    <button className="btn btn-ghost btn-xs" style={{padding:"2px 6px",color:"#ef4444",borderColor:"transparent"}} onClick={() => {
                      if (window.confirm(`Delete department "${dept}"?`)) {
                        setCf("departments")((cfg.departments||[]).filter(d => d !== dept));
                      }
                    }}>🗑️</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Designations */}
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#1e293b",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                Designations
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => {
                  setDeptDesigModal({ type: "designation", value: "", error: "" });
                }}>+ Add</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:150,overflow:"auto",border:"1px solid #e2e8f0",padding:12,borderRadius:8}}>
                {(cfg.designations||[]).length === 0 && <div style={{fontSize:12,color:"#94a3b8",fontStyle:"italic"}}>No designations added.</div>}
                {(cfg.designations||[]).map(desig => (
                  <div key={desig} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,color:"#475569"}}>
                    {desig}
                    <button className="btn btn-ghost btn-xs" style={{padding:"2px 6px",color:"#ef4444",borderColor:"transparent"}} onClick={() => {
                      if (window.confirm(`Delete designation "${desig}"?`)) {
                        setCf("designations")((cfg.designations||[]).filter(d => d !== desig));
                      }
                    }}>🗑️</button>
                  </div>
                ))}
              </div>
            </div>

            </div>
            </div>
          )}
        </div>

        {/* ── Leave Management Settings */}
        {cfg.leave_module_enabled === true && (
          <div className="profile-accordion-card">
            <div
              className="profile-accordion-header slate"
              onClick={() => setShowLeaveSettings(!showLeaveSettings)}
              title={showLeaveSettings ? "Click to collapse Leave Management Settings" : "Click to expand Leave Management Settings"}
            >
              <div style={{display: "flex", alignItems: "center", gap: 10}}>
                <span style={{fontSize: 16}}>🏖️</span>
                <span style={{fontSize: 13, fontWeight: 800, color: "#334155", letterSpacing: 0.5}}>LEAVE MANAGEMENT SETTINGS</span>
                <span className="profile-accordion-tag" style={{background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0"}}>
                  {(cfg.working_days_schedule||[1,2,3,4,5,6]).length} Work Days · {(cfg.leave_types||[]).length} Types · {(cfg.company_holidays||[]).length} Holidays
                </span>
              </div>
              <button
                className="profile-accordion-btn slate"
                onClick={(e) => { e.stopPropagation(); setShowLeaveSettings(!showLeaveSettings); }}
              >
                <span>{showLeaveSettings ? "Collapse" : "Expand"}</span>
                <span style={{fontSize: 14, lineHeight: 1}}>{showLeaveSettings ? "−" : "＋"}</span>
              </button>
            </div>
            {showLeaveSettings && (
              <div style={{padding: 24, display:"flex", flexDirection:"column", gap: 24}}>
                
                {/* Working Days Schedule */}
                <div>
                  <div style={{fontSize:13, fontWeight:700, color:"#1e293b", marginBottom:4, display:"flex", alignItems:"center", gap:8}}>
                    <span>🗓️</span> Working Days
                    <span style={{fontSize:11, fontWeight:700, background:"#f1f5f9", color:"#475569", border:"1px solid #cbd5e1", padding:"2px 8px", borderRadius:12}}>
                      {(cfg.working_days_schedule || [1,2,3,4,5,6]).length} days / week
                    </span>
                  </div>
                  <div style={{fontSize: 11, color: "#64748b", marginBottom: 12}}>
                    Selected days are treated as regular working days. Unselected days (weekends) will not count towards leave balances when applying for leaves.
                  </div>
                  <div style={{display:"flex", gap: 10, flexWrap: "wrap"}}>
                    {[{label: "Mon", val: 1}, {label: "Tue", val: 2}, {label: "Wed", val: 3}, {label: "Thu", val: 4}, {label: "Fri", val: 5}, {label: "Sat", val: 6}, {label: "Sun", val: 0}].map(d => {
                      const isChecked = (cfg.working_days_schedule || [1,2,3,4,5,6]).includes(d.val);
                      return (
                        <label 
                          key={d.val} 
                          style={{
                            display:"flex", 
                            alignItems:"center", 
                            gap: 8, 
                            padding: "8px 14px", 
                            borderRadius: 8, 
                            border: isChecked ? "1.5px solid #64748b" : "1px solid #cbd5e1", 
                            background: isChecked ? "#f1f5f9" : "#ffffff",
                            cursor:"pointer",
                            transition: "all 0.15s ease",
                            userSelect: "none"
                          }}
                        >
                          <input 
                            type="checkbox" 
                            style={{width: 16, height: 16, cursor: "pointer", accentColor: "#475569"}} 
                            checked={isChecked} 
                            onChange={(e) => {
                              let sched = [...(cfg.working_days_schedule || [1,2,3,4,5,6])];
                              if (e.target.checked && !sched.includes(d.val)) sched.push(d.val);
                              else if (!e.target.checked) sched = sched.filter(x => x !== d.val);
                              setCf("working_days_schedule")(sched);
                            }} 
                          /> 
                          <span style={{fontWeight: 700, fontSize: 13, color: isChecked ? "#0f172a" : "#64748b"}}>{d.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Custom Leave Types */}
                <div style={{borderTop: "1px solid #f1f5f9", paddingTop: 16}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#1e293b",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    Leave Types &amp; Quotas
                    <button 
                      type="button"
                      className="btn btn-ghost btn-xs add-leave-type-btn" 
                      onClick={() => {
                        setNewLeaveName("");
                        setNewLeaveDays(12);
                        setAddLeaveError("");
                        setShowAddLeaveModal(true);
                      }}
                    >
                      + Add Leave Type
                    </button>
                  </div>
                  <div style={{fontSize:12,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:6,padding:"8px 10px",marginBottom:12,lineHeight:1.5}}>
                    <strong>Note:</strong> Earned Leave (EL) is also mandatory for eligible employees under the Karnataka Shops &amp; Commercial Establishments Act. Refer to Salary Structure → Statutory Guidelines → 4. Karnataka Earned Leave (EL).
                  </div>
                  <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:14}}>
                    {(cfg.leave_types||[]).length === 0 && <div style={{fontSize:12,color:"#94a3b8",fontStyle:"italic"}}>No leave types defined.</div>}
                    {(cfg.leave_types||[]).map((lt, idx) => (
                      <div key={idx} className="leave-type-card">
                        <div style={{minWidth: 0, flex: 1}}>
                          <div className="leave-type-name" style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} title={lt.name}>
                            {lt.name}
                          </div>
                          <div>
                            <span className="leave-type-quota-badge">
                              {lt.days} {lt.days === 1 ? "day" : "days"} / year
                            </span>
                          </div>
                        </div>
                        <div style={{display: "flex", gap: 6, alignItems: "center", flexShrink: 0}}>
                          <button 
                            type="button" 
                            className="btn btn-ghost btn-xs" 
                            style={{padding: "4px 10px", fontSize: 12, fontWeight: 600, color: "#2563eb", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", display: "inline-flex", alignItems: "center", gap: 4}}
                            title="Edit yearly quota"
                            onClick={() => {
                              setNewLeaveName(lt.name);
                              setNewLeaveDays(lt.days);
                              setAddLeaveError("");
                              setShowAddLeaveModal(true);
                            }}
                          >
                            ✎ Edit
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-ghost btn-xs leave-type-delete-btn" 
                            style={{padding: "4px 8px", fontSize: 13, color:"#ef4444", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2"}} 
                            title={`Delete ${lt.name}`}
                            onClick={() => {
                              if (window.confirm(`Delete ${lt.name}?`)) {
                                const arr = [...cfg.leave_types];
                                arr.splice(idx, 1);
                                setCf("leave_types")(arr);
                              }
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Centered Add / Edit Leave Type Modal */}
                  {showAddLeaveModal && (
                    <div 
                      className="overlay" 
                      style={{zIndex: 999999, background: "rgba(15, 23, 42, 0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16}}
                      onClick={() => setShowAddLeaveModal(false)}
                    >
                      <div 
                        className="modal-content"
                        style={{
                          width: "100%",
                          maxWidth: 440,
                          borderRadius: 16,
                          background: "#ffffff",
                          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
                          overflow: "hidden",
                          border: "1px solid #cbd5e1"
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div style={{background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", padding: "16px 20px", color: "#ffffff", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                          <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            <span style={{fontSize: 20}}>🌴</span>
                            <div>
                              <h4 style={{margin: 0, fontSize: 15, fontWeight: 800, color: "#ffffff"}}>
                                {cfg.leave_types?.some(x => x.name.toLowerCase() === newLeaveName.trim().toLowerCase()) ? "Edit Leave Type" : "Add Leave Type"}
                              </h4>
                              <div style={{fontSize: 11, color: "#94a3b8"}}>Set leave type name and yearly days in one step</div>
                            </div>
                          </div>
                          <button 
                            type="button"
                            onClick={() => setShowAddLeaveModal(false)}
                            style={{background: "rgba(255,255,255,0.1)", border: "none", color: "#94a3b8", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14}}
                          >
                            ✕
                          </button>
                        </div>

                        {/* Body */}
                        <div style={{padding: 20, display: "flex", flexDirection: "column", gap: 14}}>
                          {addLeaveError && (
                            <div style={{padding: "8px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: 6, fontSize: 12, border: "1px solid #fecaca"}}>
                              {addLeaveError}
                            </div>
                          )}

                          {/* Quick Presets */}
                          <div>
                            <div style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6}}>
                              Quick Presets
                            </div>
                            <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                              {[
                                { name: "Casual Leave", days: 12 },
                                { name: "Sick Leave", days: 6 },
                                { name: "Earned Leave", days: 15 },
                                { name: "Comp-Off", days: 6 },
                                { name: "Maternity Leave", days: 182 },
                                { name: "Paternity Leave", days: 5 },
                              ].map((p, pIdx) => (
                                <button
                                  key={pIdx}
                                  type="button"
                                  className="btn btn-ghost btn-xs"
                                  style={{
                                    fontSize: 11,
                                    padding: "3px 8px",
                                    borderRadius: 6,
                                    border: "1px solid #cbd5e1",
                                    background: newLeaveName === p.name ? "#eff6ff" : "#f8fafc",
                                    color: newLeaveName === p.name ? "#1d4ed8" : "#334155",
                                    fontWeight: newLeaveName === p.name ? 700 : 500
                                  }}
                                  onClick={() => {
                                    setNewLeaveName(p.name);
                                    setNewLeaveDays(p.days);
                                    setAddLeaveError("");
                                  }}
                                >
                                  {p.name} ({p.days}d)
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Field 1: Leave Type Name */}
                          <div>
                            <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 5}}>
                              Leave Type Name <span style={{color: "#dc2626"}}>*</span>
                            </label>
                            <input 
                              type="text" 
                              className="config-input" 
                              placeholder="e.g. Casual Leave, Privilege Leave"
                              value={newLeaveName} 
                              onChange={e => { setNewLeaveName(e.target.value); setAddLeaveError(""); }}
                              autoFocus
                              style={{width: "100%", fontSize: 13, padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6}}
                            />
                          </div>

                          {/* Field 2: Yearly Days Quota */}
                          <div>
                            <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 5}}>
                              Yearly Quota (Days / Year) <span style={{color: "#dc2626"}}>*</span>
                            </label>
                            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                              <input 
                                type="number" 
                                min="1" 
                                max="365" 
                                className="config-input" 
                                placeholder="12"
                                value={newLeaveDays} 
                                onChange={e => { setNewLeaveDays(e.target.value); setAddLeaveError(""); }}
                                style={{width: 100, fontSize: 14, fontWeight: 700, padding: "8px 12px", fontFamily: "monospace", border: "1px solid #cbd5e1", borderRadius: 6}}
                              />
                              <span style={{fontSize: 12, color: "#64748b"}}>days per calendar year</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6, paddingTop: 12, borderTop: "1px solid #f1f5f9"}}>
                            <button 
                              type="button" 
                              className="btn btn-ghost btn-sm" 
                              onClick={() => setShowAddLeaveModal(false)}
                            >
                              Cancel
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-primary btn-sm" 
                              style={{background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", padding: "6px 16px", fontWeight: 700}}
                              onClick={() => {
                                if (!newLeaveName || !newLeaveName.trim()) {
                                  setAddLeaveError("Please enter a leave type name.");
                                  return;
                                }
                                const daysNum = parseInt(newLeaveDays);
                                if (isNaN(daysNum) || daysNum <= 0) {
                                  setAddLeaveError("Please enter valid quota days (at least 1 day).");
                                  return;
                                }
                                const lt = [...(cfg.leave_types || [])];
                                const existIdx = lt.findIndex(x => x.name.toLowerCase() === newLeaveName.trim().toLowerCase());
                                if (existIdx >= 0) {
                                  lt[existIdx] = { name: lt[existIdx].name, days: daysNum };
                                } else {
                                  lt.push({ name: newLeaveName.trim(), days: daysNum });
                                }
                                setCf("leave_types")(lt);
                                setShowAddLeaveModal(false);
                                setNewLeaveName("");
                                setNewLeaveDays(12);
                                setAddLeaveError("");
                              }}
                            >
                              ✓ Save Leave Type
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Holidays */}
                <div style={{borderTop: "1px solid #f1f5f9", paddingTop: 16}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8}}>
                    <div style={{fontSize:13, fontWeight:700, color:"#1e293b", display: "flex", alignItems: "center", gap: 6}}>
                      <span>📅</span> Company Holidays List
                      <span className="holiday-count-badge" style={{fontSize:11, fontWeight:700, background:"#f1f5f9", color:"#475569", border:"1px solid #cbd5e1", padding:"2px 8px", borderRadius:12}}>
                        {(cfg.company_holidays || []).length} active
                      </span>
                    </div>
                    <div className="holiday-year-badge" style={{fontSize:12, fontWeight:700, color:"#2563eb", background:"#eff6ff", border:"1px solid #bfdbfe", padding:"2px 10px", borderRadius:12}}>
                      Calendar Year {new Date().getFullYear()}
                    </div>
                  </div>
                  <div style={{fontSize:11, color:"#64748b", marginBottom:16}}>
                    Select mandatory and festival holidays. Days falling on these dates will not be deducted from employee leave balances.
                  </div>
                  
                  <div style={{display:"grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12}}>
                    {[
                      { id: "h1", name: "Republic Day", defaultDate: "-01-26", mandatory: true },
                      { id: "h2", name: "Independence Day", defaultDate: "-08-15", mandatory: true },
                      { id: "h3", name: "Gandhi Jayanti", defaultDate: "-10-02", mandatory: true },
                      { id: "h4", name: "New Year's Day", defaultDate: "-01-01", mandatory: false },
                      { id: "h5", name: "Makar Sankranti", defaultDate: "-01-14", mandatory: false },
                      { id: "h6", name: "Holi", defaultDate: "-03-04", mandatory: false },
                      { id: "h7", name: "Good Friday", defaultDate: "-04-03", mandatory: false },
                      { id: "h8", name: "Labour Day", defaultDate: "-05-01", mandatory: false },
                      { id: "h9", name: "Eid al-Fitr", defaultDate: "-03-20", mandatory: false },
                      { id: "h10", name: "Eid al-Adha", defaultDate: "-05-27", mandatory: false },
                      { id: "h11", name: "Raksha Bandhan", defaultDate: "-08-28", mandatory: false },
                      { id: "h12", name: "Ganesh Chaturthi", defaultDate: "-09-14", mandatory: false },
                      { id: "h13", name: "Dussehra", defaultDate: "-10-19", mandatory: false },
                      { id: "h14", name: "Diwali", defaultDate: "-11-08", mandatory: false },
                      { id: "h15", name: "Christmas", defaultDate: "-12-25", mandatory: false }
                    ].map(h => {
                      const hols = cfg.company_holidays || [];
                      const exists = hols.find(x => x.id === h.id);
                      const isChecked = !!exists;
                      const activeDate = exists?.date || (new Date().getFullYear() + h.defaultDate);
                      
                      // Calculate Day of Week and formatted date info safely
                      let dayOfWeek = "";
                      let monthShort = "";
                      let dayNum = "";
                      if (activeDate && activeDate.includes("-")) {
                        const parts = activeDate.split("-").map(Number);
                        if (parts.length === 3) {
                          const dt = new Date(parts[0], parts[1] - 1, parts[2]);
                          dayOfWeek = dt.toLocaleDateString("en-US", { weekday: "long" });
                          monthShort = dt.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
                          dayNum = String(parts[2]).padStart(2, "0");
                        }
                      }

                      return (
                        <div 
                          key={h.id} 
                          className={`company-holiday-card ${isChecked ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
                            <label style={{display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flex: 1, margin: 0}}>
                              <input 
                                type="checkbox" 
                                style={{
                                  width: 18, 
                                  height: 18, 
                                  cursor: "pointer",
                                  accentColor: "#475569"
                                }} 
                                checked={isChecked} 
                                onChange={e => {
                                  let newHols = [...hols];
                                  if (e.target.checked) {
                                    newHols.push({ id: h.id, name: h.name, date: activeDate });
                                  } else {
                                    newHols = newHols.filter(x => x.id !== h.id);
                                  }
                                  setCf("company_holidays")(newHols);
                                }} 
                              />
                              <div>
                                <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
                                  <span className="holiday-title">
                                    {h.name}
                                  </span>
                                  {h.mandatory && (
                                    <span 
                                      className="holiday-mandatory-badge"
                                      style={{
                                        fontSize: 9, 
                                        fontWeight: 800, 
                                        letterSpacing: 0.5,
                                        background: "linear-gradient(135deg, #fee2e2, #fecaca)", 
                                        color: "#b91c1c", 
                                        border: "1px solid #fca5a5",
                                        padding: "1px 6px", 
                                        borderRadius: 6
                                      }}
                                    >
                                      MANDATORY
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>

                            {/* Tear-off Calendar Icon Badge */}
                            {isChecked && (
                              <div 
                                className="holiday-cal-badge"
                                style={{
                                  width: 44,
                                  height: 46,
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  border: "1px solid #cbd5e1",
                                  background: "#ffffff",
                                  boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  flexShrink: 0
                                }}
                              >
                                <div style={{
                                  background: h.mandatory ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, #64748b, #475569)",
                                  color: "#ffffff",
                                  fontSize: 9,
                                  fontWeight: 800,
                                  letterSpacing: 0.8,
                                  width: "100%",
                                  textAlign: "center",
                                  padding: "2px 0"
                                }}>
                                  {monthShort || "HOL"}
                                </div>
                                <div style={{
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 15,
                                  fontWeight: 800,
                                  color: "#0f172a",
                                  lineHeight: 1
                                }}>
                                  {dayNum || "--"}
                                </div>
                              </div>
                            )}
                          </div>

                          {isChecked && (
                            <div style={{
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "space-between", 
                              gap: 10,
                              background: "#f8fafc", 
                              padding: "8px 12px", 
                              borderRadius: 8, 
                              border: "1px dashed #cbd5e1"
                            }}>
                              <div style={{display: "flex", alignItems: "center", gap: 6}}>
                                <span 
                                  className="holiday-day-pill"
                                  style={{
                                    fontSize: 11, 
                                    fontWeight: 700, 
                                    color: "#1e40af", 
                                    background: "#dbeafe", 
                                    border: "1px solid #bfdbfe", 
                                    padding: "3px 8px", 
                                    borderRadius: 12,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4
                                  }}
                                >
                                  🗓️ {dayOfWeek}
                                </span>
                              </div>
                              <div style={{display: "flex", alignItems: "center", gap: 6}}>
                                <input 
                                  type="date" 
                                  className="config-input holiday-date-input" 
                                  style={{
                                    width: 135, 
                                    padding: "4px 8px", 
                                    fontSize: 12, 
                                    height: 30, 
                                    margin: 0,
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    border: "1px solid #94a3b8",
                                    cursor: "pointer"
                                  }} 
                                  value={exists.date} 
                                  onChange={e => {
                                    let newHols = [...hols];
                                    const idx = newHols.findIndex(x => x.id === h.id);
                                    if (idx > -1) newHols[idx].date = e.target.value;
                                    setCf("company_holidays")(newHols);
                                  }} 
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ── Attendance Settings */}
        {cfg.attendance_enabled && (
          <div className="profile-accordion-card">
            <div
              className="profile-accordion-header purple"
              onClick={() => setShowAttendanceSettings(!showAttendanceSettings)}
              title={showAttendanceSettings ? "Click to collapse Attendance Settings" : "Click to expand Attendance Settings"}
            >
              <div style={{display: "flex", alignItems: "center", gap: 10}}>
                <span style={{fontSize: 16}}>⏱️</span>
                <span style={{fontSize: 13, fontWeight: 800, color: "#6d28d9", letterSpacing: 0.5}}>ATTENDANCE & TIMING SETTINGS</span>
                <span className="profile-accordion-tag" style={{background: "#f3e8ff", color: "#6b21a8"}}>
                  Preferred: {formatPreferredTime(cfg.attendance_punch_in || "09:00")} - {formatPreferredTime(cfg.attendance_punch_out || "18:00")} · {cfg.attendance_grace_minutes !== undefined ? cfg.attendance_grace_minutes : 15}m Grace
                </span>
              </div>
              <button
                className="profile-accordion-btn purple"
                onClick={(e) => { e.stopPropagation(); setShowAttendanceSettings(!showAttendanceSettings); }}
              >
                <span>{showAttendanceSettings ? "Collapse" : "Expand"}</span>
                <span style={{fontSize: 14, lineHeight: 1}}>{showAttendanceSettings ? "−" : "＋"}</span>
              </button>
            </div>
            {showAttendanceSettings && (
              <div style={{padding: 24, display:"flex", flexDirection:"column", gap: 20}}>
                <div style={{fontSize: 12, color: "#64748b"}}>
                  Configure preferred office punch-in and punch-out timings. The system will automatically compare employee check-in times to flag on-time arrivals, late arrivals, and early departures.
                </div>
                <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16}}>
                  <div className="config-field">
                    <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 6}}>
                      Preferred Punch-In Time
                    </label>
                    <input
                      type="time"
                      className="config-input"
                      value={cfg.attendance_punch_in || "09:00"}
                      onChange={e => setCf("attendance_punch_in")(e.target.value)}
                    />
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 4}}>Official work start time (e.g. 09:00 AM)</div>
                  </div>

                  <div className="config-field">
                    <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 6}}>
                      Preferred Punch-Out Time
                    </label>
                    <input
                      type="time"
                      className="config-input"
                      value={cfg.attendance_punch_out || "18:00"}
                      onChange={e => setCf("attendance_punch_out")(e.target.value)}
                    />
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 4}}>Official work end time (e.g. 06:00 PM)</div>
                  </div>

                  <div className="config-field">
                    <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 6}}>
                      Grace Period for Late In (Minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      className="config-input"
                      value={cfg.attendance_grace_minutes !== undefined ? cfg.attendance_grace_minutes : 15}
                      onChange={e => setCf("attendance_grace_minutes")(parseInt(e.target.value) || 0)}
                    />
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 4}}>Arrivals after Preferred Time + Grace will be flagged Late</div>
                  </div>

                  <div className="config-field">
                    <label style={{fontSize: 12, fontWeight: 700, color: "#1e293b", display: "block", marginBottom: 6}}>
                      End-Of-Day Auto Cutoff Time
                    </label>
                    <input
                      type="time"
                      className="config-input"
                      value={cfg.attendance_eod_cutoff || "19:00"}
                      onChange={e => setCf("attendance_eod_cutoff")(e.target.value)}
                    />
                    <div style={{fontSize: 11, color: "#64748b", marginTop: 4}}>Cutoff boundary for daily attendance calculations</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isCompanyRole ? <CompanyEmailPanel company={company}/> : <CompanyConnectivitySetup key={company.id} company={company}/>}

      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:24}}>
        <div>
          {msg && (
            msg.includes("successfully") ? (
              <div className="success-msg" style={{margin: 0}}>✅ {msg}</div>
            ) : (
              <div className="err-msg" style={{margin: 0}}>⚠️ {msg}</div>
            )
          )}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn btn-primary" onClick={handleSave}>💾 Save All Changes</button>
          <button className="btn btn-ghost" onClick={onBack}>Cancel</button>
        </div>
      </div>

      {/* ── Centered Modal for Add Department / Add Designation ── */}
      {deptDesigModal && (
        <div 
          className="overlay" 
          style={{ 
            zIndex: 99999, 
            background: "rgba(15, 23, 42, 0.65)", 
            backdropFilter: "blur(4px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            padding: 16 
          }} 
          onClick={() => setDeptDesigModal(null)}
        >
          <div 
            className="modal" 
            style={{ 
              maxWidth: 440, 
              width: "100%", 
              margin: "0 auto", 
              textAlign: "left", 
              padding: 24, 
              borderRadius: 16, 
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)" 
            }} 
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{deptDesigModal.type === "department" ? "🏢" : "💼"}</span>
                <span>{deptDesigModal.type === "department" ? "Add Department" : "Add Designation"}</span>
              </div>
              <button 
                type="button" 
                className="btn btn-ghost btn-xs" 
                style={{ fontSize: 20, padding: 0, lineHeight: 1, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }} 
                onClick={() => setDeptDesigModal(null)}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.5 }}>
              {deptDesigModal.type === "department"
                ? `Enter new department name to add to ${form.name || company?.name || "this company"}.`
                : `Enter new designation to add to ${form.name || company?.name || "this company"}.`}
            </div>

            <form onSubmit={handleSaveCompanyDeptDesig}>
              <div className="config-field" style={{ marginBottom: 16 }}>
                <div className="config-label" style={{ fontWeight: 800, marginBottom: 6 }}>
                  {deptDesigModal.type === "department" ? "Department Name" : "Designation Title"} <span style={{ color: "#dc2626" }}>*</span>
                </div>
                <input
                  autoFocus
                  className="config-input"
                  value={deptDesigModal.value}
                  onChange={e => setDeptDesigModal({ ...deptDesigModal, value: e.target.value, error: "" })}
                  placeholder={deptDesigModal.type === "department" ? "e.g. Engineering, Sales, Marketing" : "e.g. Senior Software Engineer, Team Lead"}
                  style={{ width: "100%", fontSize: 14 }}
                />
                {deptDesigModal.error && (
                  <div style={{ fontSize: 11, color: "#ef4444", marginTop: 6, fontWeight: 600 }}>
                    ⚠️ {deptDesigModal.error}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                <button 
                  type="button" 
                  className="btn btn-ghost" 
                  onClick={() => setDeptDesigModal(null)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={!deptDesigModal.value?.trim()}
                >
                  {deptDesigModal.type === "department" ? "Add Department" : "Add Designation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Indian Number to Words Helper for Payslip Display
function numToWordsINR(num) {
  const n = Math.round(Number(num) || 0);
  if (n <= 0) return "Zero Rupees Only";
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inWords = (val) => {
    let str = '';
    if (val > 19) {
      str += b[Math.floor(val / 10)] + (val % 10 !== 0 ? ' ' + a[val % 10] : ' ');
    } else {
      str += a[val];
    }
    return str;
  };
  let str = '';
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundred = Math.floor((n % 1000) / 100);
  const rest = n % 100;

  if (crore > 0) str += inWords(crore) + 'Crore ';
  if (lakh > 0) str += inWords(lakh) + 'Lakh ';
  if (thousand > 0) str += inWords(thousand) + 'Thousand ';
  if (hundred > 0) str += inWords(hundred) + 'Hundred ';
  if (rest > 0) {
    if (str !== '') str += 'and ';
    str += inWords(rest);
  }
  return "Rupees " + str.trim() + " Only";
}

// ── Sample Payslip Preview Modal (for testing and verifying Salary Configuration)
export function SamplePayslipModal({ company, cfg, onClose, onStyleSaved, persistenceClient = supabase }) {
  const [payslipStyle, setPayslipStyle] = useState(() => normalizePayslipStyle(cfg?.payslip_style || company?.config?.payslip_style));
  const [savingStyle, setSavingStyle] = useState(false);
  const [styleMessage, setStyleMessage] = useState("");
  const [styleError, setStyleError] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewError, setPdfPreviewError] = useState("");
  const previewCfg = useMemo(() => ({ ...cfg, payslip_style: payslipStyle }), [cfg, payslipStyle]);
  const saveStyle = async () => {
    setSavingStyle(true); setStyleMessage(""); setStyleError(false);
    try {
      const saved = await saveCompanyPayslipStyle(persistenceClient, company?.id, payslipStyle);
      onStyleSaved?.(company.id, saved);
      setStyleMessage("Payslip style saved for this company. Final downloads will use this design.");
    } catch (error) { setStyleError(true); setStyleMessage(error.message); }
    finally { setSavingStyle(false); }
  };
  const companyHeaderDetails = getPayslipCompanyDetails(company);
  const [grossInput, setGrossInput] = useState(50000);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [customBasicPct, setCustomBasicPct] = useState(cfg?.basic_pct !== undefined ? String(cfg.basic_pct) : "50");
  const [customHra, setCustomHra] = useState("");
  const [vpfAmount, setVpfAmount] = useState("");
  const [tdsAmount, setTdsAmount] = useState("");
  const [absentDays, setAbsentDays] = useState("");
  const [otHours, setOtHours] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const grossNum = Math.max(0, parseFloat(grossInput) || 0);

  const calc = useMemo(() => {
    return calcSalaryFull({
      basic: grossNum,
      month: Number(month),
      year: Number(year),
      absent_days: parseFloat(absentDays) || 0,
      ot_hours: parseFloat(otHours) || 0,
      cfg: cfg || {},
      emp_id: "SAMPLE_PREVIEW_001",
      extra: {
        vpf_enabled: (parseFloat(vpfAmount) || 0) > 0,
        vpf_pct: parseFloat(vpfAmount) || 0,
        tds_amount: parseFloat(tdsAmount) || 0,
        custom_basic_pct: (cfg?.custom_basic_enabled && customBasicPct !== "") ? (parseFloat(customBasicPct) || 50) : undefined,
        custom_hra_pct: (cfg?.hra_customised && customHra !== "") ? (parseFloat(customHra) || 0) : undefined,
        emp_dob: "1995-05-15"
      }
    });
  }, [grossNum, month, year, absentDays, otHours, cfg, customBasicPct, customHra, vpfAmount, tdsAmount]);

  const actWd = (cfg?.working_days === -1 || !cfg?.working_days) ? new Date(year, month, 0).getDate() : cfg.working_days;
  const presentDays = Math.max(0, actWd - (parseFloat(absentDays) || 0));
  const lastDay = new Date(year, month, 0).getDate();
  const payDateStr = `${String(lastDay).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;

  const makeSamplePdf = async () => {
    const sampleEmp = {
      name: "Sample Employee (Preview)", employee_code: "EMP-SAMPLE-001", pan: "ABCDE1234F", doj: "2024-01-01",
      uan: cfg?.pf_applicable !== false ? "101234567890" : "",
      esi_id: (cfg?.esi_applicable !== false && calc.esi_emp > 0) ? "31001234560001" : "",
      designation: "Software Engineer", department: "Engineering",
      custom_basic_pct: cfg?.custom_basic_enabled ? customBasicPct : undefined,
      custom_hra_pct: cfg?.hra_customised ? customHra : undefined,
    };
    return generatePayslipPdfDoc(sampleEmp, { ...calc, month: Number(month), year: Number(year), absent_days: absentDays, ot_hours: otHours }, { ...company, config: previewCfg });
  };
  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setPdfPreviewUrl("");
    setPdfPreviewError("");
    const timer = setTimeout(async () => {
      try {
        const pdf = await makeSamplePdf();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(pdf.output("blob"));
        setPdfPreviewUrl(objectUrl); setPdfPreviewError("");
      } catch (error) { if (!cancelled) { setPdfPreviewUrl(""); setPdfPreviewError("PDF preview could not load: " + error.message); } }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [calc, month, year, absentDays, otHours, company, previewCfg, customBasicPct, customHra]);
  const handleDownloadSamplePdf = async () => {
    setDownloading(true);
    try { const pdf = await makeSamplePdf(); pdf.save(`Sample_Payslip_${payslipStyle.template}_${month}_${year}.pdf`); }
    catch (error) { setStyleError(true); setStyleMessage("Sample download failed: " + error.message); }
    finally { setDownloading(false); }
  };

  // Compile Earnings rows
  const configuredBasicPct = cfg?.custom_basic_enabled ? (parseFloat(customBasicPct) || 50) : (cfg?.basic_pct !== undefined ? cfg.basic_pct : 50);
  const rawBasicFromPct = Math.round(grossNum * configuredBasicPct / 100);
  const basicWasElevated = calc.basic > rawBasicFromPct && rawBasicFromPct > 0;
  const earns = [
    { 
      label: "Basic Salary", 
      amount: calc.basic, 
      note: basicWasElevated
        ? `Min-wage floor: ₹${Math.round(calc.basic).toLocaleString("en-IN")} (${configuredBasicPct}% would be ₹${Math.round(rawBasicFromPct).toLocaleString("en-IN")})`
        : `${configuredBasicPct}% of Gross`,
      warn: basicWasElevated
    },
  ];
  if (cfg?.da && calc.da > 0) {
    earns.push({ label: `DA (${cfg.da_pct}% of Basic)`, amount: calc.da, note: "Dearness Allowance" });
  }

  // HRA: show cap warning when min-wage squeezed the HRA budget
  const hraLabel = cfg?.hra_customised
    ? "HRA (Custom ₹)"
    : (cfg?.hra_type === "fixed" ? `HRA (Fixed ₹${Math.round(cfg.hra_value || 0)})` : `HRA (${cfg?.hra_value !== undefined ? cfg.hra_value : (cfg?.hra_pct || 40)}% of Basic)`);
  const hraNote = calc.hra_was_capped
    ? `Capped: budget ₹${Math.round(grossNum - calc.basic).toLocaleString("en-IN")} (formula gives ₹${Math.round(calc.hra_raw||0).toLocaleString("en-IN")})`
    : (cfg?.hra_customised ? "Custom amount" : (cfg?.hra_type === "fixed" ? "Fixed allowance" : "Percentage of Basic"));
  earns.push({ label: hraLabel, amount: calc.hra, note: hraNote, warn: calc.hra_was_capped });

  if (cfg?.lta && calc.lta > 0) {
    earns.push({ label: `LTA (${cfg.lta_pct}% of Basic)`, amount: calc.lta, note: "Leave Travel Allowance" });
  }
  if (calc.special > 0 || cfg?.special_allowance) {
    earns.push({ label: cfg?.custom_special_display_name || "Special / Other Allowance", amount: calc.special, note: "Balances Earnings to Total Gross" });
  }
  if (cfg?.conveyance && calc.conveyance > 0) {
    earns.push({ label: "Conveyance Allowance", amount: calc.conveyance, note: "Fixed conveyance" });
  }
  if (cfg?.medical && calc.medical > 0) {
    earns.push({ label: "Medical Allowance", amount: calc.medical, note: "Fixed medical" });
  }
  // BONUS: always show row — with amount if applicable, with reason if not
  const bonusConfigured = cfg?.bonus !== false;
  const bonusEligibleBySalary = (calc.basic + (calc.da || 0)) <= 21000;
  const bonusMonths = cfg?.bonus ? (cfg.bonus_months && cfg.bonus_months.length > 0 ? cfg.bonus_months : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]) : [];
  const isCurrentMonthBonusMonth = cfg?.bonus_frequency === "monthly" || bonusMonths.includes(Number(month));
  const isBonusInGross = cfg?.bonus_in_gross !== false;
  if (calc.bonus > 0) {
    earns.push({ 
      label: cfg?.bonus_display_name || `Statutory Bonus (${cfg?.bonus_pct || 8.33}%)`, 
      amount: calc.bonus, 
      note: isBonusInGross ? "Eligible — Basic ≤ ₹21,000 (Included in Gross)" : `Eligible — Basic ≤ ₹21,000 (${MONTHS[month]} Payout)`
    });
  } else if (bonusConfigured && bonusEligibleBySalary && !isCurrentMonthBonusMonth) {
    const monthsText = bonusMonths.map(m => MONTHS[m]).join(", ");
    earns.push({ label: cfg?.bonus_display_name || `Statutory Bonus (${cfg?.bonus_pct || 8.33}%)`, amount: 0, note: `Paid in ${monthsText} (Change month above to preview, or configure in Salary Structure)`, dim: true });
  } else if (bonusConfigured && !bonusEligibleBySalary) {
    earns.push({ label: cfg?.bonus_display_name || `Statutory Bonus (${cfg?.bonus_pct || 8.33}%)`, amount: 0, note: `Not eligible — Basic ₹${Math.round(calc.basic).toLocaleString("en-IN")} exceeds ₹21,000 ceiling`, dim: true });
  } else if (!bonusConfigured) {
    earns.push({ label: "Statutory Bonus", amount: 0, note: "Disabled in salary configuration", dim: true });
  }
  if (calc.ot_pay > 0) {
    earns.push({ label: `Overtime Payment (${otHours} hrs)`, amount: calc.ot_pay, note: "Hourly overtime" });
  }

  // Compile Deductions rows
  const deds = [];
  if (cfg?.pf_applicable !== false) {
    deds.push({ 
      label: "PF Employee (12%)", 
      amount: calc.pf_emp, 
      note: calc.pfWage < (calc.basic + (calc.da || 0)) ? `Capped at ₹${Math.round(calc.pfWage).toLocaleString("en-IN")} statutory ceiling` : `12% of Basic (+ DA)` 
    });
  } else {
    deds.push({ label: "PF Employee (12%)", amount: 0, note: "Exempt (PF disabled in config)" });
  }

  if (calc.vpf > 0) {
    deds.push({ label: "VPF (Voluntary PF)", amount: calc.vpf, note: "Voluntary employee contribution" });
  }

  if (cfg?.esi_applicable !== false) {
    if (calc.esi_emp > 0) {
      deds.push({ label: "ESI Employee (0.75%)", amount: calc.esi_emp, note: "Eligible (Wage <= ₹21,000/mo)" });
    } else {
      deds.push({ label: "ESI Employee (0.75%)", amount: 0, note: "Exempt (Wage > ₹21,000/mo limit)" });
    }
  } else {
    deds.push({ label: "ESI Employee (0.75%)", amount: 0, note: "Exempt (ESI disabled in config)" });
  }

  deds.push({ 
    label: "Professional Tax (PT)", 
    amount: calc.pt, 
    note: calc.pt === 0 ? "Exempt (Gross < ₹25,000/mo)" : (Number(month) === 2 ? "Karnataka slab: ₹300 in Feb" : "Standard monthly slab: ₹200") 
  });

  if (calc.tds > 0) {
    deds.push({ label: "TDS (Income Tax)", amount: calc.tds, note: "Tax deducted at source" });
  }

  if (calc.lwf > 0) {
    deds.push({ label: "LWF (Labour Welfare Fund)", amount: calc.lwf, note: "Karnataka rule: ₹50 in Dec" });
  }

  if (calc.absence_deduction > 0) {
    deds.push({ label: `Absence Deduction (${absentDays} days)`, amount: calc.absence_deduction, note: `Deducted from ${cfg?.absence_basis || "wage"}` });
  }

  // Balance row counts so columns align evenly
  const maxRows = Math.max(earns.length, deds.length);
  const balancedEarns = [...earns];
  const balancedDeds = [...deds];
  while (balancedEarns.length < maxRows) balancedEarns.push({ label: "—", amount: null, note: "" });
  while (balancedDeds.length < maxRows) balancedDeds.push({ label: "—", amount: null, note: "" });

  const presets = [
    { label: "₹18,000", val: 18000, desc: "ESI Active (≤21k) • PT Exempt (<25k)" },
    { label: "₹24,000", val: 24000, desc: "ESI Active (≤21k) • PT Exempt (<25k)" },
    { label: "₹50,000", val: 50000, desc: "Standard • PF Capped (25k) • PT Active" },
    { label: "₹1,00,000", val: 100000, desc: "High CTC • PF Capped • PT Active" },
  ];

  return (
    <div className="overlay" style={{zIndex: 99999, background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16}} onClick={onClose}>
      <div 
        className="modal-content"
        style={{
          width: "100%",
          maxWidth: 860,
          maxHeight: "92vh",
          borderRadius: 20,
          background: "#ffffff",
          boxShadow: "0 25px 60px -15px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #cbd5e1"
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", padding: "18px 24px", color: "#ffffff", borderBottom: "1px solid #334155", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <div style={{display: "flex", alignItems: "center", gap: 10}}>
            <span style={{fontSize: 26}}>🧾</span>
            <div>
              <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <h3 style={{margin: 0, fontSize: 18, fontWeight: 800, color: "#ffffff"}}>Sample Payslip Preview</h3>
                <span style={{fontSize: 10, fontWeight: 800, textTransform: "uppercase", background: "rgba(16, 185, 129, 0.2)", color: "#6ee7b7", border: "1px solid rgba(110, 231, 183, 0.3)", padding: "2px 8px", borderRadius: 10}}>
                  Live Simulator
                </span>
              </div>
              <div style={{fontSize: 12, color: "#94a3b8", marginTop: 2}}>
                Previewing payslip breakdown generated from active company salary configuration
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{background: "rgba(255,255,255,0.1)", border: "none", color: "#94a3b8", borderRadius: "50%", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, transition: "all 0.2s"}}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#94a3b8"; }}
          >
            ✕
          </button>
        </div>

        {/* Interactive Simulator Controls Toolbar */}
        <div style={{background: "#f8fafc", padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", flexDirection: "column", gap: 12}}>
          <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12}}>
            {/* Gross Salary Input */}
            <div style={{display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 260}}>
              <label style={{fontSize: 12.5, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap"}}>
                Gross CTC (₹):
              </label>
              <div style={{position: "relative", flex: 1, maxWidth: 220}}>
                <span style={{position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#64748b", fontWeight: 700, fontSize: 14}}>₹</span>
                <input 
                  type="number"
                  min="0"
                  step="500"
                  value={grossInput}
                  onChange={e => setGrossInput(e.target.value)}
                  placeholder="50000"
                  style={{
                    width: "100%",
                    padding: "8px 12px 8px 24px",
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#0f172a",
                    border: "2px solid #cbd5e1",
                    borderRadius: 8,
                    background: "#ffffff",
                    outline: "none"
                  }}
                  onFocus={e => e.target.style.borderColor = "#2563eb"}
                  onBlur={e => e.target.style.borderColor = "#cbd5e1"}
                />
              </div>
            </div>

            {/* Quick Test Presets */}
            <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
              <span style={{fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Presets:</span>
              {presets.map(p => (
                <button
                  key={p.val}
                  type="button"
                  onClick={() => setGrossInput(p.val)}
                  title={p.desc}
                  style={{
                    background: Number(grossInput) === p.val ? "#0f172a" : "#ffffff",
                    color: Number(grossInput) === p.val ? "#ffffff" : "#334155",
                    border: "1px solid #cbd5e1",
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Month & Year Selectors */}
            <div style={{display: "flex", alignItems: "center", gap: 6}}>
              <select 
                value={month} 
                onChange={e => setMonth(Number(e.target.value))}
                style={{padding: "6px 8px", fontSize: 12, fontWeight: 700, borderRadius: 6, border: "1px solid #cbd5e1", background: "#ffffff", color: "#1e293b", cursor: "pointer"}}
                title="Select month to test specific statutory month rules (e.g. Feb PT ₹300, Dec LWF ₹50)"
              >
                {MONTHS.slice(1).map((m, idx) => (
                  <option key={idx + 1} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select 
                value={year} 
                onChange={e => setYear(Number(e.target.value))}
                style={{padding: "6px 8px", fontSize: 12, fontWeight: 700, borderRadius: 6, border: "1px solid #cbd5e1", background: "#ffffff", color: "#1e293b", cursor: "pointer"}}
              >
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button 
                type="button" 
                onClick={() => setShowAdvanced(!showAdvanced)}
                style={{
                  padding: "6px 10px",
                  fontSize: 11.5,
                  fontWeight: 700,
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: showAdvanced ? "#e2e8f0" : "#ffffff",
                  color: "#334155",
                  cursor: "pointer"
                }}
              >
                {showAdvanced ? "▲ Less" : "▼ Overrides"}
              </button>
            </div>
          </div>

          {/* Collapsible Overrides & Custom Parameters */}
          {showAdvanced && (
            <div style={{background: "#ffffff", padding: "12px 16px", borderRadius: 8, border: "1px solid #e2e8f0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 4}}>
              {cfg?.custom_basic_enabled && (
                <div>
                  <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Custom Basic %</div>
                  <input 
                    type="number"
                    min="10"
                    max="100"
                    value={customBasicPct}
                    onChange={e => setCustomBasicPct(e.target.value)}
                    placeholder="50"
                    style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                  />
                </div>
              )}
              {cfg?.hra_customised && (
                <div>
                  <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Custom HRA (₹)</div>
                  <input 
                    type="number"
                    min="0"
                    value={customHra}
                    onChange={e => setCustomHra(e.target.value)}
                    placeholder="0"
                    style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                  />
                </div>
              )}
              <div>
                <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Test VPF (₹)</div>
                <input 
                  type="number"
                  min="0"
                  value={vpfAmount}
                  onChange={e => setVpfAmount(e.target.value)}
                  placeholder="0"
                  style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                />
              </div>
              <div>
                <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Test TDS (₹)</div>
                <input 
                  type="number"
                  min="0"
                  value={tdsAmount}
                  onChange={e => setTdsAmount(e.target.value)}
                  placeholder="0"
                  style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                />
              </div>
              <div>
                <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>Absent Days</div>
                <input 
                  type="number"
                  min="0"
                  max="31"
                  value={absentDays}
                  onChange={e => setAbsentDays(e.target.value)}
                  placeholder="0"
                  style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                />
              </div>
              {cfg?.ot_applicable && (
                <div>
                  <div style={{fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase"}}>OT Hours</div>
                  <input 
                    type="number"
                    min="0"
                    value={otHours}
                    onChange={e => setOtHours(e.target.value)}
                    placeholder="0"
                    style={{width: "100%", padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 3}}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Payslip Paper Body */}
        <div style={{padding: "24px", overflowY: "auto", flex: 1, background: "#f1f5f9"}}>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
            <span style={{fontSize:12,fontWeight:800,color:"#334155"}}>Payslip design</span>
            {PAYSLIP_TEMPLATES.map(template=><button key={template.id} type="button" disabled={savingStyle} aria-pressed={payslipStyle.template === template.id} title={template.description} className="btn btn-ghost btn-sm" style={{background:payslipStyle.template === template.id ? "#e2e8f0" : "#fff",border:"1px solid #cbd5e1"}} onClick={()=>{setPayslipStyle(previous=>({...previous,template:template.id}));setStyleMessage("");}}>{template.label}</button>)}
            <span style={{fontSize:12,color:"#64748b"}}>Colour</span>
            {PAYSLIP_COLOURS.map(colour=><button key={colour.id} type="button" disabled={savingStyle} aria-label={`${colour.label} payslip colour`} aria-pressed={payslipStyle.colour === colour.id} title={colour.label} style={{width:26,height:26,borderRadius:"50%",background:colour.hex,border:payslipStyle.colour === colour.id?"3px solid #fff":"2px solid transparent",outline:payslipStyle.colour === colour.id?`2px solid ${colour.hex}`:"none",cursor:"pointer"}} onClick={()=>{setPayslipStyle(previous=>({...previous,colour:colour.id}));setStyleMessage("");}} />)}
          </div>
          <div style={{fontSize:12,color:"#64748b",marginBottom:12}}>This is the same PDF layout used for final salary slips. Save the style to apply it to this company.</div>
          {pdfPreviewError && <div role="alert" style={{fontSize:12,color:"#b91c1c",marginBottom:12}}>{pdfPreviewError}</div>}
          {pdfPreviewUrl ? <iframe title="Exact salary slip PDF preview" src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&view=FitH`} style={{width:"100%",height:"65vh",minHeight:440,border:"1px solid #cbd5e1",borderRadius:10,background:"#fff"}} /> : !pdfPreviewError && <div role="status" style={{padding:24,color:"#64748b"}}>Preparing PDF preview…</div>}
          <details style={{marginTop:16}}>
            <summary style={{cursor:"pointer",fontSize:13,fontWeight:700,color:"#475569",marginBottom:12}}>Calculation breakdown &amp; notes</summary>
          <div 
            style={{
              background: "#ffffff",
              borderRadius: 12,
              padding: "24px 28px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid #cbd5e1",
              maxWidth: 780,
              margin: "0 auto"
            }}
          >
            {/* Payslip Header */}
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingBottom: 16, borderBottom: "2px solid #0f172a"}}>
              <div style={{display: "flex", alignItems: "center", gap: 14, minWidth:0, flex:1}}>
                {cfg?.logo_base64 ? (
                  <img src={cfg.logo_base64} alt="Company Logo" style={{maxHeight: 52, maxWidth: 130, objectFit: "contain"}} />
                ) : (
                  <div style={{width: 48, height: 48, borderRadius: 10, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: "1px solid #cbd5e1"}}>
                    🏢
                  </div>
                )}
                <div>
                  <div style={{fontSize: 18, fontWeight: 900, color: "#0f172a", lineHeight: 1.2}}>
                    {company?.name || "TrustWork Client Organization"}
                  </div>
                  <div style={{fontSize: 11.5, color: "#64748b", marginTop: 3}}>
                    {companyHeaderDetails.address}
                  </div>
                  {companyHeaderDetails.contact && <div style={{fontSize:10.5,color:"#64748b",marginTop:4,overflowWrap:"anywhere"}}>{companyHeaderDetails.contact}</div>}
                  {companyHeaderDetails.registrations && <div style={{fontSize:10,color:"#64748b",marginTop:4,overflowWrap:"anywhere"}}>{companyHeaderDetails.registrations}</div>}
                </div>
              </div>
              <div style={{textAlign: "right"}}>
                <span style={{fontSize: 11, fontWeight: 800, textTransform: "uppercase", background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", padding: "3px 8px", borderRadius: 6, display: "inline-block", marginBottom: 4}}>
                  Preview Simulation
                </span>
                <div style={{fontSize: 15, fontWeight: 900, color: "#1a3a5c", letterSpacing: 0.5}}>
                  PAYSLIP — {MONTHS[month]?.toUpperCase()} {year}
                </div>
              </div>
            </div>

            {/* Employee Metadata Box */}
            <div style={{background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", padding: "12px 16px", marginTop: 16}}>
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px 16px", fontSize: 12}}>
                <div><span style={{color: "#64748b", fontWeight: 600}}>Employee Name:</span> <strong style={{color: "#0f172a"}}>Sample Employee (Preview)</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>Employee ID:</span> <strong style={{color: "#0f172a"}}>EMP-SAMPLE-001</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>Pay Period:</span> <strong style={{color: "#0f172a"}}>{MONTHS[month]} {year}</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>Pay Date:</span> <strong style={{color: "#0f172a"}}>{payDateStr}</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>Designation:</span> <strong style={{color: "#0f172a"}}>Software Engineer</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>PAN:</span> <strong style={{color: "#0f172a"}}>ABCDE1234F</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>PF UAN:</span> <strong style={{color: "#0f172a"}}>{cfg?.pf_applicable !== false ? "101234567890" : "— (Exempt)"}</strong></div>
                <div><span style={{color: "#64748b", fontWeight: 600}}>ESI IP No:</span> <strong style={{color: "#0f172a"}}>{cfg?.esi_applicable !== false && calc.esi_emp > 0 ? "31001234560001" : "— (Exempt)"}</strong></div>
              </div>
            </div>

            {/* Attendance Bar */}
            <div style={{background: "#f1f5f9", borderRadius: 6, border: "1px solid #e2e8f0", padding: "6px 14px", marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", fontSize: 11.5, color: "#334155", fontWeight: 700}}>
              <div>📅 Total Working Days: <strong>{actWd}</strong></div>
              <div>✅ Present Days: <strong>{presentDays}</strong></div>
              <div>🏖️ Absent Days: <strong>{absentDays}</strong></div>
              {cfg?.ot_applicable && <div>⏱️ OT Hours: <strong>{otHours} hrs</strong></div>}
            </div>

            {/* Two-Column Earnings & Deductions Table */}
            <div style={{marginTop: 14, border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden"}}>
              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", background: "#1a3a5c", color: "#ffffff", padding: "10px 14px", fontWeight: 800, fontSize: 12.5, letterSpacing: 0.5}}>
                <div style={{display: "flex", justifyContent: "space-between", paddingRight: 16, borderRight: "1px solid rgba(255,255,255,0.2)"}}>
                  <span>EARNINGS</span>
                  <span>AMOUNT (₹)</span>
                </div>
                <div style={{display: "flex", justifyContent: "space-between", paddingLeft: 16}}>
                  <span>DEDUCTIONS</span>
                  <span>AMOUNT (₹)</span>
                </div>
              </div>

              {/* Rows */}
              {balancedEarns.map((eItem, idx) => {
                const dItem = balancedDeds[idx];
                const isEven = idx % 2 === 0;
                return (
                  <div 
                    key={idx}
                    style={{
                      display: "grid", 
                      gridTemplateColumns: "1fr 1fr", 
                      background: eItem.warn ? "#fffbeb" : (isEven ? "#f8fafc" : "#ffffff"), 
                      padding: "8px 14px", 
                      fontSize: 12,
                      borderBottom: "1px solid #f1f5f9",
                      borderLeft: eItem.warn ? "3px solid #f59e0b" : "none"
                    }}
                  >
                    {/* Earnings Col */}
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 16, borderRight: "1px solid #e2e8f0"}}>
                      <div>
                        <span style={{color: eItem.dim ? "#94a3b8" : (eItem.amount !== null ? "#0f172a" : "#cbd5e1"), fontWeight: eItem.dim ? 500 : 600}}>
                          {eItem.label}
                        </span>
                        {eItem.warn && <span style={{fontSize: 9.5, fontWeight: 700, color: "#b45309", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 4, padding: "1px 5px", marginLeft: 6}}>MIN WAGE</span>}
                        {eItem.note && (
                          <span style={{fontSize: 10, color: eItem.warn ? "#b45309" : (eItem.dim ? "#94a3b8" : "#64748b"), marginLeft: 6}}>
                            {eItem.note}
                          </span>
                        )}
                      </div>
                      <span style={{fontWeight: 700, color: eItem.dim ? "#94a3b8" : "#0f172a"}}>
                        {eItem.amount !== null ? `₹${Math.round(eItem.amount || 0).toLocaleString("en-IN")}` : "—"}
                      </span>
                    </div>

                    {/* Deductions Col */}
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", paddingLeft: 16}}>
                      <div>
                        <span style={{color: dItem.amount !== null ? "#0f172a" : "#cbd5e1", fontWeight: 600}}>
                          {dItem.label}
                        </span>
                        {dItem.note && (
                          <span style={{fontSize: 10, color: dItem.amount === 0 ? "#10b981" : "#64748b", marginLeft: 6}}>
                            {dItem.note}
                          </span>
                        )}
                      </div>
                      <span style={{fontWeight: 700, color: dItem.amount > 0 ? "#dc2626" : (dItem.amount === 0 ? "#94a3b8" : "#cbd5e1")}}>
                        {dItem.amount !== null ? `₹${Math.round(dItem.amount || 0).toLocaleString("en-IN")}` : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Subtotals Row */}
              <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", background: "#e2e8f0", padding: "10px 14px", fontSize: 12.5, fontWeight: 800}}>
                <div style={{display: "flex", justifyContent: "space-between", paddingRight: 16, borderRight: "1px solid #cbd5e1", color: "#0f172a"}}>
                  <span>TOTAL GROSS EARNINGS:</span>
                  <span style={{color: "#0284c7"}}>₹{Math.round(calc.gross || 0).toLocaleString("en-IN")}</span>
                </div>
                <div style={{display: "flex", justifyContent: "space-between", paddingLeft: 16, color: "#0f172a"}}>
                  <span>TOTAL DEDUCTIONS:</span>
                  <span style={{color: "#dc2626"}}>₹{Math.round(calc.total_deductions || 0).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>

            {/* Net Take-Home Salary Banner */}
            <div 
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                borderRadius: 10,
                color: "#ffffff",
                padding: "16px 20px",
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "0 4px 14px rgba(15,23,42,0.2)"
              }}
            >
              <div>
                <div style={{fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, color: "#93c5fd"}}>
                  Net Salary (Take Home Pay)
                </div>
                <div style={{fontSize: 12, color: "#cbd5e1", marginTop: 4, fontStyle: "italic"}}>
                  {numToWordsINR(calc.net)}
                </div>
              </div>
              <div style={{textAlign: "right"}}>
                <div style={{fontSize: 26, fontWeight: 900, color: "#4ade80", lineHeight: 1}}>
                  ₹{Math.round(calc.net || 0).toLocaleString("en-IN")}
                </div>
                <div style={{fontSize: 11, color: "#94a3b8", marginTop: 4}}>
                  Gross ₹{Math.round(calc.gross || 0).toLocaleString("en-IN")} − Deductions ₹{Math.round(calc.total_deductions || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            {/* Employer Contributions & Cost to Company (CTC) */}
            <div style={{background: "#f8fafc", borderRadius: 10, border: "1px solid #cbd5e1", padding: "14px 18px", marginTop: 14}}>
              <div style={{fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10}}>
                Employer Statutory Contributions &amp; CTC Calculation
              </div>
              <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, fontSize: 12}}>
                <div style={{background: "#ffffff", padding: "8px 12px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                  <div style={{color: "#64748b", fontSize: 11}}>PF Employer (13%)</div>
                  <div style={{fontSize: 14, fontWeight: 800, color: "#0f172a", marginTop: 2}}>
                    ₹{Math.round(calc.pf_er || 0).toLocaleString("en-IN")}
                  </div>
                  <div style={{fontSize: 10, color: "#94a3b8"}}>12% EPF + 1% admin fee</div>
                </div>

                <div style={{background: "#ffffff", padding: "8px 12px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                  <div style={{color: "#64748b", fontSize: 11}}>ESI Employer (3.25%)</div>
                  <div style={{fontSize: 14, fontWeight: 800, color: calc.esi_er > 0 ? "#0f172a" : "#94a3b8", marginTop: 2}}>
                    ₹{Math.round(calc.esi_er || 0).toLocaleString("en-IN")}
                  </div>
                  <div style={{fontSize: 10, color: "#94a3b8"}}>
                    {calc.esi_er > 0 ? "3.25% on eligible wage" : "Exempt (Wage > 21k)"}
                  </div>
                </div>

                <div style={{background: "#ffffff", padding: "8px 12px", borderRadius: 6, border: "1px solid #e2e8f0"}}>
                  <div style={{color: "#64748b", fontSize: 11}}>Gratuity Provision</div>
                  <div style={{fontSize: 14, fontWeight: 800, color: calc.gratuity > 0 ? "#0f172a" : "#94a3b8", marginTop: 2}}>
                    ₹{Math.round(calc.gratuity || 0).toLocaleString("en-IN")}
                  </div>
                  <div style={{fontSize: 10, color: "#94a3b8"}}>
                    {cfg?.gratuity !== false ? "4.81% on Basic" : "Disabled in config"}
                  </div>
                </div>

                <div style={{background: "#eff6ff", padding: "8px 12px", borderRadius: 6, border: "1px solid #bfdbfe"}}>
                  <div style={{color: "#1e40af", fontSize: 11, fontWeight: 700}}>Total Monthly CTC</div>
                  <div style={{fontSize: 16, fontWeight: 900, color: "#1e3a8a", marginTop: 2}}>
                    ₹{Math.round(calc.ctc || 0).toLocaleString("en-IN")}
                  </div>
                  <div style={{fontSize: 10, color: "#3b82f6", fontWeight: 600}}>
                    Annual: ₹{Math.round((calc.ctc || 0) * 12).toLocaleString("en-IN")}
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Disclaimer */}
            <div style={{fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 14, fontStyle: "italic"}}>
              🔒 Computer-generated interactive preview simulator. Values calculate reactively based on configured salary structure formulas.
            </div>
          </div>
          </details>
        </div>

        {/* Modal Footer Actions */}
        <div style={{background: "#ffffff", padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap:10, flexWrap:"wrap"}}>
          {styleMessage && <div role={styleError ? "alert" : "status"} style={{width:"100%",fontSize:12,color:styleError?"#b91c1c":"#166534"}}>{styleMessage}</div>}
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleDownloadSamplePdf} 
            disabled={downloading}
            style={{display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px"}}
          >
            {downloading ? "Generating PDF…" : "⬇ Download Sample PDF"}
          </button>

          <button type="button" className="btn btn-primary" disabled={savingStyle || !company?.id} onClick={saveStyle}>{savingStyle ? "Saving Style…" : "Save Payslip Style"}</button>
          <button 
            type="button" 
            className="btn btn-ghost" 
            onClick={onClose}
            style={{fontWeight: 700, borderRadius: 8}}
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Salary Config Modal (salary structure only, no company info fields)
function SalaryConfigPage({company,onSave,onBack,isCompanyRole,msg,setMsg,onDirtyChange,onStyleSaved}){
  const initial = company;
  const initialCfg = useMemo(() => ({
    hra_type: "pct", hra_value: 40,
    allowance_amount: 0,
    absence_basis: "none",
    absence_deduction: false,
    working_days: -1,
    min_wage_enabled: true,
    min_wage: 15000,
    pf_applicable: true,
    esi_applicable: true,
    bonus_above_21k: true,
    lwf_applicable: true,
    ...(initial?.config || initial || {}),
  }), [initial]);

  const [cfg, setCfg] = useState(initialCfg);
  const [showPreview, setShowPreview] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [openSec, setOpenSec] = useState({1:false, 2:false, 3:false, 4:false});
  const [leftOpen, setLeftOpen] = useState({1:false, 2:false, 3:false});
  const tGuide = () => setGuideOpen(!guideOpen);
  const tSec = (n) => setOpenSec(p=>({...p,[n]:!p[n]}));

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(JSON.stringify(cfg) !== JSON.stringify(initialCfg));
    }
  }, [cfg, initialCfg, onDirtyChange]);

  useEffect(() => {
    setCfg(initialCfg);
  }, [company?.id]);

  const set = k => v => { setCfg(p=>({...p,[k]:v})); setMsg(""); };

  const handleSave = () => {
    onSave({...initial, config: {...cfg, _configured: true}});
  };

  return (
    <div>
      {!isCompanyRole && (
        <div className="back-btn" onClick={onBack}>← Back to Companies</div>
      )}
      <div className="page-header" style={{marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:'#0f172a'}}>⚙️ Salary Configuration</div>
          <div style={{fontSize:13,color:"#64748b",marginTop:4}}>Configure organization-wide wage formulas, statutory compliance rules &amp; test sample payslips.</div>
        </div>
        <button 
          type="button"
          className="btn btn-primary" 
          onClick={() => setShowPreview(true)} 
          style={{display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", fontSize: 13.5}}
        >
          <span>👁️</span>
          <span>Preview Sample Payslip</span>
        </button>
      </div>

      <div className="config-section" style={{background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32}}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

            <SalaryConfigInner cfg={cfg} set={set} leftOpen={leftOpen} setLeftOpen={setLeftOpen} />
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",justifyContent:"flex-start",background:"linear-gradient(to right, #f4eee1 0%, #fcfaf5 4%, #fcfaf5 100%)",borderRadius:12,border:"1px solid #e5e0d8",boxShadow:"inset 3px 0 0 rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.02)",padding:24}}>
          <div style={{fontSize:13,fontWeight:800,color:"#334155",marginBottom:16,textTransform:"uppercase",letterSpacing:0.8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>Statutory Guidelines</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>tSec(1)}>
                  <span>1. Basic Salary Minimums</span>
                  <span style={{fontSize:16,color:"#94a3b8"}}>{openSec[1] ? "−" : "+"}</span>
                </div>
                {openSec[1] && (
                  <div style={{marginTop:12,fontSize:13,color:"#475569",lineHeight:1.6}}>
                    Basic Salary is calculated as 50% of Gross Salary. If 50% of Gross falls below the minimum wage (e.g. ₹15,000), Basic will be elevated to meet the minimum wage.
                  </div>
                )}
              </div>

              <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>tSec(2)}>
                  <span>2. PF & ESI Contributions</span>
                  <span style={{fontSize:16,color:"#94a3b8"}}>{openSec[2] ? "−" : "+"}</span>
                </div>
                {openSec[2] && (
                  <div style={{marginTop:12}}>
                    <div style={{marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12, color: "#334155", lineHeight: 1.5}}>
                      <div style={{fontWeight: 800, marginBottom: 4, color: "#0f172a"}}>📌 Statutory Applicability:</div>
                      <div>• <strong>EPF (Retirement Benefits):</strong> Mandatory for organizations with <strong>20 or more</strong> employees.</div>
                      <div style={{marginTop: 2}}>• <strong>ESI (Medical Benefits):</strong> Mandatory for organizations with <strong>10 or more</strong> employees.</div>
                    </div>
                    <strong style={{color:"#1e293b",fontSize:13}}>PF (Provident Fund):</strong>
                    <ul style={{margin:0,marginTop:6,marginBottom:12,paddingLeft:20,fontSize:13,color:"#475569",lineHeight:1.6}}>
                      <li><strong style={{color:"#334155"}}>Employer Contribution:</strong> 12% + 1% (admin charges by PF department) = <strong style={{color:"#0284c7"}}>Total 13%</strong></li>
                      <li><strong style={{color:"#334155"}}>Employee Contribution:</strong> 12% of Basic + DA</li>
                    </ul>
                    <strong style={{color:"#1e293b",fontSize:13}}>ESI (Employee State Insurance):</strong>
                    <ul style={{margin:0,marginTop:6,paddingLeft:20,fontSize:13,color:"#475569",lineHeight:1.6}}>
                      <li><strong style={{color:"#334155"}}>Employer Contribution:</strong> 3.25%</li>
                      <li><strong style={{color:"#334155"}}>Employee Contribution:</strong> 0.75%</li>
                    </ul>
                    <div style={{marginTop:8,fontSize:12,color:"#b45309",background:"#fef3c7",padding:"6px 10px",borderRadius:6,border:"1px solid #fde68a",display:"inline-block",fontWeight:600}}>
                      ⚠️ ESI is eligible only if Basic + DA is less than or equal to ₹21,000 / Month
                    </div>
                  </div>
                )}
              </div>

              <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>tSec(3)}>
                  <span>3. Additional Rules (Bonus, PT, Gratuity, LWF)</span>
                  <span style={{fontSize:16,color:"#94a3b8"}}>{openSec[3] ? "−" : "+"}</span>
                </div>
                {openSec[3] && (
                  <ul style={{margin:0,marginTop:12,paddingLeft:20,fontSize:13,color:"#475569",lineHeight:1.6}}>
                    <li><strong style={{color:"#334155"}}>Bonus:</strong> Mandatory at <strong style={{color:"#0284c7"}}>8.33% of basic</strong> for employees whose Basic Salary is less than or equal to ₹21,000/Month.</li>
                    <li><strong style={{color:"#334155"}}>Professional Tax (PT):</strong> Not applicable if Gross/Total Salary is less than ₹25,000/Month. When applicable, the standard deduction is ₹200/month (₹300 for the month of February).</li>
                    <li><strong style={{color:"#334155"}}>Gratuity:</strong> Applicable at <strong style={{color:"#0284c7"}}>4.8%</strong> of Basic Salary.</li>
                    <li><strong style={{color:"#334155"}}>LWF (Labour Welfare Fund):</strong> Deducted in December only. <strong style={{color:"#0284c7"}}>₹50</strong> Employee contribution and <strong style={{color:"#0284c7"}}>₹100</strong> Employer contribution.</li>
                    <li><strong style={{color:"#334155"}}>VPF (Voluntary PF):</strong> Employee can voluntarily contribute any amount up to Basic Salary. EPF + VPF combined is tax-free up to <strong style={{color:"#0284c7"}}>₹2,50,000/year (₹20,833/month)</strong>. <span style={{color:"#b45309",fontWeight:600}}>⚠️ If EPF (12% of Basic) + VPF exceeds ₹20,833/month, interest earned on the excess amount will be taxable.</span></li>
                  </ul>
                )}
              </div>

              <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
                <button type="button" aria-expanded={!!openSec[4]} aria-controls="karnataka-el-guidelines" style={{width:"100%",padding:0,background:"transparent",border:0,textAlign:"left",fontFamily:"inherit",fontSize:13,fontWeight:700,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}} onClick={()=>tSec(4)}>
                  <span>4. Karnataka Earned Leave (EL)</span>
                  <span style={{fontSize:16,color:"#94a3b8"}}>{openSec[4] ? "−" : "+"}</span>
                </button>
                {openSec[4] && (
                  <div id="karnataka-el-guidelines" style={{marginTop:12,fontSize:13,color:"#475569",lineHeight:1.6}}>
                    <div style={{fontWeight:700,color:"#334155",marginBottom:8}}>Karnataka Shops &amp; Commercial Establishments Act</div>
                    <ul style={{margin:0,paddingLeft:20}}>
                      <li><strong style={{color:"#334155"}}>Eligibility:</strong> After <strong style={{color:"#0284c7"}}>12 months of service</strong>, subject to applicable statutory conditions.</li>
                      <li><strong style={{color:"#334155"}}>Accrual:</strong> <strong style={{color:"#0284c7"}}>1 day per 20 days actually worked</strong> for employees aged <strong style={{color:"#0284c7"}}>18 or older</strong>. About <strong style={{color:"#0284c7"}}>1.5 days/month or 18 days/year</strong> is an estimate; entitlement depends on days worked.</li>
                      <li><strong style={{color:"#334155"}}>Carry forward:</strong> Up to <strong style={{color:"#0284c7"}}>45 days</strong> (raised from 30 days by the 2021 amendment); confirm the applicable notification with the client’s compliance consultant.</li>
                      <li><strong style={{color:"#334155"}}>Full and final settlement:</strong> Pay the <strong style={{color:"#0284c7"}}>eligible unused EL balance in cash</strong>, subject to the Act’s separation provisions.</li>
                      <li><strong style={{color:"#334155"}}>Common formula to confirm:</strong> <strong style={{color:"#0284c7"}}>(Basic + DA) ÷ 26 × unused EL days</strong>, or <strong style={{color:"#0284c7"}}>÷ 30</strong> where legally applicable under company policy. Confirm the wage basis and divisor before use.</li>
                    </ul>
                    <div style={{marginTop:12,padding:"10px 12px",background:"#fef3c7",border:"1px solid #fde68a",borderRadius:6,color:"#92400e",fontSize:12}}>
                      <strong>Confirm with the client’s compliance consultant:</strong> The 45-day carry-forward limit and the ÷26 versus ÷30 divisor. Company policy must meet the applicable statutory requirements.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{marginTop:24, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
          <button className="btn btn-primary" onClick={handleSave}>💾 Save Config</button>
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={() => setShowPreview(true)}
            style={{display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700}}
          >
            👁️ Preview Sample Payslip
          </button>
          <button className="btn btn-ghost" onClick={onBack}>Cancel</button>
          {msg && (
            msg.includes("successfully") ? (
              <div className="success-msg" style={{margin: 0, padding: "8px 14px", display: "inline-flex", alignItems: "center", fontWeight: 600}}>
                ✅ {msg}
              </div>
            ) : (
              <div className="err-msg" style={{margin: 0, padding: "8px 14px", display: "inline-flex", alignItems: "center", fontWeight: 600}}>
                ⚠️ {msg}
              </div>
            )
          )}
        </div>
        {showPreview && (
          <SamplePayslipModal company={company} cfg={cfg} onStyleSaved={(id,style)=>{setCfg(previous=>({...previous,payslip_style:style}));onStyleSaved?.(id,style);}} onClose={() => setShowPreview(false)} />
        )}
      </div>
  );
}



function SalaryConfigModal({initial,onSave,onClose,msg,setMsg,onStyleSaved}){
  const [cfg, setCfg] = useState({
    hra_type: "pct", hra_value: 40,
    allowance_amount: 0,
    absence_basis: "none",
    absence_deduction: false,
    working_days: -1,
    min_wage_enabled: true,
    min_wage: 15000,
    pf_applicable: true,
    esi_applicable: true,
    bonus_above_21k: true,
    lwf_applicable: true,
    ...(initial?.config || initial || {}),
  });
  const [showPreview, setShowPreview] = useState(false);
  const [openSec, setOpenSec] = useState({ 1: false, 2: false, 3: false });

  const set = k => v => { setCfg(p=>({...p,[k]:v})); setMsg(""); };

  const handleSave = () => {
    onSave({...initial, config: {...cfg, _configured: true}});
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
          <div style={{fontSize:22,fontWeight:900,color:'#0f172a'}}>⚙️ Salary Configuration</div>
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={() => setShowPreview(true)}
            style={{display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700}}
          >
            👁️ Preview Sample Payslip
          </button>
        </div>


        <div className="config-section" style={{maxHeight:"65vh",overflowY:"auto",paddingRight:8}}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <SalaryConfigInner cfg={cfg} set={set} leftOpen={openSec} setLeftOpen={setOpenSec} />
          </div>
        </div>

        <div className="modal-footer" style={{marginTop:24, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          {msg && (
            msg.includes("successfully") ? (
              <div className="success-msg" style={{margin: 0, padding: "8px 14px", display: "inline-flex", alignItems: "center", fontWeight: 600}}>✅ {msg}</div>
            ) : (
              <div className="err-msg" style={{margin: 0, padding: "8px 14px", display: "inline-flex", alignItems: "center", fontWeight: 600}}>⚠️ {msg}</div>
            )
          )}
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={() => setShowPreview(true)}
            style={{display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700}}
          >
            👁️ Preview Sample Payslip
          </button>
          <button className="btn btn-primary" onClick={handleSave}>💾 Save Config</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>

        {showPreview && (
          <SamplePayslipModal company={initial} cfg={cfg} onStyleSaved={(id,style)=>{setCfg(previous=>({...previous,payslip_style:style}));onStyleSaved?.(id,style);}} onClose={() => setShowPreview(false)} />
        )}
      </div>
    </div>
  );
}

function EmployeeFormPage({ initial, company, currentUser, companyEmps, onSave, onBack, msg, setMsg }) {
  const [showMore, setShowMore] = useState(false);
  const [showVoluntary, setShowVoluntary] = useState(false);
  const cfg = company?.config || {};
  
  // Merge cfg departments with existing employee departments to capture any missed ones
  const existingDepts = [...new Set(companyEmps.map(e => e.department).filter(Boolean))];
  const mergedDepts = [...new Set([...(cfg.departments || []), ...existingDepts])];
  const existingDesigs = [...new Set(companyEmps.map(e => e.designation).filter(Boolean))];
  const mergedDesigs = [...new Set([...(cfg.designations || []), ...existingDesigs])];

  const [deptOptions, setDeptOptions] = useState(mergedDepts);
  const [desigOptions, setDesigOptions] = useState(mergedDesigs);
  const [quickAddModal, setQuickAddModal] = useState(null); // { type: 'department' | 'designation', value: '', error: '', saving: false }
  const [form, setForm] = useState(() => {
    let generatedCode = "";
    if (!initial) {
      const prefix = (cfg.emp_id_prefix || "").trim();
      const startNumStr = (cfg.emp_id_start_number || "").toString().trim();
      
      if (startNumStr || prefix) {
        let maxNum = parseInt(startNumStr || "1", 10) - 1;
        if (isNaN(maxNum)) maxNum = 0;
        
        companyEmps.forEach(e => {
          const code = (e.employee_code || "").trim();
          if (prefix === "" || code.startsWith(prefix)) {
            const numPart = code.substring(prefix.length);
            if (/^\d+$/.test(numPart)) {
              const num = parseInt(numPart, 10);
              if (num > maxNum) maxNum = num;
            }
          }
        });
        
        let nextNumStr = (maxNum + 1).toString();
        if (startNumStr.length > nextNumStr.length && startNumStr.startsWith("0")) {
          nextNumStr = nextNumStr.padStart(startNumStr.length, "0");
        }
        
        generatedCode = prefix + nextNumStr;
      }
    }

    return {
      employee_code: generatedCode, name: "", pan: "", doj: "", dob: "", date_of_exit: "", gender: "Male",
      phone: "", email: "", father_name: "", department: "", designation: "",
      basic_salary: "", uan: "", esi_id: "", specially_abled: "No",
      bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "",
      emergency_name: "", emergency_relation: "", emergency_phone: "", custom_basic_pct: "", custom_hra_pct: "", login_pin: "",
      reporting_manager_id: initial?.reporting_manager_id || (company?.config?.reporting_hierarchy?.[initial?.id] || company?.config?.reporting_hierarchy?.[initial?.employee_code] || ""),
      emp_photo: initial?.emp_photo || (company?.config?.employee_photos?.[initial?.id] || company?.config?.employee_photos?.[initial?.employee_code] || (initial?.id ? localStorage.getItem("tw_photo_" + initial.id) : "") || ""),
      ...initial,
      reporting_manager_id: initial?.reporting_manager_id || (company?.config?.reporting_hierarchy?.[initial?.id] || company?.config?.reporting_hierarchy?.[initial?.employee_code] || ""),
      emp_photo: initial?.emp_photo || (company?.config?.employee_photos?.[initial?.id] || company?.config?.employee_photos?.[initial?.employee_code] || (initial?.id ? localStorage.getItem("tw_photo_" + initial.id) : "") || ""),
      basic_salary: initial?.basic_salary || initial?.basic || "",
      custom_basic_pct: initial?.custom_basic_pct ?? "",
      custom_hra_pct: initial?.custom_hra_pct ?? "",
      custom_special_allowance: initial?.custom_special_allowance ?? "",
      esi_forced_until: initial?.esi_forced_until || "",
      login_pin: "",
    };
  });


  const set = k => v => {
    if (typeof v === 'string') {
      if (k === 'name' || k === 'father_name' || k === 'emergency_name') v = v.slice(0, 100);
      if (k === 'phone' || k === 'emergency_phone') v = getPhone10(v);
      else if (k === 'bank_ifsc' || k === 'pan') v = v.toUpperCase().trim();
    }
    
    let updates = { [k]: v };
    setForm(p=>({...p, ...updates})); 
    setMsg(""); 
  };

  const handleSave = () => {
    if(!form.employee_code?.trim()){ setMsg("Employee ID / Code is required."); return; }
    if(!form.name?.trim()){ setMsg("Employee Name is required."); return; }
    if(form.name.trim().length > 100){ setMsg("Employee name cannot exceed 100 characters."); return; }
    if(form.father_name && form.father_name.trim().length > 100){ setMsg("Father's name cannot exceed 100 characters."); return; }
    if(form.emergency_name && form.emergency_name.trim().length > 100){ setMsg("Emergency contact name cannot exceed 100 characters."); return; }
    if(!form.dob){ setMsg("Date of Birth is required."); return; }
    const dobDate = parseDateFlexible(form.dob);
    if(!dobDate || isNaN(dobDate.getTime())){ setMsg("Invalid Date of Birth."); return; }
    const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
    if(dobDate > todayEnd){ setMsg("Date of Birth cannot be in the future."); return; }
    if(!form.doj){ setMsg("Date of Joining is required."); return; }
    const dojCheck = validateDOJ(form.doj);
    if (!dojCheck.ok) { setMsg(dojCheck.msg); return; }
    if(!form.pan?.trim()){ setMsg("PAN Number is required."); return; }
    if(!validatePAN(form.pan)){ setMsg("Invalid PAN format (Must be 10 characters: 5 letters, 4 digits, 1 letter, e.g. ABCDE1234F)."); return; }
    if(!form.basic_salary){ setMsg("Gross Salary is required."); return; }
    
    if(form.dob && form.doj) {
      const dojDate = parseDateFlexible(form.doj);
      if (dojDate) {
        let joinAge = dojDate.getFullYear() - dobDate.getFullYear();
        const jm = dojDate.getMonth() - dobDate.getMonth();
        if (jm < 0 || (jm === 0 && dojDate.getDate() < dobDate.getDate())) joinAge--;
        if (joinAge < 18) { setMsg('Employee must be at least 18 years old on the Date of Joining.'); return; }
      }
    }
    if(form.date_of_exit && form.doj) {
      const exitD = parseDateFlexible(form.date_of_exit);
      const dojD = parseDateFlexible(form.doj);
      if (exitD && dojD && exitD < dojD) {
        setMsg("Date of Exit cannot be before Date of Joining.");
        return;
      }
    }
    if(form.phone && !/^\d{10}$/.test(form.phone.trim())){ setMsg("Phone number must be exactly 10 digits."); return; }
    if(form.email && !isValidEmail(form.email)){ setMsg("Invalid email format (e.g. employee@example.com)."); return; }
    if(form.bank_ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.bank_ifsc.trim())){ setMsg("Invalid IFSC Code format."); return; }
    if(form.bank_account && !isValidBankAccount(form.bank_account)){ setMsg("Bank Account Number must be between 9 and 18 digits."); return; }
    if(form.uan && !isValidUAN(form.uan)){ setMsg("Universal Account Number (UAN) must be exactly 12 numeric digits (or leave blank if unknown)."); return; }
    onSave({
      ...form,
      name: form.name.trim().slice(0, 100),
      father_name: form.father_name ? form.father_name.trim().slice(0, 100) : "",
      emergency_name: form.emergency_name ? form.emergency_name.trim().slice(0, 100) : "",
      employee_code: form.employee_code.trim(),
    });
  };

  const panVal = (form.pan || "").trim().toUpperCase();
  const isPanValid = validatePAN(panVal);
  const isPanPatternMatch = !panVal || /^[A-Z]{0,5}$/.test(panVal) || /^[A-Z]{5}[0-9]{0,4}$/.test(panVal) || /^[A-Z]{5}[0-9]{4}[A-Z]{0,1}$/.test(panVal);
  const panHasError = panVal.length > 0 && (!isPanPatternMatch || (panVal.length === 10 && !isPanValid));

  const todayDate = new Date();
  const todayStr = todayDate.toISOString().split("T")[0];

  let dobHelper = "";
  let dobHasError = false;
  if (form.dob) {
    const dobD = parseDateFlexible(form.dob);
    if (!dobD || isNaN(dobD.getTime())) {
      dobHelper = "⚠️ Invalid Date of Birth";
      dobHasError = true;
    } else if (dobD > todayDate) {
      dobHelper = "⚠️ Date of Birth cannot be in the future";
      dobHasError = true;
    } else {
      let currentAge = todayDate.getFullYear() - dobD.getFullYear();
      const m = todayDate.getMonth() - dobD.getMonth();
      if (m < 0 || (m === 0 && todayDate.getDate() < dobD.getDate())) currentAge--;
      dobHelper = `Current Age: ${currentAge} years`;
    }
  }

  let dojHelper = "";
  let dojHasError = false;
  if (form.doj) {
    const dojCheck = validateDOJ(form.doj);
    if (!dojCheck.ok) {
      dojHelper = `⚠️ ${dojCheck.msg}`;
      dojHasError = true;
    } else if (form.dob) {
      const dobD = parseDateFlexible(form.dob);
      const dojD = parseDateFlexible(form.doj);
      if (dobD && dojD) {
        let joinAge = dojD.getFullYear() - dobD.getFullYear();
        const m = dojD.getMonth() - dobD.getMonth();
        if (m < 0 || (m === 0 && dojD.getDate() < dobD.getDate())) joinAge--;
        if (joinAge < 18) {
          dojHelper = `⚠️ Age on joining is ${joinAge} yrs (must be at least 18)`;
          dojHasError = true;
        } else {
          dojHelper = `✓ Age on joining: ${joinAge} yrs`;
        }
      }
    }
  }

  const handleSaveQuickAdd = async (e) => {
    if (e) e.preventDefault();
    if (!quickAddModal) return;
    const val = (quickAddModal.value || "").trim();
    const type = quickAddModal.type;

    if (!val) {
      setQuickAddModal(prev => ({
        ...prev,
        error: `Please enter a valid ${type === "department" ? "department name" : "designation title"}.`
      }));
      return;
    }

    setQuickAddModal(prev => ({ ...prev, saving: true, error: "" }));
    try {
      if (type === "department") {
        const exists = deptOptions.some(d => d.toLowerCase() === val.toLowerCase());
        let newOpts = deptOptions;
        if (!exists) {
          newOpts = [...deptOptions, val];
          setDeptOptions(newOpts);
          if (company?.id) {
            const newCfg = { ...(company.config || {}), departments: newOpts };
            company.config = newCfg;
            await supabase.from("companies").update({ config: newCfg }).eq("id", company.id);
          }
        }
        set("department")(val);
      } else {
        const exists = desigOptions.some(d => d.toLowerCase() === val.toLowerCase());
        let newOpts = desigOptions;
        if (!exists) {
          newOpts = [...desigOptions, val];
          setDesigOptions(newOpts);
          if (company?.id) {
            const newCfg = { ...(company.config || {}), designations: newOpts };
            company.config = newCfg;
            await supabase.from("companies").update({ config: newCfg }).eq("id", company.id);
          }
        }
        set("designation")(val);
      }
      setQuickAddModal(null);
    } catch (err) {
      console.error("Failed to add option:", err);
      setQuickAddModal(prev => ({
        ...prev,
        saving: false,
        error: err.message || "Failed to save. Please try again."
      }));
    }
  };

  return (
    <div>
      <div className="back-btn" onClick={onBack}>← Back to Employees</div>
      <div className="page-header" style={{marginBottom: 20}}>
        <div>
          <div className="page-title">{initial?"✏️ Edit Employee":"👤 Add Employee"}</div>
          <div className="page-sub">Enter employee details and statutory configurations.</div>
        </div>
      </div>
      {msg&&<div className="err-msg">⚠️ {msg}</div>}

      <div className="config-section" style={{background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:24, maxWidth:800}}>
        <EmployeePhotoUploader
          value={form.emp_photo || ""}
          onChange={photo => set("emp_photo")(photo)}
          label="Employee Photograph"
          empCode={form.employee_code || form.emp_code || form.id}
          empName={form.name}
        />

        <div style={{marginBottom:8,fontSize:11,fontWeight:800,color:"#2563eb",textTransform:"uppercase",letterSpacing:0.8,display:"flex",alignItems:"center",gap:6}}>
          👤 Mandatory Details
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:10}}>
            <div className="config-field">
              <div className="config-label" style={{fontWeight:800}}>Employee ID <span style={{color:"#dc2626"}}>*</span></div>
              <input className="config-input" value={form.employee_code||""} onChange={e=>set("employee_code")(e.target.value)} placeholder="EMP-001" style={{fontFamily:"monospace"}} autoFocus />
            </div>
            <div className="config-field">
              <div className="config-label" style={{fontWeight:800}}>Employee Name <span style={{color:"#dc2626"}}>*</span></div>
              <input className="config-input" maxLength={100} value={form.name||""} onChange={e=>set("name")(e.target.value.slice(0, 100))} placeholder="e.g. Rahul Kumar" />
            </div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div className="config-field">
            <div className="config-label" style={{fontWeight:800}}>Date of Birth <span style={{color:"#dc2626"}}>*</span></div>
            <DatePickerDDMMYYYY 
              className="config-input" 
              value={form.dob||""} 
              max={todayStr}
              onChange={val=>set("dob")(val)} 
              style={{width: "100%", borderColor: dobHasError ? "#ef4444" : undefined}}
            />
            {dobHelper && (
              <div style={{fontSize: 10, marginTop: 3, fontWeight: 600, color: dobHasError ? "#ef4444" : "#64748b"}}>
                {dobHelper}
              </div>
            )}
          </div>
          <div className="config-field">
            <div className="config-label" style={{fontWeight:800}}>Date of Joining <span style={{color:"#dc2626"}}>*</span></div>
            <DatePickerDDMMYYYY 
              className="config-input" 
              value={form.doj||""} 
              max={todayStr}
              onChange={val=>set("doj")(val)} 
              style={{width: "100%", borderColor: dojHasError ? "#ef4444" : undefined}}
            />
            {dojHelper && (
              <div style={{fontSize: 10, marginTop: 3, fontWeight: 600, color: dojHasError ? "#ef4444" : "#10b981"}}>
                {dojHelper}
              </div>
            )}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div className="config-field">
            <div className="config-label" style={{fontWeight:800}}>PAN Number <span style={{color:"#dc2626"}}>*</span></div>
            <div style={{position:"relative"}}>
              <input 
                className="config-input" 
                value={form.pan||""} 
                onChange={e=>set("pan")(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))} 
                placeholder="ABCDE1234F" 
                style={{
                  fontFamily:"monospace", 
                  letterSpacing: 1.5,
                  paddingRight: panVal ? 32 : 12,
                  borderColor: panHasError ? "#ef4444" : (isPanValid ? "#10b981" : undefined)
                }} 
                maxLength={10} 
              />
              {panVal && (
                <span style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 13,
                  pointerEvents: "none"
                }}>
                  {isPanValid ? "✅" : "⚠️"}
                </span>
              )}
            </div>
            <div style={{
              fontSize: 10,
              marginTop: 3,
              fontWeight: 600,
              color: panHasError ? "#ef4444" : (isPanValid ? "#10b981" : (!panVal ? "#94a3b8" : "#64748b"))
            }}>
              {!panVal
                ? "Format: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F)"
                : (isPanValid
                    ? "✓ Valid PAN format"
                    : (!isPanPatternMatch
                        ? "⚠️ First 5 chars must be letters (A-Z), next 4 digits, last 1 letter"
                        : (panVal.length === 10
                            ? "⚠️ Invalid PAN format (e.g. ABCDE1234F)"
                            : `${panVal.length}/10 characters`)))}
            </div>
          </div>
          <div className="config-field">
            <div className="config-label" style={{fontWeight:800}}>Employee login</div>
            {initial?.id ? <><EmployeeWelcomeEmail company={company} employee={initial}/><EmployeeTemporaryPassword key={`${initial.id}-${initial.email}`} company={company} employee={initial}/></> : <p className="people-muted">Save an individual employee email, then send an invitation or set a temporary password.</p>}

          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div className="config-field">
            <div className="config-label" style={{fontWeight:800}}>Gross Salary (₹/month) <span style={{color:"#dc2626"}}>*</span></div>
            <input className="config-input" type="number" value={form.basic_salary||""} onChange={e=>set("basic_salary")(e.target.value)} placeholder="25000" style={{fontFamily:"monospace"}} />
            {parseFloat(form.basic_salary) * (cfg.da ? 1 / (1 + (cfg.da_pct||0)/100) : 1) * 0.5 + (cfg.da ? parseFloat(form.basic_salary)*0.5*(cfg.da_pct||0)/100 : 0) > 21000 && (
              <div style={{fontSize:11,color:'#b45309',marginTop:4,fontWeight:600}}>⚠️ Basic+DA &gt; ₹21,000. ESI stops unless forced below.</div>
            )}
          </div>
          {cfg.esi_applicable && (
            <div className="config-field">
              <div className="config-label" style={{fontWeight:800}}>Force ESI Until (Month/Year)</div>
              <select className="config-input" value={form.esi_forced_until||""} onChange={e=>set("esi_forced_until")(e.target.value)}>
                <option value="">-- No Force ESI --</option>
                {(() => {
                  const now = new Date();
                  const y = now.getFullYear();
                  const m = now.getMonth() + 1; // 1-12
                  // Generating next few March and September cycles
                  const opts = [];
                  if(m <= 3) { opts.push(`03-${y}`, `09-${y}`, `03-${y+1}`); }
                  else if(m <= 9) { opts.push(`09-${y}`, `03-${y+1}`, `09-${y+1}`); }
                  else { opts.push(`03-${y+1}`, `09-${y+1}`, `03-${y+2}`); }
                  return opts.map(val => {
                    const [mm, yy] = val.split('-');
                    const label = mm === '03' ? `March ${yy}` : `September ${yy}`;
                    return <option key={`${yy}-${mm}`} value={`${yy}-${mm}`}>{label}</option>;
                  });
                })()}
              </select>
              <div style={{fontSize:11,color:'#64748b',marginTop:4}}>Forces ESI deduction if Basic+DA &gt; 21k (for active cycle).</div>
            </div>
          )}
        </div>
        {(cfg.hra_customised || cfg.custom_special_allowance_enabled) && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
            {cfg.hra_customised && (
              <div className="config-field">
                <div className="config-label" style={{fontWeight:800}}>
                  Custom HRA (₹) 
                </div>
                <input className="config-input" type="number" value={form.custom_hra_pct||""} onChange={e=>set("custom_hra_pct")(e.target.value)} placeholder="Enter fixed amount (e.g. 0 or 5000)" />
              </div>
            )}
            {cfg.custom_special_allowance_enabled && (
              <div className="config-field">
                <div className="config-label" style={{fontWeight:800}}>
                  {cfg.custom_special_display_name || "Custom Special Allow."} (₹)
                </div>
                <input className="config-input" type="number" value={form.custom_special_allowance||""} onChange={e=>set("custom_special_allowance")(e.target.value)} placeholder="Enter fixed amount" />
              </div>
            )}
          </div>
        )}

        {cfg.vpf_tds_enabled && (
          <div style={{marginTop:16}}>
            <button className="btn btn-ghost btn-sm" onClick={()=>setShowVoluntary(!showVoluntary)} style={{fontWeight:700}}>
              {showVoluntary ? "➖ Voluntary Deductions (Optional)" : "➕ Voluntary Deductions (Optional)"}
            </button>
            {showVoluntary && (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
                <div className="config-field">
                  <div className="config-label">VPF Amount (₹/month)</div>
                  <input className="config-input" type="number" min="0"
                    value={form.vpf_pct||""}
                    onChange={e => {
                      const val = parseFloat(e.target.value)||0;
                      const basic = (parseFloat(form.basic_salary)||0) * 0.5;
                      if(val > basic) { setMsg(`VPF cannot exceed Basic Salary (₹${Math.floor(basic)}/month)`); return; }
                      setMsg("");
                      set("vpf_pct")(val);
                    }}
                    placeholder="e.g. 2000" style={{fontFamily:"monospace"}} />
                  {(() => {
                    const gross = parseFloat(form.basic_salary)||0;
                    const basic = gross * 0.5;
                    const epf = basic * 0.12;
                    const vpf = parseFloat(form.vpf_pct)||0;
                    const totalPF = epf + vpf;
                    return (
                      <div style={{fontSize:10,color:'#64748b',marginTop:4}}>
                        Max: <strong>₹{Math.floor(basic).toLocaleString('en-IN')}</strong> (Basic)
                        {totalPF > 20833 && <span style={{display:'block',color:'#b45309',fontWeight:600,marginTop:2}}>⚠️ EPF+VPF &gt; ₹20,833/mo (interest taxable).</span>}
                      </div>
                    );
                  })()}
                </div>
                <div className="config-field">
                  <div className="config-label">TDS Amount (₹/month)</div>
                  <input className="config-input" type="number" min="0"
                    value={form.tds_amount||""}
                    onChange={e => {
                      const val = parseFloat(e.target.value)||0;
                      const gross = parseFloat(form.basic_salary)||0;
                      if(gross && val >= gross) { setMsg("TDS cannot be equal to or more than Gross Salary."); return; }
                      setMsg("");
                      set("tds_amount")(val);
                    }}
                    placeholder="e.g. 1500" style={{fontFamily:"monospace"}} />
                  <div style={{fontSize:10,color:'#64748b',marginTop:4}}>Fixed monthly deduction.</div>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{marginTop:16}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowMore(!showMore)} style={{fontWeight:700}}>
            {showMore ? "➖ Additional Details (Optional)" : "➕ Additional Details (Optional)"}
          </button>
        </div>

        {showMore && (
          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #cbd5e1"}}>
            <div style={{fontSize:11,fontWeight:800,color:"#64748b",textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>
              Additional Info (Optional)
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="config-field">
                <div className="config-label">Phone</div>
                <div style={{display:"flex",gap:6}}>
                  <div style={{background:"#f1f5f9",border:"1.5px solid #e2e8f0",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#64748b",fontWeight:600,display:"flex",alignItems:"center"}}>+91</div>
                  <input className="config-input" value={getPhone10(form.phone)} onChange={e=>set("phone")(getPhone10(e.target.value))} placeholder="9876543210" style={{fontFamily:"monospace"}} maxLength={16} />
                </div>
              </div>
              <div className="config-field">
                <div className="config-label">Gender</div>
                <select className="config-input" value={form.gender||"Male"} onChange={e=>set("gender")(e.target.value)}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="config-field">
                <div className="config-label">Specially Abled</div>
                <select className="config-input" value={form.specially_abled||"No"} onChange={e=>set("specially_abled")(e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div className="config-field">
                <div className="config-label">Email</div>
                <div style={{position:"relative"}}>
                  <input className="config-input" type="email" value={form.email||""} 
                    onChange={e=>set("email")(e.target.value.replace(/\s/g, ''))} 
                    placeholder="employee@example.com" 
                    style={{
                      paddingRight: (form.email||"").trim() ? 32 : 12,
                      borderColor: (form.email||"").trim() ? (isValidEmail(form.email) ? "#10b981" : "#ef4444") : undefined
                    }}
                  />
                  {(form.email||"").trim() && (
                    <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:13,pointerEvents:"none"}}>
                      {isValidEmail(form.email) ? "✅" : "⚠️"}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize:10,marginTop:3,fontWeight:600,
                  color: !(form.email||"").trim() ? "#94a3b8" : (isValidEmail(form.email) ? "#10b981" : "#ef4444")
                }}>
                  {!(form.email||"").trim() ? "Optional (e.g. employee@example.com)" : (isValidEmail(form.email) ? "✓ Valid email format" : "⚠️ Invalid email format (e.g. employee@example.com)")}
                </div>
              </div>
              <div className="config-field">
                <div className="config-label">Father's Name</div>
                <input className="config-input" maxLength={100} value={form.father_name||""} onChange={e=>set("father_name")(e.target.value.slice(0, 100))} placeholder="Father's full name" />
              </div>
              <div className="config-field">
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2}}>
                  <div className="config-label" style={{marginBottom: 0}}>Department</div>
                  <button 
                    type="button" 
                    className="btn btn-ghost btn-xs" 
                    style={{fontSize: 11, padding: "2px 6px", color: "#2563eb", fontWeight: 700}}
                    onClick={() => setQuickAddModal({ type: "department", value: "", error: "", saving: false })}
                  >
                    + Add New
                  </button>
                </div>
                <select 
                  className="config-input" 
                  value={form.department||""} 
                  onChange={e => {
                    if(e.target.value === "__ADD_NEW__") {
                      setQuickAddModal({ type: "department", value: "", error: "", saving: false });
                    } else {
                      set("department")(e.target.value);
                    }
                  }}
                >
                  <option value="">Select Department...</option>
                  {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  {form.department && !deptOptions.includes(form.department) && <option value={form.department}>{form.department}</option>}
                  <option value="__ADD_NEW__">+ Add New...</option>
                </select>
              </div>
              <div className="config-field">
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2}}>
                  <div className="config-label" style={{marginBottom: 0}}>Designation</div>
                  <button 
                    type="button" 
                    className="btn btn-ghost btn-xs" 
                    style={{fontSize: 11, padding: "2px 6px", color: "#2563eb", fontWeight: 700}}
                    onClick={() => setQuickAddModal({ type: "designation", value: "", error: "", saving: false })}
                  >
                    + Add New
                  </button>
                </div>
                <select 
                  className="config-input" 
                  value={form.designation||""} 
                  onChange={e => {
                    if(e.target.value === "__ADD_NEW__") {
                      setQuickAddModal({ type: "designation", value: "", error: "", saving: false });
                    } else {
                      set("designation")(e.target.value);
                    }
                  }}
                >
                  <option value="">Select Designation...</option>
                  {desigOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  {form.designation && !desigOptions.includes(form.designation) && <option value={form.designation}>{form.designation}</option>}
                  <option value="__ADD_NEW__">+ Add New...</option>
                </select>
              </div>
              <div className="config-field">
                <div className="config-label" style={{display: "flex", alignItems: "center", gap: 5}}>
                  <span>👔</span> <span>Reporting Manager</span>
                </div>
                <select className="config-input" value={form.reporting_manager_id||""} onChange={e=>set("reporting_manager_id")(e.target.value)}>
                  <option value="">No direct manager</option>
                  {(companyEmps || [])
                    .filter(e => (!initial?.id || e.id !== initial.id) && e.employment_status !== "terminated" && e.is_active !== false)
                    .map(e => (
                      <option key={e.id} value={e.id}>
                        👔 {e.name} {e.employee_code ? `(${e.employee_code})` : ""} {e.designation ? `— ${e.designation}` : ""} {e.department ? `[${e.department}]` : ""}
                      </option>
                    ))
                  }
                </select>
                <div style={{fontSize: 10, color: "#64748b", marginTop: 4}}>
                  Defines organizational hierarchy, approvals, and team tree.
                </div>
              </div>
              <div className="config-field" style={{background: "#fff1f2", padding: 8, borderRadius: 8, border: "1px solid #fecdd3"}}>
                <div className="config-label" style={{color: "#be123c", fontWeight: 800}}>Last Working Day (LWD) / Exit Date</div>
                <DatePickerDDMMYYYY
                  className="config-input" 
                  value={form.date_of_exit||""} 
                  onChange={val => {
                    set("date_of_exit")(val);
                    set("employment_status")(val ? "terminated" : "confirmed");
                  }} 
                  style={{width: "100%", borderColor: "#fecdd3"}} 
                />
                {form.date_of_exit && <div style={{fontSize: 10, color: "#be123c", marginTop: 4, fontWeight: 600}}>Setting LWD automatically prorates salary in payroll and marks as exited.</div>}
              </div>
            </div>

            <div style={{border:"1.5px solid #e2e8f0",borderRadius:10,padding:14,marginTop:16,background:"#f8fafc"}}>
              <div style={{fontSize:10,fontWeight:800,color:"#475569",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Statutory Details (PF / ESI)</div>
              
              <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'#166534',lineHeight:1.45}}>
                <div style={{fontWeight:700,marginBottom:3,display:'flex',alignItems:'center',gap:6}}>
                  <span>💡</span> Don’t know UAN? Leave blank — we will fetch it from the employee's mobile number.
                </div>
                <div style={{fontSize:11.5,color:'#15803d'}}>
                  • <strong>Mobile Number:</strong> Registered mobile number should not be changed (linked with EPFO profile).<br/>
                  • <strong>Lifelong UAN:</strong> UAN remains identical even if employee changes companies (cannot have multiple UANs).
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div className="config-field">
                  <div className="config-label">UAN (PF Account)</div>
                  <input
                    className="config-input"
                    value={form.uan||""}
                    onChange={e=>set("uan")(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="100234567890"
                    maxLength={12}
                    style={{fontFamily:"monospace"}}
                  />
                  {form.uan && (
                    <div style={{fontSize: 11, marginTop: 3, fontWeight: 700, color: isValidUAN(form.uan) ? '#166534' : '#b45309'}}>
                      {isValidUAN(form.uan) ? `✅ 12 digits (Valid UAN)` : `⚠️ ${form.uan.length}/12 digits (Must be 12 digits if provided)`}
                    </div>
                  )}
                </div>
                <div className="config-field">
                  <div className="config-label">ESI ID</div>
                  <input className="config-input" value={form.esi_id||""} onChange={e=>set("esi_id")(e.target.value.replace(/\D/g, '').slice(0, 17))} placeholder="3100123456" maxLength={17} style={{fontFamily:"monospace"}} />
                </div>
              </div>
            </div>

            <div style={{border:"1.5px solid #e2e8f0",borderRadius:10,padding:14,marginTop:16,background:"#f8fafc"}}>
              <div style={{fontSize:10,fontWeight:800,color:"#475569",textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Bank Details</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div className="config-field">
                  <div className="config-label">Bank Name</div>
                  <input className="config-input" value={form.bank_name||""} onChange={e=>set("bank_name")(e.target.value)} placeholder="State Bank of India" />
                </div>
                <div className="config-field">
                  <div className="config-label">Account Number</div>
                  <input
                    className="config-input"
                    value={form.bank_account||""}
                    onChange={e=>set("bank_account")(e.target.value.replace(/\D/g, '').slice(0, 18))}
                    placeholder="123456789012"
                    maxLength={18}
                    style={{fontFamily:"monospace"}}
                  />
                  {form.bank_account && (
                    <div style={{fontSize: 11, marginTop: 3, fontWeight: 700, color: isValidBankAccount(form.bank_account) ? '#166534' : '#b45309'}}>
                      {isValidBankAccount(form.bank_account) ? `✅ ${form.bank_account.length} digits (Valid)` : `⚠️ ${form.bank_account.length}/18 digits (9–18 digits required)`}
                    </div>
                  )}
                </div>
                <div className="config-field">
                  <div className="config-label">IFSC Code</div>
                  <input className="config-input" value={form.bank_ifsc||""} onChange={e=>set("bank_ifsc")(e.target.value.toUpperCase())} placeholder="SBIN0001234" style={{fontFamily:"monospace"}} maxLength={11} />
                </div>
                <div className="config-field">
                  <div className="config-label">Branch</div>
                  <input className="config-input" value={form.bank_branch||""} onChange={e=>set("bank_branch")(e.target.value)} placeholder="Main Branch" />
                </div>
              </div>
            </div>

            <div style={{border:"1.5px solid #e2e8f0",borderRadius:10,padding:14,marginTop:16,background:"#f8fafc"}}>
              <div style={{fontSize:10,fontWeight:800,color:"#475569",textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Emergency Contact</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div className="config-field">
                  <div className="config-label">Contact Name</div>
                  <input className="config-input" maxLength={100} value={form.emergency_name||""} onChange={e=>set("emergency_name")(e.target.value.slice(0, 100))} placeholder="Spouse / Parent Name" />
                </div>
                <div className="config-field">
                  <div className="config-label">Relation</div>
                  <input className="config-input" value={form.emergency_relation||""} onChange={e=>set("emergency_relation")(e.target.value)} placeholder="e.g. Spouse, Father" />
                </div>
                <div className="config-field">
                  <div className="config-label">Phone</div>
                  <div style={{display:"flex",gap:6}}>
                    <div style={{background:"#f1f5f9",border:"1.5px solid #e2e8f0",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#64748b",fontWeight:600,display:"flex",alignItems:"center"}}>+91</div>
                    <input className="config-input" value={getPhone10(form.emergency_phone)} onChange={e=>set("emergency_phone")(getPhone10(e.target.value))} placeholder="9876543210" style={{fontFamily:"monospace"}} maxLength={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1.5px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12
        }}>
          <div style={{flex: 1, minWidth: 260}}>
            {msg && (
              <div className="err-msg" style={{
                margin: 0, 
                display: "flex", 
                alignItems: "center", 
                gap: 8, 
                fontWeight: 600, 
                fontSize: 13,
                animation: "fadeIn .2s ease"
              }}>
                <span>⚠️</span>
                <span>{msg}</span>
              </div>
            )}
          </div>
          <div style={{display: "flex", gap: 10, alignItems: "center"}}>
            <button className="btn btn-ghost" onClick={onBack}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>
              {initial ? "💾 Save Employee" : "➕ Add Employee"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Centered Modal for Add Department / Add Designation ── */}
      {quickAddModal && (
        <div 
          className="overlay" 
          style={{ 
            zIndex: 99999, 
            background: "rgba(15, 23, 42, 0.65)", 
            backdropFilter: "blur(4px)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            padding: 16 
          }} 
          onClick={() => setQuickAddModal(null)}
        >
          <div 
            className="modal" 
            style={{ 
              maxWidth: 440, 
              width: "100%", 
              margin: "0 auto", 
              textAlign: "left", 
              padding: 24, 
              borderRadius: 16, 
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)" 
            }} 
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{quickAddModal.type === "department" ? "🏢" : "💼"}</span>
                <span>{quickAddModal.type === "department" ? "Add Department" : "Add Designation"}</span>
              </div>
              <button 
                type="button" 
                className="btn btn-ghost btn-xs" 
                style={{ fontSize: 20, padding: 0, lineHeight: 1, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }} 
                onClick={() => setQuickAddModal(null)}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.5 }}>
              {quickAddModal.type === "department"
                ? `Enter the department name to add it to ${company?.name || "the company"}. It will automatically be selected for this employee.`
                : `Enter the designation to add it to ${company?.name || "the company"}. It will automatically be selected for this employee.`}
            </div>

            <form onSubmit={handleSaveQuickAdd}>
              <div className="config-field" style={{ marginBottom: 16 }}>
                <div className="config-label" style={{ fontWeight: 800, marginBottom: 6 }}>
                  {quickAddModal.type === "department" ? "Department Name" : "Designation Title"} <span style={{ color: "#dc2626" }}>*</span>
                </div>
                <input
                  autoFocus
                  className="config-input"
                  value={quickAddModal.value}
                  onChange={e => setQuickAddModal({ ...quickAddModal, value: e.target.value, error: "" })}
                  placeholder={quickAddModal.type === "department" ? "e.g. Engineering, Sales, Marketing" : "e.g. Senior Software Engineer, Team Lead"}
                  style={{ width: "100%", fontSize: 14 }}
                />
                {quickAddModal.error && (
                  <div style={{ fontSize: 11, color: "#ef4444", marginTop: 6, fontWeight: 600 }}>
                    ⚠️ {quickAddModal.error}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                <button 
                  type="button" 
                  className="btn btn-ghost" 
                  onClick={() => setQuickAddModal(null)}
                  disabled={quickAddModal.saving}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={quickAddModal.saving || !quickAddModal.value?.trim()}
                >
                  {quickAddModal.saving ? "Saving..." : (quickAddModal.type === "department" ? "Add Department" : "Add Designation")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SalaryModal({initial,cfg,employee,onSave,onClose,msg,setMsg,isEdit}){
  const now = new Date();
  const [form, setForm] = useState({
    month: initial?.month || now.getMonth() + 1,
    year: initial?.year || now.getFullYear(),
    basic: initial?.basic || employee?.basic_salary || employee?.basic || 0,
    absent_days: initial?.absent_days || initial?.days_absent || 0,
    tds_amount: initial?.tds || employee?.tds_amount || 0,
    ...initial,
  });

  const set = k => v => { setForm(p=>({...p,[k]:v})); setMsg(""); };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-title">{isEdit?"Edit Salary Record":"Add Salary Record"}</div>
        <div className="modal-sub">For {employee?.name}</div>
        {msg&&<div className="err-msg">⚠️ {msg}</div>}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div className="config-field">
            <div className="config-label">Month</div>
            <select className="config-input" value={form.month} onChange={e=>set("month")(parseInt(e.target.value))}>
              {MONTHS.slice(1).map((m,idx)=><option key={idx+1} value={idx+1}>{m}</option>)}
            </select>
          </div>
          <div className="config-field">
            <div className="config-label">Year</div>
            <input className="config-input" type="number" value={form.year} onChange={e=>set("year")(parseInt(e.target.value))} />
          </div>
          <div className="config-field" style={{gridColumn:"1/-1"}}>
            <div className="config-label">Gross Salary (₹)</div>
            <input className="config-input" type="number" value={form.basic||""} onChange={e=>set("basic")(parseFloat(e.target.value)||0)} placeholder="25000" style={{fontFamily:"monospace"}} />
          </div>
          <div className="config-field">
            <div className="config-label">Absent Days</div>
            <input className="config-input" type="number" value={form.absent_days||""} onChange={e=>set("absent_days")(parseFloat(e.target.value)||0)} placeholder="0" style={{fontFamily:"monospace"}} />
          </div>
          <div className="config-field">
            <div className="config-label">TDS Amount (₹)</div>
            <input className="config-input" type="number" value={form.tds_amount||""} onChange={e=>set("tds_amount")(parseFloat(e.target.value)||0)} placeholder="0" style={{fontFamily:"monospace"}} />
          </div>
        </div>

        <div className="modal-footer" style={{marginTop:18}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>{isEdit?"💾 Save":"➕ Add Record"}</button>
        </div>
      </div>
    </div>
  );
}

// ── ESI / PF Modal
function ESIPFModal({type,initial,onSave,onClose,msg,setMsg}){
  const isESI=type==="esi";
  const [form,setForm]=useState({...initial});
  const [members,setMembers]=useState(initial?.members||[]);
  const set=k=>v=>{setForm(p=>({...p,[k]:v}));setMsg("");};
  return(
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-title">{isESI?"Edit ESI Details":"Edit PF Details"}</div>
         <div className="modal-sub">{isESI?"ESI registration details":"PF / UAN details"}</div>
        {msg&&<div className="err-msg">⚠ {msg}</div>}
        <div className="form-grid">
          {isESI?<>
            <div className="field full"><label>ESI Number</label><input value={form.esi_id||""} onChange={e=>set("esi_id")(e.target.value)} placeholder="31-00-123456-000" style={{fontFamily:"monospace"}} /></div>
          </>:<>
            <div className="field full"><label>UAN (PF)</label><input value={form.uan||""} onChange={e=>set("uan")(e.target.value)} placeholder="100200300400500" style={{fontFamily:"monospace",letterSpacing:1}} /></div>
          </>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>onSave({...form,members})}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Modal
function ChangePasswordModal({ currentUser, activeCompany, companies, setCompanies, setActiveCompany, onClose }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw]         = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [msg, setMsg]             = useState("");
  const [success, setSuccess]     = useState(false);
  const [saving, setSaving]       = useState(false);

  const isCompany = currentUser?.role === "company";
  const targetCompany = activeCompany || (isCompany ? companies.find(c => c.id === currentUser.companyId) : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!currentPw) { setMsg("Please enter current password."); return; }
    if (!newPw) { setMsg("Please enter a new password."); return; }
    if (newPw.length < 12) { setMsg("New password must be at least 12 characters long."); return; }
    if (newPw !== confirmPw) { setMsg("New password and confirm password do not match."); return; }

    setSaving(true);
    try {
      const { error: verificationError } = await supabase.auth.signInWithPassword({ email: currentUser.login_username, password: currentPw });
      if (verificationError) throw new Error('Current password is incorrect.');
      const { error: changeError } = await supabase.auth.updateUser({ password: newPw });
      if (changeError) throw changeError;
      setSuccess(true);
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      setMsg("Failed to update password: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:420,background:"#ffffff",border:"1.5px solid #cbd5e1",borderRadius:20,padding:"28px 24px"}} onClick={e => e.stopPropagation()}>
        <div style={{fontSize:20,fontWeight:800,color:"#0f172a",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
          🔑 Change Password
        </div>
        <div style={{fontSize:13,color:"#64748b",marginBottom:16,fontWeight:500}}>
          {isCompany ? `Account password for ${targetCompany?.name || "Company"}` : "Administrator Account"}
        </div>
        {msg && <div style={{background:"#fef2f2",border:"1.5px solid #fca5a5",color:"#991b1b",padding:"10px 14px",borderRadius:10,fontSize:13,fontWeight:600,marginBottom:14,textAlign:"center"}}>⚠️ {msg}</div>}
        {success && <div style={{background:"#f0fdf4",border:"1.5px solid #86efac",color:"#166534",padding:"10px 14px",borderRadius:10,fontSize:13,fontWeight:700,marginBottom:14,textAlign:"center"}}>✅ Password updated successfully!</div>}
        
        <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="config-field">
            <div className="config-label" style={{fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",marginBottom:4}}>Current Password</div>
            <input
              className="config-input"
              type="password"
              value={currentPw}
              onChange={e => { setCurrentPw(e.target.value); setMsg(""); }}
              placeholder="Enter current password"
              autoFocus
              style={{background:"#f8fafc",border:"1.5px solid #cbd5e1",borderRadius:10,padding:"10px 14px",fontSize:14,color:"#0f172a"}}
            />
          </div>
          <div className="config-field">
            <div className="config-label" style={{fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",marginBottom:4}}>New Password</div>
            <input
              className="config-input"
              type="password"
              value={newPw}
              onChange={e => { setNewPw(e.target.value); setMsg(""); }}
              placeholder="Enter new password (min 4 characters)"
              style={{background:"#f8fafc",border:"1.5px solid #cbd5e1",borderRadius:10,padding:"10px 14px",fontSize:14,color:"#0f172a"}}
            />
          </div>
          <div className="config-field">
            <div className="config-label" style={{fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",marginBottom:4}}>Confirm New Password</div>
            <input
              className="config-input"
              type="password"
              value={confirmPw}
              onChange={e => { setConfirmPw(e.target.value); setMsg(""); }}
              placeholder="Re-enter new password"
              style={{background:"#f8fafc",border:"1.5px solid #cbd5e1",borderRadius:10,padding:"10px 14px",fontSize:14,color:"#0f172a"}}
            />
          </div>
          <div className="modal-footer" style={{marginTop:12,display:"flex",justifyContent:"flex-end",gap:10}}>
            <button type="button" className="btn btn-ghost" style={{borderRadius:10,padding:"9px 16px"}} onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{borderRadius:10,padding:"9px 18px",fontWeight:800}} disabled={saving}>
              {saving ? "Saving…" : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── System Test Runner
export function SystemTestRunner({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const busyRef = useRef(false);
  const lastRunRef = useRef(undefined);
  const cleanupStorageKey = "tw_pending_system_test_cleanup";
  if (lastRunRef.current === undefined) {
    try {
      const pending=JSON.parse(sessionStorage.getItem(cleanupStorageKey)||"null");
      lastRunRef.current=pending?.runId && Array.isArray(pending.ids)?{identity:{runId:pending.runId},ids:new Set(pending.ids)}:null;
    } catch (_) {lastRunRef.current=null;}
  }
  const rememberRun = run => {
    lastRunRef.current=run;
    try {
      if(run)sessionStorage.setItem(cleanupStorageKey,JSON.stringify({runId:run.identity.runId,ids:[...run.ids]}));
      else sessionStorage.removeItem(cleanupStorageKey);
    } catch (_) { /* Database ownership markers still support stale-fixture cleanup. */ }
  };
  const checks = useMemo(()=>createSystemRegressionChecks({OrgAndCulture:OrgAndCultureDashboard,CommandPalette:CommandPaletteModal,SamplePayslip:SamplePayslipModal,generatePdf:generatePayslipPdfDoc}),[]);
  const log = (msg,status="info")=>setLogs(previous=>[...previous,{msg,status}]);

  const cleanup = async (onlyLastRun = false) => {
    const last = lastRunRef.current;
    if (last) await cleanupSystemTestFixtures(supabase,{runId:last.identity.runId,trackedIds:[...last.ids]});
    if (!onlyLastRun) await cleanupSystemTestFixtures(supabase,{staleOnly:true});
    rememberRun(null);
    log("Cleanup verified: no records remain for the test fixtures inspected.","pass");
  };
  const runTests = async () => {
    if (busyRef.current) return;
    busyRef.current=true;setRunning(true);setLogs([]);setSummary(null);
    const identity=createSystemTestIdentity();
    const trackedIds=new Set();
    let legacyPassed=0,legacyError="",cleanupError="",results=[];
    try {
      log("Database checks use a uniquely marked temporary company. Regression UI/workflow checks use isolated storage.");
      try {
        try {await cleanup();} catch(error){cleanupError=error.message;throw error;}
        rememberRun({identity,ids:trackedIds});
        await runLegacySystemTests({client:supabase,identity,onCompanyCreated:id=>{trackedIds.add(id);rememberRun({identity,ids:trackedIds});},log:(msg,status)=>{if(status==="pass")legacyPassed++;log(msg,status);}});
      } catch(error) {legacyError=error.message;log("Legacy suite stopped: "+error.message,"fail");}
      finally {
        try {await cleanupSystemTestFixtures(supabase,{runId:identity.runId,trackedIds:[...trackedIds]});if(lastRunRef.current?.identity.runId===identity.runId)rememberRun(null);log("Temporary database company, employees and salary records removed and verified.","pass");}
        catch(error){cleanupError=error.message;log("CLEANUP REQUIRED: "+error.message+" Use Clean Test Residues to retry.","fail");}
      }
      log("Running shared regression checks (also used by the developer test suite)…");
      results=await runSystemRegressionChecks(checks,result=>log(`${result.group} · ${result.name}${result.error ? ": "+result.error : ""}`,result.status));
      const passed=results.filter(result=>result.status==="pass").length;
      const failed=results.length-passed;
      setSummary({legacyPassed,legacyError,passed,failed,total:checks.length,cleanupError});
      log(!legacyError&&!cleanupError&&!failed?"All checks passed and cleanup verified.":"Run finished with issues. Review failed checks and cleanup status above.",!legacyError&&!cleanupError&&!failed?"pass":"fail");
    } catch(error) {log("Runner error: "+error.message,"fail");}
    finally {busyRef.current=false;setRunning(false);}
  };
  const cleanResidues = async () => {
    if(busyRef.current)return;
    busyRef.current=true;setRunning(true);
    try {await cleanup();setSummary(previous=>previous?{...previous,cleanupError:""}:previous);}
    catch(error){log("Cleanup could not be verified: "+error.message,"fail");}
    finally {busyRef.current=false;setRunning(false);}
  };
  return <div className="overlay"><div className="modal" style={{maxWidth:850,width:"95%"}}>
    <div className="modal-title">🧪 System Diagnostics &amp; Regression Tests</div>
    <p style={{fontSize:13,color:"#64748b",lineHeight:1.6,marginBottom:16}}>
      Run the existing 28 payroll/database checks plus {checks.length} shared checks for employee validation, the ₹17,800 HRA fix, payroll approval/payment, org search, collapsible navigation, Quick Search, payslip templates and training progress/employee completion.
      Training browser checks use isolated samples; training SQL permission/transition checks remain in the offline PostgreSQL suite.
      UI and workflow regressions use isolated storage; database checks create a temporary company and verify its cleanup, including after failures. Active fixtures from other sessions are preserved.
    </p>
    {summary && <div role="status" style={{padding:12,background:summary.legacyError||summary.failed||summary.cleanupError?"#fffbeb":"#f0fdf4",border:"1px solid #e2e8f0",borderRadius:8,fontSize:12,marginBottom:12,lineHeight:1.7}}>
      <div><strong>Legacy:</strong> {summary.legacyPassed}/28 passed{summary.legacyError?" · stopped on failure; remaining dependent checks were not run":""} · <strong>Shared regressions:</strong> {summary.passed}/{summary.total} passed · {summary.failed} failed</div>
      <div><strong>Cleanup:</strong> {summary.cleanupError?"Needs retry — "+summary.cleanupError:"Verified"}</div>
    </div>}
    <div role="log" aria-label="System test results" style={{background:"#0f172a",borderRadius:8,padding:16,maxHeight:400,overflowY:"auto",fontFamily:"monospace",fontSize:12,marginBottom:16}}>
      {!logs.length&&<div style={{color:"#94a3b8"}}>Ready. No tests run yet.</div>}
      {logs.map((entry,index)=><div key={index} style={{color:entry.status==="pass"?"#6ee7b7":entry.status==="fail"?"#fca5a5":entry.status==="running"?"#fcd34d":"#93c5fd",marginBottom:5}}>{entry.status==="pass"?"✓ ":entry.status==="fail"?"✗ ":entry.status==="running"?"… ":""}{entry.msg}</div>)}
    </div>
    <div className="modal-footer" style={{display:"flex",gap:10,flexWrap:"wrap"}}>
      <button className="btn btn-ghost" onClick={onClose} disabled={running}>Close</button>
      <button className="btn btn-ghost" onClick={cleanResidues} disabled={running}>🧹 Clean Test Residues</button>
      <button className="btn btn-primary" onClick={runTests} disabled={running}>{running?"Running / Cleaning…":"▶ Run All Checks"}</button>
    </div>
  </div></div>;
}

function SalaryConfigInner({cfg, set, leftOpen, setLeftOpen}) {
  return (
    <>
            {/* SECTION 1: Basic & HRA */}
            <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
              <div style={{fontSize:13,fontWeight:800,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>setLeftOpen(p=>({...p,1:!p[1]}))}>
                <span>💵 Basic & HRA</span>
                <span style={{fontSize:16,color:"#94a3b8"}}>{leftOpen[1] ? "−" : "+"}</span>
              </div>
              {leftOpen[1] && (
                <div style={{marginTop:16, display: "flex", flexDirection: "column", gap: 8}}>
                  <div className="toggle-row">
                    <div className="toggle-label">Working Days / Month</div>
                    <div style={{display:"flex",gap:4}}>
                      <select className="config-input" style={{flex:1}} value={[-1, 30, 26, 22].includes(cfg.working_days) ? cfg.working_days : "custom"} onChange={e=>{
                        let val = e.target.value;
                        if (val !== "custom") set("working_days")(parseInt(val));
                        else set("working_days")("");
                      }}>
                        <option value="-1">Calendar Days (Dynamic)</option>
                        <option value="30">Fixed 30 Days</option>
                        <option value="26">Fixed 26 Days</option>
                        <option value="22">Fixed 22 Days</option>
                        <option value="custom">Custom...</option>
                      </select>
                      {![-1, 30, 26, 22].includes(cfg.working_days) && (
                        <input className="config-input" style={{width:"40%"}} type="number" min="1" max="31" value={cfg.working_days} onChange={e=>{
                          let v = parseInt(e.target.value);
                          if(v > 31) v = 31;
                          set("working_days")(isNaN(v) ? "" : v);
                        }} onBlur={e=>{
                          let v = parseInt(e.target.value);
                          if(isNaN(v) || v < 1) set("working_days")(26);
                        }} placeholder="Days" />
                      )}
                    </div>
                  </div>
                  
                  <div className="toggle-row">
                    <div className="toggle-label">
                      Allow Customised Basic per Employee
                      <span className="custom-tooltip-container" style={{marginLeft: 4}}>
                        ℹ️
                        <span className="custom-tooltip">If enabled, you can specify a custom Basic Salary % per employee.</span>
                      </span>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={cfg.custom_basic_enabled||false} onChange={e=>set("custom_basic_enabled")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  
                  {!cfg.custom_basic_enabled && (
                    <div className="toggle-row" style={{paddingLeft:16,borderLeft:"2px solid #2563eb",marginTop:-4}}>
                      <div className="toggle-label">Global Basic (% of Gross)</div>
                      <div style={{display:"flex",gap:4,width:"50%",alignItems:"center"}}>
                        <input className="config-input" style={{flex:1}} type="number" max="100" min="0" value={cfg.basic_pct!==undefined?cfg.basic_pct:50} onChange={e=>{
                          let val = parseFloat(e.target.value);
                          if (val > 100) val = 100;
                          set("basic_pct")(isNaN(val) ? "" : val);
                        }} onBlur={e=>{
                          if (isNaN(parseFloat(e.target.value))) set("basic_pct")(50);
                        }} placeholder="50" />
                        <span style={{fontSize:13,color:"#475569",fontWeight:600}}>%</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="toggle-row">
                    <div className="toggle-label">
                      Allow Customised HRA per Employee
                      <span className="custom-tooltip-container" style={{marginLeft: 4}}>
                        ℹ️
                        <span className="custom-tooltip">If enabled, you can specify a custom HRA amount per employee when adding them.</span>
                      </span>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={cfg.hra_customised||false} onChange={e=>set("hra_customised")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  
                  {!cfg.hra_customised && (
                    <div className="toggle-row" style={{paddingLeft:16,borderLeft:"2px solid #2563eb",marginTop:-4}}>
                      <div className="toggle-label">Global HRA (House Rent Allowance)</div>
                      <div style={{display:"flex",gap:4,width:"50%"}}>
                        <select className="config-input" style={{width:"40%"}} value={cfg.hra_type||"pct"} onChange={e=>set("hra_type")(e.target.value)}>
                          <option value="pct">% of Basic</option>
                          <option value="fixed">Fixed ₹</option>
                        </select>
                        <input className="config-input" style={{flex:1}} type="number" max={cfg.hra_type==="pct"?50:undefined} value={cfg.hra_value!==undefined?cfg.hra_value:(cfg.hra_pct??40)} onChange={e=>{
                          let val = parseFloat(e.target.value);
                          if (cfg.hra_type==="pct" && val > 50) val = 50;
                          set("hra_value")(isNaN(val) ? "" : val);
                        }} onBlur={e=>{
                          if (isNaN(parseFloat(e.target.value))) set("hra_value")(cfg.hra_type==="pct"?40:0);
                        }} placeholder={cfg.hra_type==="fixed"?"5000":"40"} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 2: EPF & ESI & Statutory */}
            <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
              <div style={{fontSize:13,fontWeight:800,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>setLeftOpen(p=>({...p,2:!p[2]}))}>
                <span>🏛️ EPF, ESI & Statutory</span>
                <span style={{fontSize:16,color:"#94a3b8"}}>{leftOpen[2] ? "−" : "+"}</span>
              </div>
              {leftOpen[2] && (
                <div style={{marginTop:16, display: "flex", flexDirection: "column", gap: 8}}>
                  <div className="toggle-row">
                    <div className="toggle-label">
                      Minimum Wage Floor (Basic + DA)
                      <span className="custom-tooltip-container" style={{marginLeft: 4}}>
                        ℹ️
                        <span className="custom-tooltip">If employee's Basic+DA falls below this, it will be raised to this floor before computing PF & ESI.</span>
                      </span>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center",width:"50%"}}>
                      <label className="toggle" style={{marginBottom:0}}>
                        <input type="checkbox" checked={cfg.min_wage_enabled !== false} onChange={e=>set("min_wage_enabled")(e.target.checked)} />
                        <span className="toggle-slider" />
                      </label>
                      {cfg.min_wage_enabled !== false && (
                        <input className="config-input" style={{flex:1}} type="number" min="0" 
                          value={cfg.min_wage !== undefined && cfg.min_wage !== null ? cfg.min_wage : 15000}
                          onChange={e => {
                            const val = e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0);
                            set("min_wage")(val);
                          }}
                          onBlur={e => {
                            if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                              set("min_wage")(15000);
                            }
                          }}
                          placeholder="e.g. 15000" />
                      )}
                    </div>
                  </div>
                  
                  <div className="toggle-row">
                    <span className="toggle-label">PF Contribution Applicable (12%)</span>
                    <label className="toggle" title="ON: Deducts 12% EPF from employee's salary. OFF: No EPF deduction.">
                      <input type="checkbox" checked={cfg.pf_applicable!==false} onChange={e=>set("pf_applicable")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  {cfg.pf_applicable !== false && (
                    <div className="toggle-row" style={{paddingLeft:16,borderLeft:"2px solid #2563eb",marginTop:-4}}>
                      <span className="toggle-label" style={{fontSize:12.5,fontWeight:600,color:"#334155"}}>
                        PF Wage at Statutory Limit (₹25,000 / month)
                        <span className="custom-tooltip-container" style={{marginLeft:4}} tabIndex={0} aria-label="PF wage limit information">
                          ℹ️
                          <span className="custom-tooltip">When enabled, PF is calculated on Basic + DA capped at ₹25,000 per month. When disabled, PF is calculated on the full Basic + DA without an upper limit.</span>
                        </span>
                      </span>
                      <label className="toggle" title="ON: PF calculation is capped at the statutory limit. OFF: PF is calculated on the full Basic+DA with no upper limit.">
                        <input type="checkbox" checked={cfg.pf_wage_cap!==false} onChange={e=>set("pf_wage_cap")(e.target.checked)} />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  )}
                  <div className="toggle-row">
                    <span className="toggle-label">ESI Contribution Applicable (0.75%)</span>
                    <label className="toggle" title="ON: Deducts 0.75% ESI for employees with a Rate of Wage ₹21,000 or below. OFF: No ESI deduction.">
                      <input type="checkbox" checked={cfg.esi_applicable!==false} onChange={e=>set("esi_applicable")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  
                  <div className="toggle-row">
                    <span className="toggle-label">LWF Applicable (Karnataka — ₹50 emp / Dec)</span>
                    <label className="toggle" title="ON: Deducts ₹50 from employee's salary for LWF in the December payroll cycle. OFF: No LWF deduction.">
                      <input type="checkbox" checked={cfg.lwf_applicable||false} onChange={e=>set("lwf_applicable")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-label" title="ON: Accrues 8.33% of Basic as Bonus for eligible employees (Basic <= 21k). OFF: No Bonus calculation.">
                      <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                        <span>Bonus (8.33%) Applicable if Basic &lt;= ₹21,000 / month</span>
                        {cfg.bonus && (
                          <input className="config-input" style={{padding: "2px 6px", fontSize: 12, width: 180}} 
                                 value={cfg.bonus_display_name || ""} 
                                 onChange={e => set("bonus_display_name")(e.target.value)} 
                                 placeholder="Payslip Label (e.g. Bonus)" />
                        )}
                      </div>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={cfg.bonus||false} onChange={e=>{
                        const isChecked = e.target.checked;
                        set("bonus")(isChecked);
                        if(isChecked) {
                          set("bonus_pct")(cfg.bonus_pct || 8.33);
                          if (cfg.bonus_in_gross === undefined) set("bonus_in_gross")(true);
                          if (!cfg.bonus_months || cfg.bonus_months.length === 0) {
                            set("bonus_months")([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
                          }
                        }
                      }} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  {cfg.bonus && (
                    <div style={{padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", marginTop: -4, marginBottom: 8, borderLeft: "3px solid #2563eb"}}>
                      {/* Option 2 Mode Toggle: Bonus Included in Gross vs Over and Above */}
                      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#ffffff", borderRadius: 6, border: "1px solid #cbd5e1", marginBottom: 12}}>
                        <div>
                          <div style={{fontSize: 12.5, fontWeight: 700, color: "#1e293b"}}>
                            Part of Agreed Monthly Gross (CTC Model)
                          </div>
                          <div style={{fontSize: 11, color: "#64748b", marginTop: 2}}>
                            {cfg.bonus_in_gross !== false
                              ? "✓ Enabled: Bonus is carved out within agreed Gross (Gross stays strictly at agreed salary, no extra company outflow)"
                              : "✕ Disabled: Bonus is paid over & above regular Gross (Company pays extra bonus disbursement in payout months)"}
                          </div>
                        </div>
                        <label className="toggle" title="Toggle between CTC model (Bonus within Gross) and traditional Payout model (Bonus over Gross)">
                          <input type="checkbox" checked={cfg.bonus_in_gross !== false} onChange={e => set("bonus_in_gross")(e.target.checked)} />
                          <span className="toggle-slider" />
                        </label>
                      </div>

                      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8}}>
                        <div>
                          <div style={{fontSize: 12, fontWeight: 700, color: "#1e293b"}}>Bonus Payout Schedule</div>
                          <div style={{fontSize: 11, color: "#64748b"}}>Choose which payroll cycles include the 8.33% statutory bonus payout</div>
                        </div>
                        <div style={{display: "flex", gap: 6, flexWrap: "wrap"}}>
                          <button 
                            type="button" 
                            className="btn btn-ghost" 
                            style={{fontSize: 11, padding: "3px 8px", height: "auto", border: "1px solid #cbd5e1", background: (cfg.bonus_months?.length === 12 ? "#eff6ff" : "#fff"), color: (cfg.bonus_months?.length === 12 ? "#1d4ed8" : "#334155")}}
                            onClick={() => set("bonus_months")([1,2,3,4,5,6,7,8,9,10,11,12])}
                          >
                            All 12 Months (Monthly)
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-ghost" 
                            style={{fontSize: 11, padding: "3px 8px", height: "auto", border: "1px solid #cbd5e1", background: "#fff"}}
                            onClick={() => set("bonus_months")([3, 10])}
                          >
                            Mar &amp; Oct (Diwali / Year-end)
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-ghost" 
                            style={{fontSize: 11, padding: "3px 8px", height: "auto", border: "1px solid #cbd5e1", background: "#fff"}}
                            onClick={() => set("bonus_months")([3])}
                          >
                            March Only
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-ghost" 
                            style={{fontSize: 11, padding: "3px 8px", height: "auto", border: "1px solid #cbd5e1", background: "#fff", color: "#dc2626"}}
                            onClick={() => set("bonus_months")([])}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div style={{display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6}}>
                        {MONTHS.slice(1).map((mName, idx) => {
                          const mNum = idx + 1;
                          const selectedMonths = cfg.bonus_months || [];
                          const isSelected = selectedMonths.includes(mNum);
                          return (
                            <button
                              key={mNum}
                              type="button"
                              onClick={() => {
                                const nextMonths = isSelected 
                                  ? selectedMonths.filter(m => m !== mNum)
                                  : [...selectedMonths, mNum].sort((a,b) => a - b);
                                set("bonus_months")(nextMonths);
                              }}
                              style={{
                                padding: "4px 10px",
                                fontSize: 11.5,
                                fontWeight: 700,
                                borderRadius: 6,
                                cursor: "pointer",
                                border: isSelected ? "1.5px solid #2563eb" : "1.5px solid #cbd5e1",
                                background: isSelected ? "#eff6ff" : "#ffffff",
                                color: isSelected ? "#1d4ed8" : "#64748b",
                                transition: "all 0.15s ease"
                              }}
                            >
                              {isSelected ? "✓ " : ""}{mName.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{fontSize: 11, color: "#64748b", marginTop: 8}}>
                        Currently active: <strong style={{color: (cfg.bonus_months?.length ? "#1e293b" : "#dc2626")}}>
                          {cfg.bonus_months?.length 
                            ? (cfg.bonus_months.length === 12 ? "Every Month (All 12 Months)" : cfg.bonus_months.map(m => MONTHS[m]).join(", ")) 
                            : "None selected (no bonus will be paid in any month)"}
                        </strong>
                      </div>
                    </div>
                  )}
                  <div className="toggle-row">
                    <span className="toggle-label">Gratuity Provision Applicable (4.81%)</span>
                    <label className="toggle" title="ON: Accrues 4.81% of Basic towards Gratuity provision. OFF: No Gratuity calculation.">
                      <input type="checkbox" checked={cfg.gratuity!==false} onChange={e=>set("gratuity")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 3: Other Components */}
            <div style={{background:"#fff",padding:16,borderRadius:8,border:"1px solid #e2e8f0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
              <div style={{fontSize:13,fontWeight:800,color:"#1e293b",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#2563eb"; e.currentTarget.style.transform="translateX(4px)";}} onMouseLeave={e=>{e.currentTarget.style.color="#1e293b"; e.currentTarget.style.transform="translateX(0)";}} onClick={()=>setLeftOpen(p=>({...p,3:!p[3]}))}>
                <span>⚙️ Other Components</span>
                <span style={{fontSize:16,color:"#94a3b8"}}>{leftOpen[3] ? "−" : "+"}</span>
              </div>
              {leftOpen[3] && (
                <div style={{marginTop:16, display: "flex", flexDirection: "column", gap: 8}}>
                  <div className="toggle-row">
                    <div className="toggle-label" style={{whiteSpace: "nowrap"}}>Absence Deduction From</div>
                    <select className="config-input" style={{width:"170px"}} value={(cfg.absence_basis === "none" || cfg.absence_basis === "no_deduct" || cfg.absence_deduction === false) ? "none" : (cfg.absence_basis || "none")} onChange={e=>{
                      const val = e.target.value;
                      if(val === "none"){
                        set("absence_basis")("none");
                        set("absence_deduction")(false);
                      } else {
                        set("absence_basis")(val);
                        set("absence_deduction")(true);
                      }
                    }}>
                      <option value="none">None (No Deduction)</option>
                      <option value="basic">Basic Salary</option>
                      <option value="gross">Gross Salary</option>
                    </select>
                  </div>
                  <div className="toggle-row">
                    <span className="toggle-label">Overtime Applicable</span>
                    <label className="toggle" title="ON: Enables OT calculation for employees based on hourly rate. OFF: No OT calculated regardless of OT hours entered.">
                      <input type="checkbox" checked={cfg.ot_applicable!==false} onChange={e=>set("ot_applicable")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  {cfg.ot_applicable !== false && (
                    <div className="toggle-row" style={{paddingLeft:16,borderLeft:"2px solid #2563eb",marginTop:-4, flexDirection:"column", alignItems:"flex-start", gap:6}}>
                      <span className="toggle-label" style={{fontSize:12.5,fontWeight:600,color:"#334155"}}>Calculate Overtime (Per Hour) from:</span>
                      <select className="config-input" value={cfg.ot_basis||"basic"} onChange={e=>set("ot_basis")(e.target.value)} style={{width:"100%"}}>
                        <option value="basic">Basic + DA</option>
                        <option value="gross">Gross Salary</option>
                      </select>
                      <span className="toggle-label" style={{fontSize:12.5,fontWeight:600,color:"#334155",marginTop:4}}>Overtime Payment Multiplier:</span>
                      <select className="config-input" value={cfg.ot_rate_multiplier||1} onChange={e=>set("ot_rate_multiplier")(parseInt(e.target.value))} style={{width:"100%"}}>
                        <option value="1">1x (Standard Hourly Rate)</option>
                        <option value="2">2x (Double Hourly Rate)</option>
                      </select>
                    </div>
                  )}

                  <div className="toggle-row">
                    <div className="toggle-label" title="ON: Allows a custom Special Allowance / CCA amount to be configured per-employee. OFF: No per-employee custom allowance.">
                      <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                        <span>Enable Custom Special Allowance (Per-Employee)</span>
                        {cfg.custom_special_allowance_enabled && (
                          <input className="config-input" style={{padding: "2px 6px", fontSize: 12, width: 180}} 
                                 value={cfg.custom_special_display_name || ""} 
                                 onChange={e => set("custom_special_display_name")(e.target.value)} 
                                 placeholder="Payslip Label (e.g. CCA)" />
                        )}
                      </div>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={cfg.custom_special_allowance_enabled||false} onChange={e=>set("custom_special_allowance_enabled")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  <div className="toggle-row">
                    <span className="toggle-label">
                      Enable VPF & TDS Deductions
                      <span className="custom-tooltip-container" style={{marginLeft: 4}}>
                        ℹ️
                        <span className="custom-tooltip">If enabled, you can specify custom Voluntary Provident Fund and Tax Deducted at Source on a per-employee basis.</span>
                      </span>
                    </span>
                    <label className="toggle">
                      <input type="checkbox" checked={cfg.vpf_tds_enabled||false} onChange={e=>set("vpf_tds_enabled")(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                </div>
              )}
            </div>
    </>
  );
}


function ChangeLoginIDModal({ currentUser, activeCompany, companies, setCompanies, setActiveCompany, onClose }) {
  const [newId, setNewId] = useState(currentUser?.login_username || "");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      if (!newId.trim()) throw new Error("Login ID cannot be empty.");
      
      const idStr = newId.trim();
      if (idStr.toLowerCase() === "admin") {
        throw new Error("This login ID is not available. Please choose a different one.");
      }
      if (companies.some(c => c.login_username && c.login_username.trim().toLowerCase() === idStr.toLowerCase() && c.id !== activeCompany.id)) {
        throw new Error("This login ID is not available. Please choose a different one.");
      }

      setSaving(true);
      if (!isValidEmail(idStr)) throw new Error('Enter a valid email address.');
      const { error } = await supabase.auth.updateUser({ email: idStr });
      if (error) throw error;
      alert('Check your email to confirm this login email change. Your current email remains active until confirmation.');
      onClose();
      // alert("Login ID updated successfully!");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth: 400}}>
        <div className="modal-title">Change Login ID</div>
        <div style={{marginTop:16, display: "flex", flexDirection: "column", gap: 16}}>
          <div>
            <div style={{fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 4}}>New Login ID</div>
            <input className="config-input" value={newId} onChange={e=>setNewId(e.target.value)} placeholder="hr@company.com" />
          </div>
          {msg && <div style={{fontSize:13, color:"#ef4444"}}>{msg}</div>}
          <div style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop: 16}}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Login ID"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

